/**
 * ag-grid-community - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components * @version v22.1.1
 * @link http://www.ag-grid.com/
' * @license MIT
 */
'use strict';

Object.defineProperty(exports, '__esModule', {
  value: true
});
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */

var Color =
/** @class */
function () {
  /**
   * Every color component should be in the [0, 1] range.
   * Some easing functions (such as elastic easing) can overshoot the target value by some amount.
   * So, when animating colors, if the source or target color components are already near
   * or at the edge of the allowed [0, 1] range, it is possible for the intermediate color
   * component value to end up outside of that range mid-animation. For this reason the constructor
   * performs range checking/constraining.
   * @param r Red component.
   * @param g Green component.
   * @param b Blue component.
   * @param a Alpha (opacity) component.
   */
  function Color(r, g, b, a) {
    if (a === void 0) {
      a = 1;
    } // NaN is treated as 0.


    this.r = Math.min(1, Math.max(0, r || 0));
    this.g = Math.min(1, Math.max(0, g || 0));
    this.b = Math.min(1, Math.max(0, b || 0));
    this.a = Math.min(1, Math.max(0, a || 0));
  }
  /**
   * The given string can be in one of the following formats:
   * - #rgb
   * - #rrggbb
   * - rgb(r, g, b)
   * - rgba(r, g, b, a)
   * - CSS color name such as 'white', 'orange', 'cyan', etc.
   * @param str
   */


  Color.fromString = function (str) {
    // hexadecimal notation
    if (str.indexOf('#') >= 0) {
      // there can be some leading whitespace
      return Color.fromHexString(str);
    } // color name


    var hex = Color.nameToHex[str];

    if (hex) {
      return Color.fromHexString(hex);
    } // rgb(a) notation


    if (str.indexOf('rgb') >= 0) {
      return Color.fromRgbaString(str);
    }

    throw new Error("Invalid color string: '" + str + "'");
  }; // Using separate RegExp for the short hex notation because strings like `#abcd`
  // are matched as ['#abcd', 'ab', 'c', 'd', undefined] when the `{1,2}` quantifier is used.


  Color.fromHexString = function (str) {
    var values = str.match(Color.hexRe);

    if (values) {
      var r = parseInt(values[1], 16);
      var g = parseInt(values[2], 16);
      var b = parseInt(values[3], 16);
      var a = values[4] !== undefined ? parseInt(values[4], 16) : 255;
      return new Color(r / 255, g / 255, b / 255, a / 255);
    }

    values = str.match(Color.shortHexRe);

    if (values) {
      var r = parseInt(values[1], 16);
      var g = parseInt(values[2], 16);
      var b = parseInt(values[3], 16);
      var a = values[4] !== undefined ? parseInt(values[4], 16) : 15;
      r += r * 16;
      g += g * 16;
      b += b * 16;
      a += a * 16;
      return new Color(r / 255, g / 255, b / 255, a / 255);
    }

    throw new Error("Malformed hexadecimal color string: '" + str + "'");
  };

  Color.fromRgbaString = function (str) {
    var values = str.match(Color.rgbRe);

    if (values) {
      return new Color(+values[1] / 255, +values[2] / 255, +values[3] / 255);
    }

    values = str.match(Color.rgbaRe);

    if (values) {
      return new Color(+values[1] / 255, +values[2] / 255, +values[3] / 255, +values[4]);
    }

    throw new Error("Malformed rgb/rgba color string: '" + str + "'");
  };

  Color.fromArray = function (arr) {
    if (arr.length === 4) {
      return new Color(arr[0], arr[1], arr[2], arr[3]);
    }

    if (arr.length === 3) {
      return new Color(arr[0], arr[1], arr[2]);
    }

    throw new Error('The given array should contain 3 or 4 color components (numbers).');
  };
  /**
   * Creates an instance of the Color class from the given HSB(A) components.
   * @param h Hue in the [0, 360) range.
   * @param s Saturation in the [0, 1] range.
   * @param b Brightness in the [0, 1] range.
   * @param alpha Opacity in the [0, 1] range. Defaults to 1 (completely opaque).
   */


  Color.fromHSB = function (h, s, b, alpha) {
    if (alpha === void 0) {
      alpha = 1;
    }

    var rgb = Color.HSBtoRGB(h, s, b);
    return new Color(rgb[0], rgb[1], rgb[2], alpha);
  };

  Color.padHex = function (str) {
    // Can't use `padStart(2, '0')` here because of IE.
    return str.length === 1 ? '0' + str : str;
  };

  Color.prototype.toHexString = function () {
    var hex = '#' + Color.padHex(Math.round(this.r * 255).toString(16)) + Color.padHex(Math.round(this.g * 255).toString(16)) + Color.padHex(Math.round(this.b * 255).toString(16));

    if (this.a < 1) {
      hex += Color.padHex(Math.round(this.a * 255).toString(16));
    }

    return hex;
  };

  Color.prototype.toRgbaString = function (fractionDigits) {
    if (fractionDigits === void 0) {
      fractionDigits = 3;
    }

    var components = [Math.round(this.r * 255), Math.round(this.g * 255), Math.round(this.b * 255)];
    var k = Math.pow(10, fractionDigits);

    if (this.a !== 1) {
      components.push(Math.round(this.a * k) / k);
      return "rgba(" + components.join(', ') + ")";
    }

    return "rgb(" + components.join(', ') + ")";
  };

  Color.prototype.toString = function () {
    if (this.a === 1) {
      return this.toHexString();
    }

    return this.toRgbaString();
  };

  Color.prototype.toHSB = function () {
    return Color.RGBtoHSB(this.r, this.g, this.b);
  };
  /**
   * Converts the given RGB triple to an array of HSB (HSV) components.
   * The hue component will be `NaN` for achromatic colors.
   */


  Color.RGBtoHSB = function (r, g, b) {
    var min = Math.min(r, g, b);
    var max = Math.max(r, g, b);
    var S = max !== 0 ? (max - min) / max : 0;
    var H = NaN; // min == max, means all components are the same
    // and the color is a shade of gray with no hue (H is NaN)

    if (min !== max) {
      var delta = max - min;
      var rc = (max - r) / delta;
      var gc = (max - g) / delta;
      var bc = (max - b) / delta;

      if (r === max) {
        H = bc - gc;
      } else if (g === max) {
        H = 2.0 + rc - bc;
      } else {
        H = 4.0 + gc - rc;
      }

      H /= 6.0;

      if (H < 0) {
        H = H + 1.0;
      }
    }

    return [H * 360, S, max];
  };
  /**
   * Converts the given HSB (HSV) triple to an array of RGB components.
   */


  Color.HSBtoRGB = function (H, S, B) {
    if (isNaN(H)) {
      H = 0;
    }

    H = (H % 360 + 360) % 360 / 360; // normalize hue to [0, 360] interval, then scale to [0, 1]

    var r = 0;
    var g = 0;
    var b = 0;

    if (S === 0) {
      r = g = b = B;
    } else {
      var h = (H - Math.floor(H)) * 6;
      var f = h - Math.floor(h);
      var p = B * (1 - S);
      var q = B * (1 - S * f);
      var t = B * (1 - S * (1 - f));

      switch (h >> 0) {
        // discard the floating point part of the number
        case 0:
          r = B;
          g = t;
          b = p;
          break;

        case 1:
          r = q;
          g = B;
          b = p;
          break;

        case 2:
          r = p;
          g = B;
          b = t;
          break;

        case 3:
          r = p;
          g = q;
          b = B;
          break;

        case 4:
          r = t;
          g = p;
          b = B;
          break;

        case 5:
          r = B;
          g = p;
          b = q;
          break;
      }
    }

    return [r, g, b];
  };

  Color.prototype.derive = function (hueShift, saturationFactor, brightnessFactor, opacityFactor) {
    var hsb = Color.RGBtoHSB(this.r, this.g, this.b);
    var b = hsb[2];

    if (b == 0 && brightnessFactor > 1.0) {
      b = 0.05;
    }

    var h = ((hsb[0] + hueShift) % 360 + 360) % 360;
    var s = Math.max(Math.min(hsb[1] * saturationFactor, 1.0), 0.0);
    b = Math.max(Math.min(b * brightnessFactor, 1.0), 0.0);
    var a = Math.max(Math.min(this.a * opacityFactor, 1.0), 0.0);
    var rgba = Color.HSBtoRGB(h, s, b);
    rgba.push(a);
    return Color.fromArray(rgba);
  };

  Color.prototype.brighter = function () {
    return this.derive(0, 1.0, 1.0 / 0.7, 1.0);
  };

  Color.prototype.darker = function () {
    return this.derive(0, 1.0, 0.7, 1.0);
  }; // See https://drafts.csswg.org/css-color/#hex-notation


  Color.hexRe = /\s*#([0-9a-fA-F]{2})([0-9a-fA-F]{2})([0-9a-fA-F]{2})([0-9a-fA-F]{2})?\s*$/;
  Color.shortHexRe = /\s*#([0-9a-fA-F])([0-9a-fA-F])([0-9a-fA-F])([0-9a-fA-F])?\s*$/;
  Color.rgbRe = /\s*rgb\((\d+),\s*(\d+),\s*(\d+)\)\s*/;
  Color.rgbaRe = /\s*rgba\((\d+),\s*(\d+),\s*(\d+),\s*([.\d]+)\)\s*/;
  /**
   * CSS Color Module Level 4:
   * https://drafts.csswg.org/css-color/#named-colors
   */

  Color.nameToHex = Object.freeze({
    aliceblue: '#F0F8FF',
    antiquewhite: '#FAEBD7',
    aqua: '#00FFFF',
    aquamarine: '#7FFFD4',
    azure: '#F0FFFF',
    beige: '#F5F5DC',
    bisque: '#FFE4C4',
    black: '#000000',
    blanchedalmond: '#FFEBCD',
    blue: '#0000FF',
    blueviolet: '#8A2BE2',
    brown: '#A52A2A',
    burlywood: '#DEB887',
    cadetblue: '#5F9EA0',
    chartreuse: '#7FFF00',
    chocolate: '#D2691E',
    coral: '#FF7F50',
    cornflowerblue: '#6495ED',
    cornsilk: '#FFF8DC',
    crimson: '#DC143C',
    cyan: '#00FFFF',
    darkblue: '#00008B',
    darkcyan: '#008B8B',
    darkgoldenrod: '#B8860B',
    darkgray: '#A9A9A9',
    darkgreen: '#006400',
    darkgrey: '#A9A9A9',
    darkkhaki: '#BDB76B',
    darkmagenta: '#8B008B',
    darkolivegreen: '#556B2F',
    darkorange: '#FF8C00',
    darkorchid: '#9932CC',
    darkred: '#8B0000',
    darksalmon: '#E9967A',
    darkseagreen: '#8FBC8F',
    darkslateblue: '#483D8B',
    darkslategray: '#2F4F4F',
    darkslategrey: '#2F4F4F',
    darkturquoise: '#00CED1',
    darkviolet: '#9400D3',
    deeppink: '#FF1493',
    deepskyblue: '#00BFFF',
    dimgray: '#696969',
    dimgrey: '#696969',
    dodgerblue: '#1E90FF',
    firebrick: '#B22222',
    floralwhite: '#FFFAF0',
    forestgreen: '#228B22',
    fuchsia: '#FF00FF',
    gainsboro: '#DCDCDC',
    ghostwhite: '#F8F8FF',
    gold: '#FFD700',
    goldenrod: '#DAA520',
    gray: '#808080',
    green: '#008000',
    greenyellow: '#ADFF2F',
    grey: '#808080',
    honeydew: '#F0FFF0',
    hotpink: '#FF69B4',
    indianred: '#CD5C5C',
    indigo: '#4B0082',
    ivory: '#FFFFF0',
    khaki: '#F0E68C',
    lavender: '#E6E6FA',
    lavenderblush: '#FFF0F5',
    lawngreen: '#7CFC00',
    lemonchiffon: '#FFFACD',
    lightblue: '#ADD8E6',
    lightcoral: '#F08080',
    lightcyan: '#E0FFFF',
    lightgoldenrodyellow: '#FAFAD2',
    lightgray: '#D3D3D3',
    lightgreen: '#90EE90',
    lightgrey: '#D3D3D3',
    lightpink: '#FFB6C1',
    lightsalmon: '#FFA07A',
    lightseagreen: '#20B2AA',
    lightskyblue: '#87CEFA',
    lightslategray: '#778899',
    lightslategrey: '#778899',
    lightsteelblue: '#B0C4DE',
    lightyellow: '#FFFFE0',
    lime: '#00FF00',
    limegreen: '#32CD32',
    linen: '#FAF0E6',
    magenta: '#FF00FF',
    maroon: '#800000',
    mediumaquamarine: '#66CDAA',
    mediumblue: '#0000CD',
    mediumorchid: '#BA55D3',
    mediumpurple: '#9370DB',
    mediumseagreen: '#3CB371',
    mediumslateblue: '#7B68EE',
    mediumspringgreen: '#00FA9A',
    mediumturquoise: '#48D1CC',
    mediumvioletred: '#C71585',
    midnightblue: '#191970',
    mintcream: '#F5FFFA',
    mistyrose: '#FFE4E1',
    moccasin: '#FFE4B5',
    navajowhite: '#FFDEAD',
    navy: '#000080',
    oldlace: '#FDF5E6',
    olive: '#808000',
    olivedrab: '#6B8E23',
    orange: '#FFA500',
    orangered: '#FF4500',
    orchid: '#DA70D6',
    palegoldenrod: '#EEE8AA',
    palegreen: '#98FB98',
    paleturquoise: '#AFEEEE',
    palevioletred: '#DB7093',
    papayawhip: '#FFEFD5',
    peachpuff: '#FFDAB9',
    peru: '#CD853F',
    pink: '#FFC0CB',
    plum: '#DDA0DD',
    powderblue: '#B0E0E6',
    purple: '#800080',
    rebeccapurple: '#663399',
    red: '#FF0000',
    rosybrown: '#BC8F8F',
    royalblue: '#4169E1',
    saddlebrown: '#8B4513',
    salmon: '#FA8072',
    sandybrown: '#F4A460',
    seagreen: '#2E8B57',
    seashell: '#FFF5EE',
    sienna: '#A0522D',
    silver: '#C0C0C0',
    skyblue: '#87CEEB',
    slateblue: '#6A5ACD',
    slategray: '#708090',
    slategrey: '#708090',
    snow: '#FFFAFA',
    springgreen: '#00FF7F',
    steelblue: '#4682B4',
    tan: '#D2B48C',
    teal: '#008080',
    thistle: '#D8BFD8',
    tomato: '#FF6347',
    turquoise: '#40E0D0',
    violet: '#EE82EE',
    wheat: '#F5DEB3',
    white: '#FFFFFF',
    whitesmoke: '#F5F5F5',
    yellow: '#FFFF00',
    yellowgreen: '#9ACD32'
  });
  return Color;
}();
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var Constants =
/** @class */
function () {
  function Constants() {}

  Constants.STEP_EVERYTHING = 0;
  Constants.STEP_FILTER = 1;
  Constants.STEP_SORT = 2;
  Constants.STEP_MAP = 3;
  Constants.STEP_AGGREGATE = 4;
  Constants.STEP_PIVOT = 5;
  Constants.ROW_BUFFER_SIZE = 10;
  Constants.LAYOUT_INTERVAL = 500;
  Constants.BATCH_WAIT_MILLIS = 50;
  Constants.EXPORT_TYPE_DRAG_COPY = 'dragCopy';
  Constants.EXPORT_TYPE_CLIPBOARD = 'clipboard';
  Constants.EXPORT_TYPE_EXCEL = 'excel';
  Constants.EXPORT_TYPE_CSV = 'csv';
  Constants.KEY_BACKSPACE = 8;
  Constants.KEY_TAB = 9;
  Constants.KEY_NEW_LINE = 10;
  Constants.KEY_ENTER = 13;
  Constants.KEY_SHIFT = 16;
  Constants.KEY_ESCAPE = 27;
  Constants.KEY_SPACE = 32;
  Constants.KEY_LEFT = 37;
  Constants.KEY_UP = 38;
  Constants.KEY_RIGHT = 39;
  Constants.KEY_DOWN = 40;
  Constants.KEY_DELETE = 46;
  Constants.KEY_A = 65;
  Constants.KEY_C = 67;
  Constants.KEY_V = 86;
  Constants.KEY_D = 68;
  Constants.KEY_Z = 90;
  Constants.KEY_Y = 89;
  Constants.KEY_F2 = 113;
  Constants.KEY_PAGE_UP = 33;
  Constants.KEY_PAGE_DOWN = 34;
  Constants.KEY_PAGE_HOME = 36;
  Constants.KEY_PAGE_END = 35;
  Constants.ROW_MODEL_TYPE_INFINITE = 'infinite';
  Constants.ROW_MODEL_TYPE_VIEWPORT = 'viewport';
  Constants.ROW_MODEL_TYPE_CLIENT_SIDE = 'clientSide';
  Constants.ROW_MODEL_TYPE_SERVER_SIDE = 'serverSide';
  Constants.DEPRECATED_ROW_MODEL_TYPE_NORMAL = 'normal';
  Constants.ALWAYS = 'always';
  Constants.ONLY_WHEN_GROUPING = 'onlyWhenGrouping';
  Constants.PINNED_TOP = 'top';
  Constants.PINNED_BOTTOM = 'bottom';
  Constants.DOM_LAYOUT_NORMAL = 'normal';
  Constants.DOM_LAYOUT_PRINT = 'print';
  Constants.DOM_LAYOUT_AUTO_HEIGHT = 'autoHeight';
  Constants.GROUP_AUTO_COLUMN_ID = 'ag-Grid-AutoColumn';
  Constants.SOURCE_PASTE = 'paste';
  Constants.PINNED_RIGHT = 'right';
  Constants.PINNED_LEFT = 'left';
  Constants.SORT_ASC = 'asc';
  Constants.SORT_DESC = 'desc';
  return Constants;
}();
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var FUNCTION_STRIP_COMMENTS = /((\/\/.*$)|(\/\*[\s\S]*?\*\/))/mg;
var FUNCTION_ARGUMENT_NAMES = /([^\s,]+)/g;
var AG_GRID_STOP_PROPAGATION = '__ag_Grid_Stop_Propagation';
/**
 * HTML Escapes.
 */

var HTML_ESCAPES = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#39;'
};
var reUnescapedHtml = /[&<>"']/g;

var Utils =
/** @class */
function () {
  function Utils() {}
  /**
   * If the key was passed before, then doesn't execute the func
   * @param {Function} func
   * @param {string} key
   */


  Utils.doOnce = function (func, key) {
    if (this.doOnceFlags[key]) {
      return;
    }

    func();
    this.doOnceFlags[key] = true;
  };

  Utils.getMaxSafeInteger = function () {
    // eslint-disable-next-line
    if (!Number.MAX_SAFE_INTEGER) {
      return 9007199254740991;
    } // eslint-disable-next-line


    return Number.MAX_SAFE_INTEGER;
  };
  /**
   * Checks if event was issued by a left click
   * from https://stackoverflow.com/questions/3944122/detect-left-mouse-button-press
   * @param {MouseEvent} mouseEvent
   * @returns {boolean}
   */


  Utils.isLeftClick = function (mouseEvent) {
    if ("buttons" in mouseEvent) {
      return mouseEvent.buttons == 1;
    }

    var button = mouseEvent.which || mouseEvent.button;
    return button == 1;
  };
  /**
   * `True` if the event is close to the original event by X pixels either vertically or horizontally.
   * we only start dragging after X pixels so this allows us to know if we should start dragging yet.
   * @param {MouseEvent | TouchEvent} e1
   * @param {MouseEvent | TouchEvent} e2
   * @param {number} pixelCount
   * @returns {boolean}
   */


  Utils.areEventsNear = function (e1, e2, pixelCount) {
    // by default, we wait 4 pixels before starting the drag
    if (pixelCount === 0) {
      return false;
    }

    var diffX = Math.abs(e1.clientX - e2.clientX);
    var diffY = Math.abs(e1.clientY - e2.clientY);
    return Math.max(diffX, diffY) <= pixelCount;
  };

  Utils.jsonEquals = function (val1, val2) {
    var val1Json = val1 ? JSON.stringify(val1) : null;
    var val2Json = val2 ? JSON.stringify(val2) : null;
    return val1Json === val2Json;
  };

  Utils.shallowCompare = function (arr1, arr2) {
    // if both are missing, then they are the same
    if (this.missing(arr1) && this.missing(arr2)) {
      return true;
    } // if one is present, but other is missing, then they are different


    if (this.missing(arr1) || this.missing(arr2)) {
      return false;
    }

    if (arr1.length !== arr2.length) {
      return false;
    }

    for (var i = 0; i < arr1.length; i++) {
      if (arr1[i] !== arr2[i]) {
        return false;
      }
    }

    return true;
  };

  Utils.getNameOfClass = function (theClass) {
    var funcNameRegex = /function (.{1,})\(/;
    var funcAsString = theClass.toString();
    var results = funcNameRegex.exec(funcAsString);
    return results && results.length > 1 ? results[1] : "";
  };

  Utils.getValueUsingField = function (data, field, fieldContainsDots) {
    if (!field || !data) {
      return;
    } // if no '.', then it's not a deep value


    if (!fieldContainsDots) {
      return data[field];
    } else {
      // otherwise it is a deep value, so need to dig for it
      var fields = field.split('.');
      var currentObject = data;

      for (var i = 0; i < fields.length; i++) {
        currentObject = currentObject[fields[i]];

        if (this.missing(currentObject)) {
          return null;
        }
      }

      return currentObject;
    }
  };

  Utils.getElementSize = function (el) {
    var _a = window.getComputedStyle(el),
        height = _a.height,
        width = _a.width,
        paddingTop = _a.paddingTop,
        paddingRight = _a.paddingRight,
        paddingBottom = _a.paddingBottom,
        paddingLeft = _a.paddingLeft,
        marginTop = _a.marginTop,
        marginRight = _a.marginRight,
        marginBottom = _a.marginBottom,
        marginLeft = _a.marginLeft,
        boxSizing = _a.boxSizing;

    return {
      height: parseFloat(height),
      width: parseFloat(width),
      paddingTop: parseFloat(paddingTop),
      paddingRight: parseFloat(paddingRight),
      paddingBottom: parseFloat(paddingBottom),
      paddingLeft: parseFloat(paddingLeft),
      marginTop: parseFloat(marginTop),
      marginRight: parseFloat(marginRight),
      marginBottom: parseFloat(marginBottom),
      marginLeft: parseFloat(marginLeft),
      boxSizing: boxSizing
    };
  };

  Utils.getInnerHeight = function (el) {
    var size = this.getElementSize(el);

    if (size.boxSizing === 'border-box') {
      return size.height - size.paddingTop - size.paddingBottom;
    }

    return size.height;
  };

  Utils.getInnerWidth = function (el) {
    var size = this.getElementSize(el);

    if (size.boxSizing === 'border-box') {
      return size.width - size.paddingLeft - size.paddingRight;
    }

    return size.width;
  };

  Utils.getAbsoluteHeight = function (el) {
    var size = this.getElementSize(el);
    var marginRight = size.marginBottom + size.marginTop;
    return Math.ceil(el.offsetHeight + marginRight);
  };

  Utils.getAbsoluteWidth = function (el) {
    var size = this.getElementSize(el);
    var marginWidth = size.marginLeft + size.marginRight;
    return Math.ceil(el.offsetWidth + marginWidth);
  };

  Utils.getScrollLeft = function (element, rtl) {
    var scrollLeft = element.scrollLeft;

    if (rtl) {
      // Absolute value - for FF that reports RTL scrolls in negative numbers
      scrollLeft = Math.abs(scrollLeft); // Get Chrome to return the same value as well

      if (this.isBrowserChrome()) {
        scrollLeft = element.scrollWidth - element.clientWidth - scrollLeft;
      }
    }

    return scrollLeft;
  };

  Utils.cleanNumber = function (value) {
    if (typeof value === 'string') {
      value = parseInt(value, 10);
    }

    if (typeof value === 'number') {
      value = Math.floor(value);
    } else {
      value = null;
    }

    return value;
  };

  Utils.setScrollLeft = function (element, value, rtl) {
    if (rtl) {
      // Chrome and Safari when doing RTL have the END position of the scroll as zero, not the start
      if (this.isBrowserSafari() || this.isBrowserChrome()) {
        value = element.scrollWidth - element.clientWidth - value;
      } // Firefox uses negative numbers when doing RTL scrolling


      if (this.isBrowserFirefox()) {
        value *= -1;
      }
    }

    element.scrollLeft = value;
  };

  Utils.iterateNamedNodeMap = function (map, callback) {
    if (!map) {
      return;
    }

    for (var i = 0; i < map.length; i++) {
      var attr = map[i];
      callback(attr.name, attr.value);
    }
  };

  Utils.iterateObject = function (object, callback) {
    if (!object || this.missing(object)) {
      return;
    }

    if (Array.isArray(object)) {
      object.forEach(function (value, index) {
        return callback("" + index, value);
      });
    } else {
      Object.keys(object).forEach(function (key) {
        return callback(key, object[key]);
      });
    }
  };

  Utils.cloneObject = function (object) {
    var copy = {};
    var keys = Object.keys(object);

    for (var i = 0; i < keys.length; i++) {
      var key = keys[i];
      var value = object[key];
      copy[key] = value;
    }

    return copy;
  };
  /**
   * Will copy the specified properties from `source` into the equivalent properties on `target`, ignoring properties with
   * a value of `undefined`.
   */


  Utils.copyPropertiesIfPresent = function (source, target) {
    var _this = this;

    var properties = [];

    for (var _i = 2; _i < arguments.length; _i++) {
      properties[_i - 2] = arguments[_i];
    }

    properties.forEach(function (p) {
      return _this.copyPropertyIfPresent(source, target, p);
    });
  };
  /**
   * Will copy the specified property from `source` into the equivalent property on `target`, unless the property has a
   * value of `undefined`. If a transformation is provided, it will be applied to the value before being set on `target`.
   */


  Utils.copyPropertyIfPresent = function (source, target, property, transform) {
    var value = this.getProperty(source, property);

    if (value !== undefined) {
      this.setProperty(target, property, transform ? transform(value) : value);
    }
  };

  Utils.getAllKeysInObjects = function (objects) {
    var allValues = {};
    objects.forEach(function (obj) {
      if (obj) {
        Object.keys(obj).forEach(function (key) {
          return allValues[key] = null;
        });
      }
    });
    return Object.keys(allValues);
  };

  Utils.mergeDeep = function (dest, source, copyUndefined) {
    if (copyUndefined === void 0) {
      copyUndefined = true;
    }

    if (!this.exists(source)) {
      return;
    }

    this.iterateObject(source, function (key, newValue) {
      var oldValue = dest[key];

      if (oldValue === newValue) {
        return;
      }

      if (typeof oldValue === 'object' && typeof newValue === 'object' && !Array.isArray(oldValue)) {
        Utils.mergeDeep(oldValue, newValue);
      } else if (copyUndefined || newValue !== undefined) {
        dest[key] = newValue;
      }
    });
  };

  Utils.assign = function (object) {
    var _this = this;

    var sources = [];

    for (var _i = 1; _i < arguments.length; _i++) {
      sources[_i - 1] = arguments[_i];
    }

    sources.forEach(function (source) {
      if (_this.exists(source)) {
        _this.iterateObject(source, function (key, value) {
          object[key] = value;
        });
      }
    });
    return object;
  };

  Utils.flatten = function (arrayOfArrays) {
    return [].concat.apply([], arrayOfArrays);
  };

  Utils.parseYyyyMmDdToDate = function (yyyyMmDd, separator) {
    try {
      if (!yyyyMmDd) {
        return null;
      }

      if (yyyyMmDd.indexOf(separator) === -1) {
        return null;
      }

      var fields = yyyyMmDd.split(separator);

      if (fields.length != 3) {
        return null;
      }

      return new Date(Number(fields[0]), Number(fields[1]) - 1, Number(fields[2]));
    } catch (e) {
      return null;
    }
  };

  Utils.serializeDateToYyyyMmDd = function (date, separator) {
    if (!date) {
      return null;
    }

    return date.getFullYear() + separator + this.padStart(date.getMonth() + 1, 2) + separator + this.padStart(date.getDate(), 2);
  };

  Utils.padStart = function (num, totalStringSize) {
    var asString = num + "";

    while (asString.length < totalStringSize) {
      asString = "0" + asString;
    }

    return asString;
  };

  Utils.pushAll = function (target, source) {
    if (this.missing(source) || this.missing(target)) {
      return;
    }

    source.forEach(function (func) {
      return target.push(func);
    });
  };

  Utils.createArrayOfNumbers = function (first, last) {
    var result = [];

    for (var i = first; i <= last; i++) {
      result.push(i);
    }

    return result;
  };

  Utils.getFunctionParameters = function (func) {
    var fnStr = func.toString().replace(FUNCTION_STRIP_COMMENTS, '');
    var result = fnStr.slice(fnStr.indexOf('(') + 1, fnStr.indexOf(')')).match(FUNCTION_ARGUMENT_NAMES);

    if (result === null) {
      return [];
    } else {
      return result;
    }
  };

  Utils.find = function (collection, predicate, value) {
    if (collection === null || collection === undefined) {
      return null;
    }

    if (!Array.isArray(collection)) {
      var objToArray = this.values(collection);
      return this.find(objToArray, predicate, value);
    }

    var collectionAsArray = collection;
    var firstMatchingItem = null;

    for (var i = 0; i < collectionAsArray.length; i++) {
      var item = collectionAsArray[i];

      if (typeof predicate === 'string') {
        if (item[predicate] === value) {
          firstMatchingItem = item;
          break;
        }
      } else {
        var callback = predicate;

        if (callback(item)) {
          firstMatchingItem = item;
          break;
        }
      }
    }

    return firstMatchingItem;
  };

  Utils.toStrings = function (array) {
    return array.map(function (item) {
      if (item === undefined || item === null || !item.toString) {
        return null;
      } else {
        return item.toString();
      }
    });
  };

  Utils.findIndex = function (collection, predicate) {
    for (var i = 0; i < collection.length; i++) {
      if (predicate(collection[i], i, collection)) {
        return i;
      }
    }

    return -1;
  };
  /**
   * Returns true if it is a DOM node
   * taken from: http://stackoverflow.com/questions/384286/javascript-isdom-how-do-you-check-if-a-javascript-object-is-a-dom-object
   * @param {any} o
   * @return {boolean}
   */


  Utils.isNode = function (o) {
    return typeof Node === "function" ? o instanceof Node : o && typeof o === "object" && typeof o.nodeType === "number" && typeof o.nodeName === "string";
  }; //

  /**
   * Returns true if it is a DOM element
   * taken from: http://stackoverflow.com/questions/384286/javascript-isdom-how-do-you-check-if-a-javascript-object-is-a-dom-object
   * @param {any} o
   * @returns {boolean}
   */


  Utils.isElement = function (o) {
    return typeof HTMLElement === "function" ? o instanceof HTMLElement : //DOM2
    o && typeof o === "object" && o !== null && o.nodeType === 1 && typeof o.nodeName === "string";
  };

  Utils.isNodeOrElement = function (o) {
    return this.isNode(o) || this.isElement(o);
  };
  /**
   * Makes a copy of a node list into a list
   * @param {NodeList} nodeList
   * @returns {Node[]}
   */


  Utils.copyNodeList = function (nodeList) {
    var childCount = nodeList ? nodeList.length : 0;
    var res = [];

    for (var i = 0; i < childCount; i++) {
      res.push(nodeList[i]);
    }

    return res;
  };

  Utils.isEventFromPrintableCharacter = function (event) {
    var pressedChar = String.fromCharCode(event.charCode); // newline is an exception, as it counts as a printable character, but we don't
    // want to start editing when it is pressed. without this check, if user is in chrome
    // and editing a cell, and they press ctrl+enter, the cell stops editing, and then
    // starts editing again with a blank value (two 'key down' events are fired). to
    // test this, remove the line below, edit a cell in chrome and hit ctrl+enter while editing.
    // https://ag-grid.atlassian.net/browse/AG-605

    if (this.isKeyPressed(event, Constants.KEY_NEW_LINE)) {
      return false;
    } // no allowed printable chars have alt or ctrl key combinations


    if (event.altKey || event.ctrlKey) {
      return false;
    }

    if (_.exists(event.key)) {
      // modern browser will implement key, so we return if key is length 1, eg if it is 'a' for the
      // a key, or '2' for the '2' key. non-printable characters have names, eg 'Enter' or 'Backspace'.
      var printableCharacter = event.key.length === 1; // IE11 & Edge treat the numpad del key differently - with numlock on we get "Del" for key,
      // so this addition checks if its IE11/Edge and handles that specific case the same was as all other browsers

      var numpadDelWithNumlockOnForEdgeOrIe = Utils.isNumpadDelWithNumlockOnForEdgeOrIe(event);
      return printableCharacter || numpadDelWithNumlockOnForEdgeOrIe;
    } else {
      // otherwise, for older browsers, we test against a list of characters, which doesn't include
      // accents for non-English, but don't care much, as most users are on modern browsers
      return Utils.PRINTABLE_CHARACTERS.indexOf(pressedChar) >= 0;
    }
  };
  /**
   * Allows user to tell the grid to skip specific keyboard events
   * @param {GridOptionsWrapper} gridOptionsWrapper
   * @param {KeyboardEvent} keyboardEvent
   * @param {RowNode} rowNode
   * @param {Column} column
   * @param {boolean} editing
   * @returns {boolean}
   */


  Utils.isUserSuppressingKeyboardEvent = function (gridOptionsWrapper, keyboardEvent, rowNode, column, editing) {
    var gridOptionsFunc = gridOptionsWrapper.getSuppressKeyboardEventFunc();
    var colDefFunc = column.getColDef().suppressKeyboardEvent; // if no callbacks provided by user, then do nothing

    if (!gridOptionsFunc && !colDefFunc) {
      return false;
    }

    var params = {
      event: keyboardEvent,
      editing: editing,
      column: column,
      api: gridOptionsWrapper.getApi(),
      node: rowNode,
      data: rowNode.data,
      colDef: column.getColDef(),
      context: gridOptionsWrapper.getContext(),
      columnApi: gridOptionsWrapper.getColumnApi()
    }; // colDef get first preference on suppressing events

    if (colDefFunc) {
      var colDefFuncResult = colDefFunc(params); // if colDef func suppressed, then return now, no need to call gridOption func

      if (colDefFuncResult) {
        return true;
      }
    }

    if (gridOptionsFunc) {
      // if gridOption func, return the result
      return gridOptionsFunc(params);
    } else {
      // otherwise return false, don't suppress, as colDef didn't suppress and no func on gridOptions
      return false;
    }
  };

  Utils.getCellCompForEvent = function (gridOptionsWrapper, event) {
    var sourceElement = this.getTarget(event);

    while (sourceElement) {
      var renderedCell = gridOptionsWrapper.getDomData(sourceElement, 'cellComp');

      if (renderedCell) {
        return renderedCell;
      }

      sourceElement = sourceElement.parentElement;
    }

    return null;
  };
  /**
   * Adds all type of change listeners to an element, intended to be a text field
   * @param {HTMLElement} element
   * @param {EventListener} listener
   */


  Utils.addChangeListener = function (element, listener) {
    element.addEventListener("changed", listener);
    element.addEventListener("paste", listener);
    element.addEventListener("input", listener); // IE doesn't fire changed for special keys (eg delete, backspace), so need to
    // listen for this further ones

    element.addEventListener("keydown", listener);
    element.addEventListener("keyup", listener);
  };
  /**
   * If value is undefined, null or blank, returns null, otherwise returns the value
   * @param {T} value
   * @returns {T | null}
   */


  Utils.makeNull = function (value) {
    var valueNoType = value;

    if (value === null || value === undefined || valueNoType === "") {
      return null;
    }

    return value;
  };

  Utils.missing = function (value) {
    return !this.exists(value);
  };

  Utils.missingOrEmpty = function (value) {
    return !value || this.missing(value) || value.length === 0;
  };

  Utils.missingOrEmptyObject = function (value) {
    return this.missing(value) || Object.keys(value).length === 0;
  };

  Utils.exists = function (value, allowEmptyString) {
    if (allowEmptyString === void 0) {
      allowEmptyString = false;
    }

    return value != null && (value !== '' || allowEmptyString);
  };

  Utils.firstExistingValue = function () {
    var values = [];

    for (var _i = 0; _i < arguments.length; _i++) {
      values[_i] = arguments[_i];
    }

    for (var i = 0; i < values.length; i++) {
      var value = values[i];

      if (_.exists(value)) {
        return value;
      }
    }

    return null;
  };

  Utils.anyExists = function (values) {
    if (values) {
      for (var i = 0; i < values.length; i++) {
        if (this.exists(values[i])) {
          return true;
        }
      }
    }

    return false;
  };

  Utils.existsAndNotEmpty = function (value) {
    return value != null && this.exists(value) && value.length > 0;
  };

  Utils.clearElement = function (el) {
    while (el && el.firstChild) {
      el.removeChild(el.firstChild);
    }
  };

  Utils.removeElement = function (parent, cssSelector) {
    this.removeFromParent(parent.querySelector(cssSelector));
  };

  Utils.removeFromParent = function (node) {
    if (node && node.parentNode) {
      node.parentNode.removeChild(node);
    }
  };

  Utils.isVisible = function (element) {
    return element.offsetParent !== null;
  };

  Utils.callIfPresent = function (func) {
    if (func) {
      func();
    }
  };
  /**
   * Loads the template and returns it as an element. makes up for no simple way in
   * the dom api to load html directly, eg we cannot do this: document.createElement(template)
   * @param {string} template
   * @returns {HTMLElement}
   */


  Utils.loadTemplate = function (template) {
    var tempDiv = document.createElement("div");
    tempDiv.innerHTML = template;
    return tempDiv.firstChild;
  };

  Utils.appendHtml = function (eContainer, htmlTemplate) {
    if (eContainer.lastChild) {
      // https://developer.mozilla.org/en-US/docs/Web/API/Element/insertAdjacentHTML
      // we put the items at the start, so new items appear underneath old items,
      // so when expanding/collapsing groups, the new rows don't go on top of the
      // rows below that are moving our of the way
      eContainer.insertAdjacentHTML('afterbegin', htmlTemplate);
    } else {
      eContainer.innerHTML = htmlTemplate;
    }
  };

  Utils.addOrRemoveCssClass = function (element, className, addOrRemove) {
    if (addOrRemove) {
      this.addCssClass(element, className);
    } else {
      this.removeCssClass(element, className);
    }
  };
  /**
   * This method adds a class to an element and remove that class from all siblings.
   * Useful for toggling state.
   * @param {HTMLElement} element The element to receive the class
   * @param {string} className The class to be assigned to the element
   * @param {boolean} [inverted] This inverts the effect, adding the class to all siblings and
   *        removing from the relevant element (useful when adding a class to hide non-selected elements).
   */


  Utils.radioCssClass = function (element, className, inverted) {
    var parent = element.parentElement;
    var sibling = parent.firstChild;

    while (sibling) {
      _.addOrRemoveCssClass(sibling, className, inverted ? sibling !== element : sibling === element);

      sibling = sibling.nextSibling;
    }
  };

  Utils.addCssClass = function (element, className) {
    var _this = this;

    if (!className || className.length === 0) {
      return;
    }

    if (className.indexOf(' ') >= 0) {
      className.split(' ').forEach(function (value) {
        return _this.addCssClass(element, value);
      });
      return;
    }

    if (element.classList) {
      element.classList.add(className);
    } else if (element.className && element.className.length > 0) {
      var cssClasses = element.className.split(' ');

      if (cssClasses.indexOf(className) < 0) {
        cssClasses.push(className);
        element.setAttribute('class', cssClasses.join(' '));
      }
    } else {
      // do not use element.classList = className here, it will cause
      // a read-only assignment error on some browsers (IE/Edge).
      element.setAttribute('class', className);
    }
  };

  Utils.removeCssClass = function (element, className) {
    if (element.classList) {
      element.classList.remove(className);
    } else if (element.className && element.className.length > 0) {
      var newClassName = element.className.split(' ').filter(function (c) {
        return c !== className;
      }).join(' ');
      element.setAttribute('class', newClassName);
    }
  };

  Utils.containsClass = function (element, className) {
    if (element.classList) {
      // for modern browsers
      return element.classList.contains(className);
    } else if (element.className) {
      // for older browsers, check against the string of class names
      // if only one class, can check for exact match
      var onlyClass = element.className === className; // if many classes, check for class name, we have to pad with ' ' to stop other
      // class names that are a substring of this class

      var contains = element.className.indexOf(' ' + className + ' ') >= 0; // the padding above then breaks when it's the first or last class names

      var startsWithClass = element.className.indexOf(className + ' ') === 0;
      var endsWithClass = element.className.lastIndexOf(' ' + className) === element.className.length - className.length - 1;
      return onlyClass || contains || startsWithClass || endsWithClass;
    } else {
      // if item is not a node
      return false;
    }
  };

  Utils.getElementAttribute = function (element, attributeName) {
    if (element.attributes && element.attributes[attributeName]) {
      var attribute = element.attributes[attributeName];
      return attribute.value;
    } else {
      return null;
    }
  };

  Utils.offsetHeight = function (element) {
    return element && element.clientHeight ? element.clientHeight : 0;
  };

  Utils.offsetWidth = function (element) {
    return element && element.clientWidth ? element.clientWidth : 0;
  };

  Utils.sortNumerically = function (array) {
    return array.sort(function (a, b) {
      return a - b;
    });
  };

  Utils.removeRepeatsFromArray = function (array, object) {
    if (!array) {
      return;
    }

    for (var index = array.length - 2; index >= 0; index--) {
      var thisOneMatches = array[index] === object;
      var nextOneMatches = array[index + 1] === object;

      if (thisOneMatches && nextOneMatches) {
        array.splice(index + 1, 1);
      }
    }
  };

  Utils.removeFromArray = function (array, object) {
    var index = array.indexOf(object);

    if (index >= 0) {
      array.splice(index, 1);
    }
  };

  Utils.removeAllFromArray = function (array, toRemove) {
    var _this = this;

    toRemove.forEach(function (item) {
      return _this.removeFromArray(array, item);
    });
  };

  Utils.insertIntoArray = function (array, object, toIndex) {
    array.splice(toIndex, 0, object);
  };

  Utils.insertArrayIntoArray = function (dest, src, toIndex) {
    if (this.missing(dest) || this.missing(src)) {
      return;
    } // put items in backwards, otherwise inserted items end up in reverse order


    for (var i = src.length - 1; i >= 0; i--) {
      var item = src[i];
      this.insertIntoArray(dest, item, toIndex);
    }
  };

  Utils.moveInArray = function (array, objectsToMove, toIndex) {
    var _this = this; // first take out it items from the array


    objectsToMove.forEach(function (obj) {
      _this.removeFromArray(array, obj);
    }); // now add the objects, in same order as provided to us, that means we start at the end
    // as the objects will be pushed to the right as they are inserted

    objectsToMove.slice().reverse().forEach(function (obj) {
      _this.insertIntoArray(array, obj, toIndex);
    });
  };

  Utils.defaultComparator = function (valueA, valueB, accentedCompare) {
    if (accentedCompare === void 0) {
      accentedCompare = false;
    }

    var valueAMissing = valueA === null || valueA === undefined;
    var valueBMissing = valueB === null || valueB === undefined; // this is for aggregations sum and avg, where the result can be a number that is wrapped.
    // if we didn't do this, then the toString() value would be used, which would result in
    // the strings getting used instead of the numbers.

    if (valueA && valueA.toNumber) {
      valueA = valueA.toNumber();
    }

    if (valueB && valueB.toNumber) {
      valueB = valueB.toNumber();
    }

    if (valueAMissing && valueBMissing) {
      return 0;
    }

    if (valueAMissing) {
      return -1;
    }

    if (valueBMissing) {
      return 1;
    }

    if (typeof valueA === "string") {
      if (!accentedCompare) {
        return doQuickCompare(valueA, valueB);
      } else {
        try {
          // using local compare also allows chinese comparisons
          return valueA.localeCompare(valueB);
        } catch (e) {
          // if something wrong with localeCompare, eg not supported
          // by browser, then just continue with the quick one
          return doQuickCompare(valueA, valueB);
        }
      }
    }

    if (valueA < valueB) {
      return -1;
    } else if (valueA > valueB) {
      return 1;
    } else {
      return 0;
    }

    function doQuickCompare(a, b) {
      return a > b ? 1 : a < b ? -1 : 0;
    }
  };

  Utils.last = function (arr) {
    if (!arr || !arr.length) {
      return undefined;
    }

    return arr[arr.length - 1];
  };

  Utils.compareArrays = function (array1, array2) {
    if (this.missing(array1) && this.missing(array2)) {
      return true;
    }

    if (this.missing(array1) || this.missing(array2) || !array1 || !array2) {
      return false;
    }

    if (array1.length !== array2.length) {
      return false;
    }

    for (var i = 0; i < array1.length; i++) {
      if (array1[i] !== array2[i]) {
        return false;
      }
    }

    return true;
  };

  Utils.ensureDomOrder = function (eContainer, eChild, eChildBefore) {
    // if already in right order, do nothing
    if (eChildBefore && eChildBefore.nextSibling === eChild) {
      return;
    }

    if (eChildBefore) {
      if (eChildBefore.nextSibling) {
        // insert between the eRowBefore and the row after it
        eContainer.insertBefore(eChild, eChildBefore.nextSibling);
      } else {
        // if nextSibling is missing, means other row is at end, so just append new row at the end
        eContainer.appendChild(eChild);
      }
    } else {
      // otherwise put at start
      if (eContainer.firstChild && eContainer.firstChild !== eChild) {
        // insert it at the first location
        eContainer.insertAdjacentElement('afterbegin', eChild);
      }
    }
  };

  Utils.setDomChildOrder = function (eContainer, orderedChildren) {
    for (var i = 0; i < orderedChildren.length; i++) {
      var correctCellAtIndex = orderedChildren[i];
      var actualCellAtIndex = eContainer.children[i];

      if (actualCellAtIndex !== correctCellAtIndex) {
        eContainer.insertBefore(correctCellAtIndex, actualCellAtIndex);
      }
    }
  };

  Utils.insertTemplateWithDomOrder = function (eContainer, htmlTemplate, eChildBefore) {
    var res;

    if (eChildBefore) {
      // if previous element exists, just slot in after the previous element
      eChildBefore.insertAdjacentHTML('afterend', htmlTemplate);
      res = eChildBefore.nextSibling;
    } else {
      if (eContainer.firstChild) {
        // insert it at the first location
        eContainer.insertAdjacentHTML('afterbegin', htmlTemplate);
      } else {
        // otherwise eContainer is empty, so just append it
        eContainer.innerHTML = htmlTemplate;
      }

      res = eContainer.firstChild;
    }

    return res;
  };

  Utils.every = function (items, callback) {
    if (!items || items.length === 0) {
      return true;
    }

    for (var i = 0; i < items.length; i++) {
      if (!callback(items[i])) {
        return false;
      }
    }

    return true;
  };

  Utils.toStringOrNull = function (value) {
    if (this.exists(value) && value.toString) {
      return value.toString();
    } else {
      return null;
    }
  };

  Utils.formatSize = function (size) {
    if (typeof size === "number") {
      return size + "px";
    } else {
      return size;
    }
  };

  Utils.formatNumberTwoDecimalPlacesAndCommas = function (value) {
    if (typeof value !== 'number') {
      return '';
    } // took this from: http://blog.tompawlak.org/number-currency-formatting-javascript


    return (Math.round(value * 100) / 100).toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1,");
  };

  Utils.findLineByLeastSquares = function (values) {
    var len = values.length;

    if (len <= 1) {
      return values;
    }

    var sum_x = 0;
    var sum_y = 0;
    var sum_xy = 0;
    var sum_xx = 0;
    var y = 0;

    for (var x = 0; x < len; x++) {
      y = values[x];
      sum_x += x;
      sum_y += y;
      sum_xx += x * x;
      sum_xy += x * y;
    }

    var m = (len * sum_xy - sum_x * sum_y) / (len * sum_xx - sum_x * sum_x);
    var b = sum_y / len - m * sum_x / len;
    var result = [];

    for (var x = 0; x <= len; x++) {
      result.push(x * m + b);
    }

    return result;
  };
  /**
   * the native method number.toLocaleString(undefined, {minimumFractionDigits: 0})
   * puts in decimal places in IE, so we use this method instead
   * from: http://blog.tompawlak.org/number-currency-formatting-javascript
   * @param {number} value
   * @returns {string}
   */


  Utils.formatNumberCommas = function (value) {
    if (typeof value !== 'number') {
      return '';
    }

    return value.toString().replace(/(\d)(?=(\d{3})+(?!\d))/g, "$1,");
  };

  Utils.prependDC = function (parent, documentFragment) {
    if (this.exists(parent.firstChild)) {
      parent.insertBefore(documentFragment, parent.firstChild);
    } else {
      parent.appendChild(documentFragment);
    }
  };
  /**
   * If icon provided, use this (either a string, or a function callback).
   * if not, then use the default icon from the theme
   * @param {string} iconName
   * @param {GridOptionsWrapper} gridOptionsWrapper
   * @param {Column | null} [column]
   * @returns {HTMLElement}
   */


  Utils.createIcon = function (iconName, gridOptionsWrapper, column) {
    var iconContents = this.createIconNoSpan(iconName, gridOptionsWrapper, column);

    if (iconContents.className.indexOf('ag-icon') > -1) {
      return iconContents;
    } else {
      var eResult = document.createElement('span');
      eResult.appendChild(iconContents);
      return eResult;
    }
  };

  Utils.createIconNoSpan = function (iconName, gridOptionsWrapper, column, forceCreate) {
    var userProvidedIcon = null; // check col for icon first

    var icons = column && column.getColDef().icons;

    if (icons) {
      userProvidedIcon = icons[iconName];
    } // if not in col, try grid options


    if (gridOptionsWrapper && !userProvidedIcon) {
      var optionsIcons = gridOptionsWrapper.getIcons();

      if (optionsIcons) {
        userProvidedIcon = optionsIcons[iconName];
      }
    } // now if user provided, use it


    if (userProvidedIcon) {
      var rendererResult = void 0;

      if (typeof userProvidedIcon === 'function') {
        rendererResult = userProvidedIcon();
      } else if (typeof userProvidedIcon === 'string') {
        rendererResult = userProvidedIcon;
      } else {
        throw new Error('icon from grid options needs to be a string or a function');
      }

      if (typeof rendererResult === 'string') {
        return this.loadTemplate(rendererResult);
      } else if (this.isNodeOrElement(rendererResult)) {
        return rendererResult;
      } else {
        console.warn('ag-Grid: iconRenderer should return back a string or a dom object');
      }
    } else {
      var span = document.createElement('span');
      var cssClass = this.iconNameClassMap[iconName];

      if (!cssClass) {
        if (!forceCreate) {
          console.warn("ag-Grid: Did not find icon " + iconName);
          cssClass = '';
        } else {
          cssClass = iconName;
        }
      }

      span.setAttribute("class", "ag-icon ag-icon-" + cssClass);
      span.setAttribute("unselectable", "on");
      return span;
    }
  };

  Utils.addStylesToElement = function (eElement, styles) {
    var _this = this;

    if (!styles) {
      return;
    }

    Object.keys(styles).forEach(function (key) {
      var keyCamelCase = _this.hyphenToCamelCase(key);

      if (keyCamelCase) {
        eElement.style[keyCamelCase] = styles[key];
      }
    });
  };

  Utils.isHorizontalScrollShowing = function (element) {
    return element.clientWidth < element.scrollWidth;
  };

  Utils.isVerticalScrollShowing = function (element) {
    return element.clientHeight < element.scrollHeight;
  };

  Utils.getMaxDivHeight = function () {
    if (!document.body) {
      return -1;
    }

    var res = 1000000; // FF reports the height back but still renders blank after ~6M px

    var testUpTo = navigator.userAgent.toLowerCase().match(/firefox/) ? 6000000 : 1000000000;
    var div = this.loadTemplate("<div/>");
    document.body.appendChild(div);

    while (true) {
      var test = res * 2;
      div.style.height = test + 'px';

      if (test > testUpTo || div.clientHeight !== test) {
        break;
      } else {
        res = test;
      }
    }

    document.body.removeChild(div);
    return res;
  };

  Utils.getScrollbarWidth = function () {
    var body = document.body;
    var div = document.createElement("div");
    div.style.width = div.style.height = "100px";
    div.style.opacity = "0";
    div.style.overflow = "scroll";
    div.style.msOverflowStyle = "scrollbar"; // needed for WinJS apps

    div.style.position = "absolute";
    body.appendChild(div);
    var width = div.offsetWidth - div.clientWidth; // remove divs

    if (div.parentNode) {
      div.parentNode.removeChild(div);
    }

    return width;
  };

  Utils.hasOverflowScrolling = function () {
    var prefixes = ['webkit', 'moz', 'o', 'ms'];
    var div = document.createElement('div');
    var body = document.getElementsByTagName('body')[0];
    var found = false;
    var p;
    body.appendChild(div);
    div.setAttribute('style', prefixes.map(function (prefix) {
      return "-" + prefix + "-overflow-scrolling: touch";
    }).concat('overflow-scrolling: touch').join(';'));
    var computedStyle = window.getComputedStyle(div);

    if (computedStyle.overflowScrolling === 'touch') {
      found = true;
    }

    if (!found) {
      for (var _i = 0, prefixes_1 = prefixes; _i < prefixes_1.length; _i++) {
        p = prefixes_1[_i];

        if (computedStyle[p + "OverflowScrolling"] === 'touch') {
          found = true;
          break;
        }
      }
    }

    if (div.parentNode) {
      div.parentNode.removeChild(div);
    }

    return found;
  };

  Utils.isKeyPressed = function (event, keyToCheck) {
    var pressedKey = event.which || event.keyCode;
    return pressedKey === keyToCheck;
  };

  Utils.isCharacterKey = function (event) {
    // from: https://stackoverflow.com/questions/4179708/how-to-detect-if-the-pressed-key-will-produce-a-character-inside-an-input-text
    var which = event.which;

    if (typeof which === 'number' && which) {
      return !event.ctrlKey && !event.metaKey && !event.altKey && event.which !== 8 && event.which !== 16;
    }

    return which === undefined;
  };

  Utils.setDisplayed = function (element, displayed) {
    this.addOrRemoveCssClass(element, 'ag-hidden', !displayed);
  };

  Utils.setVisible = function (element, visible) {
    this.addOrRemoveCssClass(element, 'ag-invisible', !visible);
  };

  Utils.setElementWidth = function (element, width) {
    if (width === 'flex') {
      element.style.width = null;
      element.style.minWidth = null;
      element.style.maxWidth = null;
      element.style.flex = '1 1 auto';
    } else {
      this.setFixedWidth(element, width);
    }
  };

  Utils.setFixedWidth = function (element, width) {
    width = this.formatSize(width);
    element.style.width = width;
    element.style.maxWidth = width;
    element.style.minWidth = width;
  };

  Utils.setElementHeight = function (element, height) {
    if (height === 'flex') {
      element.style.height = null;
      element.style.minHeight = null;
      element.style.maxHeight = null;
      element.style.flex = '1 1 auto';
    } else {
      this.setFixedHeight(element, height);
    }
  };

  Utils.setFixedHeight = function (element, height) {
    height = this.formatSize(height);
    element.style.height = height;
    element.style.maxHeight = height;
    element.style.minHeight = height;
  };

  Utils.isBrowserIE = function () {
    if (this.isIE === undefined) {
      this.isIE =
      /*@cc_on!@*/
      !!document.documentMode; // At least IE6
    }

    return this.isIE;
  };

  Utils.isBrowserEdge = function () {
    if (this.isEdge === undefined) {
      this.isEdge = !this.isBrowserIE() && !!window.StyleMedia;
    }

    return this.isEdge;
  };

  Utils.isBrowserSafari = function () {
    if (this.isSafari === undefined) {
      var anyWindow = window; // taken from https://github.com/ag-grid/ag-grid/issues/550

      this.isSafari = Object.prototype.toString.call(anyWindow.HTMLElement).indexOf('Constructor') > 0 || function (p) {
        return p ? p.toString() === "[object SafariRemoteNotification]" : false;
      }(!anyWindow.safari || anyWindow.safari.pushNotification);
    }

    return this.isSafari;
  };

  Utils.isBrowserChrome = function () {
    if (this.isChrome === undefined) {
      var win = window;
      this.isChrome = !!win.chrome && (!!win.chrome.webstore || !!win.chrome.runtime) || /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);
    }

    return this.isChrome;
  };

  Utils.isBrowserFirefox = function () {
    if (this.isFirefox === undefined) {
      var win = window;
      this.isFirefox = typeof win.InstallTrigger !== 'undefined';
    }

    return this.isFirefox;
  };

  Utils.isIOSUserAgent = function () {
    if (this.isIOS === undefined) {
      // taken from https://stackoverflow.com/a/58064481/1388233
      this.isIOS = (/iPad|iPhone|iPod/.test(navigator.platform) || // eslint-disable-next-line
      navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1) && !window.MSStream;
    }

    return this.isIOS;
  };
  /**
   * srcElement is only available in IE. In all other browsers it is target
   * http://stackoverflow.com/questions/5301643/how-can-i-make-event-srcelement-work-in-firefox-and-what-does-it-mean
   * @param {Event} event
   * @returns {Element}
   */


  Utils.getTarget = function (event) {
    var eventNoType = event;
    return eventNoType.target || eventNoType.srcElement;
  };

  Utils.isElementChildOfClass = function (element, cls, maxNest) {
    var counter = 0;

    while (element) {
      if (this.containsClass(element, cls)) {
        return true;
      }

      element = element.parentElement;

      if (maxNest && ++counter > maxNest) {
        break;
      }
    }

    return false;
  };

  Utils.isElementInEventPath = function (element, event) {
    if (!event || !element) {
      return false;
    }

    var path = _.getEventPath(event);

    return path.indexOf(element) >= 0;
  };

  Utils.isFunction = function (val) {
    return !!(val && val.constructor && val.call && val.apply);
  };

  Utils.createEventPath = function (event) {
    var res = [];

    var pointer = _.getTarget(event);

    while (pointer) {
      res.push(pointer);
      pointer = pointer.parentElement;
    }

    return res;
  };
  /**
   * firefox doesn't have event.path set, or any alternative to it, so we hack
   * it in. this is needed as it's to late to work out the path when the item is
   * removed from the dom. used by MouseEventService, where it works out if a click
   * was from the current grid, or a detail grid (master / detail).
   * @param {Event} event
   */


  Utils.addAgGridEventPath = function (event) {
    event.__agGridEventPath = this.getEventPath(event);
  };
  /**
   * Gets the path for an Event.
   * https://stackoverflow.com/questions/39245488/event-path-undefined-with-firefox-and-vue-js
   * https://developer.mozilla.org/en-US/docs/Web/API/Event
   * @param {Event} event
   * @returns {EventTarget[]}
   */


  Utils.getEventPath = function (event) {
    var eventNoType = event;

    if (eventNoType.deepPath) {
      // IE supports deep path
      return eventNoType.deepPath();
    } else if (eventNoType.path) {
      // Chrome supports path
      return eventNoType.path;
    } else if (eventNoType.composedPath) {
      // Firefox supports composePath
      return eventNoType.composedPath();
    } else if (eventNoType.__agGridEventPath) {
      // Firefox supports composePath
      return eventNoType.__agGridEventPath;
    } else {
      // and finally, if none of the above worked,
      // we create the path ourselves
      return this.createEventPath(event);
    }
  };

  Utils.forEachSnapshotFirst = function (list, callback) {
    if (list) {
      var arrayCopy = list.slice(0);
      arrayCopy.forEach(callback);
    }
  };
  /**
   * Gets the document body width
   * from: http://stackoverflow.com/questions/1038727/how-to-get-browser-width-using-javascript-code
   * @returns {number}
   */


  Utils.getBodyWidth = function () {
    if (document.body) {
      return document.body.clientWidth;
    }

    if (window.innerHeight) {
      return window.innerWidth;
    }

    if (document.documentElement && document.documentElement.clientWidth) {
      return document.documentElement.clientWidth;
    }

    return -1;
  };
  /**
   * Gets the body height
   * from: http://stackoverflow.com/questions/1038727/how-to-get-browser-width-using-javascript-code
   * @returns {number}
   */


  Utils.getBodyHeight = function () {
    if (document.body) {
      return document.body.clientHeight;
    }

    if (window.innerHeight) {
      return window.innerHeight;
    }

    if (document.documentElement && document.documentElement.clientHeight) {
      return document.documentElement.clientHeight;
    }

    return -1;
  };

  Utils.setCheckboxState = function (eCheckbox, state) {
    if (typeof state === 'boolean') {
      eCheckbox.checked = state;
      eCheckbox.indeterminate = false;
    } else {
      // isNodeSelected returns back undefined if it's a group and the children
      // are a mix of selected and unselected
      eCheckbox.indeterminate = true;
    }
  };

  Utils.traverseNodesWithKey = function (nodes, callback) {
    var keyParts = [];
    recursiveSearchNodes(nodes);

    function recursiveSearchNodes(currentNodes) {
      currentNodes.forEach(function (node) {
        // also checking for children for tree data
        if (node.group || node.hasChildren()) {
          keyParts.push(node.key);
          var key = keyParts.join('|');
          callback(node, key);
          recursiveSearchNodes(node.childrenAfterGroup);
          keyParts.pop();
        }
      });
    }
  };
  /**
   * Converts a camelCase string into hyphenated string
   * from https://gist.github.com/youssman/745578062609e8acac9f
   * @param {string} str
   * @return {string}
   */


  Utils.camelCaseToHyphen = function (str) {
    if (str === null || str === undefined) {
      return null;
    }

    return str.replace(/([A-Z])/g, function (g) {
      return '-' + g[0].toLowerCase();
    });
  };
  /**
   * Converts a hyphenated string into camelCase string
   * from https://stackoverflow.com/questions/6660977/convert-hyphens-to-camel-case-camelcase
   * @param {string} str
   * @return {string}
   */


  Utils.hyphenToCamelCase = function (str) {
    if (str === null || str === undefined) {
      return null;
    }

    return str.replace(/-([a-z])/g, function (g) {
      return g[1].toUpperCase();
    });
  };

  Utils.capitalise = function (str) {
    return str[0].toUpperCase() + str.substr(1).toLowerCase();
  };
  /**
   * Converts a CSS object into string
   * @param {Object} stylesToUse an object eg: {color: 'black', top: '25px'}
   * @return {string} A string like "color: black; top: 25px;" for html
   */


  Utils.cssStyleObjectToMarkup = function (stylesToUse) {
    var _this = this;

    if (!stylesToUse) {
      return '';
    }

    var resParts = [];
    this.iterateObject(stylesToUse, function (styleKey, styleValue) {
      var styleKeyDashed = _this.camelCaseToHyphen(styleKey);

      resParts.push(styleKeyDashed + ": " + styleValue + ";");
    });
    return resParts.join(' ');
  };
  /**
   * Check if a value is numeric
   * from http://stackoverflow.com/questions/9716468/is-there-any-function-like-isnumeric-in-javascript-to-validate-numbers
   * @param {any} value
   * @return {boolean}
   */


  Utils.isNumeric = function (value) {
    if (value === '') {
      return false;
    }

    return !isNaN(parseFloat(value)) && isFinite(value);
  };

  Utils.escape = function (toEscape) {
    if (toEscape === null || toEscape === undefined || !toEscape.replace) {
      return toEscape;
    }

    return toEscape.replace(reUnescapedHtml, function (chr) {
      return HTML_ESCAPES[chr];
    });
  };
  /**
   * Mouse wheel (and 2-finger trackpad) support on the web sucks.  It is
   * complicated, thus this doc is long and (hopefully) detailed enough to answer
   * your questions.
   *
   * If you need to react to the mouse wheel in a predictable way, this code is
   * like your bestest friend. * hugs *
   *
   * As of today, there are 4 DOM event types you can listen to:
   *
   *   'wheel'                -- Chrome(31+), FF(17+), IE(9+)
   *   'mousewheel'           -- Chrome, IE(6+), Opera, Safari
   *   'MozMousePixelScroll'  -- FF(3.5 only!) (2010-2013) -- don't bother!
   *   'DOMMouseScroll'       -- FF(0.9.7+) since 2003
   *
   * So what to do?  The is the best:
   *
   *   normalizeWheel.getEventType();
   *
   * In your event callback, use this code to get sane interpretation of the
   * deltas.  This code will return an object with properties:
   *
   *   spinX   -- normalized spin speed (use for zoom) - x plane
   *   spinY   -- " - y plane
   *   pixelX  -- normalized distance (to pixels) - x plane
   *   pixelY  -- " - y plane
   *
   * Wheel values are provided by the browser assuming you are using the wheel to
   * scroll a web page by a number of lines or pixels (or pages).  Values can vary
   * significantly on different platforms and browsers, forgetting that you can
   * scroll at different speeds.  Some devices (like trackpads) emit more events
   * at smaller increments with fine granularity, and some emit massive jumps with
   * linear speed or acceleration.
   *
   * This code does its best to normalize the deltas for you:
   *
   *   - spin is trying to normalize how far the wheel was spun (or trackpad
   *     dragged).  This is super useful for zoom support where you want to
   *     throw away the chunky scroll steps on the PC and make those equal to
   *     the slow and smooth tiny steps on the Mac. Key data: This code tries to
   *     resolve a single slow step on a wheel to 1.
   *
   *   - pixel is normalizing the desired scroll delta in pixel units.  You'll
   *     get the crazy differences between browsers, but at least it'll be in
   *     pixels!
   *
   *   - positive value indicates scrolling DOWN/RIGHT, negative UP/LEFT.  This
   *     should translate to positive value zooming IN, negative zooming OUT.
   *     This matches the newer 'wheel' event.
   *
   * Why are there spinX, spinY (or pixels)?
   *
   *   - spinX is a 2-finger side drag on the trackpad, and a shift + wheel turn
   *     with a mouse.  It results in side-scrolling in the browser by default.
   *
   *   - spinY is what you expect -- it's the classic axis of a mouse wheel.
   *
   *   - I dropped spinZ/pixelZ.  It is supported by the DOM 3 'wheel' event and
   *     probably is by browsers in conjunction with fancy 3D controllers .. but
   *     you know.
   *
   * Implementation info:
   *
   * Examples of 'wheel' event if you scroll slowly (down) by one step with an
   * average mouse:
   *
   *   OS X + Chrome  (mouse)     -    4   pixel delta  (wheelDelta -120)
   *   OS X + Safari  (mouse)     -  N/A   pixel delta  (wheelDelta  -12)
   *   OS X + Firefox (mouse)     -    0.1 line  delta  (wheelDelta  N/A)
   *   Win8 + Chrome  (mouse)     -  100   pixel delta  (wheelDelta -120)
   *   Win8 + Firefox (mouse)     -    3   line  delta  (wheelDelta -120)
   *
   * On the trackpad:
   *
   *   OS X + Chrome  (trackpad)  -    2   pixel delta  (wheelDelta   -6)
   *   OS X + Firefox (trackpad)  -    1   pixel delta  (wheelDelta  N/A)
   *
   * On other/older browsers.. it's more complicated as there can be multiple and
   * also missing delta values.
   *
   * The 'wheel' event is more standard:
   *
   * http://www.w3.org/TR/DOM-Level-3-Events/#events-wheelevents
   *
   * The basics is that it includes a unit, deltaMode (pixels, lines, pages), and
   * deltaX, deltaY and deltaZ.  Some browsers provide other values to maintain
   * backward compatibility with older events.  Those other values help us
   * better normalize spin speed.  Example of what the browsers provide:
   *
   *                          | event.wheelDelta | event.detail
   *        ------------------+------------------+--------------
   *          Safari v5/OS X  |       -120       |       0
   *          Safari v5/Win7  |       -120       |       0
   *         Chrome v17/OS X  |       -120       |       0
   *         Chrome v17/Win7  |       -120       |       0
   *                IE9/Win7  |       -120       |   undefined
   *         Firefox v4/OS X  |     undefined    |       1
   *         Firefox v4/Win7  |     undefined    |       3
   *
   * from: https://github.com/facebook/fixed-data-table/blob/master/src/vendor_upstream/dom/normalizeWheel.js
   * @param {any} event
   * @return {any}
   */


  Utils.normalizeWheel = function (event) {
    var PIXEL_STEP = 10;
    var LINE_HEIGHT = 40;
    var PAGE_HEIGHT = 800; // spinX, spinY

    var sX = 0;
    var sY = 0; // pixelX, pixelY

    var pX = 0;
    var pY = 0; // Legacy

    if ('detail' in event) {
      sY = event.detail;
    }

    if ('wheelDelta' in event) {
      sY = -event.wheelDelta / 120;
    }

    if ('wheelDeltaY' in event) {
      sY = -event.wheelDeltaY / 120;
    }

    if ('wheelDeltaX' in event) {
      sX = -event.wheelDeltaX / 120;
    } // side scrolling on FF with DOMMouseScroll


    if ('axis' in event && event.axis === event.HORIZONTAL_AXIS) {
      sX = sY;
      sY = 0;
    }

    pX = sX * PIXEL_STEP;
    pY = sY * PIXEL_STEP;

    if ('deltaY' in event) {
      pY = event.deltaY;
    }

    if ('deltaX' in event) {
      pX = event.deltaX;
    }

    if ((pX || pY) && event.deltaMode) {
      if (event.deltaMode == 1) {
        // delta in LINE units
        pX *= LINE_HEIGHT;
        pY *= LINE_HEIGHT;
      } else {
        // delta in PAGE units
        pX *= PAGE_HEIGHT;
        pY *= PAGE_HEIGHT;
      }
    } // Fall-back if spin cannot be determined


    if (pX && !sX) {
      sX = pX < 1 ? -1 : 1;
    }

    if (pY && !sY) {
      sY = pY < 1 ? -1 : 1;
    }

    return {
      spinX: sX,
      spinY: sY,
      pixelX: pX,
      pixelY: pY
    };
  };
  /**
   * from https://stackoverflow.com/questions/24004791/can-someone-explain-the-debounce-function-in-javascript
   * @param {Function} func The function to be debounced
   * @param {number} wait The time in ms to debounce
   * @param {boolean} immediate If it should run immediately or wait for the initial debounce delay
   * @return {Function} The debounced function
   */


  Utils.debounce = function (func, wait, immediate) {
    if (immediate === void 0) {
      immediate = false;
    } // 'private' variable for instance
    // The returned function will be able to reference this due to closure.
    // Each call to the returned function will share this common timer.


    var timeout; // Calling debounce returns a new anonymous function

    return function () {
      var args = [];

      for (var _i = 0; _i < arguments.length; _i++) {
        args[_i] = arguments[_i];
      } // reference the context and args for the setTimeout function


      var context = this; // Should the function be called now? If immediate is true
      //   and not already in a timeout then the answer is: Yes

      var callNow = immediate && !timeout; // This is the basic debounce behaviour where you can call this
      //   function several times, but it will only execute once
      //   [before or after imposing a delay].
      //   Each time the returned function is called, the timer starts over.

      window.clearTimeout(timeout); // Set the new timeout

      timeout = window.setTimeout(function () {
        // Inside the timeout function, clear the timeout variable
        // which will let the next execution run when in 'immediate' mode
        timeout = null; // Check if the function already ran with the immediate flag

        if (!immediate) {
          // Call the original function with apply
          // apply lets you define the 'this' object as well as the arguments
          //    (both captured before setTimeout)
          func.apply(context, args);
        }
      }, wait); // Immediate mode and no wait timer? Execute the function..

      if (callNow) {
        func.apply(context, args);
      }
    };
  };
  /**
   * a user once raised an issue - they said that when you opened a popup (eg context menu)
   * and then clicked on a selection checkbox, the popup wasn't closed. this is because the
   * popup listens for clicks on the body, however ag-grid WAS stopping propagation on the
   * checkbox clicks (so the rows didn't pick them up as row selection selection clicks).
   * to get around this, we have a pattern to stop propagation for the purposes of ag-Grid,
   * but we still let the event pass back to teh body.
   * @param {Event} event
   */


  Utils.stopPropagationForAgGrid = function (event) {
    event[AG_GRID_STOP_PROPAGATION] = true;
  };

  Utils.isStopPropagationForAgGrid = function (event) {
    return event[AG_GRID_STOP_PROPAGATION] === true;
  };

  Utils.executeInAWhile = function (funcs) {
    this.executeAfter(funcs, 400);
  };

  Utils.executeNextVMTurn = function (funcs) {
    this.executeAfter(funcs, 0);
  };

  Utils.executeAfter = function (funcs, millis) {
    if (funcs.length > 0) {
      window.setTimeout(function () {
        funcs.forEach(function (func) {
          return func();
        });
      }, millis);
    }
  };

  Utils.referenceCompare = function (left, right) {
    if (left == null && right == null) {
      return true;
    }

    if (left == null && right) {
      return false;
    }

    if (left && right == null) {
      return false;
    }

    return left === right;
  };

  Utils.get = function (source, expression, defaultValue) {
    if (source == null) {
      return defaultValue;
    }

    var keys = expression.split(".");
    var objectToRead = source;

    while (keys.length > 1) {
      objectToRead = objectToRead[keys.shift()];

      if (objectToRead == null) {
        return defaultValue;
      }
    }

    var value = objectToRead[keys[0]];
    return value != null ? value : defaultValue;
  };

  Utils.set = function (target, expression, value) {
    if (target == null) {
      return;
    }

    var keys = expression.split(".");
    var objectToUpdate = target;

    while (keys.length > 1) {
      objectToUpdate = objectToUpdate[keys.shift()];

      if (objectToUpdate == null) {
        return;
      }
    }

    objectToUpdate[keys[0]] = value;
  };

  Utils.addSafePassiveEventListener = function (frameworkOverrides, eElement, event, listener) {
    var isPassive = this.includes(Utils.PASSIVE_EVENTS, event);
    var isOutsideAngular = this.includes(Utils.OUTSIDE_ANGULAR_EVENTS, event);
    var options = isPassive ? {
      passive: true
    } : undefined;

    if (isOutsideAngular) {
      frameworkOverrides.addEventListenerOutsideAngular(eElement, event, listener, options);
    } else {
      eElement.addEventListener(event, listener, options);
    }
  };
  /**
   * Converts a camelCase string into regular text
   * from: https://stackoverflow.com/questions/15369566/putting-space-in-camel-case-string-using-regular-expression
   * @param {string} camelCase
   * @return {string}
   */


  Utils.camelCaseToHumanText = function (camelCase) {
    if (!camelCase || camelCase == null) {
      return null;
    }

    var rex = /([A-Z])([A-Z])([a-z])|([a-z])([A-Z])/g;
    var words = camelCase.replace(rex, '$1$4 $2$3$5').replace('.', ' ').split(' ');
    return words.map(function (word) {
      return word.substring(0, 1).toUpperCase() + (word.length > 1 ? word.substring(1, word.length) : '');
    }).join(' ');
  };
  /**
   * Displays a message to the browser. this is useful in iPad, where you can't easily see the console.
   * so the javascript code can use this to give feedback. this is NOT intended to be called in production.
   * it is intended the ag-Grid developer calls this to troubleshoot, but then takes out the calls before
   * checking in.
   * @param {string} msg
   */


  Utils.message = function (msg) {
    var eMessage = document.createElement('div');
    eMessage.innerHTML = msg;
    var eBox = document.querySelector('#__ag__message');

    if (!eBox) {
      var template = "<div id=\"__ag__message\" style=\"display: inline-block; position: absolute; top: 0px; left: 0px; color: white; background-color: black; z-index: 20; padding: 2px; border: 1px solid darkred; height: 200px; overflow-y: auto;\"></div>";
      eBox = this.loadTemplate(template);

      if (document.body) {
        document.body.appendChild(eBox);
      }
    }

    eBox.insertBefore(eMessage, eBox.children[0]);
  };
  /**
   * Gets called by: a) ClientSideNodeManager and b) GroupStage to do sorting.
   * when in ClientSideNodeManager we always have indexes (as this sorts the items the
   * user provided) but when in GroupStage, the nodes can contain filler nodes that
   * don't have order id's
   * @param {RowNode[]} rowNodes
   * @param {Object} rowNodeOrder
   */


  Utils.sortRowNodesByOrder = function (rowNodes, rowNodeOrder) {
    if (!rowNodes) {
      return;
    }

    var comparator = function comparator(nodeA, nodeB) {
      var positionA = rowNodeOrder[nodeA.id];
      var positionB = rowNodeOrder[nodeB.id];
      var aHasIndex = positionA !== undefined;
      var bHasIndex = positionB !== undefined;
      var bothNodesAreUserNodes = aHasIndex && bHasIndex;
      var bothNodesAreFillerNodes = !aHasIndex && !bHasIndex;

      if (bothNodesAreUserNodes) {
        // when comparing two nodes the user has provided, they always
        // have indexes
        return positionA - positionB;
      } else if (bothNodesAreFillerNodes) {
        // when comparing two filler nodes, we have no index to compare them
        // against, however we want this sorting to be deterministic, so that
        // the rows don't jump around as the user does delta updates. so we
        // want the same sort result. so we use the __objectId - which doesn't make sense
        // from a sorting point of view, but does give consistent behaviour between
        // calls. otherwise groups jump around as delta updates are done.
        // note: previously here we used nodeId, however this gave a strange order
        // as string ordering of numbers is wrong, so using id based on creation order
        // as least gives better looking order.
        return nodeA.__objectId - nodeB.__objectId;
      } else if (aHasIndex) {
        return 1;
      }

      return -1;
    }; // check if the list first needs sorting


    var rowNodeA;
    var rowNodeB;
    var atLeastOneOutOfOrder = false;

    for (var i = 0; i < rowNodes.length - 1; i++) {
      rowNodeA = rowNodes[i];
      rowNodeB = rowNodes[i + 1];

      if (comparator(rowNodeA, rowNodeB) > 0) {
        atLeastOneOutOfOrder = true;
        break;
      }
    }

    if (atLeastOneOutOfOrder) {
      rowNodes.sort(comparator);
    }
  };

  Utils.fuzzyCheckStrings = function (inputValues, validValues, allSuggestions) {
    var _this = this;

    var fuzzyMatches = {};
    var invalidInputs = inputValues.filter(function (inputValue) {
      return !validValues.some(function (validValue) {
        return validValue === inputValue;
      });
    });

    if (invalidInputs.length > 0) {
      invalidInputs.forEach(function (invalidInput) {
        return fuzzyMatches[invalidInput] = _this.fuzzySuggestions(invalidInput, allSuggestions);
      });
    }

    return fuzzyMatches;
  };
  /**
   *
   * @param {String} inputValue The value to be compared against a list of strings
   * @param allSuggestions The list of strings to be compared against
   * @param hideIrrelevant By default, fuzzy suggestions will just sort the allSuggestions list, set this to true
   *        to filter out the irrelevant values
   * @param weighted Set this to true, to make letters matched in the order they were typed have priority in the results.
   */


  Utils.fuzzySuggestions = function (inputValue, allSuggestions, hideIrrelevant, weighted) {
    var search = weighted ? _.string_weighted_distances : _.string_distances;
    var thisSuggestions = allSuggestions.map(function (text) {
      return {
        value: text,
        relevance: search(inputValue.toLowerCase(), text.toLocaleLowerCase())
      };
    });
    thisSuggestions.sort(function (a, b) {
      return b.relevance - a.relevance;
    });

    if (hideIrrelevant) {
      thisSuggestions = thisSuggestions.filter(function (suggestion) {
        return suggestion.relevance !== 0;
      });
    }

    return thisSuggestions.map(function (suggestion) {
      return suggestion.value;
    });
  };
  /**
   * Algorithm to do fuzzy search
   * from https://stackoverflow.com/questions/23305000/javascript-fuzzy-search-that-makes-sense
   * @param {string} from
   * @return {[]}
   */


  Utils.get_bigrams = function (from) {
    var s = from.toLowerCase();
    var v = new Array(s.length - 1);
    var i;
    var j;
    var ref;

    for (i = j = 0, ref = v.length; j <= ref; i = j += 1) {
      v[i] = s.slice(i, i + 2);
    }

    return v;
  };

  Utils.string_distances = function (str1, str2) {
    if (str1.length === 0 && str2.length === 0) {
      return 0;
    }

    var pairs1 = _.get_bigrams(str1);

    var pairs2 = _.get_bigrams(str2);

    var union = pairs1.length + pairs2.length;
    var hit_count = 0;
    var j;
    var len;

    for (j = 0, len = pairs1.length; j < len; j++) {
      var x = pairs1[j];
      var k = void 0;
      var len1 = void 0;

      for (k = 0, len1 = pairs2.length; k < len1; k++) {
        var y = pairs2[k];

        if (x === y) {
          hit_count++;
        }
      }
    }

    return hit_count > 0 ? 2 * hit_count / union : 0;
  };

  Utils.string_weighted_distances = function (str1, str2) {
    var a = str1.replace(/\s/g, '');
    var b = str2.replace(/\s/g, '');
    var weight = 0;
    var lastIndex = 0;

    for (var i = 0; i < a.length; i++) {
      var idx = b.indexOf(a[i]);

      if (idx === -1) {
        continue;
      }

      lastIndex = idx;
      weight += (b.length - lastIndex) * 100 / b.length;
      weight *= weight;
    }

    return weight;
  };

  Utils.isNumpadDelWithNumlockOnForEdgeOrIe = function (event) {
    if (Utils.isBrowserEdge() || Utils.isBrowserIE()) {
      return event.key === Utils.NUMPAD_DEL_NUMLOCK_ON_KEY && event.charCode === Utils.NUMPAD_DEL_NUMLOCK_ON_CHARCODE;
    }

    return false;
  };
  /**
   * cell renderers are used in a few places. they bind to dom slightly differently to other cell renderes as they
   * can return back strings (instead of html elemnt) in the getGui() method. common code placed here to handle that.
   * @param {Promise<ICellRendererComp>} cellRendererPromise
   * @param {HTMLElement} eTarget
   */


  Utils.bindCellRendererToHtmlElement = function (cellRendererPromise, eTarget) {
    cellRendererPromise.then(function (cellRenderer) {
      var gui = cellRenderer.getGui();

      if (gui != null) {
        if (typeof gui == 'object') {
          eTarget.appendChild(gui);
        } else {
          eTarget.innerHTML = gui;
        }
      }
    });
  };

  Utils.convertToSet = function (list) {
    var set = new Set();
    list.forEach(function (x) {
      return set.add(x);
    });
    return set;
  };

  Utils.deepFreeze = function (object) {
    var _this = this;

    Object.freeze(object);

    _.values(object).filter(function (v) {
      return v != null;
    }).forEach(function (v) {
      if (typeof v === 'object' || typeof v === 'function') {
        _this.deepFreeze(v);
      }
    });

    return object;
  };

  Utils.PASSIVE_EVENTS = ['touchstart', 'touchend', 'touchmove', 'touchcancel'];
  Utils.OUTSIDE_ANGULAR_EVENTS = ['mouseover', 'mouseout', 'mouseenter', 'mouseleave'];
  Utils.PRINTABLE_CHARACTERS = 'qwertyuiopasdfghjklzxcvbnmQWERTYUIOPASDFGHJKLZXCVBNM1234567890!"£$%^&*()_+-=[];\'#,./\\|<>?:@~{}';
  Utils.NUMPAD_DEL_NUMLOCK_ON_KEY = 'Del';
  Utils.NUMPAD_DEL_NUMLOCK_ON_CHARCODE = 46;
  Utils.doOnceFlags = {};
  Utils.supports = {};

  Utils.isEventSupported = function () {
    var tags = {
      select: 'input',
      change: 'input',
      submit: 'form',
      reset: 'form',
      error: 'img',
      load: 'img',
      abort: 'img'
    };

    var isEventSupported = function isEventSupported(eventName) {
      if (typeof Utils.supports[eventName] === 'boolean') {
        return Utils.supports[eventName];
      }

      var el = document.createElement(tags[eventName] || 'div');
      eventName = 'on' + eventName;
      var isSupported = eventName in el;

      if (!isSupported) {
        el.setAttribute(eventName, 'return;');
        isSupported = typeof el[eventName] == 'function';
      }

      el = null;
      return Utils.supports[eventName] = isSupported;
    };

    return isEventSupported;
  }();

  Utils.areEqual = function (a, b) {
    return a.length === b.length && a.every(function (value, index) {
      return b[index] === value;
    });
  };

  Utils.keys = function (map) {
    var keys = [];
    map.forEach(function (_, key) {
      return keys.push(key);
    });
    return keys;
  };

  Utils.values = function (object) {
    return Object.keys(object).map(function (key) {
      return object[key];
    });
  };

  Utils.includes = function (array, value) {
    return array.indexOf(value) > -1;
  };

  Utils.compose = function () {
    var fns = [];

    for (var _i = 0; _i < arguments.length; _i++) {
      fns[_i] = arguments[_i];
    }

    return function (arg) {
      return fns.reduce(function (composed, f) {
        return f(composed);
      }, arg);
    };
  };

  Utils.decToHex = function (number, bytes) {
    var hex = '';

    for (var i = 0; i < bytes; i++) {
      hex += String.fromCharCode(number & 0xff);
      number >>>= 8;
    }

    return hex;
  };
  /**
   * It encodes any string in UTF-8 format
   * taken from https://github.com/mathiasbynens/utf8.js
   * @param {string} s
   * @returns {string}
   */


  Utils.utf8_encode = function (s) {
    var stringFromCharCode = String.fromCharCode;

    function ucs2decode(string) {
      var output = [];
      var counter = 0;
      var length = string.length;
      var value;
      var extra;

      while (counter < length) {
        value = string.charCodeAt(counter++);

        if (value >= 0xD800 && value <= 0xDBFF && counter < length) {
          // high surrogate, and there is a next character
          extra = string.charCodeAt(counter++);

          if ((extra & 0xFC00) == 0xDC00) {
            // low surrogate
            output.push(((value & 0x3FF) << 10) + (extra & 0x3FF) + 0x10000);
          } else {
            // unmatched surrogate; only append this code unit, in case the next
            // code unit is the high surrogate of a surrogate pair
            output.push(value);
            counter--;
          }
        } else {
          output.push(value);
        }
      }

      return output;
    }

    function checkScalarValue(codePoint) {
      if (codePoint >= 0xD800 && codePoint <= 0xDFFF) {
        throw Error('Lone surrogate U+' + codePoint.toString(16).toUpperCase() + ' is not a scalar value');
      }
    }

    function createByte(codePoint, shift) {
      return stringFromCharCode(codePoint >> shift & 0x3F | 0x80);
    }

    function encodeCodePoint(codePoint) {
      if ((codePoint & 0xFFFFFF80) == 0) {
        // 1-byte sequence
        return stringFromCharCode(codePoint);
      }

      var symbol = '';

      if ((codePoint & 0xFFFFF800) == 0) {
        // 2-byte sequence
        symbol = stringFromCharCode(codePoint >> 6 & 0x1F | 0xC0);
      } else if ((codePoint & 0xFFFF0000) == 0) {
        // 3-byte sequence
        checkScalarValue(codePoint);
        symbol = stringFromCharCode(codePoint >> 12 & 0x0F | 0xE0);
        symbol += createByte(codePoint, 6);
      } else if ((codePoint & 0xFFE00000) == 0) {
        // 4-byte sequence
        symbol = stringFromCharCode(codePoint >> 18 & 0x07 | 0xF0);
        symbol += createByte(codePoint, 12);
        symbol += createByte(codePoint, 6);
      }

      symbol += stringFromCharCode(codePoint & 0x3F | 0x80);
      return symbol;
    }

    var codePoints = ucs2decode(s);
    var length = codePoints.length;
    var index = -1;
    var codePoint;
    var byteString = '';

    while (++index < length) {
      codePoint = codePoints[index];
      byteString += encodeCodePoint(codePoint);
    }

    return byteString;
  };

  Utils.deepCloneObject = function (object) {
    return JSON.parse(JSON.stringify(object));
  };

  Utils.getProperty = function (object, key) {
    return object[key];
  };

  Utils.setProperty = function (object, key, value) {
    return object[key] = value;
  }; //
  // IMPORTANT NOTE!
  //
  // The comments below provide one example of how each icon is used, so that you can find
  // an instance of it in the UI and see what it looks like in the UI. Many icons are used
  // in multiple places.
  //


  Utils.iconNameClassMap = {
    // header column group shown when expanded (click to contract)
    columnGroupOpened: 'expanded',
    // header column group shown when contracted (click to expand)
    columnGroupClosed: 'contracted',
    // tool panel column group contracted (click to expand)
    columnSelectClosed: 'tree-closed',
    // tool panel column group expanded (click to contract)
    columnSelectOpen: 'tree-open',
    // column tool panel header expand/collapse all button, shown when some children are expanded and
    //     others are collapsed
    columnSelectIndeterminate: 'tree-indeterminate',
    // shown on ghost icon while dragging column to the side of the grid to pin
    columnMovePin: 'pin',
    // ??? doesn't seem to be used?
    columnMoveAdd: 'plus',
    // shown on ghost icon while dragging over part of the page that is not a drop zone
    columnMoveHide: 'eye-slash',
    // shown on ghost icon while dragging columns to reorder
    columnMoveMove: 'arrows',
    // animating icon shown when dragging a column to the right of the grid causes horizontal scrolling
    columnMoveLeft: 'left',
    // animating icon shown when dragging a column to the left of the grid causes horizontal scrolling
    columnMoveRight: 'right',
    // shown on ghost icon while dragging over Row Groups drop zone
    columnMoveGroup: 'group',
    // shown on ghost icon while dragging over Values drop zone
    columnMoveValue: 'aggregation',
    // shown on ghost icon while dragging over pivot drop zone
    columnMovePivot: 'pivot',
    // shown on ghost icon while dragging over drop zone that doesn't support it, e.g.
    //     string column over aggregation drop zone
    dropNotAllowed: 'not-allowed',
    // shown on row group when contracted (click to expand)
    groupContracted: 'contracted',
    // shown on row group when expanded (click to contract)
    groupExpanded: 'expanded',
    // context menu chart item
    chart: 'chart',
    // chart window title bar
    close: 'cross',
    // X (remove) on column 'pill' after adding it to a drop zone list
    cancel: 'cancel',
    // indicates the currently active pin state in the "Pin column" sub-menu of the column menu
    check: 'tick',
    // the following checkbox-* items are for checkboxes on themes that don't use a
    //     native <input type="radio">
    checkboxChecked: 'checkbox-checked',
    checkboxUnchecked: 'checkbox-unchecked',
    checkboxIndeterminate: 'checkbox-indeterminate',
    checkboxCheckedReadOnly: 'checkbox-checked-readonly',
    checkboxUncheckedReadOnly: 'checkbox-unchecked-readonly',
    checkboxIndeterminateReadOnly: 'checkbox-indeterminate-readonly',
    // "go to first" button in pagination controls
    first: 'first',
    // "go to previous" button in pagination controls
    previous: 'previous',
    // "go to next" button in pagination controls
    next: 'next',
    // "go to last" button in pagination controls
    last: 'last',
    // shown on top right of chart when chart is linked to range data (click to unlink)
    linked: 'linked',
    // shown on top right of chart when chart is not linked to range data (click to link)
    unlinked: 'unlinked',
    // "Choose colour" button on chart settings tab
    colorPicker: 'color-picker',
    // the following radio-button-* items are for radio buttons on themes that don't use a
    //     native <input type="radio">
    radioButtonOn: 'radio-button-on',
    radioButtonOff: 'radio-button-off',
    // rotating spinner shown by the loading cell renderer
    groupLoading: 'loading',
    // ??? doesn't seem to be used?
    data: 'data',
    // button to launch enterprise column menu
    menu: 'menu',
    // filter tool panel tab
    filter: 'filter',
    // column tool panel tab
    columns: 'columns',
    // button in chart regular size window title bar (click to maximise)
    maximize: 'maximize',
    // button in chart maximised window title bar (click to make regular size)
    minimize: 'minimize',
    // "Pin column" item in column header menu
    menuPin: 'pin',
    // "Value aggregation" column menu item (shown on numeric columns when grouping is active)"
    menuValue: 'aggregation',
    // "Group by {column-name}" item in column header menu
    menuAddRowGroup: 'group',
    // "Un-Group by {column-name}" item in column header menu
    menuRemoveRowGroup: 'group',
    // context menu copy item
    clipboardCopy: 'copy',
    // ??? doesn't seem toi be used?
    clipboardCut: 'cut',
    // context menu paste item
    clipboardPaste: 'paste',
    // identifies the pivot drop zone
    pivotPanel: 'pivot',
    // "Row groups" drop zone in column tool panel
    rowGroupPanel: 'group',
    // columns tool panel Values drop zone
    valuePanel: 'aggregation',
    // drag handle used to pick up draggable columns
    columnDrag: 'grip',
    // drag handle used to pick up draggable rows
    rowDrag: 'grip',
    // context menu export item
    save: 'save',
    // version of small-right used in RTL mode
    smallLeft: 'small-left',
    // separater between column 'pills' when you add multiple columns to the header drop zone
    smallRight: 'small-right',
    // ??? doesn't seem to be used?
    smallUp: 'small-up',
    // ??? doesn't seem to be used?
    smallDown: 'small-down',
    // show on column header when column is sorted ascending
    sortAscending: 'asc',
    // show on column header when column is sorted descending
    sortDescending: 'desc',
    // show on column header when column has no sort, only when enabled with gridOptions.unSortIcon=true
    sortUnSort: 'none'
  };
  return Utils;
}();

var NumberSequence =
/** @class */
function () {
  function NumberSequence(initValue, step) {
    if (initValue === void 0) {
      initValue = 0;
    }

    if (step === void 0) {
      step = 1;
    }

    this.nextValue = initValue;
    this.step = step;
  }

  NumberSequence.prototype.next = function () {
    var valToReturn = this.nextValue;
    this.nextValue += this.step;
    return valToReturn;
  };

  NumberSequence.prototype.peek = function () {
    return this.nextValue;
  };

  NumberSequence.prototype.skip = function (count) {
    this.nextValue += count;
  };

  return NumberSequence;
}();

var _ = Utils;
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */

var PromiseStatus;

(function (PromiseStatus) {
  PromiseStatus[PromiseStatus["IN_PROGRESS"] = 0] = "IN_PROGRESS";
  PromiseStatus[PromiseStatus["RESOLVED"] = 1] = "RESOLVED";
})(PromiseStatus || (PromiseStatus = {}));

var Promise =
/** @class */
function () {
  function Promise(callback) {
    this.status = PromiseStatus.IN_PROGRESS;
    this.resolution = null;
    this.listOfWaiters = [];
    callback(this.onDone.bind(this), this.onReject.bind(this));
  }

  Promise.all = function (toCombine) {
    return new Promise(function (resolve) {
      var combinedValues = [];
      var remainingToResolve = toCombine.length;
      toCombine.forEach(function (source, index) {
        source.then(function (sourceResolved) {
          remainingToResolve--;
          combinedValues[index] = sourceResolved;

          if (remainingToResolve == 0) {
            resolve(combinedValues);
          }
        });
        combinedValues.push(null); // spl todo: review with Alberto - why?
      });
    });
  };

  Promise.resolve = function (value) {
    return new Promise(function (resolve) {
      return resolve(value);
    });
  };

  Promise.external = function () {
    var capture;
    var promise = new Promise(function (resolve) {
      capture = resolve;
    });
    return {
      promise: promise,
      resolve: function resolve(value) {
        capture(value);
      }
    };
  };

  Promise.prototype.then = function (func) {
    if (this.status === PromiseStatus.IN_PROGRESS) {
      this.listOfWaiters.push(func);
    } else {
      func(this.resolution);
    }
  };

  Promise.prototype.firstOneOnly = function (func) {
    if (this.status === PromiseStatus.IN_PROGRESS) {
      if (this.listOfWaiters.length === 0) {
        this.listOfWaiters.push(func);
      }
    } else {
      func(this.resolution);
    }
  };

  Promise.prototype.map = function (adapter) {
    var _this = this;

    return new Promise(function (resolve) {
      _this.then(function (unmapped) {
        resolve(adapter(unmapped));
      });
    });
  };

  Promise.prototype.resolveNow = function (ifNotResolvedValue, ifResolved) {
    if (this.status == PromiseStatus.IN_PROGRESS) {
      return ifNotResolvedValue;
    }

    return ifResolved(this.resolution);
  };

  Promise.prototype.onDone = function (value) {
    this.status = PromiseStatus.RESOLVED;
    this.resolution = value;
    this.listOfWaiters.forEach(function (waiter) {
      return waiter(value);
    });
  };

  Promise.prototype.onReject = function (params) {
    console.warn('TBI');
  };

  return Promise;
}();
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var ColumnKeyCreator =
/** @class */
function () {
  function ColumnKeyCreator() {
    this.existingKeys = {};
  }

  ColumnKeyCreator.prototype.addExistingKeys = function (keys) {
    for (var i = 0; i < keys.length; i++) {
      this.existingKeys[keys[i]] = true;
    }
  };

  ColumnKeyCreator.prototype.getUniqueKey = function (colId, colField) {
    // in case user passed in number for colId, convert to string
    colId = _.toStringOrNull(colId);
    var count = 0;

    while (true) {
      var idToTry = void 0;

      if (colId) {
        idToTry = colId;

        if (count !== 0) {
          idToTry += '_' + count;
        }
      } else if (colField) {
        idToTry = colField;

        if (count !== 0) {
          idToTry += '_' + count;
        }
      } else {
        idToTry = '' + count;
      }

      if (!this.existingKeys[idToTry]) {
        this.existingKeys[idToTry] = true;
        return idToTry;
      }

      count++;
    }
  };

  return ColumnKeyCreator;
}();
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var Context =
/** @class */
function () {
  function Context(params, logger) {
    this.beanWrappers = {};
    this.componentsMappedByName = {};
    this.destroyed = false;

    if (!params || !params.beanClasses) {
      return;
    }

    this.contextParams = params;
    this.logger = logger;
    this.logger.log(">> creating ag-Application Context");
    this.setupComponents();
    this.createBeans();
    var beanInstances = this.getBeanInstances();
    this.wireBeans(beanInstances);
    this.logger.log(">> ag-Application Context ready - component is alive");
  }

  Context.prototype.getBeanInstances = function () {
    return _.values(this.beanWrappers).map(function (beanEntry) {
      return beanEntry.beanInstance;
    });
  };

  Context.prototype.setupComponents = function () {
    var _this = this;

    if (this.contextParams.components) {
      this.contextParams.components.forEach(function (componentMeta) {
        return _this.addComponent(componentMeta);
      });
    }
  };

  Context.prototype.addComponent = function (componentMeta) {
    // get name of the class as a string
    // let className = _.getNameOfClass(ComponentClass);
    // insert a dash after every capital letter
    // let classEscaped = className.replace(/([A-Z])/g, "-$1").toLowerCase();
    var classEscaped = componentMeta.componentName.replace(/([a-z])([A-Z])/g, "$1-$2").toLowerCase(); // put all to upper case

    var classUpperCase = classEscaped.toUpperCase(); // finally store

    this.componentsMappedByName[classUpperCase] = componentMeta.componentClass;
  };

  Context.prototype.createComponentFromElement = function (element, afterPreCreateCallback) {
    var key = element.nodeName;
    return this.createComponent(key, afterPreCreateCallback);
  };

  Context.prototype.createComponent = function (key, afterPreCreateCallback) {
    if (this.componentsMappedByName && this.componentsMappedByName[key]) {
      var newComponent = new this.componentsMappedByName[key]();
      this.wireBean(newComponent, afterPreCreateCallback);
      return newComponent;
    }

    return null;
  };

  Context.prototype.wireBean = function (bean, afterPreCreateCallback) {
    if (!bean) {
      throw Error("Can't wire to bean since it is null");
    }

    this.wireBeans([bean], afterPreCreateCallback);
  };

  Context.prototype.wireBeans = function (beanInstances, afterPreCreateCallback) {
    this.autoWireBeans(beanInstances);
    this.methodWireBeans(beanInstances);
    this.callLifeCycleMethods(beanInstances, 'preConstructMethods'); // the callback sets the attributes, so the component has access to attributes
    // before postConstruct methods in the component are executed

    if (_.exists(afterPreCreateCallback)) {
      beanInstances.forEach(afterPreCreateCallback);
    }

    this.callLifeCycleMethods(beanInstances, 'postConstructMethods');
  };

  Context.prototype.createBeans = function () {
    var _this = this; // register all normal beans


    this.contextParams.beanClasses.forEach(this.createBeanWrapper.bind(this)); // register override beans, these will overwrite beans above of same name
    // instantiate all beans - overridden beans will be left out

    _.iterateObject(this.beanWrappers, function (key, beanEntry) {
      var constructorParamsMeta;

      if (beanEntry.bean.__agBeanMetaData && beanEntry.bean.__agBeanMetaData.autowireMethods && beanEntry.bean.__agBeanMetaData.autowireMethods.agConstructor) {
        constructorParamsMeta = beanEntry.bean.__agBeanMetaData.autowireMethods.agConstructor;
      }

      var constructorParams = _this.getBeansForParameters(constructorParamsMeta, beanEntry.bean.name);

      var newInstance = applyToConstructor(beanEntry.bean, constructorParams);
      beanEntry.beanInstance = newInstance;
    });

    var createdBeanNames = Object.keys(this.beanWrappers).join(', ');
    this.logger.log("created beans: " + createdBeanNames);
  }; // tslint:disable-next-line


  Context.prototype.createBeanWrapper = function (Bean) {
    var metaData = Bean.__agBeanMetaData;

    if (!metaData) {
      var beanName = void 0;

      if (Bean.prototype.constructor) {
        beanName = Bean.prototype.constructor.name;
      } else {
        beanName = "" + Bean;
      }

      console.error("context item " + beanName + " is not a bean");
      return;
    }

    var beanEntry = {
      bean: Bean,
      beanInstance: null,
      beanName: metaData.beanName
    };
    this.beanWrappers[metaData.beanName] = beanEntry;
  };

  Context.prototype.autoWireBeans = function (beanInstances) {
    var _this = this;

    beanInstances.forEach(function (beanInstance) {
      _this.forEachMetaDataInHierarchy(beanInstance, function (metaData, beanName) {
        var attributes = metaData.agClassAttributes;

        if (!attributes) {
          return;
        }

        attributes.forEach(function (attribute) {
          var otherBean = _this.lookupBeanInstance(beanName, attribute.beanName, attribute.optional);

          beanInstance[attribute.attributeName] = otherBean;
        });
      });
    });
  };

  Context.prototype.methodWireBeans = function (beanInstances) {
    var _this = this;

    beanInstances.forEach(function (beanInstance) {
      _this.forEachMetaDataInHierarchy(beanInstance, function (metaData, beanName) {
        _.iterateObject(metaData.autowireMethods, function (methodName, wireParams) {
          // skip constructor, as this is dealt with elsewhere
          if (methodName === "agConstructor") {
            return;
          }

          var initParams = _this.getBeansForParameters(wireParams, beanName);

          beanInstance[methodName].apply(beanInstance, initParams);
        });
      });
    });
  };

  Context.prototype.forEachMetaDataInHierarchy = function (beanInstance, callback) {
    var prototype = Object.getPrototypeOf(beanInstance);

    while (prototype != null) {
      var constructor = prototype.constructor;

      if (constructor.hasOwnProperty('__agBeanMetaData')) {
        var metaData = constructor.__agBeanMetaData;
        var beanName = this.getBeanName(constructor);
        callback(metaData, beanName);
      }

      prototype = Object.getPrototypeOf(prototype);
    }
  };

  Context.prototype.getBeanName = function (constructor) {
    if (constructor.__agBeanMetaData && constructor.__agBeanMetaData.beanName) {
      return constructor.__agBeanMetaData.beanName;
    }

    var constructorString = constructor.toString();
    var beanName = constructorString.substring(9, constructorString.indexOf("("));
    return beanName;
  };

  Context.prototype.getBeansForParameters = function (parameters, beanName) {
    var _this = this;

    var beansList = [];

    if (parameters) {
      _.iterateObject(parameters, function (paramIndex, otherBeanName) {
        var otherBean = _this.lookupBeanInstance(beanName, otherBeanName);

        beansList[Number(paramIndex)] = otherBean;
      });
    }

    return beansList;
  };

  Context.prototype.lookupBeanInstance = function (wiringBean, beanName, optional) {
    if (optional === void 0) {
      optional = false;
    }

    if (beanName === "context") {
      return this;
    } else if (this.contextParams.providedBeanInstances && this.contextParams.providedBeanInstances.hasOwnProperty(beanName)) {
      return this.contextParams.providedBeanInstances[beanName];
    } else {
      var beanEntry = this.beanWrappers[beanName];

      if (beanEntry) {
        return beanEntry.beanInstance;
      }

      if (!optional) {
        console.error("ag-Grid: unable to find bean reference " + beanName + " while initialising " + wiringBean);
      }

      return null;
    }
  };

  Context.prototype.callLifeCycleMethods = function (beanInstances, lifeCycleMethod) {
    var _this = this;

    beanInstances.forEach(function (beanInstance) {
      _this.forEachMetaDataInHierarchy(beanInstance, function (metaData) {
        var methods = metaData[lifeCycleMethod];

        if (!methods) {
          return;
        }

        methods.forEach(function (methodName) {
          return beanInstance[methodName]();
        });
      });
    });
  };

  Context.prototype.getBean = function (name) {
    return this.lookupBeanInstance("getBean", name, true);
  };

  Context.prototype.destroy = function () {
    // should only be able to destroy once
    if (this.destroyed) {
      return;
    }

    this.logger.log(">> Shutting down ag-Application Context");
    var beanInstances = this.getBeanInstances();
    this.callLifeCycleMethods(beanInstances, 'preDestroyMethods');
    this.contextParams.providedBeanInstances = null;
    this.destroyed = true;
    this.logger.log(">> ag-Application Context shut down - component is dead");
  };

  return Context;
}(); // taken from: http://stackoverflow.com/questions/3362471/how-can-i-call-a-javascript-constructor-using-call-or-apply
// allows calling 'apply' on a constructor


function applyToConstructor(constructor, argArray) {
  var args = [null].concat(argArray);
  var factoryFunction = constructor.bind.apply(constructor, args);
  return new factoryFunction();
}

function PreConstruct(target, methodName, descriptor) {
  var props = getOrCreateProps(target.constructor);

  if (!props.postConstructMethods) {
    props.preConstructMethods = [];
  }

  props.preConstructMethods.push(methodName);
}

function PostConstruct(target, methodName, descriptor) {
  var props = getOrCreateProps(target.constructor);

  if (!props.postConstructMethods) {
    props.postConstructMethods = [];
  }

  props.postConstructMethods.push(methodName);
}

function PreDestroy(target, methodName, descriptor) {
  var props = getOrCreateProps(target.constructor);

  if (!props.preDestroyMethods) {
    props.preDestroyMethods = [];
  }

  props.preDestroyMethods.push(methodName);
}

function Bean(beanName) {
  return function (classConstructor) {
    var props = getOrCreateProps(classConstructor);
    props.beanName = beanName;
  };
}

function Autowired(name) {
  return function (target, propertyKey, descriptor) {
    autowiredFunc(target, name, false, target, propertyKey, null);
  };
}

function Optional(name) {
  return function (target, propertyKey, descriptor) {
    autowiredFunc(target, name, true, target, propertyKey, null);
  };
}

function autowiredFunc(target, name, optional, classPrototype, methodOrAttributeName, index) {
  if (name === null) {
    console.error("ag-Grid: Autowired name should not be null");
    return;
  }

  if (typeof index === "number") {
    console.error("ag-Grid: Autowired should be on an attribute");
    return;
  } // it's an attribute on the class


  var props = getOrCreateProps(target.constructor);

  if (!props.agClassAttributes) {
    props.agClassAttributes = [];
  }

  props.agClassAttributes.push({
    attributeName: methodOrAttributeName,
    beanName: name,
    optional: optional
  });
}

function Qualifier(name) {
  return function (classPrototype, methodOrAttributeName, index) {
    var constructor = typeof classPrototype == "function" ? classPrototype : classPrototype.constructor;
    var props;

    if (typeof index === "number") {
      // it's a parameter on a method
      var methodName = void 0;

      if (methodOrAttributeName) {
        props = getOrCreateProps(constructor);
        methodName = methodOrAttributeName;
      } else {
        props = getOrCreateProps(constructor);
        methodName = "agConstructor";
      }

      if (!props.autowireMethods) {
        props.autowireMethods = {};
      }

      if (!props.autowireMethods[methodName]) {
        props.autowireMethods[methodName] = {};
      }

      props.autowireMethods[methodName][index] = name;
    }
  };
}

function getOrCreateProps(target) {
  if (!target.hasOwnProperty("__agBeanMetaData")) {
    target.__agBeanMetaData = {};
  }

  return target.__agBeanMetaData;
}
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __decorate = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var __param = undefined && undefined.__param || function (paramIndex, decorator) {
  return function (target, key) {
    decorator(target, key, paramIndex);
  };
};

var EventService =
/** @class */
function () {
  function EventService() {
    this.allSyncListeners = new Map();
    this.allAsyncListeners = new Map();
    this.globalSyncListeners = new Set();
    this.globalAsyncListeners = new Set();
    this.asyncFunctionsQueue = [];
    this.scheduled = false; // using an object performs better than a Set for the number of different events we have

    this.firedEvents = {};
  }

  EventService_1 = EventService; // because this class is used both inside the context and outside the context, we do not
  // use autowired attributes, as that would be confusing, as sometimes the attributes
  // would be wired, and sometimes not.
  //
  // the global event servers used by ag-Grid is autowired by the context once, and this
  // setBeans method gets called once.
  //
  // the times when this class is used outside of the context (eg RowNode has an instance of this
  // class) then it is not a bean, and this setBeans method is not called.

  EventService.prototype.setBeans = function (loggerFactory, gridOptionsWrapper, globalEventListener) {
    if (globalEventListener === void 0) {
      globalEventListener = null;
    }

    this.logger = loggerFactory.create('EventService');

    if (globalEventListener) {
      var async = gridOptionsWrapper.useAsyncEvents();
      this.addGlobalListener(globalEventListener, async);
    }
  };

  EventService.prototype.getListeners = function (eventType, async) {
    var listenerMap = async ? this.allAsyncListeners : this.allSyncListeners;
    var listeners = listenerMap.get(eventType);

    if (!listeners) {
      listeners = new Set();
      listenerMap.set(eventType, listeners);
    }

    return listeners;
  };

  EventService.prototype.addEventListener = function (eventType, listener, async) {
    if (async === void 0) {
      async = false;
    }

    this.getListeners(eventType, async).add(listener);
  };

  EventService.prototype.removeEventListener = function (eventType, listener, async) {
    if (async === void 0) {
      async = false;
    }

    this.getListeners(eventType, async).delete(listener);
  }; // for some events, it's important that the model gets to hear about them before the view,
  // as the model may need to update before the view works on the info. if you register
  // via this method, you get notified before the view parts


  EventService.prototype.addModalPriorityEventListener = function (eventType, listener, async) {
    if (async === void 0) {
      async = false;
    }

    this.addEventListener(eventType + EventService_1.PRIORITY, listener, async);
  };

  EventService.prototype.addGlobalListener = function (listener, async) {
    if (async === void 0) {
      async = false;
    }

    (async ? this.globalAsyncListeners : this.globalSyncListeners).add(listener);
  };

  EventService.prototype.removeGlobalListener = function (listener, async) {
    if (async === void 0) {
      async = false;
    }

    (async ? this.globalAsyncListeners : this.globalSyncListeners).delete(listener);
  };

  EventService.prototype.dispatchEvent = function (event) {
    this.dispatchToListeners(event, true);
    this.dispatchToListeners(event, false);
    this.firedEvents[event.type] = true;
  };

  EventService.prototype.dispatchEventOnce = function (event) {
    if (!this.firedEvents[event.type]) {
      this.dispatchEvent(event);
    }
  };

  EventService.prototype.dispatchToListeners = function (event, async) {
    var _this = this;

    var eventType = event.type;

    var processEventListeners = function processEventListeners(listeners) {
      return listeners.forEach(function (listener) {
        if (async) {
          _this.dispatchAsync(function () {
            return listener(event);
          });
        } else {
          listener(event);
        }
      });
    }; // PRIORITY events should be processed first


    processEventListeners(this.getListeners(eventType + EventService_1.PRIORITY, async));
    processEventListeners(this.getListeners(eventType, async));
    var globalListeners = async ? this.globalAsyncListeners : this.globalSyncListeners;
    globalListeners.forEach(function (listener) {
      if (async) {
        _this.dispatchAsync(function () {
          return listener(eventType, event);
        });
      } else {
        listener(eventType, event);
      }
    });
  }; // this gets called inside the grid's thread, for each event that it
  // wants to set async. the grid then batches the events into one setTimeout()
  // because setTimeout() is an expensive operation. ideally we would have
  // each event in it's own setTimeout(), but we batch for performance.


  EventService.prototype.dispatchAsync = function (func) {
    // add to the queue for executing later in the next VM turn
    this.asyncFunctionsQueue.push(func); // check if timeout is already scheduled. the first time the grid calls
    // this within it's thread turn, this should be false, so it will schedule
    // the 'flush queue' method the first time it comes here. then the flag is
    // set to 'true' so it will know it's already scheduled for subsequent calls.

    if (!this.scheduled) {
      // if not scheduled, schedule one
      window.setTimeout(this.flushAsyncQueue.bind(this), 0); // mark that it is scheduled

      this.scheduled = true;
    }
  }; // this happens in the next VM turn only, and empties the queue of events


  EventService.prototype.flushAsyncQueue = function () {
    this.scheduled = false; // we take a copy, because the event listener could be using
    // the grid, which would cause more events, which would be potentially
    // added to the queue, so safe to take a copy, the new events will
    // get executed in a later VM turn rather than risk updating the
    // queue as we are flushing it.

    var queueCopy = this.asyncFunctionsQueue.slice();
    this.asyncFunctionsQueue = []; // execute the queue

    queueCopy.forEach(function (func) {
      return func();
    });
  };

  var EventService_1; // this is an old idea niall had, should really take it out, was to do with ordering who gets to process
  // events first, to give model and service objects preference over the view

  EventService.PRIORITY = '-P1';

  __decorate([__param(0, Qualifier('loggerFactory')), __param(1, Qualifier('gridOptionsWrapper')), __param(2, Qualifier('globalEventListener'))], EventService.prototype, "setBeans", null);

  EventService = EventService_1 = __decorate([Bean('eventService')], EventService);
  return EventService;
}();
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


(function (ModuleNames) {
  // when using modules, user references this
  ModuleNames["CommunityCoreModule"] = "@ag-grid-community/core"; // when not using modules, user references this

  ModuleNames["CommunityAllModules"] = "@ag-grid-community/all"; // community modules

  ModuleNames["InfiniteRowModelModule"] = "@ag-grid-community/infinite-row-model";
  ModuleNames["ClientSideRowModelModule"] = "@ag-grid-community/client-side-row-model";
  ModuleNames["CsvExportModule"] = "@ag-grid-community/csv-export";
  ModuleNames["RowNodeCache"] = "@ag-grid-community/row-node-cache"; // enterprise core - users never import on this, but other enterprise modules do

  ModuleNames["EnterpriseCoreModule"] = "@ag-grid-enterprise/core"; // when not using modules, user references this

  ModuleNames["EnterpriseAllModules"] = "@ag-grid-enterprise/all"; // enterprise modules

  ModuleNames["RowGroupingModule"] = "@ag-grid-enterprise/row-grouping";
  ModuleNames["ColumnToolPanelModule"] = "@ag-grid-enterprise/column-tool-panel";
  ModuleNames["FiltersToolPanelModule"] = "@ag-grid-enterprise/filters-tool-panel";
  ModuleNames["MenuModule"] = "@ag-grid-enterprise/menu";
  ModuleNames["SetFilterModule"] = "@ag-grid-enterprise/set-filter";
  ModuleNames["StatusBarModule"] = "@ag-grid-enterprise/status-bar";
  ModuleNames["SideBarModule"] = "@ag-grid-enterprise/side-bar";
  ModuleNames["RangeSelectionModule"] = "@ag-grid-enterprise/range-selection";
  ModuleNames["MasterDetailModule"] = "@ag-grid-enterprise/master-detail";
  ModuleNames["RichSelectModule"] = "@ag-grid-enterprise/rich-select";
  ModuleNames["GridChartsModule"] = "@ag-grid-enterprise/charts";
  ModuleNames["ViewportRowModelModule"] = "@ag-grid-enterprise/viewport-row-model";
  ModuleNames["ServerSideRowModelModule"] = "@ag-grid-enterprise/server-side-row-model";
  ModuleNames["ExcelExportModule"] = "@ag-grid-enterprise/excel-export";
  ModuleNames["ClipboardModule"] = "@ag-grid-enterprise/clipboard"; // framework wrappers currently don't provide beans, comps etc, so no need to be modules,
  // however i argue they should be as in theory they 'could' provide beans etc

  ModuleNames["AngularModule"] = "@ag-grid-community/angular";
  ModuleNames["ReactModule"] = "@ag-grid-community/react";
  ModuleNames["VueModule"] = "@ag-grid-community/vue";
  ModuleNames["PolymerModule"] = "@ag-grid-community/polymer"; // and then this, which is definitely not a grid module, as it should not have any dependency
  // on the grid (ie shouldn't even reference the Module interface)
  // ChartsModule = "@ag-grid-community/charts-core",
})(exports.ModuleNames || (exports.ModuleNames = {}));
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var ModuleRegistry =
/** @class */
function () {
  function ModuleRegistry() {}

  ModuleRegistry.register = function (module) {
    ModuleRegistry.modulesMap[module.moduleName] = module;
  };

  ModuleRegistry.registerModules = function (modules) {
    if (!modules) {
      return;
    }

    modules.forEach(ModuleRegistry.register);
  };

  ModuleRegistry.assertRegistered = function (moduleName, reason) {
    if (this.isRegistered(moduleName)) {
      return true;
    }

    var warningKey = reason + moduleName;
    var warningMessage = "ag-Grid: unable to use " + reason + " as module " + moduleName + " is not present. " + ("You need to load the module with: import \"" + moduleName + "\"");

    _.doOnce(function () {
      console.warn(warningMessage);
    }, warningKey);

    return false;
  };

  ModuleRegistry.isRegistered = function (moduleName) {
    return !!ModuleRegistry.modulesMap[moduleName];
  };

  ModuleRegistry.getRegisteredModules = function () {
    return _.values(ModuleRegistry.modulesMap);
  }; // having in a map a) removes duplicates and b) allows fast lookup


  ModuleRegistry.modulesMap = {};
  return ModuleRegistry;
}();
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __decorate$1 = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
}; // Wrapper around a user provide column definition. The grid treats the column definition as ready only.
// This class contains all the runtime information about a column, plus some logic (the definition has no logic).
// This class implements both interfaces ColumnGroupChild and OriginalColumnGroupChild as the class can
// appear as a child of either the original tree or the displayed tree. However the relevant group classes
// for each type only implements one, as each group can only appear in it's associated tree (eg OriginalColumnGroup
// can only appear in OriginalColumn tree).


var Column =
/** @class */
function () {
  function Column(colDef, userProvidedColDef, colId, primary) {
    this.moving = false;
    this.menuVisible = false;
    this.filterActive = false;
    this.eventService = new EventService();
    this.rowGroupActive = false;
    this.pivotActive = false;
    this.aggregationActive = false;
    this.colDef = colDef;
    this.userProvidedColDef = userProvidedColDef;
    this.visible = !colDef.hide;
    this.sort = colDef.sort;
    this.sortedAt = colDef.sortedAt;
    this.colId = colId;
    this.primary = primary;
  } // gets called when user provides an alternative colDef, eg


  Column.prototype.setColDef = function (colDef, userProvidedColDef) {
    this.colDef = colDef;
    this.userProvidedColDef = userProvidedColDef;
  };

  Column.prototype.getUserProvidedColDef = function () {
    return this.userProvidedColDef;
  };

  Column.prototype.setParent = function (parent) {
    this.parent = parent;
  };

  Column.prototype.getParent = function () {
    return this.parent;
  };

  Column.prototype.setOriginalParent = function (originalParent) {
    this.originalParent = originalParent;
  };

  Column.prototype.getOriginalParent = function () {
    return this.originalParent;
  }; // this is done after constructor as it uses gridOptionsWrapper


  Column.prototype.initialise = function () {
    this.setPinned(this.colDef.pinned);
    var minColWidth = this.gridOptionsWrapper.getMinColWidth();
    var maxColWidth = this.gridOptionsWrapper.getMaxColWidth();

    if (this.colDef.minWidth) {
      this.minWidth = this.colDef.minWidth;
    } else {
      this.minWidth = minColWidth;
    }

    if (this.colDef.maxWidth) {
      this.maxWidth = this.colDef.maxWidth;
    } else {
      this.maxWidth = maxColWidth;
    }

    if (this.colDef.flex) {
      this.flex = this.colDef.flex;
    }

    this.actualWidth = this.columnUtils.calculateColInitialWidth(this.colDef);
    var suppressDotNotation = this.gridOptionsWrapper.isSuppressFieldDotNotation();
    this.fieldContainsDots = _.exists(this.colDef.field) && this.colDef.field.indexOf('.') >= 0 && !suppressDotNotation;
    this.tooltipFieldContainsDots = _.exists(this.colDef.tooltipField) && this.colDef.tooltipField.indexOf('.') >= 0 && !suppressDotNotation;
    this.validate();
  };

  Column.prototype.isEmptyGroup = function () {
    return false;
  };

  Column.prototype.isRowGroupDisplayed = function (colId) {
    if (_.missing(this.colDef) || _.missing(this.colDef.showRowGroup)) {
      return false;
    }

    var showingAllGroups = this.colDef.showRowGroup === true;
    var showingThisGroup = this.colDef.showRowGroup === colId;
    return showingAllGroups || showingThisGroup;
  };

  Column.prototype.getUniqueId = function () {
    return this.getId();
  };

  Column.prototype.isPrimary = function () {
    return this.primary;
  };

  Column.prototype.isFilterAllowed = function () {
    // filter defined means it's a string, class or true.
    // if its false, null or undefined then it's false.
    var filterDefined = !!this.colDef.filter || !!this.colDef.filterFramework;
    return this.primary && filterDefined;
  };

  Column.prototype.isFieldContainsDots = function () {
    return this.fieldContainsDots;
  };

  Column.prototype.isTooltipFieldContainsDots = function () {
    return this.tooltipFieldContainsDots;
  };

  Column.prototype.validate = function () {
    var colDefAny = this.colDef;

    function warnOnce(msg, key, obj) {
      _.doOnce(function () {
        if (obj) {
          console.warn(msg, obj);
        } else {
          _.doOnce(function () {
            return console.warn(msg);
          }, key);
        }
      }, key);
    }

    if (!ModuleRegistry.isRegistered(exports.ModuleNames.RowGroupingModule)) {
      var rowGroupingItems = ['enableRowGroup', 'rowGroup', 'rowGroupIndex', 'enablePivot', 'enableValue', 'pivot', 'pivotIndex', 'aggFunc'];
      rowGroupingItems.forEach(function (item) {
        if (_.exists(colDefAny[item])) {
          warnOnce("ag-Grid: " + item + " is only valid with module Row Grouping, your column definition " + ("should not have " + item), 'ColumnRowGroupingMissing' + item);
        }
      });
    }

    if (!ModuleRegistry.isRegistered(exports.ModuleNames.RichSelectModule)) {
      if (this.colDef.cellEditor === 'agRichSelect' || this.colDef.cellEditor === 'agRichSelectCellEditor') {
        warnOnce("ag-Grid: " + this.colDef.cellEditor + " can only be used with " + ("module " + exports.ModuleNames.RichSelectModule), 'ColumnRichSelectMissing');
      }
    }

    if (this.gridOptionsWrapper.isTreeData()) {
      var itemsNotAllowedWithTreeData = ['rowGroup', 'rowGroupIndex', 'pivot', 'pivotIndex'];
      itemsNotAllowedWithTreeData.forEach(function (item) {
        if (_.exists(colDefAny[item])) {
          warnOnce("ag-Grid: " + item + " is not possible when doing tree data, your " + ("column definition should not have " + item), 'TreeDataCannotRowGroup');
        }
      });
    }

    if (_.exists(this.colDef.width) && typeof this.colDef.width !== 'number') {
      warnOnce('ag-Grid: colDef.width should be a number, not ' + typeof this.colDef.width, 'ColumnCheck_asdfawef');
    }

    if (_.get(this, 'colDef.cellRendererParams.restrictToOneGroup', null)) {
      warnOnce('ag-Grid: Since ag-grid 11.0.0 cellRendererParams.restrictToOneGroup is deprecated. You should use showRowGroup', 'ColumnCheck_sksldjf');
    }

    if (_.get(this, 'colDef.cellRendererParams.keyMap', null)) {
      warnOnce('ag-Grid: Since ag-grid 11.0.0 cellRendererParams.keyMap is deprecated. You should use colDef.keyCreator', 'ColumnCheck_ieiruhgdf');
    }

    if (_.get(this, 'colDef.cellRendererParams.keyMap', null)) {
      warnOnce('ag-Grid: Since ag-grid 11.0.0 cellRendererParams.keyMap is deprecated. You should use colDef.keyCreator', 'ColumnCheck_uitolghj');
    }

    if (colDefAny.floatingCellRenderer) {
      warnOnce('ag-Grid: since v11, floatingCellRenderer is now pinnedRowCellRenderer', 'ColumnCheck_soihwewe');
      this.colDef.pinnedRowCellRenderer = colDefAny.floatingCellRenderer;
    }

    if (colDefAny.floatingRendererFramework) {
      warnOnce('ag-Grid: since v11, floatingRendererFramework is now pinnedRowCellRendererFramework', 'ColumnCheck_zdkiouhwer');
      this.colDef.pinnedRowCellRendererFramework = colDefAny.floatingRendererFramework;
    }

    if (colDefAny.floatingRendererParams) {
      console.warn('ag-Grid: since v11, floatingRendererParams is now pinnedRowCellRendererParams', 'ColumnCheck_retiuhjs');
      this.colDef.pinnedRowCellRendererParams = colDefAny.floatingRendererParams;
    }

    if (colDefAny.floatingValueFormatter) {
      warnOnce('ag-Grid: since v11, floatingValueFormatter is now pinnedRowValueFormatter', 'ColumnCheck_qwroeihjdf');
      this.colDef.pinnedRowValueFormatter = colDefAny.floatingValueFormatter;
    }

    if (colDefAny.cellFormatter) {
      warnOnce('ag-Grid: since v12, cellFormatter is now valueFormatter', 'ColumnCheck_eoireknml');

      if (_.missing(this.colDef.valueFormatter)) {
        this.colDef.valueFormatter = colDefAny.cellFormatter;
      }
    }

    if (colDefAny.headerCellTemplate) {
      warnOnce('ag-Grid: since v15, headerCellTemplate is gone, use header component instead.', 'ColumnCheck_eroihxcm');
    }

    if (colDefAny.headerCellRenderer) {
      warnOnce('ag-Grid: since v15, headerCellRenderer is gone, use header component instead.', 'ColumnCheck_terteuh');
    }

    if (colDefAny.volatile) {
      warnOnce('ag-Grid: since v16, colDef.volatile is gone, please check refresh docs on how to refresh specific cells.', 'ColumnCheck_weoihjxcv');
    }

    if (colDefAny.suppressSorting) {
      warnOnce("ag-Grid: since v20, colDef.suppressSorting is gone, instead use colDef.sortable=false.", 'ColumnCheck_43ljrer', this.colDef);
      this.colDef.sortable = false;
    }

    if (colDefAny.suppressFilter) {
      warnOnce("ag-Grid: since v20, colDef.suppressFilter is gone, instead use colDef.filter=false.", 'ColumnCheck_erlkhfdm', this.colDef);
      this.colDef.filter = false;
    }

    if (colDefAny.suppressResize) {
      warnOnce("ag-Grid: since v20, colDef.suppressResize is gone, instead use colDef.resizable=false.", 'ColumnCheck_weoihjxcv', this.colDef);
      this.colDef.resizable = false;
    }

    if (colDefAny.tooltip) {
      warnOnce("ag-Grid: since v20.1, colDef.tooltip is gone, instead use colDef.tooltipValueGetter.", 'ColumnCheck_adslknjwef', this.colDef);
      this.colDef.tooltipValueGetter = colDefAny.tooltip;
    }

    if (colDefAny.suppressToolPanel) {
      warnOnce("ag-Grid: since v22, colDef.suppressToolPanel is gone, instead use suppressColumnsToolPanel / suppressFiltersToolPanel.", 'ColumnCheck_weihjlsjkdf', this.colDef);
      this.colDef.suppressColumnsToolPanel = true;
    }
  };

  Column.prototype.addEventListener = function (eventType, listener) {
    this.eventService.addEventListener(eventType, listener);
  };

  Column.prototype.removeEventListener = function (eventType, listener) {
    this.eventService.removeEventListener(eventType, listener);
  };

  Column.prototype.createIsColumnFuncParams = function (rowNode) {
    return {
      node: rowNode,
      data: rowNode.data,
      column: this,
      colDef: this.colDef,
      context: this.gridOptionsWrapper.getContext(),
      api: this.gridOptionsWrapper.getApi(),
      columnApi: this.gridOptionsWrapper.getColumnApi()
    };
  };

  Column.prototype.isSuppressNavigable = function (rowNode) {
    // if boolean set, then just use it
    if (typeof this.colDef.suppressNavigable === 'boolean') {
      return this.colDef.suppressNavigable;
    } // if function, then call the function to find out


    if (typeof this.colDef.suppressNavigable === 'function') {
      var params = this.createIsColumnFuncParams(rowNode);
      var userFunc = this.colDef.suppressNavigable;
      return userFunc(params);
    }

    return false;
  };

  Column.prototype.isCellEditable = function (rowNode) {
    // only allow editing of groups if the user has this option enabled
    if (rowNode.group && !this.gridOptionsWrapper.isEnableGroupEdit()) {
      return false;
    }

    return this.isColumnFunc(rowNode, this.colDef.editable);
  };

  Column.prototype.isRowDrag = function (rowNode) {
    return this.isColumnFunc(rowNode, this.colDef.rowDrag);
  };

  Column.prototype.isDndSource = function (rowNode) {
    return this.isColumnFunc(rowNode, this.colDef.dndSource);
  };

  Column.prototype.isCellCheckboxSelection = function (rowNode) {
    return this.isColumnFunc(rowNode, this.colDef.checkboxSelection);
  };

  Column.prototype.isSuppressPaste = function (rowNode) {
    return this.isColumnFunc(rowNode, this.colDef ? this.colDef.suppressPaste : null);
  };

  Column.prototype.isResizable = function () {
    return this.colDef.resizable === true;
  };

  Column.prototype.isColumnFunc = function (rowNode, value) {
    // if boolean set, then just use it
    if (typeof value === 'boolean') {
      return value;
    } // if function, then call the function to find out


    if (typeof value === 'function') {
      var params = this.createIsColumnFuncParams(rowNode);
      var editableFunc = value;
      return editableFunc(params);
    }

    return false;
  };

  Column.prototype.setMoving = function (moving, source) {
    if (source === void 0) {
      source = "api";
    }

    this.moving = moving;
    this.eventService.dispatchEvent(this.createColumnEvent(Column.EVENT_MOVING_CHANGED, source));
  };

  Column.prototype.createColumnEvent = function (type, source) {
    return {
      api: this.gridApi,
      columnApi: this.columnApi,
      type: type,
      column: this,
      columns: [this],
      source: source
    };
  };

  Column.prototype.isMoving = function () {
    return this.moving;
  };

  Column.prototype.getSort = function () {
    return this.sort;
  };

  Column.prototype.setSort = function (sort, source) {
    if (source === void 0) {
      source = "api";
    }

    if (this.sort !== sort) {
      this.sort = sort;
      this.eventService.dispatchEvent(this.createColumnEvent(Column.EVENT_SORT_CHANGED, source));
    }
  };

  Column.prototype.setMenuVisible = function (visible, source) {
    if (source === void 0) {
      source = "api";
    }

    if (this.menuVisible !== visible) {
      this.menuVisible = visible;
      this.eventService.dispatchEvent(this.createColumnEvent(Column.EVENT_MENU_VISIBLE_CHANGED, source));
    }
  };

  Column.prototype.isMenuVisible = function () {
    return this.menuVisible;
  };

  Column.prototype.isSortAscending = function () {
    return this.sort === Constants.SORT_ASC;
  };

  Column.prototype.isSortDescending = function () {
    return this.sort === Constants.SORT_DESC;
  };

  Column.prototype.isSortNone = function () {
    return _.missing(this.sort);
  };

  Column.prototype.isSorting = function () {
    return _.exists(this.sort);
  };

  Column.prototype.getSortedAt = function () {
    return this.sortedAt;
  };

  Column.prototype.setSortedAt = function (sortedAt) {
    this.sortedAt = sortedAt;
  };

  Column.prototype.setAggFunc = function (aggFunc) {
    this.aggFunc = aggFunc;
  };

  Column.prototype.getAggFunc = function () {
    return this.aggFunc;
  };

  Column.prototype.getLeft = function () {
    return this.left;
  };

  Column.prototype.getOldLeft = function () {
    return this.oldLeft;
  };

  Column.prototype.getRight = function () {
    return this.left + this.actualWidth;
  };

  Column.prototype.setLeft = function (left, source) {
    if (source === void 0) {
      source = "api";
    }

    this.oldLeft = this.left;

    if (this.left !== left) {
      this.left = left;
      this.eventService.dispatchEvent(this.createColumnEvent(Column.EVENT_LEFT_CHANGED, source));
    }
  };

  Column.prototype.isFilterActive = function () {
    return this.filterActive;
  }; // additionalEventAttributes is used by provided simple floating filter, so it can add 'floatingFilter=true' to the event


  Column.prototype.setFilterActive = function (active, source, additionalEventAttributes) {
    if (source === void 0) {
      source = "api";
    }

    if (this.filterActive !== active) {
      this.filterActive = active;
      this.eventService.dispatchEvent(this.createColumnEvent(Column.EVENT_FILTER_ACTIVE_CHANGED, source));
    }

    var filterChangedEvent = this.createColumnEvent(Column.EVENT_FILTER_CHANGED, source);

    if (additionalEventAttributes) {
      _.mergeDeep(filterChangedEvent, additionalEventAttributes);
    }

    this.eventService.dispatchEvent(filterChangedEvent);
  };

  Column.prototype.setPinned = function (pinned) {
    if (pinned === true || pinned === Constants.PINNED_LEFT) {
      this.pinned = Constants.PINNED_LEFT;
    } else if (pinned === Constants.PINNED_RIGHT) {
      this.pinned = Constants.PINNED_RIGHT;
    } else {
      this.pinned = null;
    }
  };

  Column.prototype.setFirstRightPinned = function (firstRightPinned, source) {
    if (source === void 0) {
      source = "api";
    }

    if (this.firstRightPinned !== firstRightPinned) {
      this.firstRightPinned = firstRightPinned;
      this.eventService.dispatchEvent(this.createColumnEvent(Column.EVENT_FIRST_RIGHT_PINNED_CHANGED, source));
    }
  };

  Column.prototype.setLastLeftPinned = function (lastLeftPinned, source) {
    if (source === void 0) {
      source = "api";
    }

    if (this.lastLeftPinned !== lastLeftPinned) {
      this.lastLeftPinned = lastLeftPinned;
      this.eventService.dispatchEvent(this.createColumnEvent(Column.EVENT_LAST_LEFT_PINNED_CHANGED, source));
    }
  };

  Column.prototype.isFirstRightPinned = function () {
    return this.firstRightPinned;
  };

  Column.prototype.isLastLeftPinned = function () {
    return this.lastLeftPinned;
  };

  Column.prototype.isPinned = function () {
    return this.pinned === Constants.PINNED_LEFT || this.pinned === Constants.PINNED_RIGHT;
  };

  Column.prototype.isPinnedLeft = function () {
    return this.pinned === Constants.PINNED_LEFT;
  };

  Column.prototype.isPinnedRight = function () {
    return this.pinned === Constants.PINNED_RIGHT;
  };

  Column.prototype.getPinned = function () {
    return this.pinned;
  };

  Column.prototype.setVisible = function (visible, source) {
    if (source === void 0) {
      source = "api";
    }

    var newValue = visible === true;

    if (this.visible !== newValue) {
      this.visible = newValue;
      this.eventService.dispatchEvent(this.createColumnEvent(Column.EVENT_VISIBLE_CHANGED, source));
    }
  };

  Column.prototype.isVisible = function () {
    return this.visible;
  };

  Column.prototype.getColDef = function () {
    return this.colDef;
  };

  Column.prototype.getColumnGroupShow = function () {
    return this.colDef.columnGroupShow;
  };

  Column.prototype.getColId = function () {
    return this.colId;
  };

  Column.prototype.getId = function () {
    return this.getColId();
  };

  Column.prototype.getDefinition = function () {
    return this.colDef;
  };

  Column.prototype.getActualWidth = function () {
    return this.actualWidth;
  };

  Column.prototype.createBaseColDefParams = function (rowNode) {
    var params = {
      node: rowNode,
      data: rowNode.data,
      colDef: this.colDef,
      column: this,
      api: this.gridOptionsWrapper.getApi(),
      columnApi: this.gridOptionsWrapper.getColumnApi(),
      context: this.gridOptionsWrapper.getContext()
    };
    return params;
  };

  Column.prototype.getColSpan = function (rowNode) {
    if (_.missing(this.colDef.colSpan)) {
      return 1;
    }

    var params = this.createBaseColDefParams(rowNode);
    var colSpan = this.colDef.colSpan(params); // colSpan must be number equal to or greater than 1

    return Math.max(colSpan, 1);
  };

  Column.prototype.getRowSpan = function (rowNode) {
    if (_.missing(this.colDef.rowSpan)) {
      return 1;
    }

    var params = this.createBaseColDefParams(rowNode);
    var rowSpan = this.colDef.rowSpan(params); // rowSpan must be number equal to or greater than 1

    return Math.max(rowSpan, 1);
  };

  Column.prototype.setActualWidth = function (actualWidth, source) {
    if (source === void 0) {
      source = "api";
    }

    if (this.minWidth != null) {
      actualWidth = Math.max(actualWidth, this.minWidth);
    }

    if (this.maxWidth != null) {
      actualWidth = Math.min(actualWidth, this.maxWidth);
    }

    if (this.actualWidth !== actualWidth) {
      // disable flex for this column if it was manually resized.
      if (this.flex && source !== 'flex') {
        this.flex = 0;
      }

      this.actualWidth = actualWidth;
      this.eventService.dispatchEvent(this.createColumnEvent(Column.EVENT_WIDTH_CHANGED, source));
    }
  };

  Column.prototype.isGreaterThanMax = function (width) {
    if (this.maxWidth != null) {
      return width > this.maxWidth;
    }

    return false;
  };

  Column.prototype.getMinWidth = function () {
    return this.minWidth;
  };

  Column.prototype.getMaxWidth = function () {
    return this.maxWidth;
  };

  Column.prototype.getFlex = function () {
    return this.flex || 0;
  };

  Column.prototype.setMinimum = function (source) {
    if (source === void 0) {
      source = "api";
    }

    this.setActualWidth(this.minWidth, source);
  };

  Column.prototype.setRowGroupActive = function (rowGroup, source) {
    if (source === void 0) {
      source = "api";
    }

    if (this.rowGroupActive !== rowGroup) {
      this.rowGroupActive = rowGroup;
      this.eventService.dispatchEvent(this.createColumnEvent(Column.EVENT_ROW_GROUP_CHANGED, source));
    }
  };

  Column.prototype.isRowGroupActive = function () {
    return this.rowGroupActive;
  };

  Column.prototype.setPivotActive = function (pivot, source) {
    if (source === void 0) {
      source = "api";
    }

    if (this.pivotActive !== pivot) {
      this.pivotActive = pivot;
      this.eventService.dispatchEvent(this.createColumnEvent(Column.EVENT_PIVOT_CHANGED, source));
    }
  };

  Column.prototype.isPivotActive = function () {
    return this.pivotActive;
  };

  Column.prototype.isAnyFunctionActive = function () {
    return this.isPivotActive() || this.isRowGroupActive() || this.isValueActive();
  };

  Column.prototype.isAnyFunctionAllowed = function () {
    return this.isAllowPivot() || this.isAllowRowGroup() || this.isAllowValue();
  };

  Column.prototype.setValueActive = function (value, source) {
    if (source === void 0) {
      source = "api";
    }

    if (this.aggregationActive !== value) {
      this.aggregationActive = value;
      this.eventService.dispatchEvent(this.createColumnEvent(Column.EVENT_VALUE_CHANGED, source));
    }
  };

  Column.prototype.isValueActive = function () {
    return this.aggregationActive;
  };

  Column.prototype.isAllowPivot = function () {
    return this.colDef.enablePivot === true;
  };

  Column.prototype.isAllowValue = function () {
    return this.colDef.enableValue === true;
  };

  Column.prototype.isAllowRowGroup = function () {
    return this.colDef.enableRowGroup === true;
  };

  Column.prototype.getMenuTabs = function (defaultValues) {
    var menuTabs = this.getColDef().menuTabs;

    if (menuTabs == null) {
      menuTabs = defaultValues;
    }

    return menuTabs;
  }; // this used to be needed, as previous version of ag-grid had lockPosition as column state,
  // so couldn't depend on colDef version.


  Column.prototype.isLockPosition = function () {
    console.warn('ag-Grid: since v21, col.isLockPosition() should not be used, please use col.getColDef().lockPosition instead.');
    return this.colDef ? !!this.colDef.lockPosition : false;
  }; // this used to be needed, as previous version of ag-grid had lockVisible as column state,
  // so couldn't depend on colDef version.


  Column.prototype.isLockVisible = function () {
    console.warn('ag-Grid: since v21, col.isLockVisible() should not be used, please use col.getColDef().lockVisible instead.');
    return this.colDef ? !!this.colDef.lockVisible : false;
  }; // this used to be needed, as previous version of ag-grid had lockPinned as column state,
  // so couldn't depend on colDef version.


  Column.prototype.isLockPinned = function () {
    console.warn('ag-Grid: since v21, col.isLockPinned() should not be used, please use col.getColDef().lockPinned instead.');
    return this.colDef ? !!this.colDef.lockPinned : false;
  }; // + renderedHeaderCell - for making header cell transparent when moving


  Column.EVENT_MOVING_CHANGED = 'movingChanged'; // + renderedCell - changing left position

  Column.EVENT_LEFT_CHANGED = 'leftChanged'; // + renderedCell - changing width

  Column.EVENT_WIDTH_CHANGED = 'widthChanged'; // + renderedCell - for changing pinned classes

  Column.EVENT_LAST_LEFT_PINNED_CHANGED = 'lastLeftPinnedChanged';
  Column.EVENT_FIRST_RIGHT_PINNED_CHANGED = 'firstRightPinnedChanged'; // + renderedColumn - for changing visibility icon

  Column.EVENT_VISIBLE_CHANGED = 'visibleChanged'; // + every time the filter changes, used in the floating filters

  Column.EVENT_FILTER_CHANGED = 'filterChanged'; // + renderedHeaderCell - marks the header with filter icon

  Column.EVENT_FILTER_ACTIVE_CHANGED = 'filterActiveChanged'; // + renderedHeaderCell - marks the header with sort icon

  Column.EVENT_SORT_CHANGED = 'sortChanged';
  Column.EVENT_MENU_VISIBLE_CHANGED = 'menuVisibleChanged'; // + toolpanel, for gui updates

  Column.EVENT_ROW_GROUP_CHANGED = 'columnRowGroupChanged'; // + toolpanel, for gui updates

  Column.EVENT_PIVOT_CHANGED = 'columnPivotChanged'; // + toolpanel, for gui updates

  Column.EVENT_VALUE_CHANGED = 'columnValueChanged';

  __decorate$1([Autowired('gridOptionsWrapper')], Column.prototype, "gridOptionsWrapper", void 0);

  __decorate$1([Autowired('columnUtils')], Column.prototype, "columnUtils", void 0);

  __decorate$1([Autowired('columnApi')], Column.prototype, "columnApi", void 0);

  __decorate$1([Autowired('gridApi')], Column.prototype, "gridApi", void 0);

  __decorate$1([Autowired('context')], Column.prototype, "context", void 0);

  __decorate$1([PostConstruct], Column.prototype, "initialise", null);

  return Column;
}();
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __decorate$2 = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var ColumnGroup =
/** @class */
function () {
  function ColumnGroup(originalColumnGroup, groupId, instanceId, pinned) {
    // depends on the open/closed state of the group, only displaying columns are stored here
    this.displayedChildren = [];
    this.localEventService = new EventService();
    this.groupId = groupId;
    this.instanceId = instanceId;
    this.originalColumnGroup = originalColumnGroup;
    this.pinned = pinned;
  } // this is static, a it is used outside of this class


  ColumnGroup.createUniqueId = function (groupId, instanceId) {
    return groupId + '_' + instanceId;
  }; // as the user is adding and removing columns, the groups are recalculated.
  // this reset clears out all children, ready for children to be added again


  ColumnGroup.prototype.reset = function () {
    this.parent = null;
    this.children = null;
    this.displayedChildren = null;
  };

  ColumnGroup.prototype.getParent = function () {
    return this.parent;
  };

  ColumnGroup.prototype.setParent = function (parent) {
    this.parent = parent;
  };

  ColumnGroup.prototype.getUniqueId = function () {
    return ColumnGroup.createUniqueId(this.groupId, this.instanceId);
  };

  ColumnGroup.prototype.isEmptyGroup = function () {
    return this.displayedChildren.length === 0;
  };

  ColumnGroup.prototype.isMoving = function () {
    var allLeafColumns = this.getOriginalColumnGroup().getLeafColumns();

    if (!allLeafColumns || allLeafColumns.length === 0) {
      return false;
    }

    return allLeafColumns.every(function (col) {
      return col.isMoving();
    });
  };

  ColumnGroup.prototype.checkLeft = function () {
    // first get all children to setLeft, as it impacts our decision below
    this.displayedChildren.forEach(function (child) {
      if (child instanceof ColumnGroup) {
        child.checkLeft();
      }
    }); // set our left based on first displayed column

    if (this.displayedChildren.length > 0) {
      if (this.gridOptionsWrapper.isEnableRtl()) {
        var lastChild = _.last(this.displayedChildren);

        var lastChildLeft = lastChild.getLeft();
        this.setLeft(lastChildLeft);
      } else {
        var firstChildLeft = this.displayedChildren[0].getLeft();
        this.setLeft(firstChildLeft);
      }
    } else {
      // this should never happen, as if we have no displayed columns, then
      // this groups should not even exist.
      this.setLeft(null);
    }
  };

  ColumnGroup.prototype.getLeft = function () {
    return this.left;
  };

  ColumnGroup.prototype.getOldLeft = function () {
    return this.oldLeft;
  };

  ColumnGroup.prototype.setLeft = function (left) {
    this.oldLeft = left;

    if (this.left !== left) {
      this.left = left;
      this.localEventService.dispatchEvent(this.createAgEvent(ColumnGroup.EVENT_LEFT_CHANGED));
    }
  };

  ColumnGroup.prototype.getPinned = function () {
    return this.pinned;
  };

  ColumnGroup.prototype.createAgEvent = function (type) {
    return {
      type: type
    };
  };

  ColumnGroup.prototype.addEventListener = function (eventType, listener) {
    this.localEventService.addEventListener(eventType, listener);
  };

  ColumnGroup.prototype.removeEventListener = function (eventType, listener) {
    this.localEventService.removeEventListener(eventType, listener);
  };

  ColumnGroup.prototype.getGroupId = function () {
    return this.groupId;
  };

  ColumnGroup.prototype.getInstanceId = function () {
    return this.instanceId;
  };

  ColumnGroup.prototype.isChildInThisGroupDeepSearch = function (wantedChild) {
    var result = false;
    this.children.forEach(function (foundChild) {
      if (wantedChild === foundChild) {
        result = true;
      }

      if (foundChild instanceof ColumnGroup) {
        if (foundChild.isChildInThisGroupDeepSearch(wantedChild)) {
          result = true;
        }
      }
    });
    return result;
  };

  ColumnGroup.prototype.getActualWidth = function () {
    var groupActualWidth = 0;

    if (this.displayedChildren) {
      this.displayedChildren.forEach(function (child) {
        groupActualWidth += child.getActualWidth();
      });
    }

    return groupActualWidth;
  };

  ColumnGroup.prototype.isResizable = function () {
    if (!this.displayedChildren) {
      return false;
    } // if at least one child is resizable, then the group is resizable


    var result = false;
    this.displayedChildren.forEach(function (child) {
      if (child.isResizable()) {
        result = true;
      }
    });
    return result;
  };

  ColumnGroup.prototype.getMinWidth = function () {
    var result = 0;
    this.displayedChildren.forEach(function (groupChild) {
      result += groupChild.getMinWidth();
    });
    return result;
  };

  ColumnGroup.prototype.addChild = function (child) {
    if (!this.children) {
      this.children = [];
    }

    this.children.push(child);
  };

  ColumnGroup.prototype.getDisplayedChildren = function () {
    return this.displayedChildren;
  };

  ColumnGroup.prototype.getLeafColumns = function () {
    var result = [];
    this.addLeafColumns(result);
    return result;
  };

  ColumnGroup.prototype.getDisplayedLeafColumns = function () {
    var result = [];
    this.addDisplayedLeafColumns(result);
    return result;
  }; // why two methods here doing the same thing?


  ColumnGroup.prototype.getDefinition = function () {
    return this.originalColumnGroup.getColGroupDef();
  };

  ColumnGroup.prototype.getColGroupDef = function () {
    return this.originalColumnGroup.getColGroupDef();
  };

  ColumnGroup.prototype.isPadding = function () {
    return this.originalColumnGroup.isPadding();
  };

  ColumnGroup.prototype.isExpandable = function () {
    return this.originalColumnGroup.isExpandable();
  };

  ColumnGroup.prototype.isExpanded = function () {
    return this.originalColumnGroup.isExpanded();
  };

  ColumnGroup.prototype.setExpanded = function (expanded) {
    this.originalColumnGroup.setExpanded(expanded);
  };

  ColumnGroup.prototype.addDisplayedLeafColumns = function (leafColumns) {
    this.displayedChildren.forEach(function (child) {
      if (child instanceof Column) {
        leafColumns.push(child);
      } else if (child instanceof ColumnGroup) {
        child.addDisplayedLeafColumns(leafColumns);
      }
    });
  };

  ColumnGroup.prototype.addLeafColumns = function (leafColumns) {
    this.children.forEach(function (child) {
      if (child instanceof Column) {
        leafColumns.push(child);
      } else if (child instanceof ColumnGroup) {
        child.addLeafColumns(leafColumns);
      }
    });
  };

  ColumnGroup.prototype.getChildren = function () {
    return this.children;
  };

  ColumnGroup.prototype.getColumnGroupShow = function () {
    return this.originalColumnGroup.getColumnGroupShow();
  };

  ColumnGroup.prototype.getOriginalColumnGroup = function () {
    return this.originalColumnGroup;
  };

  ColumnGroup.prototype.calculateDisplayedColumns = function () {
    var _this = this; // clear out last time we calculated


    this.displayedChildren = [];
    var topLevelGroup = this; // find the column group that is controlling expandable. this is relevant when we have padding (empty)
    // groups, where the expandable is actually the first parent that is not a padding group.

    if (this.isPadding()) {
      while (topLevelGroup.getParent() && topLevelGroup.isPadding()) {
        topLevelGroup = topLevelGroup.getParent();
      }
    }

    var isExpandable = topLevelGroup.originalColumnGroup.isExpandable(); // it not expandable, everything is visible

    if (!isExpandable) {
      this.displayedChildren = this.children;
    } else {
      // Add cols based on columnGroupShow
      // Note - the below also adds padding groups, these are always added because they never have
      // colDef.columnGroupShow set.
      this.children.forEach(function (abstractColumn) {
        var headerGroupShow = abstractColumn.getColumnGroupShow();

        switch (headerGroupShow) {
          case ColumnGroup.HEADER_GROUP_SHOW_OPEN:
            // when set to open, only show col if group is open
            if (topLevelGroup.originalColumnGroup.isExpanded()) {
              _this.displayedChildren.push(abstractColumn);
            }

            break;

          case ColumnGroup.HEADER_GROUP_SHOW_CLOSED:
            // when set to open, only show col if group is open
            if (!topLevelGroup.originalColumnGroup.isExpanded()) {
              _this.displayedChildren.push(abstractColumn);
            }

            break;

          default:
            // if this abstractColumn is padding, we just want to add it
            // to the displayedChildren list if it has displayedChildren itself.
            if (!(abstractColumn instanceof ColumnGroup && abstractColumn.isPadding() && !abstractColumn.displayedChildren.length)) {
              _this.displayedChildren.push(abstractColumn);
            }

            break;
        }
      });
    }

    this.localEventService.dispatchEvent(this.createAgEvent(ColumnGroup.EVENT_DISPLAYED_CHILDREN_CHANGED));
  };

  ColumnGroup.HEADER_GROUP_SHOW_OPEN = 'open';
  ColumnGroup.HEADER_GROUP_SHOW_CLOSED = 'closed';
  ColumnGroup.HEADER_GROUP_PADDING = 'padding';
  ColumnGroup.EVENT_LEFT_CHANGED = 'leftChanged';
  ColumnGroup.EVENT_DISPLAYED_CHILDREN_CHANGED = 'displayedChildrenChanged';

  __decorate$2([Autowired('gridOptionsWrapper')], ColumnGroup.prototype, "gridOptionsWrapper", void 0);

  return ColumnGroup;
}();
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var OriginalColumnGroup =
/** @class */
function () {
  function OriginalColumnGroup(colGroupDef, groupId, padding, level) {
    this.localEventService = new EventService();
    this.expandable = false;
    this.colGroupDef = colGroupDef;
    this.groupId = groupId;
    this.expanded = colGroupDef && !!colGroupDef.openByDefault;
    this.padding = padding;
    this.level = level;
  }

  OriginalColumnGroup.prototype.setOriginalParent = function (originalParent) {
    this.originalParent = originalParent;
  };

  OriginalColumnGroup.prototype.getOriginalParent = function () {
    return this.originalParent;
  };

  OriginalColumnGroup.prototype.getLevel = function () {
    return this.level;
  };

  OriginalColumnGroup.prototype.isVisible = function () {
    // return true if at least one child is visible
    if (this.children) {
      return this.children.some(function (child) {
        return child.isVisible();
      });
    }

    return false;
  };

  OriginalColumnGroup.prototype.isPadding = function () {
    return this.padding;
  };

  OriginalColumnGroup.prototype.setExpanded = function (expanded) {
    this.expanded = expanded === undefined ? false : expanded;
    var event = {
      type: OriginalColumnGroup.EVENT_EXPANDED_CHANGED
    };
    this.localEventService.dispatchEvent(event);
  };

  OriginalColumnGroup.prototype.isExpandable = function () {
    return this.expandable;
  };

  OriginalColumnGroup.prototype.isExpanded = function () {
    return this.expanded;
  };

  OriginalColumnGroup.prototype.getGroupId = function () {
    return this.groupId;
  };

  OriginalColumnGroup.prototype.getId = function () {
    return this.getGroupId();
  };

  OriginalColumnGroup.prototype.setChildren = function (children) {
    this.children = children;
  };

  OriginalColumnGroup.prototype.getChildren = function () {
    return this.children;
  };

  OriginalColumnGroup.prototype.getColGroupDef = function () {
    return this.colGroupDef;
  };

  OriginalColumnGroup.prototype.getLeafColumns = function () {
    var result = [];
    this.addLeafColumns(result);
    return result;
  };

  OriginalColumnGroup.prototype.addLeafColumns = function (leafColumns) {
    if (!this.children) {
      return;
    }

    this.children.forEach(function (child) {
      if (child instanceof Column) {
        leafColumns.push(child);
      } else if (child instanceof OriginalColumnGroup) {
        child.addLeafColumns(leafColumns);
      }
    });
  };

  OriginalColumnGroup.prototype.getColumnGroupShow = function () {
    return this.padding ? ColumnGroup.HEADER_GROUP_PADDING : this.colGroupDef.columnGroupShow;
  }; // need to check that this group has at least one col showing when both expanded and contracted.
  // if not, then we don't allow expanding and contracting on this group


  OriginalColumnGroup.prototype.setupExpandable = function () {
    var _this = this;

    this.setExpandable(); // note - we should be removing this event listener

    this.getLeafColumns().forEach(function (col) {
      return col.addEventListener(Column.EVENT_VISIBLE_CHANGED, _this.onColumnVisibilityChanged.bind(_this));
    });
  };

  OriginalColumnGroup.prototype.setExpandable = function () {
    if (this.isPadding()) {
      return;
    } // want to make sure the group doesn't disappear when it's open


    var atLeastOneShowingWhenOpen = false; // want to make sure the group doesn't disappear when it's closed

    var atLeastOneShowingWhenClosed = false; // want to make sure the group has something to show / hide

    var atLeastOneChangeable = false;
    var children = this.findChildren();

    for (var i = 0, j = children.length; i < j; i++) {
      var abstractColumn = children[i];

      if (!abstractColumn.isVisible()) {
        continue;
      } // if the abstractColumn is a grid generated group, there will be no colDef


      var headerGroupShow = abstractColumn.getColumnGroupShow();

      if (headerGroupShow === ColumnGroup.HEADER_GROUP_SHOW_OPEN) {
        atLeastOneShowingWhenOpen = true;
        atLeastOneChangeable = true;
      } else if (headerGroupShow === ColumnGroup.HEADER_GROUP_SHOW_CLOSED) {
        atLeastOneShowingWhenClosed = true;
        atLeastOneChangeable = true;
      } else {
        atLeastOneShowingWhenOpen = true;
        atLeastOneShowingWhenClosed = true;

        if (headerGroupShow === ColumnGroup.HEADER_GROUP_PADDING) {
          var column = abstractColumn;
          atLeastOneChangeable = atLeastOneChangeable || column.children.some(function (child) {
            return child.getColumnGroupShow() !== undefined;
          });
        }
      }
    }

    var expandable = atLeastOneShowingWhenOpen && atLeastOneShowingWhenClosed && atLeastOneChangeable;

    if (this.expandable !== expandable) {
      this.expandable = expandable;
      var event_1 = {
        type: OriginalColumnGroup.EVENT_EXPANDABLE_CHANGED
      };
      this.localEventService.dispatchEvent(event_1);
    }
  };

  OriginalColumnGroup.prototype.findChildren = function () {
    var children = this.children;
    var firstChild = children[0];

    if (firstChild && (!firstChild.isPadding || !firstChild.isPadding())) {
      return children;
    }

    while (children.length === 1 && children[0] instanceof OriginalColumnGroup) {
      children = children[0].children;
    }

    return children;
  };

  OriginalColumnGroup.prototype.onColumnVisibilityChanged = function () {
    this.setExpandable();
  };

  OriginalColumnGroup.prototype.addEventListener = function (eventType, listener) {
    this.localEventService.addEventListener(eventType, listener);
  };

  OriginalColumnGroup.prototype.removeEventListener = function (eventType, listener) {
    this.localEventService.removeEventListener(eventType, listener);
  };

  OriginalColumnGroup.EVENT_EXPANDED_CHANGED = 'expandedChanged';
  OriginalColumnGroup.EVENT_EXPANDABLE_CHANGED = 'expandableChanged';
  return OriginalColumnGroup;
}();
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var DefaultColumnTypes = {
  numericColumn: {
    headerClass: "ag-numeric-header",
    cellClass: "ag-numeric-cell"
  }
};
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */

var __decorate$3 = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var __param$1 = undefined && undefined.__param || function (paramIndex, decorator) {
  return function (target, key) {
    decorator(target, key, paramIndex);
  };
}; // takes ColDefs and ColGroupDefs and turns them into Columns and OriginalGroups


var ColumnFactory =
/** @class */
function () {
  function ColumnFactory() {}

  ColumnFactory.prototype.setBeans = function (loggerFactory) {
    this.logger = loggerFactory.create('ColumnFactory');
  };

  ColumnFactory.prototype.createColumnTree = function (defs, primaryColumns, existingColumns) {
    // column key creator dishes out unique column id's in a deterministic way,
    // so if we have two grids (that could be master/slave) with same column definitions,
    // then this ensures the two grids use identical id's.
    var columnKeyCreator = new ColumnKeyCreator();

    if (existingColumns) {
      var existingKeys = existingColumns.map(function (col) {
        return col.getId();
      });
      columnKeyCreator.addExistingKeys(existingKeys);
    } // we take a copy of the columns as we are going to be removing from them


    var existingColsCopy = existingColumns ? existingColumns.slice() : null; // create am unbalanced tree that maps the provided definitions

    var unbalancedTree = this.recursivelyCreateColumns(defs, 0, primaryColumns, existingColsCopy, columnKeyCreator, null);
    var treeDept = this.findMaxDept(unbalancedTree, 0);
    this.logger.log('Number of levels for grouped columns is ' + treeDept);
    var columnTree = this.balanceColumnTree(unbalancedTree, 0, treeDept, columnKeyCreator);

    var deptFirstCallback = function deptFirstCallback(child, parent) {
      if (child instanceof OriginalColumnGroup) {
        child.setupExpandable();
      } // we set the original parents at the end, rather than when we go along, as balancing the tree
      // adds extra levels into the tree. so we can only set parents when balancing is done.


      child.setOriginalParent(parent);
    };

    this.columnUtils.depthFirstOriginalTreeSearch(null, columnTree, deptFirstCallback);
    return {
      columnTree: columnTree,
      treeDept: treeDept
    };
  };

  ColumnFactory.prototype.createForAutoGroups = function (autoGroupCols, gridBalancedTree) {
    var _this = this;

    var autoColBalancedTree = [];
    autoGroupCols.forEach(function (col) {
      var fakeTreeItem = _this.createAutoGroupTreeItem(gridBalancedTree, col);

      autoColBalancedTree.push(fakeTreeItem);
    });
    return autoColBalancedTree;
  };

  ColumnFactory.prototype.createAutoGroupTreeItem = function (balancedColumnTree, column) {
    var dept = this.findDepth(balancedColumnTree); // at the end, this will be the top of the tree item.

    var nextChild = column;

    for (var i = dept - 1; i >= 0; i--) {
      var autoGroup = new OriginalColumnGroup(null, "FAKE_PATH_" + column.getId() + "}_" + i, true, i);
      this.context.wireBean(autoGroup);
      autoGroup.setChildren([nextChild]);
      nextChild.setOriginalParent(autoGroup);
      nextChild = autoGroup;
    } // at this point, the nextChild is the top most item in the tree


    return nextChild;
  };

  ColumnFactory.prototype.findDepth = function (balancedColumnTree) {
    var dept = 0;
    var pointer = balancedColumnTree;

    while (pointer && pointer[0] && pointer[0] instanceof OriginalColumnGroup) {
      dept++;
      pointer = pointer[0].getChildren();
    }

    return dept;
  };

  ColumnFactory.prototype.balanceColumnTree = function (unbalancedTree, currentDept, columnDept, columnKeyCreator) {
    var result = []; // go through each child, for groups, recurse a level deeper,
    // for columns we need to pad

    for (var i = 0; i < unbalancedTree.length; i++) {
      var child = unbalancedTree[i];

      if (child instanceof OriginalColumnGroup) {
        // child is a group, all we do is go to the next level of recursion
        var originalGroup = child;
        var newChildren = this.balanceColumnTree(originalGroup.getChildren(), currentDept + 1, columnDept, columnKeyCreator);
        originalGroup.setChildren(newChildren);
        result.push(originalGroup);
      } else {
        // child is a column - so here we add in the padded column groups if needed
        var firstPaddedGroup = void 0;
        var currentPaddedGroup = void 0; // this for loop will NOT run any loops if no padded column groups are needed

        for (var j = columnDept - 1; j >= currentDept; j--) {
          var newColId = columnKeyCreator.getUniqueKey(null, null);
          var colGroupDefMerged = this.createMergedColGroupDef(null);
          var paddedGroup = new OriginalColumnGroup(colGroupDefMerged, newColId, true, currentDept);
          this.context.wireBean(paddedGroup);

          if (currentPaddedGroup) {
            currentPaddedGroup.setChildren([paddedGroup]);
          }

          currentPaddedGroup = paddedGroup;

          if (!firstPaddedGroup) {
            firstPaddedGroup = currentPaddedGroup;
          }
        } // likewise this if statement will not run if no padded groups


        if (firstPaddedGroup) {
          result.push(firstPaddedGroup);
          var hasGroups = unbalancedTree.some(function (child) {
            return child instanceof OriginalColumnGroup;
          });

          if (hasGroups) {
            currentPaddedGroup.setChildren([child]);
            continue;
          } else {
            currentPaddedGroup.setChildren(unbalancedTree);
            break;
          }
        }

        result.push(child);
      }
    }

    return result;
  };

  ColumnFactory.prototype.findMaxDept = function (treeChildren, dept) {
    var maxDeptThisLevel = dept;

    for (var i = 0; i < treeChildren.length; i++) {
      var abstractColumn = treeChildren[i];

      if (abstractColumn instanceof OriginalColumnGroup) {
        var originalGroup = abstractColumn;
        var newDept = this.findMaxDept(originalGroup.getChildren(), dept + 1);

        if (maxDeptThisLevel < newDept) {
          maxDeptThisLevel = newDept;
        }
      }
    }

    return maxDeptThisLevel;
  };

  ColumnFactory.prototype.recursivelyCreateColumns = function (defs, level, primaryColumns, existingColsCopy, columnKeyCreator, parent) {
    var _this = this;

    var result = [];

    if (!defs) {
      return result;
    }

    defs.forEach(function (def) {
      var newGroupOrColumn;

      if (_this.isColumnGroup(def)) {
        newGroupOrColumn = _this.createColumnGroup(primaryColumns, def, level, existingColsCopy, columnKeyCreator, parent);
      } else {
        newGroupOrColumn = _this.createColumn(primaryColumns, def, existingColsCopy, columnKeyCreator, parent);
      }

      result.push(newGroupOrColumn);
    });
    return result;
  };

  ColumnFactory.prototype.createColumnGroup = function (primaryColumns, colGroupDef, level, existingColumns, columnKeyCreator, parent) {
    var colGroupDefMerged = this.createMergedColGroupDef(colGroupDef);
    var groupId = columnKeyCreator.getUniqueKey(colGroupDefMerged.groupId, null);
    var originalGroup = new OriginalColumnGroup(colGroupDefMerged, groupId, false, level);
    this.context.wireBean(originalGroup);
    var children = this.recursivelyCreateColumns(colGroupDefMerged.children, level + 1, primaryColumns, existingColumns, columnKeyCreator, originalGroup);
    originalGroup.setChildren(children);
    return originalGroup;
  };

  ColumnFactory.prototype.createMergedColGroupDef = function (colGroupDef) {
    var colGroupDefMerged = {};

    _.assign(colGroupDefMerged, this.gridOptionsWrapper.getDefaultColGroupDef());

    _.assign(colGroupDefMerged, colGroupDef);

    this.checkForDeprecatedItems(colGroupDefMerged);
    return colGroupDefMerged;
  };

  ColumnFactory.prototype.createColumn = function (primaryColumns, colDef, existingColsCopy, columnKeyCreator, parent) {
    var colDefMerged = this.mergeColDefs(colDef);
    this.checkForDeprecatedItems(colDefMerged); // see if column already exists

    var column = this.findExistingColumn(colDef, existingColsCopy);

    if (!column) {
      // no existing column, need to create one
      var colId = columnKeyCreator.getUniqueKey(colDefMerged.colId, colDefMerged.field);
      column = new Column(colDefMerged, colDef, colId, primaryColumns);
      this.context.wireBean(column);
    } else {
      column.setColDef(colDefMerged, colDef);
    }

    return column;
  };

  ColumnFactory.prototype.findExistingColumn = function (colDef, existingColsCopy) {
    var res = _.find(existingColsCopy, function (col) {
      var oldColDef = col.getUserProvidedColDef();

      if (!oldColDef) {
        return false;
      } // first check object references


      if (oldColDef === colDef) {
        return true;
      } // second check id's


      var oldColHadId = oldColDef.colId !== null && oldColDef.colId !== undefined;

      if (oldColHadId) {
        return oldColDef.colId === colDef.colId;
      }

      return false;
    }); // make sure we remove, so if user provided duplicate id, then we don't have more than
    // one column instance for colDef with common id


    if (res) {
      _.removeFromArray(existingColsCopy, res);
    }

    return res;
  };

  ColumnFactory.prototype.mergeColDefs = function (colDef) {
    // start with empty merged definition
    var colDefMerged = {}; // merge properties from default column definitions

    _.assign(colDefMerged, this.gridOptionsWrapper.getDefaultColDef()); // merge properties from column type properties


    if (colDef.type) {
      this.assignColumnTypes(colDef, colDefMerged);
    } // merge properties from column definitions


    _.assign(colDefMerged, colDef);

    return colDefMerged;
  };

  ColumnFactory.prototype.assignColumnTypes = function (colDef, colDefMerged) {
    var typeKeys;

    if (colDef.type instanceof Array) {
      var invalidArray = colDef.type.some(function (a) {
        return typeof a !== 'string';
      });

      if (invalidArray) {
        console.warn("ag-grid: if colDef.type is supplied an array it should be of type 'string[]'");
      } else {
        typeKeys = colDef.type;
      }
    } else if (typeof colDef.type === 'string') {
      typeKeys = colDef.type.split(',');
    } else {
      console.warn("ag-grid: colDef.type should be of type 'string' | 'string[]'");
      return;
    } // merge user defined with default column types


    var allColumnTypes = _.assign({}, this.gridOptionsWrapper.getColumnTypes(), DefaultColumnTypes);

    typeKeys.forEach(function (t) {
      var typeColDef = allColumnTypes[t.trim()];

      if (typeColDef) {
        _.assign(colDefMerged, typeColDef);
      } else {
        console.warn("ag-grid: colDef.type '" + t + "' does not correspond to defined gridOptions.columnTypes");
      }
    });
  };

  ColumnFactory.prototype.checkForDeprecatedItems = function (colDef) {
    if (colDef) {
      var colDefNoType = colDef; // take out the type, so we can access attributes not defined in the type

      if (colDefNoType.group !== undefined) {
        console.warn('ag-grid: colDef.group is invalid, please check documentation on how to do grouping as it changed in version 3');
      }

      if (colDefNoType.headerGroup !== undefined) {
        console.warn('ag-grid: colDef.headerGroup is invalid, please check documentation on how to do grouping as it changed in version 3');
      }

      if (colDefNoType.headerGroupShow !== undefined) {
        console.warn('ag-grid: colDef.headerGroupShow is invalid, should be columnGroupShow, please check documentation on how to do grouping as it changed in version 3');
      }

      if (colDefNoType.suppressRowGroup !== undefined) {
        console.warn('ag-grid: colDef.suppressRowGroup is deprecated, please use colDef.type instead');
      }

      if (colDefNoType.suppressAggregation !== undefined) {
        console.warn('ag-grid: colDef.suppressAggregation is deprecated, please use colDef.type instead');
      }

      if (colDefNoType.suppressRowGroup || colDefNoType.suppressAggregation) {
        console.warn('ag-grid: colDef.suppressAggregation and colDef.suppressRowGroup are deprecated, use allowRowGroup, allowPivot and allowValue instead');
      }

      if (colDefNoType.displayName) {
        console.warn("ag-grid: Found displayName " + colDefNoType.displayName + ", please use headerName instead, displayName is deprecated.");
        colDefNoType.headerName = colDefNoType.displayName;
      }
    }
  }; // if object has children, we assume it's a group


  ColumnFactory.prototype.isColumnGroup = function (abstractColDef) {
    return abstractColDef.children !== undefined;
  };

  __decorate$3([Autowired('gridOptionsWrapper')], ColumnFactory.prototype, "gridOptionsWrapper", void 0);

  __decorate$3([Autowired('columnUtils')], ColumnFactory.prototype, "columnUtils", void 0);

  __decorate$3([Autowired('context')], ColumnFactory.prototype, "context", void 0);

  __decorate$3([__param$1(0, Qualifier('loggerFactory'))], ColumnFactory.prototype, "setBeans", null);

  ColumnFactory = __decorate$3([Bean('columnFactory')], ColumnFactory);
  return ColumnFactory;
}();
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var Events =
/** @class */
function () {
  function Events() {}
  /** Everything has changed with the columns. Either complete new set of columns set, or user called setState()*/


  Events.EVENT_COLUMN_EVERYTHING_CHANGED = 'columnEverythingChanged';
  /** User has set in new columns. */

  Events.EVENT_NEW_COLUMNS_LOADED = 'newColumnsLoaded';
  /** The pivot mode flag was changed */

  Events.EVENT_COLUMN_PIVOT_MODE_CHANGED = 'columnPivotModeChanged';
  /** A row group column was added, removed or order changed. */

  Events.EVENT_COLUMN_ROW_GROUP_CHANGED = 'columnRowGroupChanged';
  /** expandAll / collapseAll was called from the api. */

  Events.EVENT_EXPAND_COLLAPSE_ALL = 'expandOrCollapseAll';
  /** A pivot column was added, removed or order changed. */

  Events.EVENT_COLUMN_PIVOT_CHANGED = 'columnPivotChanged';
  /** The list of grid columns has changed. */

  Events.EVENT_GRID_COLUMNS_CHANGED = 'gridColumnsChanged';
  /** A value column was added, removed or agg function was changed. */

  Events.EVENT_COLUMN_VALUE_CHANGED = 'columnValueChanged';
  /** A column was moved */

  Events.EVENT_COLUMN_MOVED = 'columnMoved';
  /** One or more columns was shown / hidden */

  Events.EVENT_COLUMN_VISIBLE = 'columnVisible';
  /** One or more columns was pinned / unpinned*/

  Events.EVENT_COLUMN_PINNED = 'columnPinned';
  /** A column group was opened / closed */

  Events.EVENT_COLUMN_GROUP_OPENED = 'columnGroupOpened';
  /** One or more columns was resized. If just one, the column in the event is set. */

  Events.EVENT_COLUMN_RESIZED = 'columnResized';
  /** The list of displayed columns has changed, can result from columns open / close, column move, pivot, group, etc */

  Events.EVENT_DISPLAYED_COLUMNS_CHANGED = 'displayedColumnsChanged';
  /** The list of virtual columns has changed, results from viewport changing */

  Events.EVENT_VIRTUAL_COLUMNS_CHANGED = 'virtualColumnsChanged';
  /** A row group was opened / closed */

  Events.EVENT_ROW_GROUP_OPENED = 'rowGroupOpened';
  /** The client has set new data into the grid */

  Events.EVENT_ROW_DATA_CHANGED = 'rowDataChanged';
  /** The client has updated data for the grid */

  Events.EVENT_ROW_DATA_UPDATED = 'rowDataUpdated';
  /** The client has set new floating data into the grid */

  Events.EVENT_PINNED_ROW_DATA_CHANGED = 'pinnedRowDataChanged';
  /** Range selection has changed */

  Events.EVENT_RANGE_SELECTION_CHANGED = 'rangeSelectionChanged';
  /** Chart Range selection has changed */

  Events.EVENT_CHART_RANGE_SELECTION_CHANGED = 'chartRangeSelectionChanged';
  /** Chart Options have changed */

  Events.EVENT_CHART_OPTIONS_CHANGED = 'chartOptionsChanged';
  /** For when the tool panel is shown / hidden */

  Events.EVENT_TOOL_PANEL_VISIBLE_CHANGED = 'toolPanelVisibleChanged';
  /** Model was updated - grid updates the drawn rows when this happens */

  Events.EVENT_MODEL_UPDATED = 'modelUpdated';
  Events.EVENT_PASTE_START = 'pasteStart';
  Events.EVENT_PASTE_END = 'pasteEnd';
  Events.EVENT_FILL_START = 'fillStart';
  Events.EVENT_FILL_END = 'fillEnd';
  Events.EVENT_CELL_CLICKED = 'cellClicked';
  Events.EVENT_CELL_DOUBLE_CLICKED = 'cellDoubleClicked';
  Events.EVENT_CELL_MOUSE_DOWN = 'cellMouseDown';
  Events.EVENT_CELL_CONTEXT_MENU = 'cellContextMenu';
  Events.EVENT_CELL_VALUE_CHANGED = 'cellValueChanged';
  Events.EVENT_ROW_VALUE_CHANGED = 'rowValueChanged';
  Events.EVENT_CELL_FOCUSED = 'cellFocused';
  Events.EVENT_ROW_SELECTED = 'rowSelected';
  Events.EVENT_SELECTION_CHANGED = 'selectionChanged';
  Events.EVENT_CELL_KEY_DOWN = 'cellKeyDown';
  Events.EVENT_CELL_KEY_PRESS = 'cellKeyPress';
  Events.EVENT_CELL_MOUSE_OVER = 'cellMouseOver';
  Events.EVENT_CELL_MOUSE_OUT = 'cellMouseOut';
  /** 2 events for filtering. The grid LISTENS for filterChanged and afterFilterChanged */

  Events.EVENT_FILTER_CHANGED = 'filterChanged';
  /** Filter was change but not applied. Only useful if apply buttons are used in filters. */

  Events.EVENT_FILTER_MODIFIED = 'filterModified';
  Events.EVENT_FILTER_OPENED = 'filterOpened';
  Events.EVENT_SORT_CHANGED = 'sortChanged';
  /** A row was removed from the dom, for any reason. Use to clean up resources (if any) used by the row. */

  Events.EVENT_VIRTUAL_ROW_REMOVED = 'virtualRowRemoved';
  Events.EVENT_ROW_CLICKED = 'rowClicked';
  Events.EVENT_ROW_DOUBLE_CLICKED = 'rowDoubleClicked';
  /** Gets called once after the grid has finished initialising. */

  Events.EVENT_GRID_READY = 'gridReady';
  /** Width of height of the main grid div has changed. Grid listens for this and does layout of grid if it's
   * changed, so always filling the space it was given. */

  Events.EVENT_GRID_SIZE_CHANGED = 'gridSizeChanged';
  /** The indexes of the rows rendered has changed, eg user has scrolled to a new vertical position. */

  Events.EVENT_VIEWPORT_CHANGED = 'viewportChanged';
  /** Rows were rendered for the first time (ie on async data load). */

  Events.EVENT_FIRST_DATA_RENDERED = 'firstDataRendered';
  /** A column drag has started, either resizing a column or moving a column. */

  Events.EVENT_DRAG_STARTED = 'dragStarted';
  /** A column drag has stopped */

  Events.EVENT_DRAG_STOPPED = 'dragStopped';
  Events.EVENT_ROW_EDITING_STARTED = 'rowEditingStarted';
  Events.EVENT_ROW_EDITING_STOPPED = 'rowEditingStopped';
  Events.EVENT_CELL_EDITING_STARTED = 'cellEditingStarted';
  Events.EVENT_CELL_EDITING_STOPPED = 'cellEditingStopped';
  /** Main body of grid has scrolled, either horizontally or vertically */

  Events.EVENT_BODY_SCROLL = 'bodyScroll';
  Events.EVENT_ANIMATION_QUEUE_EMPTY = 'animationQueueEmpty';
  Events.EVENT_HEIGHT_SCALE_CHANGED = 'heightScaleChanged';
  /** The displayed page for pagination has changed. For example the data was filtered or sorted,
   * or the user has moved to a different page. */

  Events.EVENT_PAGINATION_CHANGED = 'paginationChanged';
  /** Only used by React, Angular 2+, Web Components and VueJS ag-Grid components
   * (not used if doing plain JavaScript or Angular 1.x). If the grid receives changes due
   * to bound properties, this event fires after the grid has finished processing the change. */

  Events.EVENT_COMPONENT_STATE_CHANGED = 'componentStateChanged';
  /** All items from here down are used internally by the grid, not intended for external use. */
  // not documented, either experimental, or we just don't want users using an depending on them

  Events.EVENT_BODY_HEIGHT_CHANGED = 'bodyHeightChanged';
  Events.EVENT_DISPLAYED_COLUMNS_WIDTH_CHANGED = 'displayedColumnsWidthChanged';
  Events.EVENT_SCROLL_VISIBILITY_CHANGED = 'scrollVisibilityChanged';
  Events.EVENT_COLUMN_HOVER_CHANGED = 'columnHoverChanged';
  Events.EVENT_FLASH_CELLS = 'flashCells';
  Events.EVENT_ROW_DRAG_ENTER = 'rowDragEnter';
  Events.EVENT_ROW_DRAG_MOVE = 'rowDragMove';
  Events.EVENT_ROW_DRAG_LEAVE = 'rowDragLeave';
  Events.EVENT_ROW_DRAG_END = 'rowDragEnd'; // primarily for charts

  Events.EVENT_POPUP_TO_FRONT = 'popupToFront'; // these are used for server side group and agg - only used by CS with Viewport Row Model - intention is
  // to design these better around server side functions and then release to general public when fully working with
  // all the row models.

  Events.EVENT_COLUMN_ROW_GROUP_CHANGE_REQUEST = 'columnRowGroupChangeRequest';
  Events.EVENT_COLUMN_PIVOT_CHANGE_REQUEST = 'columnPivotChangeRequest';
  Events.EVENT_COLUMN_VALUE_CHANGE_REQUEST = 'columnValueChangeRequest';
  Events.EVENT_COLUMN_AGG_FUNC_CHANGE_REQUEST = 'columnAggFuncChangeRequest';
  return Events;
}();
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */
// class returns unique instance id's for columns.
// eg, the following calls (in this order) will result in:
//
// getInstanceIdForKey('country') => 0
// getInstanceIdForKey('country') => 1
// getInstanceIdForKey('country') => 2
// getInstanceIdForKey('country') => 3
// getInstanceIdForKey('age') => 0
// getInstanceIdForKey('age') => 1
// getInstanceIdForKey('country') => 4


var GroupInstanceIdCreator =
/** @class */
function () {
  function GroupInstanceIdCreator() {
    // this map contains keys to numbers, so we remember what the last call was
    this.existingIds = {};
  }

  GroupInstanceIdCreator.prototype.getInstanceIdForKey = function (key) {
    var lastResult = this.existingIds[key];
    var result;

    if (typeof lastResult !== 'number') {
      // first time this key
      result = 0;
    } else {
      result = lastResult + 1;
    }

    this.existingIds[key] = result;
    return result;
  };

  return GroupInstanceIdCreator;
}();
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __decorate$4 = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var __param$2 = undefined && undefined.__param || function (paramIndex, decorator) {
  return function (target, key) {
    decorator(target, key, paramIndex);
  };
};

var ColumnController =
/** @class */
function () {
  function ColumnController() {
    // header row count, based on user provided columns
    this.primaryHeaderRowCount = 0;
    this.secondaryHeaderRowCount = 0;
    this.secondaryColumnsPresent = false; // header row count, either above, or based on pivoting if we are pivoting

    this.gridHeaderRowCount = 0; // these are the lists used by the rowRenderer to render nodes. almost the leaf nodes of the above
    // displayed trees, however it also takes into account if the groups are open or not.

    this.displayedLeftColumns = [];
    this.displayedRightColumns = [];
    this.displayedCenterColumns = []; // all three lists above combined

    this.allDisplayedColumns = []; // same as above, except trimmed down to only columns within the viewport

    this.allDisplayedVirtualColumns = [];
    this.allDisplayedCenterVirtualColumns = [];
    this.rowGroupColumns = [];
    this.valueColumns = [];
    this.pivotColumns = [];
    this.ready = false;
    this.autoGroupsNeedBuilding = false;
    this.pivotMode = false;
    this.bodyWidth = 0;
    this.leftWidth = 0;
    this.rightWidth = 0;
    this.bodyWidthDirty = true;
    this.flexActive = false;
  }

  ColumnController.prototype.init = function () {
    var pivotMode = this.gridOptionsWrapper.isPivotMode();
    this.suppressColumnVirtualisation = this.gridOptionsWrapper.isSuppressColumnVirtualisation();

    if (this.isPivotSettingAllowed(pivotMode)) {
      this.pivotMode = pivotMode;
    }

    this.usingTreeData = this.gridOptionsWrapper.isTreeData();
  };

  ColumnController.prototype.setColumnDefs = function (columnDefs, source) {
    if (source === void 0) {
      source = "api";
    }

    var colsPreviouslyExisted = !!this.columnDefs;
    this.columnDefs = columnDefs; // always invalidate cache on changing columns, as the column id's for the new columns
    // could overlap with the old id's, so the cache would return old values for new columns.

    this.valueCache.expire(); // NOTE ==================
    // we should be destroying the existing columns and groups if they exist, for example, the original column
    // group adds a listener to the columns, it should be also removing the listeners

    this.autoGroupsNeedBuilding = true;
    var oldPrimaryColumns = this.primaryColumns;
    var balancedTreeResult = this.columnFactory.createColumnTree(columnDefs, true, oldPrimaryColumns);
    this.primaryColumnTree = balancedTreeResult.columnTree;
    this.primaryHeaderRowCount = balancedTreeResult.treeDept + 1;
    this.primaryColumns = this.getColumnsFromTree(this.primaryColumnTree);
    this.extractRowGroupColumns(source, oldPrimaryColumns);
    this.extractPivotColumns(source, oldPrimaryColumns);
    this.createValueColumns(source, oldPrimaryColumns);
    this.ready = true;
    this.updateGridColumns();
    this.updateDisplayedColumns(source);
    this.checkDisplayedVirtualColumns();

    if (this.gridOptionsWrapper.isDeltaColumnMode() && colsPreviouslyExisted) {
      this.resetColumnState(true, source);
    }

    var eventEverythingChanged = {
      type: Events.EVENT_COLUMN_EVERYTHING_CHANGED,
      api: this.gridApi,
      columnApi: this.columnApi,
      source: source
    };
    this.eventService.dispatchEvent(eventEverythingChanged);
    var newColumnsLoadedEvent = {
      type: Events.EVENT_NEW_COLUMNS_LOADED,
      api: this.gridApi,
      columnApi: this.columnApi
    };
    this.eventService.dispatchEvent(newColumnsLoadedEvent);
    this.flexActive = !!_.find(this.getDisplayedCenterColumns(), function (col) {
      return !!col.getFlex();
    });
  };

  ColumnController.prototype.isAutoRowHeightActive = function () {
    return this.autoRowHeightColumns && this.autoRowHeightColumns.length > 0;
  };

  ColumnController.prototype.getAllAutoRowHeightCols = function () {
    return this.autoRowHeightColumns;
  };

  ColumnController.prototype.setVirtualViewportLeftAndRight = function () {
    if (this.gridOptionsWrapper.isEnableRtl()) {
      this.viewportLeft = this.bodyWidth - this.scrollPosition - this.scrollWidth;
      this.viewportRight = this.bodyWidth - this.scrollPosition;
    } else {
      this.viewportLeft = this.scrollPosition;
      this.viewportRight = this.scrollWidth + this.scrollPosition;
    }
  }; // used by clipboard service, to know what columns to paste into


  ColumnController.prototype.getDisplayedColumnsStartingAt = function (column) {
    var currentColumn = column;
    var result = [];

    while (currentColumn && _.exists(currentColumn)) {
      result.push(currentColumn);
      currentColumn = this.getDisplayedColAfter(currentColumn);
    }

    return result;
  }; // checks what columns are currently displayed due to column virtualisation. fires an event
  // if the list of columns has changed.
  // + setColumnWidth(), setVirtualViewportPosition(), setColumnDefs(), sizeColumnsToFit()


  ColumnController.prototype.checkDisplayedVirtualColumns = function () {
    // check displayCenterColumnTree exists first, as it won't exist when grid is initialising
    if (!_.exists(this.displayedCenterColumns)) {
      return;
    }

    var hashBefore = this.allDisplayedVirtualColumns.map(function (column) {
      return column.getId();
    }).join('#');
    this.updateVirtualSets();
    var hashAfter = this.allDisplayedVirtualColumns.map(function (column) {
      return column.getId();
    }).join('#');

    if (hashBefore !== hashAfter) {
      var event_1 = {
        type: Events.EVENT_VIRTUAL_COLUMNS_CHANGED,
        api: this.gridApi,
        columnApi: this.columnApi
      };
      this.eventService.dispatchEvent(event_1);
    }
  };

  ColumnController.prototype.setVirtualViewportPosition = function (scrollWidth, scrollPosition) {
    if (scrollWidth !== this.scrollWidth || scrollPosition !== this.scrollPosition || this.bodyWidthDirty) {
      this.scrollWidth = scrollWidth;
      this.scrollPosition = scrollPosition; // we need to call setVirtualViewportLeftAndRight() at least once after the body width changes,
      // as the viewport can stay the same, but in RTL, if body width changes, we need to work out the
      // virtual columns again

      this.bodyWidthDirty = true;
      this.setVirtualViewportLeftAndRight();

      if (this.ready) {
        this.checkDisplayedVirtualColumns();
      }
    }
  };

  ColumnController.prototype.isPivotMode = function () {
    return this.pivotMode;
  };

  ColumnController.prototype.isPivotSettingAllowed = function (pivot) {
    if (pivot) {
      if (this.gridOptionsWrapper.isTreeData()) {
        console.warn("ag-Grid: Pivot mode not available in conjunction Tree Data i.e. 'gridOptions.treeData: true'");
        return false;
      }

      return true;
    }

    return true;
  };

  ColumnController.prototype.setPivotMode = function (pivotMode, source) {
    if (source === void 0) {
      source = "api";
    }

    if (pivotMode === this.pivotMode) {
      return;
    }

    if (!this.isPivotSettingAllowed(this.pivotMode)) {
      return;
    }

    this.pivotMode = pivotMode; // we need to update grid columns to cover the scenario where user has groupSuppressAutoColumn=true, as
    // this means we don't use auto group column UNLESS we are in pivot mode (it's mandatory in pivot mode),
    // so need to updateGridColumn() to check it autoGroupCol needs to be added / removed

    this.autoGroupsNeedBuilding = true;
    this.updateGridColumns();
    this.updateDisplayedColumns(source);
    var event = {
      type: Events.EVENT_COLUMN_PIVOT_MODE_CHANGED,
      api: this.gridApi,
      columnApi: this.columnApi
    };
    this.eventService.dispatchEvent(event);
  };

  ColumnController.prototype.getSecondaryPivotColumn = function (pivotKeys, valueColKey) {
    if (!this.secondaryColumnsPresent) {
      return null;
    }

    var valueColumnToFind = this.getPrimaryColumn(valueColKey);
    var foundColumn = null;

    if (this.secondaryColumns) {
      this.secondaryColumns.forEach(function (column) {
        var thisPivotKeys = column.getColDef().pivotKeys;
        var pivotValueColumn = column.getColDef().pivotValueColumn;

        var pivotKeyMatches = _.compareArrays(thisPivotKeys, pivotKeys);

        var pivotValueMatches = pivotValueColumn === valueColumnToFind;

        if (pivotKeyMatches && pivotValueMatches) {
          foundColumn = column;
        }
      });
    }

    return foundColumn;
  };

  ColumnController.prototype.setBeans = function (loggerFactory) {
    this.logger = loggerFactory.create('ColumnController');
  };

  ColumnController.prototype.setFirstRightAndLastLeftPinned = function (source) {
    var lastLeft;
    var firstRight;

    if (this.gridOptionsWrapper.isEnableRtl()) {
      lastLeft = this.displayedLeftColumns ? this.displayedLeftColumns[0] : null;
      firstRight = this.displayedRightColumns ? _.last(this.displayedRightColumns) : null;
    } else {
      lastLeft = this.displayedLeftColumns ? _.last(this.displayedLeftColumns) : null;
      firstRight = this.displayedRightColumns ? this.displayedRightColumns[0] : null;
    }

    this.gridColumns.forEach(function (column) {
      column.setLastLeftPinned(column === lastLeft, source);
      column.setFirstRightPinned(column === firstRight, source);
    });
  };

  ColumnController.prototype.autoSizeColumns = function (keys, skipHeader, source) {
    // because of column virtualisation, we can only do this function on columns that are
    // actually rendered, as non-rendered columns (outside the viewport and not rendered
    // due to column virtualisation) are not present. this can result in all rendered columns
    // getting narrowed, which in turn introduces more rendered columns on the RHS which
    // did not get autosized in the original run, leaving the visible grid with columns on
    // the LHS sized, but RHS no. so we keep looping through the visible columns until
    // no more cols are available (rendered) to be resized
    var _this = this;

    if (source === void 0) {
      source = "api";
    } // keep track of which cols we have resized in here


    var columnsAutosized = []; // initialise with anything except 0 so that while loop executes at least once

    var changesThisTimeAround = -1;

    if (skipHeader == null) {
      skipHeader = this.gridOptionsWrapper.isSkipHeaderOnAutoSize();
    }

    while (changesThisTimeAround !== 0) {
      changesThisTimeAround = 0;
      this.actionOnGridColumns(keys, function (column) {
        // if already autosized, skip it
        if (columnsAutosized.indexOf(column) >= 0) {
          return false;
        } // get how wide this col should be


        var preferredWidth = _this.autoWidthCalculator.getPreferredWidthForColumn(column, skipHeader); // preferredWidth = -1 if this col is not on the screen


        if (preferredWidth > 0) {
          var newWidth = _this.normaliseColumnWidth(column, preferredWidth);

          column.setActualWidth(newWidth, source);
          columnsAutosized.push(column);
          changesThisTimeAround++;
        }

        return true;
      }, source);
    }

    if (columnsAutosized.length) {
      var event_2 = {
        type: Events.EVENT_COLUMN_RESIZED,
        columns: columnsAutosized,
        column: columnsAutosized.length === 1 ? columnsAutosized[0] : null,
        finished: true,
        api: this.gridApi,
        columnApi: this.columnApi,
        source: "autosizeColumns"
      };
      this.eventService.dispatchEvent(event_2);
    }
  };

  ColumnController.prototype.autoSizeColumn = function (key, skipHeader, source) {
    if (source === void 0) {
      source = "api";
    }

    if (key) {
      this.autoSizeColumns([key], skipHeader, source);
    }
  };

  ColumnController.prototype.autoSizeAllColumns = function (skipHeader, source) {
    if (source === void 0) {
      source = "api";
    }

    var allDisplayedColumns = this.getAllDisplayedColumns();
    this.autoSizeColumns(allDisplayedColumns, skipHeader, source);
  };

  ColumnController.prototype.getColumnsFromTree = function (rootColumns) {
    var result = [];

    var recursiveFindColumns = function recursiveFindColumns(childColumns) {
      for (var i = 0; i < childColumns.length; i++) {
        var child = childColumns[i];

        if (child instanceof Column) {
          result.push(child);
        } else if (child instanceof OriginalColumnGroup) {
          recursiveFindColumns(child.getChildren());
        }
      }
    };

    recursiveFindColumns(rootColumns);
    return result;
  };

  ColumnController.prototype.getAllDisplayedColumnGroups = function () {
    if (this.displayedLeftColumnTree && this.displayedRightColumnTree && this.displayedCentreColumnTree) {
      return this.displayedLeftColumnTree.concat(this.displayedCentreColumnTree).concat(this.displayedRightColumnTree);
    }

    return null;
  }; // + columnSelectPanel


  ColumnController.prototype.getPrimaryColumnTree = function () {
    return this.primaryColumnTree;
  }; // + gridPanel -> for resizing the body and setting top margin


  ColumnController.prototype.getHeaderRowCount = function () {
    return this.gridHeaderRowCount;
  }; // + headerRenderer -> setting pinned body width


  ColumnController.prototype.getLeftDisplayedColumnGroups = function () {
    return this.displayedLeftColumnTree;
  }; // + headerRenderer -> setting pinned body width


  ColumnController.prototype.getRightDisplayedColumnGroups = function () {
    return this.displayedRightColumnTree;
  }; // + headerRenderer -> setting pinned body width


  ColumnController.prototype.getCenterDisplayedColumnGroups = function () {
    return this.displayedCentreColumnTree;
  };

  ColumnController.prototype.getDisplayedColumnGroups = function (type) {
    switch (type) {
      case Constants.PINNED_LEFT:
        return this.getLeftDisplayedColumnGroups();

      case Constants.PINNED_RIGHT:
        return this.getRightDisplayedColumnGroups();

      default:
        return this.getCenterDisplayedColumnGroups();
    }
  }; // gridPanel -> ensureColumnVisible


  ColumnController.prototype.isColumnDisplayed = function (column) {
    return this.getAllDisplayedColumns().indexOf(column) >= 0;
  }; // + csvCreator


  ColumnController.prototype.getAllDisplayedColumns = function () {
    return this.allDisplayedColumns;
  };

  ColumnController.prototype.getAllDisplayedVirtualColumns = function () {
    return this.allDisplayedVirtualColumns;
  };

  ColumnController.prototype.getDisplayedLeftColumnsForRow = function (rowNode) {
    if (!this.colSpanActive) {
      return this.displayedLeftColumns;
    }

    return this.getDisplayedColumnsForRow(rowNode, this.displayedLeftColumns);
  };

  ColumnController.prototype.getDisplayedRightColumnsForRow = function (rowNode) {
    if (!this.colSpanActive) {
      return this.displayedRightColumns;
    }

    return this.getDisplayedColumnsForRow(rowNode, this.displayedRightColumns);
  };

  ColumnController.prototype.getDisplayedColumnsForRow = function (rowNode, displayedColumns, filterCallback, emptySpaceBeforeColumn) {
    var result = [];
    var lastConsideredCol = null;

    var _loop_1 = function _loop_1(i) {
      var col = displayedColumns[i];
      var maxAllowedColSpan = displayedColumns.length - i;
      var colSpan = Math.min(col.getColSpan(rowNode), maxAllowedColSpan);
      var columnsToCheckFilter = [col];

      if (colSpan > 1) {
        var colsToRemove = colSpan - 1;

        for (var j = 1; j <= colsToRemove; j++) {
          columnsToCheckFilter.push(displayedColumns[i + j]);
        }

        i += colsToRemove;
      } // see which cols we should take out for column virtualisation


      var filterPasses;

      if (filterCallback) {
        // if user provided a callback, means some columns may not be in the viewport.
        // the user will NOT provide a callback if we are talking about pinned areas,
        // as pinned areas have no horizontal scroll and do not virtualise the columns.
        // if lots of columns, that means column spanning, and we set filterPasses = true
        // if one or more of the columns spanned pass the filter.
        filterPasses = false;
        columnsToCheckFilter.forEach(function (colForFilter) {
          if (filterCallback(colForFilter)) {
            filterPasses = true;
          }
        });
      } else {
        filterPasses = true;
      }

      if (filterPasses) {
        if (result.length === 0 && lastConsideredCol) {
          var gapBeforeColumn = emptySpaceBeforeColumn ? emptySpaceBeforeColumn(col) : false;

          if (gapBeforeColumn) {
            result.push(lastConsideredCol);
          }
        }

        result.push(col);
      }

      lastConsideredCol = col;
      out_i_1 = i;
    };

    var out_i_1;

    for (var i = 0; i < displayedColumns.length; i++) {
      _loop_1(i);

      i = out_i_1;
    }

    return result;
  }; // + rowRenderer
  // if we are not column spanning, this just returns back the virtual centre columns,
  // however if we are column spanning, then different rows can have different virtual
  // columns, so we have to work out the list for each individual row.


  ColumnController.prototype.getAllDisplayedCenterVirtualColumnsForRow = function (rowNode) {
    var _this = this;

    if (!this.colSpanActive) {
      return this.allDisplayedCenterVirtualColumns;
    }

    var emptySpaceBeforeColumn = function emptySpaceBeforeColumn(col) {
      return col.getLeft() > _this.viewportLeft;
    }; // if doing column virtualisation, then we filter based on the viewport.


    var filterCallback = this.suppressColumnVirtualisation ? null : this.isColumnInViewport.bind(this);
    return this.getDisplayedColumnsForRow(rowNode, this.displayedCenterColumns, filterCallback, emptySpaceBeforeColumn);
  };

  ColumnController.prototype.isColumnInViewport = function (col) {
    var columnLeft = col.getLeft();
    var columnRight = col.getLeft() + col.getActualWidth(); // adding 200 for buffer size, so some cols off viewport are rendered.
    // this helps horizontal scrolling so user rarely sees white space (unless
    // they scroll horizontally fast). however we are conservative, as the more
    // buffer the slower the vertical redraw speed

    var leftBounds = this.viewportLeft - 200;
    var rightBounds = this.viewportRight + 200;
    var columnToMuchLeft = columnLeft < leftBounds && columnRight < leftBounds;
    var columnToMuchRight = columnLeft > rightBounds && columnRight > rightBounds;
    return !columnToMuchLeft && !columnToMuchRight;
  }; // used by:
  // + angularGrid -> setting pinned body width
  // note: this should be cached


  ColumnController.prototype.getPinnedLeftContainerWidth = function () {
    return this.getWidthOfColsInList(this.displayedLeftColumns);
  }; // note: this should be cached


  ColumnController.prototype.getPinnedRightContainerWidth = function () {
    return this.getWidthOfColsInList(this.displayedRightColumns);
  };

  ColumnController.prototype.updatePrimaryColumnList = function (keys, masterList, actionIsAdd, columnCallback, eventType, source) {
    var _this = this;

    if (source === void 0) {
      source = "api";
    }

    if (!keys || _.missingOrEmpty(keys)) {
      return;
    }

    var atLeastOne = false;
    keys.forEach(function (key) {
      var columnToAdd = _this.getPrimaryColumn(key);

      if (!columnToAdd) {
        return;
      }

      if (actionIsAdd) {
        if (masterList.indexOf(columnToAdd) >= 0) {
          return;
        }

        masterList.push(columnToAdd);
      } else {
        if (masterList.indexOf(columnToAdd) < 0) {
          return;
        }

        _.removeFromArray(masterList, columnToAdd);
      }

      columnCallback(columnToAdd);
      atLeastOne = true;
    });

    if (!atLeastOne) {
      return;
    }

    if (this.autoGroupsNeedBuilding) {
      this.updateGridColumns();
    }

    this.updateDisplayedColumns(source);
    var event = {
      type: eventType,
      columns: masterList,
      column: masterList.length === 1 ? masterList[0] : null,
      api: this.gridApi,
      columnApi: this.columnApi,
      source: source
    };
    this.eventService.dispatchEvent(event);
  };

  ColumnController.prototype.setRowGroupColumns = function (colKeys, source) {
    if (source === void 0) {
      source = "api";
    }

    this.autoGroupsNeedBuilding = true;
    this.setPrimaryColumnList(colKeys, this.rowGroupColumns, Events.EVENT_COLUMN_ROW_GROUP_CHANGED, this.setRowGroupActive.bind(this), source);
  };

  ColumnController.prototype.setRowGroupActive = function (active, column, source) {
    if (active === column.isRowGroupActive()) {
      return;
    }

    column.setRowGroupActive(active, source);

    if (!active && !this.gridOptionsWrapper.isSuppressMakeColumnVisibleAfterUnGroup()) {
      column.setVisible(true, source);
    }
  };

  ColumnController.prototype.addRowGroupColumn = function (key, source) {
    if (source === void 0) {
      source = "api";
    }

    if (key) {
      this.addRowGroupColumns([key], source);
    }
  };

  ColumnController.prototype.addRowGroupColumns = function (keys, source) {
    if (source === void 0) {
      source = "api";
    }

    this.autoGroupsNeedBuilding = true;
    this.updatePrimaryColumnList(keys, this.rowGroupColumns, true, this.setRowGroupActive.bind(this, true), Events.EVENT_COLUMN_ROW_GROUP_CHANGED, source);
  };

  ColumnController.prototype.removeRowGroupColumns = function (keys, source) {
    if (source === void 0) {
      source = "api";
    }

    this.autoGroupsNeedBuilding = true;
    this.updatePrimaryColumnList(keys, this.rowGroupColumns, false, this.setRowGroupActive.bind(this, false), Events.EVENT_COLUMN_ROW_GROUP_CHANGED, source);
  };

  ColumnController.prototype.removeRowGroupColumn = function (key, source) {
    if (source === void 0) {
      source = "api";
    }

    if (key) {
      this.removeRowGroupColumns([key], source);
    }
  };

  ColumnController.prototype.addPivotColumns = function (keys, source) {
    if (source === void 0) {
      source = "api";
    }

    this.updatePrimaryColumnList(keys, this.pivotColumns, true, function (column) {
      return column.setPivotActive(true, source);
    }, Events.EVENT_COLUMN_PIVOT_CHANGED, source);
  };

  ColumnController.prototype.setPivotColumns = function (colKeys, source) {
    if (source === void 0) {
      source = "api";
    }

    this.setPrimaryColumnList(colKeys, this.pivotColumns, Events.EVENT_COLUMN_PIVOT_CHANGED, function (added, column) {
      column.setPivotActive(added, source);
    }, source);
  };

  ColumnController.prototype.addPivotColumn = function (key, source) {
    if (source === void 0) {
      source = "api";
    }

    this.addPivotColumns([key], source);
  };

  ColumnController.prototype.removePivotColumns = function (keys, source) {
    if (source === void 0) {
      source = "api";
    }

    this.updatePrimaryColumnList(keys, this.pivotColumns, false, function (column) {
      return column.setPivotActive(false, source);
    }, Events.EVENT_COLUMN_PIVOT_CHANGED, source);
  };

  ColumnController.prototype.removePivotColumn = function (key, source) {
    if (source === void 0) {
      source = "api";
    }

    this.removePivotColumns([key], source);
  };

  ColumnController.prototype.setPrimaryColumnList = function (colKeys, masterList, eventName, columnCallback, source) {
    var _this = this;

    masterList.length = 0;

    if (_.exists(colKeys)) {
      colKeys.forEach(function (key) {
        var column = _this.getPrimaryColumn(key);

        if (column) {
          masterList.push(column);
        }
      });
    }

    this.primaryColumns.forEach(function (column) {
      var added = masterList.indexOf(column) >= 0;
      columnCallback(added, column);
    });

    if (this.autoGroupsNeedBuilding) {
      this.updateGridColumns();
    }

    this.updateDisplayedColumns(source);
    var event = {
      type: eventName,
      columns: masterList,
      column: masterList.length === 1 ? masterList[0] : null,
      api: this.gridApi,
      columnApi: this.columnApi,
      source: source
    };
    this.eventService.dispatchEvent(event);
  };

  ColumnController.prototype.setValueColumns = function (colKeys, source) {
    if (source === void 0) {
      source = "api";
    }

    this.setPrimaryColumnList(colKeys, this.valueColumns, Events.EVENT_COLUMN_VALUE_CHANGED, this.setValueActive.bind(this), source);
  };

  ColumnController.prototype.setValueActive = function (active, column, source) {
    if (active === column.isValueActive()) {
      return;
    }

    column.setValueActive(active, source);

    if (active && !column.getAggFunc()) {
      var defaultAggFunc = this.aggFuncService.getDefaultAggFunc(column);
      column.setAggFunc(defaultAggFunc);
    }
  };

  ColumnController.prototype.addValueColumns = function (keys, source) {
    if (source === void 0) {
      source = "api";
    }

    this.updatePrimaryColumnList(keys, this.valueColumns, true, this.setValueActive.bind(this, true), Events.EVENT_COLUMN_VALUE_CHANGED, source);
  };

  ColumnController.prototype.addValueColumn = function (colKey, source) {
    if (source === void 0) {
      source = "api";
    }

    if (colKey) {
      this.addValueColumns([colKey], source);
    }
  };

  ColumnController.prototype.removeValueColumn = function (colKey, source) {
    if (source === void 0) {
      source = "api";
    }

    this.removeValueColumns([colKey], source);
  };

  ColumnController.prototype.removeValueColumns = function (keys, source) {
    if (source === void 0) {
      source = "api";
    }

    this.updatePrimaryColumnList(keys, this.valueColumns, false, this.setValueActive.bind(this, false), Events.EVENT_COLUMN_VALUE_CHANGED, source);
  }; // returns the width we can set to this col, taking into consideration min and max widths


  ColumnController.prototype.normaliseColumnWidth = function (column, newWidth) {
    if (newWidth < column.getMinWidth()) {
      newWidth = column.getMinWidth();
    }

    if (column.isGreaterThanMax(newWidth)) {
      newWidth = column.getMaxWidth();
    }

    return newWidth;
  };

  ColumnController.prototype.getPrimaryOrGridColumn = function (key) {
    var column = this.getPrimaryColumn(key);
    return column || this.getGridColumn(key);
  };

  ColumnController.prototype.setColumnWidth = function (key, // @key - the column who's size we want to change
  newWidth, // @newWidth - width in pixels
  shiftKey, // @takeFromAdjacent - if user has 'shift' pressed, then pixels are taken from adjacent column
  finished, // @finished - ends up in the event, tells the user if more events are to come
  source) {
    if (source === void 0) {
      source = "api";
    }

    var col = this.getPrimaryOrGridColumn(key);

    if (!col) {
      return;
    }

    var sets = [];
    sets.push({
      width: newWidth,
      ratios: [1],
      columns: [col]
    }); // if user wants to do shift resize by default, then we invert the shift operation

    var defaultIsShift = this.gridOptionsWrapper.getColResizeDefault() === 'shift';

    if (defaultIsShift) {
      shiftKey = !shiftKey;
    }

    if (shiftKey) {
      var otherCol = this.getDisplayedColAfter(col);

      if (!otherCol) {
        return;
      }

      var widthDiff = col.getActualWidth() - newWidth;
      var otherColWidth = otherCol.getActualWidth() + widthDiff;
      sets.push({
        width: otherColWidth,
        ratios: [1],
        columns: [otherCol]
      });
    }

    this.resizeColumnSets(sets, finished, source);

    if (this.flexActive) {
      this.refreshFlexedColumns();
    }
  };

  ColumnController.prototype.checkMinAndMaxWidthsForSet = function (columnResizeSet) {
    var columns = columnResizeSet.columns,
        width = columnResizeSet.width; // every col has a min width, so sum them all up and see if we have enough room
    // for all the min widths

    var minWidthAccumulated = 0;
    var maxWidthAccumulated = 0;
    var maxWidthActive = true;
    columns.forEach(function (col) {
      minWidthAccumulated += col.getMinWidth();

      if (col.getMaxWidth() > 0) {
        maxWidthAccumulated += col.getMaxWidth();
      } else {
        // if at least one columns has no max width, it means the group of columns
        // then has no max width, as at least one column can take as much width as possible
        maxWidthActive = false;
      }
    });
    var minWidthPasses = width >= minWidthAccumulated;
    var maxWidthPasses = !maxWidthActive || width <= maxWidthAccumulated;
    return minWidthPasses && maxWidthPasses;
  }; // method takes sets of columns and resizes them. either all sets will be resized, or nothing
  // be resized. this is used for example when user tries to resize a group and holds shift key,
  // then both the current group (grows), and the adjacent group (shrinks), will get resized,
  // so that's two sets for this method.


  ColumnController.prototype.resizeColumnSets = function (resizeSets, finished, source) {
    var passMinMaxCheck = _.every(resizeSets, this.checkMinAndMaxWidthsForSet.bind(this));

    if (!passMinMaxCheck) {
      // even though we are not going to resize beyond min/max size, we still need to raise event when finished
      if (finished) {
        var columns = resizeSets && resizeSets.length > 0 ? resizeSets[0].columns : null;
        var event_3 = {
          type: Events.EVENT_COLUMN_RESIZED,
          columns: columns,
          column: columns && columns.length === 1 ? columns[0] : null,
          finished: finished,
          api: this.gridApi,
          columnApi: this.columnApi,
          source: source
        };
        this.eventService.dispatchEvent(event_3);
      }

      return; // don't resize!
    }

    var changedCols = [];
    var allCols = [];
    resizeSets.forEach(function (set) {
      var width = set.width,
          columns = set.columns,
          ratios = set.ratios; // keep track of pixels used, and last column gets the remaining,
      // to cater for rounding errors, and min width adjustments

      var newWidths = {};
      var finishedCols = {};
      columns.forEach(function (col) {
        return allCols.push(col);
      }); // the loop below goes through each col. if a col exceeds it's min/max width,
      // it then gets set to its min/max width and the column is removed marked as 'finished'
      // and the calculation is done again leaving this column out. take for example columns
      // {A, width: 50, maxWidth: 100}
      // {B, width: 50}
      // {C, width: 50}
      // and then the set is set to width 600 - on the first pass the grid tries to set each column
      // to 200. it checks A and sees 200 > 100 and so sets the width to 100. col A is then marked
      // as 'finished' and the calculation is done again with the remaining cols B and C, which end up
      // splitting the remaining 500 pixels.

      var finishedColsGrew = true;
      var loopCount = 0;

      var _loop_2 = function _loop_2() {
        loopCount++;

        if (loopCount > 1000) {
          // this should never happen, but in the future, someone might introduce a bug here,
          // so we stop the browser from hanging and report bug properly
          console.error('ag-Grid: infinite loop in resizeColumnSets');
          return "break";
        }

        finishedColsGrew = false;
        var subsetCols = [];
        var subsetRatioTotal = 0;
        var pixelsToDistribute = width;
        columns.forEach(function (col, index) {
          var thisColFinished = finishedCols[col.getId()];

          if (thisColFinished) {
            pixelsToDistribute -= newWidths[col.getId()];
          } else {
            subsetCols.push(col);
            var ratioThisCol = ratios[index];
            subsetRatioTotal += ratioThisCol;
          }
        }); // because we are not using all of the ratios (cols can be missing),
        // we scale the ratio. if all columns are included, then subsetRatioTotal=1,
        // and so the ratioScale will be 1.

        var ratioScale = 1 / subsetRatioTotal;
        subsetCols.forEach(function (col, index) {
          var lastCol = index === subsetCols.length - 1;
          var colNewWidth;

          if (lastCol) {
            colNewWidth = pixelsToDistribute;
          } else {
            colNewWidth = Math.round(ratios[index] * width * ratioScale);
            pixelsToDistribute -= colNewWidth;
          }

          if (colNewWidth < col.getMinWidth()) {
            colNewWidth = col.getMinWidth();
            finishedCols[col.getId()] = true;
            finishedColsGrew = true;
          } else if (col.getMaxWidth() > 0 && colNewWidth > col.getMaxWidth()) {
            colNewWidth = col.getMaxWidth();
            finishedCols[col.getId()] = true;
            finishedColsGrew = true;
          }

          newWidths[col.getId()] = colNewWidth;
        });
      };

      while (finishedColsGrew) {
        var state_1 = _loop_2();

        if (state_1 === "break") break;
      }

      columns.forEach(function (col) {
        var newWidth = newWidths[col.getId()];

        if (col.getActualWidth() !== newWidth) {
          col.setActualWidth(newWidth, source);
          changedCols.push(col);
        }
      });
    }); // if no cols changed, then no need to update more or send event.

    var atLeastOneColChanged = changedCols.length > 0;

    if (atLeastOneColChanged) {
      this.setLeftValues(source);
      this.updateBodyWidths();
      this.checkDisplayedVirtualColumns();
    } // check for change first, to avoid unnecessary firing of events
    // however we always fire 'finished' events. this is important
    // when groups are resized, as if the group is changing slowly,
    // eg 1 pixel at a time, then each change will fire change events
    // in all the columns in the group, but only one with get the pixel.


    if (atLeastOneColChanged || finished) {
      var event_4 = {
        type: Events.EVENT_COLUMN_RESIZED,
        columns: allCols,
        column: allCols.length === 1 ? allCols[0] : null,
        finished: finished,
        api: this.gridApi,
        columnApi: this.columnApi,
        source: source
      };
      this.eventService.dispatchEvent(event_4);
    }
  };

  ColumnController.prototype.setColumnAggFunc = function (column, aggFunc, source) {
    if (source === void 0) {
      source = "api";
    }

    if (!column) {
      return;
    }

    column.setAggFunc(aggFunc);
    var event = {
      type: Events.EVENT_COLUMN_VALUE_CHANGED,
      columns: [column],
      column: column,
      api: this.gridApi,
      columnApi: this.columnApi,
      source: source
    };
    this.eventService.dispatchEvent(event);
  };

  ColumnController.prototype.moveRowGroupColumn = function (fromIndex, toIndex, source) {
    if (source === void 0) {
      source = "api";
    }

    var column = this.rowGroupColumns[fromIndex];
    this.rowGroupColumns.splice(fromIndex, 1);
    this.rowGroupColumns.splice(toIndex, 0, column);
    var event = {
      type: Events.EVENT_COLUMN_ROW_GROUP_CHANGED,
      columns: this.rowGroupColumns,
      column: this.rowGroupColumns.length === 1 ? this.rowGroupColumns[0] : null,
      api: this.gridApi,
      columnApi: this.columnApi,
      source: source
    };
    this.eventService.dispatchEvent(event);
  };

  ColumnController.prototype.moveColumns = function (columnsToMoveKeys, toIndex, source) {
    if (source === void 0) {
      source = "api";
    }

    this.columnAnimationService.start();

    if (toIndex > this.gridColumns.length - columnsToMoveKeys.length) {
      console.warn('ag-Grid: tried to insert columns in invalid location, toIndex = ' + toIndex);
      console.warn('ag-Grid: remember that you should not count the moving columns when calculating the new index');
      return;
    } // we want to pull all the columns out first and put them into an ordered list


    var columnsToMove = this.getGridColumns(columnsToMoveKeys);
    var failedRules = !this.doesMovePassRules(columnsToMove, toIndex);

    if (failedRules) {
      return;
    }

    _.moveInArray(this.gridColumns, columnsToMove, toIndex);

    this.updateDisplayedColumns(source);
    var event = {
      type: Events.EVENT_COLUMN_MOVED,
      columns: columnsToMove,
      column: columnsToMove.length === 1 ? columnsToMove[0] : null,
      toIndex: toIndex,
      api: this.gridApi,
      columnApi: this.columnApi,
      source: source
    };
    this.eventService.dispatchEvent(event);
    this.columnAnimationService.finish();
  };

  ColumnController.prototype.doesMovePassRules = function (columnsToMove, toIndex) {
    // make a copy of what the grid columns would look like after the move
    var proposedColumnOrder = this.gridColumns.slice();

    _.moveInArray(proposedColumnOrder, columnsToMove, toIndex); // then check that the new proposed order of the columns passes all rules


    if (!this.doesMovePassMarryChildren(proposedColumnOrder)) {
      return false;
    }

    if (!this.doesMovePassLockedPositions(proposedColumnOrder)) {
      return false;
    }

    return true;
  };

  ColumnController.prototype.doesMovePassLockedPositions = function (proposedColumnOrder) {
    var foundNonLocked = false;
    var rulePassed = true; // go though the cols, see if any non-locked appear before any locked

    proposedColumnOrder.forEach(function (col) {
      if (col.getColDef().lockPosition) {
        if (foundNonLocked) {
          rulePassed = false;
        }
      } else {
        foundNonLocked = true;
      }
    });
    return rulePassed;
  };

  ColumnController.prototype.doesMovePassMarryChildren = function (allColumnsCopy) {
    var rulePassed = true;
    this.columnUtils.depthFirstOriginalTreeSearch(null, this.gridBalancedTree, function (child) {
      if (!(child instanceof OriginalColumnGroup)) {
        return;
      }

      var columnGroup = child;
      var marryChildren = columnGroup.getColGroupDef() && columnGroup.getColGroupDef().marryChildren;

      if (!marryChildren) {
        return;
      }

      var newIndexes = [];
      columnGroup.getLeafColumns().forEach(function (col) {
        var newColIndex = allColumnsCopy.indexOf(col);
        newIndexes.push(newColIndex);
      });
      var maxIndex = Math.max.apply(Math, newIndexes);
      var minIndex = Math.min.apply(Math, newIndexes); // spread is how far the first column in this group is away from the last column

      var spread = maxIndex - minIndex;
      var maxSpread = columnGroup.getLeafColumns().length - 1; // if the columns

      if (spread > maxSpread) {
        rulePassed = false;
      } // console.log(`maxIndex = ${maxIndex}, minIndex = ${minIndex}, spread = ${spread}, maxSpread = ${maxSpread}, fail = ${spread > (count-1)}`)
      // console.log(allColumnsCopy.map( col => col.getColDef().field).join(','));

    });
    return rulePassed;
  };

  ColumnController.prototype.moveColumn = function (key, toIndex, source) {
    if (source === void 0) {
      source = "api";
    }

    this.moveColumns([key], toIndex, source);
  };

  ColumnController.prototype.moveColumnByIndex = function (fromIndex, toIndex, source) {
    if (source === void 0) {
      source = "api";
    }

    var column = this.gridColumns[fromIndex];
    this.moveColumn(column, toIndex, source);
  }; // used by:
  // + angularGrid -> for setting body width
  // + rowController -> setting main row widths (when inserting and resizing)
  // need to cache this


  ColumnController.prototype.getBodyContainerWidth = function () {
    return this.bodyWidth;
  };

  ColumnController.prototype.getContainerWidth = function (pinned) {
    switch (pinned) {
      case Constants.PINNED_LEFT:
        return this.leftWidth;

      case Constants.PINNED_RIGHT:
        return this.rightWidth;

      default:
        return this.bodyWidth;
    }
  }; // after setColumnWidth or updateGroupsAndDisplayedColumns


  ColumnController.prototype.updateBodyWidths = function () {
    var newBodyWidth = this.getWidthOfColsInList(this.displayedCenterColumns);
    var newLeftWidth = this.getWidthOfColsInList(this.displayedLeftColumns);
    var newRightWidth = this.getWidthOfColsInList(this.displayedRightColumns); // this is used by virtual col calculation, for RTL only, as a change to body width can impact displayed
    // columns, due to RTL inverting the y coordinates

    this.bodyWidthDirty = this.bodyWidth !== newBodyWidth;
    var atLeastOneChanged = this.bodyWidth !== newBodyWidth || this.leftWidth !== newLeftWidth || this.rightWidth !== newRightWidth;

    if (atLeastOneChanged) {
      this.bodyWidth = newBodyWidth;
      this.leftWidth = newLeftWidth;
      this.rightWidth = newRightWidth; // when this fires, it is picked up by the gridPanel, which ends up in
      // gridPanel calling setWidthAndScrollPosition(), which in turn calls setVirtualViewportPosition()

      var event_5 = {
        type: Events.EVENT_DISPLAYED_COLUMNS_WIDTH_CHANGED,
        api: this.gridApi,
        columnApi: this.columnApi
      };
      this.eventService.dispatchEvent(event_5);
    }
  }; // + rowController


  ColumnController.prototype.getValueColumns = function () {
    return this.valueColumns ? this.valueColumns : [];
  }; // + rowController


  ColumnController.prototype.getPivotColumns = function () {
    return this.pivotColumns ? this.pivotColumns : [];
  }; // + clientSideRowModel


  ColumnController.prototype.isPivotActive = function () {
    return this.pivotColumns && this.pivotColumns.length > 0 && this.pivotMode;
  }; // + toolPanel


  ColumnController.prototype.getRowGroupColumns = function () {
    return this.rowGroupColumns ? this.rowGroupColumns : [];
  }; // + rowController -> while inserting rows


  ColumnController.prototype.getDisplayedCenterColumns = function () {
    return this.displayedCenterColumns;
  }; // + rowController -> while inserting rows


  ColumnController.prototype.getDisplayedLeftColumns = function () {
    return this.displayedLeftColumns;
  };

  ColumnController.prototype.getDisplayedRightColumns = function () {
    return this.displayedRightColumns;
  };

  ColumnController.prototype.getDisplayedColumns = function (type) {
    switch (type) {
      case Constants.PINNED_LEFT:
        return this.getDisplayedLeftColumns();

      case Constants.PINNED_RIGHT:
        return this.getDisplayedRightColumns();

      default:
        return this.getDisplayedCenterColumns();
    }
  }; // used by:
  // + clientSideRowController -> sorting, building quick filter text
  // + headerRenderer -> sorting (clearing icon)


  ColumnController.prototype.getAllPrimaryColumns = function () {
    return this.primaryColumns ? this.primaryColumns.slice() : null;
  };

  ColumnController.prototype.getSecondaryColumns = function () {
    return this.secondaryColumns ? this.secondaryColumns.slice() : null;
  };

  ColumnController.prototype.getAllColumnsForQuickFilter = function () {
    return this.columnsForQuickFilter;
  }; // + moveColumnController


  ColumnController.prototype.getAllGridColumns = function () {
    return this.gridColumns;
  };

  ColumnController.prototype.isEmpty = function () {
    return _.missingOrEmpty(this.gridColumns);
  };

  ColumnController.prototype.isRowGroupEmpty = function () {
    return _.missingOrEmpty(this.rowGroupColumns);
  };

  ColumnController.prototype.setColumnVisible = function (key, visible, source) {
    if (source === void 0) {
      source = "api";
    }

    this.setColumnsVisible([key], visible, source);
  };

  ColumnController.prototype.setColumnsVisible = function (keys, visible, source) {
    var _this = this;

    if (source === void 0) {
      source = "api";
    }

    this.columnAnimationService.start();
    this.actionOnGridColumns(keys, function (column) {
      if (column.isVisible() !== visible) {
        column.setVisible(visible, source);
        return true;
      }

      return false;
    }, source, function () {
      var event = {
        type: Events.EVENT_COLUMN_VISIBLE,
        visible: visible,
        column: null,
        columns: null,
        api: _this.gridApi,
        columnApi: _this.columnApi,
        source: source
      };
      return event;
    });
    this.columnAnimationService.finish();
  };

  ColumnController.prototype.setColumnPinned = function (key, pinned, source) {
    if (source === void 0) {
      source = "api";
    }

    if (key) {
      this.setColumnsPinned([key], pinned, source);
    }
  };

  ColumnController.prototype.setColumnsPinned = function (keys, pinned, source) {
    var _this = this;

    if (source === void 0) {
      source = "api";
    }

    if (this.gridOptionsWrapper.getDomLayout() === 'print') {
      console.warn("Changing the column pinning status is not allowed with domLayout='print'");
      return;
    }

    this.columnAnimationService.start();
    var actualPinned;

    if (pinned === true || pinned === Constants.PINNED_LEFT) {
      actualPinned = Constants.PINNED_LEFT;
    } else if (pinned === Constants.PINNED_RIGHT) {
      actualPinned = Constants.PINNED_RIGHT;
    } else {
      actualPinned = null;
    }

    this.actionOnGridColumns(keys, function (col) {
      if (col.getPinned() !== actualPinned) {
        col.setPinned(actualPinned);
        return true;
      }

      return false;
    }, source, function () {
      var event = {
        type: Events.EVENT_COLUMN_PINNED,
        pinned: actualPinned,
        column: null,
        columns: null,
        api: _this.gridApi,
        columnApi: _this.columnApi,
        source: source
      };
      return event;
    });
    this.columnAnimationService.finish();
  }; // does an action on a set of columns. provides common functionality for looking up the
  // columns based on key, getting a list of effected columns, and then updated the event
  // with either one column (if it was just one col) or a list of columns
  // used by: autoResize, setVisible, setPinned


  ColumnController.prototype.actionOnGridColumns = function ( // the column keys this action will be on
  keys, // the action to do - if this returns false, the column was skipped
  // and won't be included in the event
  action, // should return back a column event of the right type
  source, createEvent) {
    var _this = this;

    if (_.missingOrEmpty(keys)) {
      return;
    }

    var updatedColumns = [];
    keys.forEach(function (key) {
      var column = _this.getGridColumn(key);

      if (!column) {
        return;
      } // need to check for false with type (ie !== instead of !=)
      // as not returning anything (undefined) would also be false


      var resultOfAction = action(column);

      if (resultOfAction !== false) {
        updatedColumns.push(column);
      }
    });

    if (!updatedColumns.length) {
      return;
    }

    this.updateDisplayedColumns(source);

    if (_.exists(createEvent) && createEvent) {
      var event_6 = createEvent();
      event_6.columns = updatedColumns;
      event_6.column = updatedColumns.length === 1 ? updatedColumns[0] : null;
      this.eventService.dispatchEvent(event_6);
    }
  };

  ColumnController.prototype.getDisplayedColBefore = function (col) {
    var allDisplayedColumns = this.getAllDisplayedColumns();
    var oldIndex = allDisplayedColumns.indexOf(col);

    if (oldIndex > 0) {
      return allDisplayedColumns[oldIndex - 1];
    }

    return null;
  }; // used by:
  // + rowRenderer -> for navigation


  ColumnController.prototype.getDisplayedColAfter = function (col) {
    var allDisplayedColumns = this.getAllDisplayedColumns();
    var oldIndex = allDisplayedColumns.indexOf(col);

    if (oldIndex < allDisplayedColumns.length - 1) {
      return allDisplayedColumns[oldIndex + 1];
    }

    return null;
  };

  ColumnController.prototype.getDisplayedGroupAfter = function (columnGroup) {
    // pick one col in this group at random
    var col = columnGroup.getDisplayedLeafColumns()[0];
    var requiredLevel = columnGroup.getOriginalColumnGroup().getLevel();

    while (true) {
      // keep moving to the next col, until we get to another group
      col = this.getDisplayedColAfter(col); // if no col after, means no group after

      if (!col) {
        return null;
      } // get group at same level as the one we are looking for


      var groupPointer = col.getParent();

      while (groupPointer.getOriginalColumnGroup().getLevel() !== requiredLevel) {
        groupPointer = groupPointer.getParent();
      }

      if (groupPointer !== columnGroup) {
        return groupPointer;
      }
    }
  };

  ColumnController.prototype.isPinningLeft = function () {
    return this.displayedLeftColumns.length > 0;
  };

  ColumnController.prototype.isPinningRight = function () {
    return this.displayedRightColumns.length > 0;
  };

  ColumnController.prototype.getPrimaryAndSecondaryAndAutoColumns = function () {
    var result = this.primaryColumns ? this.primaryColumns.slice(0) : [];

    if (this.groupAutoColumns && _.exists(this.groupAutoColumns)) {
      this.groupAutoColumns.forEach(function (col) {
        return result.push(col);
      });
    }

    if (this.secondaryColumnsPresent && this.secondaryColumns) {
      this.secondaryColumns.forEach(function (column) {
        return result.push(column);
      });
    }

    return result;
  };

  ColumnController.prototype.createStateItemFromColumn = function (column) {
    var rowGroupIndex = column.isRowGroupActive() ? this.rowGroupColumns.indexOf(column) : null;
    var pivotIndex = column.isPivotActive() ? this.pivotColumns.indexOf(column) : null;
    var aggFunc = column.isValueActive() ? column.getAggFunc() : null;
    return {
      colId: column.getColId(),
      hide: !column.isVisible(),
      aggFunc: aggFunc,
      width: column.getActualWidth(),
      pivotIndex: pivotIndex,
      pinned: column.getPinned(),
      rowGroupIndex: rowGroupIndex
    };
  };

  ColumnController.prototype.getColumnState = function () {
    if (_.missing(this.primaryColumns)) {
      return [];
    }

    var primaryColumnState = this.primaryColumns.map(this.createStateItemFromColumn.bind(this));
    var groupAutoColumnState = this.groupAutoColumns // if groupAutoCols, then include them
    ? this.groupAutoColumns.map(this.createStateItemFromColumn.bind(this)) // otherwise no
    : [];
    var columnStateList = groupAutoColumnState.concat(primaryColumnState);

    if (!this.pivotMode) {
      this.orderColumnStateList(columnStateList);
    }

    return columnStateList;
  };

  ColumnController.prototype.orderColumnStateList = function (columnStateList) {
    var gridColumnIds = this.gridColumns.map(function (column) {
      return column.getColId();
    });
    columnStateList.sort(function (itemA, itemB) {
      var posA = gridColumnIds.indexOf(itemA.colId);
      var posB = gridColumnIds.indexOf(itemB.colId);
      return posA - posB;
    });
  };

  ColumnController.prototype.resetColumnState = function (suppressEverythingEvent, source) {
    // NOTE = there is one bug here that no customer has noticed - if a column has colDef.lockPosition,
    // this is ignored  below when ordering the cols. to work, we should always put lockPosition cols first.
    // As a work around, developers should just put lockPosition columns first in their colDef list.
    if (suppressEverythingEvent === void 0) {
      suppressEverythingEvent = false;
    }

    if (source === void 0) {
      source = "api";
    } // we can't use 'allColumns' as the order might of messed up, so get the primary ordered list


    var primaryColumns = this.getColumnsFromTree(this.primaryColumnTree);
    var columnStates = []; // we start at 1000, so if user has mix of rowGroup and group specified, it will work with both.
    // eg IF user has ColA.rowGroupIndex=0, ColB.rowGroupIndex=1, ColC.rowGroup=true,
    // THEN result will be ColA.rowGroupIndex=0, ColB.rowGroupIndex=1, ColC.rowGroup=1000

    var letRowGroupIndex = 1000;
    var letPivotIndex = 1000;

    if (primaryColumns) {
      primaryColumns.forEach(function (column) {
        var rowGroupIndex = column.getColDef().rowGroupIndex;
        var rowGroup = column.getColDef().rowGroup;
        var pivotIndex = column.getColDef().pivotIndex;
        var pivot = column.getColDef().pivot;
        var stateItem = {
          colId: column.getColId(),
          aggFunc: column.getColDef().aggFunc,
          hide: column.getColDef().hide,
          pinned: column.getColDef().pinned,
          rowGroupIndex: rowGroupIndex,
          pivotIndex: column.getColDef().pivotIndex,
          width: column.getColDef().width
        };

        if (_.missing(rowGroupIndex) && rowGroup) {
          stateItem.rowGroupIndex = letRowGroupIndex++;
        }

        if (_.missing(pivotIndex) && pivot) {
          stateItem.pivotIndex = letPivotIndex++;
        }

        columnStates.push(stateItem);
      });
    }

    this.setColumnState(columnStates, suppressEverythingEvent, source);
  };

  ColumnController.prototype.setColumnState = function (columnStates, suppressEverythingEvent, source) {
    var _this = this;

    if (suppressEverythingEvent === void 0) {
      suppressEverythingEvent = false;
    }

    if (source === void 0) {
      source = "api";
    }

    if (_.missingOrEmpty(this.primaryColumns)) {
      return false;
    }

    var columnStateBefore = this.getColumnState();
    this.autoGroupsNeedBuilding = true; // at the end below, this list will have all columns we got no state for

    var columnsWithNoState = this.primaryColumns.slice();
    this.rowGroupColumns = [];
    this.valueColumns = [];
    this.pivotColumns = [];
    var success = true;
    var rowGroupIndexes = {};
    var pivotIndexes = {};
    var autoGroupColumnStates = [];

    if (columnStates) {
      columnStates.forEach(function (state) {
        // auto group columns are re-created so deferring syncing with ColumnState
        if (_.exists(_this.getAutoColumn(state.colId))) {
          autoGroupColumnStates.push(state);
          return;
        }

        var column = _this.getPrimaryColumn(state.colId);

        if (!column) {
          console.warn('ag-grid: column ' + state.colId + ' not found');
          success = false;
        } else {
          _this.syncColumnWithStateItem(column, state, rowGroupIndexes, pivotIndexes, source);

          _.removeFromArray(columnsWithNoState, column);
        }
      });
    } // anything left over, we got no data for, so add in the column as non-value, non-rowGroup and hidden


    columnsWithNoState.forEach(this.syncColumnWithNoState.bind(this)); // sort the lists according to the indexes that were provided

    this.rowGroupColumns.sort(this.sortColumnListUsingIndexes.bind(this, rowGroupIndexes));
    this.pivotColumns.sort(this.sortColumnListUsingIndexes.bind(this, pivotIndexes));
    this.updateGridColumns(); // sync newly created auto group columns with ColumnState

    autoGroupColumnStates.forEach(function (stateItem) {
      var autoCol = _this.getAutoColumn(stateItem.colId);

      _this.syncColumnWithStateItem(autoCol, stateItem, rowGroupIndexes, pivotIndexes, source);
    });

    if (columnStates) {
      var orderOfColIds_1 = columnStates.map(function (stateItem) {
        return stateItem.colId;
      });
      this.gridColumns.sort(function (colA, colB) {
        var indexA = orderOfColIds_1.indexOf(colA.getId());
        var indexB = orderOfColIds_1.indexOf(colB.getId());
        return indexA - indexB;
      });
    } // this is already done in updateGridColumns, however we changed the order above (to match the order of the state
    // columns) so we need to do it again. we could of put logic into the order above to take into account fixed
    // columns, however if we did then we would have logic for updating fixed columns twice. reusing the logic here
    // is less sexy for the code here, but it keeps consistency.


    this.putFixedColumnsFirst();
    this.updateDisplayedColumns(source);

    if (!suppressEverythingEvent) {
      var event_7 = {
        type: Events.EVENT_COLUMN_EVERYTHING_CHANGED,
        api: this.gridApi,
        columnApi: this.columnApi,
        source: source
      };
      this.eventService.dispatchEvent(event_7);
    }

    this.raiseColumnEvents(columnStateBefore, source);
    return success;
  };

  ColumnController.prototype.raiseColumnEvents = function (columnStateBefore, source) {
    var _this = this;

    if (this.gridOptionsWrapper.isSuppressSetColumnStateEvents()) {
      return;
    }

    var columnStateAfter = this.getColumnState(); // raises generic ColumnEvents where all columns are returned rather than what has changed

    var raiseEventWithAllColumns = function raiseEventWithAllColumns(eventType, idMapper, columns) {
      var unchanged = _.compareArrays(columnStateBefore.map(idMapper).sort(), columnStateAfter.map(idMapper).sort());

      if (unchanged) {
        return;
      } // returning all columns rather than what has changed!


      var event = {
        type: eventType,
        columns: columns,
        column: columns.length === 1 ? columns[0] : null,
        api: _this.gridApi,
        columnApi: _this.columnApi,
        source: source
      };

      _this.eventService.dispatchEvent(event);
    }; // determines which columns have changed according to supplied predicate


    var getChangedColumns = function getChangedColumns(changedPredicate) {
      var changedColumns = [];
      var columnStateBeforeMap = {};
      columnStateBefore.forEach(function (col) {
        columnStateBeforeMap[col.colId] = col;
      });

      _this.gridColumns.forEach(function (column) {
        var colStateBefore = columnStateBeforeMap[column.getColId()];

        if (!colStateBefore || changedPredicate(colStateBefore, column)) {
          changedColumns.push(column);
        }
      });

      return changedColumns;
    }; // generic ColumnEvents which return current column list


    var valueColumnIdMapper = function valueColumnIdMapper(cs) {
      return cs.colId + '-' + cs.aggFunc;
    };

    raiseEventWithAllColumns(Events.EVENT_COLUMN_VALUE_CHANGED, valueColumnIdMapper, this.valueColumns);

    var pivotColumnIdMapper = function pivotColumnIdMapper(cs) {
      return cs.colId + '-' + cs.pivotIndex;
    };

    raiseEventWithAllColumns(Events.EVENT_COLUMN_PIVOT_CHANGED, pivotColumnIdMapper, this.pivotColumns);

    var rowGroupColumnIdMapper = function rowGroupColumnIdMapper(cs) {
      return cs.colId + '-' + cs.rowGroupIndex;
    };

    raiseEventWithAllColumns(Events.EVENT_COLUMN_ROW_GROUP_CHANGED, rowGroupColumnIdMapper, this.rowGroupColumns); // specific ColumnEvents which return what's changed

    var pinnedChangePredicate = function pinnedChangePredicate(cs, c) {
      return cs.pinned !== c.getPinned();
    };

    this.raiseColumnPinnedEvent(getChangedColumns(pinnedChangePredicate), source);

    var visibilityChangePredicate = function visibilityChangePredicate(cs, c) {
      return cs.hide === c.isVisible();
    };

    var cols = getChangedColumns(visibilityChangePredicate);
    this.raiseColumnVisibleEvent(cols, source);

    var resizeChangePredicate = function resizeChangePredicate(cs, c) {
      return cs.width !== c.getActualWidth();
    };

    this.raiseColumnResizeEvent(getChangedColumns(resizeChangePredicate), source); // special handling for moved column events

    this.raiseColumnMovedEvent(columnStateBefore, source);
  };

  ColumnController.prototype.raiseColumnPinnedEvent = function (changedColumns, source) {
    if (!changedColumns.length) {
      return;
    }

    var event = {
      type: Events.EVENT_COLUMN_PINNED,
      pinned: null,
      columns: changedColumns,
      column: null,
      api: this.gridApi,
      columnApi: this.columnApi,
      source: source
    };
    this.eventService.dispatchEvent(event);
  };

  ColumnController.prototype.raiseColumnVisibleEvent = function (changedColumns, source) {
    if (!changedColumns.length) {
      return;
    }

    var event = {
      type: Events.EVENT_COLUMN_VISIBLE,
      visible: undefined,
      columns: changedColumns,
      column: null,
      api: this.gridApi,
      columnApi: this.columnApi,
      source: source
    };
    this.eventService.dispatchEvent(event);
  };

  ColumnController.prototype.raiseColumnResizeEvent = function (changedColumns, source) {
    if (!changedColumns.length) {
      return;
    }

    var event = {
      type: Events.EVENT_COLUMN_RESIZED,
      columns: changedColumns,
      column: null,
      finished: true,
      api: this.gridApi,
      columnApi: this.columnApi,
      source: source
    };
    this.eventService.dispatchEvent(event);
  };

  ColumnController.prototype.raiseColumnMovedEvent = function (columnStateBefore, source) {
    var movedColumns = [];
    var columnStateAfter = this.getColumnState();

    var _loop_3 = function _loop_3(i) {
      var before = columnStateBefore[i];
      var after = columnStateAfter[i]; // don't consider column if reintroduced or hidden

      if (!before || after.hide) {
        return "continue";
      }

      if (before.colId !== after.colId) {
        var predicate = function predicate(column) {
          return column.getColId() === after.colId;
        };

        var movedColumn = _.find(this_1.allDisplayedColumns, predicate);

        movedColumns.push(movedColumn);
      }
    };

    var this_1 = this;

    for (var i = 0; i < columnStateAfter.length; i++) {
      _loop_3(i);
    }

    if (!movedColumns.length) {
      return;
    }

    var event = {
      type: Events.EVENT_COLUMN_MOVED,
      columns: movedColumns,
      column: null,
      toIndex: undefined,
      api: this.gridApi,
      columnApi: this.columnApi,
      source: source
    };
    this.eventService.dispatchEvent(event);
  };

  ColumnController.prototype.sortColumnListUsingIndexes = function (indexes, colA, colB) {
    var indexA = indexes[colA.getId()];
    var indexB = indexes[colB.getId()];
    return indexA - indexB;
  };

  ColumnController.prototype.syncColumnWithNoState = function (column, source) {
    column.setVisible(false, source);
    column.setAggFunc(null);
    column.setPinned(null);
    column.setRowGroupActive(false, source);
    column.setPivotActive(false, source);
    column.setValueActive(false, source);
  };

  ColumnController.prototype.syncColumnWithStateItem = function (column, stateItem, rowGroupIndexes, pivotIndexes, source) {
    if (!column) {
      return;
    } // following ensures we are left with boolean true or false, eg converts (null, undefined, 0) all to true


    column.setVisible(!stateItem.hide, source); // sets pinned to 'left' or 'right'

    column.setPinned(stateItem.pinned); // if width provided and valid, use it, otherwise stick with the old width

    var minColWidth = this.gridOptionsWrapper.getMinColWidth();

    if (stateItem.width && minColWidth && stateItem.width >= minColWidth) {
      column.setActualWidth(stateItem.width, source);
    }

    if (typeof stateItem.aggFunc === 'string') {
      column.setAggFunc(stateItem.aggFunc);
      column.setValueActive(true, source);
      this.valueColumns.push(column);
    } else {
      if (_.exists(stateItem.aggFunc)) {
        console.warn('ag-Grid: stateItem.aggFunc must be a string. if using your own aggregation ' + 'functions, register the functions first before using them in get/set state. This is because it is' + 'intended for the column state to be stored and retrieved as simple JSON.');
      }

      column.setAggFunc(null);
      column.setValueActive(false, source);
    }

    if (typeof stateItem.rowGroupIndex === 'number') {
      this.rowGroupColumns.push(column);
      column.setRowGroupActive(true, source);
      rowGroupIndexes[column.getId()] = stateItem.rowGroupIndex;
    } else {
      column.setRowGroupActive(false, source);
    }

    if (typeof stateItem.pivotIndex === 'number') {
      this.pivotColumns.push(column);
      column.setPivotActive(true, source);
      pivotIndexes[column.getId()] = stateItem.pivotIndex;
    } else {
      column.setPivotActive(false, source);
    }
  };

  ColumnController.prototype.getGridColumns = function (keys) {
    return this.getColumns(keys, this.getGridColumn.bind(this));
  };

  ColumnController.prototype.getColumns = function (keys, columnLookupCallback) {
    var foundColumns = [];

    if (keys) {
      keys.forEach(function (key) {
        var column = columnLookupCallback(key);

        if (column) {
          foundColumns.push(column);
        }
      });
    }

    return foundColumns;
  }; // used by growGroupPanel


  ColumnController.prototype.getColumnWithValidation = function (key) {
    if (key == null) {
      return null;
    }

    var column = this.getGridColumn(key);

    if (!column) {
      console.warn('ag-Grid: could not find column ' + key);
    }

    return column;
  };

  ColumnController.prototype.getPrimaryColumn = function (key) {
    return this.getColumn(key, this.primaryColumns);
  };

  ColumnController.prototype.getGridColumn = function (key) {
    return this.getColumn(key, this.gridColumns);
  };

  ColumnController.prototype.getColumn = function (key, columnList) {
    if (!key) {
      return null;
    }

    for (var i = 0; i < columnList.length; i++) {
      if (this.columnsMatch(columnList[i], key)) {
        return columnList[i];
      }
    }

    return this.getAutoColumn(key);
  };

  ColumnController.prototype.getAutoColumn = function (key) {
    var _this = this;

    if (!this.groupAutoColumns || !_.exists(this.groupAutoColumns) || _.missing(this.groupAutoColumns)) {
      return null;
    }

    return _.find(this.groupAutoColumns, function (groupCol) {
      return _this.columnsMatch(groupCol, key);
    });
  };

  ColumnController.prototype.columnsMatch = function (column, key) {
    var columnMatches = column === key;
    var colDefMatches = column.getColDef() === key;
    var idMatches = column.getColId() == key;
    return columnMatches || colDefMatches || idMatches;
  };

  ColumnController.prototype.getDisplayNameForColumn = function (column, location, includeAggFunc) {
    if (includeAggFunc === void 0) {
      includeAggFunc = false;
    }

    if (!column) {
      return null;
    }

    var headerName = this.getHeaderName(column.getColDef(), column, null, null, location);

    if (includeAggFunc) {
      return this.wrapHeaderNameWithAggFunc(column, headerName);
    }

    return headerName;
  };

  ColumnController.prototype.getDisplayNameForOriginalColumnGroup = function (columnGroup, originalColumnGroup, location) {
    var colGroupDef = originalColumnGroup ? originalColumnGroup.getColGroupDef() : null;

    if (colGroupDef) {
      return this.getHeaderName(colGroupDef, null, columnGroup, originalColumnGroup, location);
    }

    return null;
  };

  ColumnController.prototype.getDisplayNameForColumnGroup = function (columnGroup, location) {
    return this.getDisplayNameForOriginalColumnGroup(columnGroup, columnGroup.getOriginalColumnGroup(), location);
  }; // location is where the column is going to appear, ie who is calling us


  ColumnController.prototype.getHeaderName = function (colDef, column, columnGroup, originalColumnGroup, location) {
    var headerValueGetter = colDef.headerValueGetter;

    if (headerValueGetter) {
      var params = {
        colDef: colDef,
        column: column,
        columnGroup: columnGroup,
        originalColumnGroup: originalColumnGroup,
        location: location,
        api: this.gridOptionsWrapper.getApi(),
        context: this.gridOptionsWrapper.getContext()
      };

      if (typeof headerValueGetter === 'function') {
        // valueGetter is a function, so just call it
        return headerValueGetter(params);
      } else if (typeof headerValueGetter === 'string') {
        // valueGetter is an expression, so execute the expression
        return this.expressionService.evaluate(headerValueGetter, params);
      }

      console.warn('ag-grid: headerValueGetter must be a function or a string');
      return '';
    } else if (colDef.headerName != null) {
      return colDef.headerName;
    } else if (colDef.field) {
      return _.camelCaseToHumanText(colDef.field);
    }

    return '';
  };
  /*
      private getHeaderGroupName(columnGroup: ColumnGroup): string {
          let colGroupDef = columnGroup.getOriginalColumnGroup().getColGroupDef();
          let headerValueGetter = colGroupDef.headerValueGetter;
           if (headerValueGetter) {
              let params = {
                  columnGroup: columnGroup,
                  colDef: colGroupDef,
                  api: this.gridOptionsWrapper.getApi(),
                  context: this.gridOptionsWrapper.getContext()
              };
               if (typeof headerValueGetter === 'function') {
                  // valueGetter is a function, so just call it
                  return headerValueGetter(params);
              } else if (typeof headerValueGetter === 'string') {
                  // valueGetter is an expression, so execute the expression
                  return this.expressionService.evaluate(headerValueGetter, params);
              } else {
                  console.warn('ag-grid: headerValueGetter must be a function or a string');
                  return '';
              }
          } else {
              return colGroupDef.headerName;
          }
      }
  */


  ColumnController.prototype.wrapHeaderNameWithAggFunc = function (column, headerName) {
    if (this.gridOptionsWrapper.isSuppressAggFuncInHeader()) {
      return headerName;
    } // only columns with aggregation active can have aggregations


    var pivotValueColumn = column.getColDef().pivotValueColumn;

    var pivotActiveOnThisColumn = _.exists(pivotValueColumn);

    var aggFunc = null;
    var aggFuncFound; // otherwise we have a measure that is active, and we are doing aggregation on it

    if (pivotActiveOnThisColumn) {
      aggFunc = pivotValueColumn ? pivotValueColumn.getAggFunc() : null;
      aggFuncFound = true;
    } else {
      var measureActive = column.isValueActive();
      var aggregationPresent = this.pivotMode || !this.isRowGroupEmpty();

      if (measureActive && aggregationPresent) {
        aggFunc = column.getAggFunc();
        aggFuncFound = true;
      } else {
        aggFuncFound = false;
      }
    }

    if (aggFuncFound) {
      var aggFuncString = typeof aggFunc === 'string' ? aggFunc : 'func';
      var localeTextFunc = this.gridOptionsWrapper.getLocaleTextFunc();
      var aggFuncStringTranslated = localeTextFunc(aggFuncString, aggFuncString);
      return aggFuncStringTranslated + "(" + headerName + ")";
    }

    return headerName;
  }; // returns the group with matching colId and instanceId. If instanceId is missing,
  // matches only on the colId.


  ColumnController.prototype.getColumnGroup = function (colId, instanceId) {
    if (!colId) {
      return null;
    }

    if (colId instanceof ColumnGroup) {
      return colId;
    }

    var allColumnGroups = this.getAllDisplayedColumnGroups();
    var checkInstanceId = typeof instanceId === 'number';
    var result = null;
    this.columnUtils.depthFirstAllColumnTreeSearch(allColumnGroups, function (child) {
      if (child instanceof ColumnGroup) {
        var columnGroup = child;
        var matched = void 0;

        if (checkInstanceId) {
          matched = colId === columnGroup.getGroupId() && instanceId === columnGroup.getInstanceId();
        } else {
          matched = colId === columnGroup.getGroupId();
        }

        if (matched) {
          result = columnGroup;
        }
      }
    });
    return result;
  };

  ColumnController.prototype.isReady = function () {
    return this.ready;
  };

  ColumnController.prototype.createValueColumns = function (source, oldPrimaryColumns) {
    this.valueColumns = this.extractColumns(oldPrimaryColumns, this.valueColumns, function (col, flag) {
      return col.setValueActive(flag, source);
    }, // aggFunc doesn't have index variant, cos order of value cols doesn't matter, so always return null
    function () {
      return null;
    }, // aggFunc is a string, so return it's existence
    function (colDef) {
      return !!colDef.aggFunc;
    }); // all new columns added will have aggFunc missing, so set it to what is in the colDef

    this.valueColumns.forEach(function (col) {
      if (!col.getAggFunc()) {
        col.setAggFunc(col.getColDef().aggFunc);
      }
    });
  };

  ColumnController.prototype.extractRowGroupColumns = function (source, oldPrimaryColumns) {
    this.rowGroupColumns = this.extractColumns(oldPrimaryColumns, this.rowGroupColumns, function (col, flag) {
      return col.setRowGroupActive(flag, source);
    }, function (colDef) {
      return colDef.rowGroupIndex;
    }, function (colDef) {
      return colDef.rowGroup;
    });
  };

  ColumnController.prototype.extractColumns = function (oldPrimaryColumns, previousCols, setFlagFunc, getIndexFunc, getValueFunc) {
    var _this = this;

    if (!previousCols) {
      previousCols = [];
    } // remove cols that no longer exist


    var colPresentInPrimaryFunc = function colPresentInPrimaryFunc(col) {
      return _this.primaryColumns.indexOf(col) >= 0;
    };

    var colMissingFromPrimaryFunc = function colMissingFromPrimaryFunc(col) {
      return _this.primaryColumns.indexOf(col) < 0;
    };

    var colNewFunc = function colNewFunc(col) {
      return !oldPrimaryColumns || oldPrimaryColumns.indexOf(col) < 0;
    };

    var removedCols = previousCols.filter(colMissingFromPrimaryFunc);
    var existingCols = previousCols.filter(colPresentInPrimaryFunc);
    var newPrimaryCols = this.primaryColumns.filter(colNewFunc);
    removedCols.forEach(function (col) {
      return setFlagFunc(col, false);
    });
    var newCols = []; // we only want to work on new columns, as old columns already got processed first time around
    // pull out items with xxxIndex

    newPrimaryCols.forEach(function (col) {
      var index = getIndexFunc(col.getColDef());

      if (typeof index === 'number') {
        newCols.push(col);
      }
    }); // then sort them

    newCols.sort(function (colA, colB) {
      var indexA = getIndexFunc(colA.getColDef());
      var indexB = getIndexFunc(colB.getColDef());

      if (indexA === indexB) {
        return 0;
      } else if (indexA < indexB) {
        return -1;
      }

      return 1;
    }); // now just pull out items xxx (boolean value), they will be added at the end
    // after the indexed ones, but in the order the columns appear

    newPrimaryCols.forEach(function (col) {
      var booleanValue = getValueFunc(col.getColDef());

      if (booleanValue) {
        // if user already specified xxxIndex then we skip it as this col already included
        if (newCols.indexOf(col) >= 0) {
          return;
        }

        newCols.push(col);
      }
    });
    newCols.forEach(function (col) {
      return setFlagFunc(col, true);
    });
    var res = existingCols.concat(newCols);
    return res;
  };

  ColumnController.prototype.extractPivotColumns = function (source, oldPrimaryColumns) {
    this.pivotColumns = this.extractColumns(oldPrimaryColumns, this.pivotColumns, function (col, flag) {
      return col.setPivotActive(flag, source);
    }, function (colDef) {
      return colDef.pivotIndex;
    }, function (colDef) {
      return colDef.pivot;
    });
  };

  ColumnController.prototype.resetColumnGroupState = function (source) {
    if (source === void 0) {
      source = "api";
    }

    var stateItems = [];
    this.columnUtils.depthFirstOriginalTreeSearch(null, this.primaryColumnTree, function (child) {
      if (child instanceof OriginalColumnGroup) {
        var groupState = {
          groupId: child.getGroupId(),
          open: child.getColGroupDef().openByDefault
        };
        stateItems.push(groupState);
      }
    });
    this.setColumnGroupState(stateItems, source);
  };

  ColumnController.prototype.getColumnGroupState = function () {
    var columnGroupState = [];
    this.columnUtils.depthFirstOriginalTreeSearch(null, this.gridBalancedTree, function (node) {
      if (node instanceof OriginalColumnGroup) {
        var originalColumnGroup = node;
        columnGroupState.push({
          groupId: originalColumnGroup.getGroupId(),
          open: originalColumnGroup.isExpanded()
        });
      }
    });
    return columnGroupState;
  };

  ColumnController.prototype.setColumnGroupState = function (stateItems, source) {
    var _this = this;

    if (source === void 0) {
      source = "api";
    }

    this.columnAnimationService.start();
    var impactedGroups = [];
    stateItems.forEach(function (stateItem) {
      var groupKey = stateItem.groupId;
      var newValue = stateItem.open;

      var originalColumnGroup = _this.getOriginalColumnGroup(groupKey);

      if (!originalColumnGroup) {
        return;
      }

      if (originalColumnGroup.isExpanded() === newValue) {
        return;
      }

      _this.logger.log('columnGroupOpened(' + originalColumnGroup.getGroupId() + ',' + newValue + ')');

      originalColumnGroup.setExpanded(newValue);
      impactedGroups.push(originalColumnGroup);
    });
    this.updateGroupsAndDisplayedColumns(source);
    this.setFirstRightAndLastLeftPinned(source);
    impactedGroups.forEach(function (originalColumnGroup) {
      var event = {
        type: Events.EVENT_COLUMN_GROUP_OPENED,
        columnGroup: originalColumnGroup,
        api: _this.gridApi,
        columnApi: _this.columnApi
      };

      _this.eventService.dispatchEvent(event);
    });
    this.columnAnimationService.finish();
  }; // called by headerRenderer - when a header is opened or closed


  ColumnController.prototype.setColumnGroupOpened = function (key, newValue, source) {
    if (source === void 0) {
      source = "api";
    }

    var keyAsString;

    if (key instanceof OriginalColumnGroup) {
      keyAsString = key.getId();
    } else {
      keyAsString = key;
    }

    this.setColumnGroupState([{
      groupId: keyAsString,
      open: newValue
    }], source);
  };

  ColumnController.prototype.getOriginalColumnGroup = function (key) {
    if (key instanceof OriginalColumnGroup) {
      return key;
    }

    if (typeof key !== 'string') {
      console.error('ag-Grid: group key must be a string');
    } // otherwise, search for the column group by id


    var res = null;
    this.columnUtils.depthFirstOriginalTreeSearch(null, this.gridBalancedTree, function (node) {
      if (node instanceof OriginalColumnGroup) {
        var originalColumnGroup = node;

        if (originalColumnGroup.getId() === key) {
          res = originalColumnGroup;
        }
      }
    });
    return res;
  };

  ColumnController.prototype.calculateColumnsForDisplay = function () {
    var _this = this;

    var columnsForDisplay;

    if (this.pivotMode && !this.secondaryColumnsPresent) {
      // pivot mode is on, but we are not pivoting, so we only
      // show columns we are aggregating on
      columnsForDisplay = this.gridColumns.filter(function (column) {
        var isAutoGroupCol = _this.groupAutoColumns && _.includes(_this.groupAutoColumns, column);

        var isValueCol = _this.valueColumns && _.includes(_this.valueColumns, column);

        return isAutoGroupCol || isValueCol;
      });
    } else {
      // otherwise continue as normal. this can be working on the primary
      // or secondary columns, whatever the gridColumns are set to
      columnsForDisplay = this.gridColumns.filter(function (column) {
        // keep col if a) it's auto-group or b) it's visible
        var isAutoGroupCol = _this.groupAutoColumns && _.includes(_this.groupAutoColumns, column);

        return isAutoGroupCol || column.isVisible();
      });
    }

    return columnsForDisplay;
  };

  ColumnController.prototype.checkColSpanActiveInCols = function (columns) {
    var result = false;
    columns.forEach(function (col) {
      if (_.exists(col.getColDef().colSpan)) {
        result = true;
      }
    });
    return result;
  };

  ColumnController.prototype.calculateColumnsForGroupDisplay = function () {
    var _this = this;

    this.groupDisplayColumns = [];

    var checkFunc = function checkFunc(col) {
      var colDef = col.getColDef();

      if (colDef && _.exists(colDef.showRowGroup)) {
        _this.groupDisplayColumns.push(col);
      }
    };

    this.gridColumns.forEach(checkFunc);

    if (this.groupAutoColumns) {
      this.groupAutoColumns.forEach(checkFunc);
    }
  };

  ColumnController.prototype.getGroupDisplayColumns = function () {
    return this.groupDisplayColumns;
  };

  ColumnController.prototype.updateDisplayedColumns = function (source) {
    var columnsForDisplay = this.calculateColumnsForDisplay();
    this.buildDisplayedTrees(columnsForDisplay);
    this.calculateColumnsForGroupDisplay(); // also called when group opened/closed

    this.updateGroupsAndDisplayedColumns(source); // also called when group opened/closed

    this.setFirstRightAndLastLeftPinned(source);
  };

  ColumnController.prototype.isSecondaryColumnsPresent = function () {
    return this.secondaryColumnsPresent;
  };

  ColumnController.prototype.setSecondaryColumns = function (colDefs, source) {
    if (source === void 0) {
      source = "api";
    }

    var newColsPresent = colDefs && colDefs.length > 0; // if not cols passed, and we had to cols anyway, then do nothing

    if (!newColsPresent && !this.secondaryColumnsPresent) {
      return;
    }

    if (newColsPresent) {
      this.processSecondaryColumnDefinitions(colDefs);
      var balancedTreeResult = this.columnFactory.createColumnTree(colDefs, false);
      this.secondaryBalancedTree = balancedTreeResult.columnTree;
      this.secondaryHeaderRowCount = balancedTreeResult.treeDept + 1;
      this.secondaryColumns = this.getColumnsFromTree(this.secondaryBalancedTree);
      this.secondaryColumnsPresent = true;
    } else {
      this.secondaryBalancedTree = null;
      this.secondaryHeaderRowCount = -1;
      this.secondaryColumns = null;
      this.secondaryColumnsPresent = false;
    }

    this.updateGridColumns();
    this.updateDisplayedColumns(source);
  };

  ColumnController.prototype.processSecondaryColumnDefinitions = function (colDefs) {
    var columnCallback = this.gridOptionsWrapper.getProcessSecondaryColDefFunc();
    var groupCallback = this.gridOptionsWrapper.getProcessSecondaryColGroupDefFunc();

    if (!columnCallback && !groupCallback) {
      return undefined;
    }

    var searchForColDefs = function searchForColDefs(colDefs2) {
      colDefs2.forEach(function (abstractColDef) {
        var isGroup = _.exists(abstractColDef.children);

        if (isGroup) {
          var colGroupDef = abstractColDef;

          if (groupCallback) {
            groupCallback(colGroupDef);
          }

          searchForColDefs(colGroupDef.children);
        } else {
          var colDef = abstractColDef;

          if (columnCallback) {
            columnCallback(colDef);
          }
        }
      });
    };

    if (colDefs) {
      searchForColDefs(colDefs);
    }
  }; // called from: setColumnState, setColumnDefs, setSecondaryColumns


  ColumnController.prototype.updateGridColumns = function () {
    if (this.gridColsArePrimary) {
      this.lastPrimaryOrder = this.gridColumns;
    }

    if (this.secondaryColumns && this.secondaryBalancedTree) {
      this.gridBalancedTree = this.secondaryBalancedTree.slice();
      this.gridHeaderRowCount = this.secondaryHeaderRowCount;
      this.gridColumns = this.secondaryColumns.slice();
      this.gridColsArePrimary = false;
    } else {
      this.gridBalancedTree = this.primaryColumnTree.slice();
      this.gridHeaderRowCount = this.primaryHeaderRowCount;
      this.gridColumns = this.primaryColumns.slice();
      this.gridColsArePrimary = true; // updateGridColumns gets called after user adds a row group. we want to maintain the order of the columns
      // when this happens (eg if user moved a column) rather than revert back to the original column order.
      // likewise if changing in/out of pivot mode, we want to maintain the order of the primary cols

      this.orderGridColsLikeLastPrimary();
    }

    this.addAutoGroupToGridColumns();
    this.autoRowHeightColumns = this.gridColumns.filter(function (col) {
      return col.getColDef().autoHeight;
    });
    this.putFixedColumnsFirst();
    this.setupQuickFilterColumns();
    this.clearDisplayedColumns();
    this.colSpanActive = this.checkColSpanActiveInCols(this.gridColumns);
    var event = {
      type: Events.EVENT_GRID_COLUMNS_CHANGED,
      api: this.gridApi,
      columnApi: this.columnApi
    };
    this.eventService.dispatchEvent(event);
  };

  ColumnController.prototype.orderGridColsLikeLastPrimary = function () {
    var _this = this;

    if (_.missing(this.lastPrimaryOrder)) {
      return;
    } // only do the sort if at least one column is accounted for. columns will be not accounted for
    // if changing from secondary to primary columns


    var noColsFound = true;
    this.gridColumns.forEach(function (col) {
      if (_this.lastPrimaryOrder.indexOf(col) >= 0) {
        noColsFound = false;
      }
    });

    if (noColsFound) {
      return;
    } // order cols in the same order as before. we need to make sure that all
    // cols still exists, so filter out any that no longer exist.


    var oldColsOrdered = this.lastPrimaryOrder.filter(function (col) {
      return _this.gridColumns.indexOf(col) >= 0;
    });
    var newColsOrdered = this.gridColumns.filter(function (col) {
      return oldColsOrdered.indexOf(col) < 0;
    }); // add in the new columns, at the end (if no group), or at the end of the group (if a group)

    var newGridColumns = oldColsOrdered.slice();
    newColsOrdered.forEach(function (newCol) {
      var parent = newCol.getOriginalParent(); // if no parent, means we are not grouping, so just add the column to the end

      if (!parent) {
        newGridColumns.push(newCol);
        return;
      } // find the group the column belongs to. if no siblings at the current level (eg col in group on it's
      // own) then go up one level and look for siblings there.


      var siblings = [];

      while (!siblings.length && parent) {
        var leafCols = parent.getLeafColumns();
        leafCols.forEach(function (leafCol) {
          var presentInNewGriColumns = newGridColumns.indexOf(leafCol) >= 0;
          var noYetInSiblings = siblings.indexOf(leafCol) < 0;

          if (presentInNewGriColumns && noYetInSiblings) {
            siblings.push(leafCol);
          }
        });
        parent = parent.getOriginalParent();
      } // if no siblings exist at any level, this means the col is in a group (or parent groups) on it's own


      if (!siblings.length) {
        newGridColumns.push(newCol);
        return;
      } // find index of last column in the group


      var indexes = siblings.map(function (col) {
        return newGridColumns.indexOf(col);
      });
      var lastIndex = Math.max.apply(Math, indexes);

      _.insertIntoArray(newGridColumns, newCol, lastIndex + 1);
    });
    this.gridColumns = newGridColumns;
  };

  ColumnController.prototype.isPrimaryColumnGroupsPresent = function () {
    return this.primaryHeaderRowCount > 1;
  }; // if we are using autoGroupCols, then they should be included for quick filter. this covers the
  // following scenarios:
  // a) user provides 'field' into autoGroupCol of normal grid, so now because a valid col to filter leafs on
  // b) using tree data and user depends on autoGroupCol for first col, and we also want to filter on this
  //    (tree data is a bit different, as parent rows can be filtered on, unlike row grouping)


  ColumnController.prototype.setupQuickFilterColumns = function () {
    if (this.groupAutoColumns) {
      this.columnsForQuickFilter = this.primaryColumns.concat(this.groupAutoColumns);
    } else {
      this.columnsForQuickFilter = this.primaryColumns;
    }
  };

  ColumnController.prototype.putFixedColumnsFirst = function () {
    var locked = this.gridColumns.filter(function (c) {
      return c.getColDef().lockPosition;
    });
    var unlocked = this.gridColumns.filter(function (c) {
      return !c.getColDef().lockPosition;
    });
    this.gridColumns = locked.concat(unlocked);
  };

  ColumnController.prototype.addAutoGroupToGridColumns = function () {
    // add in auto-group here
    this.createGroupAutoColumnsIfNeeded();

    if (_.missing(this.groupAutoColumns)) {
      return;
    }

    this.gridColumns = this.groupAutoColumns ? this.groupAutoColumns.concat(this.gridColumns) : this.gridColumns;
    var autoColBalancedTree = this.columnFactory.createForAutoGroups(this.groupAutoColumns, this.gridBalancedTree);
    this.gridBalancedTree = autoColBalancedTree.concat(this.gridBalancedTree);
  }; // gets called after we copy down grid columns, to make sure any part of the gui
  // that tries to draw, eg the header, it will get empty lists of columns rather
  // than stale columns. for example, the header will received gridColumnsChanged
  // event, so will try and draw, but it will draw successfully when it acts on the
  // virtualColumnsChanged event


  ColumnController.prototype.clearDisplayedColumns = function () {
    this.displayedLeftColumnTree = [];
    this.displayedRightColumnTree = [];
    this.displayedCentreColumnTree = [];
    this.displayedLeftHeaderRows = {};
    this.displayedRightHeaderRows = {};
    this.displayedCentreHeaderRows = {};
    this.displayedLeftColumns = [];
    this.displayedRightColumns = [];
    this.displayedCenterColumns = [];
    this.allDisplayedColumns = [];
    this.allDisplayedVirtualColumns = [];
  };

  ColumnController.prototype.updateGroupsAndDisplayedColumns = function (source) {
    this.updateOpenClosedVisibilityInColumnGroups();
    this.updateDisplayedColumnsFromTrees(source);
    this.updateVirtualSets();
    this.updateBodyWidths(); // this event is picked up by the gui, headerRenderer and rowRenderer, to recalculate what columns to display

    var event = {
      type: Events.EVENT_DISPLAYED_COLUMNS_CHANGED,
      api: this.gridApi,
      columnApi: this.columnApi
    };
    this.eventService.dispatchEvent(event);
  };

  ColumnController.prototype.updateDisplayedColumnsFromTrees = function (source) {
    this.addToDisplayedColumns(this.displayedLeftColumnTree, this.displayedLeftColumns);
    this.addToDisplayedColumns(this.displayedCentreColumnTree, this.displayedCenterColumns);
    this.addToDisplayedColumns(this.displayedRightColumnTree, this.displayedRightColumns);
    this.setupAllDisplayedColumns();
    this.setLeftValues(source);
  };

  ColumnController.prototype.setupAllDisplayedColumns = function () {
    if (this.gridOptionsWrapper.isEnableRtl()) {
      this.allDisplayedColumns = this.displayedRightColumns.concat(this.displayedCenterColumns).concat(this.displayedLeftColumns);
    } else {
      this.allDisplayedColumns = this.displayedLeftColumns.concat(this.displayedCenterColumns).concat(this.displayedRightColumns);
    }
  }; // sets the left pixel position of each column


  ColumnController.prototype.setLeftValues = function (source) {
    this.setLeftValuesOfColumns(source);
    this.setLeftValuesOfGroups();
  };

  ColumnController.prototype.setLeftValuesOfColumns = function (source) {
    var _this = this; // go through each list of displayed columns


    var allColumns = this.primaryColumns.slice(0); // let totalColumnWidth = this.getWidthOfColsInList()

    var doingRtl = this.gridOptionsWrapper.isEnableRtl();
    [this.displayedLeftColumns, this.displayedRightColumns, this.displayedCenterColumns].forEach(function (columns) {
      if (doingRtl) {
        // when doing RTL, we start at the top most pixel (ie RHS) and work backwards
        var left_1 = _this.getWidthOfColsInList(columns);

        columns.forEach(function (column) {
          left_1 -= column.getActualWidth();
          column.setLeft(left_1, source);
        });
      } else {
        // otherwise normal LTR, we start at zero
        var left_2 = 0;
        columns.forEach(function (column) {
          column.setLeft(left_2, source);
          left_2 += column.getActualWidth();
        });
      }

      _.removeAllFromArray(allColumns, columns);
    }); // items left in allColumns are columns not displayed, so remove the left position. this is
    // important for the rows, as if a col is made visible, then taken out, then made visible again,
    // we don't want the animation of the cell floating in from the old position, whatever that was.

    allColumns.forEach(function (column) {
      column.setLeft(null, source);
    });
  };

  ColumnController.prototype.setLeftValuesOfGroups = function () {
    // a groups left value is the lest left value of it's children
    [this.displayedLeftColumnTree, this.displayedRightColumnTree, this.displayedCentreColumnTree].forEach(function (columns) {
      columns.forEach(function (column) {
        if (column instanceof ColumnGroup) {
          var columnGroup = column;
          columnGroup.checkLeft();
        }
      });
    });
  };

  ColumnController.prototype.addToDisplayedColumns = function (displayedColumnTree, displayedColumns) {
    displayedColumns.length = 0;
    this.columnUtils.depthFirstDisplayedColumnTreeSearch(displayedColumnTree, function (child) {
      if (child instanceof Column) {
        displayedColumns.push(child);
      }
    });
  };

  ColumnController.prototype.updateDisplayedCenterVirtualColumns = function () {
    if (this.suppressColumnVirtualisation) {
      // no virtualisation, so don't filter
      this.allDisplayedCenterVirtualColumns = this.displayedCenterColumns;
    } else {
      // filter out what should be visible
      this.allDisplayedCenterVirtualColumns = this.filterOutColumnsWithinViewport();
    }

    this.allDisplayedVirtualColumns = this.allDisplayedCenterVirtualColumns.concat(this.displayedLeftColumns).concat(this.displayedRightColumns); // return map of virtual col id's, for easy lookup when building the groups.
    // the map will be colId=>true, ie col id's mapping to 'true'.

    var result = {};
    this.allDisplayedVirtualColumns.forEach(function (col) {
      result[col.getId()] = true;
    });
    return result;
  };

  ColumnController.prototype.getVirtualHeaderGroupRow = function (type, dept) {
    var result;

    switch (type) {
      case Constants.PINNED_LEFT:
        result = this.displayedLeftHeaderRows[dept];
        break;

      case Constants.PINNED_RIGHT:
        result = this.displayedRightHeaderRows[dept];
        break;

      default:
        result = this.displayedCentreHeaderRows[dept];
        break;
    }

    if (_.missing(result)) {
      result = [];
    }

    return result;
  };

  ColumnController.prototype.updateDisplayedVirtualGroups = function (virtualColIds) {
    // go through each group, see if any of it's cols are displayed, and if yes,
    // then this group is included
    this.displayedLeftHeaderRows = {};
    this.displayedRightHeaderRows = {};
    this.displayedCentreHeaderRows = {};

    var testGroup = function testGroup(children, result, dept) {
      var returnValue = false;

      for (var i = 0; i < children.length; i++) {
        // see if this item is within viewport
        var child = children[i];
        var addThisItem = void 0;

        if (child instanceof Column) {
          // for column, test if column is included
          addThisItem = virtualColIds[child.getId()] === true;
        } else {
          // if group, base decision on children
          var columnGroup = child;
          addThisItem = testGroup(columnGroup.getDisplayedChildren(), result, dept + 1);
        }

        if (addThisItem) {
          returnValue = true;

          if (!result[dept]) {
            result[dept] = [];
          }

          result[dept].push(child);
        }
      }

      return returnValue;
    };

    testGroup(this.displayedLeftColumnTree, this.displayedLeftHeaderRows, 0);
    testGroup(this.displayedRightColumnTree, this.displayedRightHeaderRows, 0);
    testGroup(this.displayedCentreColumnTree, this.displayedCentreHeaderRows, 0);
  };

  ColumnController.prototype.updateVirtualSets = function () {
    var virtualColIds = this.updateDisplayedCenterVirtualColumns();
    this.updateDisplayedVirtualGroups(virtualColIds);
  };

  ColumnController.prototype.filterOutColumnsWithinViewport = function () {
    return this.displayedCenterColumns.filter(this.isColumnInViewport.bind(this));
  };

  ColumnController.prototype.refreshFlexedColumns = function (updatedFlexViewportWidth, source) {
    if (source === void 0) {
      source = 'flex';
    }

    if (!this.flexActive) {
      return;
    }

    this.flexViewportWidth = updatedFlexViewportWidth || this.flexViewportWidth;

    if (!this.flexViewportWidth) {
      return;
    } // If the grid has left-over space, divide it between flexing columns in proportion to their flex value.
    // A "flexing column" is one that has a 'flex' value set and is not currently being constrained by its
    // minWidth or maxWidth rules.


    var knownWidthColumns = this.displayedCenterColumns.filter(function (col) {
      return !col.getFlex();
    });
    var flexingColumns = this.displayedCenterColumns.filter(function (col) {
      return col.getFlex();
    });
    var flexingColumnSizes = [];
    var spaceForFlexingColumns;

    outer: while (true) {
      var totalFlex = flexingColumns.reduce(function (count, col) {
        return count + col.getFlex();
      }, 0);
      spaceForFlexingColumns = this.flexViewportWidth - this.getWidthOfColsInList(knownWidthColumns);

      for (var i = 0; i < flexingColumns.length; i++) {
        var col = flexingColumns[i];
        var widthByFlexRule = spaceForFlexingColumns * col.getFlex() / totalFlex;
        var constrainedWidth = void 0;

        if (widthByFlexRule < col.getMinWidth()) {
          constrainedWidth = col.getMinWidth();
        } else if (col.getMaxWidth() != null && widthByFlexRule > col.getMaxWidth()) {
          constrainedWidth = col.getMaxWidth();
        }

        if (constrainedWidth) {
          // This column is not in fact flexing as it is being constrained to a specific size
          // so remove it from the list of flexing columns and start again
          col.setActualWidth(constrainedWidth, source);

          _.removeFromArray(flexingColumns, col);

          knownWidthColumns.push(col);
          continue outer;
        }

        flexingColumnSizes[i] = Math.round(widthByFlexRule);
      }

      break;
    }

    var remainingSpace = spaceForFlexingColumns;
    flexingColumns.forEach(function (col, i) {
      col.setActualWidth(Math.min(flexingColumnSizes[i], remainingSpace), source);
      remainingSpace -= flexingColumnSizes[i];
    });
    this.setLeftValues(source);
    this.updateBodyWidths();
    this.fireResizedEventForColumns(flexingColumns, source);
  }; // called from api


  ColumnController.prototype.sizeColumnsToFit = function (gridWidth, source, silent) {
    if (source === void 0) {
      source = "sizeColumnsToFit";
    } // avoid divide by zero


    var allDisplayedColumns = this.getAllDisplayedColumns();

    if (gridWidth <= 0 || !allDisplayedColumns.length) {
      return;
    }

    var colsToSpread = [];
    var colsToNotSpread = [];
    allDisplayedColumns.forEach(function (column) {
      if (column.getColDef().suppressSizeToFit === true) {
        colsToNotSpread.push(column);
      } else {
        colsToSpread.push(column);
      }
    }); // make a copy of the cols that are going to be resized

    var colsToFireEventFor = colsToSpread.slice(0);
    var finishedResizing = false;

    var moveToNotSpread = function moveToNotSpread(column) {
      _.removeFromArray(colsToSpread, column);

      colsToNotSpread.push(column);
    };

    while (!finishedResizing) {
      finishedResizing = true;
      var availablePixels = gridWidth - this.getWidthOfColsInList(colsToNotSpread);

      if (availablePixels <= 0) {
        // no width, set everything to minimum
        colsToSpread.forEach(function (column) {
          column.setMinimum(source);
        });
      } else {
        var scale = availablePixels / this.getWidthOfColsInList(colsToSpread); // we set the pixels for the last col based on what's left, as otherwise
        // we could be a pixel or two short or extra because of rounding errors.

        var pixelsForLastCol = availablePixels; // backwards through loop, as we are removing items as we go

        for (var i = colsToSpread.length - 1; i >= 0; i--) {
          var column = colsToSpread[i];
          var newWidth = Math.round(column.getActualWidth() * scale);

          if (newWidth < column.getMinWidth()) {
            column.setMinimum(source);
            moveToNotSpread(column);
            finishedResizing = false;
          } else if (column.isGreaterThanMax(newWidth)) {
            column.setActualWidth(column.getMaxWidth(), source);
            moveToNotSpread(column);
            finishedResizing = false;
          } else {
            var onLastCol = i === 0;

            if (onLastCol) {
              column.setActualWidth(pixelsForLastCol, source);
            } else {
              column.setActualWidth(newWidth, source);
            }
          }

          pixelsForLastCol -= newWidth;
        }
      }
    }

    this.setLeftValues(source);
    this.updateBodyWidths();

    if (silent) {
      return;
    }

    this.fireResizedEventForColumns(colsToFireEventFor, source);
  };

  ColumnController.prototype.fireResizedEventForColumns = function (columns, source) {
    var _this = this;

    columns.forEach(function (column) {
      var event = {
        type: Events.EVENT_COLUMN_RESIZED,
        column: column,
        columns: [column],
        finished: true,
        api: _this.gridApi,
        columnApi: _this.columnApi,
        source: source
      };

      _this.eventService.dispatchEvent(event);
    });
  };

  ColumnController.prototype.buildDisplayedTrees = function (visibleColumns) {
    var leftVisibleColumns = [];
    var rightVisibleColumns = [];
    var centerVisibleColumns = [];
    visibleColumns.forEach(function (column) {
      switch (column.getPinned()) {
        case "left":
          leftVisibleColumns.push(column);
          break;

        case "right":
          rightVisibleColumns.push(column);
          break;

        default:
          centerVisibleColumns.push(column);
          break;
      }
    });
    var groupInstanceIdCreator = new GroupInstanceIdCreator();
    this.displayedLeftColumnTree = this.displayedGroupCreator.createDisplayedGroups(leftVisibleColumns, this.gridBalancedTree, groupInstanceIdCreator, Constants.PINNED_LEFT, this.displayedLeftColumnTree);
    this.displayedRightColumnTree = this.displayedGroupCreator.createDisplayedGroups(rightVisibleColumns, this.gridBalancedTree, groupInstanceIdCreator, Constants.PINNED_RIGHT, this.displayedRightColumnTree);
    this.displayedCentreColumnTree = this.displayedGroupCreator.createDisplayedGroups(centerVisibleColumns, this.gridBalancedTree, groupInstanceIdCreator, null, this.displayedCentreColumnTree);
  };

  ColumnController.prototype.updateOpenClosedVisibilityInColumnGroups = function () {
    var allColumnGroups = this.getAllDisplayedColumnGroups();
    this.columnUtils.depthFirstAllColumnTreeSearch(allColumnGroups, function (child) {
      if (child instanceof ColumnGroup) {
        var columnGroup = child;
        columnGroup.calculateDisplayedColumns();
      }
    });
  };

  ColumnController.prototype.getGroupAutoColumns = function () {
    return this.groupAutoColumns;
  };

  ColumnController.prototype.createGroupAutoColumnsIfNeeded = function () {
    if (!this.autoGroupsNeedBuilding) {
      return;
    }

    this.autoGroupsNeedBuilding = false;
    var groupFullWidthRow = this.gridOptionsWrapper.isGroupUseEntireRow(this.pivotMode); // we never suppress auto col for pivot mode, as there is no way for user to provide group columns
    // in pivot mode. pivot mode has auto group column (provide by grid) and value columns (provided by
    // pivot feature in the grid).

    var groupSuppressAutoColumn = this.gridOptionsWrapper.isGroupSuppressAutoColumn() && !this.pivotMode;
    var groupSuppressRow = this.gridOptionsWrapper.isGroupSuppressRow();
    var groupingActive = this.rowGroupColumns.length > 0 || this.usingTreeData;
    var needAutoColumns = groupingActive && !groupSuppressAutoColumn && !groupFullWidthRow && !groupSuppressRow;

    if (needAutoColumns) {
      var newAutoGroupCols = this.autoGroupColService.createAutoGroupColumns(this.rowGroupColumns);
      var autoColsDifferent = !this.autoColsEqual(newAutoGroupCols, this.groupAutoColumns);

      if (autoColsDifferent) {
        this.groupAutoColumns = newAutoGroupCols;
      }
    } else {
      this.groupAutoColumns = null;
    }
  };

  ColumnController.prototype.autoColsEqual = function (colsA, colsB) {
    var bothMissing = !colsA && !colsB;

    if (bothMissing) {
      return true;
    }

    var atLeastOneListMissing = !colsA || !colsB;

    if (atLeastOneListMissing || colsA.length !== colsB.length) {
      return false;
    }

    for (var i = 0; i < colsA.length; i++) {
      var colA = colsA[i];
      var colB = colsB[i];

      if (colA.getColId() !== colB.getColId()) {
        return false;
      }
    }

    return true;
  };

  ColumnController.prototype.getWidthOfColsInList = function (columnList) {
    return columnList.reduce(function (width, col) {
      return width + col.getActualWidth();
    }, 0);
  };

  ColumnController.prototype.getGridBalancedTree = function () {
    return this.gridBalancedTree;
  };

  __decorate$4([Autowired('gridOptionsWrapper')], ColumnController.prototype, "gridOptionsWrapper", void 0);

  __decorate$4([Autowired('expressionService')], ColumnController.prototype, "expressionService", void 0);

  __decorate$4([Autowired('columnFactory')], ColumnController.prototype, "columnFactory", void 0);

  __decorate$4([Autowired('displayedGroupCreator')], ColumnController.prototype, "displayedGroupCreator", void 0);

  __decorate$4([Autowired('autoWidthCalculator')], ColumnController.prototype, "autoWidthCalculator", void 0);

  __decorate$4([Autowired('eventService')], ColumnController.prototype, "eventService", void 0);

  __decorate$4([Autowired('columnUtils')], ColumnController.prototype, "columnUtils", void 0);

  __decorate$4([Autowired('context')], ColumnController.prototype, "context", void 0);

  __decorate$4([Autowired('columnAnimationService')], ColumnController.prototype, "columnAnimationService", void 0);

  __decorate$4([Autowired('autoGroupColService')], ColumnController.prototype, "autoGroupColService", void 0);

  __decorate$4([Optional('aggFuncService')], ColumnController.prototype, "aggFuncService", void 0);

  __decorate$4([Optional('valueCache')], ColumnController.prototype, "valueCache", void 0);

  __decorate$4([Autowired('columnApi')], ColumnController.prototype, "columnApi", void 0);

  __decorate$4([Autowired('gridApi')], ColumnController.prototype, "gridApi", void 0);

  __decorate$4([PostConstruct], ColumnController.prototype, "init", null);

  __decorate$4([__param$2(0, Qualifier('loggerFactory'))], ColumnController.prototype, "setBeans", null);

  ColumnController = __decorate$4([Bean('columnController')], ColumnController);
  return ColumnController;
}();
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __decorate$5 = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
}; // takes in a list of columns, as specified by the column definitions, and returns column groups


var ColumnUtils =
/** @class */
function () {
  function ColumnUtils() {}

  ColumnUtils.prototype.calculateColInitialWidth = function (colDef) {
    var optionsWrapper = this.gridOptionsWrapper;
    var minColWidth = colDef.minWidth != null ? colDef.minWidth : optionsWrapper.getMinColWidth();
    var maxColWidth = colDef.maxWidth != null ? colDef.maxWidth : optionsWrapper.getMaxColWidth() || _.getMaxSafeInteger();
    var width = colDef.width != null ? colDef.width : optionsWrapper.getColWidth();
    return Math.max(Math.min(width, maxColWidth), minColWidth);
  };

  ColumnUtils.prototype.getOriginalPathForColumn = function (column, originalBalancedTree) {
    var result = [];
    var found = false;

    var recursePath = function recursePath(balancedColumnTree, dept) {
      for (var i = 0; i < balancedColumnTree.length; i++) {
        if (found) {
          return;
        } // quit the search, so 'result' is kept with the found result


        var node = balancedColumnTree[i];

        if (node instanceof OriginalColumnGroup) {
          var nextNode = node;
          recursePath(nextNode.getChildren(), dept + 1);
          result[dept] = node;
        } else if (node === column) {
          found = true;
        }
      }
    };

    recursePath(originalBalancedTree, 0); // we should always find the path, but in case there is a bug somewhere, returning null
    // will make it fail rather than provide a 'hard to track down' bug

    return found ? result : null;
  };
  /*    public getPathForColumn(column: Column, allDisplayedColumnGroups: ColumnGroupChild[]): ColumnGroup[] {
          let result: ColumnGroup[] = [];
          let found = false;
  
          recursePath(allDisplayedColumnGroups, 0);
  
          // we should always find the path, but in case there is a bug somewhere, returning null
          // will make it fail rather than provide a 'hard to track down' bug
          if (found) {
              return result;
          } else {
              return null;
          }
  
          function recursePath(balancedColumnTree: ColumnGroupChild[], dept: number): void {
  
              for (let i = 0; i<balancedColumnTree.length; i++) {
                  if (found) {
                      // quit the search, so 'result' is kept with the found result
                      return;
                  }
                  let node = balancedColumnTree[i];
                  if (node instanceof ColumnGroup) {
                      let nextNode = <ColumnGroup> node;
                      recursePath(nextNode.getChildren(), dept+1);
                      result[dept] = node;
                  } else {
                      if (node === column) {
                          found = true;
                      }
                  }
              }
          }
      }*/


  ColumnUtils.prototype.depthFirstOriginalTreeSearch = function (parent, tree, callback) {
    var _this = this;

    if (!tree) {
      return;
    }

    tree.forEach(function (child) {
      if (child instanceof OriginalColumnGroup) {
        _this.depthFirstOriginalTreeSearch(child, child.getChildren(), callback);
      }

      callback(child, parent);
    });
  };

  ColumnUtils.prototype.depthFirstAllColumnTreeSearch = function (tree, callback) {
    var _this = this;

    if (!tree) {
      return;
    }

    tree.forEach(function (child) {
      if (child instanceof ColumnGroup) {
        _this.depthFirstAllColumnTreeSearch(child.getChildren(), callback);
      }

      callback(child);
    });
  };

  ColumnUtils.prototype.depthFirstDisplayedColumnTreeSearch = function (tree, callback) {
    var _this = this;

    if (!tree) {
      return;
    }

    tree.forEach(function (child) {
      if (child instanceof ColumnGroup) {
        _this.depthFirstDisplayedColumnTreeSearch(child.getDisplayedChildren(), callback);
      }

      callback(child);
    });
  };

  __decorate$5([Autowired('gridOptionsWrapper')], ColumnUtils.prototype, "gridOptionsWrapper", void 0);

  ColumnUtils = __decorate$5([Bean('columnUtils')], ColumnUtils);
  return ColumnUtils;
}();
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __decorate$6 = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
}; // takes in a list of columns, as specified by the column definitions, and returns column groups


var DisplayedGroupCreator =
/** @class */
function () {
  function DisplayedGroupCreator() {}

  DisplayedGroupCreator.prototype.createDisplayedGroups = function ( // all displayed columns sorted - this is the columns the grid should show
  sortedVisibleColumns, // the tree of columns, as provided by the users, used to know what groups columns roll up into
  balancedColumnTree, // creates unique id's for the group
  groupInstanceIdCreator, // whether it's left, right or center col
  pinned, // we try to reuse old groups if we can, to allow gui to do animation
  oldDisplayedGroups) {
    var _this = this;

    var result = [];
    var previousRealPath;
    var previousOriginalPath;
    var oldColumnsMapped = this.mapOldGroupsById(oldDisplayedGroups); // go through each column, then do a bottom up comparison to the previous column, and start
    // to share groups if they converge at any point.

    sortedVisibleColumns.forEach(function (currentColumn) {
      var currentOriginalPath = _this.getOriginalPathForColumn(balancedColumnTree, currentColumn);

      var currentRealPath = [];
      var firstColumn = !previousOriginalPath;

      for (var i = 0; i < currentOriginalPath.length; i++) {
        if (firstColumn || currentOriginalPath[i] !== previousOriginalPath[i]) {
          // new group needed
          var newGroup = _this.createColumnGroup(currentOriginalPath[i], groupInstanceIdCreator, oldColumnsMapped, pinned);

          currentRealPath[i] = newGroup; // if top level, add to result, otherwise add to parent

          if (i == 0) {
            result.push(newGroup);
          } else {
            currentRealPath[i - 1].addChild(newGroup);
          }
        } else {
          // reuse old group
          currentRealPath[i] = previousRealPath[i];
        }
      }

      var noColumnGroups = currentRealPath.length === 0;

      if (noColumnGroups) {
        // if we are not grouping, then the result of the above is an empty
        // path (no groups), and we just add the column to the root list.
        result.push(currentColumn);
      } else {
        var leafGroup = _.last(currentRealPath);

        leafGroup.addChild(currentColumn);
      }

      previousRealPath = currentRealPath;
      previousOriginalPath = currentOriginalPath;
    });
    this.setupParentsIntoColumns(result, null);
    return result;
  };

  DisplayedGroupCreator.prototype.createColumnGroup = function (originalGroup, groupInstanceIdCreator, oldColumnsMapped, pinned) {
    var groupId = originalGroup.getGroupId();
    var instanceId = groupInstanceIdCreator.getInstanceIdForKey(groupId);
    var uniqueId = ColumnGroup.createUniqueId(groupId, instanceId);
    var columnGroup = oldColumnsMapped[uniqueId]; // if the user is setting new colDefs, it is possible that the id's overlap, and we
    // would have a false match from above. so we double check we are talking about the
    // same original column group.

    if (columnGroup && columnGroup.getOriginalColumnGroup() !== originalGroup) {
      columnGroup = null;
    }

    if (_.exists(columnGroup)) {
      // clean out the old column group here, as we will be adding children into it again
      columnGroup.reset();
    } else {
      columnGroup = new ColumnGroup(originalGroup, groupId, instanceId, pinned);
      this.context.wireBean(columnGroup);
    }

    return columnGroup;
  }; // returns back a 2d map of ColumnGroup as follows: groupId -> instanceId -> ColumnGroup


  DisplayedGroupCreator.prototype.mapOldGroupsById = function (displayedGroups) {
    var result = {};

    var recursive = function recursive(columnsOrGroups) {
      columnsOrGroups.forEach(function (columnOrGroup) {
        if (columnOrGroup instanceof ColumnGroup) {
          var columnGroup = columnOrGroup;
          result[columnOrGroup.getUniqueId()] = columnGroup;
          recursive(columnGroup.getChildren());
        }
      });
    };

    if (displayedGroups) {
      recursive(displayedGroups);
    }

    return result;
  };

  DisplayedGroupCreator.prototype.setupParentsIntoColumns = function (columnsOrGroups, parent) {
    var _this = this;

    columnsOrGroups.forEach(function (columnsOrGroup) {
      columnsOrGroup.setParent(parent);

      if (columnsOrGroup instanceof ColumnGroup) {
        var columnGroup = columnsOrGroup;

        _this.setupParentsIntoColumns(columnGroup.getChildren(), columnGroup);
      }
    });
  };

  DisplayedGroupCreator.prototype.getOriginalPathForColumn = function (balancedColumnTree, column) {
    var result = [];
    var found = false;

    var recursePath = function recursePath(balancedColumnTree, dept) {
      for (var i = 0; i < balancedColumnTree.length; i++) {
        // quit the search, so 'result' is kept with the found result
        if (found) {
          return;
        }

        var node = balancedColumnTree[i];

        if (node instanceof OriginalColumnGroup) {
          var nextNode = node;
          recursePath(nextNode.getChildren(), dept + 1);
          result[dept] = node;
        } else if (node === column) {
          found = true;
        }
      }
    };

    recursePath(balancedColumnTree, 0); // it's possible we didn't find a path. this happens if the column is generated
    // by the grid (auto-group), in that the definition didn't come from the client. in this case,
    // we create a fake original path.

    if (found) {
      return result;
    }

    console.warn('could not get path');
    return null;
  };

  __decorate$6([Autowired('columnUtils')], DisplayedGroupCreator.prototype, "columnUtils", void 0);

  __decorate$6([Autowired('context')], DisplayedGroupCreator.prototype, "context", void 0);

  DisplayedGroupCreator = __decorate$6([Bean('displayedGroupCreator')], DisplayedGroupCreator);
  return DisplayedGroupCreator;
}();
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __spreadArrays = undefined && undefined.__spreadArrays || function () {
  for (var s = 0, i = 0, il = arguments.length; i < il; i++) {
    s += arguments[i].length;
  }

  for (var r = Array(s), k = 0, i = 0; i < il; i++) {
    for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++) {
      r[k] = a[j];
    }
  }

  return r;
};
/**
 * These keys are used for validating properties supplied on a gridOptions object, and for code generation.
 * If you change the properties on the gridOptions interface, you *must* update this file as well to be consistent.
 */


var PropertyKeys =
/** @class */
function () {
  function PropertyKeys() {}

  PropertyKeys.STRING_PROPERTIES = ['sortingOrder', 'rowClass', 'rowSelection', 'overlayLoadingTemplate', 'overlayNoRowsTemplate', 'quickFilterText', 'rowModelType', 'editType', 'domLayout', 'clipboardDeliminator', 'rowGroupPanelShow', 'multiSortKey', 'pivotColumnGroupTotals', 'pivotRowTotals', 'pivotPanelShow'];
  PropertyKeys.OBJECT_PROPERTIES = ['components', 'frameworkComponents', 'rowStyle', 'context', 'autoGroupColumnDef', 'groupColumnDef', 'localeText', 'icons', 'datasource', 'serverSideDatasource', 'viewportDatasource', 'groupRowRendererParams', 'aggFuncs', 'fullWidthCellRendererParams', 'defaultColGroupDef', 'defaultColDef', 'defaultExportParams', 'columnTypes', 'rowClassRules', 'detailGridOptions', 'detailCellRendererParams', 'loadingCellRendererParams', 'loadingOverlayComponentParams', 'noRowsOverlayComponentParams', 'popupParent', 'colResizeDefault', 'reduxStore', 'statusBar', 'sideBar'];
  PropertyKeys.ARRAY_PROPERTIES = ['slaveGrids', 'alignedGrids', 'rowData', 'columnDefs', 'excelStyles', 'pinnedTopRowData', 'pinnedBottomRowData'];
  PropertyKeys.NUMBER_PROPERTIES = ['rowHeight', 'detailRowHeight', 'rowBuffer', 'colWidth', 'headerHeight', 'groupHeaderHeight', 'floatingFiltersHeight', 'pivotHeaderHeight', 'pivotGroupHeaderHeight', 'groupDefaultExpanded', 'minColWidth', 'maxColWidth', 'viewportRowModelPageSize', 'viewportRowModelBufferSize', 'autoSizePadding', 'maxBlocksInCache', 'maxConcurrentDatasourceRequests', 'cacheOverflowSize', 'paginationPageSize', 'cacheBlockSize', 'infiniteInitialRowCount', 'scrollbarWidth', 'paginationStartPage', 'infiniteBlockSize', 'batchUpdateWaitMillis', 'blockLoadDebounceMillis', 'keepDetailRowsCount', 'undoRedoCellEditingLimit'];
  PropertyKeys.BOOLEAN_PROPERTIES = ['toolPanelSuppressRowGroups', 'toolPanelSuppressValues', 'toolPanelSuppressPivots', 'toolPanelSuppressPivotMode', 'toolPanelSuppressSideButtons', 'toolPanelSuppressColumnFilter', 'toolPanelSuppressColumnSelectAll', 'toolPanelSuppressColumnExpandAll', 'suppressMakeColumnVisibleAfterUnGroup', 'suppressRowClickSelection', 'suppressCellSelection', 'suppressHorizontalScroll', 'alwaysShowVerticalScroll', 'debug', 'enableBrowserTooltips', 'enableColResize', 'enableCellExpressions', 'enableSorting', 'enableServerSideSorting', 'enableFilter', 'enableServerSideFilter', 'angularCompileRows', 'angularCompileFilters', 'angularCompileHeaders', 'groupSuppressAutoColumn', 'groupSelectsChildren', 'groupIncludeFooter', 'groupIncludeTotalFooter', 'groupUseEntireRow', 'groupSuppressRow', 'groupSuppressBlankHeader', 'forPrint', 'suppressMenuHide', 'rowDeselection', 'unSortIcon', 'suppressMultiSort', 'singleClickEdit', 'suppressLoadingOverlay', 'suppressNoRowsOverlay', 'suppressAutoSize', 'skipHeaderOnAutoSize', 'suppressParentsInRowNodes', 'showToolPanel', 'suppressColumnMoveAnimation', 'suppressMovableColumns', 'suppressFieldDotNotation', 'enableRangeSelection', 'enableRangeHandle', 'enableFillHandle', 'suppressClearOnFillReduction', 'deltaSort', 'suppressTouch', 'suppressAsyncEvents', 'allowContextMenuWithControlKey', 'suppressContextMenu', 'suppressMenuFilterPanel', 'suppressMenuMainPanel', 'suppressMenuColumnPanel', 'rememberGroupStateWhenNewData', 'enableCellChangeFlash', 'suppressDragLeaveHidesColumns', 'suppressMiddleClickScrolls', 'suppressPreventDefaultOnMouseWheel', 'suppressUseColIdForGroups', 'suppressCopyRowsToClipboard', 'copyHeadersToClipboard', 'pivotMode', 'suppressAggFuncInHeader', 'suppressColumnVirtualisation', 'suppressAggAtRootLevel', 'suppressFocusAfterRefresh', 'functionsPassive', 'functionsReadOnly', 'animateRows', 'groupSelectsFiltered', 'groupRemoveSingleChildren', 'groupRemoveLowestSingleChildren', 'enableRtl', 'suppressClickEdit', 'rowDragManaged', 'suppressRowDrag', 'enableGroupEdit', 'embedFullWidthRows', 'deprecatedEmbedFullWidthRows', 'suppressTabbing', 'suppressPaginationPanel', 'floatingFilter', 'groupHideOpenParents', 'groupMultiAutoColumn', 'pagination', 'stopEditingWhenGridLosesFocus', 'paginationAutoPageSize', 'suppressScrollOnNewData', 'purgeClosedRowNodes', 'cacheQuickFilter', 'deltaRowDataMode', 'ensureDomOrder', 'accentedSort', 'pivotTotals', 'suppressChangeDetection', 'valueCache', 'valueCacheNeverExpires', 'aggregateOnlyChangedColumns', 'suppressAnimationFrame', 'suppressExcelExport', 'suppressCsvExport', 'treeData', 'masterDetail', 'suppressMultiRangeSelection', 'enterMovesDownAfterEdit', 'enterMovesDown', 'suppressPropertyNamesCheck', 'rowMultiSelectWithClick', 'contractColumnSelection', 'suppressEnterpriseResetOnNewColumns', 'enableOldSetFilterModel', 'suppressRowHoverHighlight', 'gridAutoHeight', 'suppressRowTransform', 'suppressClipboardPaste', 'serverSideSortingAlwaysResets', 'reactNext', 'suppressSetColumnStateEvents', 'enableCharts', 'deltaColumnMode', 'suppressMaintainUnsortedOrder', 'enableCellTextSelection', 'suppressBrowserResizeObserver', 'suppressMaxRenderedRowRestriction', 'excludeChildrenWhenTreeDataFiltering', 'keepDetailRows', 'paginateChildRows', 'preventDefaultOnContextMenu', 'undoRedoCellEditing', 'allowDragFromColumnsToolPanel'];
  /** You do not need to include event callbacks in this list, as they are generated automatically. */

  PropertyKeys.FUNCTION_PROPERTIES = ['localeTextFunc', 'groupRowInnerRenderer', 'groupRowInnerRendererFramework', 'dateComponent', 'dateComponentFramework', 'groupRowRenderer', 'groupRowRendererFramework', 'isExternalFilterPresent', 'getRowHeight', 'doesExternalFilterPass', 'getRowClass', 'getRowStyle', 'getRowClassRules', 'traverseNode', 'getContextMenuItems', 'getMainMenuItems', 'processRowPostCreate', 'processCellForClipboard', 'getNodeChildDetails', 'groupRowAggNodes', 'getRowNodeId', 'isFullWidthCell', 'fullWidthCellRenderer', 'fullWidthCellRendererFramework', 'doesDataFlower', 'processSecondaryColDef', 'processSecondaryColGroupDef', 'getBusinessKeyForNode', 'sendToClipboard', 'navigateToNextCell', 'tabToNextCell', 'getDetailRowData', 'processCellFromClipboard', 'getDocument', 'postProcessPopup', 'getChildCount', 'getDataPath', 'loadingCellRenderer', 'loadingCellRendererFramework', 'loadingOverlayComponent', 'loadingOverlayComponentFramework', 'noRowsOverlayComponent', 'noRowsOverlayComponentFramework', 'detailCellRenderer', 'detailCellRendererFramework', 'defaultGroupSortComparator', 'isRowMaster', 'isRowSelectable', 'postSort', 'processHeaderForClipboard', 'paginationNumberFormatter', 'processDataFromClipboard', 'getServerSideGroupKey', 'isServerSideGroup', 'suppressKeyboardEvent', 'createChartContainer', 'processChartOptions', 'getChartToolbarItems', 'fillOperation'];
  PropertyKeys.ALL_PROPERTIES = __spreadArrays(PropertyKeys.ARRAY_PROPERTIES, PropertyKeys.OBJECT_PROPERTIES, PropertyKeys.STRING_PROPERTIES, PropertyKeys.NUMBER_PROPERTIES, PropertyKeys.FUNCTION_PROPERTIES, PropertyKeys.BOOLEAN_PROPERTIES);
  /**
   * Used when performing property checks. This avoids noise caused when using frameworks, which can add their own
   * framework-specific properties to colDefs, gridOptions etc.
   */

  PropertyKeys.FRAMEWORK_PROPERTIES = ['__ob__', '__metadata__', 'mappedColumnProperties', 'hasChildColumns', 'toColDef', 'createColDefFromGridColumn'];
  return PropertyKeys;
}();
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var ColDefUtil =
/** @class */
function () {
  function ColDefUtil() {}

  ColDefUtil.STRING_PROPERTIES = ['headerName', 'columnGroupShow', 'headerClass', 'toolPanelClass', 'headerValueGetter', 'pivotKeys', 'groupId', 'colId', 'sort', 'field', 'type', 'tooltipComponent', 'tooltipField', 'headerTooltip', 'cellClass', 'showRowGroup', 'template', 'templateUrl', 'filter', 'aggFunc', 'cellRenderer', 'cellEditor', 'pinned', 'chartDataType'];
  ColDefUtil.OBJECT_PROPERTIES = ['headerGroupComponent', 'headerGroupComponentFramework', 'headerGroupComponentParams', 'cellStyle', 'cellRendererParams', 'cellEditorFramework', 'cellEditorParams', 'pinnedRowCellRendererFramework', 'pinnedRowCellRendererParams', 'filterFramework', 'filterParams', 'pivotValueColumn', 'headerComponent', 'headerComponentFramework', 'headerComponentParams', 'floatingFilterComponent', 'floatingFilterComponentParams', 'floatingFilterComponentFramework', 'tooltipComponent', 'tooltipComponentParams', 'tooltipComponentFramework', 'refData'];
  ColDefUtil.ARRAY_PROPERTIES = ['children', 'sortingOrder', 'allowedAggFuncs', 'menuTabs', 'pivotTotalColumnIds', 'cellClassRules', 'icons'];
  ColDefUtil.NUMBER_PROPERTIES = ['sortedAt', 'flex', 'width', 'minWidth', 'maxWidth', 'rowGroupIndex', 'pivotIndex'];
  ColDefUtil.BOOLEAN_PROPERTIES = ['suppressCellFlash', 'suppressColumnsToolPanel', 'suppressFiltersToolPanel', 'openByDefault', 'marryChildren', 'hide', 'rowGroup', 'pivot', 'checkboxSelection', 'headerCheckboxSelection', 'headerCheckboxSelectionFilteredOnly', 'suppressMenu', 'suppressSorting', 'suppressMovable', 'suppressFilter', 'lockPosition', 'lockVisible', 'lockPinned', 'unSortIcon', 'suppressSizeToFit', 'suppressResize', 'suppressAutoSize', 'enableRowGroup', 'enablePivot', 'enableValue', 'editable', 'suppressPaste', 'suppressNavigable', 'enableCellChangeFlash', 'rowDrag', 'dndSource', 'autoHeight', 'sortable', 'resizable', 'singleClickEdit'];
  ColDefUtil.FUNCTION_PROPERTIES = ['dndSourceOnRowDrag', 'valueGetter', 'valueSetter', 'filterValueGetter', 'keyCreator', 'cellRenderer', 'cellRendererFramework', 'pinnedRowCellRenderer', 'valueFormatter', 'pinnedRowValueFormatter', 'valueParser', 'comparator', 'equals', 'pivotComparator', 'suppressKeyboardEvent', 'colSpan', 'rowSpan', 'getQuickFilterText', 'newValueHandler', 'onCellValueChanged', 'onCellClicked', 'onCellDoubleClicked', 'onCellContextMenu', 'tooltip', 'tooltipValueGetter', 'tooltipComponent', 'tooltipComponentFramework', 'cellRendererSelector', 'cellEditorSelector'];
  ColDefUtil.ALL_PROPERTIES = ColDefUtil.ARRAY_PROPERTIES.concat(ColDefUtil.OBJECT_PROPERTIES).concat(ColDefUtil.STRING_PROPERTIES).concat(ColDefUtil.NUMBER_PROPERTIES).concat(ColDefUtil.FUNCTION_PROPERTIES).concat(ColDefUtil.BOOLEAN_PROPERTIES); // used when doing property checks - this causes noise when using frameworks which can add their own fw specific
  // properties to colDefs, gridOptions etc

  ColDefUtil.FRAMEWORK_PROPERTIES = ['__ob__', '__metadata__', 'mappedColumnProperties', 'hasChildColumns', 'toColDef', 'createColDefFromGridColumn'];
  return ColDefUtil;
}();
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var SideBarDefParser =
/** @class */
function () {
  function SideBarDefParser() {}

  SideBarDefParser.parse = function (toParse) {
    if (!toParse) {
      return null;
    }

    if (toParse === true) {
      return {
        toolPanels: [SideBarDefParser.DEFAULT_COLUMN_COMP, SideBarDefParser.DEFAULT_FILTER_COMP],
        defaultToolPanel: 'columns'
      };
    }

    if (typeof toParse === 'string') {
      return SideBarDefParser.parse([toParse]);
    }

    if (Array.isArray(toParse)) {
      var comps_1 = [];
      toParse.forEach(function (key) {
        var lookupResult = SideBarDefParser.DEFAULT_BY_KEY[key];

        if (!lookupResult) {
          console.warn("ag-grid: the key " + key + " is not a valid key for specifying a tool panel, valid keys are: " + Object.keys(SideBarDefParser.DEFAULT_BY_KEY).join(','));
          return;
        }

        comps_1.push(lookupResult);
      });

      if (comps_1.length === 0) {
        return null;
      }

      return {
        toolPanels: comps_1,
        defaultToolPanel: comps_1[0].id
      };
    }

    var result = {
      toolPanels: SideBarDefParser.parseComponents(toParse.toolPanels),
      defaultToolPanel: toParse.defaultToolPanel,
      hiddenByDefault: toParse.hiddenByDefault,
      position: toParse.position
    };
    return result;
  };

  SideBarDefParser.parseComponents = function (from) {
    var result = [];
    from.forEach(function (it) {
      var toAdd = null;

      if (typeof it === 'string') {
        var lookupResult = SideBarDefParser.DEFAULT_BY_KEY[it];

        if (!lookupResult) {
          console.warn("ag-grid: the key " + it + " is not a valid key for specifying a tool panel, valid keys are: " + Object.keys(SideBarDefParser.DEFAULT_BY_KEY).join(','));
          return;
        }

        toAdd = lookupResult;
      } else {
        toAdd = it;
      }

      result.push(toAdd);
    });
    return result;
  };

  SideBarDefParser.DEFAULT_COLUMN_COMP = {
    id: 'columns',
    labelDefault: 'Columns',
    labelKey: 'columns',
    iconKey: 'columns',
    toolPanel: 'agColumnsToolPanel'
  };
  SideBarDefParser.DEFAULT_FILTER_COMP = {
    id: 'filters',
    labelDefault: 'Filters',
    labelKey: 'filters',
    iconKey: 'filter',
    toolPanel: 'agFiltersToolPanel'
  };
  SideBarDefParser.DEFAULT_BY_KEY = {
    columns: SideBarDefParser.DEFAULT_COLUMN_COMP,
    filters: SideBarDefParser.DEFAULT_FILTER_COMP
  };
  return SideBarDefParser;
}();
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __decorate$7 = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var __param$3 = undefined && undefined.__param || function (paramIndex, decorator) {
  return function (target, key) {
    decorator(target, key, paramIndex);
  };
};

var __spreadArrays$1 = undefined && undefined.__spreadArrays || function () {
  for (var s = 0, i = 0, il = arguments.length; i < il; i++) {
    s += arguments[i].length;
  }

  for (var r = Array(s), k = 0, i = 0; i < il; i++) {
    for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++) {
      r[k] = a[j];
    }
  }

  return r;
};

var DEFAULT_ROW_HEIGHT = 25;
var DEFAULT_DETAIL_ROW_HEIGHT = 300;
var DEFAULT_VIEWPORT_ROW_MODEL_PAGE_SIZE = 5;
var DEFAULT_VIEWPORT_ROW_MODEL_BUFFER_SIZE = 5;
var DEFAULT_KEEP_DETAIL_ROW_COUNT = 10;

function isTrue(value) {
  return value === true || value === 'true';
}

function zeroOrGreater(value, defaultValue) {
  if (value >= 0) {
    return value;
  } // zero gets returned if number is missing or the wrong type


  return defaultValue;
}

function oneOrGreater(value, defaultValue) {
  if (value > 0) {
    return value;
  } // zero gets returned if number is missing or the wrong type


  return defaultValue;
}

var GridOptionsWrapper =
/** @class */
function () {
  function GridOptionsWrapper() {
    this.propertyEventService = new EventService();
    this.domDataKey = '__AG_' + Math.random().toString();
    this.layoutElements = [];
  }

  GridOptionsWrapper_1 = GridOptionsWrapper;

  GridOptionsWrapper.prototype.agWire = function (gridApi, columnApi) {
    this.gridOptions.api = gridApi;
    this.gridOptions.columnApi = columnApi;
    this.checkForDeprecated();
    this.checkForViolations();
  };

  GridOptionsWrapper.prototype.destroy = function () {
    // need to remove these, as we don't own the lifecycle of the gridOptions, we need to
    // remove the references in case the user keeps the grid options, we want the rest
    // of the grid to be picked up by the garbage collector
    this.gridOptions.api = null;
    this.gridOptions.columnApi = null;
  };

  GridOptionsWrapper.prototype.init = function () {
    if (!(this.gridOptions.suppressPropertyNamesCheck === true)) {
      this.checkGridOptionsProperties();
      this.checkColumnDefProperties();
    }

    var async = this.useAsyncEvents();
    this.eventService.addGlobalListener(this.globalEventHandler.bind(this), async);

    if (this.isGroupSelectsChildren() && this.isSuppressParentsInRowNodes()) {
      console.warn("ag-Grid: 'groupSelectsChildren' does not work with 'suppressParentsInRowNodes', this selection method needs the part in rowNode to work");
    }

    if (this.isGroupSelectsChildren()) {
      if (!this.isRowSelectionMulti()) {
        console.warn("ag-Grid: rowSelection must be 'multiple' for groupSelectsChildren to make sense");
      }

      if (this.isRowModelServerSide()) {
        console.warn('ag-Grid: group selects children is NOT support for Server Side Row Model. ' + 'This is because the rows are lazy loaded, so selecting a group is not possible as' + 'the grid has no way of knowing what the children are.');
      }
    }

    if (this.isGroupRemoveSingleChildren() && this.isGroupHideOpenParents()) {
      console.warn("ag-Grid: groupRemoveSingleChildren and groupHideOpenParents do not work with each other, you need to pick one. And don't ask us how to us these together on our support forum either you will get the same answer!");
    }

    if (this.isEnableRangeSelection()) {
      ModuleRegistry.assertRegistered(exports.ModuleNames.RangeSelectionModule, 'enableRangeSelection');
    }

    if (!this.isEnableRangeSelection() && (this.isEnableRangeHandle() || this.isEnableFillHandle())) {
      console.warn("ag-Grid: 'enableRangeHandle' and 'enableFillHandle' will not work unless 'enableRangeSelection' is set to true");
    }

    this.addEventListener(GridOptionsWrapper_1.PROP_DOM_LAYOUT, this.updateLayoutClasses.bind(this));
  };

  GridOptionsWrapper.prototype.checkColumnDefProperties = function () {
    var _this = this;

    if (this.gridOptions.columnDefs == null) {
      return;
    }

    this.gridOptions.columnDefs.forEach(function (colDef) {
      var userProperties = Object.getOwnPropertyNames(colDef);

      var validProperties = __spreadArrays$1(ColDefUtil.ALL_PROPERTIES, ColDefUtil.FRAMEWORK_PROPERTIES);

      _this.checkProperties(userProperties, validProperties, validProperties, 'colDef', 'https://www.ag-grid.com/javascript-grid-column-properties/');
    });
  };

  GridOptionsWrapper.prototype.checkGridOptionsProperties = function () {
    var userProperties = Object.getOwnPropertyNames(this.gridOptions);

    var validProperties = __spreadArrays$1(PropertyKeys.ALL_PROPERTIES, PropertyKeys.FRAMEWORK_PROPERTIES, _.values(Events).map(function (event) {
      return ComponentUtil.getCallbackForEvent(event);
    }));

    var validPropertiesAndExceptions = __spreadArrays$1(validProperties, ['api', 'columnApi']);

    this.checkProperties(userProperties, validPropertiesAndExceptions, validProperties, 'gridOptions', 'https://www.ag-grid.com/javascript-grid-properties/');
  };

  GridOptionsWrapper.prototype.checkProperties = function (userProperties, validPropertiesAndExceptions, validProperties, containerName, docsUrl) {
    var invalidProperties = _.fuzzyCheckStrings(userProperties, validPropertiesAndExceptions, validProperties);

    _.iterateObject(invalidProperties, function (key, value) {
      console.warn("ag-grid: invalid " + containerName + " property '" + key + "' did you mean any of these: " + value.slice(0, 8).join(", "));
    });

    if (Object.keys(invalidProperties).length > 0) {
      console.warn("ag-grid: to see all the valid " + containerName + " properties please check: " + docsUrl);
    }
  }; // returns the dom data, or undefined if not found


  GridOptionsWrapper.prototype.getDomData = function (element, key) {
    var domData = element[this.domDataKey];
    return domData ? domData[key] : undefined;
  };

  GridOptionsWrapper.prototype.setDomData = function (element, key, value) {
    var domData = element[this.domDataKey];

    if (_.missing(domData)) {
      domData = {};
      element[this.domDataKey] = domData;
    }

    domData[key] = value;
  };

  GridOptionsWrapper.prototype.isRowSelection = function () {
    return this.gridOptions.rowSelection === 'single' || this.gridOptions.rowSelection === 'multiple';
  };

  GridOptionsWrapper.prototype.isRowDeselection = function () {
    return isTrue(this.gridOptions.rowDeselection);
  };

  GridOptionsWrapper.prototype.isRowSelectionMulti = function () {
    return this.gridOptions.rowSelection === 'multiple';
  };

  GridOptionsWrapper.prototype.isRowMultiSelectWithClick = function () {
    return isTrue(this.gridOptions.rowMultiSelectWithClick);
  };

  GridOptionsWrapper.prototype.getContext = function () {
    return this.gridOptions.context;
  };

  GridOptionsWrapper.prototype.isPivotMode = function () {
    return isTrue(this.gridOptions.pivotMode);
  };

  GridOptionsWrapper.prototype.isPivotTotals = function () {
    return isTrue(this.gridOptions.pivotTotals);
  };

  GridOptionsWrapper.prototype.getPivotColumnGroupTotals = function () {
    return this.gridOptions.pivotColumnGroupTotals;
  };

  GridOptionsWrapper.prototype.getPivotRowTotals = function () {
    return this.gridOptions.pivotRowTotals;
  };

  GridOptionsWrapper.prototype.isRowModelInfinite = function () {
    return this.gridOptions.rowModelType === Constants.ROW_MODEL_TYPE_INFINITE;
  };

  GridOptionsWrapper.prototype.isRowModelViewport = function () {
    return this.gridOptions.rowModelType === Constants.ROW_MODEL_TYPE_VIEWPORT;
  };

  GridOptionsWrapper.prototype.isRowModelServerSide = function () {
    return this.gridOptions.rowModelType === Constants.ROW_MODEL_TYPE_SERVER_SIDE;
  };

  GridOptionsWrapper.prototype.isRowModelDefault = function () {
    return _.missing(this.gridOptions.rowModelType) || this.gridOptions.rowModelType === Constants.ROW_MODEL_TYPE_CLIENT_SIDE || this.gridOptions.rowModelType === Constants.DEPRECATED_ROW_MODEL_TYPE_NORMAL;
  };

  GridOptionsWrapper.prototype.isFullRowEdit = function () {
    return this.gridOptions.editType === 'fullRow';
  };

  GridOptionsWrapper.prototype.isSuppressFocusAfterRefresh = function () {
    return isTrue(this.gridOptions.suppressFocusAfterRefresh);
  };

  GridOptionsWrapper.prototype.isSuppressBrowserResizeObserver = function () {
    return isTrue(this.gridOptions.suppressBrowserResizeObserver);
  };

  GridOptionsWrapper.prototype.isSuppressMaintainUnsortedOrder = function () {
    return isTrue(this.gridOptions.suppressMaintainUnsortedOrder);
  };

  GridOptionsWrapper.prototype.isSuppressClearOnFillReduction = function () {
    return isTrue(this.gridOptions.suppressClearOnFillReduction);
  };

  GridOptionsWrapper.prototype.isShowToolPanel = function () {
    return isTrue(this.gridOptions.sideBar && Array.isArray(this.getSideBar().toolPanels));
  };

  GridOptionsWrapper.prototype.getSideBar = function () {
    return this.gridOptions.sideBar;
  };

  GridOptionsWrapper.prototype.isSuppressTouch = function () {
    return isTrue(this.gridOptions.suppressTouch);
  };

  GridOptionsWrapper.prototype.isSuppressRowTransform = function () {
    return isTrue(this.gridOptions.suppressRowTransform);
  };

  GridOptionsWrapper.prototype.isSuppressSetColumnStateEvents = function () {
    return isTrue(this.gridOptions.suppressSetColumnStateEvents);
  };

  GridOptionsWrapper.prototype.isAllowDragFromColumnsToolPanel = function () {
    return isTrue(this.gridOptions.allowDragFromColumnsToolPanel);
  };

  GridOptionsWrapper.prototype.useAsyncEvents = function () {
    return !isTrue(this.gridOptions.suppressAsyncEvents);
  };

  GridOptionsWrapper.prototype.isEnableCellChangeFlash = function () {
    return isTrue(this.gridOptions.enableCellChangeFlash);
  };

  GridOptionsWrapper.prototype.isGroupSelectsChildren = function () {
    var result = isTrue(this.gridOptions.groupSelectsChildren);

    if (result && this.isTreeData()) {
      console.warn('ag-Grid: groupSelectsChildren does not work with tree data');
      return false;
    }

    return result;
  };

  GridOptionsWrapper.prototype.isSuppressRowHoverHighlight = function () {
    return isTrue(this.gridOptions.suppressRowHoverHighlight);
  };

  GridOptionsWrapper.prototype.isGroupSelectsFiltered = function () {
    return isTrue(this.gridOptions.groupSelectsFiltered);
  };

  GridOptionsWrapper.prototype.isGroupHideOpenParents = function () {
    return isTrue(this.gridOptions.groupHideOpenParents);
  }; // if we are doing hideOpenParents, then we always have groupMultiAutoColumn, otherwise hideOpenParents would not work


  GridOptionsWrapper.prototype.isGroupMultiAutoColumn = function () {
    return isTrue(this.gridOptions.groupMultiAutoColumn) || isTrue(this.gridOptions.groupHideOpenParents);
  };

  GridOptionsWrapper.prototype.isGroupRemoveSingleChildren = function () {
    return isTrue(this.gridOptions.groupRemoveSingleChildren);
  };

  GridOptionsWrapper.prototype.isGroupRemoveLowestSingleChildren = function () {
    return isTrue(this.gridOptions.groupRemoveLowestSingleChildren);
  };

  GridOptionsWrapper.prototype.isGroupIncludeFooter = function () {
    return isTrue(this.gridOptions.groupIncludeFooter);
  };

  GridOptionsWrapper.prototype.isGroupIncludeTotalFooter = function () {
    return isTrue(this.gridOptions.groupIncludeTotalFooter);
  };

  GridOptionsWrapper.prototype.isGroupSuppressBlankHeader = function () {
    return isTrue(this.gridOptions.groupSuppressBlankHeader);
  };

  GridOptionsWrapper.prototype.isSuppressRowClickSelection = function () {
    return isTrue(this.gridOptions.suppressRowClickSelection);
  };

  GridOptionsWrapper.prototype.isSuppressCellSelection = function () {
    return isTrue(this.gridOptions.suppressCellSelection);
  };

  GridOptionsWrapper.prototype.isSuppressMultiSort = function () {
    return isTrue(this.gridOptions.suppressMultiSort);
  };

  GridOptionsWrapper.prototype.isMultiSortKeyCtrl = function () {
    return this.gridOptions.multiSortKey === 'ctrl';
  };

  GridOptionsWrapper.prototype.isGroupSuppressAutoColumn = function () {
    return isTrue(this.gridOptions.groupSuppressAutoColumn);
  };

  GridOptionsWrapper.prototype.isSuppressDragLeaveHidesColumns = function () {
    return isTrue(this.gridOptions.suppressDragLeaveHidesColumns);
  };

  GridOptionsWrapper.prototype.isSuppressScrollOnNewData = function () {
    return isTrue(this.gridOptions.suppressScrollOnNewData);
  };

  GridOptionsWrapper.prototype.isRowDragManaged = function () {
    return isTrue(this.gridOptions.rowDragManaged);
  };

  GridOptionsWrapper.prototype.isSuppressRowDrag = function () {
    return isTrue(this.gridOptions.suppressRowDrag);
  }; // returns either 'print', 'autoHeight' or 'normal' (normal is the default)


  GridOptionsWrapper.prototype.getDomLayout = function () {
    var domLayout = this.gridOptions.domLayout || Constants.DOM_LAYOUT_NORMAL;
    var validLayouts = [Constants.DOM_LAYOUT_PRINT, Constants.DOM_LAYOUT_AUTO_HEIGHT, Constants.DOM_LAYOUT_NORMAL];

    if (validLayouts.indexOf(domLayout) === -1) {
      _.doOnce(function () {
        return console.warn("ag-Grid: " + domLayout + " is not valid for DOM Layout, valid values are " + Constants.DOM_LAYOUT_NORMAL + ", " + Constants.DOM_LAYOUT_AUTO_HEIGHT + " and " + Constants.DOM_LAYOUT_PRINT);
      }, 'warn about dom layout values');

      return Constants.DOM_LAYOUT_NORMAL;
    }

    return domLayout;
  };

  GridOptionsWrapper.prototype.isSuppressHorizontalScroll = function () {
    return isTrue(this.gridOptions.suppressHorizontalScroll);
  };

  GridOptionsWrapper.prototype.isSuppressMaxRenderedRowRestriction = function () {
    return isTrue(this.gridOptions.suppressMaxRenderedRowRestriction);
  };

  GridOptionsWrapper.prototype.isExcludeChildrenWhenTreeDataFiltering = function () {
    return isTrue(this.gridOptions.excludeChildrenWhenTreeDataFiltering);
  };

  GridOptionsWrapper.prototype.isAlwaysShowVerticalScroll = function () {
    return isTrue(this.gridOptions.alwaysShowVerticalScroll);
  };

  GridOptionsWrapper.prototype.isSuppressLoadingOverlay = function () {
    return isTrue(this.gridOptions.suppressLoadingOverlay);
  };

  GridOptionsWrapper.prototype.isSuppressNoRowsOverlay = function () {
    return isTrue(this.gridOptions.suppressNoRowsOverlay);
  };

  GridOptionsWrapper.prototype.isSuppressFieldDotNotation = function () {
    return isTrue(this.gridOptions.suppressFieldDotNotation);
  };

  GridOptionsWrapper.prototype.getPinnedTopRowData = function () {
    return this.gridOptions.pinnedTopRowData;
  };

  GridOptionsWrapper.prototype.getPinnedBottomRowData = function () {
    return this.gridOptions.pinnedBottomRowData;
  };

  GridOptionsWrapper.prototype.isFunctionsPassive = function () {
    return isTrue(this.gridOptions.functionsPassive);
  };

  GridOptionsWrapper.prototype.isSuppressTabbing = function () {
    return isTrue(this.gridOptions.suppressTabbing);
  };

  GridOptionsWrapper.prototype.isSuppressChangeDetection = function () {
    return isTrue(this.gridOptions.suppressChangeDetection);
  };

  GridOptionsWrapper.prototype.isSuppressAnimationFrame = function () {
    return isTrue(this.gridOptions.suppressAnimationFrame);
  };

  GridOptionsWrapper.prototype.getQuickFilterText = function () {
    return this.gridOptions.quickFilterText;
  };

  GridOptionsWrapper.prototype.isCacheQuickFilter = function () {
    return isTrue(this.gridOptions.cacheQuickFilter);
  };

  GridOptionsWrapper.prototype.isUnSortIcon = function () {
    return isTrue(this.gridOptions.unSortIcon);
  };

  GridOptionsWrapper.prototype.isSuppressMenuHide = function () {
    return isTrue(this.gridOptions.suppressMenuHide);
  };

  GridOptionsWrapper.prototype.isEnterMovesDownAfterEdit = function () {
    return isTrue(this.gridOptions.enterMovesDownAfterEdit);
  };

  GridOptionsWrapper.prototype.isEnterMovesDown = function () {
    return isTrue(this.gridOptions.enterMovesDown);
  };

  GridOptionsWrapper.prototype.isUndoRedoCellEditing = function () {
    return isTrue(this.gridOptions.undoRedoCellEditing);
  };

  GridOptionsWrapper.prototype.getUndoRedoCellEditingLimit = function () {
    return this.gridOptions.undoRedoCellEditingLimit;
  };

  GridOptionsWrapper.prototype.getRowStyle = function () {
    return this.gridOptions.rowStyle;
  };

  GridOptionsWrapper.prototype.getRowClass = function () {
    return this.gridOptions.rowClass;
  };

  GridOptionsWrapper.prototype.getRowStyleFunc = function () {
    return this.gridOptions.getRowStyle;
  };

  GridOptionsWrapper.prototype.getRowClassFunc = function () {
    return this.gridOptions.getRowClass;
  };

  GridOptionsWrapper.prototype.rowClassRules = function () {
    return this.gridOptions.rowClassRules;
  };

  GridOptionsWrapper.prototype.getCreateChartContainerFunc = function () {
    return this.gridOptions.createChartContainer;
  };

  GridOptionsWrapper.prototype.getPopupParent = function () {
    return this.gridOptions.popupParent;
  };

  GridOptionsWrapper.prototype.getBlockLoadDebounceMillis = function () {
    return this.gridOptions.blockLoadDebounceMillis;
  };

  GridOptionsWrapper.prototype.getPostProcessPopupFunc = function () {
    return this.gridOptions.postProcessPopup;
  };

  GridOptionsWrapper.prototype.getDoesDataFlowerFunc = function () {
    return this.gridOptions.doesDataFlower;
  };

  GridOptionsWrapper.prototype.getPaginationNumberFormatterFunc = function () {
    return this.gridOptions.paginationNumberFormatter;
  };

  GridOptionsWrapper.prototype.getChildCountFunc = function () {
    return this.gridOptions.getChildCount;
  };

  GridOptionsWrapper.prototype.getDefaultGroupSortComparator = function () {
    return this.gridOptions.defaultGroupSortComparator;
  };

  GridOptionsWrapper.prototype.getIsFullWidthCellFunc = function () {
    return this.gridOptions.isFullWidthCell;
  };

  GridOptionsWrapper.prototype.getFullWidthCellRendererParams = function () {
    return this.gridOptions.fullWidthCellRendererParams;
  };

  GridOptionsWrapper.prototype.isEmbedFullWidthRows = function () {
    return isTrue(this.gridOptions.embedFullWidthRows) || isTrue(this.gridOptions.deprecatedEmbedFullWidthRows);
  };

  GridOptionsWrapper.prototype.getSuppressKeyboardEventFunc = function () {
    return this.gridOptions.suppressKeyboardEvent;
  };

  GridOptionsWrapper.prototype.getBusinessKeyForNodeFunc = function () {
    return this.gridOptions.getBusinessKeyForNode;
  };

  GridOptionsWrapper.prototype.getApi = function () {
    return this.gridOptions.api;
  };

  GridOptionsWrapper.prototype.getColumnApi = function () {
    return this.gridOptions.columnApi;
  };

  GridOptionsWrapper.prototype.isDeltaRowDataMode = function () {
    return isTrue(this.gridOptions.deltaRowDataMode);
  };

  GridOptionsWrapper.prototype.isDeltaColumnMode = function () {
    return isTrue(this.gridOptions.deltaColumnMode);
  };

  GridOptionsWrapper.prototype.isEnsureDomOrder = function () {
    return isTrue(this.gridOptions.ensureDomOrder);
  };

  GridOptionsWrapper.prototype.isEnableCharts = function () {
    if (isTrue(this.gridOptions.enableCharts)) {
      return ModuleRegistry.assertRegistered(exports.ModuleNames.GridChartsModule, 'enableCharts');
    }

    return false;
  };

  GridOptionsWrapper.prototype.getColResizeDefault = function () {
    return this.gridOptions.colResizeDefault;
  };

  GridOptionsWrapper.prototype.isSingleClickEdit = function () {
    return isTrue(this.gridOptions.singleClickEdit);
  };

  GridOptionsWrapper.prototype.isSuppressClickEdit = function () {
    return isTrue(this.gridOptions.suppressClickEdit);
  };

  GridOptionsWrapper.prototype.isStopEditingWhenGridLosesFocus = function () {
    return isTrue(this.gridOptions.stopEditingWhenGridLosesFocus);
  };

  GridOptionsWrapper.prototype.getGroupDefaultExpanded = function () {
    return this.gridOptions.groupDefaultExpanded;
  };

  GridOptionsWrapper.prototype.getMaxConcurrentDatasourceRequests = function () {
    return this.gridOptions.maxConcurrentDatasourceRequests;
  };

  GridOptionsWrapper.prototype.getMaxBlocksInCache = function () {
    return this.gridOptions.maxBlocksInCache;
  };

  GridOptionsWrapper.prototype.getCacheOverflowSize = function () {
    return this.gridOptions.cacheOverflowSize;
  };

  GridOptionsWrapper.prototype.getPaginationPageSize = function () {
    return this.gridOptions.paginationPageSize;
  };

  GridOptionsWrapper.prototype.isPaginateChildRows = function () {
    // if using groupSuppressRow, means we are not showing parent rows,
    // so we always paginate on the child rows here as there are no parent rows
    var shouldPaginate = this.isGroupSuppressRow() || this.isGroupRemoveSingleChildren() || this.isGroupRemoveLowestSingleChildren();

    if (shouldPaginate) {
      return true;
    }

    return isTrue(this.gridOptions.paginateChildRows);
  };

  GridOptionsWrapper.prototype.getCacheBlockSize = function () {
    return this.gridOptions.cacheBlockSize;
  };

  GridOptionsWrapper.prototype.getInfiniteInitialRowCount = function () {
    return this.gridOptions.infiniteInitialRowCount;
  };

  GridOptionsWrapper.prototype.isPurgeClosedRowNodes = function () {
    return isTrue(this.gridOptions.purgeClosedRowNodes);
  };

  GridOptionsWrapper.prototype.isSuppressPaginationPanel = function () {
    return isTrue(this.gridOptions.suppressPaginationPanel);
  };

  GridOptionsWrapper.prototype.getRowData = function () {
    return this.gridOptions.rowData;
  }; // this property is different - we never allow groupUseEntireRow if in pivot mode,
  // as otherwise we don't see the pivot values.


  GridOptionsWrapper.prototype.isGroupUseEntireRow = function (pivotMode) {
    return pivotMode ? false : isTrue(this.gridOptions.groupUseEntireRow);
  };

  GridOptionsWrapper.prototype.isEnableRtl = function () {
    return isTrue(this.gridOptions.enableRtl);
  };

  GridOptionsWrapper.prototype.getAutoGroupColumnDef = function () {
    return this.gridOptions.autoGroupColumnDef;
  };

  GridOptionsWrapper.prototype.isGroupSuppressRow = function () {
    return isTrue(this.gridOptions.groupSuppressRow);
  };

  GridOptionsWrapper.prototype.getRowGroupPanelShow = function () {
    return this.gridOptions.rowGroupPanelShow;
  };

  GridOptionsWrapper.prototype.getPivotPanelShow = function () {
    return this.gridOptions.pivotPanelShow;
  };

  GridOptionsWrapper.prototype.isAngularCompileRows = function () {
    return isTrue(this.gridOptions.angularCompileRows);
  };

  GridOptionsWrapper.prototype.isAngularCompileFilters = function () {
    return isTrue(this.gridOptions.angularCompileFilters);
  };

  GridOptionsWrapper.prototype.isAngularCompileHeaders = function () {
    return isTrue(this.gridOptions.angularCompileHeaders);
  };

  GridOptionsWrapper.prototype.isDebug = function () {
    return isTrue(this.gridOptions.debug);
  };

  GridOptionsWrapper.prototype.getColumnDefs = function () {
    return this.gridOptions.columnDefs;
  };

  GridOptionsWrapper.prototype.getColumnTypes = function () {
    return this.gridOptions.columnTypes;
  };

  GridOptionsWrapper.prototype.getDatasource = function () {
    return this.gridOptions.datasource;
  };

  GridOptionsWrapper.prototype.getViewportDatasource = function () {
    return this.gridOptions.viewportDatasource;
  };

  GridOptionsWrapper.prototype.getServerSideDatasource = function () {
    return this.gridOptions.serverSideDatasource;
  };

  GridOptionsWrapper.prototype.isAccentedSort = function () {
    return isTrue(this.gridOptions.accentedSort);
  };

  GridOptionsWrapper.prototype.isEnableBrowserTooltips = function () {
    return isTrue(this.gridOptions.enableBrowserTooltips);
  };

  GridOptionsWrapper.prototype.isEnableCellExpressions = function () {
    return isTrue(this.gridOptions.enableCellExpressions);
  };

  GridOptionsWrapper.prototype.isEnableGroupEdit = function () {
    return isTrue(this.gridOptions.enableGroupEdit);
  };

  GridOptionsWrapper.prototype.isSuppressMiddleClickScrolls = function () {
    return isTrue(this.gridOptions.suppressMiddleClickScrolls);
  };

  GridOptionsWrapper.prototype.isPreventDefaultOnContextMenu = function () {
    return isTrue(this.gridOptions.preventDefaultOnContextMenu);
  };

  GridOptionsWrapper.prototype.isSuppressPreventDefaultOnMouseWheel = function () {
    return isTrue(this.gridOptions.suppressPreventDefaultOnMouseWheel);
  };

  GridOptionsWrapper.prototype.isSuppressColumnVirtualisation = function () {
    return isTrue(this.gridOptions.suppressColumnVirtualisation);
  };

  GridOptionsWrapper.prototype.isSuppressContextMenu = function () {
    return isTrue(this.gridOptions.suppressContextMenu);
  };

  GridOptionsWrapper.prototype.isAllowContextMenuWithControlKey = function () {
    return isTrue(this.gridOptions.allowContextMenuWithControlKey);
  };

  GridOptionsWrapper.prototype.isSuppressCopyRowsToClipboard = function () {
    return isTrue(this.gridOptions.suppressCopyRowsToClipboard);
  };

  GridOptionsWrapper.prototype.isCopyHeadersToClipboard = function () {
    return isTrue(this.gridOptions.copyHeadersToClipboard);
  };

  GridOptionsWrapper.prototype.isSuppressClipboardPaste = function () {
    return isTrue(this.gridOptions.suppressClipboardPaste);
  };

  GridOptionsWrapper.prototype.isPagination = function () {
    return isTrue(this.gridOptions.pagination);
  };

  GridOptionsWrapper.prototype.isSuppressEnterpriseResetOnNewColumns = function () {
    return isTrue(this.gridOptions.suppressEnterpriseResetOnNewColumns);
  };

  GridOptionsWrapper.prototype.getProcessDataFromClipboardFunc = function () {
    return this.gridOptions.processDataFromClipboard;
  };

  GridOptionsWrapper.prototype.getBatchUpdateWaitMillis = function () {
    return _.exists(this.gridOptions.batchUpdateWaitMillis) ? this.gridOptions.batchUpdateWaitMillis : Constants.BATCH_WAIT_MILLIS;
  };

  GridOptionsWrapper.prototype.isSuppressMovableColumns = function () {
    return isTrue(this.gridOptions.suppressMovableColumns);
  };

  GridOptionsWrapper.prototype.isAnimateRows = function () {
    // never allow animating if enforcing the row order
    if (this.isEnsureDomOrder()) {
      return false;
    }

    return isTrue(this.gridOptions.animateRows);
  };

  GridOptionsWrapper.prototype.isSuppressColumnMoveAnimation = function () {
    return isTrue(this.gridOptions.suppressColumnMoveAnimation);
  };

  GridOptionsWrapper.prototype.isSuppressAggFuncInHeader = function () {
    return isTrue(this.gridOptions.suppressAggFuncInHeader);
  };

  GridOptionsWrapper.prototype.isSuppressAggAtRootLevel = function () {
    return isTrue(this.gridOptions.suppressAggAtRootLevel);
  };

  GridOptionsWrapper.prototype.isEnableRangeSelection = function () {
    return ModuleRegistry.isRegistered(exports.ModuleNames.RangeSelectionModule) && isTrue(this.gridOptions.enableRangeSelection);
  };

  GridOptionsWrapper.prototype.isEnableRangeHandle = function () {
    return isTrue(this.gridOptions.enableRangeHandle);
  };

  GridOptionsWrapper.prototype.isEnableFillHandle = function () {
    return isTrue(this.gridOptions.enableFillHandle);
  };

  GridOptionsWrapper.prototype.getFillOperation = function () {
    return this.gridOptions.fillOperation;
  };

  GridOptionsWrapper.prototype.isSuppressMultiRangeSelection = function () {
    return isTrue(this.gridOptions.suppressMultiRangeSelection);
  };

  GridOptionsWrapper.prototype.isPaginationAutoPageSize = function () {
    return isTrue(this.gridOptions.paginationAutoPageSize);
  };

  GridOptionsWrapper.prototype.isRememberGroupStateWhenNewData = function () {
    return isTrue(this.gridOptions.rememberGroupStateWhenNewData);
  };

  GridOptionsWrapper.prototype.getIcons = function () {
    return this.gridOptions.icons;
  };

  GridOptionsWrapper.prototype.getAggFuncs = function () {
    return this.gridOptions.aggFuncs;
  };

  GridOptionsWrapper.prototype.getSortingOrder = function () {
    return this.gridOptions.sortingOrder;
  };

  GridOptionsWrapper.prototype.getAlignedGrids = function () {
    return this.gridOptions.alignedGrids;
  };

  GridOptionsWrapper.prototype.isMasterDetail = function () {
    var masterDetail = isTrue(this.gridOptions.masterDetail);

    if (masterDetail) {
      return ModuleRegistry.assertRegistered(exports.ModuleNames.MasterDetailModule, 'masterDetail');
    } else {
      return false;
    }
  };

  GridOptionsWrapper.prototype.isKeepDetailRows = function () {
    return isTrue(this.gridOptions.keepDetailRows);
  };

  GridOptionsWrapper.prototype.getKeepDetailRowsCount = function () {
    if (this.gridOptions.keepDetailRowsCount > 0) {
      return this.gridOptions.keepDetailRowsCount;
    }

    return DEFAULT_KEEP_DETAIL_ROW_COUNT;
  };

  GridOptionsWrapper.prototype.getIsRowMasterFunc = function () {
    return this.gridOptions.isRowMaster;
  };

  GridOptionsWrapper.prototype.getIsRowSelectableFunc = function () {
    return this.gridOptions.isRowSelectable;
  };

  GridOptionsWrapper.prototype.getGroupRowRendererParams = function () {
    return this.gridOptions.groupRowRendererParams;
  };

  GridOptionsWrapper.prototype.getOverlayLoadingTemplate = function () {
    return this.gridOptions.overlayLoadingTemplate;
  };

  GridOptionsWrapper.prototype.getOverlayNoRowsTemplate = function () {
    return this.gridOptions.overlayNoRowsTemplate;
  };

  GridOptionsWrapper.prototype.isSuppressAutoSize = function () {
    return isTrue(this.gridOptions.suppressAutoSize);
  };

  GridOptionsWrapper.prototype.isEnableCellTextSelection = function () {
    return isTrue(this.gridOptions.enableCellTextSelection);
  };

  GridOptionsWrapper.prototype.isSuppressParentsInRowNodes = function () {
    return isTrue(this.gridOptions.suppressParentsInRowNodes);
  };

  GridOptionsWrapper.prototype.isFunctionsReadOnly = function () {
    return isTrue(this.gridOptions.functionsReadOnly);
  };

  GridOptionsWrapper.prototype.isFloatingFilter = function () {
    return this.gridOptions.floatingFilter;
  };

  GridOptionsWrapper.prototype.isEnableCellTextSelect = function () {
    return isTrue(this.gridOptions.enableCellTextSelection);
  };

  GridOptionsWrapper.prototype.isEnableOldSetFilterModel = function () {
    return isTrue(this.gridOptions.enableOldSetFilterModel);
  };

  GridOptionsWrapper.prototype.getDefaultColDef = function () {
    return this.gridOptions.defaultColDef;
  };

  GridOptionsWrapper.prototype.getDefaultColGroupDef = function () {
    return this.gridOptions.defaultColGroupDef;
  };

  GridOptionsWrapper.prototype.getDefaultExportParams = function () {
    return this.gridOptions.defaultExportParams;
  };

  GridOptionsWrapper.prototype.isSuppressCsvExport = function () {
    return isTrue(this.gridOptions.suppressCsvExport);
  };

  GridOptionsWrapper.prototype.isAllowShowChangeAfterFilter = function () {
    return isTrue(this.gridOptions.allowShowChangeAfterFilter);
  };

  GridOptionsWrapper.prototype.isSuppressExcelExport = function () {
    return isTrue(this.gridOptions.suppressExcelExport);
  };

  GridOptionsWrapper.prototype.isSuppressMakeColumnVisibleAfterUnGroup = function () {
    return isTrue(this.gridOptions.suppressMakeColumnVisibleAfterUnGroup);
  };

  GridOptionsWrapper.prototype.getNodeChildDetailsFunc = function () {
    return this.gridOptions.getNodeChildDetails;
  };

  GridOptionsWrapper.prototype.getDataPathFunc = function () {
    return this.gridOptions.getDataPath;
  };

  GridOptionsWrapper.prototype.getIsServerSideGroupFunc = function () {
    return this.gridOptions.isServerSideGroup;
  };

  GridOptionsWrapper.prototype.getServerSideGroupKeyFunc = function () {
    return this.gridOptions.getServerSideGroupKey;
  };

  GridOptionsWrapper.prototype.getGroupRowAggNodesFunc = function () {
    return this.gridOptions.groupRowAggNodes;
  };

  GridOptionsWrapper.prototype.getContextMenuItemsFunc = function () {
    return this.gridOptions.getContextMenuItems;
  };

  GridOptionsWrapper.prototype.getMainMenuItemsFunc = function () {
    return this.gridOptions.getMainMenuItems;
  };

  GridOptionsWrapper.prototype.getChartToolbarItemsFunc = function () {
    return this.gridOptions.getChartToolbarItems;
  };

  GridOptionsWrapper.prototype.getRowNodeIdFunc = function () {
    return this.gridOptions.getRowNodeId;
  };

  GridOptionsWrapper.prototype.getNavigateToNextCellFunc = function () {
    return this.gridOptions.navigateToNextCell;
  };

  GridOptionsWrapper.prototype.getTabToNextCellFunc = function () {
    return this.gridOptions.tabToNextCell;
  };

  GridOptionsWrapper.prototype.isTreeData = function () {
    var usingTreeData = isTrue(this.gridOptions.treeData);

    if (usingTreeData) {
      return ModuleRegistry.assertRegistered(exports.ModuleNames.RowGroupingModule, 'Tree Data');
    } else {
      return false;
    }
  };

  GridOptionsWrapper.prototype.isValueCache = function () {
    return isTrue(this.gridOptions.valueCache);
  };

  GridOptionsWrapper.prototype.isValueCacheNeverExpires = function () {
    return isTrue(this.gridOptions.valueCacheNeverExpires);
  };

  GridOptionsWrapper.prototype.isDeltaSort = function () {
    return isTrue(this.gridOptions.deltaSort);
  };

  GridOptionsWrapper.prototype.isAggregateOnlyChangedColumns = function () {
    return isTrue(this.gridOptions.aggregateOnlyChangedColumns);
  };

  GridOptionsWrapper.prototype.getProcessSecondaryColDefFunc = function () {
    return this.gridOptions.processSecondaryColDef;
  };

  GridOptionsWrapper.prototype.getProcessSecondaryColGroupDefFunc = function () {
    return this.gridOptions.processSecondaryColGroupDef;
  };

  GridOptionsWrapper.prototype.getSendToClipboardFunc = function () {
    return this.gridOptions.sendToClipboard;
  };

  GridOptionsWrapper.prototype.getProcessRowPostCreateFunc = function () {
    return this.gridOptions.processRowPostCreate;
  };

  GridOptionsWrapper.prototype.getProcessCellForClipboardFunc = function () {
    return this.gridOptions.processCellForClipboard;
  };

  GridOptionsWrapper.prototype.getProcessHeaderForClipboardFunc = function () {
    return this.gridOptions.processHeaderForClipboard;
  };

  GridOptionsWrapper.prototype.getProcessCellFromClipboardFunc = function () {
    return this.gridOptions.processCellFromClipboard;
  };

  GridOptionsWrapper.prototype.getViewportRowModelPageSize = function () {
    return oneOrGreater(this.gridOptions.viewportRowModelPageSize, DEFAULT_VIEWPORT_ROW_MODEL_PAGE_SIZE);
  };

  GridOptionsWrapper.prototype.getViewportRowModelBufferSize = function () {
    return zeroOrGreater(this.gridOptions.viewportRowModelBufferSize, DEFAULT_VIEWPORT_ROW_MODEL_BUFFER_SIZE);
  }; // public getCellRenderers(): {[key: string]: {new(): ICellRenderer} | ICellRendererFunc} { return this.gridOptions.cellRenderers; }
  // public getCellEditors(): {[key: string]: {new(): ICellEditor}} { return this.gridOptions.cellEditors; }


  GridOptionsWrapper.prototype.isServerSideSortingAlwaysResets = function () {
    return isTrue(this.gridOptions.serverSideSortingAlwaysResets);
  };

  GridOptionsWrapper.prototype.getPostSortFunc = function () {
    return this.gridOptions.postSort;
  };

  GridOptionsWrapper.prototype.getProcessChartOptionsFunc = function () {
    return this.gridOptions.processChartOptions;
  };

  GridOptionsWrapper.prototype.getClipboardDeliminator = function () {
    return _.exists(this.gridOptions.clipboardDeliminator) ? this.gridOptions.clipboardDeliminator : '\t';
  };

  GridOptionsWrapper.prototype.setProperty = function (key, value) {
    var gridOptionsNoType = this.gridOptions;
    var previousValue = gridOptionsNoType[key];

    if (previousValue !== value) {
      gridOptionsNoType[key] = value;
      var event_1 = {
        type: key,
        currentValue: value,
        previousValue: previousValue
      };
      this.propertyEventService.dispatchEvent(event_1);
    }
  }; // this logic is repeated in lots of places. any element that had different CSS
  // dependent on the layout needs to have the layout class added ot it.


  GridOptionsWrapper.prototype.addLayoutElement = function (element) {
    this.layoutElements.push(element);
    this.updateLayoutClasses();
  };

  GridOptionsWrapper.prototype.updateLayoutClasses = function () {
    var domLayout = this.getDomLayout();
    var domLayoutAutoHeight = domLayout === Constants.DOM_LAYOUT_AUTO_HEIGHT;
    var domLayoutPrint = domLayout === Constants.DOM_LAYOUT_PRINT;
    var domLayoutNormal = domLayout === Constants.DOM_LAYOUT_NORMAL;
    this.layoutElements.forEach(function (e) {
      _.addOrRemoveCssClass(e, 'ag-layout-auto-height', domLayoutAutoHeight);

      _.addOrRemoveCssClass(e, 'ag-layout-normal', domLayoutNormal);

      _.addOrRemoveCssClass(e, 'ag-layout-print', domLayoutPrint);
    });
  };

  GridOptionsWrapper.prototype.addEventListener = function (key, listener) {
    GridOptionsWrapper_1.checkEventDeprecation(key);
    this.propertyEventService.addEventListener(key, listener);
  };

  GridOptionsWrapper.checkEventDeprecation = function (eventName) {
    if (eventName === 'floatingRowDataChanged') {
      console.warn('ag-Grid: floatingRowDataChanged is now called pinnedRowDataChanged');
    }
  };

  GridOptionsWrapper.prototype.removeEventListener = function (key, listener) {
    this.propertyEventService.removeEventListener(key, listener);
  };

  GridOptionsWrapper.prototype.isSkipHeaderOnAutoSize = function () {
    return !!this.gridOptions.skipHeaderOnAutoSize;
  };

  GridOptionsWrapper.prototype.getAutoSizePadding = function () {
    return this.gridOptions.autoSizePadding && this.gridOptions.autoSizePadding > 0 ? this.gridOptions.autoSizePadding : 20;
  }; // properties


  GridOptionsWrapper.prototype.getHeaderHeight = function () {
    if (typeof this.gridOptions.headerHeight === 'number') {
      return this.gridOptions.headerHeight;
    }

    return this.specialForNewMaterial(25, 'headerHeight');
  };

  GridOptionsWrapper.prototype.getFloatingFiltersHeight = function () {
    if (typeof this.gridOptions.floatingFiltersHeight === 'number') {
      return this.gridOptions.floatingFiltersHeight;
    }

    return this.specialForNewMaterial(25, 'headerHeight');
  };

  GridOptionsWrapper.prototype.getGroupHeaderHeight = function () {
    if (typeof this.gridOptions.groupHeaderHeight === 'number') {
      return this.gridOptions.groupHeaderHeight;
    }

    return this.getHeaderHeight();
  };

  GridOptionsWrapper.prototype.getPivotHeaderHeight = function () {
    if (typeof this.gridOptions.pivotHeaderHeight === 'number') {
      return this.gridOptions.pivotHeaderHeight;
    }

    return this.getHeaderHeight();
  };

  GridOptionsWrapper.prototype.getPivotGroupHeaderHeight = function () {
    if (typeof this.gridOptions.pivotGroupHeaderHeight === 'number') {
      return this.gridOptions.pivotGroupHeaderHeight;
    }

    return this.getGroupHeaderHeight();
  };

  GridOptionsWrapper.prototype.isExternalFilterPresent = function () {
    if (typeof this.gridOptions.isExternalFilterPresent === 'function') {
      return this.gridOptions.isExternalFilterPresent();
    }

    return false;
  };

  GridOptionsWrapper.prototype.doesExternalFilterPass = function (node) {
    if (typeof this.gridOptions.doesExternalFilterPass === 'function') {
      return this.gridOptions.doesExternalFilterPass(node);
    }

    return false;
  };

  GridOptionsWrapper.prototype.getDocument = function () {
    // if user is providing document, we use the users one,
    // otherwise we use the document on the global namespace.
    var result = null;

    if (this.gridOptions.getDocument && _.exists(this.gridOptions.getDocument)) {
      result = this.gridOptions.getDocument();
    }

    if (result && _.exists(result)) {
      return result;
    }

    return document;
  };

  GridOptionsWrapper.prototype.getMinColWidth = function () {
    if (this.gridOptions.minColWidth && this.gridOptions.minColWidth > GridOptionsWrapper_1.MIN_COL_WIDTH) {
      return this.gridOptions.minColWidth;
    }

    return GridOptionsWrapper_1.MIN_COL_WIDTH;
  };

  GridOptionsWrapper.prototype.getMaxColWidth = function () {
    if (this.gridOptions.maxColWidth && this.gridOptions.maxColWidth > GridOptionsWrapper_1.MIN_COL_WIDTH) {
      return this.gridOptions.maxColWidth;
    }

    return null;
  };

  GridOptionsWrapper.prototype.getColWidth = function () {
    if (typeof this.gridOptions.colWidth !== 'number' || this.gridOptions.colWidth < GridOptionsWrapper_1.MIN_COL_WIDTH) {
      return 200;
    }

    return this.gridOptions.colWidth;
  };

  GridOptionsWrapper.prototype.getRowBuffer = function () {
    var rowBuffer = this.gridOptions.rowBuffer;

    if (typeof rowBuffer === 'number') {
      if (rowBuffer < 0) {
        _.doOnce(function () {
          return console.warn("ag-Grid: rowBuffer should not be negative");
        }, 'warn rowBuffer negative');

        this.gridOptions.rowBuffer = rowBuffer = 0;
      }
    } else {
      rowBuffer = Constants.ROW_BUFFER_SIZE;
    }

    return rowBuffer;
  };

  GridOptionsWrapper.prototype.getRowBufferInPixels = function () {
    var rowsToBuffer = this.getRowBuffer();
    var defaultRowHeight = this.getRowHeightAsNumber();
    return rowsToBuffer * defaultRowHeight;
  }; // the user might be using some non-standard scrollbar, eg a scrollbar that has zero
  // width and overlays (like the Safari scrollbar, but presented in Chrome). so we
  // allow the user to provide the scroll width before we work it out.


  GridOptionsWrapper.prototype.getScrollbarWidth = function () {
    if (this.scrollWidth == null) {
      var useGridOptions = typeof this.gridOptions.scrollbarWidth === 'number' && this.gridOptions.scrollbarWidth >= 0;
      this.scrollWidth = useGridOptions ? this.gridOptions.scrollbarWidth : _.getScrollbarWidth();
    }

    return this.scrollWidth;
  };

  GridOptionsWrapper.prototype.checkForDeprecated = function () {
    var _this = this; // casting to generic object, so typescript compiles even though
    // we are looking for attributes that don't exist


    var options = this.gridOptions;

    if (options.suppressUnSort) {
      console.warn('ag-grid: as of v1.12.4 suppressUnSort is not used. Please use sortingOrder instead.');
    }

    if (options.suppressDescSort) {
      console.warn('ag-grid: as of v1.12.4 suppressDescSort is not used. Please use sortingOrder instead.');
    }

    if (options.groupAggFields) {
      console.warn('ag-grid: as of v3 groupAggFields is not used. Please add appropriate agg fields to your columns.');
    }

    if (options.groupHidePivotColumns) {
      console.warn('ag-grid: as of v3 groupHidePivotColumns is not used as pivot columns are now called rowGroup columns. Please refer to the documentation');
    }

    if (options.groupKeys) {
      console.warn('ag-grid: as of v3 groupKeys is not used. You need to set rowGroupIndex on the columns to group. Please refer to the documentation');
    }

    if (typeof options.groupDefaultExpanded === 'boolean') {
      console.warn('ag-grid: groupDefaultExpanded can no longer be boolean. for groupDefaultExpanded=true, use groupDefaultExpanded=9999 instead, to expand all the groups');
    }

    if (options.onRowDeselected || options.rowDeselected) {
      console.warn('ag-grid: since version 3.4 event rowDeselected no longer exists, please check the docs');
    }

    if (options.rowsAlreadyGrouped) {
      console.warn('ag-grid: since version 3.4 rowsAlreadyGrouped no longer exists, please use getNodeChildDetails() instead');
    }

    if (options.groupAggFunction) {
      console.warn('ag-grid: since version 4.3.x groupAggFunction is now called groupRowAggNodes');
    }

    if (options.checkboxSelection) {
      console.warn('ag-grid: since version 8.0.x checkboxSelection is not supported as a grid option. ' + 'If you want this on all columns, use defaultColDef instead and set it there');
    }

    if (options.paginationInitialRowCount) {
      console.warn('ag-grid: since version 9.0.x paginationInitialRowCount is now called infiniteInitialRowCount');
    }

    if (options.infinitePageSize) {
      console.warn('ag-grid: since version 9.0.x infinitePageSize is now called cacheBlockSize');
    }

    if (options.infiniteBlockSize) {
      console.warn('ag-grid: since version 10.0.x infiniteBlockSize is now called cacheBlockSize');
    }

    if (options.maxPagesInCache) {
      console.warn('ag-grid: since version 10.0.x maxPagesInCache is now called maxBlocksInCache');
    }

    if (options.paginationOverflowSize) {
      console.warn('ag-grid: since version 10.0.x paginationOverflowSize is now called cacheOverflowSize');
    } // if (options.forPrint) {
    //     console.warn('ag-grid: since version 10.1.x, use property domLayout="forPrint" instead of forPrint=true');
    // }


    if (options.suppressMenuFilterPanel) {
      console.warn("ag-grid: since version 11.0.x, use property colDef.menuTabs=['generalMenuTab','columnsMenuTab'] instead of suppressMenuFilterPanel=true");
    }

    if (options.suppressMenuMainPanel) {
      console.warn("ag-grid: since version 11.0.x, use property colDef.menuTabs=['filterMenuTab','columnsMenuTab'] instead of suppressMenuMainPanel=true");
    }

    if (options.suppressMenuColumnPanel) {
      console.warn("ag-grid: since version 11.0.x, use property colDef.menuTabs=['generalMenuTab','filterMenuTab'] instead of suppressMenuColumnPanel=true");
    }

    if (options.suppressUseColIdForGroups) {
      console.warn("ag-grid: since version 11.0.x, this is not in use anymore. You should be able to remove it from your definition");
    }

    if (options.groupSuppressRow) {
      console.warn("ag-grid: since version 18.2.x, 'groupSuppressRow' should not be used anymore. Instead remove row groups and perform custom sorting.");
    }

    if (options.groupColumnDef) {
      console.warn("ag-grid: since version 11.0.x, groupColumnDef has been renamed, this property is now called autoGroupColumnDef. Please change your configuration accordingly");
    }

    if (options.slaveGrids) {
      console.warn("ag-grid: since version 12.x, slaveGrids has been renamed, this property is now called alignedGrids. Please change your configuration accordingly");
    }

    if (options.floatingTopRowData) {
      console.warn("ag-grid: since version 12.x, floatingTopRowData is now called pinnedTopRowData");
    }

    if (options.floatingBottomRowData) {
      console.warn("ag-grid: since version 12.x, floatingBottomRowData is now called pinnedBottomRowData");
    }

    if (options.paginationStartPage) {
      console.warn("ag-grid: since version 12.x, paginationStartPage is gone, please call api.paginationGoToPage(" + options.paginationStartPage + ") instead.");
    }

    if (options.getHeaderCellTemplate) {
      console.warn("ag-grid: since version 15.x, getHeaderCellTemplate is gone, please check the header documentation on how to set header templates.");
    }

    if (options.headerCellTemplate) {
      console.warn("ag-grid: since version 15.x, headerCellTemplate is gone, please check the header documentation on how to set header templates.");
    }

    if (options.headerCellRenderer) {
      console.warn("ag-grid: since version 15.x, headerCellRenderer is gone, please check the header documentation on how to set header templates.");
    }

    if (options.angularCompileHeaders) {
      console.warn("ag-grid: since version 15.x, angularCompileHeaders is gone, please see the getting started for Angular 1 docs to see how to do headers in Angular 1.x.");
    }

    if (options.pivotTotals) {
      console.warn("ag-grid: since version 18.x, pivotTotals has been removed, instead if using pivotTotals, set pivotColumnGroupTotals='before'|'after'.");
      options.pivotColumnGroupTotals = 'before';
    }

    if (options.rowModelType === 'inMemory') {
      console.warn("ag-grid: since version 18.x, The In Memory Row Model has been renamed to the Client Side Row Model, set rowModelType='clientSide' instead.");
      options.rowModelType = 'clientSide';
    }

    if (options.rowModelType === 'enterprise') {
      console.warn("ag-grid: since version 18.x, The Enterprise Row Model has been renamed to the Server Side Row Model, set rowModelType='serverSide' instead.");
      options.rowModelType = 'serverSide';
    }

    if (options.layoutInterval) {
      console.warn("ag-grid: since version 18.x, layoutInterval is no longer a property. This is because the grid now uses CSS Flex for layout.");
    }

    if (options.gridAutoHeight) {
      console.warn("ag-grid: since version 19.x, gridAutoHeight is gone, please use domLayout=autoHeight instead");
      options.domLayout = 'autoHeight';
    }

    if (options.showToolPanel === true) {
      console.warn("ag-grid: since version 19.x, showToolPanel is gone, please specify toolPanel components. See https://www.ag-grid.com/javascript-grid-tool-panel/");
      options.showToolPanel = undefined;
      options.sideBar = options.sideBar || true;
    }

    if (options.showToolPanel === false) {
      console.warn("ag-grid: since version 19.x, showToolPanel is gone, please specify toolPanel components. See https://www.ag-grid.com/javascript-grid-tool-panel/");
      options.showToolPanel = undefined;
      options.sideBar = options.sideBar || false;
    }

    var oldToolPanelProperties = {
      toolPanelSuppressRowGroups: 'suppressRowGroups',
      toolPanelSuppressValues: 'suppressValues',
      toolPanelSuppressPivots: 'suppressPivots',
      toolPanelSuppressPivotMode: 'suppressPivotMode',
      toolPanelSuppressColumnFilter: 'suppressColumnFilter',
      toolPanelSuppressColumnSelectAll: 'suppressColumnSelectAll',
      toolPanelSuppressSideButtons: 'suppressSideButtons',
      toolPanelSuppressColumnExpandAll: 'suppressColumnExpandAll',
      contractColumnSelection: 'contractColumnSelection'
    };
    var toolPanelColumnsCompProps = {};
    Object.keys(oldToolPanelProperties).forEach(function (key) {
      var translation = oldToolPanelProperties[key];
      var value = _this.gridOptions[key];

      if (value !== undefined) {
        if (key === 'toolPanelSuppressSideButtons') {
          console.warn('ag-grid: since v19.0 toolPanelSuppressSideButtons has been completely removed. See https://www.ag-grid.com/javascript-grid-tool-panel/');
          return;
        }

        console.warn("ag-grid: since v19.0 gridOptions." + key + " is deprecated, please use gridOptions.sideBar.toolPanel[columnsIndex].componentParams." + translation);
        toolPanelColumnsCompProps[translation] = value;
      }
    });

    if (Object.keys(toolPanelColumnsCompProps).length > 0 && !_.exists(options.sideBar)) {
      console.warn("ag-grid: since version 19.x, sideBar is mandatory if using toolPanel related properties. See https://www.ag-grid.com/javascript-grid-tool-panel/");
      options.sideBar = true;
    }

    if (options.sideBar != null) {
      options.sideBar = SideBarDefParser.parse(options.sideBar);
    }

    var sideBarDef = this.gridOptions.sideBar;

    if (Object.keys(toolPanelColumnsCompProps).length > 0 && sideBarDef && sideBarDef.toolPanels) {
      var columnsDef = sideBarDef.toolPanels.filter(function (it) {
        return it.id === 'columns';
      });

      if (columnsDef.length === 1) {
        _.mergeDeep(columnsDef[0], {
          componentParams: toolPanelColumnsCompProps
        });
      }
    }

    if (options.enableStatusBar) {
      console.warn("ag-grid: since version 19.x, enableStatusBar is gone, please specify statusBar components");
      options.statusBar = options.statusBar || {
        components: [{
          component: 'agAggregationComponent'
        }]
      };
    }

    if (options.alwaysShowStatusBar) {
      console.warn("ag-grid: since version 19.x, alwaysShowStatusBar is gone. Please specify a min-height on the ag-status-bar css class, eg .ag-status-bar {min-height: 35px; }");
    }

    if (options.enableServerSideSorting || options.enableSorting) {
      console.warn("ag-Grid: since v20, grid options enableSorting and enableServerSideSorting are gone. Instead set sortable=true on the column definition for the columns sorting are allowed on. To migrate from gridOption.enableSorting=true, set gridOptions.defaultColDef.sortable=true");

      if (!options.defaultColDef) {
        options.defaultColDef = {};
      }

      if (!options.defaultColDef.sortable) {
        options.defaultColDef.sortable = true;
      }
    }

    if (options.enableFilter || options.enableServerSideFilter) {
      console.warn("ag-Grid: since v20, grid options enableFilter and enableServerSideFilter are gone. Instead set filter=true (if not already specifying a specific filter) on the column definition for the columns filtering is allowed on. To migrate from gridOptions.enableFilter=true, set gridOptions.defaultColDef.filter=true. If you are explicitly setting specific filters for each column (ie colDef.filter is already set) the you don't need to do anything.");

      if (!options.defaultColDef) {
        options.defaultColDef = {};
      }

      if (!options.defaultColDef.filter) {
        options.defaultColDef.filter = true;
      }
    }

    if (options.enableColResize) {
      console.warn("ag-Grid: since v20, grid options enableColResize is gone. Instead set resizable=true on the column definition for the columns resizing are allowed on. To migrate from gridOption.enableColResize=true, set gridOptions.defaultColDef.resizable=true");

      if (!options.defaultColDef) {
        options.defaultColDef = {};
      }

      if (!options.defaultColDef.resizable) {
        options.defaultColDef.resizable = true;
      }
    }

    if (options.deprecatedEmbedFullWidthRows) {
      console.warn("ag-Grid: since v21.2, deprecatedEmbedFullWidthRows has been replaced with embedFullWidthRows.");
    }

    if (options.suppressTabbing) {
      console.warn("ag-Grid: since v20.1, suppressTabbing is replaced with the more powerful grid callback suppressKeyboardEvent(params) which can suppress any keyboard event including tabbing.");
    }

    if (options.doesDataFlower) {
      console.warn('ag-Grid: since v21.1, doesDataFlower is deprecated. Master/Detail is the new way for showing child data for a row and was introduced over a year ago. Please migrate your code to use master/detail instead.');
    }

    if (options.enableOldSetFilterModel) {
      console.warn('ag-Grid: since v22.x, enableOldSetFilterModel is deprecated. Please move to the new Set Filter Model as the old one may not be supported in v23 onwards.');
    }
  };

  GridOptionsWrapper.prototype.checkForViolations = function () {
    if (this.isTreeData()) {
      this.treeDataViolations();
    }
  };

  GridOptionsWrapper.prototype.treeDataViolations = function () {
    if (this.isRowModelDefault()) {
      if (_.missing(this.getDataPathFunc())) {
        console.warn('ag-Grid: property usingTreeData=true with rowModel=clientSide, but you did not ' + 'provide getDataPath function, please provide getDataPath function if using tree data.');
      }
    }

    if (this.isRowModelServerSide()) {
      if (_.missing(this.getIsServerSideGroupFunc())) {
        console.warn('ag-Grid: property usingTreeData=true with rowModel=serverSide, but you did not ' + 'provide isServerSideGroup function, please provide isServerSideGroup function if using tree data.');
      }

      if (_.missing(this.getServerSideGroupKeyFunc())) {
        console.warn('ag-Grid: property usingTreeData=true with rowModel=serverSide, but you did not ' + 'provide getServerSideGroupKey function, please provide getServerSideGroupKey function if using tree data.');
      }
    }
  };

  GridOptionsWrapper.prototype.getLocaleTextFunc = function () {
    if (this.gridOptions.localeTextFunc) {
      return this.gridOptions.localeTextFunc;
    }

    var that = this;
    return function (key, defaultValue) {
      var localeText = that.gridOptions.localeText;

      if (localeText && localeText[key]) {
        return localeText[key];
      }

      return defaultValue;
    };
  }; // responsible for calling the onXXX functions on gridOptions


  GridOptionsWrapper.prototype.globalEventHandler = function (eventName, event) {
    var callbackMethodName = ComponentUtil.getCallbackForEvent(eventName);

    if (typeof this.gridOptions[callbackMethodName] === 'function') {
      this.gridOptions[callbackMethodName](event);
    }
  }; // we don't allow dynamic row height for virtual paging


  GridOptionsWrapper.prototype.getRowHeightAsNumber = function () {
    if (!this.gridOptions.rowHeight || _.missing(this.gridOptions.rowHeight)) {
      return this.getDefaultRowHeight();
    } else if (this.gridOptions.rowHeight && this.isNumeric(this.gridOptions.rowHeight)) {
      return this.gridOptions.rowHeight;
    }

    console.warn('ag-Grid row height must be a number if not using standard row model');
    return this.getDefaultRowHeight();
  };

  GridOptionsWrapper.prototype.getRowHeightForNode = function (rowNode, allowEstimate) {
    // check the function first, in case use set both function and
    // number, when using virtual pagination then function can be
    // used for pinned rows and the number for the body rows.
    if (allowEstimate === void 0) {
      allowEstimate = false;
    }

    if (typeof this.gridOptions.getRowHeight === 'function') {
      if (allowEstimate) {
        return {
          height: this.getDefaultRowHeight(),
          estimated: true
        };
      }

      var params = {
        node: rowNode,
        data: rowNode.data,
        api: this.gridOptions.api,
        context: this.gridOptions.context
      };
      return {
        height: this.gridOptions.getRowHeight(params),
        estimated: false
      };
    } else if (rowNode.detail && this.isMasterDetail()) {
      if (this.isNumeric(this.gridOptions.detailRowHeight)) {
        return {
          height: this.gridOptions.detailRowHeight,
          estimated: false
        };
      }

      return {
        height: DEFAULT_DETAIL_ROW_HEIGHT,
        estimated: false
      };
    }

    var defaultRowHeight = this.getDefaultRowHeight();
    var rowHeight = this.gridOptions.rowHeight && this.isNumeric(this.gridOptions.rowHeight) ? this.gridOptions.rowHeight : defaultRowHeight;
    var minRowHeight = Math.min(defaultRowHeight, rowHeight);

    if (this.columnController.isAutoRowHeightActive()) {
      if (allowEstimate) {
        return {
          height: rowHeight,
          estimated: true
        };
      }

      var autoHeight = this.autoHeightCalculator.getPreferredHeightForRow(rowNode); // never return less than the default row height - covers when auto height
      // cells are blank.

      return {
        height: Math.max(autoHeight, minRowHeight),
        estimated: false
      };
    }

    return {
      height: rowHeight,
      estimated: false
    };
  };

  GridOptionsWrapper.prototype.isDynamicRowHeight = function () {
    return typeof this.gridOptions.getRowHeight === 'function';
  };

  GridOptionsWrapper.prototype.getVirtualItemHeight = function () {
    return this.specialForNewMaterial(20, 'virtualItemHeight');
  };

  GridOptionsWrapper.prototype.useNativeCheckboxes = function () {
    return this.environment.useNativeCheckboxes();
  };

  GridOptionsWrapper.prototype.chartMenuPanelWidth = function () {
    return this.environment.chartMenuPanelWidth();
  };

  GridOptionsWrapper.prototype.isNumeric = function (value) {
    return !isNaN(value) && typeof value === 'number';
  }; // Material data table has strict guidelines about whitespace, and these values are different than the ones
  // ag-grid uses by default. We override the default ones for the sake of making it better out of the box


  GridOptionsWrapper.prototype.specialForNewMaterial = function (defaultValue, sassVariableName) {
    var theme = this.environment.getTheme().theme;

    if (theme && theme.indexOf('ag-theme') === 0) {
      return this.environment.getSassVariable(theme, sassVariableName);
    }

    return defaultValue;
  };

  GridOptionsWrapper.prototype.getDefaultRowHeight = function () {
    return this.specialForNewMaterial(DEFAULT_ROW_HEIGHT, 'rowHeight');
  };

  var GridOptionsWrapper_1;
  GridOptionsWrapper.MIN_COL_WIDTH = 10;
  GridOptionsWrapper.PROP_HEADER_HEIGHT = 'headerHeight';
  GridOptionsWrapper.PROP_GROUP_REMOVE_SINGLE_CHILDREN = 'groupRemoveSingleChildren';
  GridOptionsWrapper.PROP_GROUP_REMOVE_LOWEST_SINGLE_CHILDREN = 'groupRemoveLowestSingleChildren';
  GridOptionsWrapper.PROP_PIVOT_HEADER_HEIGHT = 'pivotHeaderHeight';
  GridOptionsWrapper.PROP_SUPPRESS_CLIPBOARD_PASTE = 'suppressClipboardPaste';
  GridOptionsWrapper.PROP_GROUP_HEADER_HEIGHT = 'groupHeaderHeight';
  GridOptionsWrapper.PROP_PIVOT_GROUP_HEADER_HEIGHT = 'pivotGroupHeaderHeight';
  GridOptionsWrapper.PROP_FLOATING_FILTERS_HEIGHT = 'floatingFiltersHeight';
  GridOptionsWrapper.PROP_SUPPRESS_ROW_DRAG = 'suppressRowDrag';
  GridOptionsWrapper.PROP_POPUP_PARENT = 'popupParent';
  GridOptionsWrapper.PROP_DOM_LAYOUT = 'domLayout';

  __decorate$7([Autowired('gridOptions')], GridOptionsWrapper.prototype, "gridOptions", void 0);

  __decorate$7([Autowired('columnController')], GridOptionsWrapper.prototype, "columnController", void 0);

  __decorate$7([Autowired('eventService')], GridOptionsWrapper.prototype, "eventService", void 0);

  __decorate$7([Autowired('gridApi')], GridOptionsWrapper.prototype, "gridApi", void 0);

  __decorate$7([Autowired('columnApi')], GridOptionsWrapper.prototype, "columnApi", void 0);

  __decorate$7([Autowired('environment')], GridOptionsWrapper.prototype, "environment", void 0);

  __decorate$7([Autowired('autoHeightCalculator')], GridOptionsWrapper.prototype, "autoHeightCalculator", void 0);

  __decorate$7([Autowired('context')], GridOptionsWrapper.prototype, "context", void 0);

  __decorate$7([__param$3(0, Qualifier('gridApi')), __param$3(1, Qualifier('columnApi'))], GridOptionsWrapper.prototype, "agWire", null);

  __decorate$7([PreDestroy], GridOptionsWrapper.prototype, "destroy", null);

  __decorate$7([PostConstruct], GridOptionsWrapper.prototype, "init", null);

  GridOptionsWrapper = GridOptionsWrapper_1 = __decorate$7([Bean('gridOptionsWrapper')], GridOptionsWrapper);
  return GridOptionsWrapper;
}();
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __spreadArrays$2 = undefined && undefined.__spreadArrays || function () {
  for (var s = 0, i = 0, il = arguments.length; i < il; i++) {
    s += arguments[i].length;
  }

  for (var r = Array(s), k = 0, i = 0; i < il; i++) {
    for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++) {
      r[k] = a[j];
    }
  }

  return r;
};

var ComponentUtil =
/** @class */
function () {
  function ComponentUtil() {}

  ComponentUtil.getEventCallbacks = function () {
    if (!ComponentUtil.EVENT_CALLBACKS) {
      ComponentUtil.EVENT_CALLBACKS = ComponentUtil.EVENTS.map(function (event) {
        return ComponentUtil.getCallbackForEvent(event);
      });
    }

    return ComponentUtil.EVENT_CALLBACKS;
  };

  ComponentUtil.copyAttributesToGridOptions = function (gridOptions, component, skipEventDeprecationCheck) {
    if (skipEventDeprecationCheck === void 0) {
      skipEventDeprecationCheck = false;
    }

    checkForDeprecated(component); // create empty grid options if none were passed

    if (typeof gridOptions !== 'object') {
      gridOptions = {};
    } // to allow array style lookup in TypeScript, take type away from 'this' and 'gridOptions'


    var pGridOptions = gridOptions;

    var keyExists = function keyExists(key) {
      return typeof component[key] !== 'undefined';
    }; // add in all the simple properties


    __spreadArrays$2(ComponentUtil.ARRAY_PROPERTIES, ComponentUtil.STRING_PROPERTIES, ComponentUtil.OBJECT_PROPERTIES, ComponentUtil.FUNCTION_PROPERTIES, ComponentUtil.getEventCallbacks()).filter(keyExists).forEach(function (key) {
      return pGridOptions[key] = component[key];
    });

    ComponentUtil.BOOLEAN_PROPERTIES.filter(keyExists).forEach(function (key) {
      return pGridOptions[key] = ComponentUtil.toBoolean(component[key]);
    });
    ComponentUtil.NUMBER_PROPERTIES.filter(keyExists).forEach(function (key) {
      return pGridOptions[key] = ComponentUtil.toNumber(component[key]);
    }); // purely for event deprecation checks (for frameworks - wouldn't apply for non-fw versions)

    if (!skipEventDeprecationCheck) {
      ComponentUtil.EVENTS // React uses onXXX, not sure why this is different to the other frameworks
      .filter(function (event) {
        return keyExists(event) || keyExists(ComponentUtil.getCallbackForEvent(event));
      }).forEach(function (event) {
        return GridOptionsWrapper.checkEventDeprecation(event);
      });
    }

    return gridOptions;
  };

  ComponentUtil.getCallbackForEvent = function (eventName) {
    if (!eventName || eventName.length < 2) {
      return eventName;
    } else {
      return 'on' + eventName[0].toUpperCase() + eventName.substr(1);
    }
  };

  ComponentUtil.processOnChange = function (changes, gridOptions, api, columnApi) {
    if (!changes) {
      return;
    }

    checkForDeprecated(changes); // to allow array style lookup in TypeScript, take type away from 'this' and 'gridOptions'

    var pGridOptions = gridOptions;

    var keyExists = function keyExists(key) {
      return changes[key];
    }; // check if any change for the simple types, and if so, then just copy in the new value


    __spreadArrays$2(ComponentUtil.ARRAY_PROPERTIES, ComponentUtil.OBJECT_PROPERTIES, ComponentUtil.STRING_PROPERTIES, ComponentUtil.getEventCallbacks()).filter(keyExists).forEach(function (key) {
      return pGridOptions[key] = changes[key].currentValue;
    });

    ComponentUtil.BOOLEAN_PROPERTIES.filter(keyExists).forEach(function (key) {
      return pGridOptions[key] = ComponentUtil.toBoolean(changes[key].currentValue);
    });
    ComponentUtil.NUMBER_PROPERTIES.filter(keyExists).forEach(function (key) {
      return pGridOptions[key] = ComponentUtil.toNumber(changes[key].currentValue);
    });

    if (changes.enableCellTextSelection) {
      api.setEnableCellTextSelection(ComponentUtil.toBoolean(changes.enableCellTextSelection.currentValue));
    }

    if (changes.showToolPanel) {
      api.showToolPanel(ComponentUtil.toBoolean(changes.showToolPanel.currentValue));
    }

    if (changes.quickFilterText) {
      api.setQuickFilter(changes.quickFilterText.currentValue);
    }

    if (changes.rowData) {
      api.setRowData(changes.rowData.currentValue);
    }

    if (changes.pinnedTopRowData) {
      api.setPinnedTopRowData(changes.pinnedTopRowData.currentValue);
    }

    if (changes.pinnedBottomRowData) {
      api.setPinnedBottomRowData(changes.pinnedBottomRowData.currentValue);
    }

    if (changes.columnDefs) {
      api.setColumnDefs(changes.columnDefs.currentValue, "gridOptionsChanged");
    }

    if (changes.datasource) {
      api.setDatasource(changes.datasource.currentValue);
    }

    if (changes.headerHeight) {
      api.setHeaderHeight(ComponentUtil.toNumber(changes.headerHeight.currentValue));
    }

    if (changes.paginationPageSize) {
      api.paginationSetPageSize(ComponentUtil.toNumber(changes.paginationPageSize.currentValue));
    }

    if (changes.pivotMode) {
      columnApi.setPivotMode(ComponentUtil.toBoolean(changes.pivotMode.currentValue));
    }

    if (changes.groupRemoveSingleChildren) {
      api.setGroupRemoveSingleChildren(ComponentUtil.toBoolean(changes.groupRemoveSingleChildren.currentValue));
    }

    if (changes.suppressRowDrag) {
      api.setSuppressRowDrag(ComponentUtil.toBoolean(changes.suppressRowDrag.currentValue));
    }

    if (changes.gridAutoHeight) {
      api.setGridAutoHeight(ComponentUtil.toBoolean(changes.gridAutoHeight.currentValue));
    }

    if (changes.suppressClipboardPaste) {
      api.setSuppressClipboardPaste(ComponentUtil.toBoolean(changes.suppressClipboardPaste.currentValue));
    }

    if (changes.sideBar) {
      api.setSideBar(changes.sideBar.currentValue);
    } // copy changes into an event for dispatch


    var event = {
      type: Events.EVENT_COMPONENT_STATE_CHANGED,
      api: gridOptions.api,
      columnApi: gridOptions.columnApi
    };

    _.iterateObject(changes, function (key, value) {
      event[key] = value;
    });

    api.dispatchEvent(event);
  };

  ComponentUtil.toBoolean = function (value) {
    if (typeof value === 'boolean') {
      return value;
    } else if (typeof value === 'string') {
      // for boolean, compare to empty String to allow attributes appearing with
      // no value to be treated as 'true'
      return value.toUpperCase() === 'TRUE' || value == '';
    } else {
      return false;
    }
  };

  ComponentUtil.toNumber = function (value) {
    if (typeof value === 'number') {
      return value;
    } else if (typeof value === 'string') {
      return Number(value);
    } else {
      return undefined;
    }
  }; // all the events are populated in here AFTER this class (at the bottom of the file).


  ComponentUtil.EVENTS = [];
  ComponentUtil.STRING_PROPERTIES = PropertyKeys.STRING_PROPERTIES;
  ComponentUtil.OBJECT_PROPERTIES = PropertyKeys.OBJECT_PROPERTIES;
  ComponentUtil.ARRAY_PROPERTIES = PropertyKeys.ARRAY_PROPERTIES;
  ComponentUtil.NUMBER_PROPERTIES = PropertyKeys.NUMBER_PROPERTIES;
  ComponentUtil.BOOLEAN_PROPERTIES = PropertyKeys.BOOLEAN_PROPERTIES;
  ComponentUtil.FUNCTION_PROPERTIES = PropertyKeys.FUNCTION_PROPERTIES;
  ComponentUtil.ALL_PROPERTIES = PropertyKeys.ALL_PROPERTIES;
  return ComponentUtil;
}();

ComponentUtil.EVENTS = _.values(Events);

function checkForDeprecated(changes) {
  if (changes.rowDeselected || changes.onRowDeselected) {
    console.warn('ag-grid: as of v3.4 rowDeselected no longer exists. Please check the docs.');
  }
}
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __decorate$8 = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var BeanStub =
/** @class */
function () {
  function BeanStub() {
    var _this = this;

    this.destroyFunctions = [];
    this.destroyed = false;

    this.getContext = function () {
      return _this.context;
    };

    this.isAlive = function () {
      return !_this.destroyed;
    };
  } // this was a test constructor niall built, when active, it prints after 5 seconds all beans/components that are
  // not destroyed. to use, create a new grid, then api.destroy() before 5 seconds. then anything that gets printed
  // points to a bean or component that was not properly disposed of.
  // constructor() {
  //     setTimeout(()=> {
  //         if (this.isAlive()) {
  //             let prototype: any = Object.getPrototypeOf(this);
  //             const constructor: any = prototype.constructor;
  //             const constructorString = constructor.toString();
  //             const beanName = constructorString.substring(9, constructorString.indexOf("("));
  //             console.log('is alive ' + beanName);
  //         }
  //     }, 5000);
  // }
  // CellComp and GridComp and override this because they get the FrameworkOverrides from the Beans bean


  BeanStub.prototype.getFrameworkOverrides = function () {
    return this.frameworkOverrides;
  };

  BeanStub.prototype.destroy = function () {
    // let prototype: any = Object.getPrototypeOf(this);
    // const constructor: any = prototype.constructor;
    // const constructorString = constructor.toString();
    // const beanName = constructorString.substring(9, constructorString.indexOf("("));
    this.destroyFunctions.forEach(function (func) {
      return func();
    });
    this.destroyFunctions.length = 0;
    this.destroyed = true;
    this.dispatchEvent({
      type: BeanStub.EVENT_DESTROYED
    });
  };

  BeanStub.prototype.addEventListener = function (eventType, listener) {
    if (!this.localEventService) {
      this.localEventService = new EventService();
    }

    this.localEventService.addEventListener(eventType, listener);
  };

  BeanStub.prototype.removeEventListener = function (eventType, listener) {
    if (this.localEventService) {
      this.localEventService.removeEventListener(eventType, listener);
    }
  };

  BeanStub.prototype.dispatchEventAsync = function (event) {
    var _this = this;

    window.setTimeout(function () {
      return _this.dispatchEvent(event);
    }, 0);
  };

  BeanStub.prototype.dispatchEvent = function (event) {
    if (this.localEventService) {
      this.localEventService.dispatchEvent(event);
    }
  };

  BeanStub.prototype.addDestroyableEventListener = function (object, event, listener) {
    var _this = this;

    if (this.destroyed) {
      return;
    }

    if (object instanceof HTMLElement) {
      _.addSafePassiveEventListener(this.getFrameworkOverrides(), object, event, listener);
    } else {
      object.addEventListener(event, listener);
    }

    var destroyFunc = function destroyFunc() {
      object.removeEventListener(event, listener);
      _this.destroyFunctions = _this.destroyFunctions.filter(function (fn) {
        return fn !== destroyFunc;
      });
    };

    this.destroyFunctions.push(destroyFunc);
    return destroyFunc;
  };

  BeanStub.prototype.addDestroyFunc = function (func) {
    // if we are already destroyed, we execute the func now
    if (this.isAlive()) {
      this.destroyFunctions.push(func);
    } else {
      func();
    }
  };

  BeanStub.prototype.wireDependentBean = function (bean, context) {
    if (bean.destroy) {
      this.addDestroyFunc(bean.destroy.bind(bean));
    }

    return this.wireBean(bean, context);
  };

  BeanStub.prototype.wireBean = function (bean, context) {
    (context || this.getContext()).wireBean(bean);
    return bean;
  };

  BeanStub.EVENT_DESTROYED = 'destroyed';

  __decorate$8([Autowired('context')], BeanStub.prototype, "context", void 0);

  __decorate$8([Autowired('frameworkOverrides')], BeanStub.prototype, "frameworkOverrides", void 0);

  __decorate$8([PreDestroy], BeanStub.prototype, "destroy", null);

  return BeanStub;
}();
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __extends = undefined && undefined.__extends || function () {
  var _extendStatics = function extendStatics(d, b) {
    _extendStatics = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics(d, b);
  };

  return function (d, b) {
    _extendStatics(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var __decorate$9 = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var compIdSequence = new NumberSequence();

var Component =
/** @class */
function (_super) {
  __extends(Component, _super);

  function Component(template) {
    var _this = _super.call(this) || this;

    _this.childComponents = [];
    _this.annotatedEventListeners = []; // if false, then CSS class "ag-hidden" is applied, which sets "display: none"

    _this.displayed = true; // if false, then CSS class "ag-invisible" is applied, which sets "visibility: hidden"

    _this.visible = true; // unique id for this row component. this is used for getting a reference to the HTML dom.
    // we cannot use the RowNode id as this is not unique (due to animation, old rows can be lying
    // around as we create a new rowComp instance for the same row node).

    _this.compId = compIdSequence.next();

    if (template) {
      _this.setTemplate(template);
    }

    return _this;
  }

  Component.prototype.getCompId = function () {
    return this.compId;
  }; // for registered components only, eg creates AgCheckbox instance from ag-checkbox HTML tag


  Component.prototype.createChildComponentsFromTags = function (parentNode) {
    var _this = this; // we MUST take a copy of the list first, as the 'swapComponentForNode' adds comments into the DOM
    // which messes up the traversal order of the children.


    var childNodeList = _.copyNodeList(parentNode.childNodes);

    childNodeList.forEach(function (childNode) {
      var childComp = _this.getContext().createComponentFromElement(childNode, function (childComp) {
        // copy over all attributes, including css classes, so any attributes user put on the tag
        // wll be carried across
        _this.copyAttributesFromNode(childNode, childComp.getGui());
      });

      if (childComp) {
        if (childComp.addItems && childNode.children.length) {
          _this.createChildComponentsFromTags(childNode); // converting from HTMLCollection to Array


          var items = Array.prototype.slice.call(childNode.children);
          childComp.addItems(items);
        } // replace the tag (eg ag-checkbox) with the proper HTMLElement (eg 'div') in the dom


        _this.swapComponentForNode(childComp, parentNode, childNode);
      } else if (childNode.childNodes) {
        _this.createChildComponentsFromTags(childNode);
      }
    });
  };

  Component.prototype.copyAttributesFromNode = function (source, dest) {
    _.iterateNamedNodeMap(source.attributes, function (name, value) {
      dest.setAttribute(name, value);
    });
  };

  Component.prototype.swapComponentForNode = function (newComponent, parentNode, childNode) {
    var eComponent = newComponent.getGui();
    parentNode.replaceChild(eComponent, childNode);
    parentNode.insertBefore(document.createComment(childNode.nodeName), eComponent);
    this.childComponents.push(newComponent);
    this.swapInComponentForQuerySelectors(newComponent, childNode);
  };

  Component.prototype.swapInComponentForQuerySelectors = function (newComponent, childNode) {
    var thisProto = Object.getPrototypeOf(this);
    var thisNoType = this;

    while (thisProto != null) {
      var metaData = thisProto.__agComponentMetaData;
      var currentProtoName = thisProto.constructor.name;

      if (metaData && metaData[currentProtoName] && metaData[currentProtoName].querySelectors) {
        metaData[currentProtoName].querySelectors.forEach(function (querySelector) {
          if (thisNoType[querySelector.attributeName] === childNode) {
            thisNoType[querySelector.attributeName] = newComponent;
          }
        });
      }

      thisProto = Object.getPrototypeOf(thisProto);
    }
  };

  Component.prototype.setTemplate = function (template) {
    var eGui = _.loadTemplate(template);

    this.setTemplateFromElement(eGui);
  };

  Component.prototype.setTemplateFromElement = function (element) {
    this.eGui = element;
    this.eGui.__agComponent = this;
    this.addAnnotatedEventListeners();
    this.wireQuerySelectors(); // context will not be available when user sets template in constructor

    var contextIsAvailable = !!this.getContext();

    if (contextIsAvailable) {
      this.createChildComponentsFromTags(this.getGui());
    }
  };

  Component.prototype.createChildComponentsPreConstruct = function () {
    // ui exists if user sets template in constructor. when this happens, we have to wait for the context
    // to be autoWired first before we can create child components.
    var uiExists = !!this.getGui();

    if (uiExists) {
      this.createChildComponentsFromTags(this.getGui());
    }
  };

  Component.prototype.wireQuerySelectors = function () {
    var _this = this;

    if (!this.eGui) {
      return;
    }

    var thisProto = Object.getPrototypeOf(this);

    var _loop_1 = function _loop_1() {
      var metaData = thisProto.__agComponentMetaData;
      var currentProtoName = thisProto.constructor.name;

      if (metaData && metaData[currentProtoName] && metaData[currentProtoName].querySelectors) {
        var thisNoType_1 = this_1;
        metaData[currentProtoName].querySelectors.forEach(function (querySelector) {
          var resultOfQuery = _this.eGui.querySelector(querySelector.querySelector);

          if (resultOfQuery) {
            var backingComponent = resultOfQuery.__agComponent;

            if (backingComponent) {
              thisNoType_1[querySelector.attributeName] = backingComponent;
            } else {
              thisNoType_1[querySelector.attributeName] = resultOfQuery;
            }
          }
        });
      }

      thisProto = Object.getPrototypeOf(thisProto);
    };

    var this_1 = this;

    while (thisProto != null) {
      _loop_1();
    }
  };

  Component.prototype.addAnnotatedEventListeners = function () {
    var _this = this;

    this.removeAnnotatedEventListeners();

    if (!this.eGui) {
      return;
    }

    var listenerMethods = this.getAgComponentMetaData('listenerMethods');

    if (_.missingOrEmpty(listenerMethods)) {
      return;
    }

    if (!this.annotatedEventListeners) {
      this.annotatedEventListeners = [];
    }

    listenerMethods.forEach(function (eventListener) {
      var listener = _this[eventListener.methodName].bind(_this);

      _this.eGui.addEventListener(eventListener.eventName, listener);

      _this.annotatedEventListeners.push({
        eventName: eventListener.eventName,
        listener: listener
      });
    });
  };

  Component.prototype.getAgComponentMetaData = function (key) {
    var res = [];
    var thisProto = Object.getPrototypeOf(this);

    while (thisProto != null) {
      var metaData = thisProto.__agComponentMetaData;
      var currentProtoName = thisProto.constructor.name; // IE does not support Function.prototype.name, so we need to extract
      // the name using a RegEx
      // from: https://matt.scharley.me/2012/03/monkey-patch-name-ie.html

      if (currentProtoName === undefined) {
        var funcNameRegex = /function\s([^(]{1,})\(/;
        var results = funcNameRegex.exec(thisProto.constructor.toString());

        if (results && results.length > 1) {
          currentProtoName = results[1].trim();
        }
      }

      if (metaData && metaData[currentProtoName] && metaData[currentProtoName][key]) {
        res = res.concat(metaData[currentProtoName][key]);
      }

      thisProto = Object.getPrototypeOf(thisProto);
    }

    return res;
  };

  Component.prototype.removeAnnotatedEventListeners = function () {
    var _this = this;

    if (!this.annotatedEventListeners || !this.eGui) {
      return;
    }

    this.annotatedEventListeners.forEach(function (eventListener) {
      _this.eGui.removeEventListener(eventListener.eventName, eventListener.listener);
    });
    this.annotatedEventListeners = [];
  };

  Component.prototype.getGui = function () {
    return this.eGui;
  };

  Component.prototype.setParentComponent = function (component) {
    this.parentComponent = component;
  };

  Component.prototype.getParentComponent = function () {
    return this.parentComponent;
  }; // this method is for older code, that wants to provide the gui element,
  // it is not intended for this to be in ag-Stack


  Component.prototype.setGui = function (eGui) {
    this.eGui = eGui;
  };

  Component.prototype.queryForHtmlElement = function (cssSelector) {
    return this.eGui.querySelector(cssSelector);
  };

  Component.prototype.queryForHtmlInputElement = function (cssSelector) {
    return this.eGui.querySelector(cssSelector);
  };

  Component.prototype.appendChild = function (newChild) {
    if (_.isNodeOrElement(newChild)) {
      this.eGui.appendChild(newChild);
    } else {
      var childComponent = newChild;
      this.eGui.appendChild(childComponent.getGui());
      this.childComponents.push(childComponent);
    }
  };

  Component.prototype.addFeature = function (feature, context) {
    this.wireDependentBean(feature, context);
  };

  Component.prototype.isDisplayed = function () {
    return this.displayed;
  };

  Component.prototype.setVisible = function (visible) {
    if (visible !== this.visible) {
      this.visible = visible;

      _.setVisible(this.eGui, visible);
    }
  };

  Component.prototype.setDisplayed = function (displayed) {
    if (displayed !== this.displayed) {
      this.displayed = displayed;

      _.setDisplayed(this.eGui, displayed);

      var event_1 = {
        type: Component.EVENT_DISPLAYED_CHANGED,
        visible: this.displayed
      };
      this.dispatchEvent(event_1);
    }
  };

  Component.prototype.addOrRemoveCssClass = function (className, addOrRemove) {
    _.addOrRemoveCssClass(this.eGui, className, addOrRemove);
  };

  Component.prototype.destroy = function () {
    _super.prototype.destroy.call(this);

    this.childComponents.forEach(function (childComponent) {
      if (childComponent && childComponent.destroy) {
        childComponent.destroy();
      }
    });
    this.childComponents.length = 0;
    this.removeAnnotatedEventListeners();
  };

  Component.prototype.addGuiEventListener = function (event, listener) {
    var _this = this;

    this.getGui().addEventListener(event, listener);
    this.addDestroyFunc(function () {
      return _this.getGui().removeEventListener(event, listener);
    });
  };

  Component.prototype.addCssClass = function (className) {
    _.addCssClass(this.getGui(), className);
  };

  Component.prototype.removeCssClass = function (className) {
    _.removeCssClass(this.getGui(), className);
  };

  Component.prototype.getAttribute = function (key) {
    var eGui = this.getGui();
    return eGui ? eGui.getAttribute(key) : null;
  };

  Component.prototype.getRefElement = function (refName) {
    return this.queryForHtmlElement('[ref="' + refName + '"]');
  };

  Component.EVENT_DISPLAYED_CHANGED = 'displayedChanged';

  __decorate$9([PreConstruct], Component.prototype, "createChildComponentsPreConstruct", null);

  return Component;
}(BeanStub);
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __extends$1 = undefined && undefined.__extends || function () {
  var _extendStatics2 = function extendStatics(d, b) {
    _extendStatics2 = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics2(d, b);
  };

  return function (d, b) {
    _extendStatics2(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var PopupComponent =
/** @class */
function (_super) {
  __extends$1(PopupComponent, _super);

  function PopupComponent() {
    return _super !== null && _super.apply(this, arguments) || this;
  }

  PopupComponent.prototype.isPopup = function () {
    return true;
  };

  PopupComponent.prototype.setParentComponent = function (container) {
    _.addCssClass(container.getGui(), 'ag-has-popup');

    _super.prototype.setParentComponent.call(this, container);
  };

  PopupComponent.prototype.destroy = function () {
    var parentComp = this.parentComponent;
    var hasParent = parentComp && parentComp.isAlive();

    if (hasParent) {
      _.removeCssClass(parentComp.getGui(), 'ag-has-popup');
    }

    _super.prototype.destroy.call(this);
  };

  return PopupComponent;
}(Component);
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __extends$2 = undefined && undefined.__extends || function () {
  var _extendStatics3 = function extendStatics(d, b) {
    _extendStatics3 = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics3(d, b);
  };

  return function (d, b) {
    _extendStatics3(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var TextCellEditor =
/** @class */
function (_super) {
  __extends$2(TextCellEditor, _super);

  function TextCellEditor() {
    var _this = _super.call(this, TextCellEditor.TEMPLATE) || this;

    _this.eInput = _this.getGui().querySelector('input');
    return _this;
  }

  TextCellEditor.prototype.init = function (params) {
    this.params = params;
    var eInput = this.eInput;
    var startValue; // cellStartedEdit is only false if we are doing fullRow editing

    if (params.cellStartedEdit) {
      this.focusAfterAttached = true;
      var keyPressBackspaceOrDelete = params.keyPress === Constants.KEY_BACKSPACE || params.keyPress === Constants.KEY_DELETE;

      if (keyPressBackspaceOrDelete) {
        startValue = '';
      } else if (params.charPress) {
        startValue = params.charPress;
      } else {
        startValue = this.getStartValue(params);

        if (params.keyPress !== Constants.KEY_F2) {
          this.highlightAllOnFocus = true;
        }
      }
    } else {
      this.focusAfterAttached = false;
      startValue = this.getStartValue(params);
    }

    if (_.exists(startValue)) {
      eInput.value = startValue;
    }

    this.addDestroyableEventListener(eInput, 'keydown', function (event) {
      var pageUp = event.keyCode === Constants.KEY_PAGE_UP;
      var pageDown = event.keyCode === Constants.KEY_PAGE_DOWN;

      if (pageUp || pageDown) {
        event.preventDefault();
      }
    });
  };

  TextCellEditor.prototype.afterGuiAttached = function () {
    if (!this.focusAfterAttached) {
      return;
    }

    var eInput = this.eInput; // Added for AG-3238. We can't remove this explicit focus() because Chrome requires an input
    // to be focussed before setSelectionRange will work. But it triggers a bug in Safari where
    // explicitly focussing then blurring an empty field will cause the parent container to scroll.

    if (!_.isBrowserSafari()) {
      eInput.focus();
    }

    if (this.highlightAllOnFocus) {
      eInput.select();
    } else {
      // when we started editing, we want the caret at the end, not the start.
      // this comes into play in two scenarios: a) when user hits F2 and b)
      // when user hits a printable character, then on IE (and only IE) the caret
      // was placed after the first character, thus 'apply' would end up as 'pplea'
      var length_1 = eInput.value ? eInput.value.length : 0;

      if (length_1 > 0) {
        eInput.setSelectionRange(length_1, length_1);
      }
    }
  }; // gets called when tabbing trough cells and in full row edit mode


  TextCellEditor.prototype.focusIn = function () {
    var eInput = this.eInput;
    eInput.focus();
    eInput.select();
  };

  TextCellEditor.prototype.getValue = function () {
    var eInput = this.eInput;
    return this.params.parseValue(eInput.value);
  };

  TextCellEditor.prototype.getStartValue = function (params) {
    var formatValue = params.useFormatter || params.column.getColDef().refData;
    return formatValue ? params.formatValue(params.value) : params.value;
  };

  TextCellEditor.prototype.isPopup = function () {
    return false;
  };

  TextCellEditor.TEMPLATE = '<div class="ag-input-wrapper" role="presentation"><input class="ag-cell-edit-input" type="text"/></div>';
  return TextCellEditor;
}(PopupComponent);
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


function QuerySelector(selector) {
  return querySelectorFunc.bind(this, selector);
}

function RefSelector(ref) {
  return querySelectorFunc.bind(this, "[ref=" + ref + "]");
}

function querySelectorFunc(selector, classPrototype, methodOrAttributeName, index) {
  if (selector === null) {
    console.error("ag-Grid: QuerySelector selector should not be null");
    return;
  }

  if (typeof index === "number") {
    console.error("ag-Grid: QuerySelector should be on an attribute");
    return;
  }

  addToObjectProps(classPrototype, 'querySelectors', {
    attributeName: methodOrAttributeName,
    querySelector: selector
  });
} // think we should take this out, put property bindings on the


function Listener(eventName) {
  return listenerFunc.bind(this, eventName);
}

function listenerFunc(eventName, target, methodName) {
  if (eventName === null) {
    console.error("ag-Grid: EventListener eventName should not be null");
    return;
  }

  addToObjectProps(target, 'listenerMethods', {
    methodName: methodName,
    eventName: eventName
  });
}

function addToObjectProps(target, key, value) {
  // it's an attribute on the class
  var props = getOrCreateProps$1(target, target.constructor.name);

  if (!props[key]) {
    props[key] = [];
  }

  props[key].push(value);
}

function getOrCreateProps$1(target, instanceName) {
  if (!target.__agComponentMetaData) {
    target.__agComponentMetaData = {};
  }

  if (!target.__agComponentMetaData[instanceName]) {
    target.__agComponentMetaData[instanceName] = {};
  }

  return target.__agComponentMetaData[instanceName];
}
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */

/** Provides sync access to async component. Date component can be lazy created - this class encapsulates
 * this by keeping value locally until DateComp has loaded, then passing DateComp the value. */


var DateCompWrapper =
/** @class */
function () {
  function DateCompWrapper(userComponentFactory, dateComponentParams, eParent) {
    var _this = this;

    this.alive = true;
    userComponentFactory.newDateComponent(dateComponentParams).then(function (dateComp) {
      // because async, check the filter still exists after component comes back
      if (!_this.alive) {
        if (dateComp.destroy) {
          dateComp.destroy();
        }

        return;
      }

      _this.dateComp = dateComp;
      eParent.appendChild(dateComp.getGui());

      if (dateComp.afterGuiAttached) {
        dateComp.afterGuiAttached();
      }

      if (_this.tempValue) {
        dateComp.setDate(_this.tempValue);
      }
    });
  }

  DateCompWrapper.prototype.destroy = function () {
    this.alive = false;

    if (this.dateComp && this.dateComp.destroy) {
      this.dateComp.destroy();
    }
  };

  DateCompWrapper.prototype.getDate = function () {
    if (this.dateComp) {
      return this.dateComp.getDate();
    } else {
      return this.tempValue;
    }
  };

  DateCompWrapper.prototype.setDate = function (value) {
    if (this.dateComp) {
      this.dateComp.setDate(value);
    } else {
      this.tempValue = value;
    }
  };

  return DateCompWrapper;
}();
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */

/* Common logic for options, used by both filters and floating filters. */


var OptionsFactory =
/** @class */
function () {
  function OptionsFactory() {
    this.customFilterOptions = {};
  }

  OptionsFactory.prototype.init = function (params, defaultOptions) {
    this.filterOptions = params.filterOptions ? params.filterOptions : defaultOptions;
    this.mapCustomOptions();
    this.selectDefaultItem(params);
  };

  OptionsFactory.prototype.getFilterOptions = function () {
    return this.filterOptions;
  };

  OptionsFactory.prototype.mapCustomOptions = function () {
    var _this = this;

    if (!this.filterOptions) {
      return;
    }

    this.filterOptions.forEach(function (filterOption) {
      if (typeof filterOption === 'string') {
        return;
      }

      if (!filterOption.displayKey) {
        console.warn("ag-Grid: ignoring FilterOptionDef as it doesn't contain a 'displayKey'");
        return;
      }

      if (!filterOption.displayName) {
        console.warn("ag-Grid: ignoring FilterOptionDef as it doesn't contain a 'displayName'");
        return;
      }

      if (!filterOption.test) {
        console.warn("ag-Grid: ignoring FilterOptionDef as it doesn't contain a 'test'");
        return;
      }

      _this.customFilterOptions[filterOption.displayKey] = filterOption;
    });
  };

  OptionsFactory.prototype.selectDefaultItem = function (params) {
    if (params.defaultOption) {
      this.defaultOption = params.defaultOption;
    } else if (this.filterOptions.length >= 1) {
      var firstFilterOption = this.filterOptions[0];

      if (typeof firstFilterOption === 'string') {
        this.defaultOption = firstFilterOption;
      } else if (firstFilterOption.displayKey) {
        this.defaultOption = firstFilterOption.displayKey;
      } else {
        console.warn("ag-Grid: invalid FilterOptionDef supplied as it doesn't contain a 'displayKey'");
      }
    } else {
      console.warn('ag-Grid: no filter options for filter');
    }
  };

  OptionsFactory.prototype.getDefaultOption = function () {
    return this.defaultOption;
  };

  OptionsFactory.prototype.getCustomOption = function (name) {
    return this.customFilterOptions[name];
  };

  return OptionsFactory;
}();
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __extends$3 = undefined && undefined.__extends || function () {
  var _extendStatics4 = function extendStatics(d, b) {
    _extendStatics4 = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics4(d, b);
  };

  return function (d, b) {
    _extendStatics4(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var __decorate$a = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};
/**
 * Contains common logic to all provided filters (apply button, clear button, etc).
 * All the filters that come with ag-Grid extend this class. User filters do not
 * extend this class.
 */


var ProvidedFilter =
/** @class */
function (_super) {
  __extends$3(ProvidedFilter, _super);

  function ProvidedFilter() {
    return _super !== null && _super.apply(this, arguments) || this;
  }
  /** @deprecated */


  ProvidedFilter.prototype.onFilterChanged = function () {
    console.warn("ag-Grid: you should not call onFilterChanged() directly on the filter, please call\n        gridApi.onFilterChanged() instead. onFilterChanged is not part of the exposed filter interface (it was\n        a method that existed on an old version of the filters that was not intended for public use.");
    this.providedFilterParams.filterChangedCallback();
  };

  ProvidedFilter.prototype.isFilterActive = function () {
    // filter is active if we have a valid applied model
    return !!this.appliedModel;
  };

  ProvidedFilter.prototype.postConstruct = function () {
    var templateString = this.createTemplate();
    this.setTemplate(templateString);
  };

  ProvidedFilter.prototype.init = function (params) {
    this.setParams(params);
    this.resetUiToDefaults();
    this.updateUiVisibility();
    this.setupOnBtApplyDebounce();
  };

  ProvidedFilter.prototype.setParams = function (params) {
    var _this = this;

    this.providedFilterParams = params;
    this.applyActive = ProvidedFilter.isUseApplyButton(params);

    if (params.newRowsAction === ProvidedFilter.NEW_ROWS_ACTION_KEEP) {
      this.newRowsActionKeep = true;
    } else if (params.newRowsAction === ProvidedFilter.NEW_ROWS_ACTION_CLEAR) {
      this.newRowsActionKeep = false;
    } else {
      // the default for SSRM and IRM is 'keep', for CSRM and VRM teh default is 'clear'
      var rowModelType = this.rowModel.getType();
      var modelsForKeep = [Constants.ROW_MODEL_TYPE_SERVER_SIDE, Constants.ROW_MODEL_TYPE_INFINITE];
      this.newRowsActionKeep = modelsForKeep.indexOf(rowModelType) >= 0;
    }

    _.setDisplayed(this.eApplyButton, this.applyActive); // we do not bind onBtApply here because onBtApply() has a parameter, and it is not the event. if we
    // just applied, the event would get passed as the second parameter, which we do not want.


    this.addDestroyableEventListener(this.eApplyButton, "click", function () {
      return _this.onBtApply();
    });
    var clearActive = params.clearButton === true;

    _.setDisplayed(this.eClearButton, clearActive);

    this.addDestroyableEventListener(this.eClearButton, "click", function () {
      return _this.onBtClear();
    });
    var resetActive = params.resetButton === true;

    _.setDisplayed(this.eResetButton, resetActive);

    this.addDestroyableEventListener(this.eResetButton, "click", function () {
      return _this.onBtReset();
    });
    var anyButtonVisible = this.applyActive || clearActive || resetActive;

    _.setDisplayed(this.eButtonsPanel, anyButtonVisible);
  }; // subclasses can override this to provide alternative debounce defaults


  ProvidedFilter.prototype.getDefaultDebounceMs = function () {
    return 0;
  };

  ProvidedFilter.prototype.setupOnBtApplyDebounce = function () {
    var debounceMs = ProvidedFilter.getDebounceMs(this.providedFilterParams, this.getDefaultDebounceMs());
    this.onBtApplyDebounce = _.debounce(this.onBtApply.bind(this), debounceMs);
  };

  ProvidedFilter.prototype.getModel = function () {
    return this.appliedModel;
  };

  ProvidedFilter.prototype.setModel = function (model) {
    if (model) {
      this.setModelIntoUi(model);
    } else {
      this.resetUiToDefaults();
    }

    this.updateUiVisibility(); // we set the model from the gui, rather than the provided model,
    // so the model is consistent. eg handling of null/undefined will be the same,
    // of if model is case insensitive, then casing is removed.

    this.applyModel();
  };

  ProvidedFilter.prototype.onBtClear = function () {
    this.resetUiToDefaults();
    this.updateUiVisibility();
    this.onUiChanged();
  };

  ProvidedFilter.prototype.onBtReset = function () {
    this.onBtClear();
    this.onBtApply();
  }; // returns true if the new model is different to the old model


  ProvidedFilter.prototype.applyModel = function () {
    var oldAppliedModel = this.appliedModel;
    this.appliedModel = this.getModelFromUi(); // models can be same if user pasted same content into text field, or maybe just changed the case
    // and it's a case insensitive filter

    var newModelDifferent = !this.areModelsEqual(this.appliedModel, oldAppliedModel);
    return newModelDifferent;
  };

  ProvidedFilter.prototype.onBtApply = function (afterFloatingFilter) {
    if (afterFloatingFilter === void 0) {
      afterFloatingFilter = false;
    }

    var newModelDifferent = this.applyModel();

    if (newModelDifferent) {
      // the floating filter uses 'afterFloatingFilter' info, so it doesn't refresh after filter changed if change
      // came from floating filter
      this.providedFilterParams.filterChangedCallback({
        afterFloatingFilter: afterFloatingFilter
      });
    }
  };

  ProvidedFilter.prototype.onNewRowsLoaded = function () {
    if (!this.newRowsActionKeep) {
      this.resetUiToDefaults();
      this.appliedModel = null;
    }
  }; // called by set filter


  ProvidedFilter.prototype.isNewRowsActionKeep = function () {
    return this.newRowsActionKeep;
  };

  ProvidedFilter.prototype.onUiChanged = function (afterFloatingFilter) {
    if (afterFloatingFilter === void 0) {
      afterFloatingFilter = false;
    }

    this.updateUiVisibility();
    this.providedFilterParams.filterModifiedCallback(); // applyNow=true for floating filter changes, we always act on these immediately

    if (afterFloatingFilter) {
      this.onBtApply(true); // otherwise if no apply button, we apply (but debounce for time delay)
    } else if (!this.applyActive) {
      this.onBtApplyDebounce();
    }
  };

  ProvidedFilter.prototype.createTemplate = function () {
    var body = this.createBodyTemplate();
    var translate = this.gridOptionsWrapper.getLocaleTextFunc();
    return "<div>\n                    <div class='ag-filter-body-wrapper' ref=\"eFilterBodyWrapper\">" + body + "</div>\n                    <div class=\"ag-filter-apply-panel\" ref=\"eButtonsPanel\">\n                        <button type=\"button\" ref=\"eClearButton\">" + translate('clearFilter', 'Clear Filter') + "</button>\n                        <button type=\"button\" ref=\"eResetButton\">" + translate('resetFilter', 'Reset Filter') + "</button>\n                        <button type=\"button\" ref=\"eApplyButton\">" + translate('applyFilter', 'Apply Filter') + "</button>\n                    </div>\n                </div>";
  }; // static, as used by floating filter also


  ProvidedFilter.getDebounceMs = function (params, debounceDefault) {
    var applyActive = ProvidedFilter.isUseApplyButton(params);

    if (applyActive) {
      if (params.debounceMs != null) {
        console.warn('ag-Grid: debounceMs is ignored when applyButton = true');
      }

      return 0;
    }

    return params.debounceMs != null ? params.debounceMs : debounceDefault;
  }; // static, as used by floating filter also


  ProvidedFilter.isUseApplyButton = function (params) {
    if (params.apply && !params.applyButton) {
      console.warn('ag-Grid: as of ag-Grid v21, filterParams.apply is now filterParams.applyButton, please change to applyButton');
      params.applyButton = true;
    }

    return params.applyButton === true;
  };

  ProvidedFilter.NEW_ROWS_ACTION_KEEP = 'keep';
  ProvidedFilter.NEW_ROWS_ACTION_CLEAR = 'clear';

  __decorate$a([RefSelector('eButtonsPanel')], ProvidedFilter.prototype, "eButtonsPanel", void 0);

  __decorate$a([RefSelector('eFilterBodyWrapper')], ProvidedFilter.prototype, "eFilterBodyWrapper", void 0);

  __decorate$a([RefSelector('eClearButton')], ProvidedFilter.prototype, "eClearButton", void 0);

  __decorate$a([RefSelector('eResetButton')], ProvidedFilter.prototype, "eResetButton", void 0);

  __decorate$a([RefSelector('eApplyButton')], ProvidedFilter.prototype, "eApplyButton", void 0);

  __decorate$a([Autowired('gridOptionsWrapper')], ProvidedFilter.prototype, "gridOptionsWrapper", void 0);

  __decorate$a([Autowired('rowModel')], ProvidedFilter.prototype, "rowModel", void 0);

  __decorate$a([PostConstruct], ProvidedFilter.prototype, "postConstruct", null);

  return ProvidedFilter;
}(Component);
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __extends$4 = undefined && undefined.__extends || function () {
  var _extendStatics5 = function extendStatics(d, b) {
    _extendStatics5 = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics5(d, b);
  };

  return function (d, b) {
    _extendStatics5(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var __decorate$b = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var ConditionPosition;

(function (ConditionPosition) {
  ConditionPosition[ConditionPosition["One"] = 0] = "One";
  ConditionPosition[ConditionPosition["Two"] = 1] = "Two";
})(ConditionPosition || (ConditionPosition = {}));

var DEFAULT_TRANSLATIONS = {
  loadingOoo: 'Loading...',
  empty: 'Choose One',
  equals: 'Equals',
  notEqual: 'Not equal',
  lessThan: 'Less than',
  greaterThan: 'Greater than',
  inRange: 'In range',
  lessThanOrEqual: 'Less than or equals',
  greaterThanOrEqual: 'Greater than or equals',
  filterOoo: 'Filter...',
  contains: 'Contains',
  notContains: 'Not contains',
  startsWith: 'Starts with',
  endsWith: 'Ends with',
  searchOoo: 'Search...',
  selectAll: 'Select All',
  applyFilter: 'Apply Filter',
  clearFilter: 'Clear Filter',
  andCondition: 'AND',
  orCondition: 'OR'
};
/**
 * Every filter with a dropdown where the user can specify a comparing type against the filter values
 */

var SimpleFilter =
/** @class */
function (_super) {
  __extends$4(SimpleFilter, _super);

  function SimpleFilter() {
    return _super !== null && _super.apply(this, arguments) || this;
  } // returns true if this type requires a 'from' field, eg any filter that requires at least one text value


  SimpleFilter.prototype.showValueFrom = function (type) {
    return !this.doesFilterHaveHiddenInput(type) && type !== SimpleFilter.EMPTY;
  }; // returns true if this type requires a 'to' field, currently only 'range' returns true


  SimpleFilter.prototype.showValueTo = function (type) {
    return type === SimpleFilter.IN_RANGE;
  }; // floating filter calls this when user applies filter from floating filter


  SimpleFilter.prototype.onFloatingFilterChanged = function (type, value) {
    this.setValueFromFloatingFilter(value);
    this.setTypeFromFloatingFilter(type);
    this.onUiChanged(true);
  };

  SimpleFilter.prototype.setTypeFromFloatingFilter = function (type) {
    this.eType1.value = type;
    this.eType2.value = null;
    this.eJoinOperatorAnd.checked = true;
  };

  SimpleFilter.prototype.getModelFromUi = function () {
    if (!this.isConditionUiComplete(ConditionPosition.One)) {
      return null;
    }

    if (this.isAllowTwoConditions() && this.isConditionUiComplete(ConditionPosition.Two)) {
      var res = {
        filterType: this.getFilterType(),
        operator: this.getJoinOperator(),
        condition1: this.createCondition(ConditionPosition.One),
        condition2: this.createCondition(ConditionPosition.Two)
      };
      return res;
    } else {
      var res = this.createCondition(ConditionPosition.One);
      return res;
    }
  };

  SimpleFilter.prototype.getCondition1Type = function () {
    return this.eType1.value;
  };

  SimpleFilter.prototype.getCondition2Type = function () {
    return this.eType2.value;
  };

  SimpleFilter.prototype.getJoinOperator = function () {
    return this.eJoinOperatorOr.checked ? 'OR' : 'AND';
  };

  SimpleFilter.prototype.areModelsEqual = function (a, b) {
    // both are missing
    if (!a && !b) {
      return true;
    } // one is missing, other present


    if (!a && b || a && !b) {
      return false;
    } // one is combined, the other is not


    var aIsSimple = !a.operator;
    var bIsSimple = !b.operator;
    var oneSimpleOneCombined = !aIsSimple && bIsSimple || aIsSimple && !bIsSimple;

    if (oneSimpleOneCombined) {
      return false;
    }

    var res; // otherwise both present, so compare

    if (aIsSimple) {
      var aSimple = a;
      var bSimple = b;
      res = this.areSimpleModelsEqual(aSimple, bSimple);
    } else {
      var aCombined = a;
      var bCombined = b;
      res = aCombined.operator === bCombined.operator && this.areSimpleModelsEqual(aCombined.condition1, bCombined.condition1) && this.areSimpleModelsEqual(aCombined.condition2, bCombined.condition2);
    }

    return res;
  };

  SimpleFilter.prototype.setModelIntoUi = function (model) {
    var isCombined = model.operator;

    if (isCombined) {
      var combinedModel = model;
      var orChecked = combinedModel.operator === 'OR';
      this.eJoinOperatorAnd.checked = !orChecked;
      this.eJoinOperatorOr.checked = orChecked;
      this.eType1.value = combinedModel.condition1.type;
      this.eType2.value = combinedModel.condition2.type;
      this.setConditionIntoUi(combinedModel.condition1, ConditionPosition.One);
      this.setConditionIntoUi(combinedModel.condition2, ConditionPosition.Two);
    } else {
      var simpleModel = model;
      this.eJoinOperatorAnd.checked = true;
      this.eJoinOperatorOr.checked = false;
      this.eType1.value = simpleModel.type;
      this.eType2.value = this.optionsFactory.getDefaultOption();
      this.setConditionIntoUi(simpleModel, ConditionPosition.One);
      this.setConditionIntoUi(null, ConditionPosition.Two);
    }
  };

  SimpleFilter.prototype.doesFilterPass = function (params) {
    var model = this.getModel();
    var isCombined = model.operator;

    if (isCombined) {
      var combinedModel = model;
      var firstResult = this.individualConditionPasses(params, combinedModel.condition1);
      var secondResult = this.individualConditionPasses(params, combinedModel.condition2);

      if (combinedModel.operator === 'AND') {
        return firstResult && secondResult;
      } else {
        return firstResult || secondResult;
      }
    } else {
      var simpleModel = model;
      var result = this.individualConditionPasses(params, simpleModel);
      return result;
    }
  };

  SimpleFilter.prototype.setParams = function (params) {
    _super.prototype.setParams.call(this, params);

    this.simpleFilterParams = params;
    this.optionsFactory = new OptionsFactory();
    this.optionsFactory.init(params, this.getDefaultFilterOptions());
    this.allowTwoConditions = !params.suppressAndOrCondition;
    this.putOptionsIntoDropdown();
    this.addChangedListeners();
  };

  SimpleFilter.prototype.putOptionsIntoDropdown = function () {
    var _this = this;

    var filterOptions = this.optionsFactory.getFilterOptions();
    filterOptions.forEach(function (option) {
      var createOption = function createOption() {
        var key = typeof option === 'string' ? option : option.displayKey;

        var localName = _this.translate(key);

        var eOption = document.createElement("option");
        eOption.text = localName;
        eOption.value = key;
        return eOption;
      };

      _this.eType1.add(createOption());

      _this.eType2.add(createOption());
    });
    var readOnly = filterOptions.length <= 1;
    this.eType1.disabled = readOnly;
    this.eType2.disabled = readOnly;
  };

  SimpleFilter.prototype.isAllowTwoConditions = function () {
    return this.allowTwoConditions;
  };

  SimpleFilter.prototype.createBodyTemplate = function () {
    var optionsTemplate1 = "<select class=\"ag-filter-select\" ref=\"eOptions1\"></select>";
    var valueTemplate1 = this.createValueTemplate(ConditionPosition.One);
    var optionsTemplate2 = "<select class=\"ag-filter-select\" ref=\"eOptions2\"></select>";
    var valueTemplate2 = this.createValueTemplate(ConditionPosition.Two);
    var uniqueGroupId = 'ag-simple-filter-and-or-' + this.getCompId();
    var translate = this.gridOptionsWrapper.getLocaleTextFunc();
    var andOrTemplate = "<div class=\"ag-filter-condition\" ref=\"eJoinOperatorPanel\">\n                    <label>\n                        <input ref=\"eJoinOperatorAnd\" type=\"radio\" class=\"and\" name=\"" + uniqueGroupId + "\" value=\"AND\")} checked=\"checked\" />\n                        " + translate('andCondition', 'AND') + "\n                    </label>\n                    <label>\n                        <input ref=\"eJoinOperatorOr\" type=\"radio\" class=\"or\" name=\"" + uniqueGroupId + "\" value=\"OR\" />\n                        " + translate('orCondition', 'OR') + "\n                    </label>\n                </div>";
    var template = optionsTemplate1 + "\n                " + valueTemplate1 + "\n                " + andOrTemplate + "\n                " + optionsTemplate2 + "\n                " + valueTemplate2;
    return template;
  };

  SimpleFilter.prototype.updateUiVisibility = function () {
    var firstConditionComplete = this.isConditionUiComplete(ConditionPosition.One);
    var showSecondFilter = this.allowTwoConditions && firstConditionComplete;

    _.setDisplayed(this.eCondition2Body, showSecondFilter);

    _.setDisplayed(this.eType2, showSecondFilter);

    _.setDisplayed(this.eJoinOperatorPanel, showSecondFilter);
  };

  SimpleFilter.prototype.resetUiToDefaults = function () {
    this.eJoinOperatorAnd.checked = true;
    var defaultOption = this.optionsFactory.getDefaultOption();
    this.eType1.value = defaultOption;
    this.eType2.value = defaultOption;
  };

  SimpleFilter.prototype.translate = function (toTranslate) {
    var translate = this.gridOptionsWrapper.getLocaleTextFunc();
    var defaultTranslation = DEFAULT_TRANSLATIONS[toTranslate];

    if (!defaultTranslation && this.optionsFactory.getCustomOption(toTranslate)) {
      defaultTranslation = this.optionsFactory.getCustomOption(toTranslate).displayName;
    }

    return translate(toTranslate, defaultTranslation);
  };

  SimpleFilter.prototype.addChangedListeners = function () {
    var _this = this;

    var listener = function listener() {
      return _this.onUiChanged();
    };

    this.addDestroyableEventListener(this.eType1, "change", listener);
    this.addDestroyableEventListener(this.eType2, "change", listener);
    this.addDestroyableEventListener(this.eJoinOperatorOr, "change", listener);
    this.addDestroyableEventListener(this.eJoinOperatorAnd, "change", listener);
  };

  SimpleFilter.prototype.doesFilterHaveHiddenInput = function (filterType) {
    var customFilterOption = this.optionsFactory.getCustomOption(filterType);
    return customFilterOption && customFilterOption.hideFilterInput;
  };

  SimpleFilter.EMPTY = 'empty';
  SimpleFilter.EQUALS = 'equals';
  SimpleFilter.NOT_EQUAL = 'notEqual';
  SimpleFilter.LESS_THAN = 'lessThan';
  SimpleFilter.LESS_THAN_OR_EQUAL = 'lessThanOrEqual';
  SimpleFilter.GREATER_THAN = 'greaterThan';
  SimpleFilter.GREATER_THAN_OR_EQUAL = 'greaterThanOrEqual';
  SimpleFilter.IN_RANGE = 'inRange';
  SimpleFilter.CONTAINS = 'contains';
  SimpleFilter.NOT_CONTAINS = 'notContains';
  SimpleFilter.STARTS_WITH = 'startsWith';
  SimpleFilter.ENDS_WITH = 'endsWith';

  __decorate$b([RefSelector('eOptions1')], SimpleFilter.prototype, "eType1", void 0);

  __decorate$b([RefSelector('eOptions2')], SimpleFilter.prototype, "eType2", void 0);

  __decorate$b([RefSelector('eJoinOperatorAnd')], SimpleFilter.prototype, "eJoinOperatorAnd", void 0);

  __decorate$b([RefSelector('eJoinOperatorOr')], SimpleFilter.prototype, "eJoinOperatorOr", void 0);

  __decorate$b([RefSelector('eCondition2Body')], SimpleFilter.prototype, "eCondition2Body", void 0);

  __decorate$b([RefSelector('eJoinOperatorPanel')], SimpleFilter.prototype, "eJoinOperatorPanel", void 0);

  return SimpleFilter;
}(ProvidedFilter);
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __extends$5 = undefined && undefined.__extends || function () {
  var _extendStatics6 = function extendStatics(d, b) {
    _extendStatics6 = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics6(d, b);
  };

  return function (d, b) {
    _extendStatics6(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var ScalerFilter =
/** @class */
function (_super) {
  __extends$5(ScalerFilter, _super);

  function ScalerFilter() {
    return _super !== null && _super.apply(this, arguments) || this;
  }

  ScalerFilter.prototype.setParams = function (params) {
    _super.prototype.setParams.call(this, params);

    this.scalarFilterParams = params;
    this.checkDeprecatedParams();
  };

  ScalerFilter.prototype.checkDeprecatedParams = function () {
    if (this.scalarFilterParams.nullComparator) {
      console.warn('ag-Grid: Since v21.0, the property filterParams.nullComparator is deprecated. ' + 'Please use filterParams.includeBlanksInEquals, filterParams.includeBlanksInLessThan and ' + 'filterParams.includeBlanksInGreaterThan instead.');
      this.scalarFilterParams.includeBlanksInEquals = this.scalarFilterParams.nullComparator.equals;
      this.scalarFilterParams.includeBlanksInLessThan = this.scalarFilterParams.nullComparator.lessThan;
      this.scalarFilterParams.includeBlanksInGreaterThan = this.scalarFilterParams.nullComparator.greaterThan;
    }
  };

  ScalerFilter.prototype.nullComparator = function (selectedOption, filterValue, gridValue) {
    if (gridValue == null) {
      var nullValue = this.canNullsPassFilter(selectedOption);

      if (selectedOption === ScalerFilter.EMPTY) {
        return 0;
      }

      if (selectedOption === ScalerFilter.EQUALS) {
        return nullValue ? 0 : 1;
      }

      if (selectedOption === ScalerFilter.GREATER_THAN) {
        return nullValue ? 1 : -1;
      }

      if (selectedOption === ScalerFilter.GREATER_THAN_OR_EQUAL) {
        return nullValue ? 1 : -1;
      }

      if (selectedOption === ScalerFilter.LESS_THAN_OR_EQUAL) {
        return nullValue ? -1 : 1;
      }

      if (selectedOption === ScalerFilter.LESS_THAN) {
        return nullValue ? -1 : 1;
      }

      if (selectedOption === ScalerFilter.NOT_EQUAL) {
        return nullValue ? 1 : 0;
      }
    }

    var actualComparator = this.comparator();
    return actualComparator(filterValue, gridValue);
  };

  ScalerFilter.prototype.canNullsPassFilter = function (type) {
    switch (type) {
      case SimpleFilter.GREATER_THAN:
      case SimpleFilter.GREATER_THAN_OR_EQUAL:
        return this.scalarFilterParams.includeBlanksInGreaterThan;

      case SimpleFilter.LESS_THAN:
      case SimpleFilter.LESS_THAN_OR_EQUAL:
        return this.scalarFilterParams.includeBlanksInLessThan;

      case SimpleFilter.EQUALS:
        return this.scalarFilterParams.includeBlanksInEquals;
    }
  };

  ScalerFilter.prototype.individualConditionPasses = function (params, filterModel) {
    var cellValue = this.scalarFilterParams.valueGetter(params.node);
    var range = this.mapRangeFromModel(filterModel);
    var filterValue = range.from;
    var filterValueTo = range.to;
    var filterType = filterModel.type;
    var customFilterOption = this.optionsFactory.getCustomOption(filterType);

    if (customFilterOption) {
      // only execute the custom filter if a value exists or a value isn't required, i.e. input is hidden
      if (filterValue != null || customFilterOption.hideFilterInput) {
        return customFilterOption.test(filterValue, cellValue);
      }
    } // why this? looks like logic that should be in parent class????
    // if (filterValue == null) {
    //     return type === FilterConditionType.MAIN ? true : this.conditionValue === 'AND';
    // }


    var compareResult = this.nullComparator(filterType, filterValue, cellValue);

    if (filterType === ScalerFilter.EQUALS) {
      return compareResult === 0;
    }

    if (filterType === ScalerFilter.GREATER_THAN) {
      return compareResult > 0;
    }

    if (filterType === ScalerFilter.GREATER_THAN_OR_EQUAL) {
      return compareResult >= 0;
    }

    if (filterType === ScalerFilter.LESS_THAN_OR_EQUAL) {
      return compareResult <= 0;
    }

    if (filterType === ScalerFilter.LESS_THAN) {
      return compareResult < 0;
    }

    if (filterType === ScalerFilter.NOT_EQUAL) {
      return compareResult != 0;
    } // From now on the type is a range and rawFilterValues must be an array!


    var compareToResult = this.nullComparator(filterType, filterValueTo, cellValue);

    if (filterType === ScalerFilter.IN_RANGE) {
      if (!this.scalarFilterParams.inRangeInclusive) {
        return compareResult > 0 && compareToResult < 0;
      } else {
        return compareResult >= 0 && compareToResult <= 0;
      }
    }

    throw new Error('Unexpected type of filter: ' + filterType);
  };

  ScalerFilter.DEFAULT_NULL_COMPARATOR = {
    equals: false,
    lessThan: false,
    greaterThan: false
  };
  return ScalerFilter;
}(SimpleFilter);
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __extends$6 = undefined && undefined.__extends || function () {
  var _extendStatics7 = function extendStatics(d, b) {
    _extendStatics7 = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics7(d, b);
  };

  return function (d, b) {
    _extendStatics7(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var __decorate$c = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var DateFilter =
/** @class */
function (_super) {
  __extends$6(DateFilter, _super);

  function DateFilter() {
    return _super !== null && _super.apply(this, arguments) || this;
  }

  DateFilter.prototype.mapRangeFromModel = function (filterModel) {
    // unlike the other filters, we do two things here:
    // 1) allow for different attribute names (same as done for other filters) (eg the 'from' and 'to'
    //    are in different locations in Date and Number filter models)
    // 2) convert the type (cos Date filter uses Dates, however model is 'string')
    //
    // NOTE: The conversion of string to date also removes the timezone - ie when user picks
    //       a date form the UI, it will have timezone info in it. This is lost when creating
    //       the model. Then when we recreate the date again here, it's without timezone.
    return {
      from: _.parseYyyyMmDdToDate(filterModel.dateFrom, "-"),
      to: _.parseYyyyMmDdToDate(filterModel.dateTo, "-")
    };
  };

  DateFilter.prototype.setValueFromFloatingFilter = function (value) {
    if (value != null) {
      var dateFrom = _.parseYyyyMmDdToDate(value, "-");

      this.dateCompFrom1.setDate(dateFrom);
    } else {
      this.dateCompFrom1.setDate(null);
    }

    this.dateCompTo1.setDate(null);
    this.dateCompFrom2.setDate(null);
    this.dateCompTo2.setDate(null);
  };

  DateFilter.prototype.setConditionIntoUi = function (model, position) {
    var positionOne = position === ConditionPosition.One;
    var dateFromString = model ? model.dateFrom : null;
    var dateToString = model ? model.dateTo : null;

    var dateFrom = _.parseYyyyMmDdToDate(dateFromString, "-");

    var dateTo = _.parseYyyyMmDdToDate(dateToString, "-");

    var compFrom = positionOne ? this.dateCompFrom1 : this.dateCompFrom2;
    var compTo = positionOne ? this.dateCompTo1 : this.dateCompTo2;
    compFrom.setDate(dateFrom);
    compTo.setDate(dateTo);
  };

  DateFilter.prototype.resetUiToDefaults = function () {
    _super.prototype.resetUiToDefaults.call(this);

    this.dateCompTo1.setDate(null);
    this.dateCompTo2.setDate(null);
    this.dateCompFrom1.setDate(null);
    this.dateCompFrom2.setDate(null);
  };

  DateFilter.prototype.comparator = function () {
    return this.dateFilterParams.comparator ? this.dateFilterParams.comparator : this.defaultComparator.bind(this);
  };

  DateFilter.prototype.defaultComparator = function (filterDate, cellValue) {
    //The default comparator assumes that the cellValue is a date
    var cellAsDate = cellValue;

    if (cellAsDate < filterDate) {
      return -1;
    }

    if (cellAsDate > filterDate) {
      return 1;
    }

    return cellValue != null ? 0 : -1;
  };

  DateFilter.prototype.setParams = function (params) {
    _super.prototype.setParams.call(this, params);

    this.dateFilterParams = params;
    this.createDateComponents();
  };

  DateFilter.prototype.createDateComponents = function () {
    var _this = this; // params to pass to all four date comps


    var dateComponentParams = {
      onDateChanged: function onDateChanged() {
        return _this.onUiChanged();
      },
      filterParams: this.dateFilterParams
    };
    this.dateCompFrom1 = new DateCompWrapper(this.userComponentFactory, dateComponentParams, this.ePanelFrom1);
    this.dateCompFrom2 = new DateCompWrapper(this.userComponentFactory, dateComponentParams, this.ePanelFrom2);
    this.dateCompTo1 = new DateCompWrapper(this.userComponentFactory, dateComponentParams, this.ePanelTo1);
    this.dateCompTo2 = new DateCompWrapper(this.userComponentFactory, dateComponentParams, this.ePanelTo2);
    this.addDestroyFunc(function () {
      _this.dateCompFrom1.destroy();

      _this.dateCompFrom2.destroy();

      _this.dateCompTo1.destroy();

      _this.dateCompTo2.destroy();
    });
  };

  DateFilter.prototype.getDefaultFilterOptions = function () {
    return DateFilter.DEFAULT_FILTER_OPTIONS;
  };

  DateFilter.prototype.createValueTemplate = function (position) {
    var positionOne = position === ConditionPosition.One;
    var pos = positionOne ? '1' : '2';
    return "<div class=\"ag-filter-body\" ref=\"eCondition" + pos + "Body\">\n                    <div class=\"ag-filter-date-from\" ref=\"ePanelFrom" + pos + "\">\n                    </div>\n                    <div class=\"ag-filter-date-to\" ref=\"ePanelTo" + pos + "\"\">\n                    </div>\n                </div>";
  };

  DateFilter.prototype.isConditionUiComplete = function (position) {
    var positionOne = position === ConditionPosition.One;
    var option = positionOne ? this.getCondition1Type() : this.getCondition2Type();
    var compFrom = positionOne ? this.dateCompFrom1 : this.dateCompFrom2;
    var compTo = positionOne ? this.dateCompTo1 : this.dateCompTo2;
    var valueFrom = compFrom.getDate();
    var valueTo = compTo.getDate();

    if (option === SimpleFilter.EMPTY) {
      return false;
    }

    if (this.doesFilterHaveHiddenInput(option)) {
      return true;
    }

    if (option === SimpleFilter.IN_RANGE) {
      return valueFrom != null && valueTo != null;
    } else {
      return valueFrom != null;
    }
  };

  DateFilter.prototype.areSimpleModelsEqual = function (aSimple, bSimple) {
    return aSimple.dateFrom === bSimple.dateFrom && aSimple.dateTo === bSimple.dateTo && aSimple.type === bSimple.type;
  }; // needed for creating filter model


  DateFilter.prototype.getFilterType = function () {
    return DateFilter.FILTER_TYPE;
  };

  DateFilter.prototype.createCondition = function (position) {
    var positionOne = position === ConditionPosition.One;
    var type = positionOne ? this.getCondition1Type() : this.getCondition2Type();
    var dateCompTo = positionOne ? this.dateCompTo1 : this.dateCompTo2;
    var dateCompFrom = positionOne ? this.dateCompFrom1 : this.dateCompFrom2;
    return {
      dateTo: _.serializeDateToYyyyMmDd(dateCompTo.getDate(), "-"),
      dateFrom: _.serializeDateToYyyyMmDd(dateCompFrom.getDate(), "-"),
      type: type,
      filterType: DateFilter.FILTER_TYPE
    };
  };

  DateFilter.prototype.updateUiVisibility = function () {
    _super.prototype.updateUiVisibility.call(this);

    var showFrom1 = this.showValueFrom(this.getCondition1Type());

    _.setDisplayed(this.ePanelFrom1, showFrom1);

    var showTo1 = this.showValueTo(this.getCondition1Type());

    _.setDisplayed(this.ePanelTo1, showTo1);

    var showFrom2 = this.showValueFrom(this.getCondition2Type());

    _.setDisplayed(this.ePanelFrom2, showFrom2);

    var showTo2 = this.showValueTo(this.getCondition2Type());

    _.setDisplayed(this.ePanelTo2, showTo2);
  };

  DateFilter.FILTER_TYPE = 'date';
  DateFilter.DEFAULT_FILTER_OPTIONS = [ScalerFilter.EQUALS, ScalerFilter.GREATER_THAN, ScalerFilter.LESS_THAN, ScalerFilter.NOT_EQUAL, ScalerFilter.IN_RANGE];

  __decorate$c([RefSelector('ePanelFrom1')], DateFilter.prototype, "ePanelFrom1", void 0);

  __decorate$c([RefSelector('ePanelFrom2')], DateFilter.prototype, "ePanelFrom2", void 0);

  __decorate$c([RefSelector('ePanelTo1')], DateFilter.prototype, "ePanelTo1", void 0);

  __decorate$c([RefSelector('ePanelTo2')], DateFilter.prototype, "ePanelTo2", void 0);

  __decorate$c([Autowired('userComponentFactory')], DateFilter.prototype, "userComponentFactory", void 0);

  return DateFilter;
}(ScalerFilter);
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var TouchListener =
/** @class */
function () {
  function TouchListener(eElement, preventMouseClick) {
    var _this = this;

    if (preventMouseClick === void 0) {
      preventMouseClick = false;
    }

    this.destroyFuncs = [];
    this.touching = false;
    this.eventService = new EventService();
    this.eElement = eElement;
    this.preventMouseClick = preventMouseClick;
    var startListener = this.onTouchStart.bind(this);
    var moveListener = this.onTouchMove.bind(this);
    var endListener = this.onTouchEnd.bind(this);
    this.eElement.addEventListener("touchstart", startListener, {
      passive: true
    });
    this.eElement.addEventListener("touchmove", moveListener, {
      passive: true
    }); // we set passive=false, as we want to prevent default on this event

    this.eElement.addEventListener("touchend", endListener, {
      passive: false
    });
    this.destroyFuncs.push(function () {
      _this.eElement.removeEventListener("touchstart", startListener, {
        passive: true
      });

      _this.eElement.removeEventListener("touchmove", moveListener, {
        passive: true
      });

      _this.eElement.removeEventListener("touchend", endListener, {
        passive: false
      });
    });
  }

  TouchListener.prototype.getActiveTouch = function (touchList) {
    for (var i = 0; i < touchList.length; i++) {
      var matches = touchList[i].identifier === this.touchStart.identifier;

      if (matches) {
        return touchList[i];
      }
    }

    return null;
  };

  TouchListener.prototype.addEventListener = function (eventType, listener) {
    this.eventService.addEventListener(eventType, listener);
  };

  TouchListener.prototype.removeEventListener = function (eventType, listener) {
    this.eventService.removeEventListener(eventType, listener);
  };

  TouchListener.prototype.onTouchStart = function (touchEvent) {
    var _this = this; // only looking at one touch point at any time


    if (this.touching) {
      return;
    }

    this.touchStart = touchEvent.touches[0];
    this.touching = true;
    this.moved = false;
    var touchStartCopy = this.touchStart;
    window.setTimeout(function () {
      var touchesMatch = _this.touchStart === touchStartCopy;

      if (_this.touching && touchesMatch && !_this.moved) {
        _this.moved = true;
        var event_1 = {
          type: TouchListener.EVENT_LONG_TAP,
          touchStart: _this.touchStart,
          touchEvent: touchEvent
        };

        _this.eventService.dispatchEvent(event_1);
      }
    }, 500);
  };

  TouchListener.prototype.onTouchMove = function (touchEvent) {
    if (!this.touching) {
      return;
    }

    var touch = this.getActiveTouch(touchEvent.touches);

    if (!touch) {
      return;
    }

    var eventIsFarAway = !_.areEventsNear(touch, this.touchStart, 4);

    if (eventIsFarAway) {
      this.moved = true;
    }
  };

  TouchListener.prototype.onTouchEnd = function (touchEvent) {
    if (!this.touching) {
      return;
    }

    if (!this.moved) {
      var event_2 = {
        type: TouchListener.EVENT_TAP,
        touchStart: this.touchStart
      };
      this.eventService.dispatchEvent(event_2);
      this.checkForDoubleTap();
    } // stops the tap from also been processed as a mouse click


    if (this.preventMouseClick) {
      touchEvent.preventDefault();
    }

    this.touching = false;
  };

  TouchListener.prototype.checkForDoubleTap = function () {
    var now = new Date().getTime();

    if (this.lastTapTime && this.lastTapTime > 0) {
      // if previous tap, see if duration is short enough to be considered double tap
      var interval = now - this.lastTapTime;

      if (interval > TouchListener.DOUBLE_TAP_MILLIS) {
        // dispatch double tap event
        var event_3 = {
          type: TouchListener.EVENT_DOUBLE_TAP,
          touchStart: this.touchStart
        };
        this.eventService.dispatchEvent(event_3); // this stops a tripple tap ending up as two double taps

        this.lastTapTime = null;
      } else {
        this.lastTapTime = now;
      }
    } else {
      this.lastTapTime = now;
    }
  };

  TouchListener.prototype.destroy = function () {
    this.destroyFuncs.forEach(function (func) {
      return func();
    });
  };

  TouchListener.EVENT_TAP = "tap";
  TouchListener.EVENT_DOUBLE_TAP = "doubleTap";
  TouchListener.EVENT_LONG_TAP = "longTap";
  TouchListener.DOUBLE_TAP_MILLIS = 500;
  return TouchListener;
}();
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __extends$7 = undefined && undefined.__extends || function () {
  var _extendStatics8 = function extendStatics(d, b) {
    _extendStatics8 = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics8(d, b);
  };

  return function (d, b) {
    _extendStatics8(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var __decorate$d = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var HeaderComp =
/** @class */
function (_super) {
  __extends$7(HeaderComp, _super);

  function HeaderComp() {
    var _this = _super !== null && _super.apply(this, arguments) || this;

    _this.lastMovingChanged = 0;
    return _this;
  }

  HeaderComp.prototype.init = function (params) {
    var template = _.firstExistingValue(params.template, HeaderComp.TEMPLATE); // take account of any newlines & whitespace before/after the actual template


    template = template && template.trim ? template.trim() : template;
    this.setTemplate(template);
    this.params = params;
    this.setupTap();
    this.setupIcons(params.column);
    this.setupMenu();
    this.setupSort();
    this.setupFilterIcon();
    this.setupText(params.displayName);
  };

  HeaderComp.prototype.setupText = function (displayName) {
    var displayNameSanitised = _.escape(displayName);

    if (this.eText) {
      this.eText.innerHTML = displayNameSanitised;
    }
  };

  HeaderComp.prototype.setupIcons = function (column) {
    this.addInIcon('sortAscending', this.eSortAsc, column);
    this.addInIcon('sortDescending', this.eSortDesc, column);
    this.addInIcon('sortUnSort', this.eSortNone, column);
    this.addInIcon('menu', this.eMenu, column);
    this.addInIcon('filter', this.eFilter, column);
  };

  HeaderComp.prototype.addInIcon = function (iconName, eParent, column) {
    if (eParent == null) {
      return;
    }

    var eIcon = _.createIconNoSpan(iconName, this.gridOptionsWrapper, column);

    eParent.appendChild(eIcon);
  };

  HeaderComp.prototype.setupTap = function () {
    var _this = this;

    var options = this.gridOptionsWrapper;

    if (options.isSuppressTouch()) {
      return;
    }

    var touchListener = new TouchListener(this.getGui(), true);
    var suppressMenuHide = options.isSuppressMenuHide();

    var tapMenuButton = suppressMenuHide && _.exists(this.eMenu);

    var menuTouchListener = tapMenuButton ? new TouchListener(this.eMenu, true) : touchListener;

    if (this.params.enableMenu) {
      var eventType = tapMenuButton ? 'EVENT_TAP' : 'EVENT_LONG_TAP';

      var showMenuFn = function showMenuFn(event) {
        options.getApi().showColumnMenuAfterMouseClick(_this.params.column, event.touchStart);
      };

      this.addDestroyableEventListener(menuTouchListener, TouchListener[eventType], showMenuFn);
    }

    if (this.params.enableSorting) {
      var tapListener = function tapListener(event) {
        var target = event.touchStart.target; // When suppressMenuHide is true, a tap on the menu icon will bubble up
        // to the header container, in that case we should not sort

        if (suppressMenuHide && _this.eMenu.contains(target)) {
          return;
        }

        _this.sortController.progressSort(_this.params.column, false, "uiColumnSorted");
      };

      this.addDestroyableEventListener(touchListener, TouchListener.EVENT_TAP, tapListener);
    } // if tapMenuButton is true `touchListener` and `menuTouchListener` are different
    // so we need to make sure to destroy both listeners here


    this.addDestroyFunc(function () {
      return touchListener.destroy();
    });

    if (tapMenuButton) {
      this.addDestroyFunc(function () {
        return menuTouchListener.destroy();
      });
    }
  };

  HeaderComp.prototype.setupMenu = function () {
    var _this = this; // if no menu provided in template, do nothing


    if (!this.eMenu) {
      return;
    } // we don't show the menu if on an iPad/iPhone, as the user cannot have a pointer device
    // Note: If suppressMenuHide is set to true the menu will be displayed, and if suppressMenuHide
    // is false (default) user will need to use longpress to display the menu.


    var suppressMenuHide = this.gridOptionsWrapper.isSuppressMenuHide();
    var hideShowMenu = !this.params.enableMenu || _.isIOSUserAgent() && !suppressMenuHide;

    if (hideShowMenu) {
      _.removeFromParent(this.eMenu);

      return;
    }

    this.addDestroyableEventListener(this.eMenu, 'click', function () {
      return _this.showMenu(_this.eMenu);
    });

    if (!suppressMenuHide) {
      this.eMenu.style.opacity = '0';
      this.addGuiEventListener('mouseover', function () {
        _this.eMenu.style.opacity = '1';
      });
      this.addGuiEventListener('mouseout', function () {
        _this.eMenu.style.opacity = '0';
      });
    }

    var style = this.eMenu.style;
    style.transition = 'opacity 0.2s, border 0.2s';
    style['-webkit-transition'] = 'opacity 0.2s, border 0.2s';
  };

  HeaderComp.prototype.showMenu = function (eventSource) {
    this.menuFactory.showMenuAfterButtonClick(this.params.column, eventSource);
  };

  HeaderComp.prototype.removeSortIcons = function () {
    _.removeFromParent(this.eSortAsc);

    _.removeFromParent(this.eSortDesc);

    _.removeFromParent(this.eSortNone);

    _.removeFromParent(this.eSortOrder);
  };

  HeaderComp.prototype.setupSort = function () {
    var _this = this;

    var enableSorting = this.params.enableSorting;

    if (!enableSorting) {
      this.removeSortIcons();
      return;
    }

    var sortUsingCtrl = this.gridOptionsWrapper.isMultiSortKeyCtrl(); // keep track of last time the moving changed flag was set

    this.addDestroyableEventListener(this.params.column, Column.EVENT_MOVING_CHANGED, function () {
      _this.lastMovingChanged = new Date().getTime();
    }); // add the event on the header, so when clicked, we do sorting

    if (this.eLabel) {
      this.addDestroyableEventListener(this.eLabel, 'click', function (event) {
        // sometimes when moving a column via dragging, this was also firing a clicked event.
        // here is issue raised by user: https://ag-grid.zendesk.com/agent/tickets/1076
        // this check stops sort if a) column is moving or b) column moved less than 200ms ago (so caters for race condition)
        var moving = _this.params.column.isMoving();

        var nowTime = new Date().getTime(); // typically there is <2ms if moving flag was set recently, as it would be done in same VM turn

        var movedRecently = nowTime - _this.lastMovingChanged < 50;
        var columnMoving = moving || movedRecently;

        if (!columnMoving) {
          var multiSort = sortUsingCtrl ? event.ctrlKey || event.metaKey : event.shiftKey;

          _this.params.progressSort(multiSort);
        }
      });
    }

    this.addDestroyableEventListener(this.params.column, Column.EVENT_SORT_CHANGED, this.onSortChanged.bind(this));
    this.onSortChanged();
    this.addDestroyableEventListener(this.eventService, Events.EVENT_SORT_CHANGED, this.setMultiSortOrder.bind(this));
    this.setMultiSortOrder();
  };

  HeaderComp.prototype.onSortChanged = function () {
    _.addOrRemoveCssClass(this.getGui(), 'ag-header-cell-sorted-asc', this.params.column.isSortAscending());

    _.addOrRemoveCssClass(this.getGui(), 'ag-header-cell-sorted-desc', this.params.column.isSortDescending());

    _.addOrRemoveCssClass(this.getGui(), 'ag-header-cell-sorted-none', this.params.column.isSortNone());

    if (this.eSortAsc) {
      _.addOrRemoveCssClass(this.eSortAsc, 'ag-hidden', !this.params.column.isSortAscending());
    }

    if (this.eSortDesc) {
      _.addOrRemoveCssClass(this.eSortDesc, 'ag-hidden', !this.params.column.isSortDescending());
    }

    if (this.eSortNone) {
      var alwaysHideNoSort = !this.params.column.getColDef().unSortIcon && !this.gridOptionsWrapper.isUnSortIcon();

      _.addOrRemoveCssClass(this.eSortNone, 'ag-hidden', alwaysHideNoSort || !this.params.column.isSortNone());
    }
  }; // we listen here for global sort events, NOT column sort events, as we want to do this
  // when sorting has been set on all column (if we listened just for our col (where we
  // set the asc / desc icons) then it's possible other cols are yet to get their sorting state.


  HeaderComp.prototype.setMultiSortOrder = function () {
    if (!this.eSortOrder) {
      return;
    }

    var col = this.params.column;
    var allColumnsWithSorting = this.sortController.getColumnsWithSortingOrdered();
    var indexThisCol = allColumnsWithSorting.indexOf(col);
    var moreThanOneColSorting = allColumnsWithSorting.length > 1;
    var showIndex = col.isSorting() && moreThanOneColSorting;

    _.setDisplayed(this.eSortOrder, showIndex);

    if (indexThisCol >= 0) {
      this.eSortOrder.innerHTML = (indexThisCol + 1).toString();
    } else {
      _.clearElement(this.eSortOrder);
    }
  };

  HeaderComp.prototype.setupFilterIcon = function () {
    if (!this.eFilter) {
      return;
    }

    this.addDestroyableEventListener(this.params.column, Column.EVENT_FILTER_CHANGED, this.onFilterChanged.bind(this));
    this.onFilterChanged();
  };

  HeaderComp.prototype.onFilterChanged = function () {
    var filterPresent = this.params.column.isFilterActive();

    _.addOrRemoveCssClass(this.eFilter, 'ag-hidden', !filterPresent);
  };

  HeaderComp.TEMPLATE = '<div class="ag-cell-label-container" role="presentation">' + '  <span ref="eMenu" class="ag-header-icon ag-header-cell-menu-button" aria-hidden="true"></span>' + '  <div ref="eLabel" class="ag-header-cell-label" role="presentation" unselectable="on">' + '    <span ref="eText" class="ag-header-cell-text" role="columnheader" unselectable="on"></span>' + '    <span ref="eFilter" class="ag-header-icon ag-filter-icon" aria-hidden="true"></span>' + '    <span ref="eSortOrder" class="ag-header-icon ag-sort-order" aria-hidden="true"></span>' + '    <span ref="eSortAsc" class="ag-header-icon ag-sort-ascending-icon" aria-hidden="true"></span>' + '    <span ref="eSortDesc" class="ag-header-icon ag-sort-descending-icon" aria-hidden="true"></span>' + '    <span ref="eSortNone" class="ag-header-icon ag-sort-none-icon" aria-hidden="true"></span>' + '  </div>' + '</div>';

  __decorate$d([Autowired('gridOptionsWrapper')], HeaderComp.prototype, "gridOptionsWrapper", void 0);

  __decorate$d([Autowired('sortController')], HeaderComp.prototype, "sortController", void 0);

  __decorate$d([Autowired('menuFactory')], HeaderComp.prototype, "menuFactory", void 0);

  __decorate$d([Autowired('eventService')], HeaderComp.prototype, "eventService", void 0);

  __decorate$d([RefSelector('eFilter')], HeaderComp.prototype, "eFilter", void 0);

  __decorate$d([RefSelector('eSortAsc')], HeaderComp.prototype, "eSortAsc", void 0);

  __decorate$d([RefSelector('eSortDesc')], HeaderComp.prototype, "eSortDesc", void 0);

  __decorate$d([RefSelector('eSortNone')], HeaderComp.prototype, "eSortNone", void 0);

  __decorate$d([RefSelector('eSortOrder')], HeaderComp.prototype, "eSortOrder", void 0);

  __decorate$d([RefSelector('eMenu')], HeaderComp.prototype, "eMenu", void 0);

  __decorate$d([RefSelector('eLabel')], HeaderComp.prototype, "eLabel", void 0);

  __decorate$d([RefSelector('eText')], HeaderComp.prototype, "eText", void 0);

  return HeaderComp;
}(Component);
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __extends$8 = undefined && undefined.__extends || function () {
  var _extendStatics9 = function extendStatics(d, b) {
    _extendStatics9 = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics9(d, b);
  };

  return function (d, b) {
    _extendStatics9(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var __decorate$e = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var HeaderGroupComp =
/** @class */
function (_super) {
  __extends$8(HeaderGroupComp, _super);

  function HeaderGroupComp() {
    return _super.call(this, HeaderGroupComp.TEMPLATE) || this;
  }

  HeaderGroupComp.prototype.init = function (params) {
    this.params = params;
    this.setupLabel();
    this.addGroupExpandIcon();
    this.setupExpandIcons();
  };

  HeaderGroupComp.prototype.setupExpandIcons = function () {
    var _this = this;

    this.addInIcon("columnGroupOpened", "agOpened");
    this.addInIcon("columnGroupClosed", "agClosed");

    var expandAction = function expandAction(event) {
      if (_.isStopPropagationForAgGrid(event)) {
        return;
      }

      var newExpandedValue = !_this.params.columnGroup.isExpanded();

      _this.columnController.setColumnGroupOpened(_this.params.columnGroup.getOriginalColumnGroup(), newExpandedValue, "uiColumnExpanded");
    };

    this.addTouchAndClickListeners(this.eCloseIcon, expandAction);
    this.addTouchAndClickListeners(this.eOpenIcon, expandAction);

    var stopPropagationAction = function stopPropagationAction(event) {
      _.stopPropagationForAgGrid(event);
    }; // adding stopPropagation to the double click for the icons prevents double click action happening
    // when the icons are clicked. if the icons are double clicked, then the groups should open and
    // then close again straight away. if we also listened to double click, then the group would open,
    // close, then open, which is not what we want. double click should only action if the user double
    // clicks outside of the icons.


    this.addDestroyableEventListener(this.eCloseIcon, "dblclick", stopPropagationAction);
    this.addDestroyableEventListener(this.eOpenIcon, "dblclick", stopPropagationAction);
    this.addDestroyableEventListener(this.getGui(), "dblclick", expandAction);
    this.updateIconVisibility();
    var originalColumnGroup = this.params.columnGroup.getOriginalColumnGroup();
    this.addDestroyableEventListener(originalColumnGroup, OriginalColumnGroup.EVENT_EXPANDED_CHANGED, this.updateIconVisibility.bind(this));
    this.addDestroyableEventListener(originalColumnGroup, OriginalColumnGroup.EVENT_EXPANDABLE_CHANGED, this.updateIconVisibility.bind(this));
  };

  HeaderGroupComp.prototype.addTouchAndClickListeners = function (eElement, action) {
    var touchListener = new TouchListener(eElement);
    this.addDestroyableEventListener(touchListener, TouchListener.EVENT_TAP, action);
    this.addDestroyFunc(function () {
      return touchListener.destroy();
    });
    this.addDestroyableEventListener(eElement, "click", action);
  };

  HeaderGroupComp.prototype.updateIconVisibility = function () {
    var columnGroup = this.params.columnGroup;

    if (columnGroup.isExpandable()) {
      var expanded = this.params.columnGroup.isExpanded();

      _.setDisplayed(this.eOpenIcon, expanded);

      _.setDisplayed(this.eCloseIcon, !expanded);
    } else {
      _.setDisplayed(this.eOpenIcon, false);

      _.setDisplayed(this.eCloseIcon, false);
    }
  };

  HeaderGroupComp.prototype.addInIcon = function (iconName, refName) {
    var eIcon = _.createIconNoSpan(iconName, this.gridOptionsWrapper, null);

    this.getRefElement(refName).appendChild(eIcon);
  };

  HeaderGroupComp.prototype.addGroupExpandIcon = function () {
    if (!this.params.columnGroup.isExpandable()) {
      _.setDisplayed(this.eOpenIcon, false);

      _.setDisplayed(this.eCloseIcon, false);

      return;
    }
  };

  HeaderGroupComp.prototype.setupLabel = function () {
    // no renderer, default text render
    if (this.params.displayName && this.params.displayName !== "") {
      var eInnerText = this.getRefElement("agLabel");
      eInnerText.innerHTML = this.params.displayName;
    }
  };

  HeaderGroupComp.TEMPLATE = "<div class=\"ag-header-group-cell-label\" ref=\"agContainer\" role=\"presentation\">" + "<span ref=\"agLabel\" class=\"ag-header-group-text\" role=\"columnheader\"></span>" + "<span ref=\"agOpened\" class=\"ag-header-icon ag-header-expand-icon ag-header-expand-icon-expanded\"></span>" + "<span ref=\"agClosed\" class=\"ag-header-icon ag-header-expand-icon ag-header-expand-icon-collapsed\"></span>" + "</div>";

  __decorate$e([Autowired("columnController")], HeaderGroupComp.prototype, "columnController", void 0);

  __decorate$e([Autowired("gridOptionsWrapper")], HeaderGroupComp.prototype, "gridOptionsWrapper", void 0);

  __decorate$e([RefSelector("agOpened")], HeaderGroupComp.prototype, "eOpenIcon", void 0);

  __decorate$e([RefSelector("agClosed")], HeaderGroupComp.prototype, "eCloseIcon", void 0);

  return HeaderGroupComp;
}(Component);
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __decorate$f = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var RowNode =
/** @class */
function () {
  function RowNode() {
    /** Children mapped by the pivot columns */
    this.childrenMapped = {};
    /** True by default - can be overridden via gridOptions.isRowSelectable(rowNode) */

    this.selectable = true;
    /** Used by sorting service - to give deterministic sort to groups. Previously we
     * just id for this, however id is a string and had slower sorting compared to numbers. */

    this.__objectId = RowNode.OBJECT_ID_SEQUENCE++;
    /** True when nodes with the same id are being removed and added as part of the same batch transaction */

    this.alreadyRendered = false;
    this.selected = false;
  }

  RowNode.prototype.setData = function (data) {
    var oldData = this.data;
    this.data = data;
    this.valueCache.onDataChanged();
    this.updateDataOnDetailNode();
    this.checkRowSelectable();
    var event = this.createDataChangedEvent(data, oldData, false);
    this.dispatchLocalEvent(event);
  }; // when we are doing master / detail, the detail node is lazy created, but then kept around.
  // so if we show / hide the detail, the same detail rowNode is used. so we need to keep the data
  // in sync, otherwise expand/collapse of the detail would still show the old values.


  RowNode.prototype.updateDataOnDetailNode = function () {
    if (this.detailNode) {
      this.detailNode.data = this.data;
    }
  };

  RowNode.prototype.createDataChangedEvent = function (newData, oldData, update) {
    return {
      type: RowNode.EVENT_DATA_CHANGED,
      node: this,
      oldData: oldData,
      newData: newData,
      update: update
    };
  };

  RowNode.prototype.createLocalRowEvent = function (type) {
    return {
      type: type,
      node: this
    };
  }; // similar to setRowData, however it is expected that the data is the same data item. this
  // is intended to be used with Redux type stores, where the whole data can be changed. we are
  // guaranteed that the data is the same entity (so grid doesn't need to worry about the id of the
  // underlying data changing, hence doesn't need to worry about selection). the grid, upon receiving
  // dataChanged event, will refresh the cells rather than rip them all out (so user can show transitions).


  RowNode.prototype.updateData = function (data) {
    var oldData = this.data;
    this.data = data;
    this.updateDataOnDetailNode();
    this.checkRowSelectable();
    this.updateDataOnDetailNode();
    var event = this.createDataChangedEvent(data, oldData, true);
    this.dispatchLocalEvent(event);
  };

  RowNode.prototype.getRowIndexString = function () {
    if (this.rowPinned === Constants.PINNED_TOP) {
      return 't-' + this.rowIndex;
    } else if (this.rowPinned === Constants.PINNED_BOTTOM) {
      return 'b-' + this.rowIndex;
    } else {
      return this.rowIndex.toString();
    }
  };

  RowNode.prototype.createDaemonNode = function () {
    var oldNode = new RowNode();
    this.context.wireBean(oldNode); // just copy the id and data, this is enough for the node to be used
    // in the selection controller (the selection controller is the only
    // place where daemon nodes can live).

    oldNode.id = this.id;
    oldNode.data = this.data;
    oldNode.daemon = true;
    oldNode.selected = this.selected;
    oldNode.level = this.level;
    return oldNode;
  };

  RowNode.prototype.setDataAndId = function (data, id) {
    var oldNode = _.exists(this.id) ? this.createDaemonNode() : null;
    var oldData = this.data;
    this.data = data;
    this.updateDataOnDetailNode();
    this.setId(id);
    this.selectionController.syncInRowNode(this, oldNode);
    this.checkRowSelectable();
    var event = this.createDataChangedEvent(data, oldData, false);
    this.dispatchLocalEvent(event);
  };

  RowNode.prototype.checkRowSelectable = function () {
    var isRowSelectableFunc = this.gridOptionsWrapper.getIsRowSelectableFunc();

    var shouldInvokeIsRowSelectable = isRowSelectableFunc && _.exists(this);

    this.setRowSelectable(shouldInvokeIsRowSelectable ? isRowSelectableFunc(this) : true);
  };

  RowNode.prototype.setRowSelectable = function (newVal) {
    if (this.selectable !== newVal) {
      this.selectable = newVal;

      if (this.eventService) {
        this.eventService.dispatchEvent(this.createLocalRowEvent(RowNode.EVENT_SELECTABLE_CHANGED));
      }
    }
  };

  RowNode.prototype.setId = function (id) {
    // see if user is providing the id's
    var getRowNodeId = this.gridOptionsWrapper.getRowNodeIdFunc();

    if (getRowNodeId) {
      // if user is providing the id's, then we set the id only after the data has been set.
      // this is important for virtual pagination and viewport, where empty rows exist.
      if (this.data) {
        this.id = getRowNodeId(this.data);
      } else {
        // this can happen if user has set blank into the rowNode after the row previously
        // having data. this happens in virtual page row model, when data is delete and
        // the page is refreshed.
        this.id = undefined;
      }
    } else {
      this.id = id;
    }
  };

  RowNode.prototype.isPixelInRange = function (pixel) {
    return pixel >= this.rowTop && pixel < this.rowTop + this.rowHeight;
  };

  RowNode.prototype.clearRowTop = function () {
    this.oldRowTop = this.rowTop;
    this.setRowTop(null);
  };

  RowNode.prototype.setFirstChild = function (firstChild) {
    if (this.firstChild === firstChild) {
      return;
    }

    this.firstChild = firstChild;

    if (this.eventService) {
      this.eventService.dispatchEvent(this.createLocalRowEvent(RowNode.EVENT_FIRST_CHILD_CHANGED));
    }
  };

  RowNode.prototype.setLastChild = function (lastChild) {
    if (this.lastChild === lastChild) {
      return;
    }

    this.lastChild = lastChild;

    if (this.eventService) {
      this.eventService.dispatchEvent(this.createLocalRowEvent(RowNode.EVENT_LAST_CHILD_CHANGED));
    }
  };

  RowNode.prototype.setChildIndex = function (childIndex) {
    if (this.childIndex === childIndex) {
      return;
    }

    this.childIndex = childIndex;

    if (this.eventService) {
      this.eventService.dispatchEvent(this.createLocalRowEvent(RowNode.EVENT_CHILD_INDEX_CHANGED));
    }
  };

  RowNode.prototype.setRowTop = function (rowTop) {
    if (this.rowTop === rowTop) {
      return;
    }

    this.rowTop = rowTop;

    if (this.eventService) {
      this.eventService.dispatchEvent(this.createLocalRowEvent(RowNode.EVENT_TOP_CHANGED));
    }
  };

  RowNode.prototype.setDragging = function (dragging) {
    if (this.dragging === dragging) {
      return;
    }

    this.dragging = dragging;

    if (this.eventService) {
      this.eventService.dispatchEvent(this.createLocalRowEvent(RowNode.EVENT_DRAGGING_CHANGED));
    }
  };

  RowNode.prototype.setAllChildrenCount = function (allChildrenCount) {
    if (this.allChildrenCount === allChildrenCount) {
      return;
    }

    this.allChildrenCount = allChildrenCount;

    if (this.eventService) {
      this.eventService.dispatchEvent(this.createLocalRowEvent(RowNode.EVENT_ALL_CHILDREN_COUNT_CHANGED));
    }
  };

  RowNode.prototype.setRowHeight = function (rowHeight, estimated) {
    if (estimated === void 0) {
      estimated = false;
    }

    this.rowHeight = rowHeight;
    this.rowHeightEstimated = estimated;

    if (this.eventService) {
      this.eventService.dispatchEvent(this.createLocalRowEvent(RowNode.EVENT_HEIGHT_CHANGED));
    }
  };

  RowNode.prototype.setRowIndex = function (rowIndex) {
    this.rowIndex = rowIndex;

    if (this.eventService) {
      this.eventService.dispatchEvent(this.createLocalRowEvent(RowNode.EVENT_ROW_INDEX_CHANGED));
    }
  };

  RowNode.prototype.setUiLevel = function (uiLevel) {
    if (this.uiLevel === uiLevel) {
      return;
    }

    this.uiLevel = uiLevel;

    if (this.eventService) {
      this.eventService.dispatchEvent(this.createLocalRowEvent(RowNode.EVENT_UI_LEVEL_CHANGED));
    }
  };

  RowNode.prototype.setExpanded = function (expanded) {
    if (this.expanded === expanded) {
      return;
    }

    this.expanded = expanded;

    if (this.eventService) {
      this.eventService.dispatchEvent(this.createLocalRowEvent(RowNode.EVENT_EXPANDED_CHANGED));
    }

    var event = this.createGlobalRowEvent(Events.EVENT_ROW_GROUP_OPENED);
    this.mainEventService.dispatchEvent(event);

    if (this.gridOptionsWrapper.isGroupIncludeFooter()) {
      this.gridApi.redrawRows({
        rowNodes: [this]
      });
    }
  };

  RowNode.prototype.createGlobalRowEvent = function (type) {
    var event = {
      type: type,
      node: this,
      data: this.data,
      rowIndex: this.rowIndex,
      rowPinned: this.rowPinned,
      context: this.gridOptionsWrapper.getContext(),
      api: this.gridOptionsWrapper.getApi(),
      columnApi: this.gridOptionsWrapper.getColumnApi()
    };
    return event;
  };

  RowNode.prototype.dispatchLocalEvent = function (event) {
    if (this.eventService) {
      this.eventService.dispatchEvent(event);
    }
  }; // we also allow editing the value via the editors. when it is done via
  // the editors, no 'cell changed' event gets fired, as it's assumed that
  // the cell knows about the change given it's in charge of the editing.
  // this method is for the client to call, so the cell listens for the change
  // event, and also flashes the cell when the change occurs.


  RowNode.prototype.setDataValue = function (colKey, newValue) {
    var column = this.columnController.getPrimaryColumn(colKey);
    this.valueService.setValue(this, column, newValue);
    this.dispatchCellChangedEvent(column, newValue);
  };

  RowNode.prototype.setGroupValue = function (colKey, newValue) {
    var column = this.columnController.getGridColumn(colKey);

    if (_.missing(this.groupData)) {
      this.groupData = {};
    }

    this.groupData[column.getColId()] = newValue;
    this.dispatchCellChangedEvent(column, newValue);
  }; // sets the data for an aggregation


  RowNode.prototype.setAggData = function (newAggData) {
    var _this = this; // find out all keys that could potentially change


    var colIds = _.getAllKeysInObjects([this.aggData, newAggData]);

    this.aggData = newAggData; // if no event service, nobody has registered for events, so no need fire event

    if (this.eventService) {
      colIds.forEach(function (colId) {
        var column = _this.columnController.getGridColumn(colId);

        var value = _this.aggData ? _this.aggData[colId] : undefined;

        _this.dispatchCellChangedEvent(column, value);
      });
    }
  };

  RowNode.prototype.hasChildren = function () {
    // we need to return true when this.group=true, as this is used by server side row model
    // (as children are lazy loaded and stored in a cache anyway). otherwise we return true
    // if children exist.
    return this.group || this.childrenAfterGroup && this.childrenAfterGroup.length > 0;
  };

  RowNode.prototype.isEmptyRowGroupNode = function () {
    return this.group && _.missingOrEmpty(this.childrenAfterGroup);
  };

  RowNode.prototype.dispatchCellChangedEvent = function (column, newValue) {
    var cellChangedEvent = {
      type: RowNode.EVENT_CELL_CHANGED,
      node: this,
      column: column,
      newValue: newValue
    };
    this.dispatchLocalEvent(cellChangedEvent);
  };

  RowNode.prototype.resetQuickFilterAggregateText = function () {
    this.quickFilterAggregateText = null;
  };

  RowNode.prototype.isExpandable = function () {
    return this.hasChildren() || this.master;
  };

  RowNode.prototype.isSelected = function () {
    // for footers, we just return what our sibling selected state is, as cannot select a footer
    if (this.footer) {
      return this.sibling.isSelected();
    }

    return this.selected;
  };

  RowNode.prototype.depthFirstSearch = function (callback) {
    if (this.childrenAfterGroup) {
      this.childrenAfterGroup.forEach(function (child) {
        return child.depthFirstSearch(callback);
      });
    }

    callback(this);
  }; // + rowController.updateGroupsInSelection()
  // + selectionController.calculatedSelectedForAllGroupNodes()


  RowNode.prototype.calculateSelectedFromChildren = function () {
    var atLeastOneSelected = false;
    var atLeastOneDeSelected = false;
    var atLeastOneMixed = false;
    var newSelectedValue;

    if (this.childrenAfterGroup) {
      for (var i = 0; i < this.childrenAfterGroup.length; i++) {
        var child = this.childrenAfterGroup[i]; // skip non-selectable nodes to prevent inconsistent selection values

        if (!child.selectable) {
          continue;
        }

        var childState = child.isSelected();

        switch (childState) {
          case true:
            atLeastOneSelected = true;
            break;

          case false:
            atLeastOneDeSelected = true;
            break;

          default:
            atLeastOneMixed = true;
            break;
        }
      }
    }

    if (atLeastOneMixed) {
      newSelectedValue = undefined;
    } else if (atLeastOneSelected && !atLeastOneDeSelected) {
      newSelectedValue = true;
    } else if (!atLeastOneSelected && atLeastOneDeSelected) {
      newSelectedValue = false;
    } else {
      newSelectedValue = undefined;
    }

    this.selectThisNode(newSelectedValue);
  };

  RowNode.prototype.setSelectedInitialValue = function (selected) {
    this.selected = selected;
  };

  RowNode.prototype.setSelected = function (newValue, clearSelection, suppressFinishActions) {
    if (clearSelection === void 0) {
      clearSelection = false;
    }

    if (suppressFinishActions === void 0) {
      suppressFinishActions = false;
    }

    this.setSelectedParams({
      newValue: newValue,
      clearSelection: clearSelection,
      suppressFinishActions: suppressFinishActions,
      rangeSelect: false
    });
  };

  RowNode.prototype.isRowPinned = function () {
    return this.rowPinned === Constants.PINNED_TOP || this.rowPinned === Constants.PINNED_BOTTOM;
  }; // to make calling code more readable, this is the same method as setSelected except it takes names parameters


  RowNode.prototype.setSelectedParams = function (params) {
    var groupSelectsChildren = this.gridOptionsWrapper.isGroupSelectsChildren();
    var newValue = params.newValue === true;
    var clearSelection = params.clearSelection === true;
    var suppressFinishActions = params.suppressFinishActions === true;
    var rangeSelect = params.rangeSelect === true; // groupSelectsFiltered only makes sense when group selects children

    var groupSelectsFiltered = groupSelectsChildren && params.groupSelectsFiltered === true;

    if (this.id === undefined) {
      console.warn('ag-Grid: cannot select node until id for node is known');
      return 0;
    }

    if (this.rowPinned) {
      console.warn('ag-Grid: cannot select pinned rows');
      return 0;
    } // if we are a footer, we don't do selection, just pass the info
    // to the sibling (the parent of the group)


    if (this.footer) {
      var count = this.sibling.setSelectedParams(params);
      return count;
    }

    if (rangeSelect) {
      var newRowClicked = this.selectionController.getLastSelectedNode() !== this;
      var allowMultiSelect = this.gridOptionsWrapper.isRowSelectionMulti();

      if (newRowClicked && allowMultiSelect) {
        return this.doRowRangeSelection();
      }
    }

    var updatedCount = 0; // when groupSelectsFiltered, then this node may end up intermediate despite
    // trying to set it to true / false. this group will be calculated further on
    // down when we call calculatedSelectedForAllGroupNodes(). we need to skip it
    // here, otherwise the updatedCount would include it.

    var skipThisNode = groupSelectsFiltered && this.group;

    if (!skipThisNode) {
      var thisNodeWasSelected = this.selectThisNode(newValue);

      if (thisNodeWasSelected) {
        updatedCount++;
      }
    }

    if (groupSelectsChildren && this.group) {
      updatedCount += this.selectChildNodes(newValue, groupSelectsFiltered);
    } // clear other nodes if not doing multi select


    if (!suppressFinishActions) {
      var clearOtherNodes = newValue && (clearSelection || !this.gridOptionsWrapper.isRowSelectionMulti());

      if (clearOtherNodes) {
        updatedCount += this.selectionController.clearOtherNodes(this);
      } // only if we selected something, then update groups and fire events


      if (updatedCount > 0) {
        this.selectionController.updateGroupsFromChildrenSelections(); // this is the very end of the 'action node', so we are finished all the updates,
        // include any parent / child changes that this method caused

        var event_1 = {
          type: Events.EVENT_SELECTION_CHANGED,
          api: this.gridApi,
          columnApi: this.columnApi
        };
        this.mainEventService.dispatchEvent(event_1);
      } // so if user next does shift-select, we know where to start the selection from


      if (newValue) {
        this.selectionController.setLastSelectedNode(this);
      }
    }

    return updatedCount;
  }; // selects all rows between this node and the last selected node (or the top if this is the first selection).
  // not to be mixed up with 'cell range selection' where you drag the mouse, this is row range selection, by
  // holding down 'shift'.


  RowNode.prototype.doRowRangeSelection = function () {
    var updatedCount = 0;
    var groupsSelectChildren = this.gridOptionsWrapper.isGroupSelectsChildren();
    var lastSelectedNode = this.selectionController.getLastSelectedNode();
    var nodesToSelect = this.rowModel.getNodesInRangeForSelection(this, lastSelectedNode);
    nodesToSelect.forEach(function (rowNode) {
      if (rowNode.group && groupsSelectChildren) {
        return;
      }

      var nodeWasSelected = rowNode.selectThisNode(true);

      if (nodeWasSelected) {
        updatedCount++;
      }
    });
    this.selectionController.updateGroupsFromChildrenSelections();
    var event = {
      type: Events.EVENT_SELECTION_CHANGED,
      api: this.gridApi,
      columnApi: this.columnApi
    };
    this.mainEventService.dispatchEvent(event);
    return updatedCount;
  };

  RowNode.prototype.isParentOfNode = function (potentialParent) {
    var parentNode = this.parent;

    while (parentNode) {
      if (parentNode === potentialParent) {
        return true;
      }

      parentNode = parentNode.parent;
    }

    return false;
  };

  RowNode.prototype.selectThisNode = function (newValue) {
    if (!this.selectable || this.selected === newValue) {
      return false;
    }

    this.selected = newValue;

    if (this.eventService) {
      this.dispatchLocalEvent(this.createLocalRowEvent(RowNode.EVENT_ROW_SELECTED));
    }

    var event = this.createGlobalRowEvent(Events.EVENT_ROW_SELECTED);
    this.mainEventService.dispatchEvent(event);
    return true;
  };

  RowNode.prototype.selectChildNodes = function (newValue, groupSelectsFiltered) {
    var children = groupSelectsFiltered ? this.childrenAfterFilter : this.childrenAfterGroup;
    var updatedCount = 0;

    if (_.missing(children)) {
      return;
    }

    for (var i = 0; i < children.length; i++) {
      updatedCount += children[i].setSelectedParams({
        newValue: newValue,
        clearSelection: false,
        suppressFinishActions: true,
        groupSelectsFiltered: groupSelectsFiltered
      });
    }

    return updatedCount;
  };

  RowNode.prototype.addEventListener = function (eventType, listener) {
    if (!this.eventService) {
      this.eventService = new EventService();
    }

    this.eventService.addEventListener(eventType, listener);
  };

  RowNode.prototype.removeEventListener = function (eventType, listener) {
    this.eventService.removeEventListener(eventType, listener);
  };

  RowNode.prototype.onMouseEnter = function () {
    this.dispatchLocalEvent(this.createLocalRowEvent(RowNode.EVENT_MOUSE_ENTER));
  };

  RowNode.prototype.onMouseLeave = function () {
    this.dispatchLocalEvent(this.createLocalRowEvent(RowNode.EVENT_MOUSE_LEAVE));
  };

  RowNode.prototype.getFirstChildOfFirstChild = function (rowGroupColumn) {
    var currentRowNode = this; // if we are hiding groups, then if we are the first child, of the first child,
    // all the way up to the column we are interested in, then we show the group cell.

    var isCandidate = true;
    var foundFirstChildPath = false;
    var nodeToSwapIn;

    while (isCandidate && !foundFirstChildPath) {
      var parentRowNode = currentRowNode.parent;
      var firstChild = _.exists(parentRowNode) && currentRowNode.firstChild;

      if (firstChild) {
        if (parentRowNode.rowGroupColumn === rowGroupColumn) {
          foundFirstChildPath = true;
          nodeToSwapIn = parentRowNode;
        }
      } else {
        isCandidate = false;
      }

      currentRowNode = parentRowNode;
    }

    return foundFirstChildPath ? nodeToSwapIn : null;
  };

  RowNode.prototype.isFullWidthCell = function () {
    var isFullWidthCellFunc = this.gridOptionsWrapper.getIsFullWidthCellFunc();
    return isFullWidthCellFunc ? isFullWidthCellFunc(this) : false;
  };

  RowNode.OBJECT_ID_SEQUENCE = 0;
  RowNode.EVENT_ROW_SELECTED = 'rowSelected';
  RowNode.EVENT_DATA_CHANGED = 'dataChanged';
  RowNode.EVENT_CELL_CHANGED = 'cellChanged';
  RowNode.EVENT_ALL_CHILDREN_COUNT_CHANGED = 'allChildrenCountChanged';
  RowNode.EVENT_MOUSE_ENTER = 'mouseEnter';
  RowNode.EVENT_MOUSE_LEAVE = 'mouseLeave';
  RowNode.EVENT_HEIGHT_CHANGED = 'heightChanged';
  RowNode.EVENT_TOP_CHANGED = 'topChanged';
  RowNode.EVENT_FIRST_CHILD_CHANGED = 'firstChildChanged';
  RowNode.EVENT_LAST_CHILD_CHANGED = 'lastChildChanged';
  RowNode.EVENT_CHILD_INDEX_CHANGED = 'childIndexChanged';
  RowNode.EVENT_ROW_INDEX_CHANGED = 'rowIndexChanged';
  RowNode.EVENT_EXPANDED_CHANGED = 'expandedChanged';
  RowNode.EVENT_SELECTABLE_CHANGED = 'selectableChanged';
  RowNode.EVENT_UI_LEVEL_CHANGED = 'uiLevelChanged';
  RowNode.EVENT_DRAGGING_CHANGED = 'draggingChanged';

  __decorate$f([Autowired('eventService')], RowNode.prototype, "mainEventService", void 0);

  __decorate$f([Autowired('gridOptionsWrapper')], RowNode.prototype, "gridOptionsWrapper", void 0);

  __decorate$f([Autowired('selectionController')], RowNode.prototype, "selectionController", void 0);

  __decorate$f([Autowired('columnController')], RowNode.prototype, "columnController", void 0);

  __decorate$f([Autowired('valueService')], RowNode.prototype, "valueService", void 0);

  __decorate$f([Autowired('rowModel')], RowNode.prototype, "rowModel", void 0);

  __decorate$f([Autowired('context')], RowNode.prototype, "context", void 0);

  __decorate$f([Autowired('valueCache')], RowNode.prototype, "valueCache", void 0);

  __decorate$f([Autowired('columnApi')], RowNode.prototype, "columnApi", void 0);

  __decorate$f([Autowired('gridApi')], RowNode.prototype, "gridApi", void 0);

  return RowNode;
}();
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __extends$9 = undefined && undefined.__extends || function () {
  var _extendStatics10 = function extendStatics(d, b) {
    _extendStatics10 = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics10(d, b);
  };

  return function (d, b) {
    _extendStatics10(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var __decorate$g = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var CheckboxSelectionComponent =
/** @class */
function (_super) {
  __extends$9(CheckboxSelectionComponent, _super);

  function CheckboxSelectionComponent() {
    return _super.call(this, "<span class=\"ag-selection-checkbox\"/>") || this;
  }

  CheckboxSelectionComponent.prototype.createAndAddIcons = function () {
    var element = this.getGui();

    if (this.gridOptionsWrapper.useNativeCheckboxes()) {
      this.checkbox = document.createElement('input');
      this.checkbox.type = 'checkbox';
      element.appendChild(this.checkbox);
    } else {
      this.eCheckedIcon = _.createIconNoSpan('checkboxChecked', this.gridOptionsWrapper, this.column);
      this.eUncheckedIcon = _.createIconNoSpan('checkboxUnchecked', this.gridOptionsWrapper, this.column);
      this.eIndeterminateIcon = _.createIconNoSpan('checkboxIndeterminate', this.gridOptionsWrapper, this.column);
      element.appendChild(this.eCheckedIcon);
      element.appendChild(this.eUncheckedIcon);
      element.appendChild(this.eIndeterminateIcon);
    }
  };

  CheckboxSelectionComponent.prototype.onDataChanged = function () {
    // when rows are loaded for the second time, this can impact the selection, as a row
    // could be loaded as already selected (if user scrolls down, and then up again).
    this.onSelectionChanged();
  };

  CheckboxSelectionComponent.prototype.onSelectableChanged = function () {
    this.showOrHideSelect();
  };

  CheckboxSelectionComponent.prototype.onSelectionChanged = function () {
    var state = this.rowNode.isSelected();

    if (this.gridOptionsWrapper.useNativeCheckboxes()) {
      this.checkbox.checked = state === true;
      this.checkbox.indeterminate = typeof state !== 'boolean';
    } else {
      _.setDisplayed(this.eCheckedIcon, state === true);

      _.setDisplayed(this.eUncheckedIcon, state === false);

      _.setDisplayed(this.eIndeterminateIcon, typeof state !== 'boolean');
    }
  };

  CheckboxSelectionComponent.prototype.onCheckedClicked = function () {
    var groupSelectsFiltered = this.gridOptionsWrapper.isGroupSelectsFiltered();
    var updatedCount = this.rowNode.setSelectedParams({
      newValue: false,
      groupSelectsFiltered: groupSelectsFiltered
    });
    return updatedCount;
  };

  CheckboxSelectionComponent.prototype.onUncheckedClicked = function (event) {
    var groupSelectsFiltered = this.gridOptionsWrapper.isGroupSelectsFiltered();
    var updatedCount = this.rowNode.setSelectedParams({
      newValue: true,
      rangeSelect: event.shiftKey,
      groupSelectsFiltered: groupSelectsFiltered
    });
    return updatedCount;
  };

  CheckboxSelectionComponent.prototype.onIndeterminateClicked = function (event) {
    var result = this.onUncheckedClicked(event);

    if (result === 0) {
      this.onCheckedClicked();
    }
  };

  CheckboxSelectionComponent.prototype.onCheckboxClicked = function (event) {
    if (this.checkbox.checked) {
      this.onUncheckedClicked(event);
    } else {
      this.onCheckedClicked();
    }
  };

  CheckboxSelectionComponent.prototype.init = function (params) {
    this.rowNode = params.rowNode;
    this.column = params.column;
    this.createAndAddIcons();
    this.onSelectionChanged(); // we don't want the row clicked event to fire when selecting the checkbox, otherwise the row
    // would possibly get selected twice

    this.addGuiEventListener('click', function (event) {
      return _.stopPropagationForAgGrid(event);
    }); // likewise we don't want double click on this icon to open a group

    this.addGuiEventListener('dblclick', function (event) {
      return _.stopPropagationForAgGrid(event);
    });

    if (this.gridOptionsWrapper.useNativeCheckboxes()) {
      this.addDestroyableEventListener(this.checkbox, 'click', this.onCheckboxClicked.bind(this));
    } else {
      this.addDestroyableEventListener(this.eCheckedIcon, 'click', this.onCheckedClicked.bind(this));
      this.addDestroyableEventListener(this.eUncheckedIcon, 'click', this.onUncheckedClicked.bind(this));
      this.addDestroyableEventListener(this.eIndeterminateIcon, 'click', this.onIndeterminateClicked.bind(this));
    }

    this.addDestroyableEventListener(this.rowNode, RowNode.EVENT_ROW_SELECTED, this.onSelectionChanged.bind(this));
    this.addDestroyableEventListener(this.rowNode, RowNode.EVENT_DATA_CHANGED, this.onDataChanged.bind(this));
    this.addDestroyableEventListener(this.rowNode, RowNode.EVENT_SELECTABLE_CHANGED, this.onSelectableChanged.bind(this));
    this.isRowSelectableFunc = this.gridOptionsWrapper.getIsRowSelectableFunc();
    var checkboxVisibleIsDynamic = this.isRowSelectableFunc || this.checkboxCallbackExists();

    if (checkboxVisibleIsDynamic) {
      this.addDestroyableEventListener(this.eventService, Events.EVENT_DISPLAYED_COLUMNS_CHANGED, this.showOrHideSelect.bind(this));
      this.showOrHideSelect();
    }
  };

  CheckboxSelectionComponent.prototype.showOrHideSelect = function () {
    // if the isRowSelectable() is not provided the row node is selectable by default
    var selectable = this.rowNode.selectable; // checkboxSelection callback is deemed a legacy solution however we will still consider it's result.
    // If selectable, then also check the colDef callback. if not selectable, this it short circuits - no need
    // to call the colDef callback.

    if (selectable && this.checkboxCallbackExists()) {
      selectable = this.column.isCellCheckboxSelection(this.rowNode);
    } // show checkbox if both conditions are true


    this.setDisplayed(selectable);
  };

  CheckboxSelectionComponent.prototype.checkboxCallbackExists = function () {
    // column will be missing if groupUseEntireRow=true
    var colDef = this.column ? this.column.getColDef() : null;
    return colDef && typeof colDef.checkboxSelection === 'function';
  };

  __decorate$g([Autowired('gridOptionsWrapper')], CheckboxSelectionComponent.prototype, "gridOptionsWrapper", void 0);

  __decorate$g([Autowired('eventService')], CheckboxSelectionComponent.prototype, "eventService", void 0);

  __decorate$g([Autowired('gridApi')], CheckboxSelectionComponent.prototype, "gridApi", void 0);

  __decorate$g([Autowired('columnApi')], CheckboxSelectionComponent.prototype, "columnApi", void 0);

  return CheckboxSelectionComponent;
}(Component);
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __decorate$h = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var ComponentSource;

(function (ComponentSource) {
  ComponentSource[ComponentSource["DEFAULT"] = 0] = "DEFAULT";
  ComponentSource[ComponentSource["REGISTERED_BY_NAME"] = 1] = "REGISTERED_BY_NAME";
  ComponentSource[ComponentSource["HARDCODED"] = 2] = "HARDCODED";
})(ComponentSource || (ComponentSource = {}));

var UserComponentFactory =
/** @class */
function () {
  function UserComponentFactory() {}

  UserComponentFactory.prototype.newDateComponent = function (params) {
    return this.createAndInitUserComponent(this.gridOptions, params, "dateComponent", "agDateInput");
  };

  UserComponentFactory.prototype.newHeaderComponent = function (params) {
    return this.createAndInitUserComponent(params.column.getColDef(), params, "headerComponent", "agColumnHeader");
  };

  UserComponentFactory.prototype.newHeaderGroupComponent = function (params) {
    return this.createAndInitUserComponent(params.columnGroup.getColGroupDef(), params, "headerGroupComponent", "agColumnGroupHeader");
  };

  UserComponentFactory.prototype.newFullWidthGroupRowInnerCellRenderer = function (params) {
    return this.createAndInitUserComponent(this.gridOptions, params, "groupRowInnerRenderer", null, true);
  }; // this one is unusual, as it can be LoadingCellRenderer, DetailCellRenderer, FullWidthCellRenderer or GroupRowRenderer.
  // so we have to pass the type in.


  UserComponentFactory.prototype.newFullWidthCellRenderer = function (params, cellRendererType, cellRendererName) {
    return this.createAndInitUserComponent(null, params, cellRendererType, cellRendererName);
  };

  UserComponentFactory.prototype.newCellRenderer = function (target, params) {
    return this.createAndInitUserComponent(target, params, "cellRenderer", null, true);
  };

  UserComponentFactory.prototype.newPinnedRowCellRenderer = function (target, params) {
    return this.createAndInitUserComponent(target, params, "pinnedRowCellRenderer", null, true);
  };

  UserComponentFactory.prototype.newCellEditor = function (colDef, params) {
    return this.createAndInitUserComponent(colDef, params, 'cellEditor', 'agCellEditor');
  };

  UserComponentFactory.prototype.newInnerCellRenderer = function (target, params) {
    return this.createAndInitUserComponent(target, params, "innerRenderer", null);
  };

  UserComponentFactory.prototype.newLoadingOverlayComponent = function (params) {
    return this.createAndInitUserComponent(this.gridOptions, params, "loadingOverlayComponent", "agLoadingOverlay");
  };

  UserComponentFactory.prototype.newNoRowsOverlayComponent = function (params) {
    return this.createAndInitUserComponent(this.gridOptions, params, "noRowsOverlayComponent", "agNoRowsOverlay");
  };

  UserComponentFactory.prototype.newTooltipComponent = function (params) {
    var colDef = params.colDef;
    return this.createAndInitUserComponent(colDef, params, "tooltipComponent", 'agTooltipComponent');
  };

  UserComponentFactory.prototype.newFilterComponent = function (colDef, params, defaultFilter, modifyParamsCallback) {
    return this.createAndInitUserComponent(colDef, params, 'filter', defaultFilter, false, modifyParamsCallback);
  };

  UserComponentFactory.prototype.newFloatingFilterComponent = function (colDef, params, defaultFloatingFilter) {
    return this.createAndInitUserComponent(colDef, params, "floatingFilterComponent", defaultFloatingFilter, true);
  };

  UserComponentFactory.prototype.newToolPanelComponent = function (toolPanelDef, params) {
    return this.createAndInitUserComponent(toolPanelDef, params, 'toolPanel');
  };

  UserComponentFactory.prototype.newStatusPanelComponent = function (def, params) {
    return this.createAndInitUserComponent(def, params, 'statusPanel');
  };
  /**
   * This method creates a component given everything needed to guess what sort of component needs to be instantiated
   * It takes
   *  @param definitionObject: This is the context for which this component needs to be created, it can be gridOptions
   *      (global) or columnDef mostly.
   *  @param paramsFromGrid: Params to be passed to the component and passed by ag-Grid. This will get merged with any params
   *      specified by the user in the configuration
   *  @param propertyName: The name of the property used in ag-grid as a convention to refer to the component, it can be:
   *      'floatingFilter', 'cellRenderer', is used to find if the user is specifying a custom component
   *  @param defaultComponentName: The actual name of the component to instantiate, this is usually the same as propertyName, but in
   *      some cases is not, like floatingFilter, if it is the same is not necessary to specify
   *  @param optional: Handy method to tell if this should return a component ALWAYS. if that is the case, but there is no
   *      component found, it throws an error, by default all components are MANDATORY
   *  @param modifyParamsCallback: A chance to customise the params passed to the init method. It receives what the current
   *  params are and the component that init is about to get called for
   */


  UserComponentFactory.prototype.createAndInitUserComponent = function (definitionObject, paramsFromGrid, propertyName, defaultComponentName, // optional items are: FloatingFilter, CellComp (for cellRenderer)
  optional, // used by FilterManager only
  modifyParamsCallback) {
    if (optional === void 0) {
      optional = false;
    }

    if (!definitionObject) {
      definitionObject = this.gridOptions;
    } // Create the component instance


    var componentAndParams = this.createComponentInstance(definitionObject, propertyName, paramsFromGrid, defaultComponentName, optional);

    if (!componentAndParams) {
      return null;
    }

    var componentInstance = componentAndParams.componentInstance; // Wire the component and call the init method with the correct params

    var params = this.createFinalParams(definitionObject, propertyName, paramsFromGrid, componentAndParams.paramsFromSelector);
    this.addReactHacks(params); // give caller chance to set any params that depend on the componentInstance (need here as the
    // componentInstance was not available when createUserComponent was called)

    var paramsAfterCallback = modifyParamsCallback ? modifyParamsCallback(params, componentInstance) : params;
    var deferredInit = this.initComponent(componentInstance, paramsAfterCallback);

    if (deferredInit == null) {
      // const p = new Promise<A>(resolve => {
      //     setTimeout( ()=> {
      //         resolve(componentInstance);
      //     }, 1000);
      // });
      // return p;
      return Promise.resolve(componentInstance);
    } else {
      var asPromise = deferredInit;
      return asPromise.map(function (notRelevant) {
        return componentInstance;
      });
    }
  };

  UserComponentFactory.prototype.addReactHacks = function (params) {
    // a temporary fix for AG-1574
    // AG-1715 raised to do a wider ranging refactor to improve this
    var agGridReact = this.context.getBean('agGridReact');

    if (agGridReact) {
      params.agGridReact = _.cloneObject(agGridReact);
    } // AG-1716 - directly related to AG-1574 and AG-1715


    var frameworkComponentWrapper = this.context.getBean('frameworkComponentWrapper');

    if (frameworkComponentWrapper) {
      params.frameworkComponentWrapper = frameworkComponentWrapper;
    }
  };
  /**
   * This method creates a component given everything needed to guess what sort of component needs to be instantiated
   * It takes
   *  @param clazz: The class to instantiate,
   *  @param agGridParams: Params to be passed to the component and passed by ag-Grid. This will get merged with any params
   *      specified by the user in the configuration
   *  @param modifyParamsCallback: A chance to customise the params passed to the init method. It receives what the current
   *  params are and the component that init is about to get called for
   */


  UserComponentFactory.prototype.createUserComponentFromConcreteClass = function (clazz, agGridParams) {
    var internalComponent = new clazz();
    this.initComponent(internalComponent, agGridParams);
    return internalComponent;
  };
  /**
   * This method returns the underlying representation of the component to be created. ie for Javascript the
   * underlying function where we should be calling new into. In case of the frameworks, the framework class
   * object that represents the component to be created.
   *
   * This method is handy for different reasons, for example if you want to check if a component has a particular
   * method implemented without having to create the component, just by inspecting the source component
   *
   * It takes
   *  @param definitionObject: This is the context for which this component needs to be created, it can be gridOptions
   *      (global) or columnDef mostly.
   *  @param propertyName: The name of the property used in ag-grid as a convention to refer to the component, it can be:
   *      'floatingFilter', 'cellRenderer', is used to find if the user is specifying a custom component
   *  @param params: Params to be passed to the dynamic component function in case it needs to be
   *      invoked
   *  @param defaultComponentName: The name of the component to load if there is no component specified
   */


  UserComponentFactory.prototype.lookupComponentClassDef = function (definitionObject, propertyName, params, defaultComponentName) {
    if (params === void 0) {
      params = null;
    }
    /**
     * There are five things that can happen when resolving a component.
     *  a) HardcodedFwComponent: That holder[propertyName]Framework has associated a Framework native component
     *  b) HardcodedJsComponent: That holder[propertyName] has associate a JS component
     *  c) hardcodedJsFunction: That holder[propertyName] has associate a JS function
     *  d) hardcodedNameComponent: That holder[propertyName] has associate a string that represents a component to load
     *  e) That none of the three previous are specified, then we need to use the DefaultRegisteredComponent
     */


    var hardcodedNameComponent = null;
    var HardcodedJsComponent = null;
    var hardcodedJsFunction = null;
    var HardcodedFwComponent = null;
    var componentSelectorFunc;

    if (definitionObject != null) {
      var componentPropertyValue = definitionObject[propertyName]; // for filters only, we allow 'true' for the component, which means default filter to be used

      var usingDefaultComponent = componentPropertyValue === true;

      if (componentPropertyValue != null && !usingDefaultComponent) {
        if (typeof componentPropertyValue === 'string') {
          hardcodedNameComponent = componentPropertyValue;
        } else if (typeof componentPropertyValue === 'boolean') ;else if (this.agComponentUtils.doesImplementIComponent(componentPropertyValue)) {
          HardcodedJsComponent = componentPropertyValue;
        } else {
          hardcodedJsFunction = componentPropertyValue;
        }
      }

      HardcodedFwComponent = definitionObject[propertyName + "Framework"];
      componentSelectorFunc = definitionObject[propertyName + "Selector"];
    }
    /**
     * Since we allow many types of flavors for specifying the components, let's make sure this is not an illegal
     * combination
     */


    if (HardcodedJsComponent && HardcodedFwComponent || hardcodedNameComponent && HardcodedFwComponent || hardcodedJsFunction && HardcodedFwComponent) {
      throw Error("ag-grid: you are trying to specify: " + propertyName + " twice as a component.");
    }

    if (HardcodedFwComponent && !this.frameworkComponentWrapper) {
      throw Error("ag-grid: you are specifying a framework component but you are not using a framework version of ag-grid for : " + propertyName);
    }

    if (componentSelectorFunc && (hardcodedNameComponent || HardcodedJsComponent || hardcodedJsFunction || HardcodedFwComponent)) {
      throw Error("ag-grid: you can't specify both, the selector and the component of ag-grid for : " + propertyName);
    }
    /**
     * At this stage we are guaranteed to either have,
     * DEPRECATED
     * - A unique HardcodedFwComponent
     * - A unique HardcodedJsComponent
     * - A unique hardcodedJsFunction
     * BY NAME- FAVOURED APPROACH
     * - A unique hardcodedNameComponent
     * - None of the previous, hence we revert to: RegisteredComponent
     */


    if (HardcodedFwComponent) {
      // console.warn(`ag-grid: Since version 12.1.0 specifying a component directly is deprecated, you should register the component by name`);
      // console.warn(`${HardcodedFwComponent}`);
      return {
        componentFromFramework: true,
        component: HardcodedFwComponent,
        source: ComponentSource.HARDCODED,
        paramsFromSelector: null
      };
    }

    if (HardcodedJsComponent) {
      // console.warn(`ag-grid: Since version 12.1.0 specifying a component directly is deprecated, you should register the component by name`);
      // console.warn(`${HardcodedJsComponent}`);
      return {
        componentFromFramework: false,
        component: HardcodedJsComponent,
        source: ComponentSource.HARDCODED,
        paramsFromSelector: null
      };
    }

    if (hardcodedJsFunction) {
      // console.warn(`ag-grid: Since version 12.1.0 specifying a function directly is deprecated, you should register the component by name`);
      // console.warn(`${hardcodedJsFunction}`);
      return this.agComponentUtils.adaptFunction(propertyName, hardcodedJsFunction, false, ComponentSource.HARDCODED);
    }

    var selectorResult = componentSelectorFunc ? componentSelectorFunc(params) : null;
    var componentNameToUse;

    if (selectorResult && selectorResult.component) {
      componentNameToUse = selectorResult.component;
    } else if (hardcodedNameComponent) {
      componentNameToUse = hardcodedNameComponent;
    } else {
      componentNameToUse = defaultComponentName;
    }

    if (!componentNameToUse) {
      return null;
    }

    var registeredCompClassDef = this.lookupFromRegisteredComponents(propertyName, componentNameToUse);

    if (!registeredCompClassDef) {
      return null;
    }

    return {
      componentFromFramework: registeredCompClassDef.componentFromFramework,
      component: registeredCompClassDef.component,
      source: registeredCompClassDef.source,
      paramsFromSelector: selectorResult ? selectorResult.params : null
    };
  };

  UserComponentFactory.prototype.lookupFromRegisteredComponents = function (propertyName, componentNameOpt) {
    var componentName = componentNameOpt != null ? componentNameOpt : propertyName;
    var registeredComponent = this.userComponentRegistry.retrieve(componentName);

    if (registeredComponent == null) {
      return null;
    } //If it is a FW it has to be registered as a component


    if (registeredComponent.componentFromFramework) {
      return {
        component: registeredComponent.component,
        componentFromFramework: true,
        source: ComponentSource.REGISTERED_BY_NAME,
        paramsFromSelector: null
      };
    } //If it is JS it may be a function or a component


    if (this.agComponentUtils.doesImplementIComponent(registeredComponent.component)) {
      return {
        component: registeredComponent.component,
        componentFromFramework: false,
        source: registeredComponent.source == RegisteredComponentSource.REGISTERED ? ComponentSource.REGISTERED_BY_NAME : ComponentSource.DEFAULT,
        paramsFromSelector: null
      };
    } // This is a function


    return this.agComponentUtils.adaptFunction(propertyName, registeredComponent.component, registeredComponent.componentFromFramework, registeredComponent.source == RegisteredComponentSource.REGISTERED ? ComponentSource.REGISTERED_BY_NAME : ComponentSource.DEFAULT);
  };
  /**
   * Useful to check what would be the resultant params for a given object
   *  @param definitionObject: This is the context for which this component needs to be created, it can be gridOptions
   *      (global) or columnDef mostly.
   *  @param propertyName: The name of the property used in ag-grid as a convention to refer to the component, it can be:
   *      'floatingFilter', 'cellRenderer', is used to find if the user is specifying a custom component
   *  @param paramsFromGrid: Params to be passed to the component and passed by ag-Grid. This will get merged with any params
   *      specified by the user in the configuration
   * @returns {any} It merges the user agGridParams with the actual params specified by the user.
   */


  UserComponentFactory.prototype.createFinalParams = function (definitionObject, propertyName, paramsFromGrid, paramsFromSelector) {
    if (paramsFromSelector === void 0) {
      paramsFromSelector = null;
    }

    var res = {};

    _.mergeDeep(res, paramsFromGrid);

    var userParams = definitionObject ? definitionObject[propertyName + "Params"] : null;

    if (userParams != null) {
      if (typeof userParams === 'function') {
        _.mergeDeep(res, userParams(paramsFromGrid));
      } else if (typeof userParams === 'object') {
        _.mergeDeep(res, userParams);
      }
    }

    _.mergeDeep(res, paramsFromSelector);

    return res;
  };

  UserComponentFactory.prototype.createComponentInstance = function (holder, propertyName, paramsForSelector, defaultComponentName, optional) {
    var componentToUse = this.lookupComponentClassDef(holder, propertyName, paramsForSelector, defaultComponentName);
    var missing = !componentToUse || !componentToUse.component;

    if (missing) {
      // to help the user, we print out the name they are looking for, rather than the default name.
      // i don't know why the default name was originally printed out (that doesn't help the user)
      var overrideName = holder ? holder[propertyName] : defaultComponentName;
      var nameToReport = overrideName ? overrideName : defaultComponentName;

      if (!optional) {
        console.error("Could not find component " + nameToReport + ", did you forget to configure this component?");
      }

      return null;
    }

    var componentInstance;

    if (componentToUse.componentFromFramework) {
      // Using framework component
      var FrameworkComponentRaw = componentToUse.component;
      var thisComponentConfig = this.componentMetadataProvider.retrieve(propertyName);
      componentInstance = this.frameworkComponentWrapper.wrap(FrameworkComponentRaw, thisComponentConfig.mandatoryMethodList, thisComponentConfig.optionalMethodList, defaultComponentName);
    } else {
      // Using plain JavaScript component
      componentInstance = new componentToUse.component();
    }

    return {
      componentInstance: componentInstance,
      paramsFromSelector: componentToUse.paramsFromSelector
    };
  };

  UserComponentFactory.prototype.initComponent = function (component, finalParams) {
    this.context.wireBean(component);

    if (component.init == null) {
      return;
    } else {
      return component.init(finalParams);
    }
  };

  __decorate$h([Autowired("gridOptions")], UserComponentFactory.prototype, "gridOptions", void 0);

  __decorate$h([Autowired("gridOptionsWrapper")], UserComponentFactory.prototype, "gridOptionsWrapper", void 0);

  __decorate$h([Autowired("context")], UserComponentFactory.prototype, "context", void 0);

  __decorate$h([Autowired("agComponentUtils")], UserComponentFactory.prototype, "agComponentUtils", void 0);

  __decorate$h([Autowired("componentMetadataProvider")], UserComponentFactory.prototype, "componentMetadataProvider", void 0);

  __decorate$h([Autowired("userComponentRegistry")], UserComponentFactory.prototype, "userComponentRegistry", void 0);

  __decorate$h([Optional("frameworkComponentWrapper")], UserComponentFactory.prototype, "frameworkComponentWrapper", void 0);

  UserComponentFactory = __decorate$h([Bean('userComponentFactory')], UserComponentFactory);
  return UserComponentFactory;
}();
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __extends$a = undefined && undefined.__extends || function () {
  var _extendStatics11 = function extendStatics(d, b) {
    _extendStatics11 = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics11(d, b);
  };

  return function (d, b) {
    _extendStatics11(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var __decorate$i = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var GroupCellRenderer =
/** @class */
function (_super) {
  __extends$a(GroupCellRenderer, _super);

  function GroupCellRenderer() {
    return _super.call(this, GroupCellRenderer.TEMPLATE) || this;
  }

  GroupCellRenderer.prototype.init = function (params) {
    this.params = params;

    if (this.gridOptionsWrapper.isGroupIncludeTotalFooter()) {
      this.assignBlankValueToGroupFooterCell(params);
    }

    var embeddedRowMismatch = this.isEmbeddedRowMismatch(); // This allows for empty strings to appear as groups since
    // it will only return for null or undefined.

    var cellIsEmpty = params.value == null;
    this.cellIsBlank = embeddedRowMismatch || cellIsEmpty;

    if (this.cellIsBlank) {
      return;
    }

    this.setupDragOpenParents();
    this.addExpandAndContract();
    this.addCheckboxIfNeeded();
    this.addValueElement();
    this.setupIndent();
  };

  GroupCellRenderer.prototype.assignBlankValueToGroupFooterCell = function (params) {
    // this is not ideal, but it was the only way we could get footer working for the root node
    if (!params.value && params.node.level == -1) {
      params.value = '';
    }
  }; // if we are doing embedded full width rows, we only show the renderer when
  // in the body, or if pinning in the pinned section, or if pinning and RTL,
  // in the right section. otherwise we would have the cell repeated in each section.


  GroupCellRenderer.prototype.isEmbeddedRowMismatch = function () {
    if (this.params.fullWidth && this.gridOptionsWrapper.isEmbedFullWidthRows()) {
      var pinnedLeftCell = this.params.pinned === Constants.PINNED_LEFT;
      var pinnedRightCell = this.params.pinned === Constants.PINNED_RIGHT;
      var bodyCell = !pinnedLeftCell && !pinnedRightCell;

      if (this.gridOptionsWrapper.isEnableRtl()) {
        if (this.columnController.isPinningLeft()) {
          return !pinnedRightCell;
        } else {
          return !bodyCell;
        }
      } else {
        if (this.columnController.isPinningLeft()) {
          return !pinnedLeftCell;
        } else {
          return !bodyCell;
        }
      }
    } else {
      return false;
    }
  };

  GroupCellRenderer.prototype.setIndent = function () {
    if (this.gridOptionsWrapper.isGroupHideOpenParents()) {
      return;
    }

    var params = this.params;
    var rowNode = params.node;
    var paddingCount = rowNode.uiLevel;
    var userProvidedPaddingPixelsTheDeprecatedWay = params.padding >= 0;

    if (userProvidedPaddingPixelsTheDeprecatedWay) {
      this.setPaddingDeprecatedWay(paddingCount, params.padding);
      return;
    }

    if (this.indentClass) {
      this.removeCssClass(this.indentClass);
    }

    this.indentClass = 'ag-row-group-indent-' + paddingCount;
    this.addCssClass(this.indentClass);
  };

  GroupCellRenderer.prototype.setPaddingDeprecatedWay = function (paddingCount, padding) {
    _.doOnce(function () {
      return console.warn('ag-Grid: since v14.2, configuring padding for groupCellRenderer should be done with Sass variables and themes. Please see the ag-Grid documentation page for Themes, in particular the property $row-group-indent-size.');
    }, 'groupCellRenderer->doDeprecatedWay');

    var paddingPx = paddingCount * padding;

    if (this.gridOptionsWrapper.isEnableRtl()) {
      // if doing rtl, padding is on the right
      this.getGui().style.paddingRight = paddingPx + 'px';
    } else {
      // otherwise it is on the left
      this.getGui().style.paddingLeft = paddingPx + 'px';
    }
  };

  GroupCellRenderer.prototype.setupIndent = function () {
    // only do this if an indent - as this overwrites the padding that
    // the theme set, which will make things look 'not aligned' for the
    // first group level.
    var node = this.params.node;
    var suppressPadding = this.params.suppressPadding;

    if (!suppressPadding) {
      this.addDestroyableEventListener(node, RowNode.EVENT_UI_LEVEL_CHANGED, this.setIndent.bind(this));
      this.setIndent();
    }
  };

  GroupCellRenderer.prototype.addValueElement = function () {
    var params = this.params;
    var rowNode = this.displayedGroup;

    if (rowNode.footer) {
      this.createFooterCell();
    } else if (rowNode.hasChildren() || _.get(params.colDef, 'cellRendererParams.innerRenderer', null) || _.get(params.colDef, 'cellRendererParams.innerRendererFramework', null)) {
      this.createGroupCell();

      if (rowNode.hasChildren()) {
        this.addChildCount();
      }
    } else {
      this.createLeafCell();
    }
  };

  GroupCellRenderer.prototype.createFooterCell = function () {
    var footerValue;
    var footerValueGetter = this.params.footerValueGetter;

    if (footerValueGetter) {
      // params is same as we were given, except we set the value as the item to display
      var paramsClone = _.cloneObject(this.params);

      paramsClone.value = this.params.value;

      if (typeof footerValueGetter === 'function') {
        footerValue = footerValueGetter(paramsClone);
      } else if (typeof footerValueGetter === 'string') {
        footerValue = this.expressionService.evaluate(footerValueGetter, paramsClone);
      } else {
        console.warn('ag-Grid: footerValueGetter should be either a function or a string (expression)');
      }
    } else {
      footerValue = 'Total ' + this.params.value;
    }

    this.eValue.innerHTML = footerValue;
  };

  GroupCellRenderer.prototype.createGroupCell = function () {
    var _this = this;

    var params = this.params;
    var rowGroupColumn = this.displayedGroup.rowGroupColumn; // we try and use the cellRenderer of the column used for the grouping if we can

    var columnToUse = rowGroupColumn ? rowGroupColumn : params.column;
    var groupName = this.params.value;
    var valueFormatted = columnToUse ? this.valueFormatterService.formatValue(columnToUse, params.node, params.scope, groupName) : null;
    params.valueFormatted = valueFormatted;
    var rendererPromise;

    if (params.fullWidth == true) {
      rendererPromise = this.useFullWidth(params);
    } else {
      rendererPromise = this.useInnerRenderer(this.params.colDef.cellRendererParams, columnToUse.getColDef(), params);
    } // retain a reference to the created renderer - we'll use this later for cleanup (in destroy)


    if (rendererPromise) {
      rendererPromise.then(function (value) {
        _this.innerCellRenderer = value;
      });
    }
  };

  GroupCellRenderer.prototype.useInnerRenderer = function (groupCellRendererParams, groupedColumnDef, // the column this group row is for, eg 'Country'
  params) {
    // when grouping, the normal case is we use the cell renderer of the grouped column. eg if grouping by country
    // and then rating, we will use the country cell renderer for each country group row and likewise the rating
    // cell renderer for each rating group row.
    //
    // however if the user has innerCellRenderer defined, this gets preference and we don't use cell renderers
    // of the grouped columns.
    //
    // so we check and use in the following order:
    //
    // 1) thisColDef.cellRendererParams.innerRenderer of the column showing the groups (eg auto group column)
    // 2) groupedColDef.cellRenderer of the grouped column
    // 3) groupedColDef.cellRendererParams.innerRenderer
    var _this = this;

    var cellRendererPromise = null; // we check if cell renderer provided for the group cell renderer, eg colDef.cellRendererParams.innerRenderer

    var groupInnerRendererClass = this.userComponentFactory.lookupComponentClassDef(groupCellRendererParams, "innerRenderer");

    if (groupInnerRendererClass && groupInnerRendererClass.component != null && groupInnerRendererClass.source != ComponentSource.DEFAULT) {
      // use the renderer defined in cellRendererParams.innerRenderer
      cellRendererPromise = this.userComponentFactory.newInnerCellRenderer(groupCellRendererParams, params);
    } else {
      // otherwise see if we can use the cellRenderer of the column we are grouping by
      var groupColumnRendererClass = this.userComponentFactory.lookupComponentClassDef(groupedColumnDef, "cellRenderer");

      if (groupColumnRendererClass && groupColumnRendererClass.source != ComponentSource.DEFAULT) {
        // Only if the original column is using a specific renderer, it it is a using a DEFAULT one ignore it
        cellRendererPromise = this.userComponentFactory.newCellRenderer(groupedColumnDef, params);
      } else if (groupColumnRendererClass && groupColumnRendererClass.source == ComponentSource.DEFAULT && _.get(groupedColumnDef, 'cellRendererParams.innerRenderer', null)) {
        // EDGE CASE - THIS COMES FROM A COLUMN WHICH HAS BEEN GROUPED DYNAMICALLY, THAT HAS AS RENDERER 'group'
        // AND HAS A INNER CELL RENDERER
        cellRendererPromise = this.userComponentFactory.newInnerCellRenderer(groupedColumnDef.cellRendererParams, params);
      } else {
        // This forces the retrieval of the default plain cellRenderer that just renders the values.
        cellRendererPromise = this.userComponentFactory.newCellRenderer({}, params);
      }
    }

    if (cellRendererPromise != null) {
      cellRendererPromise.then(function (rendererToUse) {
        if (rendererToUse == null) {
          _this.eValue.innerText = params.valueFormatted != null ? params.valueFormatted : params.value;
          return;
        }

        _.bindCellRendererToHtmlElement(cellRendererPromise, _this.eValue);
      });
    } else {
      this.eValue.innerText = params.valueFormatted != null ? params.valueFormatted : params.value;
    }

    return cellRendererPromise;
  };

  GroupCellRenderer.prototype.useFullWidth = function (params) {
    var cellRendererPromise = this.userComponentFactory.newFullWidthGroupRowInnerCellRenderer(params);

    if (cellRendererPromise != null) {
      _.bindCellRendererToHtmlElement(cellRendererPromise, this.eValue);
    } else {
      this.eValue.innerText = params.valueFormatted != null ? params.valueFormatted : params.value;
    }

    return cellRendererPromise;
  };

  GroupCellRenderer.prototype.addChildCount = function () {
    // only include the child count if it's included, eg if user doing custom aggregation,
    // then this could be left out, or set to -1, ie no child count
    if (this.params.suppressCount) {
      return;
    }

    this.addDestroyableEventListener(this.displayedGroup, RowNode.EVENT_ALL_CHILDREN_COUNT_CHANGED, this.updateChildCount.bind(this)); // filtering changes the child count, so need to cater for it

    this.updateChildCount();
  };

  GroupCellRenderer.prototype.updateChildCount = function () {
    var allChildrenCount = this.displayedGroup.allChildrenCount;
    this.eChildCount.innerHTML = allChildrenCount >= 0 ? "(" + allChildrenCount + ")" : "";
  };

  GroupCellRenderer.prototype.createLeafCell = function () {
    if (_.exists(this.params.value)) {
      this.eValue.innerText = this.params.valueFormatted ? this.params.valueFormatted : this.params.value;
    }
  };

  GroupCellRenderer.prototype.isUserWantsSelected = function () {
    var paramsCheckbox = this.params.checkbox;

    if (typeof paramsCheckbox === 'function') {
      return paramsCheckbox(this.params);
    } else {
      return paramsCheckbox === true;
    }
  };

  GroupCellRenderer.prototype.addCheckboxIfNeeded = function () {
    var rowNode = this.displayedGroup;
    var checkboxNeeded = this.isUserWantsSelected() // footers cannot be selected
    && !rowNode.footer // pinned rows cannot be selected
    && !rowNode.rowPinned // details cannot be selected
    && !rowNode.detail;

    if (checkboxNeeded) {
      var cbSelectionComponent_1 = new CheckboxSelectionComponent();
      this.getContext().wireBean(cbSelectionComponent_1);
      cbSelectionComponent_1.init({
        rowNode: rowNode,
        column: this.params.column
      });
      this.eCheckbox.appendChild(cbSelectionComponent_1.getGui());
      this.addDestroyFunc(function () {
        return cbSelectionComponent_1.destroy();
      });
    }

    _.addOrRemoveCssClass(this.eCheckbox, 'ag-invisible', !checkboxNeeded);
  };

  GroupCellRenderer.prototype.addExpandAndContract = function () {
    var params = this.params;
    var eGroupCell = params.eGridCell;

    var eExpandedIcon = _.createIconNoSpan('groupExpanded', this.gridOptionsWrapper, null);

    var eContractedIcon = _.createIconNoSpan('groupContracted', this.gridOptionsWrapper, null);

    this.eExpanded.appendChild(eExpandedIcon);
    this.eContracted.appendChild(eContractedIcon);
    this.addDestroyableEventListener(this.eExpanded, 'click', this.onExpandClicked.bind(this));
    this.addDestroyableEventListener(this.eContracted, 'click', this.onExpandClicked.bind(this)); // expand / contract as the user hits enter

    this.addDestroyableEventListener(eGroupCell, 'keydown', this.onKeyDown.bind(this));
    this.addDestroyableEventListener(params.node, RowNode.EVENT_EXPANDED_CHANGED, this.showExpandAndContractIcons.bind(this));
    this.showExpandAndContractIcons(); // because we don't show the expand / contract when there are no children, we need to check every time
    // the number of children change.

    this.addDestroyableEventListener(this.displayedGroup, RowNode.EVENT_ALL_CHILDREN_COUNT_CHANGED, this.onAllChildrenCountChanged.bind(this)); // if editing groups, then double click is to start editing

    if (!this.gridOptionsWrapper.isEnableGroupEdit() && this.isExpandable() && !params.suppressDoubleClickExpand) {
      this.addDestroyableEventListener(eGroupCell, 'dblclick', this.onCellDblClicked.bind(this));
    }
  };

  GroupCellRenderer.prototype.onAllChildrenCountChanged = function () {
    // maybe if no children now, we should hide the expand / contract icons
    this.showExpandAndContractIcons(); // if we have no children, this impacts the indent

    this.setIndent();
  };

  GroupCellRenderer.prototype.onKeyDown = function (event) {
    var enterKeyPressed = _.isKeyPressed(event, Constants.KEY_ENTER);

    if (enterKeyPressed) {
      if (this.params.suppressEnterExpand) {
        return;
      }

      var cellEditable = this.params.column && this.params.column.isCellEditable(this.params.node);

      if (cellEditable) {
        return;
      }

      event.preventDefault();
      this.onExpandOrContract();
    }
  };

  GroupCellRenderer.prototype.setupDragOpenParents = function () {
    var column = this.params.column;
    var rowNode = this.params.node;

    if (!this.gridOptionsWrapper.isGroupHideOpenParents()) {
      this.draggedFromHideOpenParents = false;
    } else if (!rowNode.hasChildren()) {
      // if we are here, and we are not a group, then we must of been dragged down,
      // as otherwise the cell would be blank, and if cell is blank, this method is never called.
      this.draggedFromHideOpenParents = true;
    } else {
      var rowGroupColumn = rowNode.rowGroupColumn;

      if (rowGroupColumn) {
        // if the displayGroup column for this col matches the rowGroupColumn we grouped by for this node,
        // then nothing was dragged down
        this.draggedFromHideOpenParents = !column.isRowGroupDisplayed(rowGroupColumn.getId());
      } else {
        // the only way we can end up here (no column, but a group) is if we are at the root node,
        // which only happens when 'groupIncludeTotalFooter' is true. here, we are never dragging
        this.draggedFromHideOpenParents = false;
      }
    }

    if (this.draggedFromHideOpenParents) {
      var pointer = rowNode.parent;

      while (true) {
        if (_.missing(pointer)) {
          break;
        }

        if (pointer.rowGroupColumn && column.isRowGroupDisplayed(pointer.rowGroupColumn.getId())) {
          this.displayedGroup = pointer;
          break;
        }

        pointer = pointer.parent;
      }
    } // if we didn't find a displayed group, set it to the row node


    if (_.missing(this.displayedGroup)) {
      this.displayedGroup = rowNode;
    }
  };

  GroupCellRenderer.prototype.onExpandClicked = function (mouseEvent) {
    if (_.isStopPropagationForAgGrid(mouseEvent)) {
      return;
    } // so if we expand a node, it does not also get selected.


    _.stopPropagationForAgGrid(mouseEvent);

    this.onExpandOrContract();
  };

  GroupCellRenderer.prototype.onCellDblClicked = function (mouseEvent) {
    if (_.isStopPropagationForAgGrid(mouseEvent)) {
      return;
    } // we want to avoid acting on double click events on the expand / contract icon,
    // as that icons already has expand / collapse functionality on it. otherwise if
    // the icon was double clicked, we would get 'click', 'click', 'dblclick' which
    // is open->close->open, however double click should be open->close only.


    var targetIsExpandIcon = _.isElementInEventPath(this.eExpanded, mouseEvent) || _.isElementInEventPath(this.eContracted, mouseEvent);

    if (!targetIsExpandIcon) {
      this.onExpandOrContract();
    }
  };

  GroupCellRenderer.prototype.onExpandOrContract = function () {
    // must use the displayedGroup, so if data was dragged down, we expand the parent, not this row
    var rowNode = this.displayedGroup;
    rowNode.setExpanded(!rowNode.expanded);
  };

  GroupCellRenderer.prototype.isExpandable = function () {
    var rowNode = this.params.node;
    var reducedLeafNode = this.columnController.isPivotMode() && rowNode.leafGroup;
    return this.draggedFromHideOpenParents || rowNode.isExpandable() && !rowNode.footer && !reducedLeafNode;
  };

  GroupCellRenderer.prototype.showExpandAndContractIcons = function () {
    var rowNode = this.params.node;

    if (this.isExpandable()) {
      // if expandable, show one based on expand state.
      // if we were dragged down, means our parent is always expanded
      var expanded = this.draggedFromHideOpenParents ? true : rowNode.expanded;

      _.setDisplayed(this.eContracted, !expanded);

      _.setDisplayed(this.eExpanded, expanded);
    } else {
      // it not expandable, show neither
      _.setDisplayed(this.eExpanded, false);

      _.setDisplayed(this.eContracted, false);
    }

    var displayedGroup = this.displayedGroup; // compensation padding for leaf nodes, so there is blank space instead of the expand icon

    var pivotModeAndLeafGroup = this.columnController.isPivotMode() && displayedGroup.leafGroup;
    var notExpandable = !displayedGroup.isExpandable();
    var addLeafIndentClass = displayedGroup.footer || notExpandable || pivotModeAndLeafGroup;
    this.addOrRemoveCssClass('ag-row-group', !addLeafIndentClass);
    this.addOrRemoveCssClass('ag-row-group-leaf-indent', addLeafIndentClass);
  };

  GroupCellRenderer.prototype.destroy = function () {
    _super.prototype.destroy.call(this);

    if (this.innerCellRenderer && this.innerCellRenderer.destroy) {
      this.innerCellRenderer.destroy();
    }
  };

  GroupCellRenderer.prototype.refresh = function () {
    return false;
  };

  GroupCellRenderer.TEMPLATE = '<span class="ag-cell-wrapper">' + '<span class="ag-group-expanded" ref="eExpanded"></span>' + '<span class="ag-group-contracted" ref="eContracted"></span>' + '<span class="ag-group-checkbox ag-invisible" ref="eCheckbox"></span>' + '<span class="ag-group-value" ref="eValue"></span>' + '<span class="ag-group-child-count" ref="eChildCount"></span>' + '</span>';

  __decorate$i([Autowired('gridOptionsWrapper')], GroupCellRenderer.prototype, "gridOptionsWrapper", void 0);

  __decorate$i([Autowired('expressionService')], GroupCellRenderer.prototype, "expressionService", void 0);

  __decorate$i([Autowired('eventService')], GroupCellRenderer.prototype, "eventService", void 0);

  __decorate$i([Autowired('valueFormatterService')], GroupCellRenderer.prototype, "valueFormatterService", void 0);

  __decorate$i([Autowired('columnController')], GroupCellRenderer.prototype, "columnController", void 0);

  __decorate$i([Autowired('mouseEventService')], GroupCellRenderer.prototype, "mouseEventService", void 0);

  __decorate$i([Autowired('userComponentFactory')], GroupCellRenderer.prototype, "userComponentFactory", void 0);

  __decorate$i([RefSelector('eExpanded')], GroupCellRenderer.prototype, "eExpanded", void 0);

  __decorate$i([RefSelector('eContracted')], GroupCellRenderer.prototype, "eContracted", void 0);

  __decorate$i([RefSelector('eCheckbox')], GroupCellRenderer.prototype, "eCheckbox", void 0);

  __decorate$i([RefSelector('eValue')], GroupCellRenderer.prototype, "eValue", void 0);

  __decorate$i([RefSelector('eChildCount')], GroupCellRenderer.prototype, "eChildCount", void 0);

  return GroupCellRenderer;
}(Component);
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __extends$b = undefined && undefined.__extends || function () {
  var _extendStatics12 = function extendStatics(d, b) {
    _extendStatics12 = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics12(d, b);
  };

  return function (d, b) {
    _extendStatics12(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var __decorate$j = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var ARROW_UP = "\u2191";
var ARROW_DOWN = "\u2193";

var AnimateShowChangeCellRenderer =
/** @class */
function (_super) {
  __extends$b(AnimateShowChangeCellRenderer, _super);

  function AnimateShowChangeCellRenderer() {
    var _this = _super.call(this, AnimateShowChangeCellRenderer.TEMPLATE) || this;

    _this.refreshCount = 0;
    return _this;
  }

  AnimateShowChangeCellRenderer.prototype.init = function (params) {
    // this.params = params;
    this.eValue = this.queryForHtmlElement('.ag-value-change-value');
    this.eDelta = this.queryForHtmlElement('.ag-value-change-delta');
    this.refresh(params);
  };

  AnimateShowChangeCellRenderer.prototype.showDelta = function (params, delta) {
    var absDelta = Math.abs(delta);
    var valueFormatted = params.formatValue(absDelta);
    var valueToUse = _.exists(valueFormatted) ? valueFormatted : absDelta;
    var deltaUp = delta >= 0;

    if (deltaUp) {
      this.eDelta.innerHTML = ARROW_UP + valueToUse;
    } else {
      // because negative, use ABS to remove sign
      this.eDelta.innerHTML = ARROW_DOWN + valueToUse;
    } // class makes it green (in ag-fresh)


    _.addOrRemoveCssClass(this.eDelta, 'ag-value-change-delta-up', deltaUp); // class makes it red (in ag-fresh)


    _.addOrRemoveCssClass(this.eDelta, 'ag-value-change-delta-down', !deltaUp);
  };

  AnimateShowChangeCellRenderer.prototype.setTimerToRemoveDelta = function () {
    var _this = this; // the refreshCount makes sure that if the value updates again while
    // the below timer is waiting, then the below timer will realise it
    // is not the most recent and will not try to remove the delta value.


    this.refreshCount++;
    var refreshCountCopy = this.refreshCount;
    window.setTimeout(function () {
      if (refreshCountCopy === _this.refreshCount) {
        _this.hideDeltaValue();
      }
    }, 2000);
  };

  AnimateShowChangeCellRenderer.prototype.hideDeltaValue = function () {
    _.removeCssClass(this.eValue, 'ag-value-change-value-highlight');

    _.clearElement(this.eDelta);
  };

  AnimateShowChangeCellRenderer.prototype.refresh = function (params) {
    var value = params.value;

    if (value === this.lastValue) {
      return;
    }

    if (_.exists(params.valueFormatted)) {
      this.eValue.innerHTML = params.valueFormatted;
    } else if (_.exists(params.value)) {
      this.eValue.innerHTML = value;
    } else {
      _.clearElement(this.eValue);
    } // we don't show the delta if we are in the middle of a filter. see comment on FilterManager
    // with regards processingFilterChange


    if (this.filterManager.isSuppressFlashingCellsBecauseFiltering()) {
      return;
    }

    if (typeof value === 'number' && typeof this.lastValue === 'number') {
      var delta = value - this.lastValue;
      this.showDelta(params, delta);
    } // highlight the current value, but only if it's not new, otherwise it
    // would get highlighted first time the value is shown


    if (this.lastValue) {
      _.addCssClass(this.eValue, 'ag-value-change-value-highlight');
    }

    this.setTimerToRemoveDelta();
    this.lastValue = value;
    return true;
  };

  AnimateShowChangeCellRenderer.TEMPLATE = '<span>' + '<span class="ag-value-change-delta"></span>' + '<span class="ag-value-change-value"></span>' + '</span>';

  __decorate$j([Autowired('filterManager')], AnimateShowChangeCellRenderer.prototype, "filterManager", void 0);

  return AnimateShowChangeCellRenderer;
}(Component);
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __extends$c = undefined && undefined.__extends || function () {
  var _extendStatics13 = function extendStatics(d, b) {
    _extendStatics13 = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics13(d, b);
  };

  return function (d, b) {
    _extendStatics13(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var __decorate$k = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var AnimateSlideCellRenderer =
/** @class */
function (_super) {
  __extends$c(AnimateSlideCellRenderer, _super);

  function AnimateSlideCellRenderer() {
    var _this = _super.call(this, AnimateSlideCellRenderer.TEMPLATE) || this;

    _this.refreshCount = 0;
    _this.eCurrent = _this.queryForHtmlElement('.ag-value-slide-current');
    return _this;
  }

  AnimateSlideCellRenderer.prototype.init = function (params) {
    this.params = params;
    this.refresh(params);
  };

  AnimateSlideCellRenderer.prototype.addSlideAnimation = function () {
    var _this = this;

    this.refreshCount++; // below we keep checking this, and stop working on the animation
    // if it no longer matches - this means another animation has started
    // and this one is stale.

    var refreshCountCopy = this.refreshCount; // if old animation, remove it

    if (this.ePrevious) {
      this.getGui().removeChild(this.ePrevious);
    }

    this.ePrevious = _.loadTemplate('<span class="ag-value-slide-previous ag-value-slide-out"></span>');
    this.ePrevious.innerHTML = this.eCurrent.innerHTML;
    this.getGui().insertBefore(this.ePrevious, this.eCurrent); // having timeout of 0 allows use to skip to the next css turn,
    // so we know the previous css classes have been applied. so the
    // complex set of setTimeout below creates the animation

    window.setTimeout(function () {
      if (refreshCountCopy !== _this.refreshCount) {
        return;
      }

      _.addCssClass(_this.ePrevious, 'ag-value-slide-out-end');
    }, 50);
    window.setTimeout(function () {
      if (refreshCountCopy !== _this.refreshCount) {
        return;
      }

      _this.getGui().removeChild(_this.ePrevious);

      _this.ePrevious = null;
    }, 3000);
  };

  AnimateSlideCellRenderer.prototype.refresh = function (params) {
    var value = params.value;

    if (_.missing(value)) {
      value = '';
    }

    if (value === this.lastValue) {
      return;
    } // we don't show the delta if we are in the middle of a filter. see comment on FilterManager
    // with regards processingFilterChange


    if (this.filterManager.isSuppressFlashingCellsBecauseFiltering()) {
      return;
    }

    this.addSlideAnimation();
    this.lastValue = value;

    if (_.exists(params.valueFormatted)) {
      this.eCurrent.innerHTML = params.valueFormatted;
    } else if (_.exists(params.value)) {
      this.eCurrent.innerHTML = value;
    } else {
      _.clearElement(this.eCurrent);
    }

    return true;
  };

  AnimateSlideCellRenderer.TEMPLATE = '<span>' + '<span class="ag-value-slide-current"></span>' + '</span>';

  __decorate$k([Autowired('filterManager')], AnimateSlideCellRenderer.prototype, "filterManager", void 0);

  return AnimateSlideCellRenderer;
}(Component);
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __extends$d = undefined && undefined.__extends || function () {
  var _extendStatics14 = function extendStatics(d, b) {
    _extendStatics14 = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics14(d, b);
  };

  return function (d, b) {
    _extendStatics14(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var __decorate$l = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var LoadingCellRenderer =
/** @class */
function (_super) {
  __extends$d(LoadingCellRenderer, _super);

  function LoadingCellRenderer() {
    return _super.call(this, LoadingCellRenderer.TEMPLATE) || this;
  }

  LoadingCellRenderer.prototype.init = function (params) {
    var eLoadingIcon = _.createIconNoSpan('groupLoading', this.gridOptionsWrapper, null);

    this.eLoadingIcon.appendChild(eLoadingIcon);
    var localeTextFunc = this.gridOptionsWrapper.getLocaleTextFunc();
    this.eLoadingText.innerText = localeTextFunc('loadingOoo', 'Loading');
  };

  LoadingCellRenderer.prototype.refresh = function (params) {
    return false;
  };

  LoadingCellRenderer.TEMPLATE = "<div class=\"ag-stub-cell\">\n            <span class=\"ag-loading-icon\" ref=\"eLoadingIcon\"></span>\n            <span class=\"ag-loading-text\" ref=\"eLoadingText\"></span>\n        </div>";

  __decorate$l([Autowired('gridOptionsWrapper')], LoadingCellRenderer.prototype, "gridOptionsWrapper", void 0);

  __decorate$l([RefSelector('eLoadingIcon')], LoadingCellRenderer.prototype, "eLoadingIcon", void 0);

  __decorate$l([RefSelector('eLoadingText')], LoadingCellRenderer.prototype, "eLoadingText", void 0);

  return LoadingCellRenderer;
}(Component);
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __extends$e = undefined && undefined.__extends || function () {
  var _extendStatics15 = function extendStatics(d, b) {
    _extendStatics15 = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics15(d, b);
  };

  return function (d, b) {
    _extendStatics15(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var __decorate$m = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var SelectCellEditor =
/** @class */
function (_super) {
  __extends$e(SelectCellEditor, _super);

  function SelectCellEditor() {
    var _this = _super.call(this, '<div class="ag-cell-edit-input"><select class="ag-cell-edit-input"/></div>') || this;

    _this.eSelect = _this.getGui().querySelector('select');
    return _this;
  }

  SelectCellEditor.prototype.init = function (params) {
    var _this = this;

    this.focusAfterAttached = params.cellStartedEdit;

    if (_.missing(params.values)) {
      console.warn('ag-Grid: no values found for select cellEditor');
      return;
    }

    params.values.forEach(function (value) {
      var option = document.createElement('option');
      option.value = value;

      var valueFormatted = _this.valueFormatterService.formatValue(params.column, null, null, value);

      var valueFormattedExits = valueFormatted !== null && valueFormatted !== undefined;
      option.text = valueFormattedExits ? valueFormatted : value;

      if (params.value === value) {
        option.selected = true;
      }

      _this.eSelect.appendChild(option);
    }); // we don't want to add this if full row editing, otherwise selecting will stop the
    // full row editing.

    if (!this.gridOptionsWrapper.isFullRowEdit()) {
      this.addDestroyableEventListener(this.eSelect, 'change', function () {
        return params.stopEditing();
      });
    }

    this.addDestroyableEventListener(this.eSelect, 'keydown', function (event) {
      var isNavigationKey = event.keyCode === Constants.KEY_UP || event.keyCode === Constants.KEY_DOWN;

      if (isNavigationKey) {
        event.stopPropagation();
      }
    });
    this.addDestroyableEventListener(this.eSelect, 'mousedown', function (event) {
      event.stopPropagation();
    });
  };

  SelectCellEditor.prototype.afterGuiAttached = function () {
    if (this.focusAfterAttached) {
      this.eSelect.focus();
    }
  };

  SelectCellEditor.prototype.focusIn = function () {
    this.eSelect.focus();
  };

  SelectCellEditor.prototype.getValue = function () {
    return this.eSelect.value;
  };

  SelectCellEditor.prototype.isPopup = function () {
    return false;
  };

  __decorate$m([Autowired('gridOptionsWrapper')], SelectCellEditor.prototype, "gridOptionsWrapper", void 0);

  __decorate$m([Autowired('valueFormatterService')], SelectCellEditor.prototype, "valueFormatterService", void 0);

  return SelectCellEditor;
}(PopupComponent);
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __extends$f = undefined && undefined.__extends || function () {
  var _extendStatics16 = function extendStatics(d, b) {
    _extendStatics16 = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics16(d, b);
  };

  return function (d, b) {
    _extendStatics16(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var PopupTextCellEditor =
/** @class */
function (_super) {
  __extends$f(PopupTextCellEditor, _super);

  function PopupTextCellEditor() {
    return _super !== null && _super.apply(this, arguments) || this;
  }

  PopupTextCellEditor.prototype.isPopup = function () {
    return true;
  };

  return PopupTextCellEditor;
}(TextCellEditor);
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __extends$g = undefined && undefined.__extends || function () {
  var _extendStatics17 = function extendStatics(d, b) {
    _extendStatics17 = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics17(d, b);
  };

  return function (d, b) {
    _extendStatics17(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var PopupSelectCellEditor =
/** @class */
function (_super) {
  __extends$g(PopupSelectCellEditor, _super);

  function PopupSelectCellEditor() {
    return _super !== null && _super.apply(this, arguments) || this;
  }

  PopupSelectCellEditor.prototype.isPopup = function () {
    return true;
  };

  return PopupSelectCellEditor;
}(SelectCellEditor);
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __extends$h = undefined && undefined.__extends || function () {
  var _extendStatics18 = function extendStatics(d, b) {
    _extendStatics18 = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics18(d, b);
  };

  return function (d, b) {
    _extendStatics18(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var LargeTextCellEditor =
/** @class */
function (_super) {
  __extends$h(LargeTextCellEditor, _super);

  function LargeTextCellEditor() {
    return _super.call(this, LargeTextCellEditor.TEMPLATE) || this;
  }

  LargeTextCellEditor.prototype.init = function (params) {
    this.params = params;
    this.focusAfterAttached = params.cellStartedEdit;
    this.textarea = document.createElement("textarea");
    this.textarea.maxLength = params.maxLength ? params.maxLength : "200";
    this.textarea.cols = params.cols ? params.cols : "60";
    this.textarea.rows = params.rows ? params.rows : "10";

    if (_.exists(params.value)) {
      this.textarea.value = params.value.toString();
    }

    this.getGui().querySelector('.ag-large-textarea').appendChild(this.textarea);
    this.addGuiEventListener('keydown', this.onKeyDown.bind(this));
  };

  LargeTextCellEditor.prototype.onKeyDown = function (event) {
    var key = event.which || event.keyCode;

    if (key == Constants.KEY_LEFT || key == Constants.KEY_UP || key == Constants.KEY_RIGHT || key == Constants.KEY_DOWN || event.shiftKey && key == Constants.KEY_ENTER) {
      // shift+enter allows for newlines
      event.stopPropagation();
    }
  };

  LargeTextCellEditor.prototype.afterGuiAttached = function () {
    if (this.focusAfterAttached) {
      this.textarea.focus();
    }
  };

  LargeTextCellEditor.prototype.getValue = function () {
    return this.params.parseValue(this.textarea.value);
  };

  LargeTextCellEditor.TEMPLATE = // tab index is needed so we can focus, which is needed for keyboard events
  '<div class="ag-large-text" tabindex="0">' + '<div class="ag-large-textarea"></div>' + '</div>';
  return LargeTextCellEditor;
}(PopupComponent);
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __extends$i = undefined && undefined.__extends || function () {
  var _extendStatics19 = function extendStatics(d, b) {
    _extendStatics19 = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics19(d, b);
  };

  return function (d, b) {
    _extendStatics19(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var __decorate$n = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var NumberFilter =
/** @class */
function (_super) {
  __extends$i(NumberFilter, _super);

  function NumberFilter() {
    return _super !== null && _super.apply(this, arguments) || this;
  }

  NumberFilter.prototype.mapRangeFromModel = function (filterModel) {
    return {
      from: filterModel.filter,
      to: filterModel.filterTo
    };
  };

  NumberFilter.prototype.getDefaultDebounceMs = function () {
    return 500;
  };

  NumberFilter.prototype.resetUiToDefaults = function () {
    _super.prototype.resetUiToDefaults.call(this);

    this.eValueFrom1.value = null;
    this.eValueFrom2.value = null;
    this.eValueTo1.value = null;
    this.eValueTo2.value = null;
  };

  NumberFilter.prototype.setConditionIntoUi = function (model, position) {
    var positionOne = position === ConditionPosition.One;
    var eValueFrom = positionOne ? this.eValueFrom1 : this.eValueFrom2;
    var eValueTo = positionOne ? this.eValueTo1 : this.eValueTo2;
    eValueFrom.value = model ? '' + model.filter : null;
    eValueTo.value = model ? '' + model.filterTo : null;
  };

  NumberFilter.prototype.setValueFromFloatingFilter = function (value) {
    this.eValueFrom1.value = value;
    this.eValueFrom2.value = null;
    this.eValueTo1.value = null;
    this.eValueTo2.value = null;
  };

  NumberFilter.prototype.comparator = function () {
    return function (left, right) {
      if (left === right) {
        return 0;
      }

      if (left < right) {
        return 1;
      }

      if (left > right) {
        return -1;
      }
    };
  };

  NumberFilter.prototype.setParams = function (params) {
    _super.prototype.setParams.call(this, params);

    this.addValueChangedListeners();
  };

  NumberFilter.prototype.addValueChangedListeners = function () {
    var _this = this;

    var listener = function listener() {
      return _this.onUiChanged();
    };

    this.addDestroyableEventListener(this.eValueFrom1, 'input', listener);
    this.addDestroyableEventListener(this.eValueFrom2, 'input', listener);
    this.addDestroyableEventListener(this.eValueTo1, 'input', listener);
    this.addDestroyableEventListener(this.eValueTo2, 'input', listener);
  };

  NumberFilter.prototype.afterGuiAttached = function () {
    this.eValueFrom1.focus();
  };

  NumberFilter.prototype.getDefaultFilterOptions = function () {
    return NumberFilter.DEFAULT_FILTER_OPTIONS;
  };

  NumberFilter.prototype.createValueTemplate = function (position) {
    var positionOne = position === ConditionPosition.One;
    var pos = positionOne ? '1' : '2';
    var translate = this.translate.bind(this);
    return "<div class=\"ag-filter-body\" ref=\"eCondition" + pos + "Body\" role=\"presentation\">\n            <div class=\"ag-input-wrapper\" role=\"presentation\">\n                <input class=\"ag-filter-filter\" ref=\"eValueFrom" + pos + "\" type=\"text\" placeholder=\"" + translate('filterOoo') + "\"/>\n            </div>\n             <div class=\"ag-input-wrapper ag-filter-number-to\" ref=\"ePanel" + pos + "\" role=\"presentation\">\n                <input class=\"ag-filter-filter\" ref=\"eValueTo" + pos + "\" type=\"text\" placeholder=\"" + translate('filterOoo') + "\"/>\n            </div>\n        </div>";
  };

  NumberFilter.prototype.isConditionUiComplete = function (position) {
    var positionOne = position === ConditionPosition.One;
    var option = positionOne ? this.getCondition1Type() : this.getCondition2Type();
    var eValue = positionOne ? this.eValueFrom1 : this.eValueFrom2;
    var eValueTo = positionOne ? this.eValueTo1 : this.eValueTo2;
    var value = this.stringToFloat(eValue.value);
    var valueTo = this.stringToFloat(eValueTo.value);

    if (option === SimpleFilter.EMPTY) {
      return false;
    }

    if (this.doesFilterHaveHiddenInput(option)) {
      return true;
    }

    if (option === SimpleFilter.IN_RANGE) {
      return value != null && valueTo != null;
    } else {
      return value != null;
    }
  };

  NumberFilter.prototype.areSimpleModelsEqual = function (aSimple, bSimple) {
    return aSimple.filter === bSimple.filter && aSimple.filterTo === bSimple.filterTo && aSimple.type === bSimple.type;
  }; // needed for creating filter model


  NumberFilter.prototype.getFilterType = function () {
    return NumberFilter.FILTER_TYPE;
  };

  NumberFilter.prototype.stringToFloat = function (value) {
    var filterText = _.makeNull(value);

    if (filterText && filterText.trim() === '') {
      filterText = null;
    }

    var newFilter;

    if (filterText !== null && filterText !== undefined) {
      newFilter = parseFloat(filterText);
    } else {
      newFilter = null;
    }

    return newFilter;
  };

  NumberFilter.prototype.createCondition = function (position) {
    var positionOne = position === ConditionPosition.One;
    var type = positionOne ? this.getCondition1Type() : this.getCondition2Type();
    var eValue = positionOne ? this.eValueFrom1 : this.eValueFrom2;
    var value = this.stringToFloat(eValue.value);
    var eValueTo = positionOne ? this.eValueTo1 : this.eValueTo2;
    var valueTo = this.stringToFloat(eValueTo.value);
    var model = {
      filterType: NumberFilter.FILTER_TYPE,
      type: type
    };

    if (!this.doesFilterHaveHiddenInput(type)) {
      model.filter = value;
      model.filterTo = valueTo; // FIX - should only populate this when filter choice has 'to' option
    }

    return model;
  };

  NumberFilter.prototype.updateUiVisibility = function () {
    _super.prototype.updateUiVisibility.call(this);

    var showFrom1 = this.showValueFrom(this.getCondition1Type());

    _.setDisplayed(this.eValueFrom1, showFrom1);

    var showTo1 = this.showValueTo(this.getCondition1Type());

    _.setDisplayed(this.eValueTo1, showTo1);

    var showFrom2 = this.showValueFrom(this.getCondition2Type());

    _.setDisplayed(this.eValueFrom2, showFrom2);

    var showTo2 = this.showValueTo(this.getCondition2Type());

    _.setDisplayed(this.eValueTo2, showTo2);
  };

  NumberFilter.FILTER_TYPE = 'number';
  NumberFilter.DEFAULT_FILTER_OPTIONS = [ScalerFilter.EQUALS, ScalerFilter.NOT_EQUAL, ScalerFilter.LESS_THAN, ScalerFilter.LESS_THAN_OR_EQUAL, ScalerFilter.GREATER_THAN, ScalerFilter.GREATER_THAN_OR_EQUAL, ScalerFilter.IN_RANGE];

  __decorate$n([RefSelector('eValueFrom1')], NumberFilter.prototype, "eValueFrom1", void 0);

  __decorate$n([RefSelector('eValueFrom2')], NumberFilter.prototype, "eValueFrom2", void 0);

  __decorate$n([RefSelector('eValueTo1')], NumberFilter.prototype, "eValueTo1", void 0);

  __decorate$n([RefSelector('eValueTo2')], NumberFilter.prototype, "eValueTo2", void 0);

  return NumberFilter;
}(ScalerFilter);
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __extends$j = undefined && undefined.__extends || function () {
  var _extendStatics20 = function extendStatics(d, b) {
    _extendStatics20 = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics20(d, b);
  };

  return function (d, b) {
    _extendStatics20(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var __decorate$o = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var LoadingOverlayComponent =
/** @class */
function (_super) {
  __extends$j(LoadingOverlayComponent, _super);

  function LoadingOverlayComponent() {
    return _super.call(this) || this;
  }

  LoadingOverlayComponent.prototype.init = function (params) {
    var template = this.gridOptionsWrapper.getOverlayLoadingTemplate() ? this.gridOptionsWrapper.getOverlayLoadingTemplate() : LoadingOverlayComponent.DEFAULT_LOADING_OVERLAY_TEMPLATE;
    var localeTextFunc = this.gridOptionsWrapper.getLocaleTextFunc();
    var localisedTemplate = template.replace('[LOADING...]', localeTextFunc('loadingOoo', 'Loading...'));
    this.setTemplate(localisedTemplate);
  };

  LoadingOverlayComponent.DEFAULT_LOADING_OVERLAY_TEMPLATE = '<span class="ag-overlay-loading-center">[LOADING...]</span>';

  __decorate$o([Autowired('gridOptionsWrapper')], LoadingOverlayComponent.prototype, "gridOptionsWrapper", void 0);

  return LoadingOverlayComponent;
}(Component);
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __extends$k = undefined && undefined.__extends || function () {
  var _extendStatics21 = function extendStatics(d, b) {
    _extendStatics21 = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics21(d, b);
  };

  return function (d, b) {
    _extendStatics21(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var __decorate$p = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var NoRowsOverlayComponent =
/** @class */
function (_super) {
  __extends$k(NoRowsOverlayComponent, _super);

  function NoRowsOverlayComponent() {
    return _super.call(this) || this;
  }

  NoRowsOverlayComponent.prototype.init = function (params) {
    var template = this.gridOptionsWrapper.getOverlayNoRowsTemplate() ? this.gridOptionsWrapper.getOverlayNoRowsTemplate() : NoRowsOverlayComponent.DEFAULT_NO_ROWS_TEMPLATE;
    var localeTextFunc = this.gridOptionsWrapper.getLocaleTextFunc();
    var localisedTemplate = template.replace('[NO_ROWS_TO_SHOW]', localeTextFunc('noRowsToShow', 'No Rows To Show'));
    this.setTemplate(localisedTemplate);
  };

  NoRowsOverlayComponent.DEFAULT_NO_ROWS_TEMPLATE = '<span class="ag-overlay-no-rows-center">[NO_ROWS_TO_SHOW]</span>';

  __decorate$p([Autowired('gridOptionsWrapper')], NoRowsOverlayComponent.prototype, "gridOptionsWrapper", void 0);

  return NoRowsOverlayComponent;
}(Component);
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __extends$l = undefined && undefined.__extends || function () {
  var _extendStatics22 = function extendStatics(d, b) {
    _extendStatics22 = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics22(d, b);
  };

  return function (d, b) {
    _extendStatics22(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var TooltipComponent =
/** @class */
function (_super) {
  __extends$l(TooltipComponent, _super);

  function TooltipComponent() {
    return _super.call(this, "<div class=\"ag-tooltip\"></div>") || this;
  } // will need to type params


  TooltipComponent.prototype.init = function (params) {
    var value = params.value;
    this.getGui().innerHTML = value;
  };

  return TooltipComponent;
}(PopupComponent);
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __extends$m = undefined && undefined.__extends || function () {
  var _extendStatics23 = function extendStatics(d, b) {
    _extendStatics23 = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics23(d, b);
  };

  return function (d, b) {
    _extendStatics23(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var DefaultDateComponent =
/** @class */
function (_super) {
  __extends$m(DefaultDateComponent, _super);

  function DefaultDateComponent() {
    return _super.call(this, "<div class=\"ag-input-wrapper\" role=\"presentation\"><input class=\"ag-filter-filter\" type=\"text\" placeholder=\"yyyy-mm-dd\"></div>") || this;
  }

  DefaultDateComponent.prototype.init = function (params) {
    this.eDateInput = this.getGui().querySelector('input');

    if (_.isBrowserChrome() || params.filterParams && params.filterParams.browserDatePicker) {
      if (_.isBrowserIE()) {
        console.warn('ag-grid: browserDatePicker is specified to true, but it is not supported in IE 11, reverting to plain text date picker');
      } else {
        this.eDateInput.type = 'date';
      }
    }

    this.listener = params.onDateChanged;
    this.addGuiEventListener('input', this.listener);
  };

  DefaultDateComponent.prototype.getDate = function () {
    return _.parseYyyyMmDdToDate(this.eDateInput.value, "-");
  };

  DefaultDateComponent.prototype.setDate = function (date) {
    this.eDateInput.value = _.serializeDateToYyyyMmDd(date, "-");
  };

  return DefaultDateComponent;
}(Component);
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __extends$n = undefined && undefined.__extends || function () {
  var _extendStatics24 = function extendStatics(d, b) {
    _extendStatics24 = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics24(d, b);
  };

  return function (d, b) {
    _extendStatics24(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var SimpleFloatingFilter =
/** @class */
function (_super) {
  __extends$n(SimpleFloatingFilter, _super);

  function SimpleFloatingFilter() {
    return _super !== null && _super.apply(this, arguments) || this;
  }

  SimpleFloatingFilter.prototype.getDefaultDebounceMs = function () {
    return 0;
  }; // used by:
  // 1) NumberFloatingFilter & TextFloatingFilter: Always, for both when editable and read only.
  // 2) DateFloatingFilter: Only when read only (as we show text rather than a date picker when read only)


  SimpleFloatingFilter.prototype.getTextFromModel = function (model) {
    if (!model) {
      return null;
    }

    var isCombined = model.operator;

    if (isCombined) {
      var combinedModel = model;
      var con1Str = this.conditionToString(combinedModel.condition1);
      var con2Str = this.conditionToString(combinedModel.condition2);
      return con1Str + " " + combinedModel.operator + " " + con2Str;
    } else {
      var condition = model;
      return this.conditionToString(condition);
    }
  };

  SimpleFloatingFilter.prototype.isEventFromFloatingFilter = function (event) {
    return event && event.afterFloatingFilter;
  };

  SimpleFloatingFilter.prototype.getLastType = function () {
    return this.lastType;
  };

  SimpleFloatingFilter.prototype.setLastTypeFromModel = function (model) {
    // if no model provided by the parent filter use default
    if (!model) {
      this.lastType = this.optionsFactory.getDefaultOption();
      return;
    }

    var isCombined = model.operator;
    var condition;

    if (isCombined) {
      var combinedModel = model;
      condition = combinedModel.condition1;
    } else {
      condition = model;
    }

    this.lastType = condition.type;
  };

  SimpleFloatingFilter.prototype.canWeEditAfterModelFromParentFilter = function (model) {
    if (!model) {
      // if no model, then we can edit as long as the lastType is something we can edit, as this
      // is the type we will provide to the parent filter if the user decides to use the floating filter.
      return this.isTypeEditable(this.lastType);
    } // never allow editing if the filter is combined (ie has two parts)


    var isCombined = model.operator;

    if (isCombined) {
      return false;
    }

    var simpleModel = model;
    var typeIsEditable = this.isTypeEditable(simpleModel.type);
    return typeIsEditable;
  };

  SimpleFloatingFilter.prototype.init = function (params) {
    this.optionsFactory = new OptionsFactory();
    this.optionsFactory.init(params.filterParams, this.getDefaultFilterOptions());
    this.lastType = this.optionsFactory.getDefaultOption(); // we are editable if:
    // 1) there is a type (user has configured filter wrong if not type)
    //  AND
    // 2) the default type is not 'in range'

    var editable = this.isTypeEditable(this.lastType);
    this.setEditable(editable);
  };

  SimpleFloatingFilter.prototype.doesFilterHaveHiddenInput = function (filterType) {
    var customFilterOption = this.optionsFactory.getCustomOption(filterType);
    return customFilterOption && customFilterOption.hideFilterInput;
  };

  SimpleFloatingFilter.prototype.isTypeEditable = function (type) {
    if (this.doesFilterHaveHiddenInput(type)) {
      return false;
    }

    return type && type != SimpleFilter.IN_RANGE && type != SimpleFilter.EMPTY;
  };

  return SimpleFloatingFilter;
}(Component);
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __extends$o = undefined && undefined.__extends || function () {
  var _extendStatics25 = function extendStatics(d, b) {
    _extendStatics25 = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics25(d, b);
  };

  return function (d, b) {
    _extendStatics25(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var __decorate$q = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var DateFloatingFilter =
/** @class */
function (_super) {
  __extends$o(DateFloatingFilter, _super);

  function DateFloatingFilter() {
    return _super.call(this, "<div class=\"ag-input-wrapper\" role=\"presentation\">\n                <input ref=\"eReadOnlyText\" disabled=\"true\" class=\"ag-floating-filter-input\">\n                <div ref=\"eDateWrapper\" style=\"display: flex; flex: 1 1 auto; overflow: hidden;\"></div>\n            </div>") || this;
  }

  DateFloatingFilter.prototype.getDefaultFilterOptions = function () {
    return DateFilter.DEFAULT_FILTER_OPTIONS;
  };

  DateFloatingFilter.prototype.conditionToString = function (condition) {
    var isRange = condition.type == SimpleFilter.IN_RANGE;

    if (isRange) {
      return condition.dateFrom + "-" + condition.dateTo;
    } else {
      // cater for when the type doesn't need a value
      if (condition.dateFrom != null) {
        return "" + condition.dateFrom;
      } else {
        return "" + condition.type;
      }
    }
  };

  DateFloatingFilter.prototype.init = function (params) {
    _super.prototype.init.call(this, params);

    this.params = params;
    this.createDateComponent();
  };

  DateFloatingFilter.prototype.setEditable = function (editable) {
    _.setDisplayed(this.eDateWrapper, editable);

    _.setDisplayed(this.eReadOnlyText, !editable);
  };

  DateFloatingFilter.prototype.onParentModelChanged = function (model, event) {
    // we don't want to update the floating filter if the floating filter caused the change.
    // as if it caused the change, the ui is already in sycn. if we didn't do this, the UI
    // would behave strange as it would be updating as the user is typing
    if (this.isEventFromFloatingFilter(event)) {
      return;
    }

    _super.prototype.setLastTypeFromModel.call(this, model);

    var allowEditing = this.canWeEditAfterModelFromParentFilter(model);
    this.setEditable(allowEditing);

    if (allowEditing) {
      if (model) {
        var dateModel = model;
        this.dateComp.setDate(_.parseYyyyMmDdToDate(dateModel.dateFrom, '-'));
      } else {
        this.dateComp.setDate(null);
      }

      this.eReadOnlyText.value = '';
    } else {
      this.eReadOnlyText.value = this.getTextFromModel(model);
      this.dateComp.setDate(null);
    }
  };

  DateFloatingFilter.prototype.onDateChanged = function () {
    var _this = this;

    var filterValueDate = this.dateComp.getDate();

    var filterValueText = _.serializeDateToYyyyMmDd(filterValueDate, "-");

    this.params.parentFilterInstance(function (filterInstance) {
      if (filterInstance) {
        var simpleFilter = filterInstance;
        simpleFilter.onFloatingFilterChanged(_this.getLastType(), filterValueText);
      }
    });
  };

  DateFloatingFilter.prototype.createDateComponent = function () {
    var _this = this;

    var debounceMs = ProvidedFilter.getDebounceMs(this.params.filterParams, this.getDefaultDebounceMs());

    var toDebounce = _.debounce(this.onDateChanged.bind(this), debounceMs);

    var dateComponentParams = {
      onDateChanged: toDebounce,
      filterParams: this.params.column.getColDef().filterParams
    };
    this.dateComp = new DateCompWrapper(this.userComponentFactory, dateComponentParams, this.eDateWrapper);
    this.addDestroyFunc(function () {
      _this.dateComp.destroy();
    });
  };

  __decorate$q([Autowired('userComponentFactory')], DateFloatingFilter.prototype, "userComponentFactory", void 0);

  __decorate$q([RefSelector('eReadOnlyText')], DateFloatingFilter.prototype, "eReadOnlyText", void 0);

  __decorate$q([RefSelector('eDateWrapper')], DateFloatingFilter.prototype, "eDateWrapper", void 0);

  return DateFloatingFilter;
}(SimpleFloatingFilter);
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __extends$p = undefined && undefined.__extends || function () {
  var _extendStatics26 = function extendStatics(d, b) {
    _extendStatics26 = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics26(d, b);
  };

  return function (d, b) {
    _extendStatics26(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var __decorate$r = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var TextFilter =
/** @class */
function (_super) {
  __extends$p(TextFilter, _super);

  function TextFilter() {
    return _super !== null && _super.apply(this, arguments) || this;
  }

  TextFilter.prototype.getDefaultDebounceMs = function () {
    return 500;
  };

  TextFilter.prototype.getValue = function (element) {
    var val = element.value;
    val = _.makeNull(val);

    if (val && val.trim() === '') {
      val = null;
    }

    return val;
  };

  TextFilter.prototype.addValueChangedListeners = function () {
    var _this = this;

    var listener = function listener() {
      return _this.onUiChanged();
    };

    this.addDestroyableEventListener(this.eValue1, 'input', listener);
    this.addDestroyableEventListener(this.eValue2, 'input', listener);
  };

  TextFilter.prototype.setParams = function (params) {
    _super.prototype.setParams.call(this, params);

    this.textFilterParams = params;
    this.comparator = this.textFilterParams.textCustomComparator ? this.textFilterParams.textCustomComparator : TextFilter.DEFAULT_COMPARATOR;
    this.formatter = this.textFilterParams.textFormatter ? this.textFilterParams.textFormatter : this.textFilterParams.caseSensitive == true ? TextFilter.DEFAULT_FORMATTER : TextFilter.DEFAULT_LOWERCASE_FORMATTER;
    this.addValueChangedListeners();
  };

  TextFilter.prototype.setConditionIntoUi = function (model, position) {
    var positionOne = position === ConditionPosition.One;
    var eValue = positionOne ? this.eValue1 : this.eValue2;
    eValue.value = model ? model.filter : null;
  };

  TextFilter.prototype.createCondition = function (position) {
    var positionOne = position === ConditionPosition.One;
    var type = positionOne ? this.getCondition1Type() : this.getCondition2Type();
    var eValue = positionOne ? this.eValue1 : this.eValue2;
    var value = this.getValue(eValue);
    var model = {
      filterType: TextFilter.FILTER_TYPE,
      type: type
    };

    if (!this.doesFilterHaveHiddenInput(type)) {
      model.filter = value;
    }

    return model;
  };

  TextFilter.prototype.getFilterType = function () {
    return TextFilter.FILTER_TYPE;
  };

  TextFilter.prototype.areSimpleModelsEqual = function (aSimple, bSimple) {
    return aSimple.filter === bSimple.filter && aSimple.type === bSimple.type;
  };

  TextFilter.prototype.resetUiToDefaults = function () {
    _super.prototype.resetUiToDefaults.call(this);

    this.eValue1.value = null;
    this.eValue2.value = null;
  };

  TextFilter.prototype.setValueFromFloatingFilter = function (value) {
    this.eValue1.value = value;
    this.eValue2.value = null;
  };

  TextFilter.prototype.getDefaultFilterOptions = function () {
    return TextFilter.DEFAULT_FILTER_OPTIONS;
  };

  TextFilter.prototype.createValueTemplate = function (position) {
    var pos = position === ConditionPosition.One ? '1' : '2';
    var translate = this.gridOptionsWrapper.getLocaleTextFunc();
    return "<div class=\"ag-filter-body\" ref=\"eCondition" + pos + "Body\" role=\"presentation\">\n            <div class=\"ag-input-wrapper\" ref=\"eInputWrapper" + pos + "\" role=\"presentation\">\n                <input class=\"ag-filter-filter\" ref=\"eValue" + pos + "\" type=\"text\" placeholder=\"" + translate('filterOoo', 'Filter...') + "\"/>\n            </div>\n        </div>";
  };

  TextFilter.prototype.updateUiVisibility = function () {
    _super.prototype.updateUiVisibility.call(this);

    var showValue1 = this.showValueFrom(this.getCondition1Type());

    _.setDisplayed(this.eInputWrapper1, showValue1);

    var showValue2 = this.showValueFrom(this.getCondition2Type());

    _.setDisplayed(this.eInputWrapper2, showValue2);
  };

  TextFilter.prototype.afterGuiAttached = function () {
    this.eValue1.focus();
  };

  TextFilter.prototype.isConditionUiComplete = function (position) {
    var positionOne = position === ConditionPosition.One;
    var option = positionOne ? this.getCondition1Type() : this.getCondition2Type();
    var eFilterValue = positionOne ? this.eValue1 : this.eValue2;

    if (option === SimpleFilter.EMPTY) {
      return false;
    }

    var value = this.getValue(eFilterValue);

    if (this.doesFilterHaveHiddenInput(option)) {
      return true;
    }

    return value != null;
  };

  TextFilter.prototype.individualConditionPasses = function (params, filterModel) {
    var filterText = filterModel.filter;
    var filterOption = filterModel.type;
    var cellValue = this.textFilterParams.valueGetter(params.node);
    var cellValueFormatted = this.formatter(cellValue);
    var customFilterOption = this.optionsFactory.getCustomOption(filterOption);

    if (customFilterOption) {
      // only execute the custom filter if a value exists or a value isn't required, i.e. input is hidden
      if (filterText != null || customFilterOption.hideFilterInput) {
        return customFilterOption.test(filterText, cellValueFormatted);
      }
    }

    if (cellValue == null) {
      return filterOption === SimpleFilter.NOT_EQUAL || filterOption === SimpleFilter.NOT_CONTAINS;
    }

    var filterTextFormatted = this.formatter(filterText);
    return this.comparator(filterOption, cellValueFormatted, filterTextFormatted);
  };

  TextFilter.FILTER_TYPE = 'text';
  TextFilter.DEFAULT_FILTER_OPTIONS = [SimpleFilter.CONTAINS, SimpleFilter.NOT_CONTAINS, SimpleFilter.EQUALS, SimpleFilter.NOT_EQUAL, SimpleFilter.STARTS_WITH, SimpleFilter.ENDS_WITH];

  TextFilter.DEFAULT_FORMATTER = function (from) {
    return from;
  };

  TextFilter.DEFAULT_LOWERCASE_FORMATTER = function (from) {
    if (from == null) {
      return null;
    }

    return from.toString().toLowerCase();
  };

  TextFilter.DEFAULT_COMPARATOR = function (filter, value, filterText) {
    switch (filter) {
      case TextFilter.CONTAINS:
        return value.indexOf(filterText) >= 0;

      case TextFilter.NOT_CONTAINS:
        return value.indexOf(filterText) === -1;

      case TextFilter.EQUALS:
        return value === filterText;

      case TextFilter.NOT_EQUAL:
        return value != filterText;

      case TextFilter.STARTS_WITH:
        return value.indexOf(filterText) === 0;

      case TextFilter.ENDS_WITH:
        var index = value.lastIndexOf(filterText);
        return index >= 0 && index === value.length - filterText.length;

      default:
        // should never happen
        console.warn('invalid filter type ' + filter);
        return false;
    }
  };

  __decorate$r([RefSelector('eValue1')], TextFilter.prototype, "eValue1", void 0);

  __decorate$r([RefSelector('eValue2')], TextFilter.prototype, "eValue2", void 0);

  __decorate$r([RefSelector('eInputWrapper1')], TextFilter.prototype, "eInputWrapper1", void 0);

  __decorate$r([RefSelector('eInputWrapper2')], TextFilter.prototype, "eInputWrapper2", void 0);

  return TextFilter;
}(SimpleFilter);
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __extends$q = undefined && undefined.__extends || function () {
  var _extendStatics27 = function extendStatics(d, b) {
    _extendStatics27 = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics27(d, b);
  };

  return function (d, b) {
    _extendStatics27(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var __decorate$s = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var TextInputFloatingFilter =
/** @class */
function (_super) {
  __extends$q(TextInputFloatingFilter, _super);

  function TextInputFloatingFilter() {
    return _super !== null && _super.apply(this, arguments) || this;
  }

  TextInputFloatingFilter.prototype.postConstruct = function () {
    this.setTemplate("<div class=\"ag-input-wrapper\" role=\"presentation\">\n                <input ref=\"eFloatingFilterText\" class=\"ag-floating-filter-input\">\n            </div>");
  };

  TextInputFloatingFilter.prototype.getDefaultDebounceMs = function () {
    return 500;
  };

  TextInputFloatingFilter.prototype.onParentModelChanged = function (model, event) {
    // we don't want to update the floating filter if the floating filter caused the change.
    // as if it caused the change, the ui is already in sycn. if we didn't do this, the UI
    // would behave strange as it would be updating as the user is typing
    if (this.isEventFromFloatingFilter(event)) {
      return;
    }

    this.setLastTypeFromModel(model);
    var modelString = this.getTextFromModel(model);
    this.eFloatingFilterText.value = modelString;
    var editable = this.canWeEditAfterModelFromParentFilter(model);
    this.setEditable(editable);
  };

  TextInputFloatingFilter.prototype.init = function (params) {
    _super.prototype.init.call(this, params);

    this.params = params;
    this.applyActive = ProvidedFilter.isUseApplyButton(this.params.filterParams);
    var debounceMs = ProvidedFilter.getDebounceMs(this.params.filterParams, this.getDefaultDebounceMs());

    var toDebounce = _.debounce(this.syncUpWithParentFilter.bind(this), debounceMs);

    this.addDestroyableEventListener(this.eFloatingFilterText, 'input', toDebounce);
    this.addDestroyableEventListener(this.eFloatingFilterText, 'keypress', toDebounce);
    this.addDestroyableEventListener(this.eFloatingFilterText, 'keydown', toDebounce);
    var columnDef = params.column.getDefinition();

    if (columnDef.filterParams && columnDef.filterParams.filterOptions && columnDef.filterParams.filterOptions.length === 1 && columnDef.filterParams.filterOptions[0] === 'inRange') {
      this.eFloatingFilterText.disabled = true;
    }
  };

  TextInputFloatingFilter.prototype.syncUpWithParentFilter = function (e) {
    var _this = this;

    var value = this.eFloatingFilterText.value;

    var enterKeyPressed = _.isKeyPressed(e, Constants.KEY_ENTER);

    if (this.applyActive && !enterKeyPressed) {
      return;
    }

    this.params.parentFilterInstance(function (filterInstance) {
      if (filterInstance) {
        var simpleFilter = filterInstance;
        simpleFilter.onFloatingFilterChanged(_this.getLastType(), value);
      }
    });
  };

  TextInputFloatingFilter.prototype.setEditable = function (editable) {
    this.eFloatingFilterText.disabled = !editable;
  };

  __decorate$s([RefSelector('eFloatingFilterText')], TextInputFloatingFilter.prototype, "eFloatingFilterText", void 0);

  __decorate$s([PostConstruct], TextInputFloatingFilter.prototype, "postConstruct", null);

  return TextInputFloatingFilter;
}(SimpleFloatingFilter);
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __extends$r = undefined && undefined.__extends || function () {
  var _extendStatics28 = function extendStatics(d, b) {
    _extendStatics28 = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics28(d, b);
  };

  return function (d, b) {
    _extendStatics28(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var NumberFloatingFilter =
/** @class */
function (_super) {
  __extends$r(NumberFloatingFilter, _super);

  function NumberFloatingFilter() {
    return _super !== null && _super.apply(this, arguments) || this;
  }

  NumberFloatingFilter.prototype.getDefaultFilterOptions = function () {
    return NumberFilter.DEFAULT_FILTER_OPTIONS;
  };

  NumberFloatingFilter.prototype.conditionToString = function (condition) {
    var isRange = condition.type == SimpleFilter.IN_RANGE;

    if (isRange) {
      return condition.filter + "-" + condition.filterTo;
    } else {
      // cater for when the type doesn't need a value
      if (condition.filter != null) {
        return "" + condition.filter;
      } else {
        return "" + condition.type;
      }
    }
  };

  return NumberFloatingFilter;
}(TextInputFloatingFilter);
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __extends$s = undefined && undefined.__extends || function () {
  var _extendStatics29 = function extendStatics(d, b) {
    _extendStatics29 = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics29(d, b);
  };

  return function (d, b) {
    _extendStatics29(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var TextFloatingFilter =
/** @class */
function (_super) {
  __extends$s(TextFloatingFilter, _super);

  function TextFloatingFilter() {
    return _super !== null && _super.apply(this, arguments) || this;
  }

  TextFloatingFilter.prototype.conditionToString = function (condition) {
    // it's not possible to have 'in range' for string, so no need to check for it.
    // also cater for when the type doesn't need a value
    if (condition.filter != null) {
      return "" + condition.filter;
    } else {
      return "" + condition.type;
    }
  };

  TextFloatingFilter.prototype.getDefaultFilterOptions = function () {
    return TextFilter.DEFAULT_FILTER_OPTIONS;
  };

  return TextFloatingFilter;
}(TextInputFloatingFilter);
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __decorate$t = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var RegisteredComponentSource;

(function (RegisteredComponentSource) {
  RegisteredComponentSource[RegisteredComponentSource["DEFAULT"] = 0] = "DEFAULT";
  RegisteredComponentSource[RegisteredComponentSource["REGISTERED"] = 1] = "REGISTERED";
})(RegisteredComponentSource || (RegisteredComponentSource = {}));

var UserComponentRegistry =
/** @class */
function () {
  function UserComponentRegistry() {
    this.agGridDefaults = {
      //date
      agDateInput: DefaultDateComponent,
      //header
      agColumnHeader: HeaderComp,
      agColumnGroupHeader: HeaderGroupComp,
      //floating filters
      agTextColumnFloatingFilter: TextFloatingFilter,
      agNumberColumnFloatingFilter: NumberFloatingFilter,
      agDateColumnFloatingFilter: DateFloatingFilter,
      // renderers
      agAnimateShowChangeCellRenderer: AnimateShowChangeCellRenderer,
      agAnimateSlideCellRenderer: AnimateSlideCellRenderer,
      agGroupCellRenderer: GroupCellRenderer,
      agGroupRowRenderer: GroupCellRenderer,
      agLoadingCellRenderer: LoadingCellRenderer,
      //editors
      agCellEditor: TextCellEditor,
      agTextCellEditor: TextCellEditor,
      agSelectCellEditor: SelectCellEditor,
      agPopupTextCellEditor: PopupTextCellEditor,
      agPopupSelectCellEditor: PopupSelectCellEditor,
      agLargeTextCellEditor: LargeTextCellEditor,
      //filter
      agTextColumnFilter: TextFilter,
      agNumberColumnFilter: NumberFilter,
      agDateColumnFilter: DateFilter,
      //overlays
      agLoadingOverlay: LoadingOverlayComponent,
      agNoRowsOverlay: NoRowsOverlayComponent,
      // tooltips
      agTooltipComponent: TooltipComponent
    };
    this.agDeprecatedNames = {
      set: {
        newComponentName: 'agSetColumnFilter',
        propertyHolder: 'filter'
      },
      text: {
        newComponentName: 'agTextColumnFilter',
        propertyHolder: 'filter'
      },
      number: {
        newComponentName: 'agNumberColumnFilter',
        propertyHolder: 'filter'
      },
      date: {
        newComponentName: 'agDateColumnFilter',
        propertyHolder: 'filter'
      },
      group: {
        newComponentName: 'agGroupCellRenderer',
        propertyHolder: 'cellRenderer'
      },
      animateShowChange: {
        newComponentName: 'agAnimateShowChangeCellRenderer',
        propertyHolder: 'cellRenderer'
      },
      animateSlide: {
        newComponentName: 'agAnimateSlideCellRenderer',
        propertyHolder: 'cellRenderer'
      },
      select: {
        newComponentName: 'agSelectCellEditor',
        propertyHolder: 'cellEditor'
      },
      largeText: {
        newComponentName: 'agLargeTextCellEditor',
        propertyHolder: 'cellEditor'
      },
      popupSelect: {
        newComponentName: 'agPopupSelectCellEditor',
        propertyHolder: 'cellEditor'
      },
      popupText: {
        newComponentName: 'agPopupTextCellEditor',
        propertyHolder: 'cellEditor'
      },
      richSelect: {
        newComponentName: 'agRichSelectCellEditor',
        propertyHolder: 'cellEditor'
      },
      headerComponent: {
        newComponentName: 'agColumnHeader',
        propertyHolder: 'headerComponent'
      }
    };
    this.jsComponents = {};
    this.frameworkComponents = {};
  }

  UserComponentRegistry.prototype.init = function () {
    var _this = this;

    if (this.gridOptions.components != null) {
      Object.keys(this.gridOptions.components).forEach(function (it) {
        _this.registerComponent(it, _this.gridOptions.components[it]);
      });
    }

    if (this.gridOptions.frameworkComponents != null) {
      Object.keys(this.gridOptions.frameworkComponents).forEach(function (it) {
        _this.registerFwComponent(it, _this.gridOptions.frameworkComponents[it]);
      });
    }
  };

  UserComponentRegistry.prototype.registerDefaultComponent = function (rawName, component) {
    var name = this.translateIfDeprecated(rawName);

    if (this.agGridDefaults[name]) {
      console.error("Trying to overwrite a default component. You should call registerComponent");
      return;
    }

    this.agGridDefaults[name] = component;
  };

  UserComponentRegistry.prototype.registerComponent = function (rawName, component) {
    var name = this.translateIfDeprecated(rawName);

    if (this.frameworkComponents[name]) {
      console.error("Trying to register a component that you have already registered for frameworks: " + name);
      return;
    }

    this.jsComponents[name] = component;
  };
  /**
   * B the business interface (ie IHeader)
   * A the agGridComponent interface (ie IHeaderComp). The final object acceptable by ag-grid
   */


  UserComponentRegistry.prototype.registerFwComponent = function (rawName, component) {
    var name = this.translateIfDeprecated(rawName);

    if (this.jsComponents[name]) {
      console.error("Trying to register a component that you have already registered for plain javascript: " + name);
      return;
    }

    this.frameworkComponents[name] = component;
  };
  /**
   * B the business interface (ie IHeader)
   * A the agGridComponent interface (ie IHeaderComp). The final object acceptable by ag-grid
   */


  UserComponentRegistry.prototype.retrieve = function (rawName) {
    var name = this.translateIfDeprecated(rawName);

    if (this.frameworkComponents[name]) {
      return {
        componentFromFramework: true,
        component: this.frameworkComponents[name],
        source: RegisteredComponentSource.REGISTERED
      };
    }

    if (this.jsComponents[name]) {
      return {
        componentFromFramework: false,
        component: this.jsComponents[name],
        source: RegisteredComponentSource.REGISTERED
      };
    }

    if (this.agGridDefaults[name]) {
      return this.agGridDefaults[name] ? {
        componentFromFramework: false,
        component: this.agGridDefaults[name],
        source: RegisteredComponentSource.DEFAULT
      } : null;
    }

    if (Object.keys(this.agGridDefaults).indexOf(name) < 0) {
      console.warn("ag-Grid: Looking for component [" + name + "] but it wasn't found.");
    }

    return null;
  };

  UserComponentRegistry.prototype.translateIfDeprecated = function (raw) {
    var deprecatedInfo = this.agDeprecatedNames[raw];

    if (deprecatedInfo != null) {
      _.doOnce(function () {
        console.warn("ag-grid. Since v15.0 component names have been renamed to be namespaced. You should rename " + deprecatedInfo.propertyHolder + ":" + raw + " to " + deprecatedInfo.propertyHolder + ":" + deprecatedInfo.newComponentName);
      }, 'DEPRECATE_COMPONENT_' + raw);

      return deprecatedInfo.newComponentName;
    }

    return raw;
  };

  __decorate$t([Autowired('gridOptions')], UserComponentRegistry.prototype, "gridOptions", void 0);

  __decorate$t([Autowired('context')], UserComponentRegistry.prototype, "context", void 0);

  __decorate$t([PostConstruct], UserComponentRegistry.prototype, "init", null);

  UserComponentRegistry = __decorate$t([Bean('userComponentRegistry')], UserComponentRegistry);
  return UserComponentRegistry;
}();
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */
// when doing transactions, or change detection, and grouping is present
// in the data, there is no need for the ClientSideRowModel to update each
// group after an update, ony parts that were impacted by the change.
// this class keeps track of all groups that were impacted by a transaction.
// the the different CSRM operations (filter, sort etc) use the forEach method
// to visit each group that was changed.


var ChangedPath =
/** @class */
function () {
  function ChangedPath(keepingColumns, rootNode) {
    // whether changed path is active of not. it is active when a) doing
    // a transaction update or b) doing change detection. if we are doing
    // a CSRM refresh for other reasons (after sort or filter, or user calling
    // setRowData() without delta mode) then we are not active. we are also
    // marked as not active if secondary columns change in pivot (as this impacts
    // aggregations)
    this.active = true; // for each node in the change path, we also store which columns need
    // to be re-aggregated.

    this.nodeIdsToColumns = {}; // for quick lookup, all items in the change path are mapped by nodeId

    this.mapToItems = {};
    this.keepingColumns = keepingColumns;
    this.pathRoot = {
      rowNode: rootNode,
      children: null
    };
    this.mapToItems[rootNode.id] = this.pathRoot;
  } // can be set inactive by:
  // a) ClientSideRowModel, if no transactions or
  // b) PivotService, if secondary columns changed


  ChangedPath.prototype.setInactive = function () {
    this.active = false;
  };

  ChangedPath.prototype.isActive = function () {
    return this.active;
  };

  ChangedPath.prototype.depthFirstSearchChangedPath = function (pathItem, callback) {
    if (pathItem.children) {
      for (var i = 0; i < pathItem.children.length; i++) {
        this.depthFirstSearchChangedPath(pathItem.children[i], callback);
      }
    }

    callback(pathItem.rowNode);
  };

  ChangedPath.prototype.depthFirstSearchEverything = function (rowNode, callback, traverseEverything) {
    if (rowNode.childrenAfterGroup) {
      for (var i = 0; i < rowNode.childrenAfterGroup.length; i++) {
        var childNode = rowNode.childrenAfterGroup[i];

        if (childNode.childrenAfterGroup) {
          this.depthFirstSearchEverything(rowNode.childrenAfterGroup[i], callback, traverseEverything);
        } else if (traverseEverything) {
          callback(childNode);
        }
      }
    }

    callback(rowNode);
  }; // traverseLeafNodes -> used when NOT doing changed path, ie traversing everything. the callback
  // will be called for child nodes in addition to parent nodes.


  ChangedPath.prototype.forEachChangedNodeDepthFirst = function (callback, traverseLeafNodes) {
    if (traverseLeafNodes === void 0) {
      traverseLeafNodes = false;
    }

    if (this.active) {
      // if we are active, then use the change path to callback
      // only for updated groups
      this.depthFirstSearchChangedPath(this.pathRoot, callback);
    } else {
      // we are not active, so callback for everything, walk the entire path
      this.depthFirstSearchEverything(this.pathRoot.rowNode, callback, traverseLeafNodes);
    }
  };

  ChangedPath.prototype.executeFromRootNode = function (callback) {
    callback(this.pathRoot.rowNode);
  };

  ChangedPath.prototype.createPathItems = function (rowNode) {
    var pointer = rowNode;
    var newEntryCount = 0;

    while (!this.mapToItems[pointer.id]) {
      var newEntry = {
        rowNode: pointer,
        children: null
      };
      this.mapToItems[pointer.id] = newEntry;
      newEntryCount++;
      pointer = pointer.parent;
    }

    return newEntryCount;
  };

  ChangedPath.prototype.populateColumnsMap = function (rowNode, columns) {
    var _this = this;

    if (!this.keepingColumns || !columns) {
      return;
    }

    var pointer = rowNode;

    while (pointer) {
      // if columns, add the columns in all the way to parent, merging
      // in any other columns that might be there already
      if (!this.nodeIdsToColumns[pointer.id]) {
        this.nodeIdsToColumns[pointer.id] = {};
      }

      columns.forEach(function (col) {
        return _this.nodeIdsToColumns[pointer.id][col.getId()] = true;
      });
      pointer = pointer.parent;
    }
  };

  ChangedPath.prototype.linkPathItems = function (rowNode, newEntryCount) {
    var pointer = rowNode;

    for (var i = 0; i < newEntryCount; i++) {
      var thisItem = this.mapToItems[pointer.id];
      var parentItem = this.mapToItems[pointer.parent.id];

      if (!parentItem.children) {
        parentItem.children = [];
      }

      parentItem.children.push(thisItem);
      pointer = pointer.parent;
    }
  }; // called by
  // 1) change detection (provides cols) and
  // 2) groupStage if doing transaction update (doesn't provide cols)


  ChangedPath.prototype.addParentNode = function (rowNode, columns) {
    if (!rowNode || rowNode.isRowPinned()) {
      return;
    } // we cannot do  both steps below in the same loop as
    // the second loop has a dependency on the first loop.
    // ie the hierarchy cannot be stitched up yet because
    // we don't have it built yet
    // create the new PathItem objects.


    var newEntryCount = this.createPathItems(rowNode); // link in the node items

    this.linkPathItems(rowNode, newEntryCount); // update columns

    this.populateColumnsMap(rowNode, columns);
  };

  ChangedPath.prototype.canSkip = function (rowNode) {
    return this.active && !this.mapToItems[rowNode.id];
  };

  ChangedPath.prototype.getValueColumnsForNode = function (rowNode, valueColumns) {
    if (!this.keepingColumns) {
      return valueColumns;
    }

    var colsForThisNode = this.nodeIdsToColumns[rowNode.id];
    var result = valueColumns.filter(function (col) {
      return colsForThisNode[col.getId()];
    });
    return result;
  };

  ChangedPath.prototype.getNotValueColumnsForNode = function (rowNode, valueColumns) {
    if (!this.keepingColumns) {
      return null;
    }

    var colsForThisNode = this.nodeIdsToColumns[rowNode.id];
    var result = valueColumns.filter(function (col) {
      return !colsForThisNode[col.getId()];
    });
    return result;
  };

  return ChangedPath;
}();
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __decorate$u = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var __param$4 = undefined && undefined.__param || function (paramIndex, decorator) {
  return function (target, key) {
    decorator(target, key, paramIndex);
  };
};

var SelectionController =
/** @class */
function () {
  function SelectionController() {}

  SelectionController.prototype.setBeans = function (loggerFactory) {
    this.logger = loggerFactory.create('SelectionController');
    this.reset();

    if (this.gridOptionsWrapper.isRowModelDefault()) {
      this.eventService.addEventListener(Events.EVENT_ROW_DATA_CHANGED, this.reset.bind(this));
    } else {
      this.logger.log('dont know what to do here');
    }
  };

  SelectionController.prototype.init = function () {
    this.groupSelectsChildren = this.gridOptionsWrapper.isGroupSelectsChildren();
    this.eventService.addEventListener(Events.EVENT_ROW_SELECTED, this.onRowSelected.bind(this));
  };

  SelectionController.prototype.setLastSelectedNode = function (rowNode) {
    this.lastSelectedNode = rowNode;
  };

  SelectionController.prototype.getLastSelectedNode = function () {
    return this.lastSelectedNode;
  };

  SelectionController.prototype.getSelectedNodes = function () {
    var selectedNodes = [];

    _.iterateObject(this.selectedNodes, function (key, rowNode) {
      if (rowNode) {
        selectedNodes.push(rowNode);
      }
    });

    return selectedNodes;
  };

  SelectionController.prototype.getSelectedRows = function () {
    var selectedRows = [];

    _.iterateObject(this.selectedNodes, function (key, rowNode) {
      if (rowNode && rowNode.data) {
        selectedRows.push(rowNode.data);
      }
    });

    return selectedRows;
  };

  SelectionController.prototype.removeGroupsFromSelection = function () {
    var _this = this;

    _.iterateObject(this.selectedNodes, function (key, rowNode) {
      if (rowNode && rowNode.group) {
        _this.selectedNodes[rowNode.id] = undefined;
      }
    });
  }; // should only be called if groupSelectsChildren=true


  SelectionController.prototype.updateGroupsFromChildrenSelections = function (changedPath) {
    // we only do this when group selection state depends on selected children
    if (!this.gridOptionsWrapper.isGroupSelectsChildren()) {
      return;
    } // also only do it if CSRM (code should never allow this anyway)


    if (this.rowModel.getType() !== Constants.ROW_MODEL_TYPE_CLIENT_SIDE) {
      return;
    }

    var clientSideRowModel = this.rowModel;
    var rootNode = clientSideRowModel.getRootNode();

    if (!changedPath) {
      changedPath = new ChangedPath(true, rootNode);
      changedPath.setInactive();
    }

    changedPath.forEachChangedNodeDepthFirst(function (rowNode) {
      if (rowNode !== rootNode) {
        rowNode.calculateSelectedFromChildren();
      }
    }); // clientSideRowModel.getTopLevelNodes()!.forEach((rowNode: RowNode) => {
    //     rowNode.depthFirstSearch((node) => {
    //         if (node.group) {
    //         }
    //     });
    // });
  };

  SelectionController.prototype.getNodeForIdIfSelected = function (id) {
    return this.selectedNodes[id];
  };

  SelectionController.prototype.clearOtherNodes = function (rowNodeToKeepSelected) {
    var _this = this;

    var groupsToRefresh = {};
    var updatedCount = 0;

    _.iterateObject(this.selectedNodes, function (key, otherRowNode) {
      if (otherRowNode && otherRowNode.id !== rowNodeToKeepSelected.id) {
        var rowNode = _this.selectedNodes[otherRowNode.id];
        updatedCount += rowNode.setSelectedParams({
          newValue: false,
          clearSelection: false,
          suppressFinishActions: true
        });

        if (_this.groupSelectsChildren && otherRowNode.parent) {
          groupsToRefresh[otherRowNode.parent.id] = otherRowNode.parent;
        }
      }
    });

    _.iterateObject(groupsToRefresh, function (key, group) {
      group.calculateSelectedFromChildren();
    });

    return updatedCount;
  };

  SelectionController.prototype.onRowSelected = function (event) {
    var rowNode = event.node; // we do not store the group rows when the groups select children

    if (this.groupSelectsChildren && rowNode.group) {
      return;
    }

    if (rowNode.isSelected()) {
      this.selectedNodes[rowNode.id] = rowNode;
    } else {
      this.selectedNodes[rowNode.id] = undefined;
    }
  };

  SelectionController.prototype.syncInRowNode = function (rowNode, oldNode) {
    this.syncInOldRowNode(rowNode, oldNode);
    this.syncInNewRowNode(rowNode);
  }; // if the id has changed for the node, then this means the rowNode
  // is getting used for a different data item, which breaks
  // our selectedNodes, as the node now is mapped by the old id
  // which is inconsistent. so to keep the old node as selected,
  // we swap in the clone (with the old id and old data). this means
  // the oldNode is effectively a daemon we keep a reference to,
  // so if client calls api.getSelectedNodes(), it gets the daemon
  // in the result. when the client un-selects, the reference to the
  // daemon is removed. the daemon, because it's an oldNode, is not
  // used by the grid for rendering, it's a copy of what the node used
  // to be like before the id was changed.


  SelectionController.prototype.syncInOldRowNode = function (rowNode, oldNode) {
    var oldNodeHasDifferentId = _.exists(oldNode) && rowNode.id !== oldNode.id;

    if (oldNodeHasDifferentId) {
      var oldNodeSelected = _.exists(this.selectedNodes[oldNode.id]);

      if (oldNodeSelected) {
        this.selectedNodes[oldNode.id] = oldNode;
      }
    }
  };

  SelectionController.prototype.syncInNewRowNode = function (rowNode) {
    if (_.exists(this.selectedNodes[rowNode.id])) {
      rowNode.setSelectedInitialValue(true);
      this.selectedNodes[rowNode.id] = rowNode;
    } else {
      rowNode.setSelectedInitialValue(false);
    }
  };

  SelectionController.prototype.reset = function () {
    this.logger.log('reset');
    this.selectedNodes = {};
    this.lastSelectedNode = null;
  }; // returns a list of all nodes at 'best cost' - a feature to be used
  // with groups / trees. if a group has all it's children selected,
  // then the group appears in the result, but not the children.
  // Designed for use with 'children' as the group selection type,
  // where groups don't actually appear in the selection normally.


  SelectionController.prototype.getBestCostNodeSelection = function () {
    if (this.rowModel.getType() !== Constants.ROW_MODEL_TYPE_CLIENT_SIDE) {
      console.warn('getBestCostNodeSelection is only available when using normal row model');
      return;
    }

    var clientSideRowModel = this.rowModel;
    var topLevelNodes = clientSideRowModel.getTopLevelNodes();

    if (topLevelNodes === null) {
      console.warn('selectAll not available doing rowModel=virtual');
      return;
    }

    var result = []; // recursive function, to find the selected nodes

    function traverse(nodes) {
      for (var i = 0, l = nodes.length; i < l; i++) {
        var node = nodes[i];

        if (node.isSelected()) {
          result.push(node);
        } else {
          // if not selected, then if it's a group, and the group
          // has children, continue to search for selections
          if (node.group && node.children) {
            traverse(node.children);
          }
        }
      }
    }

    traverse(topLevelNodes);
    return result;
  };

  SelectionController.prototype.setRowModel = function (rowModel) {
    this.rowModel = rowModel;
  };

  SelectionController.prototype.isEmpty = function () {
    var count = 0;

    _.iterateObject(this.selectedNodes, function (nodeId, rowNode) {
      if (rowNode) {
        count++;
      }
    });

    return count === 0;
  };

  SelectionController.prototype.deselectAllRowNodes = function (justFiltered) {
    if (justFiltered === void 0) {
      justFiltered = false;
    }

    var callback = function callback(rowNode) {
      return rowNode.selectThisNode(false);
    };

    var rowModelClientSide = this.rowModel.getType() === Constants.ROW_MODEL_TYPE_CLIENT_SIDE;

    if (justFiltered) {
      if (!rowModelClientSide) {
        console.error('ag-Grid: selecting just filtered only works with In Memory Row Model');
        return;
      }

      var clientSideRowModel = this.rowModel;
      clientSideRowModel.forEachNodeAfterFilter(callback);
    } else {
      _.iterateObject(this.selectedNodes, function (id, rowNode) {
        // remember the reference can be to null, as we never 'delete' from the map
        if (rowNode) {
          callback(rowNode);
        }
      }); // this clears down the map (whereas above only sets the items in map to 'undefined')


      this.reset();
    } // the above does not clean up the parent rows if they are selected


    if (rowModelClientSide && this.groupSelectsChildren) {
      this.updateGroupsFromChildrenSelections();
    }

    var event = {
      type: Events.EVENT_SELECTION_CHANGED,
      api: this.gridApi,
      columnApi: this.columnApi
    };
    this.eventService.dispatchEvent(event);
  };

  SelectionController.prototype.selectAllRowNodes = function (justFiltered) {
    if (justFiltered === void 0) {
      justFiltered = false;
    }

    if (this.rowModel.getType() !== Constants.ROW_MODEL_TYPE_CLIENT_SIDE) {
      throw new Error("selectAll only available with normal row model, ie not " + this.rowModel.getType());
    }

    var clientSideRowModel = this.rowModel;

    var callback = function callback(rowNode) {
      return rowNode.selectThisNode(true);
    };

    if (justFiltered) {
      clientSideRowModel.forEachNodeAfterFilter(callback);
    } else {
      clientSideRowModel.forEachNode(callback);
    } // the above does not clean up the parent rows if they are selected


    if (this.rowModel.getType() === Constants.ROW_MODEL_TYPE_CLIENT_SIDE && this.groupSelectsChildren) {
      this.updateGroupsFromChildrenSelections();
    }

    var event = {
      type: Events.EVENT_SELECTION_CHANGED,
      api: this.gridApi,
      columnApi: this.columnApi
    };
    this.eventService.dispatchEvent(event);
  };
  /**
   * @method
   * @deprecated
   */


  SelectionController.prototype.selectNode = function (rowNode, tryMulti) {
    if (rowNode) {
      rowNode.setSelectedParams({
        newValue: true,
        clearSelection: !tryMulti
      });
    }
  };
  /**
   * @method
   * @deprecated
   */


  SelectionController.prototype.deselectIndex = function (rowIndex) {
    var node = this.rowModel.getRow(rowIndex);
    this.deselectNode(node);
  };
  /**
   * @method
   * @deprecated
   */


  SelectionController.prototype.deselectNode = function (rowNode) {
    if (rowNode) {
      rowNode.setSelectedParams({
        newValue: false,
        clearSelection: false
      });
    }
  };
  /**
   * @method
   * @deprecated
   */


  SelectionController.prototype.selectIndex = function (index, tryMulti) {
    var node = this.rowModel.getRow(index);
    this.selectNode(node, tryMulti);
  };

  __decorate$u([Autowired('eventService')], SelectionController.prototype, "eventService", void 0);

  __decorate$u([Autowired('rowModel')], SelectionController.prototype, "rowModel", void 0);

  __decorate$u([Autowired('gridOptionsWrapper')], SelectionController.prototype, "gridOptionsWrapper", void 0);

  __decorate$u([Autowired('columnApi')], SelectionController.prototype, "columnApi", void 0);

  __decorate$u([Autowired('gridApi')], SelectionController.prototype, "gridApi", void 0);

  __decorate$u([__param$4(0, Qualifier('loggerFactory'))], SelectionController.prototype, "setBeans", null);

  __decorate$u([PostConstruct], SelectionController.prototype, "init", null);

  SelectionController = __decorate$u([Bean('selectionController')], SelectionController);
  return SelectionController;
}();
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __decorate$v = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var ColumnApi =
/** @class */
function () {
  function ColumnApi() {}

  ColumnApi.prototype.sizeColumnsToFit = function (gridWidth) {
    // AG-3403 validate that gridWidth is provided because this method has the same name as
    // a method on the grid API that takes no arguments, and it's easy to confuse the two
    if (typeof gridWidth === "undefined") {
      console.error('ag-Grid: missing parameter to columnApi.sizeColumnsToFit(gridWidth)');
    }

    this.columnController.sizeColumnsToFit(gridWidth, 'api');
  };

  ColumnApi.prototype.setColumnGroupOpened = function (group, newValue) {
    this.columnController.setColumnGroupOpened(group, newValue, 'api');
  };

  ColumnApi.prototype.getColumnGroup = function (name, instanceId) {
    return this.columnController.getColumnGroup(name, instanceId);
  };

  ColumnApi.prototype.getOriginalColumnGroup = function (name) {
    return this.columnController.getOriginalColumnGroup(name);
  };

  ColumnApi.prototype.getDisplayNameForColumn = function (column, location) {
    return this.columnController.getDisplayNameForColumn(column, location) || '';
  };

  ColumnApi.prototype.getDisplayNameForColumnGroup = function (columnGroup, location) {
    return this.columnController.getDisplayNameForColumnGroup(columnGroup, location) || '';
  };

  ColumnApi.prototype.getColumn = function (key) {
    return this.columnController.getPrimaryColumn(key);
  };

  ColumnApi.prototype.setColumnState = function (columnState) {
    return this.columnController.setColumnState(columnState, false, 'api');
  };

  ColumnApi.prototype.getColumnState = function () {
    return this.columnController.getColumnState();
  };

  ColumnApi.prototype.resetColumnState = function () {
    this.columnController.resetColumnState(false, 'api');
  };

  ColumnApi.prototype.getColumnGroupState = function () {
    return this.columnController.getColumnGroupState();
  };

  ColumnApi.prototype.setColumnGroupState = function (stateItems) {
    this.columnController.setColumnGroupState(stateItems, 'api');
  };

  ColumnApi.prototype.resetColumnGroupState = function () {
    this.columnController.resetColumnGroupState('api');
  };

  ColumnApi.prototype.isPinning = function () {
    return this.columnController.isPinningLeft() || this.columnController.isPinningRight();
  };

  ColumnApi.prototype.isPinningLeft = function () {
    return this.columnController.isPinningLeft();
  };

  ColumnApi.prototype.isPinningRight = function () {
    return this.columnController.isPinningRight();
  };

  ColumnApi.prototype.getDisplayedColAfter = function (col) {
    return this.columnController.getDisplayedColAfter(col);
  };

  ColumnApi.prototype.getDisplayedColBefore = function (col) {
    return this.columnController.getDisplayedColBefore(col);
  };

  ColumnApi.prototype.setColumnVisible = function (key, visible) {
    this.columnController.setColumnVisible(key, visible, 'api');
  };

  ColumnApi.prototype.setColumnsVisible = function (keys, visible) {
    this.columnController.setColumnsVisible(keys, visible, 'api');
  };

  ColumnApi.prototype.setColumnPinned = function (key, pinned) {
    this.columnController.setColumnPinned(key, pinned, 'api');
  };

  ColumnApi.prototype.setColumnsPinned = function (keys, pinned) {
    this.columnController.setColumnsPinned(keys, pinned, 'api');
  };

  ColumnApi.prototype.getAllColumns = function () {
    return this.columnController.getAllPrimaryColumns();
  };

  ColumnApi.prototype.getAllGridColumns = function () {
    return this.columnController.getAllGridColumns();
  };

  ColumnApi.prototype.getDisplayedLeftColumns = function () {
    return this.columnController.getDisplayedLeftColumns();
  };

  ColumnApi.prototype.getDisplayedCenterColumns = function () {
    return this.columnController.getDisplayedCenterColumns();
  };

  ColumnApi.prototype.getDisplayedRightColumns = function () {
    return this.columnController.getDisplayedRightColumns();
  };

  ColumnApi.prototype.getAllDisplayedColumns = function () {
    return this.columnController.getAllDisplayedColumns();
  };

  ColumnApi.prototype.getAllDisplayedVirtualColumns = function () {
    return this.columnController.getAllDisplayedVirtualColumns();
  };

  ColumnApi.prototype.moveColumn = function (key, toIndex) {
    if (typeof key === 'number') {
      // moveColumn used to take indexes, so this is advising user who hasn't moved to new method name
      console.warn('ag-Grid: you are using moveColumn(fromIndex, toIndex) - moveColumn takes a column key and a destination index, not two indexes, to move with indexes use moveColumnByIndex(from,to) instead');
      this.columnController.moveColumnByIndex(key, toIndex, 'api');
    } else {
      this.columnController.moveColumn(key, toIndex, 'api');
    }
  };

  ColumnApi.prototype.moveColumnByIndex = function (fromIndex, toIndex) {
    this.columnController.moveColumnByIndex(fromIndex, toIndex, 'api');
  };

  ColumnApi.prototype.moveColumns = function (columnsToMoveKeys, toIndex) {
    this.columnController.moveColumns(columnsToMoveKeys, toIndex, 'api');
  };

  ColumnApi.prototype.moveRowGroupColumn = function (fromIndex, toIndex) {
    this.columnController.moveRowGroupColumn(fromIndex, toIndex);
  };

  ColumnApi.prototype.setColumnAggFunc = function (column, aggFunc) {
    this.columnController.setColumnAggFunc(column, aggFunc);
  };

  ColumnApi.prototype.setColumnWidth = function (key, newWidth, finished) {
    if (finished === void 0) {
      finished = true;
    }

    this.columnController.setColumnWidth(key, newWidth, false, finished);
  };

  ColumnApi.prototype.setPivotMode = function (pivotMode) {
    this.columnController.setPivotMode(pivotMode);
  };

  ColumnApi.prototype.isPivotMode = function () {
    return this.columnController.isPivotMode();
  };

  ColumnApi.prototype.getSecondaryPivotColumn = function (pivotKeys, valueColKey) {
    return this.columnController.getSecondaryPivotColumn(pivotKeys, valueColKey);
  };

  ColumnApi.prototype.setValueColumns = function (colKeys) {
    this.columnController.setValueColumns(colKeys, 'api');
  };

  ColumnApi.prototype.getValueColumns = function () {
    return this.columnController.getValueColumns();
  };

  ColumnApi.prototype.removeValueColumn = function (colKey) {
    this.columnController.removeValueColumn(colKey, 'api');
  };

  ColumnApi.prototype.removeValueColumns = function (colKeys) {
    this.columnController.removeValueColumns(colKeys, 'api');
  };

  ColumnApi.prototype.addValueColumn = function (colKey) {
    this.columnController.addValueColumn(colKey, 'api');
  };

  ColumnApi.prototype.addValueColumns = function (colKeys) {
    this.columnController.addValueColumns(colKeys, 'api');
  };

  ColumnApi.prototype.setRowGroupColumns = function (colKeys) {
    this.columnController.setRowGroupColumns(colKeys, 'api');
  };

  ColumnApi.prototype.removeRowGroupColumn = function (colKey) {
    this.columnController.removeRowGroupColumn(colKey, 'api');
  };

  ColumnApi.prototype.removeRowGroupColumns = function (colKeys) {
    this.columnController.removeRowGroupColumns(colKeys, 'api');
  };

  ColumnApi.prototype.addRowGroupColumn = function (colKey) {
    this.columnController.addRowGroupColumn(colKey, 'api');
  };

  ColumnApi.prototype.addRowGroupColumns = function (colKeys) {
    this.columnController.addRowGroupColumns(colKeys, 'api');
  };

  ColumnApi.prototype.getRowGroupColumns = function () {
    return this.columnController.getRowGroupColumns();
  };

  ColumnApi.prototype.setPivotColumns = function (colKeys) {
    this.columnController.setPivotColumns(colKeys, 'api');
  };

  ColumnApi.prototype.removePivotColumn = function (colKey) {
    this.columnController.removePivotColumn(colKey, 'api');
  };

  ColumnApi.prototype.removePivotColumns = function (colKeys) {
    this.columnController.removePivotColumns(colKeys, 'api');
  };

  ColumnApi.prototype.addPivotColumn = function (colKey) {
    this.columnController.addPivotColumn(colKey, 'api');
  };

  ColumnApi.prototype.addPivotColumns = function (colKeys) {
    this.columnController.addPivotColumns(colKeys, 'api');
  };

  ColumnApi.prototype.getPivotColumns = function () {
    return this.columnController.getPivotColumns();
  };

  ColumnApi.prototype.getLeftDisplayedColumnGroups = function () {
    return this.columnController.getLeftDisplayedColumnGroups();
  };

  ColumnApi.prototype.getCenterDisplayedColumnGroups = function () {
    return this.columnController.getCenterDisplayedColumnGroups();
  };

  ColumnApi.prototype.getRightDisplayedColumnGroups = function () {
    return this.columnController.getRightDisplayedColumnGroups();
  };

  ColumnApi.prototype.getAllDisplayedColumnGroups = function () {
    return this.columnController.getAllDisplayedColumnGroups();
  };

  ColumnApi.prototype.autoSizeColumn = function (key, skipHeader) {
    return this.columnController.autoSizeColumn(key, skipHeader, 'api');
  };

  ColumnApi.prototype.autoSizeColumns = function (keys, skipHeader) {
    return this.columnController.autoSizeColumns(keys, skipHeader, 'api');
  };

  ColumnApi.prototype.autoSizeAllColumns = function (skipHeader) {
    this.columnController.autoSizeAllColumns(skipHeader, 'api');
  };

  ColumnApi.prototype.setSecondaryColumns = function (colDefs) {
    this.columnController.setSecondaryColumns(colDefs, 'api');
  };

  ColumnApi.prototype.getSecondaryColumns = function () {
    return this.columnController.getSecondaryColumns();
  };

  ColumnApi.prototype.getPrimaryColumns = function () {
    return this.columnController.getAllPrimaryColumns();
  }; // below goes through deprecated items, prints message to user, then calls the new version of the same method


  ColumnApi.prototype.columnGroupOpened = function (group, newValue) {
    console.error('ag-Grid: columnGroupOpened no longer exists, use setColumnGroupOpened');
    this.setColumnGroupOpened(group, newValue);
  };

  ColumnApi.prototype.hideColumns = function (colIds, hide) {
    console.error('ag-Grid: hideColumns is deprecated, use setColumnsVisible');
    this.columnController.setColumnsVisible(colIds, !hide, 'api');
  };

  ColumnApi.prototype.hideColumn = function (colId, hide) {
    console.error('ag-Grid: hideColumn is deprecated, use setColumnVisible');
    this.columnController.setColumnVisible(colId, !hide, 'api');
  };

  ColumnApi.prototype.setState = function (columnState) {
    console.error('ag-Grid: setState is deprecated, use setColumnState');
    return this.setColumnState(columnState);
  };

  ColumnApi.prototype.getState = function () {
    console.error('ag-Grid: getState is deprecated, use getColumnState');
    return this.getColumnState();
  };

  ColumnApi.prototype.resetState = function () {
    console.error('ag-Grid: resetState is deprecated, use resetColumnState');
    this.resetColumnState();
  };

  ColumnApi.prototype.getAggregationColumns = function () {
    console.error('ag-Grid: getAggregationColumns is deprecated, use getValueColumns');
    return this.columnController.getValueColumns();
  };

  ColumnApi.prototype.removeAggregationColumn = function (colKey) {
    console.error('ag-Grid: removeAggregationColumn is deprecated, use removeValueColumn');
    this.columnController.removeValueColumn(colKey, 'api');
  };

  ColumnApi.prototype.removeAggregationColumns = function (colKeys) {
    console.error('ag-Grid: removeAggregationColumns is deprecated, use removeValueColumns');
    this.columnController.removeValueColumns(colKeys, 'api');
  };

  ColumnApi.prototype.addAggregationColumn = function (colKey) {
    console.error('ag-Grid: addAggregationColumn is deprecated, use addValueColumn');
    this.columnController.addValueColumn(colKey, 'api');
  };

  ColumnApi.prototype.addAggregationColumns = function (colKeys) {
    console.error('ag-Grid: addAggregationColumns is deprecated, use addValueColumns');
    this.columnController.addValueColumns(colKeys, 'api');
  };

  ColumnApi.prototype.setColumnAggFunction = function (column, aggFunc) {
    console.error('ag-Grid: setColumnAggFunction is deprecated, use setColumnAggFunc');
    this.columnController.setColumnAggFunc(column, aggFunc, 'api');
  };

  ColumnApi.prototype.getDisplayNameForCol = function (column) {
    console.error('ag-Grid: getDisplayNameForCol is deprecated, use getDisplayNameForColumn');
    return this.getDisplayNameForColumn(column, null);
  };

  __decorate$v([Autowired('columnController')], ColumnApi.prototype, "columnController", void 0);

  ColumnApi = __decorate$v([Bean('columnApi')], ColumnApi);
  return ColumnApi;
}();
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


(function (CellRangeType) {
  CellRangeType[CellRangeType["VALUE"] = 0] = "VALUE";
  CellRangeType[CellRangeType["DIMENSION"] = 1] = "DIMENSION";
})(exports.CellRangeType || (exports.CellRangeType = {}));
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __decorate$w = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var __param$5 = undefined && undefined.__param || function (paramIndex, decorator) {
  return function (target, key) {
    decorator(target, key, paramIndex);
  };
};

(function (DragSourceType) {
  DragSourceType[DragSourceType["ToolPanel"] = 0] = "ToolPanel";
  DragSourceType[DragSourceType["HeaderCell"] = 1] = "HeaderCell";
  DragSourceType[DragSourceType["RowDrag"] = 2] = "RowDrag";
  DragSourceType[DragSourceType["ChartPanel"] = 3] = "ChartPanel";
})(exports.DragSourceType || (exports.DragSourceType = {}));

(function (VerticalDirection) {
  VerticalDirection[VerticalDirection["Up"] = 0] = "Up";
  VerticalDirection[VerticalDirection["Down"] = 1] = "Down";
})(exports.VerticalDirection || (exports.VerticalDirection = {}));

(function (HorizontalDirection) {
  HorizontalDirection[HorizontalDirection["Left"] = 0] = "Left";
  HorizontalDirection[HorizontalDirection["Right"] = 1] = "Right";
})(exports.HorizontalDirection || (exports.HorizontalDirection = {}));

var DragAndDropService =
/** @class */
function () {
  function DragAndDropService() {
    this.dragSourceAndParamsList = [];
    this.dropTargets = [];
  }

  DragAndDropService_1 = DragAndDropService;

  DragAndDropService.prototype.init = function () {
    this.ePinnedIcon = _.createIcon('columnMovePin', this.gridOptionsWrapper, null);
    this.ePlusIcon = _.createIcon('columnMoveAdd', this.gridOptionsWrapper, null);
    this.eHiddenIcon = _.createIcon('columnMoveHide', this.gridOptionsWrapper, null);
    this.eMoveIcon = _.createIcon('columnMoveMove', this.gridOptionsWrapper, null);
    this.eLeftIcon = _.createIcon('columnMoveLeft', this.gridOptionsWrapper, null);
    this.eRightIcon = _.createIcon('columnMoveRight', this.gridOptionsWrapper, null);
    this.eGroupIcon = _.createIcon('columnMoveGroup', this.gridOptionsWrapper, null);
    this.eAggregateIcon = _.createIcon('columnMoveValue', this.gridOptionsWrapper, null);
    this.ePivotIcon = _.createIcon('columnMovePivot', this.gridOptionsWrapper, null);
    this.eDropNotAllowedIcon = _.createIcon('dropNotAllowed', this.gridOptionsWrapper, null);
  };

  DragAndDropService.prototype.setBeans = function (loggerFactory) {
    this.logger = loggerFactory.create('OldToolPanelDragAndDropService');
  };

  DragAndDropService.prototype.addDragSource = function (dragSource, allowTouch) {
    if (allowTouch === void 0) {
      allowTouch = false;
    }

    var params = {
      eElement: dragSource.eElement,
      dragStartPixels: dragSource.dragStartPixels,
      onDragStart: this.onDragStart.bind(this, dragSource),
      onDragStop: this.onDragStop.bind(this),
      onDragging: this.onDragging.bind(this)
    };
    this.dragSourceAndParamsList.push({
      params: params,
      dragSource: dragSource
    });
    this.dragService.addDragSource(params, allowTouch);
  };

  DragAndDropService.prototype.removeDragSource = function (dragSource) {
    var sourceAndParams = _.find(this.dragSourceAndParamsList, function (item) {
      return item.dragSource === dragSource;
    });

    if (sourceAndParams) {
      this.dragService.removeDragSource(sourceAndParams.params);

      _.removeFromArray(this.dragSourceAndParamsList, sourceAndParams);
    }
  };

  DragAndDropService.prototype.destroy = function () {
    var _this = this;

    this.dragSourceAndParamsList.forEach(function (sourceAndParams) {
      return _this.dragService.removeDragSource(sourceAndParams.params);
    });
    this.dragSourceAndParamsList.length = 0;
  };

  DragAndDropService.prototype.nudge = function () {
    if (this.dragging) {
      this.onDragging(this.eventLastTime, true);
    }
  };

  DragAndDropService.prototype.onDragStart = function (dragSource, mouseEvent) {
    this.dragging = true;
    this.dragSource = dragSource;
    this.eventLastTime = mouseEvent;
    this.dragItem = this.dragSource.getDragItem();
    this.lastDropTarget = this.dragSource.dragSourceDropTarget;

    if (this.dragSource.onDragStarted) {
      this.dragSource.onDragStarted();
    }

    this.createGhost();
  };

  DragAndDropService.prototype.onDragStop = function (mouseEvent) {
    this.eventLastTime = null;
    this.dragging = false;

    if (this.dragSource.onDragStopped) {
      this.dragSource.onDragStopped();
    }

    if (this.lastDropTarget && this.lastDropTarget.onDragStop) {
      var draggingEvent = this.createDropTargetEvent(this.lastDropTarget, mouseEvent, null, null, false);
      this.lastDropTarget.onDragStop(draggingEvent);
    }

    this.lastDropTarget = null;
    this.dragItem = null;
    this.removeGhost();
  };

  DragAndDropService.prototype.onDragging = function (mouseEvent, fromNudge) {
    var hDirection = this.getHorizontalDirection(mouseEvent);
    var vDirection = this.getVerticalDirection(mouseEvent);
    this.eventLastTime = mouseEvent;
    this.positionGhost(mouseEvent); // check if mouseEvent intersects with any of the drop targets

    var dropTarget = _.find(this.dropTargets, this.isMouseOnDropTarget.bind(this, mouseEvent));

    if (dropTarget !== this.lastDropTarget) {
      this.leaveLastTargetIfExists(mouseEvent, hDirection, vDirection, fromNudge);
      this.enterDragTargetIfExists(dropTarget, mouseEvent, hDirection, vDirection, fromNudge);
      this.lastDropTarget = dropTarget;
    } else if (dropTarget) {
      var draggingEvent = this.createDropTargetEvent(dropTarget, mouseEvent, hDirection, vDirection, fromNudge);
      dropTarget.onDragging(draggingEvent);
    }
  };

  DragAndDropService.prototype.enterDragTargetIfExists = function (dropTarget, mouseEvent, hDirection, vDirection, fromNudge) {
    if (!dropTarget) {
      return;
    }

    if (dropTarget.onDragEnter) {
      var dragEnterEvent = this.createDropTargetEvent(dropTarget, mouseEvent, hDirection, vDirection, fromNudge);
      dropTarget.onDragEnter(dragEnterEvent);
    }

    this.setGhostIcon(dropTarget.getIconName ? dropTarget.getIconName() : null);
  };

  DragAndDropService.prototype.leaveLastTargetIfExists = function (mouseEvent, hDirection, vDirection, fromNudge) {
    if (!this.lastDropTarget) {
      return;
    }

    if (this.lastDropTarget.onDragLeave) {
      var dragLeaveEvent = this.createDropTargetEvent(this.lastDropTarget, mouseEvent, hDirection, vDirection, fromNudge);
      this.lastDropTarget.onDragLeave(dragLeaveEvent);
    }

    this.setGhostIcon(null);
  };

  DragAndDropService.prototype.getAllContainersFromDropTarget = function (dropTarget) {
    var containers = [dropTarget.getContainer()];
    var secondaryContainers = dropTarget.getSecondaryContainers ? dropTarget.getSecondaryContainers() : null;

    if (secondaryContainers) {
      containers = containers.concat(secondaryContainers);
    }

    return containers;
  }; // checks if the mouse is on the drop target. it checks eContainer and eSecondaryContainers


  DragAndDropService.prototype.isMouseOnDropTarget = function (mouseEvent, dropTarget) {
    var mouseOverTarget = false;
    this.getAllContainersFromDropTarget(dropTarget).filter(function (eContainer) {
      return eContainer;
    }) // secondary can be missing
    .forEach(function (eContainer) {
      var rect = eContainer.getBoundingClientRect(); // if element is not visible, then width and height are zero

      if (rect.width === 0 || rect.height === 0) {
        return;
      }

      var horizontalFit = mouseEvent.clientX >= rect.left && mouseEvent.clientX <= rect.right;
      var verticalFit = mouseEvent.clientY >= rect.top && mouseEvent.clientY <= rect.bottom;

      if (horizontalFit && verticalFit) {
        mouseOverTarget = true;
      }
    });
    return mouseOverTarget && dropTarget.isInterestedIn(this.dragSource.type);
  };

  DragAndDropService.prototype.addDropTarget = function (dropTarget) {
    this.dropTargets.push(dropTarget);
  };

  DragAndDropService.prototype.getHorizontalDirection = function (event) {
    if (this.eventLastTime.clientX > event.clientX) {
      return exports.HorizontalDirection.Left;
    } else if (this.eventLastTime.clientX < event.clientX) {
      return exports.HorizontalDirection.Right;
    } else {
      return null;
    }
  };

  DragAndDropService.prototype.getVerticalDirection = function (event) {
    if (this.eventLastTime.clientY > event.clientY) {
      return exports.VerticalDirection.Up;
    } else if (this.eventLastTime.clientY < event.clientY) {
      return exports.VerticalDirection.Down;
    } else {
      return null;
    }
  };

  DragAndDropService.prototype.createDropTargetEvent = function (dropTarget, event, hDirection, vDirection, fromNudge) {
    // localise x and y to the target component
    var rect = dropTarget.getContainer().getBoundingClientRect();
    var x = event.clientX - rect.left;
    var y = event.clientY - rect.top;
    return {
      event: event,
      x: x,
      y: y,
      vDirection: vDirection,
      hDirection: hDirection,
      dragSource: this.dragSource,
      fromNudge: fromNudge,
      dragItem: this.dragItem
    };
  };

  DragAndDropService.prototype.positionGhost = function (event) {
    var ghostRect = this.eGhost.getBoundingClientRect();
    var ghostHeight = ghostRect.height; // for some reason, without the '-2', it still overlapped by 1 or 2 pixels, which
    // then brought in scrollbars to the browser. no idea why, but putting in -2 here
    // works around it which is good enough for me.

    var browserWidth = _.getBodyWidth() - 2;
    var browserHeight = _.getBodyHeight() - 2; // put ghost vertically in middle of cursor

    var top = event.pageY - ghostHeight / 2; // horizontally, place cursor just right of icon

    var left = event.pageX - 30;
    var usrDocument = this.gridOptionsWrapper.getDocument();
    var windowScrollY = window.pageYOffset || usrDocument.documentElement.scrollTop;
    var windowScrollX = window.pageXOffset || usrDocument.documentElement.scrollLeft; // check ghost is not positioned outside of the browser

    if (browserWidth > 0 && left + this.eGhost.clientWidth > browserWidth + windowScrollX) {
      left = browserWidth + windowScrollX - this.eGhost.clientWidth;
    }

    if (left < 0) {
      left = 0;
    }

    if (browserHeight > 0 && top + this.eGhost.clientHeight > browserHeight + windowScrollY) {
      top = browserHeight + windowScrollY - this.eGhost.clientHeight;
    }

    if (top < 0) {
      top = 0;
    }

    this.eGhost.style.left = left + 'px';
    this.eGhost.style.top = top + 'px';
  };

  DragAndDropService.prototype.removeGhost = function () {
    if (this.eGhost && this.eGhostParent) {
      this.eGhostParent.removeChild(this.eGhost);
    }

    this.eGhost = null;
  };

  DragAndDropService.prototype.createGhost = function () {
    this.eGhost = _.loadTemplate(DragAndDropService_1.GHOST_TEMPLATE);
    var theme = this.environment.getTheme().theme;

    if (theme) {
      _.addCssClass(this.eGhost, theme);
    }

    this.eGhostIcon = this.eGhost.querySelector('.ag-dnd-ghost-icon');
    this.setGhostIcon(null);
    var eText = this.eGhost.querySelector('.ag-dnd-ghost-label');
    eText.innerHTML = _.escape(this.dragSource.dragItemName);
    this.eGhost.style.height = '25px';
    this.eGhost.style.top = '20px';
    this.eGhost.style.left = '20px';
    var usrDocument = this.gridOptionsWrapper.getDocument();
    this.eGhostParent = usrDocument.querySelector('body');

    if (!this.eGhostParent) {
      console.warn('ag-Grid: could not find document body, it is needed for dragging columns');
    } else {
      this.eGhostParent.appendChild(this.eGhost);
    }
  };

  DragAndDropService.prototype.setGhostIcon = function (iconName, shake) {
    if (shake === void 0) {
      shake = false;
    }

    _.clearElement(this.eGhostIcon);

    var eIcon;

    switch (iconName) {
      case DragAndDropService_1.ICON_ADD:
        eIcon = this.ePlusIcon;
        break;

      case DragAndDropService_1.ICON_PINNED:
        eIcon = this.ePinnedIcon;
        break;

      case DragAndDropService_1.ICON_MOVE:
        eIcon = this.eMoveIcon;
        break;

      case DragAndDropService_1.ICON_LEFT:
        eIcon = this.eLeftIcon;
        break;

      case DragAndDropService_1.ICON_RIGHT:
        eIcon = this.eRightIcon;
        break;

      case DragAndDropService_1.ICON_GROUP:
        eIcon = this.eGroupIcon;
        break;

      case DragAndDropService_1.ICON_AGGREGATE:
        eIcon = this.eAggregateIcon;
        break;

      case DragAndDropService_1.ICON_PIVOT:
        eIcon = this.ePivotIcon;
        break;

      case DragAndDropService_1.ICON_NOT_ALLOWED:
        eIcon = this.eDropNotAllowedIcon;
        break;

      default:
        eIcon = this.eHiddenIcon;
        break;
    }

    this.eGhostIcon.appendChild(eIcon);

    _.addOrRemoveCssClass(this.eGhostIcon, 'ag-shake-left-to-right', shake);
  };

  var DragAndDropService_1;
  DragAndDropService.ICON_PINNED = 'pinned';
  DragAndDropService.ICON_ADD = 'add';
  DragAndDropService.ICON_MOVE = 'move';
  DragAndDropService.ICON_LEFT = 'left';
  DragAndDropService.ICON_RIGHT = 'right';
  DragAndDropService.ICON_GROUP = 'group';
  DragAndDropService.ICON_AGGREGATE = 'aggregate';
  DragAndDropService.ICON_PIVOT = 'pivot';
  DragAndDropService.ICON_NOT_ALLOWED = 'notAllowed';
  DragAndDropService.GHOST_TEMPLATE = '<div class="ag-dnd-ghost">' + '  <span class="ag-dnd-ghost-icon ag-shake-left-to-right"></span>' + '  <div class="ag-dnd-ghost-label">' + '  </div>' + '</div>';

  __decorate$w([Autowired('gridOptionsWrapper')], DragAndDropService.prototype, "gridOptionsWrapper", void 0);

  __decorate$w([Autowired('dragService')], DragAndDropService.prototype, "dragService", void 0);

  __decorate$w([Autowired('environment')], DragAndDropService.prototype, "environment", void 0);

  __decorate$w([PostConstruct], DragAndDropService.prototype, "init", null);

  __decorate$w([__param$5(0, Qualifier('loggerFactory'))], DragAndDropService.prototype, "setBeans", null);

  __decorate$w([PreDestroy], DragAndDropService.prototype, "destroy", null);

  DragAndDropService = DragAndDropService_1 = __decorate$w([Bean('dragAndDropService')], DragAndDropService);
  return DragAndDropService;
}();
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __extends$t = undefined && undefined.__extends || function () {
  var _extendStatics30 = function extendStatics(d, b) {
    _extendStatics30 = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics30(d, b);
  };

  return function (d, b) {
    _extendStatics30(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var __decorate$x = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var RowDragComp =
/** @class */
function (_super) {
  __extends$t(RowDragComp, _super);

  function RowDragComp(rowNode, column, cellValue, beans) {
    var _this = _super.call(this, "<div class=\"ag-row-drag\"></div>") || this;

    _this.rowNode = rowNode;
    _this.column = column;
    _this.cellValue = cellValue;
    _this.beans = beans;
    return _this;
  }

  RowDragComp.prototype.postConstruct = function () {
    var eGui = this.getGui();
    eGui.appendChild(_.createIconNoSpan('rowDrag', this.beans.gridOptionsWrapper, null));
    this.addDragSource();
    this.checkCompatibility();
    var strategy = this.beans.gridOptionsWrapper.isRowDragManaged() ? new ManagedVisibilityStrategy(this, this.beans, this.rowNode, this.column) : new NonManagedVisibilityStrategy(this, this.beans, this.rowNode, this.column);
    this.addFeature(strategy, this.beans.context);
  }; // returns true if all compatibility items work out


  RowDragComp.prototype.checkCompatibility = function () {
    var managed = this.beans.gridOptionsWrapper.isRowDragManaged();
    var treeData = this.beans.gridOptionsWrapper.isTreeData();

    if (treeData && managed) {
      _.doOnce(function () {
        return console.warn('ag-Grid: If using row drag with tree data, you cannot have rowDragManaged=true');
      }, 'RowDragComp.managedAndTreeData');
    }
  };

  RowDragComp.prototype.addDragSource = function () {
    var _this = this;

    var dragItem = {
      rowNode: this.rowNode
    };
    var dragSource = {
      type: exports.DragSourceType.RowDrag,
      eElement: this.getGui(),
      dragItemName: this.cellValue,
      getDragItem: function getDragItem() {
        return dragItem;
      },
      dragStartPixels: 0
    };
    this.beans.dragAndDropService.addDragSource(dragSource, true);
    this.addDestroyFunc(function () {
      return _this.beans.dragAndDropService.removeDragSource(dragSource);
    });
  };

  __decorate$x([PostConstruct], RowDragComp.prototype, "postConstruct", null);

  return RowDragComp;
}(Component);

var VisibilityStrategy =
/** @class */
function (_super) {
  __extends$t(VisibilityStrategy, _super);

  function VisibilityStrategy(parent, rowNode, column) {
    var _this = _super.call(this) || this;

    _this.parent = parent;
    _this.column = column;
    _this.rowNode = rowNode;
    return _this;
  }

  VisibilityStrategy.prototype.setDisplayedOrVisible = function (neverDisplayed) {
    if (neverDisplayed) {
      this.parent.setDisplayed(false);
    } else {
      var shown = this.column.isRowDrag(this.rowNode);

      var isShownSometimes = _.isFunction(this.column.getColDef().rowDrag); // if shown sometimes, them some rows can have drag handle while other don't,
      // so we use setVisible to keep the handles horizontally aligned (as setVisible
      // keeps the empty space, whereas setDisplayed looses the space)


      if (isShownSometimes) {
        this.parent.setDisplayed(true);
        this.parent.setVisible(shown);
      } else {
        this.parent.setDisplayed(shown);
      }
    }
  };

  return VisibilityStrategy;
}(BeanStub); // when non managed, the visibility depends on suppressRowDrag property only


var NonManagedVisibilityStrategy =
/** @class */
function (_super) {
  __extends$t(NonManagedVisibilityStrategy, _super);

  function NonManagedVisibilityStrategy(parent, beans, rowNode, column) {
    var _this = _super.call(this, parent, rowNode, column) || this;

    _this.beans = beans;
    return _this;
  }

  NonManagedVisibilityStrategy.prototype.postConstruct = function () {
    this.addDestroyableEventListener(this.beans.gridOptionsWrapper, 'suppressRowDrag', this.onSuppressRowDrag.bind(this)); // in case data changes, then we need to update visibility of drag item

    this.addDestroyableEventListener(this.rowNode, RowNode.EVENT_DATA_CHANGED, this.workOutVisibility.bind(this));
    this.addDestroyableEventListener(this.rowNode, RowNode.EVENT_CELL_CHANGED, this.workOutVisibility.bind(this));
    this.workOutVisibility();
  };

  NonManagedVisibilityStrategy.prototype.onSuppressRowDrag = function () {
    this.workOutVisibility();
  };

  NonManagedVisibilityStrategy.prototype.workOutVisibility = function () {
    // only show the drag if both sort and filter are not present
    var neverDisplayed = this.beans.gridOptionsWrapper.isSuppressRowDrag();
    this.setDisplayedOrVisible(neverDisplayed);
  };

  __decorate$x([PostConstruct], NonManagedVisibilityStrategy.prototype, "postConstruct", null);

  return NonManagedVisibilityStrategy;
}(VisibilityStrategy); // when managed, the visibility depends on sort, filter and row group, as well as suppressRowDrag property


var ManagedVisibilityStrategy =
/** @class */
function (_super) {
  __extends$t(ManagedVisibilityStrategy, _super);

  function ManagedVisibilityStrategy(parent, beans, rowNode, column) {
    var _this = _super.call(this, parent, rowNode, column) || this;

    _this.beans = beans;
    return _this;
  }

  ManagedVisibilityStrategy.prototype.postConstruct = function () {
    // we do not show the component if sort, filter or grouping is active
    this.addDestroyableEventListener(this.beans.eventService, Events.EVENT_SORT_CHANGED, this.onSortChanged.bind(this));
    this.addDestroyableEventListener(this.beans.eventService, Events.EVENT_FILTER_CHANGED, this.onFilterChanged.bind(this));
    this.addDestroyableEventListener(this.beans.eventService, Events.EVENT_COLUMN_ROW_GROUP_CHANGED, this.onRowGroupChanged.bind(this)); // in case data changes, then we need to update visibility of drag item

    this.addDestroyableEventListener(this.rowNode, RowNode.EVENT_DATA_CHANGED, this.workOutVisibility.bind(this));
    this.addDestroyableEventListener(this.rowNode, RowNode.EVENT_CELL_CHANGED, this.workOutVisibility.bind(this));
    this.addDestroyableEventListener(this.beans.gridOptionsWrapper, 'suppressRowDrag', this.onSuppressRowDrag.bind(this));
    this.updateSortActive();
    this.updateFilterActive();
    this.updateRowGroupActive();
    this.workOutVisibility();
  };

  ManagedVisibilityStrategy.prototype.updateRowGroupActive = function () {
    var rowGroups = this.beans.columnController.getRowGroupColumns();
    this.rowGroupActive = !_.missingOrEmpty(rowGroups);
  };

  ManagedVisibilityStrategy.prototype.onRowGroupChanged = function () {
    this.updateRowGroupActive();
    this.workOutVisibility();
  };

  ManagedVisibilityStrategy.prototype.updateSortActive = function () {
    var sortModel = this.beans.sortController.getSortModel();
    this.sortActive = !_.missingOrEmpty(sortModel);
  };

  ManagedVisibilityStrategy.prototype.onSortChanged = function () {
    this.updateSortActive();
    this.workOutVisibility();
  };

  ManagedVisibilityStrategy.prototype.updateFilterActive = function () {
    this.filterActive = this.beans.filterManager.isAnyFilterPresent();
  };

  ManagedVisibilityStrategy.prototype.onFilterChanged = function () {
    this.updateFilterActive();
    this.workOutVisibility();
  };

  ManagedVisibilityStrategy.prototype.onSuppressRowDrag = function () {
    this.workOutVisibility();
  };

  ManagedVisibilityStrategy.prototype.workOutVisibility = function () {
    // only show the drag if both sort and filter are not present
    var sortOrFilterOrGroupActive = this.sortActive || this.filterActive || this.rowGroupActive;
    var suppressRowDrag = this.beans.gridOptionsWrapper.isSuppressRowDrag();
    var neverDisplayed = sortOrFilterOrGroupActive || suppressRowDrag;
    this.setDisplayedOrVisible(neverDisplayed);
  };

  __decorate$x([PostConstruct], ManagedVisibilityStrategy.prototype, "postConstruct", null);

  return ManagedVisibilityStrategy;
}(VisibilityStrategy);
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __extends$u = undefined && undefined.__extends || function () {
  var _extendStatics31 = function extendStatics(d, b) {
    _extendStatics31 = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics31(d, b);
  };

  return function (d, b) {
    _extendStatics31(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var __decorate$y = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var PopupEditorWrapper =
/** @class */
function (_super) {
  __extends$u(PopupEditorWrapper, _super);

  function PopupEditorWrapper(cellEditor) {
    var _this = _super.call(this, "<div class=\"ag-popup-editor\" tabindex=\"-1\"/>") || this;

    _this.getGuiCalledOnChild = false;
    _this.cellEditor = cellEditor;
    return _this;
  }

  PopupEditorWrapper.prototype.onKeyDown = function (event) {
    if (!_.isUserSuppressingKeyboardEvent(this.gridOptionsWrapper, event, this.params.node, this.params.column, true)) {
      this.params.onKeyDown(event);
    }
  };

  PopupEditorWrapper.prototype.getGui = function () {
    // we call getGui() on child here (rather than in the constructor)
    // as we should wait for 'init' to be called on child first.
    if (!this.getGuiCalledOnChild) {
      this.appendChild(this.cellEditor.getGui());
      this.getGuiCalledOnChild = true;
    }

    return _super.prototype.getGui.call(this);
  };

  PopupEditorWrapper.prototype.init = function (params) {
    var _this = this;

    this.params = params;
    this.gridOptionsWrapper.setDomData(this.getGui(), PopupEditorWrapper.DOM_KEY_POPUP_EDITOR_WRAPPER, true);
    this.addDestroyFunc(function () {
      if (_this.cellEditor.destroy) {
        _this.cellEditor.destroy();
      }
    });
    this.addDestroyableEventListener( // this needs to be 'super' and not 'this' as if we call 'this',
    // it ends up called 'getGui()' on the child before 'init' was called,
    // which is not good
    _super.prototype.getGui.call(this), 'keydown', this.onKeyDown.bind(this));
  };

  PopupEditorWrapper.prototype.afterGuiAttached = function () {
    if (this.cellEditor.afterGuiAttached) {
      this.cellEditor.afterGuiAttached();
    }
  };

  PopupEditorWrapper.prototype.getValue = function () {
    return this.cellEditor.getValue();
  };

  PopupEditorWrapper.prototype.isCancelBeforeStart = function () {
    if (this.cellEditor.isCancelBeforeStart) {
      return this.cellEditor.isCancelBeforeStart();
    }
  };

  PopupEditorWrapper.prototype.isCancelAfterEnd = function () {
    if (this.cellEditor.isCancelAfterEnd) {
      return this.cellEditor.isCancelAfterEnd();
    }
  };

  PopupEditorWrapper.prototype.focusIn = function () {
    if (this.cellEditor.focusIn) {
      this.cellEditor.focusIn();
    }
  };

  PopupEditorWrapper.prototype.focusOut = function () {
    if (this.cellEditor.focusOut) {
      this.cellEditor.focusOut();
    }
  };

  PopupEditorWrapper.DOM_KEY_POPUP_EDITOR_WRAPPER = 'popupEditorWrapper';

  __decorate$y([Autowired('gridOptionsWrapper')], PopupEditorWrapper.prototype, "gridOptionsWrapper", void 0);

  return PopupEditorWrapper;
}(PopupComponent);
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __extends$v = undefined && undefined.__extends || function () {
  var _extendStatics32 = function extendStatics(d, b) {
    _extendStatics32 = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics32(d, b);
  };

  return function (d, b) {
    _extendStatics32(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var __decorate$z = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var DndSourceComp =
/** @class */
function (_super) {
  __extends$v(DndSourceComp, _super);

  function DndSourceComp(rowNode, column, cellValue, beans, eCell) {
    var _this = _super.call(this, "<div class=\"ag-row-drag\" draggable=\"true\"></div>") || this;

    _this.rowNode = rowNode;
    _this.column = column;
    _this.cellValue = cellValue;
    _this.beans = beans;
    _this.eCell = eCell;
    return _this;
  }

  DndSourceComp.prototype.postConstruct = function () {
    var eGui = this.getGui();
    eGui.appendChild(_.createIconNoSpan('rowDrag', this.beans.gridOptionsWrapper, null));
    this.addDragSource();
    this.checkVisibility();
  };

  DndSourceComp.prototype.addDragSource = function () {
    this.addGuiEventListener('dragstart', this.onDragStart.bind(this));
  };

  DndSourceComp.prototype.onDragStart = function (dragEvent) {
    var _this = this;

    var providedOnRowDrag = this.column.getColDef().dndSourceOnRowDrag;

    var isIE = _.isBrowserIE();

    if (!isIE) {
      dragEvent.dataTransfer.setDragImage(this.eCell, 0, 0);
    } // default behaviour is to convert data to json and set into drag component


    var defaultOnRowDrag = function defaultOnRowDrag() {
      try {
        var jsonData = JSON.stringify(_this.rowNode.data);

        if (isIE) {
          dragEvent.dataTransfer.setData('text', jsonData);
        } else {
          dragEvent.dataTransfer.setData('application/json', jsonData);
          dragEvent.dataTransfer.setData('text/plain', jsonData);
        }
      } catch (e) {// if we cannot convert the data to json, then we do not set the type
      }
    };

    if (providedOnRowDrag) {
      providedOnRowDrag({
        rowNode: this.rowNode,
        dragEvent: dragEvent
      });
    } else {
      defaultOnRowDrag();
    }
  };

  DndSourceComp.prototype.checkVisibility = function () {
    var visible = this.column.isDndSource(this.rowNode);
    this.setDisplayed(visible);
  };

  __decorate$z([PostConstruct], DndSourceComp.prototype, "postConstruct", null);

  return DndSourceComp;
}(Component);
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __extends$w = undefined && undefined.__extends || function () {
  var _extendStatics33 = function extendStatics(d, b) {
    _extendStatics33 = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics33(d, b);
  };

  return function (d, b) {
    _extendStatics33(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var __assign = undefined && undefined.__assign || function () {
  __assign = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];

      for (var p in s) {
        if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
      }
    }

    return t;
  };

  return __assign.apply(this, arguments);
};

var CellComp =
/** @class */
function (_super) {
  __extends$w(CellComp, _super);

  function CellComp(scope, beans, column, rowNode, rowComp, autoHeightCell, printLayout) {
    var _this = _super.call(this) || this;

    _this.hasChartRange = false;
    _this.editingCell = false;
    _this.suppressRefreshCell = false;
    _this.scope = null; // every time we go into edit mode, or back again, this gets incremented.
    // it's the components way of dealing with the async nature of framework components,
    // so if a framework component takes a while to be created, we know if the object
    // is still relevant when creating is finished. eg we could click edit / un-edit 20
    // times before the first React edit component comes back - we should discard
    // the first 19.

    _this.cellEditorVersion = 0;
    _this.cellRendererVersion = 0;
    _this.scope = scope;
    _this.beans = beans;
    _this.column = column;
    _this.rowNode = rowNode;
    _this.rowComp = rowComp;
    _this.autoHeightCell = autoHeightCell;
    _this.printLayout = printLayout;

    _this.createGridCellVo();

    _this.rangeSelectionEnabled = _this.beans.rangeController && beans.gridOptionsWrapper.isEnableRangeSelection();
    _this.cellFocused = _this.beans.focusedCellController.isCellFocused(_this.cellPosition);
    _this.firstRightPinned = _this.column.isFirstRightPinned();
    _this.lastLeftPinned = _this.column.isLastLeftPinned();

    if (_this.rangeSelectionEnabled && _this.beans.rangeController) {
      var rangeController = _this.beans.rangeController;
      _this.rangeCount = rangeController.getCellRangeCount(_this.cellPosition);
      _this.hasChartRange = _this.getHasChartRange();
    }

    _this.getValueAndFormat();

    _this.setUsingWrapper();

    _this.chooseCellRenderer();

    _this.setupColSpan();

    _this.rowSpan = _this.column.getRowSpan(_this.rowNode);
    return _this;
  }

  CellComp.prototype.getCreateTemplate = function () {
    var unselectable = !this.beans.gridOptionsWrapper.isEnableCellTextSelection() ? 'unselectable="on"' : '';
    var templateParts = [];
    var col = this.column;
    var width = this.getCellWidth();
    var left = this.modifyLeftForPrintLayout(this.getCellLeft());
    var valueToRender = this.getInitialValueToRender();
    var valueSanitised = _.get(this.column, 'colDef.template', null) ? valueToRender : _.escape(valueToRender);
    this.tooltip = this.getToolTip();

    var tooltipSanitised = _.escape(this.tooltip);

    var colIdSanitised = _.escape(col.getId());

    var wrapperStartTemplate = '';
    var wrapperEndTemplate = '';
    var stylesFromColDef = this.preProcessStylesFromColDef();
    var cssClasses = this.getInitialCssClasses();
    var stylesForRowSpanning = this.getStylesForRowSpanning();

    var colIdxSanitised = _.escape(this.getAriaColumnIndex());

    if (this.usingWrapper) {
      wrapperStartTemplate = "<div ref=\"eCellWrapper\" class=\"ag-cell-wrapper\" role=\"presentation\">\n                <span ref=\"eCellValue\" role=\"gridcell\" aria-colindex=\"" + colIdxSanitised + "\" class=\"ag-cell-value\" " + unselectable + ">";
      wrapperEndTemplate = '</span></div>';
    }

    templateParts.push("<div");
    templateParts.push(" tabindex=\"-1\"");
    templateParts.push(" " + unselectable); // THIS IS FOR IE ONLY so text selection doesn't bubble outside of the grid

    templateParts.push(" role=\"" + (this.usingWrapper ? 'presentation' : 'gridcell') + "\"");

    if (!this.usingWrapper) {
      templateParts.push(" aria-colindex=" + colIdxSanitised);
    }

    templateParts.push(" comp-id=\"" + this.getCompId() + "\" ");
    templateParts.push(" col-id=\"" + colIdSanitised + "\"");
    templateParts.push(" class=\"" + _.escape(cssClasses.join(' ')) + "\"");

    if (this.beans.gridOptionsWrapper.isEnableBrowserTooltips() && _.exists(tooltipSanitised)) {
      templateParts.push("title=\"" + tooltipSanitised + "\"");
    }

    templateParts.push(" style=\"width: " + Number(width) + "px; left: " + Number(left) + "px; " + _.escape(stylesFromColDef) + " " + _.escape(stylesForRowSpanning) + "\" >");
    templateParts.push(wrapperStartTemplate);

    if (_.exists(valueSanitised, true)) {
      templateParts.push(valueSanitised);
    }

    templateParts.push(wrapperEndTemplate);
    templateParts.push("</div>");
    return templateParts.join('');
  };

  CellComp.prototype.getStylesForRowSpanning = function () {
    if (this.rowSpan === 1) {
      return '';
    }

    var singleRowHeight = this.beans.gridOptionsWrapper.getRowHeightAsNumber();
    var totalRowHeight = singleRowHeight * this.rowSpan;
    return "height: " + totalRowHeight + "px; z-index: 1;";
  };

  CellComp.prototype.afterAttached = function () {
    var querySelector = "[comp-id=\"" + this.getCompId() + "\"]";
    var eGui = this.eParentRow.querySelector(querySelector);
    this.setGui(eGui); // all of these have dependencies on the eGui, so only do them after eGui is set

    this.addDomData();
    this.populateTemplate();
    this.createCellRendererInstance(true);
    this.angular1Compile();
    this.refreshHandle();

    if (_.exists(this.tooltip) && !this.beans.gridOptionsWrapper.isEnableBrowserTooltips()) {
      this.beans.tooltipManager.registerTooltip(this);
    }
  };

  CellComp.prototype.onColumnHover = function () {
    var isHovered = this.beans.columnHoverService.isHovered(this.column);

    _.addOrRemoveCssClass(this.getGui(), 'ag-column-hover', isHovered);
  };

  CellComp.prototype.onCellChanged = function (event) {
    var eventImpactsThisCell = event.column === this.column;

    if (eventImpactsThisCell) {
      this.refreshCell({});
    }
  };

  CellComp.prototype.getCellLeft = function () {
    var mostLeftCol;

    if (this.beans.gridOptionsWrapper.isEnableRtl() && this.colsSpanning) {
      mostLeftCol = _.last(this.colsSpanning);
    } else {
      mostLeftCol = this.column;
    }

    return mostLeftCol.getLeft();
  };

  CellComp.prototype.getCellWidth = function () {
    if (!this.colsSpanning) {
      return this.column.getActualWidth();
    }

    return this.colsSpanning.reduce(function (width, col) {
      return width + col.getActualWidth();
    }, 0);
  };

  CellComp.prototype.onFlashCells = function (event) {
    var cellId = this.beans.cellPositionUtils.createId(this.cellPosition);
    var shouldFlash = event.cells[cellId];

    if (shouldFlash) {
      this.animateCell('highlight');
    }
  };

  CellComp.prototype.setupColSpan = function () {
    // if no col span is active, then we don't set it up, as it would be wasteful of CPU
    if (_.missing(this.getComponentHolder().colSpan)) {
      return;
    } // because we are col spanning, a reorder of the cols can change what cols we are spanning over


    this.addDestroyableEventListener(this.beans.eventService, Events.EVENT_DISPLAYED_COLUMNS_CHANGED, this.onDisplayColumnsChanged.bind(this)); // because we are spanning over multiple cols, we check for width any time any cols width changes.
    // this is expensive - really we should be explicitly checking only the cols we are spanning over
    // instead of every col, however it would be tricky code to track the cols we are spanning over, so
    // because hardly anyone will be using colSpan, am favouring this easier way for more maintainable code.

    this.addDestroyableEventListener(this.beans.eventService, Events.EVENT_DISPLAYED_COLUMNS_WIDTH_CHANGED, this.onWidthChanged.bind(this));
    this.colsSpanning = this.getColSpanningList();
  };

  CellComp.prototype.getColSpanningList = function () {
    var colSpan = this.column.getColSpan(this.rowNode);
    var colsSpanning = []; // if just one col, the col span is just the column we are in

    if (colSpan === 1) {
      colsSpanning.push(this.column);
    } else {
      var pointer = this.column;
      var pinned = this.column.getPinned();

      for (var i = 0; pointer && i < colSpan; i++) {
        colsSpanning.push(pointer);
        pointer = this.beans.columnController.getDisplayedColAfter(pointer);

        if (!pointer || _.missing(pointer)) {
          break;
        } // we do not allow col spanning to span outside of pinned areas


        if (pinned !== pointer.getPinned()) {
          break;
        }
      }
    }

    return colsSpanning;
  };

  CellComp.prototype.onDisplayColumnsChanged = function () {
    var colsSpanning = this.getColSpanningList();

    if (!_.compareArrays(this.colsSpanning, colsSpanning)) {
      this.colsSpanning = colsSpanning;
      this.onWidthChanged();
      this.onLeftChanged(); // left changes when doing RTL
    }
  };

  CellComp.prototype.getAriaColumnIndex = function () {
    var allColumns = this.beans.columnController.getAllDisplayedColumns();
    return (allColumns.indexOf(this.column) + 1).toString();
  };

  CellComp.prototype.refreshAriaIndex = function () {
    var colIdx = this.getAriaColumnIndex();
    var el = this.usingWrapper ? this.eCellValue : this.getGui();
    el.setAttribute('aria-colindex', colIdx);
  };

  CellComp.prototype.getInitialCssClasses = function () {
    var cssClasses = ["ag-cell", "ag-cell-not-inline-editing"]; // if we are putting the cell into a dummy container, to work out it's height,
    // then we don't put the height css in, as we want cell to fit height in that case.

    if (!this.autoHeightCell) {
      cssClasses.push('ag-cell-with-height');
    }

    var doingFocusCss = !this.beans.gridOptionsWrapper.isSuppressCellSelection();

    if (doingFocusCss && this.cellFocused) {
      // otherwise the class depends on the focus state
      cssClasses.push('ag-cell-focus');
    }

    if (this.firstRightPinned) {
      cssClasses.push('ag-cell-first-right-pinned');
    }

    if (this.lastLeftPinned) {
      cssClasses.push('ag-cell-last-left-pinned');
    }

    if (this.beans.columnHoverService.isHovered(this.column)) {
      cssClasses.push('ag-column-hover');
    }

    _.pushAll(cssClasses, this.preProcessClassesFromColDef());

    _.pushAll(cssClasses, this.preProcessCellClassRules());

    _.pushAll(cssClasses, this.getInitialRangeClasses()); // if using the wrapper, this class goes on the wrapper instead


    if (!this.usingWrapper) {
      cssClasses.push('ag-cell-value');
    }

    return cssClasses;
  };

  CellComp.prototype.getInitialValueToRender = function () {
    // if using a cellRenderer, then render the html from the cell renderer if it exists
    if (this.usingCellRenderer) {
      if (typeof this.cellRendererGui === 'string') {
        return this.cellRendererGui;
      }

      return '';
    }

    var colDef = this.getComponentHolder();

    if (colDef.template) {
      // template is really only used for angular 1 - as people using ng1 are used to providing templates with
      // bindings in it. in ng2, people will hopefully want to provide components, not templates.
      return colDef.template;
    }

    if (colDef.templateUrl) {
      // likewise for templateUrl - it's for ng1 really - when we move away from ng1, we can take these out.
      // niall was pro angular 1 when writing template and templateUrl, if writing from scratch now, would
      // not do these, but would follow a pattern that was friendly towards components, not templates.
      var template = this.beans.templateService.getTemplate(colDef.templateUrl, this.refreshCell.bind(this, true));
      return template || '';
    }

    return this.getValueToUse();
  };

  CellComp.prototype.getRenderedRow = function () {
    return this.rowComp;
  };

  CellComp.prototype.isSuppressNavigable = function () {
    return this.column.isSuppressNavigable(this.rowNode);
  };

  CellComp.prototype.getCellRenderer = function () {
    return this.cellRenderer;
  };

  CellComp.prototype.getCellEditor = function () {
    return this.cellEditor;
  }; // + stop editing {forceRefresh: true, suppressFlash: true}
  // + event cellChanged {}
  // + cellRenderer.params.refresh() {} -> method passes 'as is' to the cellRenderer, so params could be anything
  // + rowComp: event dataChanged {animate: update, newData: !update}
  // + rowComp: api refreshCells() {animate: true/false}
  // + rowRenderer: api softRefreshView() {}


  CellComp.prototype.refreshCell = function (params) {
    // if we are in the middle of 'stopEditing', then we don't refresh here, as refresh gets called explicitly
    if (this.suppressRefreshCell || this.editingCell) {
      return;
    }

    var colDef = this.getComponentHolder();
    var newData = params && params.newData;
    var suppressFlash = params && params.suppressFlash || colDef.suppressCellFlash;
    var forceRefresh = params && params.forceRefresh;
    var oldValue = this.value; // get latest value without invoking the value formatter as we may not be updating the cell

    this.value = this.getValue(); // for simple values only (not objects), see if the value is the same, and if it is, skip the refresh.
    // when never allow skipping after an edit, as after editing, we need to put the GUI back to the way
    // if was before the edit.

    var valuesDifferent = !this.valuesAreEqual(oldValue, this.value);
    var dataNeedsUpdating = forceRefresh || valuesDifferent;

    if (dataNeedsUpdating) {
      // now invoke the value formatter as we are going to update cell
      this.valueFormatted = this.beans.valueFormatterService.formatValue(this.column, this.rowNode, this.scope, this.value); // if it's 'new data', then we don't refresh the cellRenderer, even if refresh method is available.
      // this is because if the whole data is new (ie we are showing stock price 'BBA' now and not 'SSD')
      // then we are not showing a movement in the stock price, rather we are showing different stock.

      var cellRendererRefreshed = newData ? false : this.attemptCellRendererRefresh(); // we do the replace if not doing refresh, or if refresh was unsuccessful.
      // the refresh can be unsuccessful if we are using a framework (eg ng2 or react) and the framework
      // wrapper has the refresh method, but the underlying component doesn't

      if (!cellRendererRefreshed) {
        this.replaceContentsAfterRefresh();
      } // we don't want to flash the cells when processing a filter change, as otherwise the UI would
      // be to busy. see comment in FilterManager with regards processingFilterChange


      var processingFilterChange = this.beans.filterManager.isSuppressFlashingCellsBecauseFiltering();
      var flashCell = !suppressFlash && !processingFilterChange && (this.beans.gridOptionsWrapper.isEnableCellChangeFlash() || colDef.enableCellChangeFlash);

      if (flashCell) {
        this.flashCell();
      } // need to check rules. note, we ignore colDef classes and styles, these are assumed to be static


      this.postProcessStylesFromColDef();
      this.postProcessClassesFromColDef();
    } // we can't readily determine if the data in an angularjs template has changed, so here we just update
    // and recompile (if applicable)


    this.updateAngular1ScopeAndCompile();
    this.refreshToolTip(); // we do cellClassRules even if the value has not changed, so that users who have rules that
    // look at other parts of the row (where the other part of the row might of changed) will work.

    this.postProcessCellClassRules();
  }; // user can also call this via API


  CellComp.prototype.flashCell = function () {
    this.animateCell('data-changed');
  };

  CellComp.prototype.animateCell = function (cssName) {
    var fullName = "ag-cell-" + cssName;
    var animationFullName = "ag-cell-" + cssName + "-animation";
    var element = this.getGui(); // we want to highlight the cells, without any animation

    _.addCssClass(element, fullName);

    _.removeCssClass(element, animationFullName); // then once that is applied, we remove the highlight with animation


    window.setTimeout(function () {
      _.removeCssClass(element, fullName);

      _.addCssClass(element, animationFullName);

      window.setTimeout(function () {
        // and then to leave things as we got them, we remove the animation
        _.removeCssClass(element, animationFullName);
      }, 1000);
    }, 500);
  };

  CellComp.prototype.replaceContentsAfterRefresh = function () {
    // otherwise we rip out the cell and replace it
    _.clearElement(this.eParentOfValue); // remove old renderer component if it exists


    if (this.cellRenderer && this.cellRenderer.destroy) {
      this.cellRenderer.destroy();
    }

    this.cellRenderer = null;
    this.cellRendererGui = null; // populate

    this.putDataIntoCellAfterRefresh();
    this.updateAngular1ScopeAndCompile();
  };

  CellComp.prototype.updateAngular1ScopeAndCompile = function () {
    if (this.beans.gridOptionsWrapper.isAngularCompileRows() && this.scope) {
      this.scope.data = __assign({}, this.rowNode.data);
      this.angular1Compile();
    }
  };

  CellComp.prototype.angular1Compile = function () {
    // if angular compiling, then need to also compile the cell again (angular compiling sucks, please wait...)
    if (this.beans.gridOptionsWrapper.isAngularCompileRows()) {
      var eGui = this.getGui(); // only compile the node if it hasn't already been done
      // this prevents "orphaned" node leaks

      if (!eGui.classList.contains('ng-scope') || eGui.childElementCount === 0) {
        var compiledElement_1 = this.beans.$compile(eGui)(this.scope);
        this.addDestroyFunc(function () {
          return compiledElement_1.remove();
        });
      }
    }
  };

  CellComp.prototype.postProcessStylesFromColDef = function () {
    var stylesToUse = this.processStylesFromColDef();

    if (stylesToUse) {
      _.addStylesToElement(this.getGui(), stylesToUse);
    }
  };

  CellComp.prototype.preProcessStylesFromColDef = function () {
    return _.cssStyleObjectToMarkup(this.processStylesFromColDef());
  };

  CellComp.prototype.processStylesFromColDef = function () {
    var colDef = this.getComponentHolder();

    if (colDef.cellStyle) {
      var cssToUse = void 0;

      if (typeof colDef.cellStyle === 'function') {
        var cellStyleParams = {
          value: this.value,
          data: this.rowNode.data,
          node: this.rowNode,
          colDef: colDef,
          column: this.column,
          $scope: this.scope,
          context: this.beans.gridOptionsWrapper.getContext(),
          api: this.beans.gridOptionsWrapper.getApi()
        };
        var cellStyleFunc = colDef.cellStyle;
        cssToUse = cellStyleFunc(cellStyleParams);
      } else {
        cssToUse = colDef.cellStyle;
      }

      return cssToUse;
    }
  };

  CellComp.prototype.postProcessClassesFromColDef = function () {
    var _this = this;

    this.processClassesFromColDef(function (className) {
      return _.addCssClass(_this.getGui(), className);
    });
  };

  CellComp.prototype.preProcessClassesFromColDef = function () {
    var res = [];
    this.processClassesFromColDef(function (className) {
      return res.push(className);
    });
    return res;
  };

  CellComp.prototype.processClassesFromColDef = function (onApplicableClass) {
    var colDef = this.getComponentHolder();
    this.beans.stylingService.processStaticCellClasses(colDef, {
      value: this.value,
      data: this.rowNode.data,
      node: this.rowNode,
      colDef: colDef,
      rowIndex: this.rowNode.rowIndex,
      $scope: this.scope,
      api: this.beans.gridOptionsWrapper.getApi(),
      columnApi: this.beans.gridOptionsWrapper.getColumnApi(),
      context: this.beans.gridOptionsWrapper.getContext()
    }, onApplicableClass);
  };

  CellComp.prototype.putDataIntoCellAfterRefresh = function () {
    // template gets preference, then cellRenderer, then do it ourselves
    var colDef = this.getComponentHolder();

    if (colDef.template) {
      // template is really only used for angular 1 - as people using ng1 are used to providing templates with
      // bindings in it. in ng2, people will hopefully want to provide components, not templates.
      this.eParentOfValue.innerHTML = colDef.template;
    } else if (colDef.templateUrl) {
      // likewise for templateUrl - it's for ng1 really - when we move away from ng1, we can take these out.
      // niall was pro angular 1 when writing template and templateUrl, if writing from scratch now, would
      // not do these, but would follow a pattern that was friendly towards components, not templates.
      var template = this.beans.templateService.getTemplate(colDef.templateUrl, this.refreshCell.bind(this, true));

      if (template) {
        this.eParentOfValue.innerHTML = template;
      }
    } else {
      // we can switch from using a cell renderer back to the default if a user
      // is using cellRendererSelect
      this.chooseCellRenderer();

      if (this.usingCellRenderer) {
        this.createCellRendererInstance();
      } else {
        var valueToUse = this.getValueToUse();

        if (valueToUse != null) {
          this.eParentOfValue.innerHTML = _.escape(valueToUse);
        }
      }
    }
  };

  CellComp.prototype.attemptCellRendererRefresh = function () {
    if (_.missing(this.cellRenderer) || !this.cellRenderer || _.missing(this.cellRenderer.refresh)) {
      return false;
    } // if the cell renderer has a refresh method, we call this instead of doing a refresh


    var params = this.createCellRendererParams(); // take any custom params off of the user

    var finalParams = this.beans.userComponentFactory.createFinalParams(this.getComponentHolder(), this.cellRendererType, params);
    var result = this.cellRenderer.refresh(finalParams); // NOTE on undefined: previous version of the cellRenderer.refresh() interface
    // returned nothing, if the method existed, we assumed it refreshed. so for
    // backwards compatibility, we assume if method exists and returns nothing,
    // that it was successful.

    return result === true || result === undefined;
  };

  CellComp.prototype.refreshToolTip = function () {
    var newTooltip = this.getToolTip();

    if (this.tooltip === newTooltip) {
      return;
    }

    var hasNewTooltip = _.exists(newTooltip);

    if (hasNewTooltip && this.tooltip === newTooltip.toString()) {
      return;
    }

    var hadTooltip = _.exists(this.tooltip);

    this.tooltip = newTooltip;

    if (this.beans.gridOptionsWrapper.isEnableBrowserTooltips()) {
      if (hasNewTooltip) {
        this.eParentOfValue.setAttribute('title', this.tooltip);
      } else {
        this.eParentOfValue.removeAttribute('title');
      }
    } else {
      if (hadTooltip) {
        if (!hasNewTooltip) {
          this.beans.tooltipManager.unregisterTooltip(this);
        }
      } else if (hasNewTooltip) {
        this.beans.tooltipManager.registerTooltip(this);
      }
    }
  };

  CellComp.prototype.valuesAreEqual = function (val1, val2) {
    // if the user provided an equals method, use that, otherwise do simple comparison
    var colDef = this.getComponentHolder();
    var equalsMethod = colDef ? colDef.equals : null;
    return equalsMethod ? equalsMethod(val1, val2) : val1 === val2;
  };

  CellComp.prototype.getToolTip = function () {
    var colDef = this.getComponentHolder();
    var data = this.rowNode.data;

    if (colDef.tooltipField && _.exists(data)) {
      return _.getValueUsingField(data, colDef.tooltipField, this.column.isTooltipFieldContainsDots());
    }

    var valueGetter = colDef.tooltipValueGetter || colDef.tooltip;

    if (valueGetter) {
      return valueGetter({
        api: this.beans.gridOptionsWrapper.getApi(),
        columnApi: this.beans.gridOptionsWrapper.getColumnApi(),
        colDef: colDef,
        column: this.getColumn(),
        context: this.beans.gridOptionsWrapper.getContext(),
        value: this.value,
        valueFormatted: this.valueFormatted,
        rowIndex: this.cellPosition.rowIndex,
        node: this.rowNode,
        data: this.rowNode.data,
        $scope: this.scope
      });
    }

    return null;
  };

  CellComp.prototype.getTooltipText = function (escape) {
    if (escape === void 0) {
      escape = true;
    }

    return escape ? _.escape(this.tooltip) : this.tooltip;
  };

  CellComp.prototype.processCellClassRules = function (onApplicableClass, onNotApplicableClass) {
    var colDef = this.getComponentHolder();
    this.beans.stylingService.processClassRules(colDef.cellClassRules, {
      value: this.value,
      data: this.rowNode.data,
      node: this.rowNode,
      colDef: colDef,
      rowIndex: this.cellPosition.rowIndex,
      api: this.beans.gridOptionsWrapper.getApi(),
      columnApi: this.beans.gridOptionsWrapper.getColumnApi(),
      $scope: this.scope,
      context: this.beans.gridOptionsWrapper.getContext()
    }, onApplicableClass, onNotApplicableClass);
  };

  CellComp.prototype.postProcessCellClassRules = function () {
    var _this = this;

    this.processCellClassRules(function (className) {
      return _.addCssClass(_this.getGui(), className);
    }, function (className) {
      return _.removeCssClass(_this.getGui(), className);
    });
  };

  CellComp.prototype.preProcessCellClassRules = function () {
    var res = [];
    this.processCellClassRules(function (className) {
      return res.push(className);
    }, function (_) {// not catered for, if creating, no need
      // to remove class as it was never there
    });
    return res;
  }; // a wrapper is used when we are putting a selection checkbox in the cell with the value


  CellComp.prototype.setUsingWrapper = function () {
    var colDef = this.getComponentHolder(); // never allow selection or dragging on pinned rows

    if (this.rowNode.rowPinned) {
      this.usingWrapper = false;
      this.includeSelectionComponent = false;
      this.includeRowDraggingComponent = false;
      this.includeDndSourceComponent = false;
      return;
    }

    var cbSelectionIsFunc = typeof colDef.checkboxSelection === 'function';
    var rowDraggableIsFunc = typeof colDef.rowDrag === 'function';
    var dndSourceIsFunc = typeof colDef.dndSource === 'function';
    this.includeSelectionComponent = cbSelectionIsFunc || colDef.checkboxSelection === true;
    this.includeRowDraggingComponent = rowDraggableIsFunc || colDef.rowDrag === true;
    this.includeDndSourceComponent = dndSourceIsFunc || colDef.dndSource === true;
    var enableTextSelection = this.beans.gridOptionsWrapper.isEnableCellTextSelection();
    this.usingWrapper = enableTextSelection || this.includeRowDraggingComponent || this.includeSelectionComponent || this.includeDndSourceComponent;
  };

  CellComp.prototype.chooseCellRenderer = function () {
    // template gets preference, then cellRenderer, then do it ourselves
    var colDef = this.getComponentHolder(); // templates are for ng1, ideally we wouldn't have these, they are ng1 support
    // inside the core which is bad

    if (colDef.template || colDef.templateUrl) {
      this.usingCellRenderer = false;
      return;
    }

    var params = this.createCellRendererParams();
    var cellRenderer = this.beans.userComponentFactory.lookupComponentClassDef(colDef, 'cellRenderer', params);
    var pinnedRowCellRenderer = this.beans.userComponentFactory.lookupComponentClassDef(colDef, 'pinnedRowCellRenderer', params);

    if (pinnedRowCellRenderer && this.rowNode.rowPinned) {
      this.cellRendererType = CellComp.CELL_RENDERER_TYPE_PINNED;
      this.usingCellRenderer = true;
    } else if (cellRenderer) {
      this.cellRendererType = CellComp.CELL_RENDERER_TYPE_NORMAL;
      this.usingCellRenderer = true;
    } else {
      this.usingCellRenderer = false;
    }
  };

  CellComp.prototype.createCellRendererInstance = function (useTaskService) {
    var _this = this;

    if (useTaskService === void 0) {
      useTaskService = false;
    }

    if (!this.usingCellRenderer) {
      return;
    } // never use task service if angularCompileRows=true, as that assume the cell renderers
    // are finished when the row is created. also we never use it if animation frame service
    // is turned off.
    // and lastly we never use it if doing auto-height, as the auto-height service checks the
    // row height directly after the cell is created, it doesn't wait around for the tasks to complete


    var angularCompileRows = this.beans.gridOptionsWrapper.isAngularCompileRows();
    var suppressAnimationFrame = this.beans.gridOptionsWrapper.isSuppressAnimationFrame();

    if (angularCompileRows || suppressAnimationFrame || this.autoHeightCell) {
      useTaskService = false;
    }

    var params = this.createCellRendererParams();
    this.cellRendererVersion++;
    var callback = this.afterCellRendererCreated.bind(this, this.cellRendererVersion);
    var cellRendererTypeNormal = this.cellRendererType === CellComp.CELL_RENDERER_TYPE_NORMAL;

    this.createCellRendererFunc = function () {
      _this.createCellRendererFunc = null; // this can return null in the event that the user has switched from a renderer component to nothing, for example
      // when using a cellRendererSelect to return a component or null depending on row data etc

      var componentPromise;

      if (cellRendererTypeNormal) {
        componentPromise = _this.beans.userComponentFactory.newCellRenderer(_this.getComponentHolder(), params);
      } else {
        componentPromise = _this.beans.userComponentFactory.newPinnedRowCellRenderer(_this.getComponentHolder(), params);
      }

      if (componentPromise) {
        componentPromise.then(callback);
      }
    };

    if (useTaskService) {
      this.beans.taskQueue.createTask(this.createCellRendererFunc, this.rowNode.rowIndex, 'createTasksP2');
    } else {
      this.createCellRendererFunc();
    }
  };

  CellComp.prototype.afterCellRendererCreated = function (cellRendererVersion, cellRenderer) {
    // see if daemon
    if (!this.isAlive() || cellRendererVersion !== this.cellRendererVersion) {
      if (cellRenderer.destroy) {
        cellRenderer.destroy();
      }

      return;
    }

    this.cellRenderer = cellRenderer;
    this.cellRendererGui = this.cellRenderer.getGui();

    if (_.missing(this.cellRendererGui)) {
      return;
    } // if async components, then it's possible the user started editing since
    // this call was made


    if (!this.editingCell) {
      this.eParentOfValue.appendChild(this.cellRendererGui);
    }
  };

  CellComp.prototype.createCellRendererParams = function () {
    var _this = this;

    return {
      value: this.value,
      valueFormatted: this.valueFormatted,
      getValue: this.getValue.bind(this),
      setValue: function setValue(value) {
        return _this.beans.valueService.setValue(_this.rowNode, _this.column, value);
      },
      formatValue: this.formatValue.bind(this),
      data: this.rowNode.data,
      node: this.rowNode,
      colDef: this.getComponentHolder(),
      column: this.column,
      $scope: this.scope,
      rowIndex: this.cellPosition.rowIndex,
      api: this.beans.gridOptionsWrapper.getApi(),
      columnApi: this.beans.gridOptionsWrapper.getColumnApi(),
      context: this.beans.gridOptionsWrapper.getContext(),
      refreshCell: this.refreshCell.bind(this),
      eGridCell: this.getGui(),
      eParentOfValue: this.eParentOfValue,
      // these bits are not documented anywhere, so we could drop them?
      // it was in the olden days to allow user to register for when rendered
      // row was removed (the row comp was removed), however now that the user
      // can provide components for cells, the destroy method gets call when this
      // happens so no longer need to fire event.
      addRowCompListener: this.rowComp ? this.rowComp.addEventListener.bind(this.rowComp) : null,
      addRenderedRowListener: function addRenderedRowListener(eventType, listener) {
        console.warn('ag-Grid: since ag-Grid .v11, params.addRenderedRowListener() is now params.addRowCompListener()');

        if (_this.rowComp) {
          _this.rowComp.addEventListener(eventType, listener);
        }
      }
    };
  };

  CellComp.prototype.formatValue = function (value) {
    var valueFormatted = this.beans.valueFormatterService.formatValue(this.column, this.rowNode, this.scope, value);
    return valueFormatted != null ? valueFormatted : value;
  };

  CellComp.prototype.getValueToUse = function () {
    return this.valueFormatted != null ? this.valueFormatted : this.value;
  };

  CellComp.prototype.getValueAndFormat = function () {
    this.value = this.getValue();
    this.valueFormatted = this.beans.valueFormatterService.formatValue(this.column, this.rowNode, this.scope, this.value);
  };

  CellComp.prototype.getValue = function () {
    // if we don't check this, then the grid will render leaf groups as open even if we are not
    // allowing the user to open leaf groups. confused? remember for pivot mode we don't allow
    // opening leaf groups, so we have to force leafGroups to be closed in case the user expanded
    // them via the API, or user user expanded them in the UI before turning on pivot mode
    var lockedClosedGroup = this.rowNode.leafGroup && this.beans.columnController.isPivotMode();
    var isOpenGroup = this.rowNode.group && this.rowNode.expanded && !this.rowNode.footer && !lockedClosedGroup; // are we showing group footers

    var groupFootersEnabled = this.beans.gridOptionsWrapper.isGroupIncludeFooter(); // if doing footers, we normally don't show agg data at group level when group is open

    var groupAlwaysShowAggData = this.beans.gridOptionsWrapper.isGroupSuppressBlankHeader(); // if doing grouping and footers, we don't want to include the agg value
    // in the header when the group is open

    var ignoreAggData = isOpenGroup && groupFootersEnabled && !groupAlwaysShowAggData;
    return this.beans.valueService.getValue(this.column, this.rowNode, false, ignoreAggData);
  };

  CellComp.prototype.onMouseEvent = function (eventName, mouseEvent) {
    if (_.isStopPropagationForAgGrid(mouseEvent)) {
      return;
    }

    switch (eventName) {
      case 'click':
        this.onCellClicked(mouseEvent);
        break;

      case 'mousedown':
        this.onMouseDown(mouseEvent);
        break;

      case 'dblclick':
        this.onCellDoubleClicked(mouseEvent);
        break;

      case 'mouseout':
        this.onMouseOut(mouseEvent);
        break;

      case 'mouseover':
        this.onMouseOver(mouseEvent);
        break;
    }
  };

  CellComp.prototype.dispatchCellContextMenuEvent = function (event) {
    var colDef = this.getComponentHolder();
    var cellContextMenuEvent = this.createEvent(event, Events.EVENT_CELL_CONTEXT_MENU);
    this.beans.eventService.dispatchEvent(cellContextMenuEvent);

    if (colDef.onCellContextMenu) {
      // to make the callback async, do in a timeout
      window.setTimeout(function () {
        return colDef.onCellContextMenu(cellContextMenuEvent);
      }, 0);
    }
  };

  CellComp.prototype.createEvent = function (domEvent, eventType) {
    var event = {
      node: this.rowNode,
      data: this.rowNode.data,
      value: this.value,
      column: this.column,
      colDef: this.getComponentHolder(),
      context: this.beans.gridOptionsWrapper.getContext(),
      api: this.beans.gridApi,
      columnApi: this.beans.columnApi,
      rowPinned: this.rowNode.rowPinned,
      event: domEvent,
      type: eventType,
      rowIndex: this.rowNode.rowIndex
    }; // because we are hacking in $scope for angular 1, we have to de-reference

    if (this.scope) {
      event.$scope = this.scope;
    }

    return event;
  };

  CellComp.prototype.onMouseOut = function (mouseEvent) {
    var cellMouseOutEvent = this.createEvent(mouseEvent, Events.EVENT_CELL_MOUSE_OUT);
    this.beans.eventService.dispatchEvent(cellMouseOutEvent);
    this.beans.columnHoverService.clearMouseOver();
  };

  CellComp.prototype.onMouseOver = function (mouseEvent) {
    var cellMouseOverEvent = this.createEvent(mouseEvent, Events.EVENT_CELL_MOUSE_OVER);
    this.beans.eventService.dispatchEvent(cellMouseOverEvent);
    this.beans.columnHoverService.setMouseOver([this.column]);
  };

  CellComp.prototype.onCellDoubleClicked = function (mouseEvent) {
    var colDef = this.getComponentHolder(); // always dispatch event to eventService

    var cellDoubleClickedEvent = this.createEvent(mouseEvent, Events.EVENT_CELL_DOUBLE_CLICKED);
    this.beans.eventService.dispatchEvent(cellDoubleClickedEvent); // check if colDef also wants to handle event

    if (typeof colDef.onCellDoubleClicked === 'function') {
      // to make the callback async, do in a timeout
      window.setTimeout(function () {
        return colDef.onCellDoubleClicked(cellDoubleClickedEvent);
      }, 0);
    }

    var editOnDoubleClick = !this.beans.gridOptionsWrapper.isSingleClickEdit() && !this.beans.gridOptionsWrapper.isSuppressClickEdit();

    if (editOnDoubleClick) {
      this.startRowOrCellEdit();
    }
  }; // called by rowRenderer when user navigates via tab key


  CellComp.prototype.startRowOrCellEdit = function (keyPress, charPress) {
    if (this.beans.gridOptionsWrapper.isFullRowEdit()) {
      this.rowComp.startRowEditing(keyPress, charPress, this);
    } else {
      this.startEditingIfEnabled(keyPress, charPress, true);
    }
  };

  CellComp.prototype.isCellEditable = function () {
    return this.column.isCellEditable(this.rowNode);
  }; // either called internally if single cell editing, or called by rowRenderer if row editing


  CellComp.prototype.startEditingIfEnabled = function (keyPress, charPress, cellStartedEdit) {
    if (keyPress === void 0) {
      keyPress = null;
    }

    if (charPress === void 0) {
      charPress = null;
    }

    if (cellStartedEdit === void 0) {
      cellStartedEdit = false;
    } // don't do it if not editable


    if (!this.isCellEditable()) {
      return;
    } // don't do it if already editing


    if (this.editingCell) {
      return;
    }

    this.editingCell = true;
    this.cellEditorVersion++;
    var callback = this.afterCellEditorCreated.bind(this, this.cellEditorVersion);
    var params = this.createCellEditorParams(keyPress, charPress, cellStartedEdit);
    this.createCellEditor(params).then(callback); // if we don't do this, and editor component is async, then there will be a period
    // when the component isn't present and keyboard navigation won't work - so example
    // of user hitting tab quickly (more quickly than renderers getting created) won't work

    var cellEditorAsync = _.missing(this.cellEditor);

    if (cellEditorAsync && cellStartedEdit) {
      this.focusCell(true);
    }
  };

  CellComp.prototype.createCellEditor = function (params) {
    var _this = this;

    var cellEditorPromise = this.beans.userComponentFactory.newCellEditor(this.column.getColDef(), params);
    return cellEditorPromise.map(function (cellEditor) {
      var isPopup = cellEditor.isPopup && cellEditor.isPopup();

      if (!isPopup) {
        return cellEditor;
      }

      if (_this.beans.gridOptionsWrapper.isFullRowEdit()) {
        console.warn('ag-Grid: popup cellEditor does not work with fullRowEdit - you cannot use them both ' + '- either turn off fullRowEdit, or stop using popup editors.');
      } // if a popup, then we wrap in a popup editor and return the popup


      var popupEditorWrapper = new PopupEditorWrapper(cellEditor);

      _this.beans.context.wireBean(popupEditorWrapper);

      popupEditorWrapper.init(params);
      return popupEditorWrapper;
    });
  };

  CellComp.prototype.afterCellEditorCreated = function (cellEditorVersion, cellEditor) {
    // if editingCell=false, means user cancelled the editor before component was ready.
    // if versionMismatch, then user cancelled the edit, then started the edit again, and this
    //   is the first editor which is now stale.
    var versionMismatch = cellEditorVersion !== this.cellEditorVersion;

    if (versionMismatch || !this.editingCell) {
      if (cellEditor.destroy) {
        cellEditor.destroy();
      }

      return;
    }

    if (cellEditor.isCancelBeforeStart && cellEditor.isCancelBeforeStart()) {
      if (cellEditor.destroy) {
        cellEditor.destroy();
      }

      this.editingCell = false;
      return;
    }

    if (!cellEditor.getGui) {
      console.warn("ag-Grid: cellEditor for column " + this.column.getId() + " is missing getGui() method"); // no getGui, for React guys, see if they attached a react component directly

      if (cellEditor.render) {
        console.warn("ag-Grid: we found 'render' on the component, are you trying to set a React renderer but added it as colDef.cellEditor instead of colDef.cellEditorFmk?");
      }

      if (cellEditor.destroy) {
        cellEditor.destroy();
      }

      this.editingCell = false;
      return;
    }

    this.cellEditor = cellEditor;
    this.cellEditorInPopup = cellEditor.isPopup !== undefined && cellEditor.isPopup();
    this.setInlineEditingClass();

    if (this.cellEditorInPopup) {
      this.addPopupCellEditor();
    } else {
      this.addInCellEditor();
    }

    if (cellEditor.afterGuiAttached) {
      cellEditor.afterGuiAttached();
    }

    var event = this.createEvent(null, Events.EVENT_CELL_EDITING_STARTED);
    this.beans.eventService.dispatchEvent(event);
  };

  CellComp.prototype.addInCellEditor = function () {
    _.clearElement(this.getGui());

    if (this.cellEditor) {
      this.getGui().appendChild(this.cellEditor.getGui());
    }

    this.angular1Compile();
  };

  CellComp.prototype.addPopupCellEditor = function () {
    var _this = this;

    var ePopupGui = this.cellEditor ? this.cellEditor.getGui() : null;
    var useModelPopup = this.beans.gridOptionsWrapper.isStopEditingWhenGridLosesFocus();
    this.hideEditorPopup = this.beans.popupService.addPopup(useModelPopup, ePopupGui, true, // callback for when popup disappears
    function () {
      _this.onPopupEditorClosed();
    });
    this.beans.popupService.positionPopupOverComponent({
      column: this.column,
      rowNode: this.rowNode,
      type: 'popupCellEditor',
      eventSource: this.getGui(),
      ePopup: ePopupGui,
      keepWithinBounds: true
    });
    this.angular1Compile();
  };

  CellComp.prototype.onPopupEditorClosed = function () {
    // we only call stopEditing if we are editing, as
    // it's possible the popup called 'stop editing'
    // before this, eg if 'enter key' was pressed on
    // the editor.
    if (this.editingCell) {
      // note: this only happens when use clicks outside of the grid. if use clicks on another
      // cell, then the editing will have already stopped on this cell
      this.stopRowOrCellEdit(); // we only focus cell again if this cell is still focused. it is possible
      // it is not focused if the user cancelled the edit by clicking on another
      // cell outside of this one

      if (this.beans.focusedCellController.isCellFocused(this.cellPosition)) {
        this.focusCell(true);
      }
    }
  }; // if we are editing inline, then we don't have the padding in the cell (set in the themes)
  // to allow the text editor full access to the entire cell


  CellComp.prototype.setInlineEditingClass = function () {
    if (!this.isAlive()) {
      return;
    } // ag-cell-inline-editing - appears when user is inline editing
    // ag-cell-not-inline-editing - appears when user is no inline editing
    // ag-cell-popup-editing - appears when user is editing cell in popup (appears on the cell, not on the popup)
    // note: one of {ag-cell-inline-editing, ag-cell-not-inline-editing} is always present, they toggle.
    //       however {ag-cell-popup-editing} shows when popup, so you have both {ag-cell-popup-editing}
    //       and {ag-cell-not-inline-editing} showing at the same time.


    var editingInline = this.editingCell && !this.cellEditorInPopup;
    var popupEditorShowing = this.editingCell && this.cellEditorInPopup;

    _.addOrRemoveCssClass(this.getGui(), "ag-cell-inline-editing", editingInline);

    _.addOrRemoveCssClass(this.getGui(), "ag-cell-not-inline-editing", !editingInline);

    _.addOrRemoveCssClass(this.getGui(), "ag-cell-popup-editing", popupEditorShowing);

    _.addOrRemoveCssClass(this.getGui().parentNode, "ag-row-inline-editing", editingInline);

    _.addOrRemoveCssClass(this.getGui().parentNode, "ag-row-not-inline-editing", !editingInline);
  };

  CellComp.prototype.createCellEditorParams = function (keyPress, charPress, cellStartedEdit) {
    return {
      value: this.getValue(),
      keyPress: keyPress,
      charPress: charPress,
      column: this.column,
      colDef: this.column.getColDef(),
      rowIndex: this.cellPosition.rowIndex,
      node: this.rowNode,
      data: this.rowNode.data,
      api: this.beans.gridOptionsWrapper.getApi(),
      cellStartedEdit: cellStartedEdit,
      columnApi: this.beans.gridOptionsWrapper.getColumnApi(),
      context: this.beans.gridOptionsWrapper.getContext(),
      $scope: this.scope,
      onKeyDown: this.onKeyDown.bind(this),
      stopEditing: this.stopEditingAndFocus.bind(this),
      eGridCell: this.getGui(),
      parseValue: this.parseValue.bind(this),
      formatValue: this.formatValue.bind(this)
    };
  }; // cell editors call this, when they want to stop for reasons other
  // than what we pick up on. eg selecting from a dropdown ends editing.


  CellComp.prototype.stopEditingAndFocus = function (suppressNavigateAfterEdit) {
    if (suppressNavigateAfterEdit === void 0) {
      suppressNavigateAfterEdit = false;
    }

    this.stopRowOrCellEdit();
    this.focusCell(true);

    if (!suppressNavigateAfterEdit) {
      this.navigateAfterEdit();
    }
  };

  CellComp.prototype.parseValue = function (newValue) {
    var colDef = this.getComponentHolder();
    var params = {
      node: this.rowNode,
      data: this.rowNode.data,
      oldValue: this.value,
      newValue: newValue,
      colDef: colDef,
      column: this.column,
      api: this.beans.gridOptionsWrapper.getApi(),
      columnApi: this.beans.gridOptionsWrapper.getColumnApi(),
      context: this.beans.gridOptionsWrapper.getContext()
    };
    var valueParser = colDef.valueParser;
    return _.exists(valueParser) ? this.beans.expressionService.evaluate(valueParser, params) : newValue;
  };

  CellComp.prototype.focusCell = function (forceBrowserFocus) {
    if (forceBrowserFocus === void 0) {
      forceBrowserFocus = false;
    }

    this.beans.focusedCellController.setFocusedCell(this.cellPosition.rowIndex, this.column, this.rowNode.rowPinned, forceBrowserFocus);
  };

  CellComp.prototype.setFocusInOnEditor = function () {
    if (this.editingCell) {
      if (this.cellEditor && this.cellEditor.focusIn) {
        // if the editor is present, then we just focus it
        this.cellEditor.focusIn();
      } else {
        // if the editor is not present, it means async cell editor (eg React fibre)
        // and we are trying to set focus before the cell editor is present, so we
        // focus the cell instead
        this.focusCell(true);
      }
    }
  };

  CellComp.prototype.isEditing = function () {
    return this.editingCell;
  };

  CellComp.prototype.onKeyDown = function (event) {
    var key = event.which || event.keyCode;

    switch (key) {
      case Constants.KEY_ENTER:
        this.onEnterKeyDown();
        break;

      case Constants.KEY_F2:
        this.onF2KeyDown();
        break;

      case Constants.KEY_ESCAPE:
        this.onEscapeKeyDown();
        break;

      case Constants.KEY_TAB:
        this.onTabKeyDown(event);
        break;

      case Constants.KEY_BACKSPACE:
      case Constants.KEY_DELETE:
        this.onBackspaceOrDeleteKeyPressed(key);
        break;

      case Constants.KEY_DOWN:
      case Constants.KEY_UP:
      case Constants.KEY_RIGHT:
      case Constants.KEY_LEFT:
        this.onNavigationKeyPressed(event, key);
        break;
    }
  };

  CellComp.prototype.setFocusOutOnEditor = function () {
    if (this.editingCell && this.cellEditor && this.cellEditor.focusOut) {
      this.cellEditor.focusOut();
    }
  };

  CellComp.prototype.onNavigationKeyPressed = function (event, key) {
    if (this.editingCell) {
      return;
    }

    if (event.shiftKey && this.rangeSelectionEnabled) {
      this.onShiftRangeSelect(key);
    } else {
      this.beans.rowRenderer.navigateToNextCell(event, key, this.cellPosition, true);
    } // if we don't prevent default, the grid will scroll with the navigation keys


    event.preventDefault();
  };

  CellComp.prototype.onShiftRangeSelect = function (key) {
    if (!this.beans.rangeController) {
      return;
    }

    var endCell = this.beans.rangeController.extendLatestRangeInDirection(key);

    if (endCell) {
      this.beans.rowRenderer.ensureCellVisible(endCell);
    }
  };

  CellComp.prototype.onTabKeyDown = function (event) {
    this.beans.rowRenderer.onTabKeyDown(this, event);
  };

  CellComp.prototype.onBackspaceOrDeleteKeyPressed = function (key) {
    if (!this.editingCell) {
      this.startRowOrCellEdit(key);
    }
  };

  CellComp.prototype.onEnterKeyDown = function () {
    if (this.editingCell || this.rowComp.isEditing()) {
      this.stopEditingAndFocus();
    } else {
      if (this.beans.gridOptionsWrapper.isEnterMovesDown()) {
        this.beans.rowRenderer.navigateToNextCell(null, Constants.KEY_DOWN, this.cellPosition, false);
      } else {
        this.startRowOrCellEdit(Constants.KEY_ENTER);
      }
    }
  };

  CellComp.prototype.navigateAfterEdit = function () {
    var fullRowEdit = this.beans.gridOptionsWrapper.isFullRowEdit();

    if (fullRowEdit) {
      return;
    }

    var enterMovesDownAfterEdit = this.beans.gridOptionsWrapper.isEnterMovesDownAfterEdit();

    if (enterMovesDownAfterEdit) {
      this.beans.rowRenderer.navigateToNextCell(null, Constants.KEY_DOWN, this.cellPosition, false);
    }
  };

  CellComp.prototype.onF2KeyDown = function () {
    if (!this.editingCell) {
      this.startRowOrCellEdit(Constants.KEY_F2);
    }
  };

  CellComp.prototype.onEscapeKeyDown = function () {
    if (this.editingCell) {
      this.stopRowOrCellEdit(true);
      this.focusCell(true);
    }
  };

  CellComp.prototype.onKeyPress = function (event) {
    // check this, in case focus is on a (for example) a text field inside the cell,
    // in which cse we should not be listening for these key pressed
    var eventTarget = _.getTarget(event);

    var eventOnChildComponent = eventTarget !== this.getGui();

    if (eventOnChildComponent || this.editingCell) {
      return;
    }

    var pressedChar = String.fromCharCode(event.charCode);

    if (pressedChar === ' ') {
      this.onSpaceKeyPressed(event);
    } else if (_.isEventFromPrintableCharacter(event)) {
      this.startRowOrCellEdit(null, pressedChar); // if we don't prevent default, then the keypress also gets applied to the text field
      // (at least when doing the default editor), but we need to allow the editor to decide
      // what it wants to do. we only do this IF editing was started - otherwise it messes
      // up when the use is not doing editing, but using rendering with text fields in cellRenderer
      // (as it would block the the user from typing into text fields).

      event.preventDefault();
    }
  };

  CellComp.prototype.onSpaceKeyPressed = function (event) {
    if (!this.editingCell && this.beans.gridOptionsWrapper.isRowSelection()) {
      var selected = this.rowNode.isSelected();
      this.rowNode.setSelected(!selected);
    } // prevent default as space key, by default, moves browser scroll down


    event.preventDefault();
  };

  CellComp.prototype.onMouseDown = function (mouseEvent) {
    // we only need to pass true to focusCell in when the browser is IE
    // and we are trying to focus a cell (has ag-cell class), otherwise
    // we pass false, as we don't want the cell to focus also get the browser
    // focus. if we did, then the cellRenderer could have a text field in it,
    // for example, and as the user clicks on the text field, the text field,
    // the focus doesn't get to the text field, instead to goes to the div
    // behind, making it impossible to select the text field.
    var forceBrowserFocus = false;
    var button = mouseEvent.button,
        ctrlKey = mouseEvent.ctrlKey,
        metaKey = mouseEvent.metaKey,
        shiftKey = mouseEvent.shiftKey,
        target = mouseEvent.target;
    var _a = this.beans,
        eventService = _a.eventService,
        rangeController = _a.rangeController;

    if (rangeController) {
      var cellInRange = rangeController.isCellInAnyRange(this.getCellPosition());

      if (cellInRange && button === 2) {
        return;
      }
    }

    if ((_.isBrowserIE() || _.isBrowserEdge()) && this.getGui().contains(target)) {
      forceBrowserFocus = true;
    }

    if (!shiftKey || rangeController && !rangeController.getCellRanges().length) {
      this.focusCell(forceBrowserFocus);
    } else {
      // if a range is being changed, we need to make sure the focused cell does not change.
      mouseEvent.preventDefault();
    } // if we are clicking on a checkbox, we need to make sure the cell wrapping that checkbox
    // is focused but we don't want to change the range selection, so return here.


    if (_.isElementChildOfClass(target, 'ag-selection-checkbox', 3)) {
      return;
    } // if it's a right click, then if the cell is already in range,
    // don't change the range, however if the cell is not in a range,
    // we set a new range


    var leftMouseButtonClick = _.isLeftClick(mouseEvent);

    if (leftMouseButtonClick && rangeController) {
      var thisCell = this.cellPosition;

      if (shiftKey) {
        rangeController.extendLatestRangeToCell(thisCell);
      } else {
        var ctrlKeyPressed = ctrlKey || metaKey;
        rangeController.setRangeToCell(thisCell, ctrlKeyPressed);
      }
    }

    var cellMouseDownEvent = this.createEvent(mouseEvent, Events.EVENT_CELL_MOUSE_DOWN);
    eventService.dispatchEvent(cellMouseDownEvent);
  }; // returns true if on iPad and this is second 'click' event in 200ms


  CellComp.prototype.isDoubleClickOnIPad = function () {
    if (!_.isIOSUserAgent() || _.isEventSupported('dblclick')) {
      return false;
    }

    var nowMillis = new Date().getTime();
    var res = nowMillis - this.lastIPadMouseClickEvent < 200;
    this.lastIPadMouseClickEvent = nowMillis;
    return res;
  };

  CellComp.prototype.onCellClicked = function (mouseEvent) {
    // iPad doesn't have double click - so we need to mimic it to enable editing for iPad.
    if (this.isDoubleClickOnIPad()) {
      this.onCellDoubleClicked(mouseEvent);
      mouseEvent.preventDefault(); // if we don't do this, then iPad zooms in

      return;
    }

    var cellClickedEvent = this.createEvent(mouseEvent, Events.EVENT_CELL_CLICKED);
    this.beans.eventService.dispatchEvent(cellClickedEvent);
    var colDef = this.getComponentHolder();

    if (colDef.onCellClicked) {
      // to make callback async, do in a timeout
      window.setTimeout(function () {
        return colDef.onCellClicked(cellClickedEvent);
      }, 0);
    }

    var editOnSingleClick = (this.beans.gridOptionsWrapper.isSingleClickEdit() || colDef.singleClickEdit) && !this.beans.gridOptionsWrapper.isSuppressClickEdit();

    if (editOnSingleClick) {
      this.startRowOrCellEdit();
    }
  };

  CellComp.prototype.createGridCellVo = function () {
    this.cellPosition = {
      rowIndex: this.rowNode.rowIndex,
      rowPinned: this.rowNode.rowPinned,
      column: this.column
    };
  };

  CellComp.prototype.getCellPosition = function () {
    return this.cellPosition;
  };

  CellComp.prototype.getParentRow = function () {
    return this.eParentRow;
  };

  CellComp.prototype.setParentRow = function (eParentRow) {
    this.eParentRow = eParentRow;
  };

  CellComp.prototype.getColumn = function () {
    return this.column;
  };

  CellComp.prototype.getComponentHolder = function () {
    return this.column.getColDef();
  };

  CellComp.prototype.detach = function () {
    this.eParentRow.removeChild(this.getGui());
  }; // if the row is also getting destroyed, then we don't need to remove from dom,
  // as the row will also get removed, so no need to take out the cells from the row
  // if the row is going (removing is an expensive operation, so only need to remove
  // the top part)


  CellComp.prototype.destroy = function () {
    _super.prototype.destroy.call(this);

    if (this.createCellRendererFunc) {
      this.beans.taskQueue.cancelTask(this.createCellRendererFunc);
    }

    this.stopEditing();

    if (this.cellRenderer && this.cellRenderer.destroy) {
      this.cellRenderer.destroy();
      this.cellRenderer = null;
    }

    if (this.selectionHandle) {
      this.selectionHandle.destroy();
    }
  };

  CellComp.prototype.onLeftChanged = function () {
    var left = this.modifyLeftForPrintLayout(this.getCellLeft());
    this.getGui().style.left = left + 'px';
    this.refreshAriaIndex();
  };

  CellComp.prototype.modifyLeftForPrintLayout = function (leftPosition) {
    if (!this.printLayout || this.column.getPinned() === Constants.PINNED_LEFT) {
      return leftPosition;
    }

    if (this.column.getPinned() === Constants.PINNED_RIGHT) {
      var leftWidth_1 = this.beans.columnController.getPinnedLeftContainerWidth();
      var bodyWidth = this.beans.columnController.getBodyContainerWidth();
      return leftWidth_1 + bodyWidth + leftPosition;
    } // is in body


    var leftWidth = this.beans.columnController.getPinnedLeftContainerWidth();
    return leftWidth + leftPosition;
  };

  CellComp.prototype.onWidthChanged = function () {
    var width = this.getCellWidth();
    this.getGui().style.width = width + 'px';
  };

  CellComp.prototype.getRangeBorders = function () {
    var _this = this;

    var isRtl = this.beans.gridOptionsWrapper.isEnableRtl();
    var top = false;
    var right = false;
    var bottom = false;
    var left = false;
    var thisCol = this.cellPosition.column;
    var rangeController = this.beans.rangeController;
    var leftCol;
    var rightCol;

    if (isRtl) {
      leftCol = this.beans.columnController.getDisplayedColAfter(thisCol);
      rightCol = this.beans.columnController.getDisplayedColBefore(thisCol);
    } else {
      leftCol = this.beans.columnController.getDisplayedColBefore(thisCol);
      rightCol = this.beans.columnController.getDisplayedColAfter(thisCol);
    }

    var ranges = rangeController.getCellRanges().filter(function (range) {
      return rangeController.isCellInSpecificRange(_this.cellPosition, range);
    }); // this means we are the first column in the grid

    if (!leftCol) {
      left = true;
    } // this means we are the last column in the grid


    if (!rightCol) {
      right = true;
    }

    for (var i = 0; i < ranges.length; i++) {
      if (top && right && bottom && left) {
        break;
      }

      var range = ranges[i];
      var startRow = rangeController.getRangeStartRow(range);
      var endRow = rangeController.getRangeEndRow(range);

      if (!top && this.beans.rowPositionUtils.sameRow(startRow, this.cellPosition)) {
        top = true;
      }

      if (!bottom && this.beans.rowPositionUtils.sameRow(endRow, this.cellPosition)) {
        bottom = true;
      }

      if (!left && range.columns.indexOf(leftCol) < 0) {
        left = true;
      }

      if (!right && range.columns.indexOf(rightCol) < 0) {
        right = true;
      }
    }

    return {
      top: top,
      right: right,
      bottom: bottom,
      left: left
    };
  };

  CellComp.prototype.getInitialRangeClasses = function () {
    var classes = [];

    if (!this.rangeSelectionEnabled || !this.rangeCount) {
      return classes;
    }

    classes.push('ag-cell-range-selected');

    if (this.hasChartRange) {
      classes.push('ag-cell-range-chart');
    }

    var count = Math.min(this.rangeCount, 4);
    classes.push("ag-cell-range-selected-" + count);

    if (this.isSingleCell()) {
      classes.push('ag-cell-range-single-cell');
    }

    if (this.rangeCount > 0) {
      var borders = this.getRangeBorders();

      if (borders.top) {
        classes.push('ag-cell-range-top');
      }

      if (borders.right) {
        classes.push('ag-cell-range-right');
      }

      if (borders.bottom) {
        classes.push('ag-cell-range-bottom');
      }

      if (borders.left) {
        classes.push('ag-cell-range-left');
      }
    }

    if (!!this.selectionHandle) {
      classes.push('ag-cell-range-handle');
    }

    return classes;
  };

  CellComp.prototype.onRowIndexChanged = function () {
    // when index changes, this influences items that need the index, so we update the
    // grid cell so they are working off the new index.
    this.createGridCellVo(); // when the index of the row changes, ie means the cell may have lost or gained focus

    this.onCellFocused(); // check range selection

    this.onRangeSelectionChanged();
  };

  CellComp.prototype.onRangeSelectionChanged = function () {
    var rangeController = this.beans.rangeController;

    if (!rangeController) {
      return;
    }

    var _a = this,
        cellPosition = _a.cellPosition,
        rangeCount = _a.rangeCount;

    var newRangeCount = rangeController.getCellRangeCount(cellPosition);
    var element = this.getGui();

    if (rangeCount !== newRangeCount) {
      _.addOrRemoveCssClass(element, 'ag-cell-range-selected', newRangeCount !== 0);

      _.addOrRemoveCssClass(element, 'ag-cell-range-selected-1', newRangeCount === 1);

      _.addOrRemoveCssClass(element, 'ag-cell-range-selected-2', newRangeCount === 2);

      _.addOrRemoveCssClass(element, 'ag-cell-range-selected-3', newRangeCount === 3);

      _.addOrRemoveCssClass(element, 'ag-cell-range-selected-4', newRangeCount >= 4);

      this.rangeCount = newRangeCount;
    }

    var hasChartRange = this.getHasChartRange();

    if (hasChartRange !== this.hasChartRange) {
      this.hasChartRange = hasChartRange;

      _.addOrRemoveCssClass(element, 'ag-cell-range-chart', this.hasChartRange);
    }

    this.updateRangeBorders();

    _.addOrRemoveCssClass(element, 'ag-cell-range-single-cell', this.isSingleCell());

    this.refreshHandle();
  };

  CellComp.prototype.getHasChartRange = function () {
    var rangeController = this.beans.rangeController;

    if (!this.rangeCount || !rangeController) {
      return false;
    }

    var cellRanges = rangeController.getCellRanges();
    return cellRanges.length > 0 && cellRanges.every(function (range) {
      return _.includes([exports.CellRangeType.DIMENSION, exports.CellRangeType.VALUE], range.type);
    });
  };

  CellComp.prototype.shouldHaveSelectionHandle = function () {
    var _a = this.beans,
        gridOptionsWrapper = _a.gridOptionsWrapper,
        rangeController = _a.rangeController;
    var cellRanges = rangeController.getCellRanges();
    var rangesLen = cellRanges.length;

    if (this.rangeCount < 1 || rangesLen < 1) {
      return false;
    }

    var cellRange = _.last(cellRanges);

    var cellPosition = this.getCellPosition();
    var fillHandleIsAvailable = rangesLen === 1 && (gridOptionsWrapper.isEnableFillHandle() || gridOptionsWrapper.isEnableRangeHandle()) && !this.editingCell;

    if (this.hasChartRange) {
      var hasCategoryRange = cellRanges[0].type === exports.CellRangeType.DIMENSION;
      var isCategoryCell = hasCategoryRange && rangeController.isCellInSpecificRange(cellPosition, cellRanges[0]);

      _.addOrRemoveCssClass(this.getGui(), 'ag-cell-range-chart-category', isCategoryCell);

      fillHandleIsAvailable = cellRange.type === exports.CellRangeType.VALUE;
    }

    return fillHandleIsAvailable && cellRange.endRow != null && rangeController.isContiguousRange(cellRange) && rangeController.isLastCellOfRange(cellRange, cellPosition);
  };

  CellComp.prototype.addSelectionHandle = function () {
    var _a = this.beans,
        gridOptionsWrapper = _a.gridOptionsWrapper,
        context = _a.context,
        rangeController = _a.rangeController;

    var cellRangeType = _.last(rangeController.getCellRanges()).type;

    var type = gridOptionsWrapper.isEnableFillHandle() && _.missing(cellRangeType) ? 'fill' : 'range';

    if (this.selectionHandle && this.selectionHandle.getType() !== type) {
      this.selectionHandle.destroy();
      this.selectionHandle = undefined;
    }

    if (!this.selectionHandle) {
      this.selectionHandle = context.createComponentFromElement(document.createElement("ag-" + type + "-handle"));
    }

    this.selectionHandle.refresh(this);
  };

  CellComp.prototype.updateRangeBordersIfRangeCount = function () {
    // we only need to update range borders if we are in a range
    if (this.rangeCount > 0) {
      this.updateRangeBorders();
      this.refreshHandle();
    }
  };

  CellComp.prototype.refreshHandle = function () {
    if (!this.beans.rangeController) {
      return;
    }

    var shouldHaveSelectionHandle = this.shouldHaveSelectionHandle();

    if (this.selectionHandle && !shouldHaveSelectionHandle) {
      this.selectionHandle.destroy();
      this.selectionHandle = null;
    }

    if (shouldHaveSelectionHandle) {
      this.addSelectionHandle();
    }

    _.addOrRemoveCssClass(this.getGui(), 'ag-cell-range-handle', !!this.selectionHandle);
  };

  CellComp.prototype.updateRangeBorders = function () {
    var rangeBorders = this.getRangeBorders();
    var isSingleCell = this.isSingleCell();
    var isTop = !isSingleCell && rangeBorders.top;
    var isRight = !isSingleCell && rangeBorders.right;
    var isBottom = !isSingleCell && rangeBorders.bottom;
    var isLeft = !isSingleCell && rangeBorders.left;
    var element = this.getGui();

    _.addOrRemoveCssClass(element, 'ag-cell-range-top', isTop);

    _.addOrRemoveCssClass(element, 'ag-cell-range-right', isRight);

    _.addOrRemoveCssClass(element, 'ag-cell-range-bottom', isBottom);

    _.addOrRemoveCssClass(element, 'ag-cell-range-left', isLeft);
  };

  CellComp.prototype.onFirstRightPinnedChanged = function () {
    var firstRightPinned = this.column.isFirstRightPinned();

    if (this.firstRightPinned !== firstRightPinned) {
      this.firstRightPinned = firstRightPinned;

      _.addOrRemoveCssClass(this.getGui(), 'ag-cell-first-right-pinned', firstRightPinned);
    }
  };

  CellComp.prototype.onLastLeftPinnedChanged = function () {
    var lastLeftPinned = this.column.isLastLeftPinned();

    if (this.lastLeftPinned !== lastLeftPinned) {
      this.lastLeftPinned = lastLeftPinned;

      _.addOrRemoveCssClass(this.getGui(), 'ag-cell-last-left-pinned', lastLeftPinned);
    }
  };

  CellComp.prototype.populateTemplate = function () {
    if (this.usingWrapper) {
      this.eParentOfValue = this.getRefElement('eCellValue');
      this.eCellWrapper = this.getRefElement('eCellWrapper');
      this.eCellValue = this.getRefElement('eCellValue');

      if (this.includeRowDraggingComponent) {
        this.addRowDragging();
      }

      if (this.includeDndSourceComponent) {
        this.addDndSource();
      }

      if (this.includeSelectionComponent) {
        this.addSelectionCheckbox();
      }
    } else {
      this.eParentOfValue = this.getGui();
    }
  };

  CellComp.prototype.getFrameworkOverrides = function () {
    return this.beans.frameworkOverrides;
  };

  CellComp.prototype.addRowDragging = function () {
    var pagination = this.beans.gridOptionsWrapper.isPagination();
    var rowDragManaged = this.beans.gridOptionsWrapper.isRowDragManaged();
    var clientSideRowModelActive = this.beans.gridOptionsWrapper.isRowModelDefault();

    if (rowDragManaged) {
      // row dragging only available in default row model
      if (!clientSideRowModelActive) {
        _.doOnce(function () {
          return console.warn('ag-Grid: managed row dragging is only allowed in the Client Side Row Model');
        }, 'CellComp.addRowDragging');

        return;
      }

      if (pagination) {
        _.doOnce(function () {
          return console.warn('ag-Grid: managed row dragging is not possible when doing pagination');
        }, 'CellComp.addRowDragging');

        return;
      }
    }

    var rowDraggingComp = new RowDragComp(this.rowNode, this.column, this.getValueToUse(), this.beans);
    this.addFeature(rowDraggingComp, this.beans.context); // put the checkbox in before the value

    this.eCellWrapper.insertBefore(rowDraggingComp.getGui(), this.eParentOfValue);
  };

  CellComp.prototype.addDndSource = function () {
    var dndSourceComp = new DndSourceComp(this.rowNode, this.column, this.getValueToUse(), this.beans, this.getGui());
    this.addFeature(dndSourceComp, this.beans.context); // put the checkbox in before the value

    this.eCellWrapper.insertBefore(dndSourceComp.getGui(), this.eParentOfValue);
  };

  CellComp.prototype.addSelectionCheckbox = function () {
    var cbSelectionComponent = new CheckboxSelectionComponent();
    this.beans.context.wireBean(cbSelectionComponent);
    var visibleFunc = this.getComponentHolder().checkboxSelection;
    visibleFunc = typeof visibleFunc === 'function' ? visibleFunc : null;
    cbSelectionComponent.init({
      rowNode: this.rowNode,
      column: this.column,
      visibleFunc: visibleFunc
    });
    this.addDestroyFunc(function () {
      return cbSelectionComponent.destroy();
    }); // put the checkbox in before the value

    this.eCellWrapper.insertBefore(cbSelectionComponent.getGui(), this.eParentOfValue);
  };

  CellComp.prototype.addDomData = function () {
    var _this = this;

    var element = this.getGui();
    this.beans.gridOptionsWrapper.setDomData(element, CellComp.DOM_DATA_KEY_CELL_COMP, this);
    this.addDestroyFunc(function () {
      return _this.beans.gridOptionsWrapper.setDomData(element, CellComp.DOM_DATA_KEY_CELL_COMP, null);
    });
  };

  CellComp.prototype.isSingleCell = function () {
    var rangeController = this.beans.rangeController;
    return this.rangeCount === 1 && rangeController && !rangeController.isMoreThanOneCell();
  };

  CellComp.prototype.onCellFocused = function (event) {
    var cellFocused = this.beans.focusedCellController.isCellFocused(this.cellPosition); // see if we need to change the classes on this cell

    if (cellFocused !== this.cellFocused) {
      // if we are not doing cell selection, then the focus class does not change
      var doingFocusCss = !this.beans.gridOptionsWrapper.isSuppressCellSelection();

      if (doingFocusCss) {
        _.addOrRemoveCssClass(this.getGui(), 'ag-cell-focus', cellFocused);
      }

      this.cellFocused = cellFocused;
    } // see if we need to force browser focus - this can happen if focus is programmatically set


    if (cellFocused && event && event.forceBrowserFocus) {
      this.getGui().focus(); // Fix for AG-3465 "IE11 - After editing cell's content, selection doesn't go one cell below on enter"
      // IE can fail to focus the cell after the first call to focus(), and needs a second call

      if (!document.activeElement || document.activeElement === document.body) {
        this.getGui().focus();
      }
    } // if another cell was focused, and we are editing, then stop editing


    var fullRowEdit = this.beans.gridOptionsWrapper.isFullRowEdit();

    if (!cellFocused && !fullRowEdit && this.editingCell) {
      this.stopRowOrCellEdit();
    }
  }; // pass in 'true' to cancel the editing.


  CellComp.prototype.stopRowOrCellEdit = function (cancel) {
    if (cancel === void 0) {
      cancel = false;
    }

    if (this.beans.gridOptionsWrapper.isFullRowEdit()) {
      this.rowComp.stopRowEditing(cancel);
    } else {
      this.stopEditing(cancel);
    }
  };

  CellComp.prototype.stopEditing = function (cancel) {
    if (cancel === void 0) {
      cancel = false;
    }

    if (!this.editingCell) {
      return;
    } // if no cell editor, this means due to async, that the cell editor never got initialised,
    // so we just carry on regardless as if the editing was never started.


    if (!this.cellEditor) {
      this.editingCell = false;
      return;
    }

    var newValueExists = false;
    var newValue;

    if (!cancel) {
      // also have another option here to cancel after editing, so for example user could have a popup editor and
      // it is closed by user clicking outside the editor. then the editor will close automatically (with false
      // passed above) and we need to see if the editor wants to accept the new value.
      var userWantsToCancel = this.cellEditor.isCancelAfterEnd && this.cellEditor.isCancelAfterEnd();

      if (!userWantsToCancel) {
        newValue = this.cellEditor.getValue();
        newValueExists = true;
      }
    } // it is important we set this after setValue() above, as otherwise the cell will flash
    // when editing stops. the 'refresh' method checks editing, and doesn't refresh editing cells.
    // thus it will skip the refresh on this cell until the end of this method where we call
    // refresh directly and we suppress the flash.


    this.editingCell = false;

    if (this.cellEditor.destroy) {
      this.cellEditor.destroy();
    } // important to clear this out - as parts of the code will check for
    // this to see if an async cellEditor has yet to be created


    this.cellEditor = null;

    if (this.cellEditorInPopup && this.hideEditorPopup) {
      this.hideEditorPopup();
      this.hideEditorPopup = null;
    } else {
      _.clearElement(this.getGui()); // put the cell back the way it was before editing


      if (this.usingWrapper) {
        // if wrapper, then put the wrapper back
        this.getGui().appendChild(this.eCellWrapper);
      } else {
        // if cellRenderer, then put the gui back in. if the renderer has
        // a refresh, it will be called. however if it doesn't, then later
        // the renderer will be destroyed and a new one will be created.
        if (this.cellRenderer) {
          // we know it's a dom element (not a string) because we converted
          // it after the gui was attached if it was a string.
          var eCell = this.cellRendererGui; // can be null if cell was previously null / contained empty string,
          // this will result in new value not being rendered.

          if (eCell) {
            this.getGui().appendChild(eCell);
          }
        }
      }
    }

    this.setInlineEditingClass();

    if (newValueExists) {
      // we suppressRefreshCell because the call to rowNode.setDataValue() results in change detection
      // getting triggered, which results in all cells getting refreshed. we do not want this refresh
      // to happen on this call as we want to call it explicitly below. otherwise refresh gets called twice.
      // if we only did this refresh (and not the one below) then the cell would flash and not be forced.
      this.suppressRefreshCell = true;
      this.rowNode.setDataValue(this.column, newValue);
      this.suppressRefreshCell = false;
    } // we suppress the flash, as it is not correct to flash the cell the user has finished editing,
    // the user doesn't need to flash as they were the one who did the edit, the flash is pointless
    // (as the flash is meant to draw the user to a change that they didn't manually do themselves).


    this.refreshCell({
      forceRefresh: true,
      suppressFlash: true
    });
    var event = this.createEvent(null, Events.EVENT_CELL_EDITING_STOPPED);
    this.beans.eventService.dispatchEvent(event);
  };

  CellComp.DOM_DATA_KEY_CELL_COMP = 'cellComp';
  CellComp.CELL_RENDERER_TYPE_NORMAL = 'cellRenderer';
  CellComp.CELL_RENDERER_TYPE_PINNED = 'pinnedRowCellRenderer';
  return CellComp;
}(Component);
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __extends$x = undefined && undefined.__extends || function () {
  var _extendStatics34 = function extendStatics(d, b) {
    _extendStatics34 = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics34(d, b);
  };

  return function (d, b) {
    _extendStatics34(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var __assign$1 = undefined && undefined.__assign || function () {
  __assign$1 = Object.assign || function (t) {
    for (var s, i = 1, n = arguments.length; i < n; i++) {
      s = arguments[i];

      for (var p in s) {
        if (Object.prototype.hasOwnProperty.call(s, p)) t[p] = s[p];
      }
    }

    return t;
  };

  return __assign$1.apply(this, arguments);
};

var RowComp =
/** @class */
function (_super) {
  __extends$x(RowComp, _super);

  function RowComp(parentScope, bodyContainerComp, pinnedLeftContainerComp, pinnedRightContainerComp, fullWidthContainerComp, rowNode, beans, animateIn, useAnimationFrameForCreate, printLayout, embedFullWidth) {
    var _this = _super.call(this) || this;

    _this.eAllRowContainers = [];
    _this.active = true;
    _this.rowContainerReadyCount = 0;
    _this.refreshNeeded = false;
    _this.columnRefreshPending = false;
    _this.cellComps = {}; // for animations, there are bits we want done in the next VM turn, to all DOM to update first.
    // instead of each row doing a setTimeout(func,0), we put the functions here and the rowRenderer
    // executes them all in one timeout

    _this.createSecondPassFuncs = []; // these get called before the row is destroyed - they set up the DOM for the remove animation (ie they
    // set the DOM up for the animation), then the delayedDestroyFunctions get called when the animation is
    // complete (ie removes from the dom).

    _this.removeFirstPassFuncs = []; // for animations, these functions get called 400ms after the row is cleared, called by the rowRenderer
    // so each row isn't setting up it's own timeout

    _this.removeSecondPassFuncs = [];
    _this.initialised = false;
    _this.elementOrderChanged = false;
    _this.parentScope = parentScope;
    _this.beans = beans;
    _this.bodyContainerComp = bodyContainerComp;
    _this.pinnedLeftContainerComp = pinnedLeftContainerComp;
    _this.pinnedRightContainerComp = pinnedRightContainerComp;
    _this.fullWidthContainerComp = fullWidthContainerComp;
    _this.rowNode = rowNode;
    _this.rowIsEven = _this.rowNode.rowIndex % 2 === 0;
    _this.paginationPage = _this.beans.paginationProxy.getCurrentPage();
    _this.useAnimationFrameForCreate = useAnimationFrameForCreate;
    _this.printLayout = printLayout;
    _this.embedFullWidth = embedFullWidth;

    _this.setAnimateFlags(animateIn);

    return _this;
  }

  RowComp.prototype.init = function () {
    var _this = this;

    this.rowFocused = this.beans.focusedCellController.isRowFocused(this.rowNode.rowIndex, this.rowNode.rowPinned);
    this.scope = this.createChildScopeOrNull(this.rowNode.data);
    this.setupRowContainers();
    this.addListeners();

    if (this.slideRowIn) {
      this.createSecondPassFuncs.push(function () {
        _this.onTopChanged();
      });
    }

    if (this.fadeRowIn) {
      this.createSecondPassFuncs.push(function () {
        _this.eAllRowContainers.forEach(function (eRow) {
          return _.removeCssClass(eRow, 'ag-opacity-zero');
        });
      });
    }
  };

  RowComp.prototype.createTemplate = function (contents, extraCssClass) {
    if (extraCssClass === void 0) {
      extraCssClass = null;
    }

    var templateParts = [];
    var rowHeight = this.rowNode.rowHeight;
    var rowClasses = this.getInitialRowClasses(extraCssClass).join(' ');

    var rowIdSanitised = _.escape(this.rowNode.id);

    var userRowStyles = this.preProcessStylesFromGridOptions();
    var businessKey = this.getRowBusinessKey();

    var businessKeySanitised = _.escape(businessKey);

    var rowTopStyle = this.getInitialRowTopStyle();
    var rowIdx = this.rowNode.getRowIndexString();
    var headerRowCount = this.beans.gridPanel.headerRootComp.getHeaderRowCount();
    templateParts.push("<div");
    templateParts.push(" role=\"row\"");
    templateParts.push(" row-index=\"" + rowIdx + "\" aria-rowindex=\"" + (headerRowCount + this.rowNode.rowIndex + 1) + "\"");
    templateParts.push(rowIdSanitised ? " row-id=\"" + rowIdSanitised + "\"" : "");
    templateParts.push(businessKey ? " row-business-key=\"" + businessKeySanitised + "\"" : "");
    templateParts.push(" comp-id=\"" + this.getCompId() + "\"");
    templateParts.push(" class=\"" + rowClasses + "\"");
    templateParts.push(" style=\"height: " + rowHeight + "px; " + rowTopStyle + " " + userRowStyles + "\">"); // add in the template for the cells

    templateParts.push(contents);
    templateParts.push("</div>");
    return templateParts.join('');
  };

  RowComp.prototype.getCellForCol = function (column) {
    var cellComp = this.cellComps[column.getColId()];
    return cellComp ? cellComp.getGui() : null;
  };

  RowComp.prototype.afterFlush = function () {
    if (this.initialised) {
      return;
    }

    this.initialised = true;
    this.executeProcessRowPostCreateFunc();
  };

  RowComp.prototype.executeProcessRowPostCreateFunc = function () {
    var func = this.beans.gridOptionsWrapper.getProcessRowPostCreateFunc();

    if (!func) {
      return;
    }

    var params = {
      eRow: this.eBodyRow,
      ePinnedLeftRow: this.ePinnedLeftRow,
      ePinnedRightRow: this.ePinnedRightRow,
      node: this.rowNode,
      api: this.beans.gridOptionsWrapper.getApi(),
      rowIndex: this.rowNode.rowIndex,
      addRenderedRowListener: this.addEventListener.bind(this),
      columnApi: this.beans.gridOptionsWrapper.getColumnApi(),
      context: this.beans.gridOptionsWrapper.getContext()
    };
    func(params);
  };

  RowComp.prototype.getInitialRowTopStyle = function () {
    // print layout uses normal flow layout for row positioning
    if (this.printLayout) {
      return '';
    } // if sliding in, we take the old row top. otherwise we just set the current row top.


    var pixels = this.slideRowIn ? this.roundRowTopToBounds(this.rowNode.oldRowTop) : this.rowNode.rowTop;
    var afterPaginationPixels = this.applyPaginationOffset(pixels);
    var afterScalingPixels = this.beans.maxDivHeightScaler.getRealPixelPosition(afterPaginationPixels);
    var isSuppressRowTransform = this.beans.gridOptionsWrapper.isSuppressRowTransform();
    return isSuppressRowTransform ? "top: " + afterScalingPixels + "px; " : "transform: translateY(" + afterScalingPixels + "px);";
  };

  RowComp.prototype.getRowBusinessKey = function () {
    var businessKeyForNodeFunc = this.beans.gridOptionsWrapper.getBusinessKeyForNodeFunc();

    if (typeof businessKeyForNodeFunc !== 'function') {
      return;
    }

    return businessKeyForNodeFunc(this.rowNode);
  };

  RowComp.prototype.areAllContainersReady = function () {
    return this.rowContainerReadyCount === 3;
  };

  RowComp.prototype.lazyCreateCells = function (cols, eRow) {
    if (!this.active) {
      return;
    }

    var cellTemplatesAndComps = this.createCells(cols);
    eRow.innerHTML = cellTemplatesAndComps.template;
    this.callAfterRowAttachedOnCells(cellTemplatesAndComps.cellComps, eRow);
    this.rowContainerReadyCount++;

    if (this.areAllContainersReady() && this.refreshNeeded) {
      this.refreshCells();
    }
  };

  RowComp.prototype.createRowContainer = function (rowContainerComp, cols, callback) {
    var _this = this;

    var useAnimationsFrameForCreate = this.useAnimationFrameForCreate;
    var cellTemplatesAndComps = useAnimationsFrameForCreate ? {
      cellComps: [],
      template: ''
    } : this.createCells(cols);
    var rowTemplate = this.createTemplate(cellTemplatesAndComps.template); // the RowRenderer is probably inserting many rows. rather than inserting each template one
    // at a time, the grid inserts all rows together - so the callback here is called by the
    // rowRenderer when all RowComps are created, then all the HTML is inserted in one go,
    // and then all the callbacks are called. this is NOT done in an animation frame.

    rowContainerComp.appendRowTemplate(rowTemplate, function () {
      var eRow = rowContainerComp.getRowElement(_this.getCompId());

      _this.afterRowAttached(rowContainerComp, eRow);

      callback(eRow);

      if (useAnimationsFrameForCreate) {
        _this.beans.taskQueue.createTask(_this.lazyCreateCells.bind(_this, cols, eRow), _this.rowNode.rowIndex, 'createTasksP1');
      } else {
        _this.callAfterRowAttachedOnCells(cellTemplatesAndComps.cellComps, eRow);

        _this.rowContainerReadyCount = 3;
      }
    });
  };

  RowComp.prototype.createChildScopeOrNull = function (data) {
    var isAngularCompileRows = this.beans.gridOptionsWrapper.isAngularCompileRows();

    if (!isAngularCompileRows) {
      return null;
    }

    var newChildScope = this.parentScope.$new();
    newChildScope.data = __assign$1({}, data);
    newChildScope.rowNode = this.rowNode;
    newChildScope.context = this.beans.gridOptionsWrapper.getContext();
    this.addDestroyFunc(function () {
      newChildScope.$destroy();
      newChildScope.data = null;
      newChildScope.rowNode = null;
      newChildScope.context = null;
    });
    return newChildScope;
  };

  RowComp.prototype.setupRowContainers = function () {
    var isFullWidthCell = this.rowNode.isFullWidthCell();
    var isDetailCell = this.beans.doingMasterDetail && this.rowNode.detail;
    var pivotMode = this.beans.columnController.isPivotMode(); // we only use full width for groups, not footers. it wouldn't make sense to include footers if not looking
    // for totals. if users complain about this, then we should introduce a new property 'footerUseEntireRow'
    // so each can be set independently (as a customer complained about footers getting full width, hence
    // introducing this logic)

    var isGroupRow = this.rowNode.group && !this.rowNode.footer;
    var isFullWidthGroup = isGroupRow && this.beans.gridOptionsWrapper.isGroupUseEntireRow(pivotMode);

    if (this.rowNode.stub) {
      this.createFullWidthRows(RowComp.LOADING_CELL_RENDERER, RowComp.LOADING_CELL_RENDERER_COMP_NAME);
    } else if (isDetailCell) {
      this.createFullWidthRows(RowComp.DETAIL_CELL_RENDERER, RowComp.DETAIL_CELL_RENDERER_COMP_NAME);
    } else if (isFullWidthCell) {
      this.createFullWidthRows(RowComp.FULL_WIDTH_CELL_RENDERER, null);
    } else if (isFullWidthGroup) {
      this.createFullWidthRows(RowComp.GROUP_ROW_RENDERER, RowComp.GROUP_ROW_RENDERER_COMP_NAME);
    } else {
      this.setupNormalRowContainers();
    }
  };

  RowComp.prototype.setupNormalRowContainers = function () {
    var _this = this;

    var centerCols;
    var leftCols;
    var rightCols;

    if (this.printLayout) {
      centerCols = this.beans.columnController.getAllDisplayedColumns();
      leftCols = [];
      rightCols = [];
    } else {
      centerCols = this.beans.columnController.getAllDisplayedCenterVirtualColumnsForRow(this.rowNode);
      leftCols = this.beans.columnController.getDisplayedLeftColumnsForRow(this.rowNode);
      rightCols = this.beans.columnController.getDisplayedRightColumnsForRow(this.rowNode);
    }

    this.createRowContainer(this.bodyContainerComp, centerCols, function (eRow) {
      return _this.eBodyRow = eRow;
    });
    this.createRowContainer(this.pinnedRightContainerComp, rightCols, function (eRow) {
      return _this.ePinnedRightRow = eRow;
    });
    this.createRowContainer(this.pinnedLeftContainerComp, leftCols, function (eRow) {
      return _this.ePinnedLeftRow = eRow;
    });
  };

  RowComp.prototype.createFullWidthRows = function (type, name) {
    var _this = this;

    this.fullWidthRow = true;

    if (this.embedFullWidth) {
      this.createFullWidthRowContainer(this.bodyContainerComp, null, null, type, name, function (eRow) {
        _this.eFullWidthRowBody = eRow;
      }, function (cellRenderer) {
        _this.fullWidthRowComponentBody = cellRenderer;
      }); // printLayout doesn't put components into the pinned sections

      if (!this.printLayout) {
        this.createFullWidthRowContainer(this.pinnedLeftContainerComp, Constants.PINNED_LEFT, 'ag-cell-last-left-pinned', type, name, function (eRow) {
          _this.eFullWidthRowLeft = eRow;
        }, function (cellRenderer) {
          _this.fullWidthRowComponentLeft = cellRenderer;
        });
        this.createFullWidthRowContainer(this.pinnedRightContainerComp, Constants.PINNED_RIGHT, 'ag-cell-first-right-pinned', type, name, function (eRow) {
          _this.eFullWidthRowRight = eRow;
        }, function (cellRenderer) {
          _this.fullWidthRowComponentRight = cellRenderer;
        });
      }
    } else {
      // otherwise we add to the fullWidth container as normal
      // let previousFullWidth = ensureDomOrder ? this.lastPlacedElements.eFullWidth : null;
      this.createFullWidthRowContainer(this.fullWidthContainerComp, null, null, type, name, function (eRow) {
        _this.eFullWidthRow = eRow;
      }, function (cellRenderer) {
        _this.fullWidthRowComponent = cellRenderer;
      });
    }
  };

  RowComp.prototype.setAnimateFlags = function (animateIn) {
    if (animateIn) {
      var oldRowTopExists = _.exists(this.rowNode.oldRowTop); // if the row had a previous position, we slide it in (animate row top)


      this.slideRowIn = oldRowTopExists; // if the row had no previous position, we fade it in (animate

      this.fadeRowIn = !oldRowTopExists;
    } else {
      this.slideRowIn = false;
      this.fadeRowIn = false;
    }
  };

  RowComp.prototype.isEditing = function () {
    return this.editingRow;
  };

  RowComp.prototype.stopRowEditing = function (cancel) {
    this.stopEditing(cancel);
  };

  RowComp.prototype.isFullWidth = function () {
    return this.fullWidthRow;
  };

  RowComp.prototype.refreshFullWidth = function () {
    var _this = this; // returns 'true' if refresh succeeded


    var tryRefresh = function tryRefresh(eRow, eCellComp, pinned) {
      if (!eRow || !eCellComp) {
        // no refresh needed
        return true;
      }

      if (!eCellComp.refresh) {
        // no refresh method present, so can't refresh, hard refresh needed
        return false;
      }

      var params = _this.createFullWidthParams(eRow, pinned);

      var refreshSucceeded = eCellComp.refresh(params);
      return refreshSucceeded;
    };

    var normalSuccess = tryRefresh(this.eFullWidthRow, this.fullWidthRowComponent, null);
    var bodySuccess = tryRefresh(this.eFullWidthRowBody, this.fullWidthRowComponentBody, null);
    var leftSuccess = tryRefresh(this.eFullWidthRowLeft, this.fullWidthRowComponentLeft, Constants.PINNED_LEFT);
    var rightSuccess = tryRefresh(this.eFullWidthRowRight, this.fullWidthRowComponentRight, Constants.PINNED_RIGHT);
    var allFullWidthRowsRefreshed = normalSuccess && bodySuccess && leftSuccess && rightSuccess;
    return allFullWidthRowsRefreshed;
  };

  RowComp.prototype.addListeners = function () {
    this.addDestroyableEventListener(this.rowNode, RowNode.EVENT_HEIGHT_CHANGED, this.onRowHeightChanged.bind(this));
    this.addDestroyableEventListener(this.rowNode, RowNode.EVENT_ROW_SELECTED, this.onRowSelected.bind(this));
    this.addDestroyableEventListener(this.rowNode, RowNode.EVENT_ROW_INDEX_CHANGED, this.onRowIndexChanged.bind(this));
    this.addDestroyableEventListener(this.rowNode, RowNode.EVENT_TOP_CHANGED, this.onTopChanged.bind(this));
    this.addDestroyableEventListener(this.rowNode, RowNode.EVENT_EXPANDED_CHANGED, this.onExpandedChanged.bind(this));
    this.addDestroyableEventListener(this.rowNode, RowNode.EVENT_DATA_CHANGED, this.onRowNodeDataChanged.bind(this));
    this.addDestroyableEventListener(this.rowNode, RowNode.EVENT_CELL_CHANGED, this.onRowNodeCellChanged.bind(this));
    this.addDestroyableEventListener(this.rowNode, RowNode.EVENT_DRAGGING_CHANGED, this.onRowNodeDraggingChanged.bind(this));
    var eventService = this.beans.eventService;
    this.addDestroyableEventListener(eventService, Events.EVENT_HEIGHT_SCALE_CHANGED, this.onTopChanged.bind(this));
    this.addDestroyableEventListener(eventService, Events.EVENT_DISPLAYED_COLUMNS_CHANGED, this.onDisplayedColumnsChanged.bind(this));
    this.addDestroyableEventListener(eventService, Events.EVENT_VIRTUAL_COLUMNS_CHANGED, this.onVirtualColumnsChanged.bind(this));
    this.addDestroyableEventListener(eventService, Events.EVENT_COLUMN_RESIZED, this.onColumnResized.bind(this));
    this.addDestroyableEventListener(eventService, Events.EVENT_CELL_FOCUSED, this.onCellFocusChanged.bind(this));
    this.addDestroyableEventListener(eventService, Events.EVENT_PAGINATION_CHANGED, this.onPaginationChanged.bind(this));
    this.addDestroyableEventListener(eventService, Events.EVENT_GRID_COLUMNS_CHANGED, this.onGridColumnsChanged.bind(this));
    this.addDestroyableEventListener(eventService, Events.EVENT_MODEL_UPDATED, this.onModelUpdated.bind(this));
    this.addDestroyableEventListener(eventService, Events.EVENT_COLUMN_MOVED, this.onColumnMoved.bind(this));
    this.addListenersForCellComps();
  };

  RowComp.prototype.addListenersForCellComps = function () {
    var _this = this;

    this.addDestroyableEventListener(this.rowNode, RowNode.EVENT_ROW_INDEX_CHANGED, function () {
      _this.forEachCellComp(function (cellComp) {
        return cellComp.onRowIndexChanged();
      });
    });
    this.addDestroyableEventListener(this.rowNode, RowNode.EVENT_CELL_CHANGED, function (event) {
      _this.forEachCellComp(function (cellComp) {
        return cellComp.onCellChanged(event);
      });
    });
  }; // when grid columns change, then all cells should be cleaned out,
  // as the new columns could have same id as the previous columns and may conflict


  RowComp.prototype.onGridColumnsChanged = function () {
    this.removeRenderedCells(Object.keys(this.cellComps));
  };

  RowComp.prototype.onRowNodeDataChanged = function (event) {
    // if this is an update, we want to refresh, as this will allow the user to put in a transition
    // into the cellRenderer refresh method. otherwise this might be completely new data, in which case
    // we will want to completely replace the cells
    this.forEachCellComp(function (cellComp) {
      return cellComp.refreshCell({
        suppressFlash: !event.update,
        newData: !event.update
      });
    }); // check for selected also, as this could be after lazy loading of the row data, in which case
    // the id might of just gotten set inside the row and the row selected state may of changed
    // as a result. this is what happens when selected rows are loaded in virtual pagination.
    // - niall note - since moving to the stub component, this may no longer be true, as replacing
    // the stub component now replaces the entire row

    this.onRowSelected(); // as data has changed, then the style and class needs to be recomputed

    this.postProcessCss();
  };

  RowComp.prototype.onRowNodeCellChanged = function (event) {
    // as data has changed, then the style and class needs to be recomputed
    this.postProcessCss();
  };

  RowComp.prototype.postProcessCss = function () {
    this.postProcessStylesFromGridOptions();
    this.postProcessClassesFromGridOptions();
    this.postProcessRowClassRules();
    this.postProcessRowDragging();
  };

  RowComp.prototype.onRowNodeDraggingChanged = function () {
    this.postProcessRowDragging();
  };

  RowComp.prototype.postProcessRowDragging = function () {
    var dragging = this.rowNode.dragging;
    this.eAllRowContainers.forEach(function (row) {
      return _.addOrRemoveCssClass(row, 'ag-row-dragging', dragging);
    });
  };

  RowComp.prototype.onExpandedChanged = function () {
    var rowNode = this.rowNode;
    this.eAllRowContainers.forEach(function (row) {
      return _.addOrRemoveCssClass(row, 'ag-row-group-expanded', rowNode.expanded);
    });
    this.eAllRowContainers.forEach(function (row) {
      return _.addOrRemoveCssClass(row, 'ag-row-group-contracted', !rowNode.expanded);
    });
  };

  RowComp.prototype.onDisplayedColumnsChanged = function () {
    if (this.fullWidthRow) {
      return;
    }

    this.refreshCells();
  };

  RowComp.prototype.destroyFullWidthComponents = function () {
    if (this.fullWidthRowComponent) {
      this.beans.detailRowCompCache.addOrDestroy(this.rowNode, null, this.fullWidthRowComponent);
      this.fullWidthRowComponent = null;
    }

    if (this.fullWidthRowComponentBody) {
      this.beans.detailRowCompCache.addOrDestroy(this.rowNode, null, this.fullWidthRowComponentBody);
      this.fullWidthRowComponent = null;
    }

    if (this.fullWidthRowComponentLeft) {
      this.beans.detailRowCompCache.addOrDestroy(this.rowNode, Constants.PINNED_LEFT, this.fullWidthRowComponentLeft);
      this.fullWidthRowComponentLeft = null;
    }

    if (this.fullWidthRowComponentRight) {
      this.beans.detailRowCompCache.addOrDestroy(this.rowNode, Constants.PINNED_RIGHT, this.fullWidthRowComponentRight);
      this.fullWidthRowComponent = null;
    }
  };

  RowComp.prototype.getContainerForCell = function (pinnedType) {
    switch (pinnedType) {
      case Constants.PINNED_LEFT:
        return this.ePinnedLeftRow;

      case Constants.PINNED_RIGHT:
        return this.ePinnedRightRow;

      default:
        return this.eBodyRow;
    }
  };

  RowComp.prototype.onVirtualColumnsChanged = function () {
    if (this.fullWidthRow) {
      return;
    }

    this.refreshCells();
  };

  RowComp.prototype.onColumnResized = function () {
    if (this.fullWidthRow) {
      return;
    }

    this.refreshCells();
  };

  RowComp.prototype.refreshCells = function () {
    if (!this.areAllContainersReady()) {
      this.refreshNeeded = true;
      return;
    }

    var suppressAnimationFrame = this.beans.gridOptionsWrapper.isSuppressAnimationFrame();
    var skipAnimationFrame = suppressAnimationFrame || this.printLayout;

    if (skipAnimationFrame) {
      this.refreshCellsInAnimationFrame();
    } else {
      if (this.columnRefreshPending) {
        return;
      }

      this.beans.taskQueue.createTask(this.refreshCellsInAnimationFrame.bind(this), this.rowNode.rowIndex, 'createTasksP1');
    }
  };

  RowComp.prototype.refreshCellsInAnimationFrame = function () {
    if (!this.active) {
      return;
    }

    this.columnRefreshPending = false;
    var centerCols;
    var leftCols;
    var rightCols;

    if (this.printLayout) {
      centerCols = this.beans.columnController.getAllDisplayedColumns();
      leftCols = [];
      rightCols = [];
    } else {
      centerCols = this.beans.columnController.getAllDisplayedCenterVirtualColumnsForRow(this.rowNode);
      leftCols = this.beans.columnController.getDisplayedLeftColumnsForRow(this.rowNode);
      rightCols = this.beans.columnController.getDisplayedRightColumnsForRow(this.rowNode);
    }

    this.insertCellsIntoContainer(this.eBodyRow, centerCols);
    this.insertCellsIntoContainer(this.ePinnedLeftRow, leftCols);
    this.insertCellsIntoContainer(this.ePinnedRightRow, rightCols);
    this.elementOrderChanged = false;
    var colIdsToRemove = Object.keys(this.cellComps);
    centerCols.forEach(function (col) {
      return _.removeFromArray(colIdsToRemove, col.getId());
    });
    leftCols.forEach(function (col) {
      return _.removeFromArray(colIdsToRemove, col.getId());
    });
    rightCols.forEach(function (col) {
      return _.removeFromArray(colIdsToRemove, col.getId());
    }); // we never remove editing cells, as this would cause the cells to loose their values while editing
    // as the grid is scrolling horizontally.

    var eligibleToBeRemoved = colIdsToRemove.filter(this.isCellEligibleToBeRemoved.bind(this)); // remove old cells from gui, but we don't destroy them, we might use them again

    this.removeRenderedCells(eligibleToBeRemoved);
  };

  RowComp.prototype.onColumnMoved = function () {
    this.elementOrderChanged = true;
  };

  RowComp.prototype.removeRenderedCells = function (colIds) {
    var _this = this;

    colIds.forEach(function (key) {
      var cellComp = _this.cellComps[key]; // could be old reference, ie removed cell

      if (_.missing(cellComp)) {
        return;
      }

      cellComp.detach();
      cellComp.destroy();
      _this.cellComps[key] = null;
    });
  };

  RowComp.prototype.isCellEligibleToBeRemoved = function (indexStr) {
    var displayedColumns = this.beans.columnController.getAllDisplayedColumns();
    var REMOVE_CELL = true;
    var KEEP_CELL = false;
    var renderedCell = this.cellComps[indexStr];

    if (!renderedCell) {
      return REMOVE_CELL;
    } // always remove the cell if it's in the wrong pinned location


    if (this.isCellInWrongRow(renderedCell)) {
      return REMOVE_CELL;
    } // we want to try and keep editing and focused cells


    var editing = renderedCell.isEditing();
    var focused = this.beans.focusedCellController.isCellFocused(renderedCell.getCellPosition());
    var mightWantToKeepCell = editing || focused;

    if (mightWantToKeepCell) {
      var column = renderedCell.getColumn();
      var cellStillDisplayed = displayedColumns.indexOf(column) >= 0;
      return cellStillDisplayed ? KEEP_CELL : REMOVE_CELL;
    }

    return REMOVE_CELL;
  };

  RowComp.prototype.ensureCellInCorrectContainer = function (cellComp) {
    // for print layout, we always put cells into centre, otherwise we put in correct pinned section
    if (this.printLayout) {
      return;
    }

    var element = cellComp.getGui();
    var column = cellComp.getColumn();
    var pinnedType = column.getPinned();
    var eContainer = this.getContainerForCell(pinnedType); // if in wrong container, remove it

    var eOldContainer = cellComp.getParentRow();
    var inWrongRow = eOldContainer !== eContainer;

    if (inWrongRow) {
      // take out from old row
      if (eOldContainer) {
        eOldContainer.removeChild(element);
      }

      eContainer.appendChild(element);
      cellComp.setParentRow(eContainer);
      this.elementOrderChanged = true;
    }
  };

  RowComp.prototype.isCellInWrongRow = function (cellComp) {
    var column = cellComp.getColumn();
    var rowWeWant = this.getContainerForCell(column.getPinned()); // if in wrong container, remove it

    var oldRow = cellComp.getParentRow();
    return oldRow !== rowWeWant;
  };

  RowComp.prototype.insertCellsIntoContainer = function (eRow, cols) {
    var _this = this;

    if (!eRow) {
      return;
    }

    var cellTemplates = [];
    var newCellComps = [];
    cols.forEach(function (col) {
      var colId = col.getId();
      var existingCell = _this.cellComps[colId];

      if (existingCell) {
        _this.ensureCellInCorrectContainer(existingCell);
      } else {
        _this.createNewCell(col, eRow, cellTemplates, newCellComps);
      }
    });

    if (cellTemplates.length > 0) {
      _.appendHtml(eRow, cellTemplates.join(''));

      this.callAfterRowAttachedOnCells(newCellComps, eRow);
    }

    if (this.elementOrderChanged && this.beans.gridOptionsWrapper.isEnsureDomOrder()) {
      var correctChildOrder = cols.map(function (col) {
        return _this.getCellForCol(col);
      });

      _.setDomChildOrder(eRow, correctChildOrder);
    }
  };

  RowComp.prototype.addDomData = function (eRowContainer) {
    var gow = this.beans.gridOptionsWrapper;
    gow.setDomData(eRowContainer, RowComp.DOM_DATA_KEY_RENDERED_ROW, this);
    this.addDestroyFunc(function () {
      gow.setDomData(eRowContainer, RowComp.DOM_DATA_KEY_RENDERED_ROW, null);
    });
  };

  RowComp.prototype.createNewCell = function (col, eContainer, cellTemplates, newCellComps) {
    var newCellComp = new CellComp(this.scope, this.beans, col, this.rowNode, this, false, this.printLayout);
    var cellTemplate = newCellComp.getCreateTemplate();
    cellTemplates.push(cellTemplate);
    newCellComps.push(newCellComp);
    this.cellComps[col.getId()] = newCellComp;
    newCellComp.setParentRow(eContainer);
    this.elementOrderChanged = true;
  };

  RowComp.prototype.onMouseEvent = function (eventName, mouseEvent) {
    switch (eventName) {
      case 'dblclick':
        this.onRowDblClick(mouseEvent);
        break;

      case 'click':
        this.onRowClick(mouseEvent);
        break;
    }
  };

  RowComp.prototype.createRowEvent = function (type, domEvent) {
    return {
      type: type,
      node: this.rowNode,
      data: this.rowNode.data,
      rowIndex: this.rowNode.rowIndex,
      rowPinned: this.rowNode.rowPinned,
      context: this.beans.gridOptionsWrapper.getContext(),
      api: this.beans.gridOptionsWrapper.getApi(),
      columnApi: this.beans.gridOptionsWrapper.getColumnApi(),
      event: domEvent
    };
  };

  RowComp.prototype.createRowEventWithSource = function (type, domEvent) {
    var event = this.createRowEvent(type, domEvent); // when first developing this, we included the rowComp in the event.
    // this seems very weird. so when introducing the event types, i left the 'source'
    // out of the type, and just include the source in the two places where this event
    // was fired (rowClicked and rowDoubleClicked). it doesn't make sense for any
    // users to be using this, as the rowComp isn't an object we expose, so would be
    // very surprising if a user was using it.

    event.source = this;
    return event;
  };

  RowComp.prototype.onRowDblClick = function (mouseEvent) {
    if (_.isStopPropagationForAgGrid(mouseEvent)) {
      return;
    }

    var agEvent = this.createRowEventWithSource(Events.EVENT_ROW_DOUBLE_CLICKED, mouseEvent);
    this.beans.eventService.dispatchEvent(agEvent);
  };

  RowComp.prototype.onRowClick = function (mouseEvent) {
    var stop = _.isStopPropagationForAgGrid(mouseEvent);

    if (stop) {
      return;
    }

    var agEvent = this.createRowEventWithSource(Events.EVENT_ROW_CLICKED, mouseEvent);
    this.beans.eventService.dispatchEvent(agEvent); // ctrlKey for windows, metaKey for Apple

    var multiSelectKeyPressed = mouseEvent.ctrlKey || mouseEvent.metaKey;
    var shiftKeyPressed = mouseEvent.shiftKey;

    if ( // we do not allow selecting groups by clicking (as the click here expands the group), or if it's a detail row,
    // so return if it's a group row
    this.rowNode.group || // this is needed so we don't unselect other rows when we click this row, eg if this row is not selectable,
    // and we click it, the selection should not change (ie any currently selected row should stay selected)
    !this.rowNode.selectable || // we also don't allow selection of pinned rows
    this.rowNode.rowPinned || // if no selection method enabled, do nothing
    !this.beans.gridOptionsWrapper.isRowSelection() || // if click selection suppressed, do nothing
    this.beans.gridOptionsWrapper.isSuppressRowClickSelection()) {
      return;
    }

    var multiSelectOnClick = this.beans.gridOptionsWrapper.isRowMultiSelectWithClick();
    var rowDeselectionWithCtrl = this.beans.gridOptionsWrapper.isRowDeselection();

    if (this.rowNode.isSelected()) {
      if (multiSelectOnClick) {
        this.rowNode.setSelectedParams({
          newValue: false
        });
      } else if (multiSelectKeyPressed) {
        if (rowDeselectionWithCtrl) {
          this.rowNode.setSelectedParams({
            newValue: false
          });
        }
      } else {
        // selected with no multi key, must make sure anything else is unselected
        this.rowNode.setSelectedParams({
          newValue: true,
          clearSelection: true
        });
      }
    } else {
      var clearSelection = multiSelectOnClick ? false : !multiSelectKeyPressed;
      this.rowNode.setSelectedParams({
        newValue: true,
        clearSelection: clearSelection,
        rangeSelect: shiftKeyPressed
      });
    }
  };

  RowComp.prototype.createFullWidthRowContainer = function (rowContainerComp, pinned, extraCssClass, cellRendererType, cellRendererName, eRowCallback, cellRendererCallback) {
    var _this = this;

    var rowTemplate = this.createTemplate('', extraCssClass);
    rowContainerComp.appendRowTemplate(rowTemplate, function () {
      var eRow = rowContainerComp.getRowElement(_this.getCompId());

      var params = _this.createFullWidthParams(eRow, pinned);

      var callback = function callback(cellRenderer) {
        if (_this.isAlive()) {
          var gui = cellRenderer.getGui();
          eRow.appendChild(gui);
          cellRendererCallback(cellRenderer);
        } else {
          if (cellRenderer.destroy) {
            cellRenderer.destroy();
          }
        }
      }; // if doing master detail, it's possible we have a cached row comp from last time detail was displayed


      var cachedRowComp = _this.beans.detailRowCompCache.get(_this.rowNode, pinned);

      if (cachedRowComp) {
        callback(cachedRowComp);
      } else {
        var res = _this.beans.userComponentFactory.newFullWidthCellRenderer(params, cellRendererType, cellRendererName);

        if (!res) {
          var masterDetailModuleLoaded = ModuleRegistry.isRegistered(exports.ModuleNames.MasterDetailModule);

          if (cellRendererName === 'agDetailCellRenderer' && !masterDetailModuleLoaded) {
            console.warn("ag-Grid: cell renderer agDetailCellRenderer (for master detail) not found. Did you forget to include the master detail module?");
          } else {
            console.error("ag-Grid: fullWidthCellRenderer " + cellRendererName + " not found");
          }

          return;
        }

        res.then(callback);
      }

      _this.afterRowAttached(rowContainerComp, eRow);

      eRowCallback(eRow);

      _this.angular1Compile(eRow);
    });
  };

  RowComp.prototype.angular1Compile = function (element) {
    if (!this.scope) {
      return;
    }

    this.beans.$compile(element)(this.scope);
  };

  RowComp.prototype.createFullWidthParams = function (eRow, pinned) {
    var params = {
      fullWidth: true,
      data: this.rowNode.data,
      node: this.rowNode,
      value: this.rowNode.key,
      $scope: this.scope ? this.scope : this.parentScope,
      $compile: this.beans.$compile,
      rowIndex: this.rowNode.rowIndex,
      api: this.beans.gridOptionsWrapper.getApi(),
      columnApi: this.beans.gridOptionsWrapper.getColumnApi(),
      context: this.beans.gridOptionsWrapper.getContext(),
      // these need to be taken out, as part of 'afterAttached' now
      eGridCell: eRow,
      eParentOfValue: eRow,
      pinned: pinned,
      addRenderedRowListener: this.addEventListener.bind(this)
    };
    return params;
  };

  RowComp.prototype.getInitialRowClasses = function (extraCssClass) {
    var classes = [];
    var isTreeData = this.beans.gridOptionsWrapper.isTreeData();
    var rowNode = this.rowNode;

    if (_.exists(extraCssClass)) {
      classes.push(extraCssClass);
    }

    classes.push('ag-row');
    classes.push(this.rowFocused ? 'ag-row-focus' : 'ag-row-no-focus');

    if (this.fadeRowIn) {
      classes.push('ag-opacity-zero');
    }

    classes.push(this.rowIsEven ? 'ag-row-even' : 'ag-row-odd');

    if (rowNode.isSelected()) {
      classes.push('ag-row-selected');
    }

    if (rowNode.group) {
      classes.push('ag-row-group'); // if a group, put the level of the group in

      classes.push('ag-row-level-' + rowNode.level);

      if (rowNode.footer) {
        classes.push('ag-row-footer');
      }
    } else {
      // if a leaf, and a parent exists, put a level of the parent, else put level of 0 for top level item
      classes.push('ag-row-level-' + (rowNode.parent ? rowNode.parent.level + 1 : '0'));
    }

    if (rowNode.stub) {
      classes.push('ag-row-stub');
    }

    if (this.fullWidthRow) {
      classes.push('ag-full-width-row');
    }

    var addExpandedClass = isTreeData ? // if doing tree data, we add the expanded classes if any children, as any node can be a parent
    rowNode.allChildrenCount : // if normal row grouping, we add expanded classes to groups only
    rowNode.group && !rowNode.footer;

    if (addExpandedClass) {
      classes.push(rowNode.expanded ? 'ag-row-group-expanded' : 'ag-row-group-contracted');
    }

    if (rowNode.dragging) {
      classes.push('ag-row-dragging');
    }

    _.pushAll(classes, this.processClassesFromGridOptions());

    _.pushAll(classes, this.preProcessRowClassRules()); // we use absolute position unless we are doing print layout


    classes.push(this.printLayout ? 'ag-row-position-relative' : 'ag-row-position-absolute');
    this.firstRowOnPage = this.isFirstRowOnPage();
    this.lastRowOnPage = this.isLastRowOnPage();

    if (this.firstRowOnPage) {
      classes.push('ag-row-first');
    }

    if (this.lastRowOnPage) {
      classes.push('ag-row-last');
    }

    return classes;
  };

  RowComp.prototype.isFirstRowOnPage = function () {
    return this.rowNode.rowIndex === this.beans.paginationProxy.getPageFirstRow();
  };

  RowComp.prototype.isLastRowOnPage = function () {
    return this.rowNode.rowIndex === this.beans.paginationProxy.getPageLastRow();
  };

  RowComp.prototype.onModelUpdated = function () {
    var newFirst = this.isFirstRowOnPage();
    var newLast = this.isLastRowOnPage();

    if (this.firstRowOnPage !== newFirst) {
      this.firstRowOnPage = newFirst;
      this.eAllRowContainers.forEach(function (row) {
        return _.addOrRemoveCssClass(row, 'ag-row-first', newFirst);
      });
    }

    if (this.lastRowOnPage !== newLast) {
      this.lastRowOnPage = newLast;
      this.eAllRowContainers.forEach(function (row) {
        return _.addOrRemoveCssClass(row, 'ag-row-last', newLast);
      });
    }
  };

  RowComp.prototype.preProcessRowClassRules = function () {
    var res = [];
    this.processRowClassRules(function (className) {
      res.push(className);
    }, function (className) {// not catered for, if creating, no need
      // to remove class as it was never there
    });
    return res;
  };

  RowComp.prototype.processRowClassRules = function (onApplicableClass, onNotApplicableClass) {
    this.beans.stylingService.processClassRules(this.beans.gridOptionsWrapper.rowClassRules(), {
      value: undefined,
      colDef: undefined,
      data: this.rowNode.data,
      node: this.rowNode,
      rowIndex: this.rowNode.rowIndex,
      api: this.beans.gridOptionsWrapper.getApi(),
      columnApi: this.beans.gridOptionsWrapper.getColumnApi(),
      $scope: this.scope,
      context: this.beans.gridOptionsWrapper.getContext()
    }, onApplicableClass, onNotApplicableClass);
  };

  RowComp.prototype.stopEditing = function (cancel) {
    if (cancel === void 0) {
      cancel = false;
    }

    this.forEachCellComp(function (renderedCell) {
      renderedCell.stopEditing(cancel);
    });

    if (!this.editingRow) {
      return;
    }

    if (!cancel) {
      var event_1 = this.createRowEvent(Events.EVENT_ROW_VALUE_CHANGED);
      this.beans.eventService.dispatchEvent(event_1);
    }

    this.setEditingRow(false);
  };

  RowComp.prototype.setEditingRow = function (value) {
    this.editingRow = value;
    this.eAllRowContainers.forEach(function (row) {
      return _.addOrRemoveCssClass(row, 'ag-row-editing', value);
    });
    var event = value ? this.createRowEvent(Events.EVENT_ROW_EDITING_STARTED) : this.createRowEvent(Events.EVENT_ROW_EDITING_STOPPED);
    this.beans.eventService.dispatchEvent(event);
  };

  RowComp.prototype.startRowEditing = function (keyPress, charPress, sourceRenderedCell) {
    if (keyPress === void 0) {
      keyPress = null;
    }

    if (charPress === void 0) {
      charPress = null;
    }

    if (sourceRenderedCell === void 0) {
      sourceRenderedCell = null;
    } // don't do it if already editing


    if (this.editingRow) {
      return;
    }

    this.forEachCellComp(function (renderedCell) {
      var cellStartedEdit = renderedCell === sourceRenderedCell;

      if (cellStartedEdit) {
        renderedCell.startEditingIfEnabled(keyPress, charPress, cellStartedEdit);
      } else {
        renderedCell.startEditingIfEnabled(null, null, cellStartedEdit);
      }
    });
    this.setEditingRow(true);
  };

  RowComp.prototype.forEachCellComp = function (callback) {
    _.iterateObject(this.cellComps, function (key, cellComp) {
      if (!cellComp) {
        return;
      }

      callback(cellComp);
    });
  };

  RowComp.prototype.postProcessClassesFromGridOptions = function () {
    var _this = this;

    var cssClasses = this.processClassesFromGridOptions();

    if (!cssClasses || !cssClasses.length) {
      return;
    }

    cssClasses.forEach(function (classStr) {
      _this.eAllRowContainers.forEach(function (row) {
        return _.addCssClass(row, classStr);
      });
    });
  };

  RowComp.prototype.postProcessRowClassRules = function () {
    var _this = this;

    this.processRowClassRules(function (className) {
      _this.eAllRowContainers.forEach(function (row) {
        return _.addCssClass(row, className);
      });
    }, function (className) {
      _this.eAllRowContainers.forEach(function (row) {
        return _.removeCssClass(row, className);
      });
    });
  };

  RowComp.prototype.processClassesFromGridOptions = function () {
    var res = [];

    var process = function process(rowCls) {
      if (typeof rowCls === 'string') {
        res.push(rowCls);
      } else if (Array.isArray(rowCls)) {
        rowCls.forEach(function (e) {
          return res.push(e);
        });
      }
    }; // part 1 - rowClass


    var rowClass = this.beans.gridOptionsWrapper.getRowClass();

    if (rowClass) {
      if (typeof rowClass === 'function') {
        console.warn('ag-Grid: rowClass should not be a function, please use getRowClass instead');
        return;
      }

      process(rowClass);
    } // part 2 - rowClassFunc


    var rowClassFunc = this.beans.gridOptionsWrapper.getRowClassFunc();

    if (rowClassFunc) {
      var params = {
        node: this.rowNode,
        data: this.rowNode.data,
        rowIndex: this.rowNode.rowIndex,
        context: this.beans.gridOptionsWrapper.getContext(),
        api: this.beans.gridOptionsWrapper.getApi()
      };
      var rowClassFuncResult = rowClassFunc(params);
      process(rowClassFuncResult);
    }

    return res;
  };

  RowComp.prototype.preProcessStylesFromGridOptions = function () {
    var rowStyles = this.processStylesFromGridOptions();
    return _.cssStyleObjectToMarkup(rowStyles);
  };

  RowComp.prototype.postProcessStylesFromGridOptions = function () {
    var rowStyles = this.processStylesFromGridOptions();
    this.eAllRowContainers.forEach(function (row) {
      return _.addStylesToElement(row, rowStyles);
    });
  };

  RowComp.prototype.processStylesFromGridOptions = function () {
    // part 1 - rowStyle
    var rowStyle = this.beans.gridOptionsWrapper.getRowStyle();

    if (rowStyle && typeof rowStyle === 'function') {
      console.warn('ag-Grid: rowStyle should be an object of key/value styles, not be a function, use getRowStyle() instead');
      return;
    } // part 1 - rowStyleFunc


    var rowStyleFunc = this.beans.gridOptionsWrapper.getRowStyleFunc();
    var rowStyleFuncResult;

    if (rowStyleFunc) {
      var params = {
        data: this.rowNode.data,
        node: this.rowNode,
        api: this.beans.gridOptionsWrapper.getApi(),
        context: this.beans.gridOptionsWrapper.getContext(),
        $scope: this.scope
      };
      rowStyleFuncResult = rowStyleFunc(params);
    }

    return _.assign({}, rowStyle, rowStyleFuncResult);
  };

  RowComp.prototype.createCells = function (cols) {
    var _this = this;

    var templateParts = [];
    var newCellComps = [];
    cols.forEach(function (col) {
      var newCellComp = new CellComp(_this.scope, _this.beans, col, _this.rowNode, _this, false, _this.printLayout);
      var cellTemplate = newCellComp.getCreateTemplate();
      templateParts.push(cellTemplate);
      newCellComps.push(newCellComp);
      _this.cellComps[col.getId()] = newCellComp;
    });
    var templateAndComps = {
      template: templateParts.join(''),
      cellComps: newCellComps
    };
    return templateAndComps;
  };

  RowComp.prototype.onRowSelected = function () {
    var selected = this.rowNode.isSelected();
    this.eAllRowContainers.forEach(function (row) {
      return _.addOrRemoveCssClass(row, 'ag-row-selected', selected);
    });
  }; // called:
  // + after row created for first time
  // + after horizontal scroll, so new cells due to column virtualisation


  RowComp.prototype.callAfterRowAttachedOnCells = function (newCellComps, eRow) {
    var _this = this;

    newCellComps.forEach(function (cellComp) {
      cellComp.setParentRow(eRow);
      cellComp.afterAttached(); // if we are editing the row, then the cell needs to turn
      // into edit mode

      if (_this.editingRow) {
        cellComp.startEditingIfEnabled();
      }
    });
  };

  RowComp.prototype.afterRowAttached = function (rowContainerComp, eRow) {
    var _this = this;

    this.addDomData(eRow);
    this.removeSecondPassFuncs.push(function () {
      rowContainerComp.removeRowElement(eRow);
    });
    this.removeFirstPassFuncs.push(function () {
      if (_.exists(_this.rowNode.rowTop)) {
        // the row top is updated anyway, however we set it here again
        // to something more reasonable for the animation - ie if the
        // row top is 10000px away, the row will flash out, so this
        // gives it a rounded value, so row animates out more slowly
        var rowTop = _this.roundRowTopToBounds(_this.rowNode.rowTop);

        _this.setRowTop(rowTop);
      } else {
        _.addCssClass(eRow, 'ag-opacity-zero');
      }
    });
    this.eAllRowContainers.push(eRow); // adding hover functionality adds listener to this row, so we
    // do it lazily in an animation frame

    if (this.useAnimationFrameForCreate) {
      this.beans.taskQueue.createTask(this.addHoverFunctionality.bind(this, eRow), this.rowNode.rowIndex, 'createTasksP2');
    } else {
      this.addHoverFunctionality(eRow);
    }
  };

  RowComp.prototype.addHoverFunctionality = function (eRow) {
    var _this = this; // because we use animation frames to do this, it's possible the row no longer exists
    // by the time we get to add it


    if (!this.active) {
      return;
    } // because mouseenter and mouseleave do not propagate, we cannot listen on the gridPanel
    // like we do for all the other mouse events.
    // because of the pinning, we cannot simply add / remove the class based on the eRow. we
    // have to check all eRow's (body & pinned). so the trick is if any of the rows gets a
    // mouse hover, it sets such in the rowNode, and then all three reflect the change as
    // all are listening for event on the row node.
    // step 1 - add listener, to set flag on row node


    this.addDestroyableEventListener(eRow, 'mouseenter', function () {
      return _this.rowNode.onMouseEnter();
    });
    this.addDestroyableEventListener(eRow, 'mouseleave', function () {
      return _this.rowNode.onMouseLeave();
    }); // step 2 - listen for changes on row node (which any eRow can trigger)

    this.addDestroyableEventListener(this.rowNode, RowNode.EVENT_MOUSE_ENTER, function () {
      // if hover turned off, we don't add the class. we do this here so that if the application
      // toggles this property mid way, we remove the hover form the last row, but we stop
      // adding hovers from that point onwards.
      if (!_this.beans.gridOptionsWrapper.isSuppressRowHoverHighlight()) {
        _.addCssClass(eRow, 'ag-row-hover');
      }
    });
    this.addDestroyableEventListener(this.rowNode, RowNode.EVENT_MOUSE_LEAVE, function () {
      _.removeCssClass(eRow, 'ag-row-hover');
    });
  }; // for animation, we don't want to animate entry or exit to a very far away pixel,
  // otherwise the row would move so fast, it would appear to disappear. so this method
  // moves the row closer to the viewport if it is far away, so the row slide in / out
  // at a speed the user can see.


  RowComp.prototype.roundRowTopToBounds = function (rowTop) {
    var range = this.beans.gridPanel.getVScrollPosition();
    var minPixel = this.applyPaginationOffset(range.top, true) - 100;
    var maxPixel = this.applyPaginationOffset(range.bottom, true) + 100;
    return Math.min(Math.max(minPixel, rowTop), maxPixel);
  };

  RowComp.prototype.getFrameworkOverrides = function () {
    return this.beans.frameworkOverrides;
  };

  RowComp.prototype.onRowHeightChanged = function () {
    // check for exists first - if the user is resetting the row height, then
    // it will be null (or undefined) momentarily until the next time the flatten
    // stage is called where the row will then update again with a new height
    if (_.exists(this.rowNode.rowHeight)) {
      var heightPx_1 = this.rowNode.rowHeight + "px";
      this.eAllRowContainers.forEach(function (row) {
        return row.style.height = heightPx_1;
      });
    }
  };

  RowComp.prototype.addEventListener = function (eventType, listener) {
    if (eventType === 'renderedRowRemoved' || eventType === 'rowRemoved') {
      eventType = Events.EVENT_VIRTUAL_ROW_REMOVED;
      console.warn('ag-Grid: Since version 11, event renderedRowRemoved is now called ' + Events.EVENT_VIRTUAL_ROW_REMOVED);
    }

    _super.prototype.addEventListener.call(this, eventType, listener);
  };

  RowComp.prototype.removeEventListener = function (eventType, listener) {
    if (eventType === 'renderedRowRemoved' || eventType === 'rowRemoved') {
      eventType = Events.EVENT_VIRTUAL_ROW_REMOVED;
      console.warn('ag-Grid: Since version 11, event renderedRowRemoved and rowRemoved is now called ' + Events.EVENT_VIRTUAL_ROW_REMOVED);
    }

    _super.prototype.removeEventListener.call(this, eventType, listener);
  };

  RowComp.prototype.destroy = function (animate) {
    if (animate === void 0) {
      animate = false;
    }

    _super.prototype.destroy.call(this);

    this.active = false; // why do we have this method? shouldn't everything below be added as a destroy func beside
    // the corresponding create logic?

    this.destroyFullWidthComponents();

    if (animate) {
      this.removeFirstPassFuncs.forEach(function (func) {
        return func();
      });
      this.removeSecondPassFuncs.push(this.destroyContainingCells.bind(this));
    } else {
      this.destroyContainingCells(); // we are not animating, so execute the second stage of removal now.
      // we call getAndClear, so that they are only called once

      var delayedDestroyFunctions = this.getAndClearDelayedDestroyFunctions();
      delayedDestroyFunctions.forEach(function (func) {
        return func();
      });
    }

    var event = this.createRowEvent(Events.EVENT_VIRTUAL_ROW_REMOVED);
    this.dispatchEvent(event);
    this.beans.eventService.dispatchEvent(event);
  };

  RowComp.prototype.destroyContainingCells = function () {
    this.forEachCellComp(function (renderedCell) {
      return renderedCell.destroy();
    });
    this.destroyFullWidthComponents();
  }; // we clear so that the functions are never executed twice


  RowComp.prototype.getAndClearDelayedDestroyFunctions = function () {
    var result = this.removeSecondPassFuncs;
    this.removeSecondPassFuncs = [];
    return result;
  };

  RowComp.prototype.onCellFocusChanged = function () {
    var rowFocused = this.beans.focusedCellController.isRowFocused(this.rowNode.rowIndex, this.rowNode.rowPinned);

    if (rowFocused !== this.rowFocused) {
      this.eAllRowContainers.forEach(function (row) {
        return _.addOrRemoveCssClass(row, 'ag-row-focus', rowFocused);
      });
      this.eAllRowContainers.forEach(function (row) {
        return _.addOrRemoveCssClass(row, 'ag-row-no-focus', !rowFocused);
      });
      this.rowFocused = rowFocused;
    } // if we are editing, then moving the focus out of a row will stop editing


    if (!rowFocused && this.editingRow) {
      this.stopEditing(false);
    }
  };

  RowComp.prototype.onPaginationChanged = function () {
    var currentPage = this.beans.paginationProxy.getCurrentPage(); // it is possible this row is in the new page, but the page number has changed, which means
    // it needs to reposition itself relative to the new page

    if (this.paginationPage !== currentPage) {
      this.paginationPage = currentPage;
      this.onTopChanged();
    }
  };

  RowComp.prototype.onTopChanged = function () {
    this.setRowTop(this.rowNode.rowTop);
  }; // applies pagination offset, eg if on second page, and page height is 500px, then removes
  // 500px from the top position, so a row with rowTop 600px is displayed at location 100px.
  // reverse will take the offset away rather than add.


  RowComp.prototype.applyPaginationOffset = function (topPx, reverse) {
    if (reverse === void 0) {
      reverse = false;
    }

    if (this.rowNode.isRowPinned()) {
      return topPx;
    }

    var pixelOffset = this.beans.paginationProxy.getPixelOffset();
    var multiplier = reverse ? 1 : -1;
    return topPx + pixelOffset * multiplier;
  };

  RowComp.prototype.setRowTop = function (pixels) {
    // print layout uses normal flow layout for row positioning
    if (this.printLayout) {
      return;
    } // need to make sure rowTop is not null, as this can happen if the node was once
    // visible (ie parent group was expanded) but is now not visible


    if (_.exists(pixels)) {
      var afterPaginationPixels = this.applyPaginationOffset(pixels);
      var afterScalingPixels = this.beans.maxDivHeightScaler.getRealPixelPosition(afterPaginationPixels);
      var topPx_1 = afterScalingPixels + "px";

      if (this.beans.gridOptionsWrapper.isSuppressRowTransform()) {
        this.eAllRowContainers.forEach(function (row) {
          return row.style.top = topPx_1;
        });
      } else {
        this.eAllRowContainers.forEach(function (row) {
          return row.style.transform = "translateY(" + topPx_1 + ")";
        });
      }
    }
  }; // we clear so that the functions are never executed twice


  RowComp.prototype.getAndClearNextVMTurnFunctions = function () {
    var result = this.createSecondPassFuncs;
    this.createSecondPassFuncs = [];
    return result;
  };

  RowComp.prototype.getRowNode = function () {
    return this.rowNode;
  };

  RowComp.prototype.getRenderedCellForColumn = function (column) {
    var _this = this;

    var cellComp = this.cellComps[column.getColId()];

    if (cellComp) {
      return cellComp;
    }

    var spanList = Object.keys(this.cellComps).map(function (name) {
      return _this.cellComps[name];
    }).filter(function (cmp) {
      return cmp && cmp.getColSpanningList().indexOf(column) !== -1;
    });
    return spanList.length ? spanList[0] : undefined;
  };

  RowComp.prototype.onRowIndexChanged = function () {
    this.onCellFocusChanged();
    this.updateRowIndexes();
  };

  RowComp.prototype.updateRowIndexes = function () {
    var _this = this;

    var rowIndexStr = this.rowNode.getRowIndexString();
    var rowIsEven = this.rowNode.rowIndex % 2 === 0;
    var rowIsEvenChanged = this.rowIsEven !== rowIsEven;
    var headerRowCount = this.beans.gridPanel.headerRootComp.getHeaderRowCount();

    if (rowIsEvenChanged) {
      this.rowIsEven = rowIsEven;
    }

    this.eAllRowContainers.forEach(function (eRow) {
      eRow.setAttribute('row-index', rowIndexStr);
      eRow.setAttribute('aria-rowindex', (headerRowCount + _this.rowNode.rowIndex + 1).toString());

      if (!rowIsEvenChanged) {
        return;
      }

      _.addOrRemoveCssClass(eRow, 'ag-row-even', rowIsEven);

      _.addOrRemoveCssClass(eRow, 'ag-row-odd', !rowIsEven);
    });
  };

  RowComp.prototype.ensureDomOrder = function () {
    var sides = [{
      el: this.getBodyRowElement(),
      ct: this.bodyContainerComp
    }, {
      el: this.getPinnedLeftRowElement(),
      ct: this.pinnedLeftContainerComp
    }, {
      el: this.getPinnedRightRowElement(),
      ct: this.pinnedRightContainerComp
    }, {
      el: this.getFullWidthRowElement(),
      ct: this.fullWidthContainerComp
    }];
    sides.forEach(function (side) {
      if (!side.el) {
        return;
      }

      side.ct.ensureDomOrder(side.el);
    });
  }; // returns the pinned left container, either the normal one, or the embedded full with one if exists


  RowComp.prototype.getPinnedLeftRowElement = function () {
    return this.ePinnedLeftRow ? this.ePinnedLeftRow : this.eFullWidthRowLeft;
  }; // returns the pinned right container, either the normal one, or the embedded full with one if exists


  RowComp.prototype.getPinnedRightRowElement = function () {
    return this.ePinnedRightRow ? this.ePinnedRightRow : this.eFullWidthRowRight;
  }; // returns the body container, either the normal one, or the embedded full with one if exists


  RowComp.prototype.getBodyRowElement = function () {
    return this.eBodyRow ? this.eBodyRow : this.eFullWidthRowBody;
  }; // returns the full width container


  RowComp.prototype.getFullWidthRowElement = function () {
    return this.eFullWidthRow;
  };

  RowComp.DOM_DATA_KEY_RENDERED_ROW = 'renderedRow';
  RowComp.FULL_WIDTH_CELL_RENDERER = 'fullWidthCellRenderer';
  RowComp.GROUP_ROW_RENDERER = 'groupRowRenderer';
  RowComp.GROUP_ROW_RENDERER_COMP_NAME = 'agGroupRowRenderer';
  RowComp.LOADING_CELL_RENDERER = 'loadingCellRenderer';
  RowComp.LOADING_CELL_RENDERER_COMP_NAME = 'agLoadingCellRenderer';
  RowComp.DETAIL_CELL_RENDERER = 'detailCellRenderer';
  RowComp.DETAIL_CELL_RENDERER_COMP_NAME = 'agDetailCellRenderer';
  return RowComp;
}(Component);
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __extends$y = undefined && undefined.__extends || function () {
  var _extendStatics35 = function extendStatics(d, b) {
    _extendStatics35 = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics35(d, b);
  };

  return function (d, b) {
    _extendStatics35(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var __decorate$A = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var __param$6 = undefined && undefined.__param || function (paramIndex, decorator) {
  return function (target, key) {
    decorator(target, key, paramIndex);
  };
};

var RowRenderer =
/** @class */
function (_super) {
  __extends$y(RowRenderer, _super);

  function RowRenderer() {
    var _this = _super !== null && _super.apply(this, arguments) || this;

    _this.destroyFuncsForColumnListeners = []; // map of row ids to row objects. keeps track of which elements
    // are rendered for which rows in the dom.

    _this.rowCompsByIndex = {};
    _this.floatingTopRowComps = [];
    _this.floatingBottomRowComps = []; // we only allow one refresh at a time, otherwise the internal memory structure here
    // will get messed up. this can happen if the user has a cellRenderer, and inside the
    // renderer they call an API method that results in another pass of the refresh,
    // then it will be trying to draw rows in the middle of a refresh.

    _this.refreshInProgress = false;
    return _this;
  }

  RowRenderer.prototype.registerGridCore = function (gridCore) {
    this.gridCore = gridCore;
  };

  RowRenderer.prototype.getGridCore = function () {
    return this.gridCore;
  };

  RowRenderer.prototype.agWire = function (loggerFactory) {
    this.logger = loggerFactory.create("RowRenderer");
  };

  RowRenderer.prototype.registerGridComp = function (gridPanel) {
    this.gridPanel = gridPanel;
    this.rowContainers = this.gridPanel.getRowContainers();
    this.addDestroyableEventListener(this.eventService, Events.EVENT_PAGINATION_CHANGED, this.onPageLoaded.bind(this));
    this.addDestroyableEventListener(this.eventService, Events.EVENT_PINNED_ROW_DATA_CHANGED, this.onPinnedRowDataChanged.bind(this));
    this.addDestroyableEventListener(this.eventService, Events.EVENT_DISPLAYED_COLUMNS_CHANGED, this.onDisplayedColumnsChanged.bind(this));
    this.addDestroyableEventListener(this.eventService, Events.EVENT_BODY_SCROLL, this.redrawAfterScroll.bind(this));
    this.addDestroyableEventListener(this.eventService, Events.EVENT_BODY_HEIGHT_CHANGED, this.redrawAfterScroll.bind(this));
    this.addDestroyableEventListener(this.gridOptionsWrapper, GridOptionsWrapper.PROP_DOM_LAYOUT, this.onDomLayoutChanged.bind(this));
    this.registerCellEventListeners();
    this.printLayout = this.gridOptionsWrapper.getDomLayout() === Constants.DOM_LAYOUT_PRINT;
    this.embedFullWidthRows = this.printLayout || this.gridOptionsWrapper.isEmbedFullWidthRows();
    this.redrawAfterModelUpdate();
  }; // in a clean design, each cell would register for each of these events. however when scrolling, all the cells
  // registering and de-registering for events is a performance bottleneck. so we register here once and inform
  // all active cells.


  RowRenderer.prototype.registerCellEventListeners = function () {
    var _this = this;

    this.addDestroyableEventListener(this.eventService, Events.EVENT_CELL_FOCUSED, function (event) {
      _this.forEachCellComp(function (cellComp) {
        return cellComp.onCellFocused(event);
      });
    });
    this.addDestroyableEventListener(this.eventService, Events.EVENT_FLASH_CELLS, function (event) {
      _this.forEachCellComp(function (cellComp) {
        return cellComp.onFlashCells(event);
      });
    });
    this.addDestroyableEventListener(this.eventService, Events.EVENT_COLUMN_HOVER_CHANGED, function () {
      _this.forEachCellComp(function (cellComp) {
        return cellComp.onColumnHover();
      });
    }); // only for printLayout - because we are rendering all the cells in the same row, regardless of pinned state,
    // then changing the width of the containers will impact left position. eg the center cols all have their
    // left position adjusted by the width of the left pinned column, so if the pinned left column width changes,
    // all the center cols need to be shifted to accommodate this. when in normal layout, the pinned cols are
    // in different containers so doesn't impact.

    this.addDestroyableEventListener(this.eventService, Events.EVENT_DISPLAYED_COLUMNS_WIDTH_CHANGED, function () {
      if (_this.printLayout) {
        _this.forEachCellComp(function (cellComp) {
          return cellComp.onLeftChanged();
        });
      }
    });
    var rangeSelectionEnabled = this.gridOptionsWrapper.isEnableRangeSelection();

    if (rangeSelectionEnabled) {
      this.addDestroyableEventListener(this.eventService, Events.EVENT_RANGE_SELECTION_CHANGED, function () {
        _this.forEachCellComp(function (cellComp) {
          return cellComp.onRangeSelectionChanged();
        });
      });
      this.addDestroyableEventListener(this.eventService, Events.EVENT_COLUMN_MOVED, function () {
        _this.forEachCellComp(function (cellComp) {
          return cellComp.updateRangeBordersIfRangeCount();
        });
      });
      this.addDestroyableEventListener(this.eventService, Events.EVENT_COLUMN_PINNED, function () {
        _this.forEachCellComp(function (cellComp) {
          return cellComp.updateRangeBordersIfRangeCount();
        });
      });
      this.addDestroyableEventListener(this.eventService, Events.EVENT_COLUMN_VISIBLE, function () {
        _this.forEachCellComp(function (cellComp) {
          return cellComp.updateRangeBordersIfRangeCount();
        });
      });
    } // add listeners to the grid columns


    this.refreshListenersToColumnsForCellComps(); // if the grid columns change, then refresh the listeners again

    this.addDestroyableEventListener(this.eventService, Events.EVENT_GRID_COLUMNS_CHANGED, this.refreshListenersToColumnsForCellComps.bind(this));
    this.addDestroyFunc(this.removeGridColumnListeners.bind(this));
  }; // executes all functions in destroyFuncsForColumnListeners and then clears the list


  RowRenderer.prototype.removeGridColumnListeners = function () {
    this.destroyFuncsForColumnListeners.forEach(function (func) {
      return func();
    });
    this.destroyFuncsForColumnListeners.length = 0;
  }; // this function adds listeners onto all the grid columns, which are the column that we could have cellComps for.
  // when the grid columns change, we add listeners again. in an ideal design, each CellComp would just register to
  // the column it belongs to on creation, however this was a bottleneck with the number of cells, so do it here
  // once instead.


  RowRenderer.prototype.refreshListenersToColumnsForCellComps = function () {
    var _this = this;

    this.removeGridColumnListeners();
    var cols = this.columnController.getAllGridColumns();

    if (!cols) {
      return;
    }

    cols.forEach(function (col) {
      var forEachCellWithThisCol = function forEachCellWithThisCol(callback) {
        _this.forEachCellComp(function (cellComp) {
          if (cellComp.getColumn() === col) {
            callback(cellComp);
          }
        });
      };

      var leftChangedListener = function leftChangedListener() {
        forEachCellWithThisCol(function (cellComp) {
          return cellComp.onLeftChanged();
        });
      };

      var widthChangedListener = function widthChangedListener() {
        forEachCellWithThisCol(function (cellComp) {
          return cellComp.onWidthChanged();
        });
      };

      var firstRightPinnedChangedListener = function firstRightPinnedChangedListener() {
        forEachCellWithThisCol(function (cellComp) {
          return cellComp.onFirstRightPinnedChanged();
        });
      };

      var lastLeftPinnedChangedListener = function lastLeftPinnedChangedListener() {
        forEachCellWithThisCol(function (cellComp) {
          return cellComp.onLastLeftPinnedChanged();
        });
      };

      col.addEventListener(Column.EVENT_LEFT_CHANGED, leftChangedListener);
      col.addEventListener(Column.EVENT_WIDTH_CHANGED, widthChangedListener);
      col.addEventListener(Column.EVENT_FIRST_RIGHT_PINNED_CHANGED, firstRightPinnedChangedListener);
      col.addEventListener(Column.EVENT_LAST_LEFT_PINNED_CHANGED, lastLeftPinnedChangedListener);

      _this.destroyFuncsForColumnListeners.push(function () {
        col.removeEventListener(Column.EVENT_LEFT_CHANGED, leftChangedListener);
        col.removeEventListener(Column.EVENT_WIDTH_CHANGED, widthChangedListener);
        col.removeEventListener(Column.EVENT_FIRST_RIGHT_PINNED_CHANGED, firstRightPinnedChangedListener);
        col.removeEventListener(Column.EVENT_LAST_LEFT_PINNED_CHANGED, lastLeftPinnedChangedListener);
      });
    });
  };

  RowRenderer.prototype.onDomLayoutChanged = function () {
    var printLayout = this.gridOptionsWrapper.getDomLayout() === Constants.DOM_LAYOUT_PRINT;
    var embedFullWidthRows = printLayout || this.gridOptionsWrapper.isEmbedFullWidthRows(); // if moving towards or away from print layout, means we need to destroy all rows, as rows are not laid
    // out using absolute positioning when doing print layout

    var destroyRows = embedFullWidthRows !== this.embedFullWidthRows || this.printLayout !== printLayout;
    this.printLayout = printLayout;
    this.embedFullWidthRows = embedFullWidthRows;

    if (destroyRows) {
      this.redrawAfterModelUpdate();
    }
  }; // for row models that have datasources, when we update the datasource, we need to force the rowRenderer
  // to redraw all rows. otherwise the old rows from the old datasource will stay displayed.


  RowRenderer.prototype.datasourceChanged = function () {
    this.firstRenderedRow = 0;
    this.lastRenderedRow = -1;
    var rowIndexesToRemove = Object.keys(this.rowCompsByIndex);
    this.removeRowComps(rowIndexesToRemove);
  };

  RowRenderer.prototype.onPageLoaded = function (refreshEvent) {
    if (_.missing(refreshEvent)) {
      refreshEvent = {
        type: Events.EVENT_MODEL_UPDATED,
        api: this.gridApi,
        columnApi: this.columnApi,
        animate: false,
        keepRenderedRows: false,
        newData: false,
        newPage: false
      };
    }

    this.onModelUpdated(refreshEvent);
  };

  RowRenderer.prototype.getAllCellsForColumn = function (column) {
    var eCells = [];

    _.iterateObject(this.rowCompsByIndex, callback);

    _.iterateObject(this.floatingBottomRowComps, callback);

    _.iterateObject(this.floatingTopRowComps, callback);

    function callback(key, rowComp) {
      var eCell = rowComp.getCellForCol(column);

      if (eCell) {
        eCells.push(eCell);
      }
    }

    return eCells;
  };

  RowRenderer.prototype.refreshFloatingRowComps = function () {
    this.refreshFloatingRows(this.floatingTopRowComps, this.pinnedRowModel.getPinnedTopRowData(), this.rowContainers.floatingTopPinnedLeft, this.rowContainers.floatingTopPinnedRight, this.rowContainers.floatingTop, this.rowContainers.floatingTopFullWidth);
    this.refreshFloatingRows(this.floatingBottomRowComps, this.pinnedRowModel.getPinnedBottomRowData(), this.rowContainers.floatingBottomPinnedLeft, this.rowContainers.floatingBottomPinnedRight, this.rowContainers.floatingBottom, this.rowContainers.floatingBottomFullWith);
  };

  RowRenderer.prototype.refreshFloatingRows = function (rowComps, rowNodes, pinnedLeftContainerComp, pinnedRightContainerComp, bodyContainerComp, fullWidthContainerComp) {
    var _this = this;

    rowComps.forEach(function (row) {
      row.destroy();
    });
    rowComps.length = 0;

    if (rowNodes) {
      rowNodes.forEach(function (node) {
        var rowComp = new RowComp(_this.$scope, bodyContainerComp, pinnedLeftContainerComp, pinnedRightContainerComp, fullWidthContainerComp, node, _this.beans, false, false, _this.printLayout, _this.embedFullWidthRows);
        rowComp.init();
        rowComps.push(rowComp);
      });
    }

    this.flushContainers(rowComps);
  };

  RowRenderer.prototype.onPinnedRowDataChanged = function () {
    // recycling rows in order to ensure cell editing is not cancelled
    var params = {
      recycleRows: true
    };
    this.redrawAfterModelUpdate(params);
  };

  RowRenderer.prototype.onModelUpdated = function (refreshEvent) {
    var params = {
      recycleRows: refreshEvent.keepRenderedRows,
      animate: refreshEvent.animate,
      newData: refreshEvent.newData,
      newPage: refreshEvent.newPage,
      // because this is a model updated event (not pinned rows), we
      // can skip updating the pinned rows. this is needed so that if user
      // is doing transaction updates, the pinned rows are not getting constantly
      // trashed - or editing cells in pinned rows are not refreshed and put into read mode
      onlyBody: true
    };
    this.redrawAfterModelUpdate(params);
  }; // if the row nodes are not rendered, no index is returned


  RowRenderer.prototype.getRenderedIndexesForRowNodes = function (rowNodes) {
    var result = [];

    if (_.missing(rowNodes)) {
      return result;
    }

    _.iterateObject(this.rowCompsByIndex, function (index, renderedRow) {
      var rowNode = renderedRow.getRowNode();

      if (rowNodes.indexOf(rowNode) >= 0) {
        result.push(index);
      }
    });

    return result;
  };

  RowRenderer.prototype.redrawRows = function (rowNodes) {
    if (!rowNodes || rowNodes.length == 0) {
      return;
    } // we only need to be worried about rendered rows, as this method is
    // called to what's rendered. if the row isn't rendered, we don't care


    var indexesToRemove = this.getRenderedIndexesForRowNodes(rowNodes); // remove the rows

    this.removeRowComps(indexesToRemove); // add draw them again

    this.redrawAfterModelUpdate({
      recycleRows: true
    });
  };

  RowRenderer.prototype.getCellToRestoreFocusToAfterRefresh = function (params) {
    var focusedCell = params.suppressKeepFocus ? null : this.focusedCellController.getFocusCellToUseAfterRefresh();

    if (_.missing(focusedCell)) {
      return null;
    } // if the dom is not actually focused on a cell, then we don't try to refocus. the problem this
    // solves is with editing - if the user is editing, eg focus is on a text field, and not on the
    // cell itself, then the cell can be registered as having focus, however it's the text field that
    // has the focus and not the cell div. therefore, when the refresh is finished, the grid will focus
    // the cell, and not the textfield. that means if the user is in a text field, and the grid refreshes,
    // the focus is lost from the text field. we do not want this.


    var activeElement = document.activeElement;
    var domData = this.gridOptionsWrapper.getDomData(activeElement, CellComp.DOM_DATA_KEY_CELL_COMP);

    var elementIsNotACellDev = _.missing(domData);

    if (elementIsNotACellDev) {
      return null;
    }

    return focusedCell;
  }; // gets called after changes to the model.


  RowRenderer.prototype.redrawAfterModelUpdate = function (params) {
    if (params === void 0) {
      params = {};
    }

    this.getLockOnRefresh();
    var focusedCell = this.getCellToRestoreFocusToAfterRefresh(params);
    this.sizeContainerToPageHeight();
    this.scrollToTopIfNewData(params); // never recycle rows when print layout, we draw each row again from scratch. this is because print layout
    // uses normal dom layout to put cells into dom - it doesn't allow reordering rows.

    var recycleRows = !this.printLayout && params.recycleRows;
    var animate = params.animate && this.gridOptionsWrapper.isAnimateRows();
    var rowsToRecycle = this.binRowComps(recycleRows);
    this.redraw(rowsToRecycle, animate);

    if (!params.onlyBody) {
      this.refreshFloatingRowComps();
    }

    this.restoreFocusedCell(focusedCell);
    this.releaseLockOnRefresh();
  };

  RowRenderer.prototype.scrollToTopIfNewData = function (params) {
    var scrollToTop = params.newData || params.newPage;
    var suppressScrollToTop = this.gridOptionsWrapper.isSuppressScrollOnNewData();

    if (scrollToTop && !suppressScrollToTop) {
      this.gridPanel.scrollToTop();
    }
  };

  RowRenderer.prototype.sizeContainerToPageHeight = function () {
    var containers = [this.rowContainers.body, this.rowContainers.fullWidth, this.rowContainers.pinnedLeft, this.rowContainers.pinnedRight];

    if (this.printLayout) {
      containers.forEach(function (container) {
        return container.setHeight(null);
      });
      return;
    }

    var containerHeight = this.paginationProxy.getCurrentPageHeight(); // we need at least 1 pixel for the horizontal scroll to work. so if there are now rows,
    // we still want the scroll to be present, otherwise there would be no way to scroll the header
    // which might be needed us user wants to access columns
    // on the RHS - and if that was where the filter was that cause no rows to be presented, there
    // is no way to remove the filter.

    if (containerHeight === 0) {
      containerHeight = 1;
    }

    this.maxDivHeightScaler.setModelHeight(containerHeight);
    var realHeight = this.maxDivHeightScaler.getUiContainerHeight();
    containers.forEach(function (container) {
      return container.setHeight(realHeight);
    });
  };

  RowRenderer.prototype.getLockOnRefresh = function () {
    if (this.refreshInProgress) {
      throw new Error("ag-Grid: cannot get grid to draw rows when it is in the middle of drawing rows. " + "Your code probably called a grid API method while the grid was in the render stage. To overcome " + "this, put the API call into a timeout, eg instead of api.refreshView(), " + "call setTimeout(function(){api.refreshView(),0}). To see what part of your code " + "that caused the refresh check this stacktrace.");
    }

    this.refreshInProgress = true;
  };

  RowRenderer.prototype.releaseLockOnRefresh = function () {
    this.refreshInProgress = false;
  }; // sets the focus to the provided cell, if the cell is provided. this way, the user can call refresh without
  // worry about the focus been lost. this is important when the user is using keyboard navigation to do edits
  // and the cellEditor is calling 'refresh' to get other cells to update (as other cells might depend on the
  // edited cell).


  RowRenderer.prototype.restoreFocusedCell = function (cellPosition) {
    if (cellPosition) {
      this.focusedCellController.setFocusedCell(cellPosition.rowIndex, cellPosition.column, cellPosition.rowPinned, true);
    }
  };

  RowRenderer.prototype.stopEditing = function (cancel) {
    if (cancel === void 0) {
      cancel = false;
    }

    this.forEachRowComp(function (key, rowComp) {
      rowComp.stopEditing(cancel);
    });
  };

  RowRenderer.prototype.forEachCellComp = function (callback) {
    this.forEachRowComp(function (key, rowComp) {
      return rowComp.forEachCellComp(callback);
    });
  };

  RowRenderer.prototype.forEachRowComp = function (callback) {
    _.iterateObject(this.rowCompsByIndex, callback);

    _.iterateObject(this.floatingTopRowComps, callback);

    _.iterateObject(this.floatingBottomRowComps, callback);
  };

  RowRenderer.prototype.addRenderedRowListener = function (eventName, rowIndex, callback) {
    var rowComp = this.rowCompsByIndex[rowIndex];

    if (rowComp) {
      rowComp.addEventListener(eventName, callback);
    }
  };

  RowRenderer.prototype.flashCells = function (params) {
    if (params === void 0) {
      params = {};
    }

    this.forEachCellCompFiltered(params.rowNodes, params.columns, function (cellComp) {
      return cellComp.flashCell();
    });
  };

  RowRenderer.prototype.refreshCells = function (params) {
    if (params === void 0) {
      params = {};
    }

    var refreshCellParams = {
      forceRefresh: params.force,
      newData: false
    };
    this.forEachCellCompFiltered(params.rowNodes, params.columns, function (cellComp) {
      return cellComp.refreshCell(refreshCellParams);
    });
  };

  RowRenderer.prototype.getCellRendererInstances = function (params) {
    var res = [];
    this.forEachCellCompFiltered(params.rowNodes, params.columns, function (cellComp) {
      var cellRenderer = cellComp.getCellRenderer();

      if (cellRenderer) {
        res.push(cellRenderer);
      }
    });
    return res;
  };

  RowRenderer.prototype.getCellEditorInstances = function (params) {
    var res = [];
    this.forEachCellCompFiltered(params.rowNodes, params.columns, function (cellComp) {
      var cellEditor = cellComp.getCellEditor();

      if (cellEditor) {
        res.push(cellEditor);
      }
    });
    return res;
  };

  RowRenderer.prototype.getEditingCells = function () {
    var res = [];
    this.forEachCellComp(function (cellComp) {
      if (cellComp.isEditing()) {
        var cellPosition = cellComp.getCellPosition();
        res.push(cellPosition);
      }
    });
    return res;
  }; // calls the callback for each cellComp that match the provided rowNodes and columns. eg if one row node
  // and two columns provided, that identifies 4 cells, so callback gets called 4 times, once for each cell.


  RowRenderer.prototype.forEachCellCompFiltered = function (rowNodes, columns, callback) {
    var _this = this;

    var rowIdsMap;

    if (_.exists(rowNodes)) {
      rowIdsMap = {
        top: {},
        bottom: {},
        normal: {}
      };
      rowNodes.forEach(function (rowNode) {
        if (rowNode.rowPinned === Constants.PINNED_TOP) {
          rowIdsMap.top[rowNode.id] = true;
        } else if (rowNode.rowPinned === Constants.PINNED_BOTTOM) {
          rowIdsMap.bottom[rowNode.id] = true;
        } else {
          rowIdsMap.normal[rowNode.id] = true;
        }
      });
    }

    var colIdsMap;

    if (_.exists(columns)) {
      colIdsMap = {};
      columns.forEach(function (colKey) {
        var column = _this.columnController.getGridColumn(colKey);

        if (_.exists(column)) {
          colIdsMap[column.getId()] = true;
        }
      });
    }

    var processRow = function processRow(rowComp) {
      var rowNode = rowComp.getRowNode();
      var id = rowNode.id;
      var floating = rowNode.rowPinned; // skip this row if it is missing from the provided list

      if (_.exists(rowIdsMap)) {
        if (floating === Constants.PINNED_BOTTOM) {
          if (!rowIdsMap.bottom[id]) {
            return;
          }
        } else if (floating === Constants.PINNED_TOP) {
          if (!rowIdsMap.top[id]) {
            return;
          }
        } else {
          if (!rowIdsMap.normal[id]) {
            return;
          }
        }
      }

      rowComp.forEachCellComp(function (cellComp) {
        var colId = cellComp.getColumn().getId();
        var excludeColFromRefresh = colIdsMap && !colIdsMap[colId];

        if (excludeColFromRefresh) {
          return;
        }

        callback(cellComp);
      });
    };

    _.iterateObject(this.rowCompsByIndex, function (index, rowComp) {
      processRow(rowComp);
    });

    if (this.floatingTopRowComps) {
      this.floatingTopRowComps.forEach(processRow);
    }

    if (this.floatingBottomRowComps) {
      this.floatingBottomRowComps.forEach(processRow);
    }
  };

  RowRenderer.prototype.destroy = function () {
    _super.prototype.destroy.call(this);

    var rowIndexesToRemove = Object.keys(this.rowCompsByIndex);
    this.removeRowComps(rowIndexesToRemove);
  };

  RowRenderer.prototype.binRowComps = function (recycleRows) {
    var _this = this;

    var indexesToRemove;
    var rowsToRecycle = {};

    if (recycleRows) {
      indexesToRemove = [];

      _.iterateObject(this.rowCompsByIndex, function (index, rowComp) {
        var rowNode = rowComp.getRowNode();

        if (_.exists(rowNode.id)) {
          rowsToRecycle[rowNode.id] = rowComp;
          delete _this.rowCompsByIndex[index];
        } else {
          indexesToRemove.push(index);
        }
      });
    } else {
      indexesToRemove = Object.keys(this.rowCompsByIndex);
    }

    this.removeRowComps(indexesToRemove);
    return rowsToRecycle;
  }; // takes array of row indexes


  RowRenderer.prototype.removeRowComps = function (rowsToRemove) {
    var _this = this; // if no fromIndex then set to -1, which will refresh everything
    // let realFromIndex = -1;


    rowsToRemove.forEach(function (indexToRemove) {
      var renderedRow = _this.rowCompsByIndex[indexToRemove];
      renderedRow.destroy();
      delete _this.rowCompsByIndex[indexToRemove];
    });
  }; // gets called when rows don't change, but viewport does, so after:
  // 1) height of grid body changes, ie number of displayed rows has changed
  // 2) grid scrolled to new position
  // 3) ensure index visible (which is a scroll)


  RowRenderer.prototype.redrawAfterScroll = function () {
    this.getLockOnRefresh();
    this.redraw(null, false, true);
    this.releaseLockOnRefresh();
  };

  RowRenderer.prototype.removeRowCompsNotToDraw = function (indexesToDraw) {
    // for speedy lookup, dump into map
    var indexesToDrawMap = {};
    indexesToDraw.forEach(function (index) {
      return indexesToDrawMap[index] = true;
    });
    var existingIndexes = Object.keys(this.rowCompsByIndex);
    var indexesNotToDraw = existingIndexes.filter(function (index) {
      return !indexesToDrawMap[index];
    });
    this.removeRowComps(indexesNotToDraw);
  };

  RowRenderer.prototype.calculateIndexesToDraw = function () {
    var _this = this; // all in all indexes in the viewport


    var indexesToDraw = _.createArrayOfNumbers(this.firstRenderedRow, this.lastRenderedRow); // add in indexes of rows we want to keep, because they are currently editing


    _.iterateObject(this.rowCompsByIndex, function (indexStr, rowComp) {
      var index = Number(indexStr);

      if (index < _this.firstRenderedRow || index > _this.lastRenderedRow) {
        if (_this.doNotUnVirtualiseRow(rowComp)) {
          indexesToDraw.push(index);
        }
      }
    });

    indexesToDraw.sort(function (a, b) {
      return a - b;
    });
    return indexesToDraw;
  };

  RowRenderer.prototype.redraw = function (rowsToRecycle, animate, afterScroll) {
    var _this = this;

    if (animate === void 0) {
      animate = false;
    }

    if (afterScroll === void 0) {
      afterScroll = false;
    }

    this.maxDivHeightScaler.updateOffset();
    this.workOutFirstAndLastRowsToRender(); // the row can already exist and be in the following:
    // rowsToRecycle -> if model change, then the index may be different, however row may
    //                         exist here from previous time (mapped by id).
    // this.rowCompsByIndex -> if just a scroll, then this will contain what is currently in the viewport
    // this is all the indexes we want, including those that already exist, so this method
    // will end up going through each index and drawing only if the row doesn't already exist

    var indexesToDraw = this.calculateIndexesToDraw();
    this.removeRowCompsNotToDraw(indexesToDraw); // never animate when doing print layout - as we want to get things ready to print as quickly as possible,
    // otherwise we risk the printer printing a row that's half faded (half way through fading in)

    if (this.printLayout) {
      animate = false;
    } // add in new rows


    var nextVmTurnFunctions = [];
    var rowComps = [];
    indexesToDraw.forEach(function (rowIndex) {
      var rowComp = _this.createOrUpdateRowComp(rowIndex, rowsToRecycle, animate, afterScroll);

      if (_.exists(rowComp)) {
        rowComps.push(rowComp);

        _.pushAll(nextVmTurnFunctions, rowComp.getAndClearNextVMTurnFunctions());
      }
    });
    this.flushContainers(rowComps);

    _.executeNextVMTurn(nextVmTurnFunctions);

    var useAnimationFrame = afterScroll && !this.gridOptionsWrapper.isSuppressAnimationFrame() && !this.printLayout;

    if (useAnimationFrame) {
      this.beans.taskQueue.addDestroyTask(this.destroyRowComps.bind(this, rowsToRecycle, animate));
    } else {
      this.destroyRowComps(rowsToRecycle, animate);
    }

    this.checkAngularCompile();
    this.gridPanel.updateRowCount();
  };

  RowRenderer.prototype.flushContainers = function (rowComps) {
    _.iterateObject(this.rowContainers, function (key, rowContainerComp) {
      if (rowContainerComp) {
        rowContainerComp.flushRowTemplates();
      }
    });

    rowComps.forEach(function (rowComp) {
      return rowComp.afterFlush();
    });
  };

  RowRenderer.prototype.onDisplayedColumnsChanged = function () {
    var pinningLeft = this.columnController.isPinningLeft();
    var pinningRight = this.columnController.isPinningRight();
    var atLeastOneChanged = this.pinningLeft !== pinningLeft || pinningRight !== this.pinningRight;

    if (atLeastOneChanged) {
      this.pinningLeft = pinningLeft;
      this.pinningRight = pinningRight;

      if (this.embedFullWidthRows) {
        this.redrawFullWidthEmbeddedRows();
      }
    }
  }; // when embedding, what gets showed in each section depends on what is pinned. eg if embedding group expand / collapse,
  // then it should go into the pinned left area if pinning left, or the center area if not pinning.


  RowRenderer.prototype.redrawFullWidthEmbeddedRows = function () {
    // if either of the pinned panels has shown / hidden, then need to redraw the fullWidth bits when
    // embedded, as what appears in each section depends on whether we are pinned or not
    var rowsToRemove = [];

    _.iterateObject(this.rowCompsByIndex, function (id, rowComp) {
      if (rowComp.isFullWidth()) {
        var rowIndex = rowComp.getRowNode().rowIndex;
        rowsToRemove.push(rowIndex.toString());
      }
    });

    this.refreshFloatingRowComps();
    this.removeRowComps(rowsToRemove);
    this.redrawAfterScroll();
  };

  RowRenderer.prototype.refreshFullWidthRows = function () {
    var rowsToRemove = [];

    _.iterateObject(this.rowCompsByIndex, function (id, rowComp) {
      if (rowComp.isFullWidth()) {
        var fullWidthRowsRefreshed = rowComp.refreshFullWidth();

        if (!fullWidthRowsRefreshed) {
          var rowIndex = rowComp.getRowNode().rowIndex;
          rowsToRemove.push(rowIndex.toString());
        }
      }
    });

    this.removeRowComps(rowsToRemove);
    this.redrawAfterScroll();
  };

  RowRenderer.prototype.createOrUpdateRowComp = function (rowIndex, rowsToRecycle, animate, afterScroll) {
    var rowNode;
    var rowComp = this.rowCompsByIndex[rowIndex]; // if no row comp, see if we can get it from the previous rowComps

    if (!rowComp) {
      rowNode = this.paginationProxy.getRow(rowIndex);

      if (_.exists(rowNode) && _.exists(rowsToRecycle) && rowsToRecycle[rowNode.id] && rowNode.alreadyRendered) {
        rowComp = rowsToRecycle[rowNode.id];
        rowsToRecycle[rowNode.id] = null;
      }
    }

    var creatingNewRowComp = !rowComp;

    if (creatingNewRowComp) {
      // create a new one
      if (!rowNode) {
        rowNode = this.paginationProxy.getRow(rowIndex);
      }

      if (_.exists(rowNode)) {
        rowComp = this.createRowComp(rowNode, animate, afterScroll);
      } else {
        // this should never happen - if somehow we are trying to create
        // a row for a rowNode that does not exist.
        return;
      }
    } else {
      // ensure row comp is in right position in DOM
      rowComp.ensureDomOrder();
    }

    if (rowNode) {
      // set node as 'alreadyRendered' to ensure we only recycle rowComps that have been rendered, this ensures
      // we don't reuse rowComps that have been removed and then re-added in the same batch transaction.
      rowNode.alreadyRendered = true;
    }

    this.rowCompsByIndex[rowIndex] = rowComp;
    return rowComp;
  };

  RowRenderer.prototype.destroyRowComps = function (rowCompsMap, animate) {
    var delayedFuncs = [];

    _.iterateObject(rowCompsMap, function (nodeId, rowComp) {
      // if row was used, then it's null
      if (!rowComp) {
        return;
      }

      rowComp.destroy(animate);

      _.pushAll(delayedFuncs, rowComp.getAndClearDelayedDestroyFunctions());
    });

    _.executeInAWhile(delayedFuncs);
  };

  RowRenderer.prototype.checkAngularCompile = function () {
    var _this = this; // if we are doing angular compiling, then do digest the scope here


    if (this.gridOptionsWrapper.isAngularCompileRows()) {
      // we do it in a timeout, in case we are already in an apply
      window.setTimeout(function () {
        _this.$scope.$apply();
      }, 0);
    }
  };

  RowRenderer.prototype.workOutFirstAndLastRowsToRender = function () {
    var _this = this;

    var newFirst;
    var newLast;

    if (!this.paginationProxy.isRowsToRender()) {
      newFirst = 0;
      newLast = -1; // setting to -1 means nothing in range
    } else if (this.printLayout) {
      newFirst = this.paginationProxy.getPageFirstRow();
      newLast = this.paginationProxy.getPageLastRow();
    } else {
      var paginationOffset = this.paginationProxy.getPixelOffset();
      var maxDivHeightScaler = this.maxDivHeightScaler.getOffset();
      var bodyVRange = this.gridPanel.getVScrollPosition();
      var bodyTopPixel = bodyVRange.top;
      var bodyBottomPixel = bodyVRange.bottom;
      var bufferPixels = this.gridOptionsWrapper.getRowBufferInPixels();
      var firstPixel = bodyTopPixel + paginationOffset + maxDivHeightScaler - bufferPixels;
      var lastPixel = bodyBottomPixel + paginationOffset + maxDivHeightScaler + bufferPixels;
      this.ensureAllRowsInRangeHaveHeightsCalculated(firstPixel, lastPixel);
      var firstRowIndex = this.paginationProxy.getRowIndexAtPixel(firstPixel);
      var lastRowIndex = this.paginationProxy.getRowIndexAtPixel(lastPixel);
      var pageFirstRow = this.paginationProxy.getPageFirstRow();
      var pageLastRow = this.paginationProxy.getPageLastRow(); // adjust, in case buffer extended actual size

      if (firstRowIndex < pageFirstRow) {
        firstRowIndex = pageFirstRow;
      }

      if (lastRowIndex > pageLastRow) {
        lastRowIndex = pageLastRow;
      }

      newFirst = firstRowIndex;
      newLast = lastRowIndex;
    } // sometimes user doesn't set CSS right and ends up with grid with no height and grid ends up
    // trying to render all the rows, eg 10,000+ rows. this will kill the browser. so instead of
    // killing the browser, we limit the number of rows. just in case some use case we didn't think
    // of, we also have a property to not do this operation.


    var rowLayoutNormal = this.gridOptionsWrapper.getDomLayout() === Constants.DOM_LAYOUT_NORMAL;
    var suppressRowCountRestriction = this.gridOptionsWrapper.isSuppressMaxRenderedRowRestriction();
    var rowBufferMaxSize = Math.max(this.gridOptionsWrapper.getRowBuffer(), 500);

    if (rowLayoutNormal && !suppressRowCountRestriction) {
      if (newLast - newFirst > rowBufferMaxSize) {
        newLast = newFirst + rowBufferMaxSize;
      }
    }

    var firstDiffers = newFirst !== this.firstRenderedRow;
    var lastDiffers = newLast !== this.lastRenderedRow;

    if (firstDiffers || lastDiffers) {
      this.firstRenderedRow = newFirst;
      this.lastRenderedRow = newLast;
      var event_1 = {
        type: Events.EVENT_VIEWPORT_CHANGED,
        firstRow: newFirst,
        lastRow: newLast,
        api: this.gridApi,
        columnApi: this.columnApi
      };
      this.eventService.dispatchEvent(event_1);
    } // only dispatch firstDataRendered if we have actually rendered some data


    if (this.paginationProxy.isRowsToRender()) {
      var event_2 = {
        type: Events.EVENT_FIRST_DATA_RENDERED,
        firstRow: newFirst,
        lastRow: newLast,
        api: this.gridApi,
        columnApi: this.columnApi
      }; // added a small delay here because in some scenarios this can be fired
      // before the grid is actually rendered, causing component creation
      // on EVENT_FIRST_DATA_RENDERED to fail.

      window.setTimeout(function () {
        return _this.eventService.dispatchEventOnce(event_2);
      }, 50);
    }
  };

  RowRenderer.prototype.ensureAllRowsInRangeHaveHeightsCalculated = function (topPixel, bottomPixel) {
    // ensureRowHeightsVisible only works with CSRM, as it's the only row model that allows lazy row height calcs.
    // all the other row models just hard code so the method just returns back false
    var rowHeightsChanged = this.paginationProxy.ensureRowHeightsValid(topPixel, bottomPixel, -1, -1);

    if (rowHeightsChanged) {
      // if row heights have changed, we need to resize the containers the rows sit it
      this.sizeContainerToPageHeight(); // we also need to update heightScaler as this has dependency of row container height

      this.maxDivHeightScaler.updateOffset();
    }
  };

  RowRenderer.prototype.getFirstVirtualRenderedRow = function () {
    return this.firstRenderedRow;
  };

  RowRenderer.prototype.getLastVirtualRenderedRow = function () {
    return this.lastRenderedRow;
  }; // check that none of the rows to remove are editing or focused as:
  // a) if editing, we want to keep them, otherwise the user will loose the context of the edit,
  //    eg user starts editing, enters some text, then scrolls down and then up, next time row rendered
  //    the edit is reset - so we want to keep it rendered.
  // b) if focused, we want ot keep keyboard focus, so if user ctrl+c, it goes to clipboard,
  //    otherwise the user can range select and drag (with focus cell going out of the viewport)
  //    and then ctrl+c, nothing will happen if cell is removed from dom.
  // c) if detail record of master detail, as users complained that the context of detail rows
  //    was getting lost when detail row out of view. eg user expands to show detail row,
  //    then manipulates the detail panel (eg sorts the detail grid), then context is lost
  //    after detail panel is scrolled out of / into view.


  RowRenderer.prototype.doNotUnVirtualiseRow = function (rowComp) {
    var REMOVE_ROW = false;
    var KEEP_ROW = true;
    var rowNode = rowComp.getRowNode();
    var rowHasFocus = this.focusedCellController.isRowNodeFocused(rowNode);
    var rowIsEditing = rowComp.isEditing();
    var rowIsDetail = rowNode.detail;
    var mightWantToKeepRow = rowHasFocus || rowIsEditing || rowIsDetail; // if we deffo don't want to keep it,

    if (!mightWantToKeepRow) {
      return REMOVE_ROW;
    } // editing row, only remove if it is no longer rendered, eg filtered out or new data set.
    // the reason we want to keep is if user is scrolling up and down, we don't want to loose
    // the context of the editing in process.


    var rowNodePresent = this.paginationProxy.isRowPresent(rowNode);
    return rowNodePresent ? KEEP_ROW : REMOVE_ROW;
  };

  RowRenderer.prototype.createRowComp = function (rowNode, animate, afterScroll) {
    var suppressAnimationFrame = this.gridOptionsWrapper.isSuppressAnimationFrame(); // we don't use animations frames for printing, so the user can put the grid into print mode
    // and immediately print - otherwise the user would have to wait for the rows to draw in the background
    // (via the animation frames) which is awkward to do from code.
    // we only do the animation frames after scrolling, as this is where we want the smooth user experience.
    // having animation frames for other times makes the grid look 'jumpy'.

    var useAnimationFrameForCreate = afterScroll && !suppressAnimationFrame && !this.printLayout;
    var rowComp = new RowComp(this.$scope, this.rowContainers.body, this.rowContainers.pinnedLeft, this.rowContainers.pinnedRight, this.rowContainers.fullWidth, rowNode, this.beans, animate, useAnimationFrameForCreate, this.printLayout, this.embedFullWidthRows);
    rowComp.init();
    return rowComp;
  };

  RowRenderer.prototype.getRenderedNodes = function () {
    var renderedRows = this.rowCompsByIndex;
    return Object.keys(renderedRows).map(function (key) {
      return renderedRows[key].getRowNode();
    });
  }; // we use index for rows, but column object for columns, as the next column (by index) might not
  // be visible (header grouping) so it's not reliable, so using the column object instead.


  RowRenderer.prototype.navigateToNextCell = function (event, key, currentCell, allowUserOverride) {
    // we keep searching for a next cell until we find one. this is how the group rows get skipped
    var nextCell = currentCell;
    var finished = false;

    while (!finished) {
      // if the current cell is spanning across multiple columns, we need to move
      // our current position to be the last cell on the right before finding the
      // the next target.
      if (this.gridOptionsWrapper.isEnableRtl()) {
        if (key === Constants.KEY_LEFT) {
          nextCell = this.getLastCellOfColSpan(nextCell);
        }
      } else if (key === Constants.KEY_RIGHT) {
        nextCell = this.getLastCellOfColSpan(nextCell);
      }

      nextCell = this.cellNavigationService.getNextCellToFocus(key, nextCell); // eg if going down, and nextCell=undefined, means we are gone past the last row

      var hitEdgeOfGrid = _.missing(nextCell);

      if (hitEdgeOfGrid) {
        finished = true;
        continue;
      }

      var rowNode = this.rowPositionUtils.getRowNode(nextCell); // we do not allow focusing on detail rows and full width rows

      if (rowNode.detail || rowNode.isFullWidthCell()) {
        continue;
      } // if not a group, then we have a valid row, so quit the search


      if (!rowNode.group) {
        finished = true;
        continue;
      } // full width rows cannot be focused, so if it's a group and using full width rows,
      // we need to skip over the row


      var pivotMode = this.columnController.isPivotMode();
      var usingFullWidthRows = this.gridOptionsWrapper.isGroupUseEntireRow(pivotMode);

      if (!usingFullWidthRows) {
        finished = true;
      }
    } // allow user to override what cell to go to next. when doing normal cell navigation (with keys)
    // we allow this, however if processing 'enter after edit' we don't allow override


    if (allowUserOverride) {
      var userFunc = this.gridOptionsWrapper.getNavigateToNextCellFunc();

      if (_.exists(userFunc)) {
        var params = {
          key: key,
          previousCellPosition: currentCell,
          nextCellPosition: nextCell ? nextCell : null,
          event: event
        };
        var userCell = userFunc(params);

        if (_.exists(userCell)) {
          if (userCell.floating) {
            _.doOnce(function () {
              console.warn("ag-Grid: tabToNextCellFunc return type should have attributes: rowIndex, rowPinned, column. However you had 'floating', maybe you meant 'rowPinned'?");
            }, 'no floating in userCell');

            userCell.rowPinned = userCell.floating;
          }

          nextCell = {
            rowPinned: userCell.rowPinned,
            rowIndex: userCell.rowIndex,
            column: userCell.column
          };
        } else {
          nextCell = null;
        }
      }
    } // no next cell means we have reached a grid boundary, eg left, right, top or bottom of grid


    if (!nextCell) {
      return;
    } // in case we have col spanning we get the cellComp and use it to
    // get the position. This was we always focus the first cell inside
    // the spanning.


    this.ensureCellVisible(nextCell); // ensureCellVisible first, to make sure nextCell is rendered

    var cellComp = this.getComponentForCell(nextCell);
    nextCell = cellComp.getCellPosition(); // we call this again, as nextCell can be different to it's previous value due to Column Spanning
    // (ie if cursor moving from right to left, and cell is spanning columns, then nextCell was the
    // last column in the group, however now it's the first column in the group). if we didn't do
    // ensureCellVisible again, then we could only be showing the last portion (last column) of the
    // merged cells.

    this.ensureCellVisible(nextCell);
    this.focusedCellController.setFocusedCell(nextCell.rowIndex, nextCell.column, nextCell.rowPinned, true);

    if (this.rangeController) {
      this.rangeController.setRangeToCell(nextCell);
    }
  };

  RowRenderer.prototype.getLastCellOfColSpan = function (cell) {
    var cellComp = this.getComponentForCell(cell);

    if (!cellComp) {
      return cell;
    }

    var colSpanningList = cellComp.getColSpanningList();

    if (colSpanningList.length === 1) {
      return cell;
    }

    return {
      rowIndex: cell.rowIndex,
      column: _.last(colSpanningList),
      rowPinned: cell.rowPinned
    };
  };

  RowRenderer.prototype.ensureCellVisible = function (gridCell) {
    // this scrolls the row into view
    if (_.missing(gridCell.rowPinned)) {
      this.gridPanel.ensureIndexVisible(gridCell.rowIndex);
    }

    if (!gridCell.column.isPinned()) {
      this.gridPanel.ensureColumnVisible(gridCell.column);
    } // need to nudge the scrolls for the floating items. otherwise when we set focus on a non-visible
    // floating cell, the scrolls get out of sync


    this.gridPanel.horizontallyScrollHeaderCenterAndFloatingCenter(); // need to flush frames, to make sure the correct cells are rendered

    this.animationFrameService.flushAllFrames();
  };

  RowRenderer.prototype.startEditingCell = function (gridCell, keyPress, charPress) {
    var cell = this.getComponentForCell(gridCell);

    if (cell) {
      cell.startRowOrCellEdit(keyPress, charPress);
    }
  };

  RowRenderer.prototype.getComponentForCell = function (cellPosition) {
    var rowComponent;

    switch (cellPosition.rowPinned) {
      case Constants.PINNED_TOP:
        rowComponent = this.floatingTopRowComps[cellPosition.rowIndex];
        break;

      case Constants.PINNED_BOTTOM:
        rowComponent = this.floatingBottomRowComps[cellPosition.rowIndex];
        break;

      default:
        rowComponent = this.rowCompsByIndex[cellPosition.rowIndex];
        break;
    }

    if (!rowComponent) {
      return null;
    }

    var cellComponent = rowComponent.getRenderedCellForColumn(cellPosition.column);
    return cellComponent;
  };

  RowRenderer.prototype.getRowNode = function (gridRow) {
    switch (gridRow.rowPinned) {
      case Constants.PINNED_TOP:
        return this.pinnedRowModel.getPinnedTopRowData()[gridRow.rowIndex];

      case Constants.PINNED_BOTTOM:
        return this.pinnedRowModel.getPinnedBottomRowData()[gridRow.rowIndex];

      default:
        return this.rowModel.getRow(gridRow.rowIndex);
    }
  };

  RowRenderer.prototype.onTabKeyDown = function (previousRenderedCell, keyboardEvent) {
    var backwards = keyboardEvent.shiftKey;
    var success = this.moveToCellAfter(previousRenderedCell, backwards);

    if (success) {
      keyboardEvent.preventDefault();
    }
  };

  RowRenderer.prototype.tabToNextCell = function (backwards) {
    var focusedCell = this.focusedCellController.getFocusedCell(); // if no focus, then cannot navigate

    if (_.missing(focusedCell)) {
      return false;
    }

    var renderedCell = this.getComponentForCell(focusedCell); // if cell is not rendered, means user has scrolled away from the cell

    if (_.missing(renderedCell)) {
      return false;
    }

    var result = this.moveToCellAfter(renderedCell, backwards);
    return result;
  };

  RowRenderer.prototype.moveToCellAfter = function (previousRenderedCell, backwards) {
    var editing = previousRenderedCell.isEditing();
    var res;

    if (editing) {
      if (this.gridOptionsWrapper.isFullRowEdit()) {
        res = this.moveToNextEditingRow(previousRenderedCell, backwards);
      } else {
        res = this.moveToNextEditingCell(previousRenderedCell, backwards);
      }
    } else {
      res = this.moveToNextCellNotEditing(previousRenderedCell, backwards);
    }

    return res;
  };

  RowRenderer.prototype.moveToNextEditingCell = function (previousRenderedCell, backwards) {
    var gridCell = previousRenderedCell.getCellPosition(); // need to do this before getting next cell to edit, in case the next cell
    // has editable function (eg colDef.editable=func() ) and it depends on the
    // result of this cell, so need to save updates from the first edit, in case
    // the value is referenced in the function.

    previousRenderedCell.stopEditing(); // find the next cell to start editing

    var nextRenderedCell = this.findNextCellToFocusOn(gridCell, backwards, true);

    var foundCell = _.exists(nextRenderedCell); // only prevent default if we found a cell. so if user is on last cell and hits tab, then we default
    // to the normal tabbing so user can exit the grid.


    if (foundCell) {
      nextRenderedCell.startEditingIfEnabled(null, null, true);
      nextRenderedCell.focusCell(false);
    }

    return foundCell;
  };

  RowRenderer.prototype.moveToNextEditingRow = function (previousRenderedCell, backwards) {
    var gridCell = previousRenderedCell.getCellPosition(); // find the next cell to start editing

    var nextRenderedCell = this.findNextCellToFocusOn(gridCell, backwards, true);

    var foundCell = _.exists(nextRenderedCell); // only prevent default if we found a cell. so if user is on last cell and hits tab, then we default
    // to the normal tabbing so user can exit the grid.


    if (foundCell) {
      this.moveEditToNextCellOrRow(previousRenderedCell, nextRenderedCell);
    }

    return foundCell;
  };

  RowRenderer.prototype.moveToNextCellNotEditing = function (previousRenderedCell, backwards) {
    var gridCell = previousRenderedCell.getCellPosition(); // find the next cell to start editing

    var nextRenderedCell = this.findNextCellToFocusOn(gridCell, backwards, false);

    var foundCell = _.exists(nextRenderedCell); // only prevent default if we found a cell. so if user is on last cell and hits tab, then we default
    // to the normal tabbing so user can exit the grid.


    if (foundCell) {
      nextRenderedCell.focusCell(true);
    }

    return foundCell;
  };

  RowRenderer.prototype.moveEditToNextCellOrRow = function (previousRenderedCell, nextRenderedCell) {
    var pGridCell = previousRenderedCell.getCellPosition();
    var nGridCell = nextRenderedCell.getCellPosition();
    var rowsMatch = pGridCell.rowIndex === nGridCell.rowIndex && pGridCell.rowPinned === nGridCell.rowPinned;

    if (rowsMatch) {
      // same row, so we don't start / stop editing, we just move the focus along
      previousRenderedCell.setFocusOutOnEditor();
      nextRenderedCell.setFocusInOnEditor();
    } else {
      var pRow = previousRenderedCell.getRenderedRow();
      var nRow = nextRenderedCell.getRenderedRow();
      previousRenderedCell.setFocusOutOnEditor();
      pRow.stopEditing();
      nRow.startRowEditing();
      nextRenderedCell.setFocusInOnEditor();
    }

    nextRenderedCell.focusCell();
  }; // called by the cell, when tab is pressed while editing.
  // @return: RenderedCell when navigation successful, otherwise null


  RowRenderer.prototype.findNextCellToFocusOn = function (gridCell, backwards, startEditing) {
    var nextCell = gridCell;

    while (true) {
      if (!backwards) {
        nextCell = this.getLastCellOfColSpan(nextCell);
      }

      nextCell = this.cellNavigationService.getNextTabbedCell(nextCell, backwards); // allow user to override what cell to go to next

      var userFunc = this.gridOptionsWrapper.getTabToNextCellFunc();

      if (_.exists(userFunc)) {
        var params = {
          backwards: backwards,
          editing: startEditing,
          previousCellPosition: gridCell,
          nextCellPosition: nextCell ? nextCell : null
        };
        var userCell = userFunc(params);

        if (_.exists(userCell)) {
          if (userCell.floating) {
            _.doOnce(function () {
              console.warn("ag-Grid: tabToNextCellFunc return type should have attributes: rowIndex, rowPinned, column. However you had 'floating', maybe you meant 'rowPinned'?");
            }, 'no floating in userCell');

            userCell.rowPinned = userCell.floating;
          }

          nextCell = {
            rowIndex: userCell.rowIndex,
            column: userCell.column,
            rowPinned: userCell.rowPinned
          };
        } else {
          nextCell = null;
        }
      } // if no 'next cell', means we have got to last cell of grid, so nothing to move to,
      // so bottom right cell going forwards, or top left going backwards


      if (!nextCell) {
        return null;
      } // if editing, but cell not editable, skip cell. we do this before we do all of
      // the 'ensure index visible' and 'flush all frames', otherwise if we are skipping
      // a bunch of cells (eg 10 rows) then all the work on ensuring cell visible is useless
      // (except for the last one) which causes grid to stall for a while.


      if (startEditing) {
        var rowNode = this.lookupRowNodeForCell(nextCell);
        var cellIsEditable = nextCell.column.isCellEditable(rowNode);

        if (!cellIsEditable) {
          continue;
        }
      } // this scrolls the row into view


      var cellIsNotFloating = _.missing(nextCell.rowPinned);

      if (cellIsNotFloating) {
        this.gridPanel.ensureIndexVisible(nextCell.rowIndex);
      } // pinned columns don't scroll, so no need to ensure index visible


      if (!nextCell.column.isPinned()) {
        this.gridPanel.ensureColumnVisible(nextCell.column);
      } // need to nudge the scrolls for the floating items. otherwise when we set focus on a non-visible
      // floating cell, the scrolls get out of sync


      this.gridPanel.horizontallyScrollHeaderCenterAndFloatingCenter(); // get the grid panel to flush all animation frames - otherwise the call below to get the cellComp
      // could fail, if we just scrolled the grid (to make a cell visible) and the rendering hasn't finished.

      this.animationFrameService.flushAllFrames(); // we have to call this after ensureColumnVisible - otherwise it could be a virtual column
      // or row that is not currently in view, hence the renderedCell would not exist

      var nextCellComp = this.getComponentForCell(nextCell); // if next cell is fullWidth row, then no rendered cell,
      // as fullWidth rows have no cells, so we skip it

      if (_.missing(nextCellComp)) {
        continue;
      }

      if (nextCellComp.isSuppressNavigable()) {
        continue;
      } // by default, when we click a cell, it gets selected into a range, so to keep keyboard navigation
      // consistent, we set into range here also.


      if (this.rangeController) {
        this.rangeController.setRangeToCell(nextCell);
      } // we successfully tabbed onto a grid cell, so return true


      return nextCellComp;
    }
  };

  RowRenderer.prototype.lookupRowNodeForCell = function (cell) {
    if (cell.rowPinned === Constants.PINNED_TOP) {
      return this.pinnedRowModel.getPinnedTopRow(cell.rowIndex);
    }

    if (cell.rowPinned === Constants.PINNED_BOTTOM) {
      return this.pinnedRowModel.getPinnedBottomRow(cell.rowIndex);
    }

    return this.paginationProxy.getRow(cell.rowIndex);
  };

  __decorate$A([Autowired("paginationProxy")], RowRenderer.prototype, "paginationProxy", void 0);

  __decorate$A([Autowired("columnController")], RowRenderer.prototype, "columnController", void 0);

  __decorate$A([Autowired("gridOptionsWrapper")], RowRenderer.prototype, "gridOptionsWrapper", void 0);

  __decorate$A([Autowired("$scope")], RowRenderer.prototype, "$scope", void 0);

  __decorate$A([Autowired("eventService")], RowRenderer.prototype, "eventService", void 0);

  __decorate$A([Autowired("pinnedRowModel")], RowRenderer.prototype, "pinnedRowModel", void 0);

  __decorate$A([Autowired("rowModel")], RowRenderer.prototype, "rowModel", void 0);

  __decorate$A([Autowired("loggerFactory")], RowRenderer.prototype, "loggerFactory", void 0);

  __decorate$A([Autowired("focusedCellController")], RowRenderer.prototype, "focusedCellController", void 0);

  __decorate$A([Autowired("cellNavigationService")], RowRenderer.prototype, "cellNavigationService", void 0);

  __decorate$A([Autowired("columnApi")], RowRenderer.prototype, "columnApi", void 0);

  __decorate$A([Autowired("gridApi")], RowRenderer.prototype, "gridApi", void 0);

  __decorate$A([Autowired("beans")], RowRenderer.prototype, "beans", void 0);

  __decorate$A([Autowired("maxDivHeightScaler")], RowRenderer.prototype, "maxDivHeightScaler", void 0);

  __decorate$A([Autowired("animationFrameService")], RowRenderer.prototype, "animationFrameService", void 0);

  __decorate$A([Autowired("rowPositionUtils")], RowRenderer.prototype, "rowPositionUtils", void 0);

  __decorate$A([Optional("rangeController")], RowRenderer.prototype, "rangeController", void 0);

  __decorate$A([__param$6(0, Qualifier("loggerFactory"))], RowRenderer.prototype, "agWire", null);

  RowRenderer = __decorate$A([Bean("rowRenderer")], RowRenderer);
  return RowRenderer;
}(BeanStub);
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var CssClassApplier =
/** @class */
function () {
  function CssClassApplier() {}

  CssClassApplier.addHeaderClassesFromColDef = function (abstractColDef, eHeaderCell, gridOptionsWrapper, column, columnGroup) {
    if (_.missing(abstractColDef)) {
      return;
    }

    this.addColumnClassesFromCollDef(abstractColDef.headerClass, abstractColDef, eHeaderCell, gridOptionsWrapper, column, columnGroup);
  };

  CssClassApplier.addToolPanelClassesFromColDef = function (abstractColDef, eHeaderCell, gridOptionsWrapper, column, columnGroup) {
    if (_.missing(abstractColDef)) {
      return;
    }

    this.addColumnClassesFromCollDef(abstractColDef.toolPanelClass, abstractColDef, eHeaderCell, gridOptionsWrapper, column, columnGroup);
  };

  CssClassApplier.addColumnClassesFromCollDef = function (classesOrFunc, abstractColDef, eHeaderCell, gridOptionsWrapper, column, columnGroup) {
    if (_.missing(classesOrFunc)) {
      return;
    }

    var classToUse;

    if (typeof classesOrFunc === 'function') {
      var params = {
        // bad naming, as colDef here can be a group or a column,
        // however most people won't appreciate the difference,
        // so keeping it as colDef to avoid confusion.
        colDef: abstractColDef,
        column: column,
        columnGroup: columnGroup,
        context: gridOptionsWrapper.getContext(),
        api: gridOptionsWrapper.getApi()
      };
      var headerClassFunc = classesOrFunc;
      classToUse = headerClassFunc(params);
    } else {
      classToUse = classesOrFunc;
    }

    if (typeof classToUse === 'string') {
      _.addCssClass(eHeaderCell, classToUse);
    } else if (Array.isArray(classToUse)) {
      classToUse.forEach(function (cssClassItem) {
        _.addCssClass(eHeaderCell, cssClassItem);
      });
    }
  };

  return CssClassApplier;
}();
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __extends$z = undefined && undefined.__extends || function () {
  var _extendStatics36 = function extendStatics(d, b) {
    _extendStatics36 = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics36(d, b);
  };

  return function (d, b) {
    _extendStatics36(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var SetLeftFeature =
/** @class */
function (_super) {
  __extends$z(SetLeftFeature, _super);

  function SetLeftFeature(columnOrGroup, eCell, beans, colsSpanning) {
    var _this = _super.call(this) || this;

    _this.columnOrGroup = columnOrGroup;
    _this.eCell = eCell;
    _this.ariaEl = _this.eCell.querySelector('[role=columnheader]') || _this.eCell;
    _this.colsSpanning = colsSpanning;
    _this.beans = beans;
    _this.printLayout = beans.gridOptionsWrapper.getDomLayout() === Constants.DOM_LAYOUT_PRINT;
    return _this;
  }

  SetLeftFeature.prototype.setColsSpanning = function (colsSpanning) {
    this.colsSpanning = colsSpanning;
    this.onLeftChanged();
  };

  SetLeftFeature.prototype.getColumnOrGroup = function () {
    if (this.beans.gridOptionsWrapper.isEnableRtl() && this.colsSpanning) {
      return _.last(this.colsSpanning);
    }

    return this.columnOrGroup;
  };

  SetLeftFeature.prototype.init = function () {
    this.addDestroyableEventListener(this.columnOrGroup, Column.EVENT_LEFT_CHANGED, this.onLeftChanged.bind(this));
    this.setLeftFirstTime();
  };

  SetLeftFeature.prototype.setLeftFirstTime = function () {
    var suppressMoveAnimation = this.beans.gridOptionsWrapper.isSuppressColumnMoveAnimation();

    var oldLeftExists = _.exists(this.columnOrGroup.getOldLeft());

    var animateColumnMove = this.beans.columnAnimationService.isActive() && oldLeftExists && !suppressMoveAnimation;

    if (animateColumnMove) {
      this.animateInLeft();
    } else {
      this.onLeftChanged();
    }
  };

  SetLeftFeature.prototype.animateInLeft = function () {
    var _this = this;

    var left = this.getColumnOrGroup().getLeft();
    var oldLeft = this.getColumnOrGroup().getOldLeft();
    this.setLeft(oldLeft); // we must keep track of the left we want to set to, as this would otherwise lead to a race
    // condition, if the user changed the left value many times in one VM turn, then we want to make
    // make sure the actualLeft we set in the timeout below (in the next VM turn) is the correct left
    // position. eg if user changes column position twice, then setLeft() below executes twice in next
    // VM turn, but only one (the correct one) should get applied.

    this.actualLeft = left;
    this.beans.columnAnimationService.executeNextVMTurn(function () {
      // test this left value is the latest one to be applied, and if not, do nothing
      if (_this.actualLeft === left) {
        _this.setLeft(left);
      }
    });
  };

  SetLeftFeature.prototype.onLeftChanged = function () {
    var colOrGroup = this.getColumnOrGroup();
    var left = colOrGroup.getLeft();
    this.actualLeft = this.modifyLeftForPrintLayout(colOrGroup, left);
    this.setLeft(this.actualLeft);
  };

  SetLeftFeature.prototype.modifyLeftForPrintLayout = function (colOrGroup, leftPosition) {
    if (!this.printLayout) {
      return leftPosition;
    }

    if (colOrGroup.getPinned() === Constants.PINNED_LEFT) {
      return leftPosition;
    }

    if (colOrGroup.getPinned() === Constants.PINNED_RIGHT) {
      var leftWidth_1 = this.beans.columnController.getPinnedLeftContainerWidth();
      var bodyWidth = this.beans.columnController.getBodyContainerWidth();
      return leftWidth_1 + bodyWidth + leftPosition;
    } // is in body


    var leftWidth = this.beans.columnController.getPinnedLeftContainerWidth();
    return leftWidth + leftPosition;
  };

  SetLeftFeature.prototype.setLeft = function (value) {
    // if the value is null, then that means the column is no longer
    // displayed. there is logic in the rendering to fade these columns
    // out, so we don't try and change their left positions.
    if (_.exists(value)) {
      this.eCell.style.left = value + "px";
    }

    if (this.columnOrGroup instanceof Column) {
      var colIndex = this.beans.columnController.getAllDisplayedColumns().indexOf(this.columnOrGroup);
      this.ariaEl.setAttribute('aria-colindex', (colIndex + 1).toString());
    } else {
      this.ariaEl.removeAttribute('aria-colindex');
    }
  };

  return SetLeftFeature;
}(BeanStub);
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __extends$A = undefined && undefined.__extends || function () {
  var _extendStatics37 = function extendStatics(d, b) {
    _extendStatics37 = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics37(d, b);
  };

  return function (d, b) {
    _extendStatics37(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var __decorate$B = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var AgAbstractLabel =
/** @class */
function (_super) {
  __extends$A(AgAbstractLabel, _super);

  function AgAbstractLabel() {
    var _this = _super !== null && _super.apply(this, arguments) || this;

    _this.labelSeparator = '';
    _this.labelAlignment = 'left';
    _this.config = {};
    _this.label = '';
    return _this;
  }

  AgAbstractLabel.prototype.postConstruct = function () {
    _.addCssClass(this.getGui(), 'ag-labeled');

    var _a = this.config,
        labelSeparator = _a.labelSeparator,
        label = _a.label,
        labelWidth = _a.labelWidth,
        labelAlignment = _a.labelAlignment;

    if (labelSeparator != null) {
      this.setLabelSeparator(labelSeparator);
    }

    if (label != null) {
      this.setLabel(label);
    }

    if (labelWidth != null) {
      this.setLabelWidth(labelWidth);
    }

    this.setLabelAlignment(labelAlignment || this.labelAlignment);
    this.refreshLabel();
  };

  AgAbstractLabel.prototype.refreshLabel = function () {
    this.eLabel.innerText = this.label + this.labelSeparator;

    _.addOrRemoveCssClass(this.eLabel, 'ag-hidden', this.label === '');
  };

  AgAbstractLabel.prototype.setLabelSeparator = function (labelSeparator) {
    if (this.labelSeparator === labelSeparator) {
      return this;
    }

    this.labelSeparator = labelSeparator;

    if (this.label != null) {
      this.refreshLabel();
    }

    return this;
  };

  AgAbstractLabel.prototype.setLabel = function (label) {
    if (this.label === label) {
      return this;
    }

    this.label = label;
    this.refreshLabel();
    return this;
  };

  AgAbstractLabel.prototype.setLabelAlignment = function (alignment) {
    var eGui = this.getGui();

    _.addOrRemoveCssClass(eGui, 'ag-label-align-left', alignment === 'left');

    _.addOrRemoveCssClass(eGui, 'ag-label-align-right', alignment === 'right');

    _.addOrRemoveCssClass(eGui, 'ag-label-align-top', alignment === 'top');

    return this;
  };

  AgAbstractLabel.prototype.setLabelWidth = function (width) {
    if (this.label == null) {
      return this;
    }

    _.setElementWidth(this.eLabel, width);

    return this;
  };

  __decorate$B([PostConstruct], AgAbstractLabel.prototype, "postConstruct", null);

  return AgAbstractLabel;
}(Component);
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __extends$B = undefined && undefined.__extends || function () {
  var _extendStatics38 = function extendStatics(d, b) {
    _extendStatics38 = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics38(d, b);
  };

  return function (d, b) {
    _extendStatics38(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var AgAbstractField =
/** @class */
function (_super) {
  __extends$B(AgAbstractField, _super);

  function AgAbstractField() {
    return _super !== null && _super.apply(this, arguments) || this;
  }

  AgAbstractField.prototype.onValueChange = function (callbackFn) {
    var _this = this;

    this.addDestroyableEventListener(this, AgAbstractField.EVENT_CHANGED, function () {
      callbackFn(_this.getValue());
    });
    return this;
  };

  AgAbstractField.prototype.getWidth = function () {
    return this.getGui().clientWidth;
  };

  AgAbstractField.prototype.setWidth = function (width) {
    _.setFixedWidth(this.getGui(), width);

    return this;
  };

  AgAbstractField.prototype.getValue = function () {
    return this.value;
  };

  AgAbstractField.prototype.setValue = function (value, silent) {
    if (this.value === value) {
      return this;
    }

    this.value = value;

    if (!silent) {
      this.dispatchEvent({
        type: AgAbstractField.EVENT_CHANGED
      });
    }

    return this;
  };

  AgAbstractField.EVENT_CHANGED = 'valueChange';
  return AgAbstractField;
}(AgAbstractLabel);
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __extends$C = undefined && undefined.__extends || function () {
  var _extendStatics39 = function extendStatics(d, b) {
    _extendStatics39 = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics39(d, b);
  };

  return function (d, b) {
    _extendStatics39(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var __decorate$C = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var AgAbstractInputField =
/** @class */
function (_super) {
  __extends$C(AgAbstractInputField, _super);

  function AgAbstractInputField() {
    var _this = _super !== null && _super.apply(this, arguments) || this;

    _this.config = {};
    _this.TEMPLATE = "<div class=\"ag-input-field\" role=\"presentation\">\n            <label ref=\"eLabel\"></label>\n            <div ref=\"eWrapper\" class=\"ag-wrapper ag-input-wrapper\" role=\"presentation\">\n                <%displayField% ref=\"eInput\"></%displayField%>\n            </div>\n        </div>";
    return _this;
  }

  AgAbstractInputField.prototype.postConstruct = function () {
    _super.prototype.postConstruct.call(this);

    this.setInputType();

    _.addCssClass(this.getGui(), this.className);

    var _a = this.config,
        width = _a.width,
        value = _a.value;

    if (width != null) {
      this.setWidth(width);
    }

    if (value != null) {
      this.setValue(value);
    }

    this.addInputListeners();
  };

  AgAbstractInputField.prototype.addInputListeners = function () {
    var _this = this;

    this.addDestroyableEventListener(this.eInput, 'input', function (e) {
      var value = e.target.value;

      _this.setValue(value);
    });
  };

  AgAbstractInputField.prototype.setInputType = function () {
    if (this.inputType) {
      this.eInput.setAttribute('type', this.inputType);
    }
  };

  AgAbstractInputField.prototype.getInputElement = function () {
    return this.eInput;
  };

  AgAbstractInputField.prototype.setInputWidth = function (width) {
    _.setElementWidth(this.eWrapper, width);

    return this;
  };

  AgAbstractInputField.prototype.setInputName = function (name) {
    this.getInputElement().setAttribute('name', name);
    return this;
  };

  __decorate$C([RefSelector('eLabel')], AgAbstractInputField.prototype, "eLabel", void 0);

  __decorate$C([RefSelector('eWrapper')], AgAbstractInputField.prototype, "eWrapper", void 0);

  __decorate$C([RefSelector('eInput')], AgAbstractInputField.prototype, "eInput", void 0);

  return AgAbstractInputField;
}(AgAbstractField);
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __extends$D = undefined && undefined.__extends || function () {
  var _extendStatics40 = function extendStatics(d, b) {
    _extendStatics40 = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics40(d, b);
  };

  return function (d, b) {
    _extendStatics40(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var __decorate$D = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var AgCheckbox =
/** @class */
function (_super) {
  __extends$D(AgCheckbox, _super);

  function AgCheckbox() {
    var _this = _super.call(this) || this;

    _this.className = 'ag-checkbox';
    _this.nativeInputClassName = 'ag-native-checkbox';
    _this.displayTag = 'input';
    _this.inputType = 'checkbox';
    _this.labelAlignment = 'right';
    _this.iconMap = {
      selected: 'checkboxChecked',
      unselected: 'checkboxUnchecked',
      indeterminate: 'checkboxIndeterminate'
    };
    _this.selected = false;
    _this.readOnly = false;
    _this.passive = false;

    _this.setTemplate(_this.TEMPLATE.replace(/%displayField%/g, _this.displayTag));

    return _this;
  }

  AgCheckbox.prototype.postConstruct = function () {
    _super.prototype.postConstruct.call(this);

    if (!this.gridOptionsWrapper.useNativeCheckboxes()) {
      _.addCssClass(this.eInput, 'ag-hidden');

      this.addIconsPlaceholder();
      this.updateIcons();
    } else {
      _.addCssClass(this.eInput, this.nativeInputClassName);
    }
  };

  AgCheckbox.prototype.addInputListeners = function () {
    var _this = this;

    if (this.gridOptionsWrapper.useNativeCheckboxes()) {
      this.addDestroyableEventListener(this.eInput, 'click', this.onCheckboxClick.bind(this));
    } else {
      this.addDestroyableEventListener(this.getGui(), 'click', function (e) {
        return _this.onClick(e);
      });
      this.addDestroyableEventListener(this.eInput, 'change', function (e) {
        return _this.setValue(e.target.checked, true);
      });
    }
  };

  AgCheckbox.prototype.getNextValue = function () {
    return this.selected === undefined ? true : !this.selected;
  };

  AgCheckbox.prototype.setPassive = function (passive) {
    this.passive = passive;
  };

  AgCheckbox.prototype.isReadOnly = function () {
    return this.readOnly;
  };

  AgCheckbox.prototype.setReadOnly = function (readOnly) {
    this.eInput.readOnly = readOnly;
    this.readOnly = readOnly;
    this.updateIcons();
  };

  AgCheckbox.prototype.toggle = function () {
    var nextValue = this.getNextValue();

    if (this.passive) {
      this.dispatchChange(nextValue);
    } else {
      this.setValue(nextValue);
    }
  };

  AgCheckbox.prototype.getValue = function () {
    return this.isSelected();
  };

  AgCheckbox.prototype.setValue = function (value, silent) {
    this.setSelected(value, silent);
    return this;
  };

  AgCheckbox.prototype.isSelected = function () {
    return this.selected;
  };

  AgCheckbox.prototype.setSelected = function (selected, silent) {
    if (this.selected === selected) {
      return;
    }

    this.selected = typeof selected === 'boolean' ? selected : undefined;
    this.eInput.checked = this.selected;
    this.eInput.indeterminate = this.selected === undefined;
    this.updateIcons();

    if (!silent) {
      this.dispatchChange(this.selected);
    }
  };

  AgCheckbox.prototype.getIconName = function () {
    var value = this.getValue();
    var prop = value === undefined ? 'indeterminate' : value ? 'selected' : 'unselected';
    var readOnlyStr = this.isReadOnly() ? 'ReadOnly' : '';
    return "" + this.iconMap[prop] + readOnlyStr;
  };

  AgCheckbox.prototype.updateIcons = function () {
    if (!this.gridOptionsWrapper.useNativeCheckboxes()) {
      _.clearElement(this.eIconEl);

      this.eIconEl.appendChild(_.createIconNoSpan(this.getIconName(), this.gridOptionsWrapper, null));
    }
  };

  AgCheckbox.prototype.dispatchChange = function (selected) {
    this.dispatchEvent({
      type: AgCheckbox.EVENT_CHANGED,
      selected: selected
    });
  };

  AgCheckbox.prototype.addIconsPlaceholder = function () {
    var iconDiv = document.createElement('div');
    this.eWrapper.appendChild(iconDiv);
    this.eIconEl = iconDiv;
  };

  AgCheckbox.prototype.onClick = function (event) {
    // if we don't set the path, then won't work in Edge, as once the <span> is removed from the dom,
    // it's not possible to calculate the path by following the parent's chain. in other browser (eg
    // chrome) there is event.path for this purpose, but missing in Edge.
    _.addAgGridEventPath(event);

    if (!this.readOnly) {
      this.toggle();
    }
  };

  AgCheckbox.prototype.onCheckboxClick = function (e) {
    this.selected = e.target.checked;
    this.dispatchChange(this.selected);
  };

  __decorate$D([Autowired('gridOptionsWrapper')], AgCheckbox.prototype, "gridOptionsWrapper", void 0);

  return AgCheckbox;
}(AgAbstractInputField);
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __extends$E = undefined && undefined.__extends || function () {
  var _extendStatics41 = function extendStatics(d, b) {
    _extendStatics41 = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics41(d, b);
  };

  return function (d, b) {
    _extendStatics41(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var __decorate$E = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var SelectAllFeature =
/** @class */
function (_super) {
  __extends$E(SelectAllFeature, _super);

  function SelectAllFeature(cbSelectAll, column) {
    var _this = _super.call(this) || this;

    _this.cbSelectAllVisible = false;
    _this.processingEventFromCheckbox = false;
    _this.cbSelectAll = cbSelectAll;
    _this.column = column;
    var colDef = column.getColDef();
    _this.filteredOnly = colDef ? !!colDef.headerCheckboxSelectionFilteredOnly : false;
    return _this;
  }

  SelectAllFeature.prototype.postConstruct = function () {
    this.showOrHideSelectAll();
    this.addDestroyableEventListener(this.eventService, Events.EVENT_DISPLAYED_COLUMNS_CHANGED, this.showOrHideSelectAll.bind(this));
    this.addDestroyableEventListener(this.eventService, Events.EVENT_SELECTION_CHANGED, this.onSelectionChanged.bind(this));
    this.addDestroyableEventListener(this.eventService, Events.EVENT_MODEL_UPDATED, this.onModelChanged.bind(this));
    this.addDestroyableEventListener(this.cbSelectAll, AgCheckbox.EVENT_CHANGED, this.onCbSelectAll.bind(this));
  };

  SelectAllFeature.prototype.showOrHideSelectAll = function () {
    this.cbSelectAllVisible = this.isCheckboxSelection();
    this.cbSelectAll.setDisplayed(this.cbSelectAllVisible);

    if (this.cbSelectAllVisible) {
      // in case user is trying this feature with the wrong model type
      this.checkRightRowModelType(); // make sure checkbox is showing the right state

      this.updateStateOfCheckbox();
    }
  };

  SelectAllFeature.prototype.onModelChanged = function () {
    if (!this.cbSelectAllVisible) {
      return;
    }

    this.updateStateOfCheckbox();
  };

  SelectAllFeature.prototype.onSelectionChanged = function () {
    if (!this.cbSelectAllVisible) {
      return;
    }

    this.updateStateOfCheckbox();
  };

  SelectAllFeature.prototype.getNextCheckboxState = function (selectionCount) {
    if (selectionCount.selected === 0 && selectionCount.notSelected === 0) {
      // if no rows, always have it unselected
      return false;
    } else if (selectionCount.selected > 0 && selectionCount.notSelected > 0) {
      // if mix of selected and unselected, this is the tri-state
      return null;
    } else if (selectionCount.selected > 0) {
      // only selected
      return true;
    } else {
      // nothing selected
      return false;
    }
  };

  SelectAllFeature.prototype.updateStateOfCheckbox = function () {
    if (this.processingEventFromCheckbox) {
      return;
    }

    this.processingEventFromCheckbox = true;
    var selectionCount = this.getSelectionCount();
    var allSelected = this.getNextCheckboxState(selectionCount);
    this.cbSelectAll.setValue(allSelected);
    this.processingEventFromCheckbox = false;
  };

  SelectAllFeature.prototype.getSelectionCount = function () {
    var _this = this;

    var selectedCount = 0;
    var notSelectedCount = 0;

    var callback = function callback(node) {
      if (_this.gridOptionsWrapper.isGroupSelectsChildren() && node.group) {
        return;
      }

      if (node.isSelected()) {
        selectedCount++;
      } else if (!node.selectable) ;else {
        notSelectedCount++;
      }
    };

    if (this.filteredOnly) {
      this.gridApi.forEachNodeAfterFilter(callback);
    } else {
      this.gridApi.forEachNode(callback);
    }

    return {
      notSelected: notSelectedCount,
      selected: selectedCount
    };
  };

  SelectAllFeature.prototype.checkRightRowModelType = function () {
    var rowModelType = this.rowModel.getType();
    var rowModelMatches = rowModelType === Constants.ROW_MODEL_TYPE_CLIENT_SIDE;

    if (!rowModelMatches) {
      console.warn("ag-Grid: selectAllCheckbox is only available if using normal row model, you are using " + rowModelType);
    }
  };

  SelectAllFeature.prototype.onCbSelectAll = function () {
    if (this.processingEventFromCheckbox) {
      return;
    }

    if (!this.cbSelectAllVisible) {
      return;
    }

    var value = this.cbSelectAll.getValue();

    if (value) {
      this.selectionController.selectAllRowNodes(this.filteredOnly);
    } else {
      this.selectionController.deselectAllRowNodes(this.filteredOnly);
    }
  };

  SelectAllFeature.prototype.isCheckboxSelection = function () {
    var result = this.column.getColDef().headerCheckboxSelection;

    if (typeof result === 'function') {
      var func = result;
      result = func({
        column: this.column,
        colDef: this.column.getColDef(),
        columnApi: this.columnApi,
        api: this.gridApi
      });
    }

    if (result) {
      if (this.gridOptionsWrapper.isRowModelServerSide()) {
        console.warn('headerCheckboxSelection is not supported for Server Side Row Model');
        return false;
      }

      if (this.gridOptionsWrapper.isRowModelInfinite()) {
        console.warn('headerCheckboxSelection is not supported for Infinite Row Model');
        return false;
      }

      if (this.gridOptionsWrapper.isRowModelViewport()) {
        console.warn('headerCheckboxSelection is not supported for Viewport Row Model');
        return false;
      } // otherwise the row model is compatible, so return true


      return true;
    } else {
      return false;
    }
  };

  __decorate$E([Autowired('gridApi')], SelectAllFeature.prototype, "gridApi", void 0);

  __decorate$E([Autowired('columnApi')], SelectAllFeature.prototype, "columnApi", void 0);

  __decorate$E([Autowired('eventService')], SelectAllFeature.prototype, "eventService", void 0);

  __decorate$E([Autowired('rowModel')], SelectAllFeature.prototype, "rowModel", void 0);

  __decorate$E([Autowired('selectionController')], SelectAllFeature.prototype, "selectionController", void 0);

  __decorate$E([Autowired('gridOptionsWrapper')], SelectAllFeature.prototype, "gridOptionsWrapper", void 0);

  __decorate$E([PostConstruct], SelectAllFeature.prototype, "postConstruct", null);

  return SelectAllFeature;
}(BeanStub);
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __extends$F = undefined && undefined.__extends || function () {
  var _extendStatics42 = function extendStatics(d, b) {
    _extendStatics42 = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics42(d, b);
  };

  return function (d, b) {
    _extendStatics42(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var __decorate$F = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var HoverFeature =
/** @class */
function (_super) {
  __extends$F(HoverFeature, _super);

  function HoverFeature(columns, element) {
    var _this = _super.call(this) || this;

    _this.columns = columns;
    _this.element = element;
    return _this;
  }

  HoverFeature.prototype.postConstruct = function () {
    this.addMouseHoverListeners();
  };

  HoverFeature.prototype.addMouseHoverListeners = function () {
    this.addDestroyableEventListener(this.element, 'mouseout', this.onMouseOut.bind(this));
    this.addDestroyableEventListener(this.element, 'mouseover', this.onMouseOver.bind(this));
  };

  HoverFeature.prototype.onMouseOut = function () {
    this.columnHoverService.clearMouseOver();
  };

  HoverFeature.prototype.onMouseOver = function () {
    this.columnHoverService.setMouseOver(this.columns);
  };

  __decorate$F([Autowired('columnHoverService')], HoverFeature.prototype, "columnHoverService", void 0);

  __decorate$F([PostConstruct], HoverFeature.prototype, "postConstruct", null);

  return HoverFeature;
}(BeanStub);
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __extends$G = undefined && undefined.__extends || function () {
  var _extendStatics43 = function extendStatics(d, b) {
    _extendStatics43 = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics43(d, b);
  };

  return function (d, b) {
    _extendStatics43(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var __decorate$G = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var HeaderWrapperComp =
/** @class */
function (_super) {
  __extends$G(HeaderWrapperComp, _super);

  function HeaderWrapperComp(column, dragSourceDropTarget, pinned) {
    var _this = _super.call(this, HeaderWrapperComp.TEMPLATE) || this;

    _this.column = column;
    _this.dragSourceDropTarget = dragSourceDropTarget;
    _this.pinned = pinned;
    return _this;
  }

  HeaderWrapperComp.prototype.getColumn = function () {
    return this.column;
  };

  HeaderWrapperComp.prototype.getComponentHolder = function () {
    return this.column.getColDef();
  };

  HeaderWrapperComp.prototype.init = function () {
    var colDef = this.getComponentHolder();
    var displayName = this.columnController.getDisplayNameForColumn(this.column, 'header', true);
    var enableSorting = colDef.sortable;
    var enableMenu = this.menuFactory.isMenuEnabled(this.column) && !colDef.suppressMenu;
    this.appendHeaderComp(displayName, enableSorting, enableMenu);
    this.setupWidth();
    this.setupMovingCss();
    this.setupTooltip();
    this.setupResize();
    this.setupMenuClass();
    this.setupSortableClass(enableSorting);
    this.addColumnHoverListener();
    this.addFeature(new HoverFeature([this.column], this.getGui()));
    this.addDestroyableEventListener(this.column, Column.EVENT_FILTER_ACTIVE_CHANGED, this.onFilterChanged.bind(this));
    this.onFilterChanged();
    this.addFeature(new SelectAllFeature(this.cbSelectAll, this.column));
    var setLeftFeature = new SetLeftFeature(this.column, this.getGui(), this.beans);
    setLeftFeature.init();
    this.addDestroyFunc(setLeftFeature.destroy.bind(setLeftFeature));
    this.addAttributes();
    CssClassApplier.addHeaderClassesFromColDef(colDef, this.getGui(), this.gridOptionsWrapper, this.column, null);
  };

  HeaderWrapperComp.prototype.addColumnHoverListener = function () {
    this.addDestroyableEventListener(this.eventService, Events.EVENT_COLUMN_HOVER_CHANGED, this.onColumnHover.bind(this));
    this.onColumnHover();
  };

  HeaderWrapperComp.prototype.onColumnHover = function () {
    var isHovered = this.columnHoverService.isHovered(this.column);

    _.addOrRemoveCssClass(this.getGui(), 'ag-column-hover', isHovered);
  };

  HeaderWrapperComp.prototype.setupSortableClass = function (enableSorting) {
    if (enableSorting) {
      var element = this.getGui();

      _.addCssClass(element, 'ag-header-cell-sortable');
    }
  };

  HeaderWrapperComp.prototype.onFilterChanged = function () {
    var filterPresent = this.column.isFilterActive();

    _.addOrRemoveCssClass(this.getGui(), 'ag-header-cell-filtered', filterPresent);
  };

  HeaderWrapperComp.prototype.appendHeaderComp = function (displayName, enableSorting, enableMenu) {
    var _this = this;

    var params = {
      column: this.column,
      displayName: displayName,
      enableSorting: enableSorting,
      enableMenu: enableMenu,
      showColumnMenu: function showColumnMenu(source) {
        _this.gridApi.showColumnMenuAfterButtonClick(_this.column, source);
      },
      progressSort: function progressSort(multiSort) {
        _this.sortController.progressSort(_this.column, !!multiSort, "uiColumnSorted");
      },
      setSort: function setSort(sort, multiSort) {
        _this.sortController.setSortForColumn(_this.column, sort, !!multiSort, "uiColumnSorted");
      },
      api: this.gridApi,
      columnApi: this.columnApi,
      context: this.gridOptionsWrapper.getContext()
    };
    var callback = this.afterHeaderCompCreated.bind(this, displayName);
    this.userComponentFactory.newHeaderComponent(params).then(callback);
  };

  HeaderWrapperComp.prototype.afterHeaderCompCreated = function (displayName, headerComp) {
    this.appendChild(headerComp);
    this.setupMove(headerComp.getGui(), displayName);
  };

  HeaderWrapperComp.prototype.onColumnMovingChanged = function () {
    // this function adds or removes the moving css, based on if the col is moving.
    // this is what makes the header go dark when it is been moved (gives impression to
    // user that the column was picked up).
    if (this.column.isMoving()) {
      _.addCssClass(this.getGui(), 'ag-header-cell-moving');
    } else {
      _.removeCssClass(this.getGui(), 'ag-header-cell-moving');
    }
  };

  HeaderWrapperComp.prototype.setupMove = function (eHeaderCellLabel, displayName) {
    var _this = this;

    var suppressMove = this.gridOptionsWrapper.isSuppressMovableColumns() || this.getComponentHolder().suppressMovable || this.column.getColDef().lockPosition;

    if (suppressMove) {
      return;
    }

    if (eHeaderCellLabel) {
      var dragSource_1 = {
        type: exports.DragSourceType.HeaderCell,
        eElement: eHeaderCellLabel,
        getDragItem: function getDragItem() {
          return _this.createDragItem();
        },
        dragItemName: displayName,
        dragSourceDropTarget: this.dragSourceDropTarget,
        onDragStarted: function onDragStarted() {
          return _this.column.setMoving(true, "uiColumnMoved");
        },
        onDragStopped: function onDragStopped() {
          return _this.column.setMoving(false, "uiColumnMoved");
        }
      };
      this.dragAndDropService.addDragSource(dragSource_1, true);
      this.addDestroyFunc(function () {
        return _this.dragAndDropService.removeDragSource(dragSource_1);
      });
    }
  };

  HeaderWrapperComp.prototype.createDragItem = function () {
    var visibleState = {};
    visibleState[this.column.getId()] = this.column.isVisible();
    return {
      columns: [this.column],
      visibleState: visibleState
    };
  };

  HeaderWrapperComp.prototype.setupResize = function () {
    var _this = this;

    var colDef = this.getComponentHolder(); // if no eResize in template, do nothing

    if (!this.eResize) {
      return;
    }

    if (!this.column.isResizable()) {
      _.removeFromParent(this.eResize);

      return;
    }

    var finishedWithResizeFunc = this.horizontalResizeService.addResizeBar({
      eResizeBar: this.eResize,
      onResizeStart: this.onResizeStart.bind(this),
      onResizing: this.onResizing.bind(this, false),
      onResizeEnd: this.onResizing.bind(this, true)
    });
    this.addDestroyFunc(finishedWithResizeFunc);
    var weWantAutoSize = !this.gridOptionsWrapper.isSuppressAutoSize() && !colDef.suppressAutoSize;
    var skipHeaderOnAutoSize = this.gridOptionsWrapper.isSkipHeaderOnAutoSize();

    if (weWantAutoSize) {
      this.addDestroyableEventListener(this.eResize, 'dblclick', function () {
        _this.columnController.autoSizeColumn(_this.column, skipHeaderOnAutoSize, "uiColumnResized");
      });
      var touchListener = new TouchListener(this.eResize);
      this.addDestroyableEventListener(touchListener, TouchListener.EVENT_DOUBLE_TAP, function () {
        _this.columnController.autoSizeColumn(_this.column, skipHeaderOnAutoSize, "uiColumnResized");
      });
      this.addDestroyFunc(touchListener.destroy.bind(touchListener));
    }
  };

  HeaderWrapperComp.prototype.onResizing = function (finished, resizeAmount) {
    var resizeAmountNormalised = this.normaliseResizeAmount(resizeAmount);
    var newWidth = this.resizeStartWidth + resizeAmountNormalised;
    this.columnController.setColumnWidth(this.column, newWidth, this.resizeWithShiftKey, finished, "uiColumnDragged");

    if (finished) {
      _.removeCssClass(this.getGui(), 'ag-column-resizing');
    }
  };

  HeaderWrapperComp.prototype.onResizeStart = function (shiftKey) {
    this.resizeStartWidth = this.column.getActualWidth();
    this.resizeWithShiftKey = shiftKey;

    _.addCssClass(this.getGui(), 'ag-column-resizing');
  };

  HeaderWrapperComp.prototype.getTooltipText = function () {
    var colDef = this.getComponentHolder();
    return colDef.headerTooltip;
  };

  HeaderWrapperComp.prototype.setupTooltip = function () {
    var tooltipText = this.getTooltipText(); // add tooltip if exists

    if (tooltipText == null) {
      return;
    }

    if (this.gridOptionsWrapper.isEnableBrowserTooltips()) {
      this.getGui().setAttribute('title', tooltipText);
    } else {
      this.beans.tooltipManager.registerTooltip(this);
    }
  };

  HeaderWrapperComp.prototype.setupMovingCss = function () {
    this.addDestroyableEventListener(this.column, Column.EVENT_MOVING_CHANGED, this.onColumnMovingChanged.bind(this));
    this.onColumnMovingChanged();
  };

  HeaderWrapperComp.prototype.addAttributes = function () {
    this.getGui().setAttribute("col-id", this.column.getColId());
  };

  HeaderWrapperComp.prototype.setupWidth = function () {
    this.addDestroyableEventListener(this.column, Column.EVENT_WIDTH_CHANGED, this.onColumnWidthChanged.bind(this));
    this.onColumnWidthChanged();
  };

  HeaderWrapperComp.prototype.setupMenuClass = function () {
    this.addDestroyableEventListener(this.column, Column.EVENT_MENU_VISIBLE_CHANGED, this.onMenuVisible.bind(this));
    this.onColumnWidthChanged();
  };

  HeaderWrapperComp.prototype.onMenuVisible = function () {
    this.addOrRemoveCssClass('ag-column-menu-visible', this.column.isMenuVisible());
  };

  HeaderWrapperComp.prototype.onColumnWidthChanged = function () {
    this.getGui().style.width = this.column.getActualWidth() + 'px';
  }; // optionally inverts the drag, depending on pinned and RTL
  // note - this method is duplicated in RenderedHeaderGroupCell - should refactor out?


  HeaderWrapperComp.prototype.normaliseResizeAmount = function (dragChange) {
    var result = dragChange;

    if (this.gridOptionsWrapper.isEnableRtl()) {
      // for RTL, dragging left makes the col bigger, except when pinning left
      if (this.pinned !== Constants.PINNED_LEFT) {
        result *= -1;
      }
    } else {
      // for LTR (ie normal), dragging left makes the col smaller, except when pinning right
      if (this.pinned === Constants.PINNED_RIGHT) {
        result *= -1;
      }
    }

    return result;
  };

  HeaderWrapperComp.TEMPLATE = '<div class="ag-header-cell" role="presentation" unselectable="on">' + '  <div ref="eResize" class="ag-header-cell-resize" role="presentation"></div>' + '  <ag-checkbox ref="cbSelectAll" class="ag-header-select-all" role="presentation"></ag-checkbox>' + // <inner component goes here>
  '</div>';

  __decorate$G([Autowired('gridOptionsWrapper')], HeaderWrapperComp.prototype, "gridOptionsWrapper", void 0);

  __decorate$G([Autowired('dragAndDropService')], HeaderWrapperComp.prototype, "dragAndDropService", void 0);

  __decorate$G([Autowired('columnController')], HeaderWrapperComp.prototype, "columnController", void 0);

  __decorate$G([Autowired('horizontalResizeService')], HeaderWrapperComp.prototype, "horizontalResizeService", void 0);

  __decorate$G([Autowired('menuFactory')], HeaderWrapperComp.prototype, "menuFactory", void 0);

  __decorate$G([Autowired('gridApi')], HeaderWrapperComp.prototype, "gridApi", void 0);

  __decorate$G([Autowired('columnApi')], HeaderWrapperComp.prototype, "columnApi", void 0);

  __decorate$G([Autowired('sortController')], HeaderWrapperComp.prototype, "sortController", void 0);

  __decorate$G([Autowired('eventService')], HeaderWrapperComp.prototype, "eventService", void 0);

  __decorate$G([Autowired('userComponentFactory')], HeaderWrapperComp.prototype, "userComponentFactory", void 0);

  __decorate$G([Autowired('columnHoverService')], HeaderWrapperComp.prototype, "columnHoverService", void 0);

  __decorate$G([Autowired('beans')], HeaderWrapperComp.prototype, "beans", void 0);

  __decorate$G([RefSelector('eResize')], HeaderWrapperComp.prototype, "eResize", void 0);

  __decorate$G([RefSelector('cbSelectAll')], HeaderWrapperComp.prototype, "cbSelectAll", void 0);

  __decorate$G([PostConstruct], HeaderWrapperComp.prototype, "init", null);

  return HeaderWrapperComp;
}(Component);
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __extends$H = undefined && undefined.__extends || function () {
  var _extendStatics44 = function extendStatics(d, b) {
    _extendStatics44 = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics44(d, b);
  };

  return function (d, b) {
    _extendStatics44(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var __decorate$H = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var HeaderGroupWrapperComp =
/** @class */
function (_super) {
  __extends$H(HeaderGroupWrapperComp, _super);

  function HeaderGroupWrapperComp(columnGroup, dragSourceDropTarget, pinned) {
    var _this = _super.call(this, HeaderGroupWrapperComp.TEMPLATE) || this; // the children can change, we keep destroy functions related to listening to the children here


    _this.childColumnsDestroyFuncs = [];
    _this.columnGroup = columnGroup;
    _this.dragSourceDropTarget = dragSourceDropTarget;
    _this.pinned = pinned;
    return _this;
  }

  HeaderGroupWrapperComp.prototype.postConstruct = function () {
    CssClassApplier.addHeaderClassesFromColDef(this.getComponentHolder(), this.getGui(), this.gridOptionsWrapper, null, this.columnGroup);
    var displayName = this.columnController.getDisplayNameForColumnGroup(this.columnGroup, 'header');
    this.appendHeaderGroupComp(displayName);
    this.setupResize();
    this.addClasses();
    this.setupWidth();
    this.addAttributes();
    this.setupMovingCss();
    this.setupTooltip();
    this.addFeature(new HoverFeature(this.columnGroup.getOriginalColumnGroup().getLeafColumns(), this.getGui()));
    var setLeftFeature = new SetLeftFeature(this.columnGroup, this.getGui(), this.beans);
    setLeftFeature.init();
    this.addDestroyFunc(setLeftFeature.destroy.bind(setLeftFeature));
  };

  HeaderGroupWrapperComp.prototype.setupMovingCss = function () {
    var _this = this;

    var originalColumnGroup = this.columnGroup.getOriginalColumnGroup();
    var leafColumns = originalColumnGroup.getLeafColumns();
    leafColumns.forEach(function (col) {
      _this.addDestroyableEventListener(col, Column.EVENT_MOVING_CHANGED, _this.onColumnMovingChanged.bind(_this));
    });
    this.onColumnMovingChanged();
  };

  HeaderGroupWrapperComp.prototype.getColumn = function () {
    return this.columnGroup;
  };

  HeaderGroupWrapperComp.prototype.getComponentHolder = function () {
    return this.columnGroup.getColGroupDef();
  };

  HeaderGroupWrapperComp.prototype.getTooltipText = function () {
    var colGroupDef = this.getComponentHolder();
    return colGroupDef && colGroupDef.headerTooltip;
  };

  HeaderGroupWrapperComp.prototype.setupTooltip = function () {
    var tooltipText = this.getTooltipText();

    if (tooltipText == null) {
      return;
    }

    if (this.gridOptionsWrapper.isEnableBrowserTooltips()) {
      this.getGui().setAttribute('title', tooltipText);
    } else {
      this.beans.tooltipManager.registerTooltip(this);
    }
  };

  HeaderGroupWrapperComp.prototype.onColumnMovingChanged = function () {
    // this function adds or removes the moving css, based on if the col is moving.
    // this is what makes the header go dark when it is been moved (gives impression to
    // user that the column was picked up).
    _.addOrRemoveCssClass(this.getGui(), 'ag-header-cell-moving', this.columnGroup.isMoving());
  };

  HeaderGroupWrapperComp.prototype.addAttributes = function () {
    this.getGui().setAttribute("col-id", this.columnGroup.getUniqueId());
  };

  HeaderGroupWrapperComp.prototype.appendHeaderGroupComp = function (displayName) {
    var _this = this;

    var params = {
      displayName: displayName,
      columnGroup: this.columnGroup,
      setExpanded: function setExpanded(expanded) {
        _this.columnController.setColumnGroupOpened(_this.columnGroup.getOriginalColumnGroup(), expanded, "gridInitializing");
      },
      api: this.gridApi,
      columnApi: this.columnApi,
      context: this.gridOptionsWrapper.getContext()
    };

    if (!displayName) {
      var columnGroup = this.columnGroup;
      var leafCols = columnGroup.getLeafColumns(); // find the top most column group that represents the same columns. so if we are dragging a group, we also
      // want to visually show the parent groups dragging for the same column set. for example imaging 5 levels
      // of grouping, with each group only containing the next group, and the last group containing three columns,
      // then when you move any group (even the lowest level group) you are in-fact moving all the groups, as all
      // the groups represent the same column set.

      while (columnGroup.getParent() && columnGroup.getParent().getLeafColumns().length === leafCols.length) {
        columnGroup = columnGroup.getParent();
      }

      var colGroupDef = columnGroup.getColGroupDef();

      if (colGroupDef) {
        displayName = colGroupDef.headerName;
      }

      if (!displayName) {
        displayName = leafCols ? this.columnController.getDisplayNameForColumn(leafCols[0], 'header', true) : '';
      }
    }

    var callback = this.afterHeaderCompCreated.bind(this, displayName);
    this.userComponentFactory.newHeaderGroupComponent(params).then(callback);
  };

  HeaderGroupWrapperComp.prototype.afterHeaderCompCreated = function (displayName, headerGroupComp) {
    this.appendChild(headerGroupComp);
    this.setupMove(headerGroupComp.getGui(), displayName);
  };

  HeaderGroupWrapperComp.prototype.addClasses = function () {
    // having different classes below allows the style to not have a bottom border
    // on the group header, if no group is specified
    // columnGroup.getColGroupDef
    var style = this.columnGroup.isPadding() ? 'no' : 'with';
    this.addCssClass("ag-header-group-cell-" + style + "-group");
  };

  HeaderGroupWrapperComp.prototype.setupMove = function (eHeaderGroup, displayName) {
    var _this = this;

    if (!eHeaderGroup) {
      return;
    }

    if (this.isSuppressMoving()) {
      return;
    }

    var allLeafColumns = this.columnGroup.getOriginalColumnGroup().getLeafColumns();
    var dragSource = {
      type: exports.DragSourceType.HeaderCell,
      eElement: eHeaderGroup,
      dragItemName: displayName,
      // we add in the original group leaf columns, so we move both visible and non-visible items
      getDragItem: this.getDragItemForGroup.bind(this),
      dragSourceDropTarget: this.dragSourceDropTarget,
      onDragStarted: function onDragStarted() {
        return allLeafColumns.forEach(function (col) {
          return col.setMoving(true, "uiColumnDragged");
        });
      },
      onDragStopped: function onDragStopped() {
        return allLeafColumns.forEach(function (col) {
          return col.setMoving(false, "uiColumnDragged");
        });
      }
    };
    this.dragAndDropService.addDragSource(dragSource, true);
    this.addDestroyFunc(function () {
      return _this.dragAndDropService.removeDragSource(dragSource);
    });
  }; // when moving the columns, we want to move all the columns (contained within the DragItem) in this group in one go,
  // and in the order they are currently in the screen.


  HeaderGroupWrapperComp.prototype.getDragItemForGroup = function () {
    var allColumnsOriginalOrder = this.columnGroup.getOriginalColumnGroup().getLeafColumns(); // capture visible state, used when re-entering grid to dictate which columns should be visible

    var visibleState = {};
    allColumnsOriginalOrder.forEach(function (column) {
      return visibleState[column.getId()] = column.isVisible();
    });
    var allColumnsCurrentOrder = [];
    this.columnController.getAllDisplayedColumns().forEach(function (column) {
      if (allColumnsOriginalOrder.indexOf(column) >= 0) {
        allColumnsCurrentOrder.push(column);

        _.removeFromArray(allColumnsOriginalOrder, column);
      }
    }); // we are left with non-visible columns, stick these in at the end

    allColumnsOriginalOrder.forEach(function (column) {
      return allColumnsCurrentOrder.push(column);
    }); // create and return dragItem

    return {
      columns: allColumnsCurrentOrder,
      visibleState: visibleState
    };
  };

  HeaderGroupWrapperComp.prototype.isSuppressMoving = function () {
    // if any child is fixed, then don't allow moving
    var childSuppressesMoving = false;
    this.columnGroup.getLeafColumns().forEach(function (column) {
      if (column.getColDef().suppressMovable || column.getColDef().lockPosition) {
        childSuppressesMoving = true;
      }
    });
    var result = childSuppressesMoving || this.gridOptionsWrapper.isSuppressMovableColumns();
    return result;
  };

  HeaderGroupWrapperComp.prototype.setupWidth = function () {
    // we need to listen to changes in child columns, as they impact our width
    this.addListenersToChildrenColumns(); // the children belonging to this group can change, so we need to add and remove listeners as they change

    this.addDestroyableEventListener(this.columnGroup, ColumnGroup.EVENT_DISPLAYED_CHILDREN_CHANGED, this.onDisplayedChildrenChanged.bind(this));
    this.onWidthChanged(); // the child listeners are not tied to this components life-cycle, as children can get added and removed
    // to the group - hence they are on a different life-cycle. so we must make sure the existing children
    // listeners are removed when we finally get destroyed

    this.addDestroyFunc(this.destroyListenersOnChildrenColumns.bind(this));
  };

  HeaderGroupWrapperComp.prototype.onDisplayedChildrenChanged = function () {
    this.addListenersToChildrenColumns();
    this.onWidthChanged();
  };

  HeaderGroupWrapperComp.prototype.addListenersToChildrenColumns = function () {
    var _this = this; // first destroy any old listeners


    this.destroyListenersOnChildrenColumns(); // now add new listeners to the new set of children

    var widthChangedListener = this.onWidthChanged.bind(this);
    this.columnGroup.getLeafColumns().forEach(function (column) {
      column.addEventListener(Column.EVENT_WIDTH_CHANGED, widthChangedListener);
      column.addEventListener(Column.EVENT_VISIBLE_CHANGED, widthChangedListener);

      _this.childColumnsDestroyFuncs.push(function () {
        column.removeEventListener(Column.EVENT_WIDTH_CHANGED, widthChangedListener);
        column.removeEventListener(Column.EVENT_VISIBLE_CHANGED, widthChangedListener);
      });
    });
  };

  HeaderGroupWrapperComp.prototype.destroyListenersOnChildrenColumns = function () {
    this.childColumnsDestroyFuncs.forEach(function (func) {
      return func();
    });
    this.childColumnsDestroyFuncs = [];
  };

  HeaderGroupWrapperComp.prototype.onWidthChanged = function () {
    this.getGui().style.width = this.columnGroup.getActualWidth() + 'px';
  };

  HeaderGroupWrapperComp.prototype.setupResize = function () {
    var _this = this;

    this.eHeaderCellResize = this.getRefElement('agResize');

    if (!this.columnGroup.isResizable()) {
      _.removeFromParent(this.eHeaderCellResize);

      return;
    }

    var finishedWithResizeFunc = this.horizontalResizeService.addResizeBar({
      eResizeBar: this.eHeaderCellResize,
      onResizeStart: this.onResizeStart.bind(this),
      onResizing: this.onResizing.bind(this, false),
      onResizeEnd: this.onResizing.bind(this, true)
    });
    this.addDestroyFunc(finishedWithResizeFunc);

    if (!this.gridOptionsWrapper.isSuppressAutoSize()) {
      var skipHeaderOnAutoSize_1 = this.gridOptionsWrapper.isSkipHeaderOnAutoSize();
      this.eHeaderCellResize.addEventListener('dblclick', function (event) {
        // get list of all the column keys we are responsible for
        var keys = [];

        _this.columnGroup.getDisplayedLeafColumns().forEach(function (column) {
          // not all cols in the group may be participating with auto-resize
          if (!column.getColDef().suppressAutoSize) {
            keys.push(column.getColId());
          }
        });

        if (keys.length > 0) {
          _this.columnController.autoSizeColumns(keys, skipHeaderOnAutoSize_1, "uiColumnResized");
        }
      });
    }
  };

  HeaderGroupWrapperComp.prototype.onResizeStart = function (shiftKey) {
    var _this = this;

    var leafCols = this.columnGroup.getDisplayedLeafColumns();
    this.resizeCols = leafCols.filter(function (col) {
      return col.isResizable();
    });
    this.resizeStartWidth = 0;
    this.resizeCols.forEach(function (col) {
      return _this.resizeStartWidth += col.getActualWidth();
    });
    this.resizeRatios = [];
    this.resizeCols.forEach(function (col) {
      return _this.resizeRatios.push(col.getActualWidth() / _this.resizeStartWidth);
    });
    var takeFromGroup = null;

    if (shiftKey) {
      takeFromGroup = this.columnController.getDisplayedGroupAfter(this.columnGroup);
    }

    if (takeFromGroup) {
      var takeFromLeafCols = takeFromGroup.getDisplayedLeafColumns();
      this.resizeTakeFromCols = takeFromLeafCols.filter(function (col) {
        return col.isResizable();
      });
      this.resizeTakeFromStartWidth = 0;
      this.resizeTakeFromCols.forEach(function (col) {
        return _this.resizeTakeFromStartWidth += col.getActualWidth();
      });
      this.resizeTakeFromRatios = [];
      this.resizeTakeFromCols.forEach(function (col) {
        return _this.resizeTakeFromRatios.push(col.getActualWidth() / _this.resizeTakeFromStartWidth);
      });
    } else {
      this.resizeTakeFromCols = null;
      this.resizeTakeFromStartWidth = null;
      this.resizeTakeFromRatios = null;
    }

    _.addCssClass(this.getGui(), 'ag-column-resizing');
  };

  HeaderGroupWrapperComp.prototype.onResizing = function (finished, resizeAmount) {
    var resizeSets = [];
    var resizeAmountNormalised = this.normaliseDragChange(resizeAmount);
    resizeSets.push({
      columns: this.resizeCols,
      ratios: this.resizeRatios,
      width: this.resizeStartWidth + resizeAmountNormalised
    });

    if (this.resizeTakeFromCols) {
      resizeSets.push({
        columns: this.resizeTakeFromCols,
        ratios: this.resizeTakeFromRatios,
        width: this.resizeTakeFromStartWidth - resizeAmountNormalised
      });
    }

    this.columnController.resizeColumnSets(resizeSets, finished, 'uiColumnDragged');

    if (finished) {
      _.removeCssClass(this.getGui(), 'ag-column-resizing');
    }
  }; // optionally inverts the drag, depending on pinned and RTL
  // note - this method is duplicated in RenderedHeaderCell - should refactor out?


  HeaderGroupWrapperComp.prototype.normaliseDragChange = function (dragChange) {
    var result = dragChange;

    if (this.gridOptionsWrapper.isEnableRtl()) {
      // for RTL, dragging left makes the col bigger, except when pinning left
      if (this.pinned !== Constants.PINNED_LEFT) {
        result *= -1;
      }
    } else if (this.pinned === Constants.PINNED_RIGHT) {
      // for LTR (ie normal), dragging left makes the col smaller, except when pinning right
      result *= -1;
    }

    return result;
  };

  HeaderGroupWrapperComp.TEMPLATE = '<div class="ag-header-group-cell" role="presentation">' + '  <div ref="agResize" class="ag-header-cell-resize" role="presentation"></div>' + '</div>';

  __decorate$H([Autowired('gridOptionsWrapper')], HeaderGroupWrapperComp.prototype, "gridOptionsWrapper", void 0);

  __decorate$H([Autowired('columnController')], HeaderGroupWrapperComp.prototype, "columnController", void 0);

  __decorate$H([Autowired('horizontalResizeService')], HeaderGroupWrapperComp.prototype, "horizontalResizeService", void 0);

  __decorate$H([Autowired('dragAndDropService')], HeaderGroupWrapperComp.prototype, "dragAndDropService", void 0);

  __decorate$H([Autowired('userComponentFactory')], HeaderGroupWrapperComp.prototype, "userComponentFactory", void 0);

  __decorate$H([Autowired('gridApi')], HeaderGroupWrapperComp.prototype, "gridApi", void 0);

  __decorate$H([Autowired('columnApi')], HeaderGroupWrapperComp.prototype, "columnApi", void 0);

  __decorate$H([Autowired('beans')], HeaderGroupWrapperComp.prototype, "beans", void 0);

  __decorate$H([PostConstruct], HeaderGroupWrapperComp.prototype, "postConstruct", null);

  return HeaderGroupWrapperComp;
}(Component);
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __extends$I = undefined && undefined.__extends || function () {
  var _extendStatics45 = function extendStatics(d, b) {
    _extendStatics45 = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics45(d, b);
  };

  return function (d, b) {
    _extendStatics45(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var __decorate$I = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
}; // optional floating filter for user provided filters - instead of providing a floating filter,
// they can provide a getModelAsString() method on the filter instead. this class just displays
// the string returned from getModelAsString()


var ReadOnlyFloatingFilter =
/** @class */
function (_super) {
  __extends$I(ReadOnlyFloatingFilter, _super);

  function ReadOnlyFloatingFilter() {
    return _super.call(this, "<div class=\"ag-input-wrapper\" role=\"presentation\"><input ref=\"eFloatingFilterText\" class=\"ag-floating-filter-input\"></div>") || this;
  }

  ReadOnlyFloatingFilter.prototype.init = function (params) {
    this.params = params;
    this.eFloatingFilterText.disabled = true;
  };

  ReadOnlyFloatingFilter.prototype.onParentModelChanged = function (parentModel) {
    var _this = this;

    if (!parentModel) {
      this.eFloatingFilterText.value = '';
      return;
    }

    this.params.parentFilterInstance(function (filterInstance) {
      // getModelAsString should be present, as we check this
      // in floatingFilterWrapper
      if (filterInstance.getModelAsString) {
        var modelAsString = filterInstance.getModelAsString(parentModel);
        _this.eFloatingFilterText.value = modelAsString;
      }
    });
  };

  __decorate$I([RefSelector('eFloatingFilterText')], ReadOnlyFloatingFilter.prototype, "eFloatingFilterText", void 0);

  return ReadOnlyFloatingFilter;
}(Component);
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __extends$J = undefined && undefined.__extends || function () {
  var _extendStatics46 = function extendStatics(d, b) {
    _extendStatics46 = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics46(d, b);
  };

  return function (d, b) {
    _extendStatics46(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var __decorate$J = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var FloatingFilterWrapper =
/** @class */
function (_super) {
  __extends$J(FloatingFilterWrapper, _super);

  function FloatingFilterWrapper(column) {
    var _this = _super.call(this, FloatingFilterWrapper.TEMPLATE) || this;

    _this.column = column;
    return _this;
  }

  FloatingFilterWrapper.prototype.postConstruct = function () {
    this.setupFloatingFilter();
    this.setupWidth();
    this.setupLeftPositioning();
    this.setupColumnHover();
    this.addFeature(new HoverFeature([this.column], this.getGui()));
    this.addDestroyableEventListener(this.eButtonShowMainFilter, 'click', this.showParentFilter.bind(this));
  };

  FloatingFilterWrapper.prototype.setupFloatingFilter = function () {
    var _this = this;

    var colDef = this.column.getColDef();

    if (colDef.filter) {
      this.floatingFilterCompPromise = this.getFloatingFilterInstance();

      if (this.floatingFilterCompPromise) {
        this.floatingFilterCompPromise.then(function (compInstance) {
          if (compInstance) {
            _this.setupWithFloatingFilter(compInstance);

            _this.setupSyncWithFilter();
          } else {
            _this.setupEmpty();
          }
        });
      } else {
        this.setupEmpty();
      }
    } else {
      this.setupEmpty();
    }
  };

  FloatingFilterWrapper.prototype.setupLeftPositioning = function () {
    var setLeftFeature = new SetLeftFeature(this.column, this.getGui(), this.beans);
    setLeftFeature.init();
    this.addDestroyFunc(setLeftFeature.destroy.bind(setLeftFeature));
  };

  FloatingFilterWrapper.prototype.setupSyncWithFilter = function () {
    var _this = this;

    var syncWithFilter = function syncWithFilter(filterChangedEvent) {
      var filterComponentPromise = _this.filterManager.getFilterComponent(_this.column, 'NO_UI');

      var parentModel = filterComponentPromise.resolveNow(null, function (filter) {
        return filter.getModel();
      });

      _this.onParentModelChanged(parentModel, filterChangedEvent);
    };

    this.addDestroyableEventListener(this.column, Column.EVENT_FILTER_CHANGED, syncWithFilter);

    if (this.filterManager.isFilterActive(this.column)) {
      syncWithFilter(null);
    }
  }; // linked to event listener in template


  FloatingFilterWrapper.prototype.showParentFilter = function () {
    this.menuFactory.showMenuAfterButtonClick(this.column, this.eButtonShowMainFilter, 'filterMenuTab', ['filterMenuTab']);
  };

  FloatingFilterWrapper.prototype.setupColumnHover = function () {
    this.addDestroyableEventListener(this.eventService, Events.EVENT_COLUMN_HOVER_CHANGED, this.onColumnHover.bind(this));
    this.onColumnHover();
  };

  FloatingFilterWrapper.prototype.onColumnHover = function () {
    var isHovered = this.columnHoverService.isHovered(this.column);

    _.addOrRemoveCssClass(this.getGui(), 'ag-column-hover', isHovered);
  };

  FloatingFilterWrapper.prototype.setupWidth = function () {
    this.addDestroyableEventListener(this.column, Column.EVENT_WIDTH_CHANGED, this.onColumnWidthChanged.bind(this));
    this.onColumnWidthChanged();
  };

  FloatingFilterWrapper.prototype.onColumnWidthChanged = function () {
    this.getGui().style.width = this.column.getActualWidth() + 'px';
  };

  FloatingFilterWrapper.prototype.setupWithFloatingFilter = function (floatingFilterComp) {
    var disposeFunc = function disposeFunc() {
      if (floatingFilterComp.destroy) {
        floatingFilterComp.destroy();
      }
    };

    if (!this.isAlive()) {
      disposeFunc();
      return;
    }

    this.addDestroyFunc(disposeFunc);
    var floatingFilterCompUi = floatingFilterComp.getGui();

    _.addOrRemoveCssClass(this.eFloatingFilterBody, 'ag-floating-filter-body', !this.suppressFilterButton);

    _.addOrRemoveCssClass(this.eFloatingFilterBody, 'ag-floating-filter-full-body', this.suppressFilterButton);

    _.setDisplayed(this.eButtonWrapper, !this.suppressFilterButton);

    var eIcon = _.createIconNoSpan('filter', this.gridOptionsWrapper, this.column);

    this.eButtonShowMainFilter.appendChild(eIcon);
    this.eFloatingFilterBody.appendChild(floatingFilterCompUi);

    if (floatingFilterComp.afterGuiAttached) {
      floatingFilterComp.afterGuiAttached();
    }
  };

  FloatingFilterWrapper.prototype.parentFilterInstance = function (callback) {
    var promise = this.filterManager.getFilterComponent(this.column, 'NO_UI');
    promise.then(callback);
  };

  FloatingFilterWrapper.prototype.getFloatingFilterInstance = function () {
    var colDef = this.column.getColDef();
    var defaultFloatingFilterType;

    if (typeof colDef.filter === 'string') {
      // will be undefined if not in the map
      defaultFloatingFilterType = FloatingFilterWrapper.filterToFloatingFilterNames[colDef.filter];
    } else if (colDef.filter === true) {
      var setFilterModuleLoaded = ModuleRegistry.isRegistered(exports.ModuleNames.SetFilterModule);
      defaultFloatingFilterType = setFilterModuleLoaded ? 'agSetColumnFloatingFilter' : 'agTextColumnFloatingFilter';
    }

    var filterParams = this.filterManager.createFilterParams(this.column, this.column.getColDef());
    var finalFilterParams = this.userComponentFactory.createFinalParams(colDef, 'filter', filterParams);
    var params = {
      api: this.gridApi,
      column: this.column,
      filterParams: finalFilterParams,
      currentParentModel: this.currentParentModel.bind(this),
      parentFilterInstance: this.parentFilterInstance.bind(this),
      onFloatingFilterChanged: this.onFloatingFilterChanged.bind(this),
      suppressFilterButton: false // This one might be overridden from the colDef

    }; // this is unusual - we need a params value OUTSIDE the component the params are for.
    // the params are for the floating filter component, but this property is actually for the wrapper.

    this.suppressFilterButton = colDef.floatingFilterComponentParams ? !!colDef.floatingFilterComponentParams.suppressFilterButton : false;
    var promise = this.userComponentFactory.newFloatingFilterComponent(colDef, params, defaultFloatingFilterType);

    if (!promise) {
      var filterComponent = this.getFilterComponentPrototype(colDef);
      var getModelAsStringExists = filterComponent && filterComponent.prototype && filterComponent.prototype.getModelAsString;

      if (getModelAsStringExists) {
        var compInstance = this.userComponentFactory.createUserComponentFromConcreteClass(ReadOnlyFloatingFilter, params);
        promise = Promise.resolve(compInstance);
      }
    }

    return promise;
  };

  FloatingFilterWrapper.prototype.createDynamicParams = function () {
    return {
      column: this.column,
      colDef: this.column.getColDef(),
      api: this.gridApi,
      columnApi: this.columnApi
    };
  };

  FloatingFilterWrapper.prototype.getFilterComponentPrototype = function (colDef) {
    var resolvedComponent = this.userComponentFactory.lookupComponentClassDef(colDef, "filter", this.createDynamicParams());
    return resolvedComponent ? resolvedComponent.component : null;
  };

  FloatingFilterWrapper.prototype.setupEmpty = function () {
    _.setDisplayed(this.eButtonWrapper, false);
  };

  FloatingFilterWrapper.prototype.currentParentModel = function () {
    var filterPromise = this.filterManager.getFilterComponent(this.column, 'NO_UI');
    return filterPromise.resolveNow(null, function (filter) {
      return filter.getModel();
    });
  };

  FloatingFilterWrapper.prototype.onParentModelChanged = function (model, filterChangedEvent) {
    if (!this.floatingFilterCompPromise) {
      return;
    }

    this.floatingFilterCompPromise.then(function (floatingFilterComp) {
      floatingFilterComp.onParentModelChanged(model, filterChangedEvent);
    });
  };

  FloatingFilterWrapper.prototype.onFloatingFilterChanged = function () {
    console.warn('ag-Grid: since version 21.x, how floating filters are implemented has changed. ' + 'Instead of calling params.onFloatingFilterChanged(), get a reference to the main filter via ' + 'params.parentFilterInstance() and then set a value on the parent filter directly.');
  };

  FloatingFilterWrapper.filterToFloatingFilterNames = {
    set: 'agSetColumnFloatingFilter',
    agSetColumnFilter: 'agSetColumnFloatingFilter',
    number: 'agNumberColumnFloatingFilter',
    agNumberColumnFilter: 'agNumberColumnFloatingFilter',
    date: 'agDateColumnFloatingFilter',
    agDateColumnFilter: 'agDateColumnFloatingFilter',
    text: 'agTextColumnFloatingFilter',
    agTextColumnFilter: 'agTextColumnFloatingFilter'
  };
  FloatingFilterWrapper.TEMPLATE = "<div class=\"ag-header-cell\" role=\"presentation\">\n            <div ref=\"eFloatingFilterBody\" role=\"columnheader\"></div>\n            <div class=\"ag-floating-filter-button\" ref=\"eButtonWrapper\" role=\"presentation\">\n                <button type=\"button\" ref=\"eButtonShowMainFilter\"></button>\n            </div>\n        </div>";

  __decorate$J([Autowired('columnHoverService')], FloatingFilterWrapper.prototype, "columnHoverService", void 0);

  __decorate$J([Autowired('eventService')], FloatingFilterWrapper.prototype, "eventService", void 0);

  __decorate$J([Autowired('beans')], FloatingFilterWrapper.prototype, "beans", void 0);

  __decorate$J([Autowired('gridOptionsWrapper')], FloatingFilterWrapper.prototype, "gridOptionsWrapper", void 0);

  __decorate$J([Autowired("userComponentFactory")], FloatingFilterWrapper.prototype, "userComponentFactory", void 0);

  __decorate$J([Autowired("gridApi")], FloatingFilterWrapper.prototype, "gridApi", void 0);

  __decorate$J([Autowired("columnApi")], FloatingFilterWrapper.prototype, "columnApi", void 0);

  __decorate$J([Autowired("filterManager")], FloatingFilterWrapper.prototype, "filterManager", void 0);

  __decorate$J([Autowired('menuFactory')], FloatingFilterWrapper.prototype, "menuFactory", void 0);

  __decorate$J([RefSelector('eFloatingFilterBody')], FloatingFilterWrapper.prototype, "eFloatingFilterBody", void 0);

  __decorate$J([RefSelector('eButtonWrapper')], FloatingFilterWrapper.prototype, "eButtonWrapper", void 0);

  __decorate$J([RefSelector('eButtonShowMainFilter')], FloatingFilterWrapper.prototype, "eButtonShowMainFilter", void 0);

  __decorate$J([PostConstruct], FloatingFilterWrapper.prototype, "postConstruct", null);

  return FloatingFilterWrapper;
}(Component);
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __extends$K = undefined && undefined.__extends || function () {
  var _extendStatics47 = function extendStatics(d, b) {
    _extendStatics47 = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics47(d, b);
  };

  return function (d, b) {
    _extendStatics47(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var __decorate$K = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var HeaderRowType;

(function (HeaderRowType) {
  HeaderRowType[HeaderRowType["COLUMN_GROUP"] = 0] = "COLUMN_GROUP";
  HeaderRowType[HeaderRowType["COLUMN"] = 1] = "COLUMN";
  HeaderRowType[HeaderRowType["FLOATING_FILTER"] = 2] = "FLOATING_FILTER";
})(HeaderRowType || (HeaderRowType = {}));

var HeaderRowComp =
/** @class */
function (_super) {
  __extends$K(HeaderRowComp, _super);

  function HeaderRowComp(dept, type, pinned, dropTarget) {
    var _this = _super.call(this, "<div class=\"ag-header-row\" role=\"row\"/>") || this;

    _this.headerComps = {};
    _this.dept = dept;
    _this.type = type;
    _this.pinned = pinned;
    _this.dropTarget = dropTarget;
    return _this;
  }

  HeaderRowComp.prototype.forEachHeaderElement = function (callback) {
    var _this = this;

    Object.keys(this.headerComps).forEach(function (key) {
      callback(_this.headerComps[key]);
    });
  };

  HeaderRowComp.prototype.destroy = function () {
    var idsOfAllChildren = Object.keys(this.headerComps);
    this.removeAndDestroyChildComponents(idsOfAllChildren);

    _super.prototype.destroy.call(this);
  };

  HeaderRowComp.prototype.removeAndDestroyChildComponents = function (idsToDestroy) {
    var _this = this;

    idsToDestroy.forEach(function (id) {
      var childHeaderComp = _this.headerComps[id];

      _this.getGui().removeChild(childHeaderComp.getGui());

      childHeaderComp.destroy();
      delete _this.headerComps[id];
    });
  };

  HeaderRowComp.prototype.onRowHeightChanged = function () {
    var headerRowCount = this.columnController.getHeaderRowCount();
    var sizes = [];
    var numberOfFloating = 0;
    var groupHeight;
    var headerHeight;

    if (!this.columnController.isPivotMode()) {
      if (this.gridOptionsWrapper.isFloatingFilter()) {
        headerRowCount++;
      }

      numberOfFloating = this.gridOptionsWrapper.isFloatingFilter() ? 1 : 0;
      groupHeight = this.gridOptionsWrapper.getGroupHeaderHeight();
      headerHeight = this.gridOptionsWrapper.getHeaderHeight();
    } else {
      numberOfFloating = 0;
      groupHeight = this.gridOptionsWrapper.getPivotGroupHeaderHeight();
      headerHeight = this.gridOptionsWrapper.getPivotHeaderHeight();
    }

    var numberOfNonGroups = 1 + numberOfFloating;
    var numberOfGroups = headerRowCount - numberOfNonGroups;

    for (var i = 0; i < numberOfGroups; i++) {
      sizes.push(groupHeight);
    }

    sizes.push(headerHeight);

    for (var i = 0; i < numberOfFloating; i++) {
      sizes.push(this.gridOptionsWrapper.getFloatingFiltersHeight());
    }

    var rowHeight = 0;

    for (var i = 0; i < this.dept; i++) {
      rowHeight += sizes[i];
    }

    this.getGui().style.top = rowHeight + 'px';
    this.getGui().style.height = sizes[this.dept] + 'px';
  }; //noinspection JSUnusedLocalSymbols


  HeaderRowComp.prototype.init = function () {
    this.onRowHeightChanged();
    this.onVirtualColumnsChanged();
    this.setWidth();
    this.addDestroyableEventListener(this.gridOptionsWrapper, GridOptionsWrapper.PROP_HEADER_HEIGHT, this.onRowHeightChanged.bind(this));
    this.addDestroyableEventListener(this.gridOptionsWrapper, GridOptionsWrapper.PROP_PIVOT_HEADER_HEIGHT, this.onRowHeightChanged.bind(this));
    this.addDestroyableEventListener(this.gridOptionsWrapper, GridOptionsWrapper.PROP_GROUP_HEADER_HEIGHT, this.onRowHeightChanged.bind(this));
    this.addDestroyableEventListener(this.gridOptionsWrapper, GridOptionsWrapper.PROP_PIVOT_GROUP_HEADER_HEIGHT, this.onRowHeightChanged.bind(this));
    this.addDestroyableEventListener(this.gridOptionsWrapper, GridOptionsWrapper.PROP_FLOATING_FILTERS_HEIGHT, this.onRowHeightChanged.bind(this));
    this.addDestroyableEventListener(this.eventService, Events.EVENT_VIRTUAL_COLUMNS_CHANGED, this.onVirtualColumnsChanged.bind(this));
    this.addDestroyableEventListener(this.eventService, Events.EVENT_DISPLAYED_COLUMNS_CHANGED, this.onDisplayedColumnsChanged.bind(this));
    this.addDestroyableEventListener(this.eventService, Events.EVENT_COLUMN_RESIZED, this.onColumnResized.bind(this));
    this.addDestroyableEventListener(this.eventService, Events.EVENT_GRID_COLUMNS_CHANGED, this.onGridColumnsChanged.bind(this));
  };

  HeaderRowComp.prototype.onColumnResized = function () {
    this.setWidth();
  };

  HeaderRowComp.prototype.setWidth = function () {
    var width = this.getWidthForRow();
    this.getGui().style.width = width + 'px';
  };

  HeaderRowComp.prototype.getWidthForRow = function () {
    var printLayout = this.gridOptionsWrapper.getDomLayout() === Constants.DOM_LAYOUT_PRINT;

    if (printLayout) {
      var centerRow = _.missing(this.pinned);

      if (centerRow) {
        return this.columnController.getContainerWidth(Constants.PINNED_RIGHT) + this.columnController.getContainerWidth(Constants.PINNED_LEFT) + this.columnController.getContainerWidth(null);
      } else {
        return 0;
      }
    } else {
      // if not printing, just return the width as normal
      return this.columnController.getContainerWidth(this.pinned);
    }
  };

  HeaderRowComp.prototype.onGridColumnsChanged = function () {
    this.removeAndDestroyAllChildComponents();
  };

  HeaderRowComp.prototype.removeAndDestroyAllChildComponents = function () {
    var idsOfAllChildren = Object.keys(this.headerComps);
    this.removeAndDestroyChildComponents(idsOfAllChildren);
  };

  HeaderRowComp.prototype.onDisplayedColumnsChanged = function () {
    this.onVirtualColumnsChanged();
    this.setWidth();
  };

  HeaderRowComp.prototype.getItemsAtDepth = function () {
    var _this = this;

    var printLayout = this.gridOptionsWrapper.getDomLayout() === Constants.DOM_LAYOUT_PRINT;

    if (printLayout) {
      // for print layout, we add all columns into the center
      var centerContainer = _.missing(this.pinned);

      if (centerContainer) {
        var result_1 = [];
        [Constants.PINNED_LEFT, null, Constants.PINNED_RIGHT].forEach(function (pinned) {
          var items = _this.columnController.getVirtualHeaderGroupRow(pinned, _this.type == HeaderRowType.FLOATING_FILTER ? _this.dept - 1 : _this.dept);

          result_1 = result_1.concat(items);
        });
        return result_1;
      } else {
        return [];
      }
    } else {
      // when in normal layout, we add the columns for that container only
      return this.columnController.getVirtualHeaderGroupRow(this.pinned, this.type == HeaderRowType.FLOATING_FILTER ? this.dept - 1 : this.dept);
    }
  };

  HeaderRowComp.prototype.onVirtualColumnsChanged = function () {
    var _this = this;

    var currentChildIds = Object.keys(this.headerComps);
    var correctChildIds = [];
    var itemsAtDepth = this.getItemsAtDepth();
    itemsAtDepth.forEach(function (child) {
      // skip groups that have no displayed children. this can happen when the group is broken,
      // and this section happens to have nothing to display for the open / closed state.
      // (a broken group is one that is split, ie columns in the group have a non-group column
      // in between them)
      if (child.isEmptyGroup()) {
        return;
      }

      var idOfChild = child.getUniqueId();

      var eParentContainer = _this.getGui(); // if we already have this cell rendered, do nothing


      var colAlreadyInDom = currentChildIds.indexOf(idOfChild) >= 0;
      var headerComp;
      var eHeaderCompGui;

      if (colAlreadyInDom) {
        _.removeFromArray(currentChildIds, idOfChild);
      } else {
        headerComp = _this.createHeaderComp(child);
        _this.headerComps[idOfChild] = headerComp;
        eHeaderCompGui = headerComp.getGui();
        eParentContainer.appendChild(eHeaderCompGui);
      }

      correctChildIds.push(idOfChild);
    }); // at this point, anything left in currentChildIds is an element that is no longer in the viewport

    this.removeAndDestroyChildComponents(currentChildIds);
    var ensureDomOrder = this.gridOptionsWrapper.isEnsureDomOrder();

    if (ensureDomOrder) {
      var correctChildOrder = correctChildIds.map(function (id) {
        return _this.headerComps[id].getGui();
      });

      _.setDomChildOrder(this.getGui(), correctChildOrder);
    }
  };

  HeaderRowComp.prototype.createHeaderComp = function (columnGroupChild) {
    var result;

    switch (this.type) {
      case HeaderRowType.COLUMN:
        result = new HeaderWrapperComp(columnGroupChild, this.dropTarget, this.pinned);
        break;

      case HeaderRowType.COLUMN_GROUP:
        result = new HeaderGroupWrapperComp(columnGroupChild, this.dropTarget, this.pinned);
        break;

      case HeaderRowType.FLOATING_FILTER:
        result = new FloatingFilterWrapper(columnGroupChild);
        break;
    }

    this.getContext().wireBean(result);
    return result;
  };

  __decorate$K([Autowired('gridOptionsWrapper')], HeaderRowComp.prototype, "gridOptionsWrapper", void 0);

  __decorate$K([Autowired('gridApi')], HeaderRowComp.prototype, "gridApi", void 0);

  __decorate$K([Autowired('columnController')], HeaderRowComp.prototype, "columnController", void 0);

  __decorate$K([Autowired('eventService')], HeaderRowComp.prototype, "eventService", void 0);

  __decorate$K([Autowired('filterManager')], HeaderRowComp.prototype, "filterManager", void 0);

  __decorate$K([PostConstruct], HeaderRowComp.prototype, "init", null);

  return HeaderRowComp;
}(Component);
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __decorate$L = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var MoveColumnController =
/** @class */
function () {
  function MoveColumnController(pinned, eContainer) {
    this.needToMoveLeft = false;
    this.needToMoveRight = false;
    this.pinned = pinned;
    this.eContainer = eContainer;
    this.centerContainer = !_.exists(pinned);
  }

  MoveColumnController.prototype.registerGridComp = function (gridPanel) {
    this.gridPanel = gridPanel;
  };

  MoveColumnController.prototype.init = function () {
    this.logger = this.loggerFactory.create('MoveColumnController');
  };

  MoveColumnController.prototype.getIconName = function () {
    return this.pinned ? DragAndDropService.ICON_PINNED : DragAndDropService.ICON_MOVE;
  };

  MoveColumnController.prototype.onDragEnter = function (draggingEvent) {
    // we do dummy drag, so make sure column appears in the right location when first placed
    var columns = draggingEvent.dragItem.columns;
    var dragCameFromToolPanel = draggingEvent.dragSource.type === exports.DragSourceType.ToolPanel;

    if (dragCameFromToolPanel) {
      // the if statement doesn't work if drag leaves grid, then enters again
      this.setColumnsVisible(columns, true, "uiColumnDragged");
    } else {
      // restore previous state of visible columns upon re-entering. this means if the user drags
      // a group out, and then drags the group back in, only columns that were originally visible
      // will be visible again. otherwise a group with three columns (but only two visible) could
      // be dragged out, then when it's dragged in again, all three are visible. this stops that.
      var visibleState_1 = draggingEvent.dragItem.visibleState;
      var visibleColumns = columns.filter(function (column) {
        return visibleState_1[column.getId()];
      });
      this.setColumnsVisible(visibleColumns, true, "uiColumnDragged");
    }

    this.setColumnsPinned(columns, this.pinned, "uiColumnDragged");
    this.onDragging(draggingEvent, true);
  };

  MoveColumnController.prototype.onDragLeave = function (draggingEvent) {
    var hideColumnOnExit = !this.gridOptionsWrapper.isSuppressDragLeaveHidesColumns() && !draggingEvent.fromNudge;

    if (hideColumnOnExit) {
      var dragItem = draggingEvent.dragSource.getDragItem();
      var columns = dragItem.columns;
      this.setColumnsVisible(columns, false, "uiColumnDragged");
    }

    this.ensureIntervalCleared();
  };

  MoveColumnController.prototype.setColumnsVisible = function (columns, visible, source) {
    if (source === void 0) {
      source = "api";
    }

    if (columns) {
      var allowedCols = columns.filter(function (c) {
        return !c.getColDef().lockVisible;
      });
      this.columnController.setColumnsVisible(allowedCols, visible, source);
    }
  };

  MoveColumnController.prototype.setColumnsPinned = function (columns, pinned, source) {
    if (source === void 0) {
      source = "api";
    }

    if (columns) {
      var allowedCols = columns.filter(function (c) {
        return !c.getColDef().lockPinned;
      });
      this.columnController.setColumnsPinned(allowedCols, pinned, source);
    }
  };

  MoveColumnController.prototype.onDragStop = function () {
    this.ensureIntervalCleared();
  };

  MoveColumnController.prototype.normaliseX = function (x) {
    // flip the coordinate if doing RTL
    if (this.gridOptionsWrapper.isEnableRtl()) {
      var clientWidth = this.eContainer.clientWidth;
      x = clientWidth - x;
    } // adjust for scroll only if centre container (the pinned containers don't scroll)


    if (this.centerContainer) {
      x += this.gridPanel.getCenterViewportScrollLeft();
    }

    return x;
  };

  MoveColumnController.prototype.checkCenterForScrolling = function (xAdjustedForScroll) {
    if (this.centerContainer) {
      // scroll if the mouse has gone outside the grid (or just outside the scrollable part if pinning)
      // putting in 50 buffer, so even if user gets to edge of grid, a scroll will happen
      var firstVisiblePixel = this.gridPanel.getCenterViewportScrollLeft();
      var lastVisiblePixel = firstVisiblePixel + this.gridPanel.getCenterWidth();

      if (this.gridOptionsWrapper.isEnableRtl()) {
        this.needToMoveRight = xAdjustedForScroll < firstVisiblePixel + 50;
        this.needToMoveLeft = xAdjustedForScroll > lastVisiblePixel - 50;
      } else {
        this.needToMoveLeft = xAdjustedForScroll < firstVisiblePixel + 50;
        this.needToMoveRight = xAdjustedForScroll > lastVisiblePixel - 50;
      }

      if (this.needToMoveLeft || this.needToMoveRight) {
        this.ensureIntervalStarted();
      } else {
        this.ensureIntervalCleared();
      }
    }
  };

  MoveColumnController.prototype.onDragging = function (draggingEvent, fromEnter) {
    var _this = this;

    if (fromEnter === void 0) {
      fromEnter = false;
    }

    this.lastDraggingEvent = draggingEvent; // if moving up or down (ie not left or right) then do nothing

    if (_.missing(draggingEvent.hDirection)) {
      return;
    }

    var mouseXNormalised = this.normaliseX(draggingEvent.x); // if the user is dragging into the panel, ie coming from the side panel into the main grid,
    // we don't want to scroll the grid this time, it would appear like the table is jumping
    // each time a column is dragged in.

    if (!fromEnter) {
      this.checkCenterForScrolling(mouseXNormalised);
    }

    var hDirectionNormalised = this.normaliseDirection(draggingEvent.hDirection);
    var dragSourceType = draggingEvent.dragSource.type;
    var columnsToMove = draggingEvent.dragSource.getDragItem().columns;
    columnsToMove = columnsToMove.filter(function (col) {
      if (col.getColDef().lockPinned) {
        // if locked return true only if both col and container are same pin type.
        // double equals (==) here on purpose so that null==undefined is true (for not pinned options)
        return col.getPinned() == _this.pinned;
      } else {
        // if not pin locked, then always allowed to be in this container
        return true;
      }
    });
    this.attemptMoveColumns(dragSourceType, columnsToMove, hDirectionNormalised, mouseXNormalised, fromEnter);
  };

  MoveColumnController.prototype.normaliseDirection = function (hDirection) {
    if (this.gridOptionsWrapper.isEnableRtl()) {
      switch (hDirection) {
        case exports.HorizontalDirection.Left:
          return exports.HorizontalDirection.Right;

        case exports.HorizontalDirection.Right:
          return exports.HorizontalDirection.Left;

        default:
          console.error("ag-Grid: Unknown direction " + hDirection);
      }
    } else {
      return hDirection;
    }
  }; // returns the index of the first column in the list ONLY if the cols are all beside
  // each other. if the cols are not beside each other, then returns null


  MoveColumnController.prototype.calculateOldIndex = function (movingCols) {
    var gridCols = this.columnController.getAllGridColumns();

    var indexes = _.sortNumerically(movingCols.map(function (col) {
      return gridCols.indexOf(col);
    }));

    var firstIndex = indexes[0];

    var lastIndex = _.last(indexes);

    var spread = lastIndex - firstIndex;
    var gapsExist = spread !== indexes.length - 1;
    return gapsExist ? null : firstIndex;
  };

  MoveColumnController.prototype.attemptMoveColumns = function (dragSourceType, allMovingColumns, hDirection, mouseX, fromEnter) {
    var draggingLeft = hDirection === exports.HorizontalDirection.Left;
    var draggingRight = hDirection === exports.HorizontalDirection.Right;
    var validMoves = this.calculateValidMoves(allMovingColumns, draggingRight, mouseX); // if cols are not adjacent, then this returns null. when moving, we constrain the direction of the move
    // (ie left or right) to the mouse direction. however

    var oldIndex = this.calculateOldIndex(allMovingColumns);

    if (validMoves.length === 0) {
      return;
    }

    var firstValidMove = validMoves[0]; // the two check below stop an error when the user grabs a group my a middle column, then
    // it is possible the mouse pointer is to the right of a column while been dragged left.
    // so we need to make sure that the mouse pointer is actually left of the left most column
    // if moving left, and right of the right most column if moving right
    // we check 'fromEnter' below so we move the column to the new spot if the mouse is coming from
    // outside the grid, eg if the column is moving from side panel, mouse is moving left, then we should
    // place the column to the RHS even if the mouse is moving left and the column is already on
    // the LHS. otherwise we stick to the rule described above.

    var constrainDirection = oldIndex !== null && !fromEnter; // don't consider 'fromEnter' when dragging header cells, otherwise group can jump to opposite direction of drag

    if (dragSourceType == exports.DragSourceType.HeaderCell) {
      constrainDirection = oldIndex !== null;
    }

    if (constrainDirection) {
      // only allow left drag if this column is moving left
      if (draggingLeft && firstValidMove >= oldIndex) {
        return;
      } // only allow right drag if this column is moving right


      if (draggingRight && firstValidMove <= oldIndex) {
        return;
      }
    }

    for (var i = 0; i < validMoves.length; i++) {
      var move = validMoves[i];

      if (!this.columnController.doesMovePassRules(allMovingColumns, move)) {
        continue;
      }

      this.columnController.moveColumns(allMovingColumns, move, "uiColumnDragged"); // important to return here, so once we do the first valid move, we don't try do any more

      return;
    }
  };

  MoveColumnController.prototype.calculateValidMoves = function (movingCols, draggingRight, mouseX) {
    // this is the list of cols on the screen, so it's these we use when comparing the x mouse position
    var allDisplayedCols = this.columnController.getDisplayedColumns(this.pinned); // but this list is the list of all cols, when we move a col it's the index within this list that gets used,
    // so the result we return has to be and index location for this list

    var allGridCols = this.columnController.getAllGridColumns();
    var movingDisplayedCols = allDisplayedCols.filter(function (col) {
      return _.includes(movingCols, col);
    });
    var otherDisplayedCols = allDisplayedCols.filter(function (col) {
      return !_.includes(movingCols, col);
    });
    var otherGridCols = allGridCols.filter(function (col) {
      return !_.includes(movingCols, col);
    }); // work out how many DISPLAYED columns fit before the 'x' position. this gives us the displayIndex.
    // for example, if cols are a,b,c,d and we find a,b fit before 'x', then we want to place the moving
    // col between b and c (so that it is under the mouse position).

    var displayIndex = 0;
    var availableWidth = mouseX; // if we are dragging right, then the columns will be to the left of the mouse, so we also want to
    // include the width of the moving columns

    if (draggingRight) {
      var widthOfMovingDisplayedCols_1 = 0;
      movingDisplayedCols.forEach(function (col) {
        return widthOfMovingDisplayedCols_1 += col.getActualWidth();
      });
      availableWidth -= widthOfMovingDisplayedCols_1;
    }

    if (availableWidth > 0) {
      // now count how many of the displayed columns will fit to the left
      for (var i = 0; i < otherDisplayedCols.length; i++) {
        var col = otherDisplayedCols[i];
        availableWidth -= col.getActualWidth();

        if (availableWidth < 0) {
          break;
        }

        displayIndex++;
      } // trial and error, if going right, we adjust by one, i didn't manage to quantify why, but it works


      if (draggingRight) {
        displayIndex++;
      }
    } // the display index is with respect to all the showing columns, however when we move, it's with
    // respect to all grid columns, so we need to translate from display index to grid index


    var firstValidMove;

    if (displayIndex > 0) {
      var leftColumn = otherDisplayedCols[displayIndex - 1];
      firstValidMove = otherGridCols.indexOf(leftColumn) + 1;
    } else {
      firstValidMove = otherGridCols.indexOf(otherDisplayedCols[0]);

      if (firstValidMove === -1) {
        firstValidMove = 0;
      }
    }

    var validMoves = [firstValidMove]; // add in other valid moves due to hidden columns and married children. for example, a particular
    // move might break a group that has married children (so move isn't valid), however there could
    // be hidden columns (not displayed) that we could jump over to make the move valid. because
    // they are hidden, user doesn't see any different, however it allows moves that would otherwise
    // not work. for example imagine a group with 9 columns and all columns are hidden except the
    // middle one (so 4 hidden to left, 4 hidden to right), then when moving 'firstValidMove' will
    // be relative to the not-shown column, however we need to consider the move jumping over all the
    // hidden children. if we didn't do this, then if the group just described was at the end (RHS) of the
    // grid, there would be no way to put a column after it (as the grid would only consider beside the
    // visible column, which would fail valid move rules).

    if (draggingRight) {
      // if dragging right, then we add all the additional moves to the right. so in other words
      // if the next move is not valid, find the next move to the right that is valid.
      var pointer = firstValidMove + 1;
      var lastIndex = allGridCols.length - 1;

      while (pointer <= lastIndex) {
        validMoves.push(pointer);
        pointer++;
      }
    } else {
      // if dragging left we do the reverse of dragging right, we add in all the valid moves to the
      // left. however we also have to consider moves to the right for all hidden columns first.
      // (this logic is hard to reason with, it was worked out with trial and error,
      // move observation rather than science).
      // add moves to the right
      var pointer = firstValidMove;
      var lastIndex = allGridCols.length - 1;
      var displacedCol = allGridCols[pointer];

      while (pointer <= lastIndex && this.isColumnHidden(allDisplayedCols, displacedCol)) {
        pointer++;
        validMoves.push(pointer);
        displacedCol = allGridCols[pointer];
      } // add moves to the left


      pointer = firstValidMove - 1;
      var firstDisplayIndex = 0;

      while (pointer >= firstDisplayIndex) {
        validMoves.push(pointer);
        pointer--;
      }
    }

    return validMoves;
  }; // isHidden takes into account visible=false and group=closed, ie it is not displayed


  MoveColumnController.prototype.isColumnHidden = function (displayedColumns, col) {
    return displayedColumns.indexOf(col) < 0;
  };

  MoveColumnController.prototype.ensureIntervalStarted = function () {
    if (!this.movingIntervalId) {
      this.intervalCount = 0;
      this.failedMoveAttempts = 0;
      this.movingIntervalId = window.setInterval(this.moveInterval.bind(this), 100);

      if (this.needToMoveLeft) {
        this.dragAndDropService.setGhostIcon(DragAndDropService.ICON_LEFT, true);
      } else {
        this.dragAndDropService.setGhostIcon(DragAndDropService.ICON_RIGHT, true);
      }
    }
  };

  MoveColumnController.prototype.ensureIntervalCleared = function () {
    if (this.moveInterval) {
      window.clearInterval(this.movingIntervalId);
      this.movingIntervalId = null;
      this.dragAndDropService.setGhostIcon(DragAndDropService.ICON_MOVE);
    }
  };

  MoveColumnController.prototype.moveInterval = function () {
    // the amounts we move get bigger at each interval, so the speed accelerates, starting a bit slow
    // and getting faster. this is to give smoother user experience. we max at 100px to limit the speed.
    var pixelsToMove;
    this.intervalCount++;
    pixelsToMove = 10 + this.intervalCount * 5;

    if (pixelsToMove > 100) {
      pixelsToMove = 100;
    }

    var pixelsMoved;

    if (this.needToMoveLeft) {
      pixelsMoved = this.gridPanel.scrollHorizontally(-pixelsToMove);
    } else if (this.needToMoveRight) {
      pixelsMoved = this.gridPanel.scrollHorizontally(pixelsToMove);
    }

    if (pixelsMoved !== 0) {
      this.onDragging(this.lastDraggingEvent);
      this.failedMoveAttempts = 0;
    } else {
      // we count the failed move attempts. if we fail to move 7 times, then we pin the column.
      // this is how we achieve pining by dragging the column to the edge of the grid.
      this.failedMoveAttempts++;
      var columns = this.lastDraggingEvent.dragItem.columns;
      var columnsThatCanPin = columns.filter(function (c) {
        return !c.getColDef().lockPinned;
      });

      if (columnsThatCanPin.length > 0) {
        this.dragAndDropService.setGhostIcon(DragAndDropService.ICON_PINNED);

        if (this.failedMoveAttempts > 7) {
          var pinType = this.needToMoveLeft ? Constants.PINNED_LEFT : Constants.PINNED_RIGHT;
          this.setColumnsPinned(columnsThatCanPin, pinType, "uiColumnDragged");
          this.dragAndDropService.nudge();
        }
      }
    }
  };

  __decorate$L([Autowired('loggerFactory')], MoveColumnController.prototype, "loggerFactory", void 0);

  __decorate$L([Autowired('columnController')], MoveColumnController.prototype, "columnController", void 0);

  __decorate$L([Autowired('dragAndDropService')], MoveColumnController.prototype, "dragAndDropService", void 0);

  __decorate$L([Autowired('gridOptionsWrapper')], MoveColumnController.prototype, "gridOptionsWrapper", void 0);

  __decorate$L([PostConstruct], MoveColumnController.prototype, "init", null);

  return MoveColumnController;
}();
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __decorate$M = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var BodyDropPivotTarget =
/** @class */
function () {
  function BodyDropPivotTarget(pinned) {
    this.columnsToAggregate = [];
    this.columnsToGroup = [];
    this.columnsToPivot = [];
    this.pinned = pinned;
  }
  /** Callback for when drag enters */


  BodyDropPivotTarget.prototype.onDragEnter = function (draggingEvent) {
    var _this = this;

    this.clearColumnsList(); // in pivot mode, we don't accept any drops if functions are read only

    if (this.gridOptionsWrapper.isFunctionsReadOnly()) {
      return;
    }

    var dragColumns = draggingEvent.dragItem.columns;
    dragColumns.forEach(function (column) {
      // we don't allow adding secondary columns
      if (!column.isPrimary()) {
        return;
      }

      if (column.isAnyFunctionActive()) {
        return;
      }

      if (column.isAllowValue()) {
        _this.columnsToAggregate.push(column);
      } else if (column.isAllowRowGroup()) {
        _this.columnsToGroup.push(column);
      } else if (column.isAllowRowGroup()) {
        _this.columnsToPivot.push(column);
      }
    });
  };

  BodyDropPivotTarget.prototype.getIconName = function () {
    var totalColumns = this.columnsToAggregate.length + this.columnsToGroup.length + this.columnsToPivot.length;

    if (totalColumns > 0) {
      return this.pinned ? DragAndDropService.ICON_PINNED : DragAndDropService.ICON_MOVE;
    } else {
      return null;
    }
  };
  /** Callback for when drag leaves */


  BodyDropPivotTarget.prototype.onDragLeave = function (draggingEvent) {
    // if we are taking columns out of the center, then we remove them from the report
    this.clearColumnsList();
  };

  BodyDropPivotTarget.prototype.clearColumnsList = function () {
    this.columnsToAggregate.length = 0;
    this.columnsToGroup.length = 0;
    this.columnsToPivot.length = 0;
  };
  /** Callback for when dragging */


  BodyDropPivotTarget.prototype.onDragging = function (draggingEvent) {};
  /** Callback for when drag stops */


  BodyDropPivotTarget.prototype.onDragStop = function (draggingEvent) {
    if (this.columnsToAggregate.length > 0) {
      this.columnController.addValueColumns(this.columnsToAggregate, "toolPanelDragAndDrop");
    }

    if (this.columnsToGroup.length > 0) {
      this.columnController.addRowGroupColumns(this.columnsToGroup, "toolPanelDragAndDrop");
    }

    if (this.columnsToPivot.length > 0) {
      this.columnController.addPivotColumns(this.columnsToPivot, "toolPanelDragAndDrop");
    }
  };

  __decorate$M([Autowired('columnController')], BodyDropPivotTarget.prototype, "columnController", void 0);

  __decorate$M([Autowired('gridOptionsWrapper')], BodyDropPivotTarget.prototype, "gridOptionsWrapper", void 0);

  return BodyDropPivotTarget;
}();
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __decorate$N = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var DropType;

(function (DropType) {
  DropType[DropType["ColumnMove"] = 0] = "ColumnMove";
  DropType[DropType["Pivot"] = 1] = "Pivot";
})(DropType || (DropType = {}));

var BodyDropTarget =
/** @class */
function () {
  function BodyDropTarget(pinned, eContainer) {
    this.dropListeners = {};
    this.pinned = pinned;
    this.eContainer = eContainer;
  }

  BodyDropTarget.prototype.registerGridComp = function (gridPanel) {
    this.gridPanel = gridPanel;
    this.moveColumnController.registerGridComp(gridPanel);

    switch (this.pinned) {
      case Constants.PINNED_LEFT:
        this.eSecondaryContainers = this.gridPanel.getDropTargetLeftContainers();
        break;

      case Constants.PINNED_RIGHT:
        this.eSecondaryContainers = this.gridPanel.getDropTargetRightContainers();
        break;

      default:
        this.eSecondaryContainers = this.gridPanel.getDropTargetBodyContainers();
        break;
    }
  };

  BodyDropTarget.prototype.isInterestedIn = function (type) {
    return type === exports.DragSourceType.HeaderCell || type === exports.DragSourceType.ToolPanel && this.gridOptionsWrapper.isAllowDragFromColumnsToolPanel();
  };

  BodyDropTarget.prototype.getSecondaryContainers = function () {
    return this.eSecondaryContainers;
  };

  BodyDropTarget.prototype.getContainer = function () {
    return this.eContainer;
  };

  BodyDropTarget.prototype.init = function () {
    this.moveColumnController = new MoveColumnController(this.pinned, this.eContainer);
    this.context.wireBean(this.moveColumnController);
    var bodyDropPivotTarget = new BodyDropPivotTarget(this.pinned);
    this.context.wireBean(bodyDropPivotTarget);
    this.dropListeners[DropType.ColumnMove] = this.moveColumnController;
    this.dropListeners[DropType.Pivot] = bodyDropPivotTarget;
    this.dragAndDropService.addDropTarget(this);
  };

  BodyDropTarget.prototype.getIconName = function () {
    return this.currentDropListener.getIconName();
  }; // we want to use the bodyPivotTarget if the user is dragging columns in from the toolPanel
  // and we are in pivot mode, as it has to logic to set pivot/value/group on the columns when
  // dropped into the grid's body.


  BodyDropTarget.prototype.getDropType = function (draggingEvent) {
    if (this.columnController.isPivotMode()) {
      // in pivot mode, then if moving a column (ie didn't come from toolpanel) then it's
      // a standard column move, however if it came from teh toolpanel, then we are introducing
      // dimensions or values to the grid
      if (draggingEvent.dragSource.type === exports.DragSourceType.ToolPanel) {
        return DropType.Pivot;
      } else {
        return DropType.ColumnMove;
      }
    } else {
      // it's a column, and not pivot mode, so always moving
      return DropType.ColumnMove;
    }
  };

  BodyDropTarget.prototype.onDragEnter = function (draggingEvent) {
    // we pick the drop listener depending on whether we are in pivot mode are not. if we are
    // in pivot mode, then dropping cols changes the row group, pivot, value stats. otherwise
    // we change visibility state and position.
    // if (this.columnController.isPivotMode()) {
    var dropType = this.getDropType(draggingEvent);
    this.currentDropListener = this.dropListeners[dropType];
    this.currentDropListener.onDragEnter(draggingEvent);
  };

  BodyDropTarget.prototype.onDragLeave = function (params) {
    this.currentDropListener.onDragLeave(params);
  };

  BodyDropTarget.prototype.onDragging = function (params) {
    this.currentDropListener.onDragging(params);
  };

  BodyDropTarget.prototype.onDragStop = function (params) {
    this.currentDropListener.onDragStop(params);
  };

  __decorate$N([Autowired('context')], BodyDropTarget.prototype, "context", void 0);

  __decorate$N([Autowired('dragAndDropService')], BodyDropTarget.prototype, "dragAndDropService", void 0);

  __decorate$N([Autowired('columnController')], BodyDropTarget.prototype, "columnController", void 0);

  __decorate$N([Autowired('gridOptionsWrapper')], BodyDropTarget.prototype, "gridOptionsWrapper", void 0);

  __decorate$N([PostConstruct], BodyDropTarget.prototype, "init", null);

  return BodyDropTarget;
}();
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __decorate$O = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var HeaderContainer =
/** @class */
function () {
  function HeaderContainer(eContainer, eViewport, pinned) {
    this.headerRowComps = [];
    this.eContainer = eContainer;
    this.pinned = pinned;
    this.eViewport = eViewport;
  }

  HeaderContainer.prototype.registerGridComp = function (gridPanel) {
    this.setupDragAndDrop(gridPanel);
  };

  HeaderContainer.prototype.forEachHeaderElement = function (callback) {
    this.headerRowComps.forEach(function (headerRowComp) {
      return headerRowComp.forEachHeaderElement(callback);
    });
  };

  HeaderContainer.prototype.init = function () {
    this.scrollWidth = this.gridOptionsWrapper.getScrollbarWidth(); // if value changes, then if not pivoting, we at least need to change the label eg from sum() to avg(),
    // if pivoting, then the columns have changed

    this.eventService.addEventListener(Events.EVENT_COLUMN_VALUE_CHANGED, this.onColumnValueChanged.bind(this));
    this.eventService.addEventListener(Events.EVENT_COLUMN_ROW_GROUP_CHANGED, this.onColumnRowGroupChanged.bind(this));
    this.eventService.addEventListener(Events.EVENT_GRID_COLUMNS_CHANGED, this.onGridColumnsChanged.bind(this));
    this.eventService.addEventListener(Events.EVENT_SCROLL_VISIBILITY_CHANGED, this.onScrollVisibilityChanged.bind(this));
    this.eventService.addEventListener(Events.EVENT_COLUMN_RESIZED, this.onColumnResized.bind(this));
    this.eventService.addEventListener(Events.EVENT_DISPLAYED_COLUMNS_CHANGED, this.onDisplayedColumnsChanged.bind(this));
  }; // if row group changes, that means we may need to add aggFuncs to the column headers,
  // if the grid goes from no aggregation (ie no grouping) to grouping


  HeaderContainer.prototype.onColumnRowGroupChanged = function () {
    this.onGridColumnsChanged();
  }; // if the agg func of a column changes, then we may need to update the agg func in columns header


  HeaderContainer.prototype.onColumnValueChanged = function () {
    this.onGridColumnsChanged();
  };

  HeaderContainer.prototype.onColumnResized = function () {
    this.setWidthOfPinnedContainer();
  };

  HeaderContainer.prototype.onDisplayedColumnsChanged = function () {
    this.setWidthOfPinnedContainer();
  };

  HeaderContainer.prototype.onScrollVisibilityChanged = function () {
    this.setWidthOfPinnedContainer();
  };

  HeaderContainer.prototype.setWidthOfPinnedContainer = function () {
    var pinningLeft = this.pinned === Constants.PINNED_LEFT;
    var pinningRight = this.pinned === Constants.PINNED_RIGHT;
    var controller = this.columnController;
    var isRtl = this.gridOptionsWrapper.isEnableRtl();

    if (pinningLeft || pinningRight) {
      // size to fit all columns
      var width = controller[pinningLeft ? 'getPinnedLeftContainerWidth' : 'getPinnedRightContainerWidth'](); // if there is a scroll showing (and taking up space, so Windows, and not iOS)
      // in the body, then we add extra space to keep header aligned with the body,
      // as body width fits the cols and the scrollbar

      var addPaddingForScrollbar = this.scrollVisibleService.isVerticalScrollShowing() && (isRtl && pinningLeft || !isRtl && pinningRight);

      if (addPaddingForScrollbar) {
        width += this.scrollWidth;
      }

      _.setFixedWidth(this.eContainer, width);
    }
  };

  HeaderContainer.prototype.destroy = function () {
    this.removeHeaderRowComps();
  };

  HeaderContainer.prototype.getRowComps = function () {
    return this.headerRowComps;
  }; // grid cols have changed - this also means the number of rows in the header can have
  // changed. so we remove all the old rows and insert new ones for a complete refresh


  HeaderContainer.prototype.onGridColumnsChanged = function () {
    this.removeAndCreateAllRowComps();
  };

  HeaderContainer.prototype.removeAndCreateAllRowComps = function () {
    this.removeHeaderRowComps();
    this.createHeaderRowComps();
  }; // we expose this for gridOptions.api.refreshHeader() to call


  HeaderContainer.prototype.refresh = function () {
    this.removeAndCreateAllRowComps();
  };

  HeaderContainer.prototype.setupDragAndDrop = function (gridComp) {
    var dropContainer = this.eViewport ? this.eViewport : this.eContainer;
    var bodyDropTarget = new BodyDropTarget(this.pinned, dropContainer);
    this.context.wireBean(bodyDropTarget);
    bodyDropTarget.registerGridComp(gridComp);
  };

  HeaderContainer.prototype.removeHeaderRowComps = function () {
    this.headerRowComps.forEach(function (headerRowComp) {
      headerRowComp.destroy();
    });
    this.headerRowComps.length = 0;

    _.clearElement(this.eContainer);
  };

  HeaderContainer.prototype.createHeaderRowComps = function () {
    // if we are displaying header groups, then we have many rows here.
    // go through each row of the header, one by one.
    var rowCount = this.columnController.getHeaderRowCount();

    for (var dept = 0; dept < rowCount; dept++) {
      var groupRow = dept !== rowCount - 1;
      var type = groupRow ? HeaderRowType.COLUMN_GROUP : HeaderRowType.COLUMN;
      var headerRowComp = new HeaderRowComp(dept, type, this.pinned, this.dropTarget);
      this.context.wireBean(headerRowComp);
      this.headerRowComps.push(headerRowComp);
      headerRowComp.getGui().setAttribute('aria-rowindex', this.headerRowComps.length.toString());
      this.eContainer.appendChild(headerRowComp.getGui());
    }

    var includeFloatingFilterRow = this.gridOptionsWrapper.isFloatingFilter() && !this.columnController.isPivotMode();

    if (includeFloatingFilterRow) {
      var headerRowComp = new HeaderRowComp(rowCount, HeaderRowType.FLOATING_FILTER, this.pinned, this.dropTarget);
      this.context.wireBean(headerRowComp);
      this.headerRowComps.push(headerRowComp);
      headerRowComp.getGui().setAttribute('aria-rowindex', this.headerRowComps.length.toString());
      this.eContainer.appendChild(headerRowComp.getGui());
    }
  };

  __decorate$O([Autowired('gridOptionsWrapper')], HeaderContainer.prototype, "gridOptionsWrapper", void 0);

  __decorate$O([Autowired('context')], HeaderContainer.prototype, "context", void 0);

  __decorate$O([Autowired('$scope')], HeaderContainer.prototype, "$scope", void 0);

  __decorate$O([Autowired('dragAndDropService')], HeaderContainer.prototype, "dragAndDropService", void 0);

  __decorate$O([Autowired('columnController')], HeaderContainer.prototype, "columnController", void 0);

  __decorate$O([Autowired('eventService')], HeaderContainer.prototype, "eventService", void 0);

  __decorate$O([Autowired('scrollVisibleService')], HeaderContainer.prototype, "scrollVisibleService", void 0);

  __decorate$O([PostConstruct], HeaderContainer.prototype, "init", null);

  return HeaderContainer;
}();
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __extends$L = undefined && undefined.__extends || function () {
  var _extendStatics48 = function extendStatics(d, b) {
    _extendStatics48 = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics48(d, b);
  };

  return function (d, b) {
    _extendStatics48(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var __decorate$P = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var HeaderRootComp =
/** @class */
function (_super) {
  __extends$L(HeaderRootComp, _super);

  function HeaderRootComp() {
    return _super.call(this, HeaderRootComp.TEMPLATE) || this;
  }

  HeaderRootComp.prototype.registerGridComp = function (gridPanel) {
    this.gridPanel = gridPanel;
    this.childContainers.forEach(function (c) {
      return c.registerGridComp(gridPanel);
    });
  };

  HeaderRootComp.prototype.postConstruct = function () {
    var _this = this;

    this.printLayout = this.gridOptionsWrapper.getDomLayout() === Constants.DOM_LAYOUT_PRINT;
    this.gridApi.registerHeaderRootComp(this);
    this.autoWidthCalculator.registerHeaderRootComp(this);
    var centerContainer = new HeaderContainer(this.eHeaderContainer, this.eHeaderViewport, null);
    var pinnedLeftContainer = new HeaderContainer(this.ePinnedLeftHeader, null, Constants.PINNED_LEFT);
    var pinnedRightContainer = new HeaderContainer(this.ePinnedRightHeader, null, Constants.PINNED_RIGHT);
    this.childContainers = [centerContainer, pinnedLeftContainer, pinnedRightContainer];
    this.childContainers.forEach(function (container) {
      return _this.getContext().wireBean(container);
    }); // shotgun way to get labels to change, eg from sum(amount) to avg(amount)

    this.addDestroyableEventListener(this.eventService, Events.EVENT_COLUMN_VALUE_CHANGED, this.refreshHeader.bind(this));
    this.addDestroyableEventListener(this.gridOptionsWrapper, GridOptionsWrapper.PROP_DOM_LAYOUT, this.onDomLayoutChanged.bind(this)); // for setting ag-pivot-on / ag-pivot-off CSS classes

    this.addDestroyableEventListener(this.eventService, Events.EVENT_COLUMN_PIVOT_MODE_CHANGED, this.onPivotModeChanged.bind(this));
    this.onPivotModeChanged();
    this.addPreventHeaderScroll();

    if (this.columnController.isReady()) {
      this.refreshHeader();
    }
  };

  HeaderRootComp.prototype.onDomLayoutChanged = function () {
    var newValue = this.gridOptionsWrapper.getDomLayout() === Constants.DOM_LAYOUT_PRINT;

    if (this.printLayout !== newValue) {
      this.printLayout = newValue;
      this.refreshHeader();
    }
  };

  HeaderRootComp.prototype.setHorizontalScroll = function (offset) {
    this.eHeaderContainer.style.transform = "translateX(" + offset + "px)";
  };

  HeaderRootComp.prototype.forEachHeaderElement = function (callback) {
    this.childContainers.forEach(function (childContainer) {
      return childContainer.forEachHeaderElement(callback);
    });
  };

  HeaderRootComp.prototype.destroy = function () {
    _super.prototype.destroy.call(this);

    this.childContainers.forEach(function (container) {
      return container.destroy();
    });
  };

  HeaderRootComp.prototype.refreshHeader = function () {
    this.childContainers.forEach(function (container) {
      return container.refresh();
    });
  };

  HeaderRootComp.prototype.onPivotModeChanged = function () {
    var pivotMode = this.columnController.isPivotMode();

    _.addOrRemoveCssClass(this.getGui(), 'ag-pivot-on', pivotMode);

    _.addOrRemoveCssClass(this.getGui(), 'ag-pivot-off', !pivotMode);
  };

  HeaderRootComp.prototype.setHeight = function (height) {
    // one extra pixel is needed here to account for the
    // height of the border
    var px = height + 1 + "px";
    this.getGui().style.height = px;
    this.getGui().style.minHeight = px;
  }; // if the user is in floating filter and hits tab a few times, the header can
  // end up scrolling to show items off the screen, leaving the grid and header
  // and the grid columns no longer in sync.


  HeaderRootComp.prototype.addPreventHeaderScroll = function () {
    var _this = this;

    this.addDestroyableEventListener(this.eHeaderViewport, 'scroll', function () {
      // if the header scrolls, the header will be out of sync. so we reset the
      // header scroll, and then scroll the body, which will in turn set the offset
      // on the header, giving the impression that the header scrolled as expected.
      var scrollLeft = _this.eHeaderViewport.scrollLeft;

      if (scrollLeft !== 0) {
        _this.gridPanel.scrollHorizontally(scrollLeft);

        _this.eHeaderViewport.scrollLeft = 0;
      }
    });
  };

  HeaderRootComp.prototype.setHeaderContainerWidth = function (width) {
    this.eHeaderContainer.style.width = width + "px";
  };

  HeaderRootComp.prototype.setLeftVisible = function (visible) {
    _.setDisplayed(this.ePinnedLeftHeader, visible);
  };

  HeaderRootComp.prototype.setRightVisible = function (visible) {
    _.setDisplayed(this.ePinnedRightHeader, visible);
  };

  HeaderRootComp.prototype.getHeaderRowCount = function () {
    if (this.childContainers.length === 0) {
      return 0;
    }

    return this.childContainers[0].getRowComps().length;
  };

  HeaderRootComp.TEMPLATE = "<div class=\"ag-header\" role=\"presentation\">\n            <div class=\"ag-pinned-left-header\" ref=\"ePinnedLeftHeader\" role=\"presentation\"></div>\n            <div class=\"ag-header-viewport\" ref=\"eHeaderViewport\" role=\"presentation\">\n                <div class=\"ag-header-container\" ref=\"eHeaderContainer\" role=\"rowgroup\"></div>\n            </div>\n            <div class=\"ag-pinned-right-header\" ref=\"ePinnedRightHeader\" role=\"presentation\"></div>\n        </div>";

  __decorate$P([RefSelector('ePinnedLeftHeader')], HeaderRootComp.prototype, "ePinnedLeftHeader", void 0);

  __decorate$P([RefSelector('ePinnedRightHeader')], HeaderRootComp.prototype, "ePinnedRightHeader", void 0);

  __decorate$P([RefSelector('eHeaderContainer')], HeaderRootComp.prototype, "eHeaderContainer", void 0);

  __decorate$P([RefSelector('eHeaderViewport')], HeaderRootComp.prototype, "eHeaderViewport", void 0);

  __decorate$P([Autowired('gridOptionsWrapper')], HeaderRootComp.prototype, "gridOptionsWrapper", void 0);

  __decorate$P([Autowired('columnController')], HeaderRootComp.prototype, "columnController", void 0);

  __decorate$P([Autowired('eventService')], HeaderRootComp.prototype, "eventService", void 0);

  __decorate$P([Autowired('gridApi')], HeaderRootComp.prototype, "gridApi", void 0);

  __decorate$P([Autowired('autoWidthCalculator')], HeaderRootComp.prototype, "autoWidthCalculator", void 0);

  __decorate$P([PostConstruct], HeaderRootComp.prototype, "postConstruct", null);

  return HeaderRootComp;
}(Component);
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __decorate$Q = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var FilterManager =
/** @class */
function () {
  function FilterManager() {
    this.allFilters = {};
    this.quickFilter = null;
    this.quickFilterParts = null; // this is true when the grid is processing the filter change. this is used by the cell comps, so that they
    // don't flash when data changes due to filter changes. there is no need to flash when filter changes as the
    // user is in control, so doesn't make sense to show flashing changes. for example, go to main demo where
    // this feature is turned off (hack code to always return false for isSuppressFlashingCellsBecauseFiltering(), put in)
    // 100,000 rows and group by country. then do some filtering. all the cells flash, which is silly.

    this.processingFilterChange = false;
  }

  FilterManager_1 = FilterManager;

  FilterManager.prototype.registerGridCore = function (gridCore) {
    this.gridCore = gridCore;
  };

  FilterManager.prototype.init = function () {
    this.eventService.addEventListener(Events.EVENT_ROW_DATA_CHANGED, this.onNewRowsLoaded.bind(this));
    this.eventService.addEventListener(Events.EVENT_NEW_COLUMNS_LOADED, this.onNewColumnsLoaded.bind(this));
    this.quickFilter = this.parseQuickFilter(this.gridOptionsWrapper.getQuickFilterText());
    this.setQuickFilterParts();
    this.allowShowChangeAfterFilter = this.gridOptionsWrapper.isAllowShowChangeAfterFilter(); // check this here, in case there is a filter from the start

    this.checkExternalFilter();
  };

  FilterManager.prototype.setQuickFilterParts = function () {
    if (this.quickFilter) {
      this.quickFilterParts = this.quickFilter.split(' ');
    } else {
      this.quickFilterParts = null;
    }
  };

  FilterManager.prototype.setFilterModel = function (model) {
    var _this = this;

    var allPromises = [];

    if (model) {
      // mark the filters as we set them, so any active filters left over we stop
      var modelKeys_1 = Object.keys(model);

      _.iterateObject(this.allFilters, function (colId, filterWrapper) {
        _.removeFromArray(modelKeys_1, colId);

        var newModel = model[colId];

        _this.setModelOnFilterWrapper(filterWrapper.filterPromise, newModel);

        allPromises.push(filterWrapper.filterPromise);
      }); // at this point, processedFields contains data for which we don't have a filter working yet


      modelKeys_1.forEach(function (colId) {
        var column = _this.columnController.getPrimaryColumn(colId);

        if (!column) {
          console.warn('Warning ag-grid setFilterModel - no column found for colId ' + colId);
          return;
        }

        var filterWrapper = _this.getOrCreateFilterWrapper(column, 'NO_UI');

        _this.setModelOnFilterWrapper(filterWrapper.filterPromise, model[colId]);

        allPromises.push(filterWrapper.filterPromise);
      });
    } else {
      _.iterateObject(this.allFilters, function (key, filterWrapper) {
        _this.setModelOnFilterWrapper(filterWrapper.filterPromise, null);

        allPromises.push(filterWrapper.filterPromise);
      });
    }

    Promise.all(allPromises).then(function (whatever) {
      _this.onFilterChanged();
    });
  };

  FilterManager.prototype.setModelOnFilterWrapper = function (filterPromise, newModel) {
    filterPromise.then(function (filter) {
      if (typeof filter.setModel !== 'function') {
        console.warn('Warning ag-grid - filter missing setModel method, which is needed for setFilterModel');
        return;
      }

      filter.setModel(newModel);
    });
  };

  FilterManager.prototype.getFilterModel = function () {
    var result = {};

    _.iterateObject(this.allFilters, function (key, filterWrapper) {
      // because user can provide filters, we provide useful error checking and messages
      var filterPromise = filterWrapper.filterPromise;
      var filter = filterPromise.resolveNow(null, function (filter) {
        return filter;
      });

      if (filter == null) {
        return null;
      }

      if (typeof filter.getModel !== 'function') {
        console.warn('Warning ag-grid - filter API missing getModel method, which is needed for getFilterModel');
        return;
      }

      var model = filter.getModel();

      if (_.exists(model)) {
        result[key] = model;
      }
    });

    return result;
  }; // returns true if any advanced filter (ie not quick filter) active


  FilterManager.prototype.isAdvancedFilterPresent = function () {
    return this.advancedFilterPresent;
  }; // called by:
  // 1) onFilterChanged()
  // 2) onNewRowsLoaded()


  FilterManager.prototype.setAdvancedFilterPresent = function () {
    var atLeastOneActive = false;

    _.iterateObject(this.allFilters, function (key, filterWrapper) {
      if (filterWrapper.filterPromise.resolveNow(false, function (filter) {
        return filter.isFilterActive();
      })) {
        atLeastOneActive = true;
      }
    });

    this.advancedFilterPresent = atLeastOneActive;
  };

  FilterManager.prototype.updateFilterFlagInColumns = function (source, additionalEventAttributes) {
    _.iterateObject(this.allFilters, function (key, filterWrapper) {
      var filterActive = filterWrapper.filterPromise.resolveNow(false, function (filter) {
        return filter.isFilterActive();
      });
      filterWrapper.column.setFilterActive(filterActive, source, additionalEventAttributes);
    });
  }; // returns true if quickFilter or advancedFilter


  FilterManager.prototype.isAnyFilterPresent = function () {
    return this.isQuickFilterPresent() || this.advancedFilterPresent || this.externalFilterPresent;
  };

  FilterManager.prototype.doesFilterPass = function (node, filterToSkip) {
    var data = node.data;
    var colKeys = Object.keys(this.allFilters);

    for (var i = 0, l = colKeys.length; i < l; i++) {
      // critical code, don't use functional programming
      var colId = colKeys[i];
      var filterWrapper = this.allFilters[colId]; // if no filter, always pass

      if (filterWrapper === undefined) {
        continue;
      }

      var filter = filterWrapper.filterPromise.resolveNow(undefined, function (filter) {
        return filter;
      }); // if filter not yet there, continue

      if (filter === undefined) {
        continue;
      }

      if (filter === filterToSkip) {
        continue;
      } // don't bother with filters that are not active


      if (!filter.isFilterActive()) {
        continue;
      }

      if (!filter.doesFilterPass) {
        // because users can do custom filters, give nice error message
        console.error('Filter is missing method doesFilterPass');
      }

      var params = {
        node: node,
        data: data
      };

      if (!filter.doesFilterPass(params)) {
        return false;
      }
    } // all filters passed


    return true;
  };

  FilterManager.prototype.parseQuickFilter = function (newFilter) {
    if (_.missing(newFilter) || newFilter === "") {
      return null;
    }

    if (!this.gridOptionsWrapper.isRowModelDefault()) {
      console.warn('ag-grid: quick filtering only works with the Client-side Row Model');
      return null;
    }

    return newFilter.toUpperCase();
  }; // returns true if it has changed (not just same value again)


  FilterManager.prototype.setQuickFilter = function (newFilter) {
    var parsedFilter = this.parseQuickFilter(newFilter);

    if (this.quickFilter !== parsedFilter) {
      this.quickFilter = parsedFilter;
      this.setQuickFilterParts();
      this.onFilterChanged();
    }
  };

  FilterManager.prototype.checkExternalFilter = function () {
    this.externalFilterPresent = this.gridOptionsWrapper.isExternalFilterPresent();
  };

  FilterManager.prototype.onFilterChanged = function (additionalEventAttributes) {
    this.setAdvancedFilterPresent();
    this.updateFilterFlagInColumns("filterChanged", additionalEventAttributes);
    this.checkExternalFilter();

    _.iterateObject(this.allFilters, function (key, filterWrapper) {
      filterWrapper.filterPromise.then(function (filter) {
        if (filter.onAnyFilterChanged) {
          filter.onAnyFilterChanged();
        }
      });
    });

    var filterChangedEvent = {
      type: Events.EVENT_FILTER_CHANGED,
      api: this.gridApi,
      columnApi: this.columnApi
    };

    if (additionalEventAttributes) {
      _.mergeDeep(filterChangedEvent, additionalEventAttributes);
    } // because internal events are not async in ag-grid, when the dispatchEvent
    // method comes back, we know all listeners have finished executing.


    this.processingFilterChange = true;
    this.eventService.dispatchEvent(filterChangedEvent);
    this.processingFilterChange = false;
  };

  FilterManager.prototype.isSuppressFlashingCellsBecauseFiltering = function () {
    if (this.allowShowChangeAfterFilter) {
      // if user has elected to always flash cell changes, then return false always
      return false;
    } else {
      // otherwise we suppress flashing changes when filtering
      return this.processingFilterChange;
    }
  };

  FilterManager.prototype.isQuickFilterPresent = function () {
    return this.quickFilter !== null;
  };

  FilterManager.prototype.doesRowPassOtherFilters = function (filterToSkip, node) {
    return this.doesRowPassFilter(node, filterToSkip);
  };

  FilterManager.prototype.doesRowPassQuickFilterNoCache = function (node, filterPart) {
    var _this = this;

    var columns = this.columnController.getAllColumnsForQuickFilter();
    var filterPasses = false;
    columns.forEach(function (column) {
      if (filterPasses) {
        return;
      }

      var part = _this.getQuickFilterTextForColumn(column, node);

      if (_.exists(part)) {
        if (part.indexOf(filterPart) >= 0) {
          filterPasses = true;
        }
      }
    });
    return filterPasses;
  };

  FilterManager.prototype.doesRowPassQuickFilterCache = function (node, filterPart) {
    if (!node.quickFilterAggregateText) {
      this.aggregateRowForQuickFilter(node);
    }

    var filterPasses = node.quickFilterAggregateText.indexOf(filterPart) >= 0;
    return filterPasses;
  };

  FilterManager.prototype.doesRowPassQuickFilter = function (node) {
    var _this = this;

    var filterPasses = true;
    var usingCache = this.gridOptionsWrapper.isCacheQuickFilter();
    this.quickFilterParts.forEach(function (filterPart) {
      var partPasses = usingCache ? _this.doesRowPassQuickFilterCache(node, filterPart) : _this.doesRowPassQuickFilterNoCache(node, filterPart); // each part must pass, if any fails, then the whole filter fails

      if (!partPasses) {
        filterPasses = false;
      }
    });
    return filterPasses;
  };

  FilterManager.prototype.doesRowPassFilter = function (node, filterToSkip) {
    // the row must pass ALL of the filters, so if any of them fail,
    // we return true. that means if a row passes the quick filter,
    // but fails the column filter, it fails overall
    // first up, check quick filter
    if (this.isQuickFilterPresent()) {
      if (!this.doesRowPassQuickFilter(node)) {
        return false;
      }
    } // secondly, give the client a chance to reject this row


    if (this.externalFilterPresent) {
      if (!this.gridOptionsWrapper.doesExternalFilterPass(node)) {
        return false;
      }
    } // lastly, check our internal advanced filter


    if (this.advancedFilterPresent) {
      if (!this.doesFilterPass(node, filterToSkip)) {
        return false;
      }
    } // got this far, all filters pass


    return true;
  };

  FilterManager.prototype.getQuickFilterTextForColumn = function (column, rowNode) {
    var value = this.valueService.getValue(column, rowNode, true);
    var valueAfterCallback;
    var colDef = column.getColDef();

    if (column.getColDef().getQuickFilterText) {
      var params = {
        value: value,
        node: rowNode,
        data: rowNode.data,
        column: column,
        colDef: colDef,
        context: this.gridOptionsWrapper.getContext()
      };
      valueAfterCallback = column.getColDef().getQuickFilterText(params);
    } else {
      valueAfterCallback = value;
    }

    if (_.exists(valueAfterCallback)) {
      return valueAfterCallback.toString().toUpperCase();
    } else {
      return null;
    }
  };

  FilterManager.prototype.aggregateRowForQuickFilter = function (node) {
    var _this = this;

    var stringParts = [];
    var columns = this.columnController.getAllColumnsForQuickFilter();
    columns.forEach(function (column) {
      var part = _this.getQuickFilterTextForColumn(column, node);

      if (_.exists(part)) {
        stringParts.push(part);
      }
    });
    node.quickFilterAggregateText = stringParts.join(FilterManager_1.QUICK_FILTER_SEPARATOR);
  };

  FilterManager.prototype.onNewRowsLoaded = function (source) {
    _.iterateObject(this.allFilters, function (key, filterWrapper) {
      filterWrapper.filterPromise.then(function (filter) {
        if (filter.onNewRowsLoaded) {
          filter.onNewRowsLoaded();
        }
      });
    });

    this.updateFilterFlagInColumns(source);
    this.setAdvancedFilterPresent();
  };

  FilterManager.prototype.createValueGetter = function (column) {
    var _this = this;

    return function (node) {
      return _this.valueService.getValue(column, node, true);
    };
  };

  FilterManager.prototype.getFilterComponent = function (column, source) {
    var filterWrapper = this.getOrCreateFilterWrapper(column, source);
    return filterWrapper.filterPromise;
  };

  FilterManager.prototype.isFilterActive = function (column) {
    var filterWrapper = this.cachedFilter(column);

    if (filterWrapper) {
      return filterWrapper.filterPromise.resolveNow(false, function (filter) {
        return filter.isFilterActive();
      });
    } else {
      return false;
    }
  };

  FilterManager.prototype.getOrCreateFilterWrapper = function (column, source) {
    var filterWrapper = this.cachedFilter(column);

    if (!filterWrapper) {
      filterWrapper = this.createFilterWrapper(column, source);
      this.allFilters[column.getColId()] = filterWrapper;
    } else {
      if (source !== 'NO_UI') {
        this.putIntoGui(filterWrapper, source);
      }
    }

    return filterWrapper;
  };

  FilterManager.prototype.cachedFilter = function (column) {
    return this.allFilters[column.getColId()];
  };

  FilterManager.prototype.createFilterInstance = function (column, $scope) {
    var _this = this;

    var defaultFilter = 'agTextColumnFilter';

    if (ModuleRegistry.isRegistered(exports.ModuleNames.SetFilterModule)) {
      defaultFilter = 'agSetColumnFilter';
    }

    var sanitisedColDef = _.cloneObject(column.getColDef());

    var filterInstance;
    var params = this.createFilterParams(column, sanitisedColDef, $scope);
    params.filterChangedCallback = this.onFilterChanged.bind(this);

    params.filterModifiedCallback = function () {
      var event = {
        type: Events.EVENT_FILTER_MODIFIED,
        api: _this.gridApi,
        columnApi: _this.columnApi,
        column: column,
        filterInstance: filterInstance
      };

      _this.eventService.dispatchEvent(event);
    }; // we modify params in a callback as we need the filter instance, and this isn't available
    // when creating the params above


    var modifyParamsCallback = function modifyParamsCallback(params, filter) {
      return _.assign(params, {
        doesRowPassOtherFilter: _this.doesRowPassOtherFilters.bind(_this, filter)
      });
    };

    var res = this.userComponentFactory.newFilterComponent(sanitisedColDef, params, defaultFilter, modifyParamsCallback);

    if (res) {
      res.then(function (r) {
        return filterInstance = r;
      });
    }

    return res;
  };

  FilterManager.prototype.createFilterParams = function (column, colDef, $scope) {
    if ($scope === void 0) {
      $scope = null;
    }

    var params = {
      api: this.gridOptionsWrapper.getApi(),
      column: column,
      colDef: colDef,
      rowModel: this.rowModel,
      filterChangedCallback: null,
      filterModifiedCallback: null,
      valueGetter: this.createValueGetter(column),
      context: this.gridOptionsWrapper.getContext(),
      doesRowPassOtherFilter: null
    }; // hack in scope if using AngularJS

    if ($scope) {
      params.$scope = $scope;
    }

    return params;
  };

  FilterManager.prototype.createFilterWrapper = function (column, source) {
    var filterWrapper = {
      column: column,
      filterPromise: null,
      scope: null,
      compiledElement: null,
      guiPromise: Promise.external()
    };
    filterWrapper.scope = this.gridOptionsWrapper.isAngularCompileFilters() ? this.$scope.$new() : null;
    filterWrapper.filterPromise = this.createFilterInstance(column, filterWrapper.scope);

    if (filterWrapper.filterPromise) {
      this.putIntoGui(filterWrapper, source);
    }

    return filterWrapper;
  };

  FilterManager.prototype.putIntoGui = function (filterWrapper, source) {
    var _this = this;

    var eFilterGui = document.createElement('div');
    eFilterGui.className = 'ag-filter';
    filterWrapper.filterPromise.then(function (filter) {
      var guiFromFilter = filter.getGui();

      if (_.missing(guiFromFilter)) {
        console.warn("getGui method from filter returned " + guiFromFilter + ", it should be a DOM element or an HTML template string.");
      } // for backwards compatibility with Angular 1 - we
      // used to allow providing back HTML from getGui().
      // once we move away from supporting Angular 1
      // directly, we can change this.


      if (typeof guiFromFilter === 'string') {
        guiFromFilter = _.loadTemplate(guiFromFilter);
      }

      eFilterGui.appendChild(guiFromFilter);

      if (filterWrapper.scope) {
        var compiledElement = _this.$compile(eFilterGui)(filterWrapper.scope);

        filterWrapper.compiledElement = compiledElement;
        window.setTimeout(function () {
          return filterWrapper.scope.$apply();
        }, 0);
      }

      filterWrapper.guiPromise.resolve(eFilterGui);

      _this.eventService.dispatchEvent({
        type: Events.EVENT_FILTER_OPENED,
        column: filterWrapper.column,
        source: source,
        eGui: eFilterGui,
        api: _this.gridApi,
        columnApi: _this.columnApi
      });
    });
  };

  FilterManager.prototype.onNewColumnsLoaded = function () {
    var _this = this;

    var atLeastOneFilterGone = false;

    _.iterateObject(this.allFilters, function (key, filterWrapper) {
      var oldColumn = !_this.columnController.getPrimaryColumn(filterWrapper.column);

      if (oldColumn) {
        atLeastOneFilterGone = true;

        _this.disposeFilterWrapper(filterWrapper, "filterDestroyed");
      }
    });

    if (atLeastOneFilterGone) {
      this.onFilterChanged();
    }
  }; // destroys the filter, so it not longer takes part


  FilterManager.prototype.destroyFilter = function (column, source) {
    if (source === void 0) {
      source = "api";
    }

    var filterWrapper = this.allFilters[column.getColId()];

    if (filterWrapper) {
      this.disposeFilterWrapper(filterWrapper, source);
      this.onFilterChanged();
    }
  };

  FilterManager.prototype.disposeFilterWrapper = function (filterWrapper, source) {
    var _this = this;

    filterWrapper.filterPromise.then(function (filter) {
      filter.setModel(null);

      if (filter.destroy) {
        filter.destroy();
      }

      filterWrapper.column.setFilterActive(false, source);

      if (filterWrapper.scope) {
        if (filterWrapper.compiledElement) {
          filterWrapper.compiledElement.remove();
        }

        filterWrapper.scope.$destroy();
      }

      delete _this.allFilters[filterWrapper.column.getColId()];
    });
  };

  FilterManager.prototype.destroy = function () {
    var _this = this;

    _.iterateObject(this.allFilters, function (key, filterWrapper) {
      _this.disposeFilterWrapper(filterWrapper, "filterDestroyed");
    });
  };

  var FilterManager_1;
  FilterManager.QUICK_FILTER_SEPARATOR = '\n';

  __decorate$Q([Autowired('$compile')], FilterManager.prototype, "$compile", void 0);

  __decorate$Q([Autowired('$scope')], FilterManager.prototype, "$scope", void 0);

  __decorate$Q([Autowired('gridOptionsWrapper')], FilterManager.prototype, "gridOptionsWrapper", void 0);

  __decorate$Q([Autowired('popupService')], FilterManager.prototype, "popupService", void 0);

  __decorate$Q([Autowired('valueService')], FilterManager.prototype, "valueService", void 0);

  __decorate$Q([Autowired('columnController')], FilterManager.prototype, "columnController", void 0);

  __decorate$Q([Autowired('rowModel')], FilterManager.prototype, "rowModel", void 0);

  __decorate$Q([Autowired('eventService')], FilterManager.prototype, "eventService", void 0);

  __decorate$Q([Autowired('context')], FilterManager.prototype, "context", void 0);

  __decorate$Q([Autowired('columnApi')], FilterManager.prototype, "columnApi", void 0);

  __decorate$Q([Autowired('gridApi')], FilterManager.prototype, "gridApi", void 0);

  __decorate$Q([Autowired('userComponentFactory')], FilterManager.prototype, "userComponentFactory", void 0);

  __decorate$Q([PostConstruct], FilterManager.prototype, "init", null);

  __decorate$Q([PreDestroy], FilterManager.prototype, "destroy", null);

  FilterManager = FilterManager_1 = __decorate$Q([Bean('filterManager')], FilterManager);
  return FilterManager;
}();
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __decorate$R = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var ValueService =
/** @class */
function () {
  function ValueService() {
    this.initialised = false;
  }

  ValueService.prototype.init = function () {
    this.cellExpressions = this.gridOptionsWrapper.isEnableCellExpressions();
    this.initialised = true;
  };

  ValueService.prototype.getValue = function (column, rowNode, forFilter, ignoreAggData) {
    if (forFilter === void 0) {
      forFilter = false;
    }

    if (ignoreAggData === void 0) {
      ignoreAggData = false;
    } // hack - the grid is getting refreshed before this bean gets initialised, race condition.
    // really should have a way so they get initialised in the right order???


    if (!this.initialised) {
      this.init();
    }

    if (!rowNode) {
      return;
    } // pull these out to make code below easier to read


    var colDef = column.getColDef();
    var field = colDef.field;
    var colId = column.getId();
    var data = rowNode.data;
    var result; // if there is a value getter, this gets precedence over a field

    var groupDataExists = rowNode.groupData && rowNode.groupData[colId] !== undefined;
    var aggDataExists = !ignoreAggData && rowNode.aggData && rowNode.aggData[colId] !== undefined;

    if (forFilter && colDef.filterValueGetter) {
      result = this.executeFilterValueGetter(colDef.filterValueGetter, data, column, rowNode);
    } else if (this.gridOptionsWrapper.isTreeData() && aggDataExists) {
      result = rowNode.aggData[colId];
    } else if (this.gridOptionsWrapper.isTreeData() && colDef.valueGetter) {
      result = this.executeValueGetter(colDef.valueGetter, data, column, rowNode);
    } else if (this.gridOptionsWrapper.isTreeData() && field && data) {
      result = _.getValueUsingField(data, field, column.isFieldContainsDots());
    } else if (groupDataExists) {
      result = rowNode.groupData[colId];
    } else if (aggDataExists) {
      result = rowNode.aggData[colId];
    } else if (colDef.valueGetter) {
      result = this.executeValueGetter(colDef.valueGetter, data, column, rowNode);
    } else if (field && data) {
      result = _.getValueUsingField(data, field, column.isFieldContainsDots());
    } // the result could be an expression itself, if we are allowing cell values to be expressions


    if (this.cellExpressions && typeof result === 'string' && result.indexOf('=') === 0) {
      var cellValueGetter = result.substring(1);
      result = this.executeValueGetter(cellValueGetter, data, column, rowNode);
    }

    return result;
  };

  ValueService.prototype.setValue = function (rowNode, colKey, newValue, eventSource) {
    var column = this.columnController.getPrimaryColumn(colKey);

    if (!rowNode || !column) {
      return;
    } // this will only happen if user is trying to paste into a group row, which doesn't make sense
    // the user should not be trying to paste into group rows


    var data = rowNode.data;

    if (_.missing(data)) {
      rowNode.data = {};
    } // for backwards compatibility we are also retrieving the newValueHandler as well as the valueSetter


    var _a = column.getColDef(),
        field = _a.field,
        newValueHandler = _a.newValueHandler,
        valueSetter = _a.valueSetter; // need either a field or a newValueHandler for this to work


    if (_.missing(field) && _.missing(newValueHandler) && _.missing(valueSetter)) {
      // we don't tell user about newValueHandler, as that is deprecated
      console.warn("ag-Grid: you need either field or valueSetter set on colDef for editing to work");
      return;
    }

    var params = {
      node: rowNode,
      data: rowNode.data,
      oldValue: this.getValue(column, rowNode),
      newValue: newValue,
      colDef: column.getColDef(),
      column: column,
      api: this.gridOptionsWrapper.getApi(),
      columnApi: this.gridOptionsWrapper.getColumnApi(),
      context: this.gridOptionsWrapper.getContext()
    };
    params.newValue = newValue;
    var valueWasDifferent;

    if (newValueHandler && _.exists(newValueHandler)) {
      valueWasDifferent = newValueHandler(params);
    } else if (_.exists(valueSetter)) {
      valueWasDifferent = this.expressionService.evaluate(valueSetter, params);
    } else {
      valueWasDifferent = this.setValueUsingField(data, field, newValue, column.isFieldContainsDots());
    } // in case user forgot to return something (possible if they are not using TypeScript
    // and just forgot, or using an old newValueHandler we didn't always expect a return
    // value here), we default the return value to true, so we always refresh.


    if (valueWasDifferent === undefined) {
      valueWasDifferent = true;
    } // if no change to the value, then no need to do the updating, or notifying via events.
    // otherwise the user could be tabbing around the grid, and cellValueChange would get called
    // all the time.


    if (!valueWasDifferent) {
      return;
    } // reset quick filter on this row


    rowNode.resetQuickFilterAggregateText();
    this.valueCache.onDataChanged();
    params.newValue = this.getValue(column, rowNode);
    var onCellValueChanged = column.getColDef().onCellValueChanged;

    if (typeof onCellValueChanged === 'function') {
      // to make callback async, do in a timeout
      setTimeout(function () {
        return onCellValueChanged(params);
      }, 0);
    }

    var event = {
      type: Events.EVENT_CELL_VALUE_CHANGED,
      event: null,
      rowIndex: rowNode.rowIndex,
      rowPinned: rowNode.rowPinned,
      column: params.column,
      api: params.api,
      colDef: params.colDef,
      columnApi: params.columnApi,
      context: params.context,
      data: rowNode.data,
      node: rowNode,
      oldValue: params.oldValue,
      newValue: params.newValue,
      value: params.newValue,
      source: eventSource
    };
    this.eventService.dispatchEvent(event);
  };

  ValueService.prototype.setValueUsingField = function (data, field, newValue, isFieldContainsDots) {
    if (!field) {
      return false;
    } // if no '.', then it's not a deep value


    var valuesAreSame = false;

    if (!isFieldContainsDots) {
      data[field] = newValue;
    } else {
      // otherwise it is a deep value, so need to dig for it
      var fieldPieces = field.split('.');
      var currentObject = data;

      while (fieldPieces.length > 0 && currentObject) {
        var fieldPiece = fieldPieces.shift();

        if (fieldPieces.length === 0) {
          currentObject[fieldPiece] = newValue;
        } else {
          currentObject = currentObject[fieldPiece];
        }
      }
    }

    return !valuesAreSame;
  };

  ValueService.prototype.executeFilterValueGetter = function (valueGetter, data, column, rowNode) {
    var params = {
      data: data,
      node: rowNode,
      column: column,
      colDef: column.getColDef(),
      api: this.gridOptionsWrapper.getApi(),
      columnApi: this.gridOptionsWrapper.getColumnApi(),
      context: this.gridOptionsWrapper.getContext(),
      getValue: this.getValueCallback.bind(this, rowNode)
    };
    return this.expressionService.evaluate(valueGetter, params);
  };

  ValueService.prototype.executeValueGetter = function (valueGetter, data, column, rowNode) {
    var colId = column.getId(); // if inside the same turn, just return back the value we got last time

    var valueFromCache = this.valueCache.getValue(rowNode, colId);

    if (valueFromCache !== undefined) {
      return valueFromCache;
    }

    var params = {
      data: data,
      node: rowNode,
      column: column,
      colDef: column.getColDef(),
      api: this.gridOptionsWrapper.getApi(),
      columnApi: this.gridOptionsWrapper.getColumnApi(),
      context: this.gridOptionsWrapper.getContext(),
      getValue: this.getValueCallback.bind(this, rowNode)
    };
    var result = this.expressionService.evaluate(valueGetter, params); // if a turn is active, store the value in case the grid asks for it again

    this.valueCache.setValue(rowNode, colId, result);
    return result;
  };

  ValueService.prototype.getValueCallback = function (node, field) {
    var otherColumn = this.columnController.getPrimaryColumn(field);

    if (otherColumn) {
      return this.getValue(otherColumn, node);
    }

    return null;
  }; // used by row grouping and pivot, to get key for a row. col can be a pivot col or a row grouping col


  ValueService.prototype.getKeyForNode = function (col, rowNode) {
    var value = this.getValue(col, rowNode);
    var keyCreator = col.getColDef().keyCreator;
    var result = keyCreator ? keyCreator({
      value: value
    }) : value; // if already a string, or missing, just return it

    if (typeof result === 'string' || result == null) {
      return result;
    }

    result = String(result);

    if (result === '[object Object]') {
      _.doOnce(function () {
        console.warn('ag-Grid: a column you are grouping or pivoting by has objects as values. If you want to group by complex objects then either a) use a colDef.keyCreator (se ag-Grid docs) or b) to toString() on the object to return a key');
      }, 'getKeyForNode - warn about [object,object]');
    }

    return result;
  };

  __decorate$R([Autowired('gridOptionsWrapper')], ValueService.prototype, "gridOptionsWrapper", void 0);

  __decorate$R([Autowired('expressionService')], ValueService.prototype, "expressionService", void 0);

  __decorate$R([Autowired('columnController')], ValueService.prototype, "columnController", void 0);

  __decorate$R([Autowired('eventService')], ValueService.prototype, "eventService", void 0);

  __decorate$R([Autowired('valueCache')], ValueService.prototype, "valueCache", void 0);

  __decorate$R([PostConstruct], ValueService.prototype, "init", null);

  ValueService = __decorate$R([Bean('valueService')], ValueService);
  return ValueService;
}();
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __decorate$S = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};
/**
 * There are many instances of this component covering each of the areas a row can be entered
 * eg body, pinned left, fullWidth. The component differs from others in that it's given the
 * elements, there is no template. All of the elements are part of the GridPanel.
 */


var RowContainerComponent =
/** @class */
function () {
  function RowContainerComponent(params) {
    this.childCount = 0;
    this.rowTemplatesToAdd = [];
    this.afterGuiAttachedCallbacks = []; // this is to cater for a 'strange behaviour' where when a panel is made visible, it is firing a scroll
    // event which we want to ignore. see gridPanel.onAnyBodyScroll()

    this.lastMadeVisibleTime = 0;
    this.eContainer = params.eContainer;
    this.eViewport = params.eViewport;

    if (params.eWrapper) {
      this.eWrapper = params.eWrapper;
    }

    this.hideWhenNoChildren = params.hideWhenNoChildren;
  }

  RowContainerComponent.prototype.setVerticalScrollPosition = function (verticalScrollPosition) {
    this.scrollTop = verticalScrollPosition;
  };

  RowContainerComponent.prototype.postConstruct = function () {
    this.checkDomOrder();
    this.checkVisibility();
    this.gridOptionsWrapper.addEventListener(GridOptionsWrapper.PROP_DOM_LAYOUT, this.checkDomOrder.bind(this));
  };

  RowContainerComponent.prototype.checkDomOrder = function () {
    this.domOrder = this.gridOptionsWrapper.isEnsureDomOrder();
  };

  RowContainerComponent.prototype.getRowElement = function (compId) {
    return this.eContainer.querySelector("[comp-id=\"" + compId + "\"]");
  };

  RowContainerComponent.prototype.setHeight = function (height) {
    if (height == null) {
      this.eContainer.style.height = '';
      return;
    }

    this.eContainer.style.height = height + "px";

    if (this.eWrapper) {
      this.eWrapper.style.height = height + "px";
    }
  };

  RowContainerComponent.prototype.flushRowTemplates = function () {
    // if doing dom order, then rowTemplates will be empty,
    // or if no rows added since last time also empty.
    if (this.rowTemplatesToAdd.length !== 0) {
      var htmlToAdd = this.rowTemplatesToAdd.join('');

      _.appendHtml(this.eContainer, htmlToAdd);

      this.rowTemplatesToAdd.length = 0;
    } // this only empty if no rows since last time, as when
    // doing dom order, we still have callbacks to process


    this.afterGuiAttachedCallbacks.forEach(function (func) {
      return func();
    });
    this.afterGuiAttachedCallbacks.length = 0;
    this.lastPlacedElement = null;
  };

  RowContainerComponent.prototype.appendRowTemplate = function (rowTemplate, callback) {
    if (this.domOrder) {
      this.lastPlacedElement = _.insertTemplateWithDomOrder(this.eContainer, rowTemplate, this.lastPlacedElement);
    } else {
      this.rowTemplatesToAdd.push(rowTemplate);
    }

    this.afterGuiAttachedCallbacks.push(callback); // it is important we put items in in order, so that when we open a row group,
    // the new rows are inserted after the opened group, but before the rows below.
    // that way, the rows below are over the new rows (as dom renders last in dom over
    // items previous in dom), otherwise the child rows would cover the row below and
    // that meant the user doesn't see the rows below slide away.

    this.childCount++;
    this.checkVisibility();
  };

  RowContainerComponent.prototype.ensureDomOrder = function (eRow) {
    if (this.domOrder) {
      _.ensureDomOrder(this.eContainer, eRow, this.lastPlacedElement);

      this.lastPlacedElement = eRow;
    }
  };

  RowContainerComponent.prototype.removeRowElement = function (eRow) {
    this.eContainer.removeChild(eRow);
    this.childCount--;
    this.checkVisibility();
  };

  RowContainerComponent.prototype.checkVisibility = function () {
    if (!this.hideWhenNoChildren) {
      return;
    }

    var eGui = this.eViewport ? this.eViewport : this.eContainer;
    var visible = this.childCount > 0;

    if (this.visible !== visible) {
      this.visible = visible;
      this.lastMadeVisibleTime = new Date().getTime();

      _.setDisplayed(eGui, visible); // if we are showing the viewport, then the scroll is always zero,
      // so we need to align with the other sections (ie if this is full
      // width container, and first time showing a full width row, we need to
      // scroll it so full width rows are show in right place alongside the
      // body rows). without this, there was an issue with 'loading rows' for
      // server side row model, as loading rows are full width, and they were
      // not getting displayed in the right location when rows were expanded.


      if (visible && this.eViewport) {
        this.eViewport.scrollTop = this.scrollTop;
      }
    }
  };

  RowContainerComponent.prototype.isMadeVisibleRecently = function () {
    var now = new Date().getTime();
    var millisSinceVisible = now - this.lastMadeVisibleTime;
    return millisSinceVisible < 500;
  };

  __decorate$S([Autowired('gridOptionsWrapper')], RowContainerComponent.prototype, "gridOptionsWrapper", void 0);

  __decorate$S([PostConstruct], RowContainerComponent.prototype, "postConstruct", null);

  return RowContainerComponent;
}();
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __decorate$T = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var RowDragFeature =
/** @class */
function () {
  function RowDragFeature(eContainer, gridPanel) {
    this.eContainer = eContainer;
    this.gridPanel = gridPanel;
  }

  RowDragFeature.prototype.postConstruct = function () {
    if (this.gridOptionsWrapper.isRowModelDefault()) {
      this.clientSideRowModel = this.rowModel;
    }
  };

  RowDragFeature.prototype.getContainer = function () {
    return this.eContainer;
  };

  RowDragFeature.prototype.isInterestedIn = function (type) {
    return type === exports.DragSourceType.RowDrag;
  };

  RowDragFeature.prototype.getIconName = function () {
    return DragAndDropService.ICON_MOVE;
  };

  RowDragFeature.prototype.onDragEnter = function (draggingEvent) {
    // when entering, we fire the enter event, then in onEnterOrDragging,
    // we also fire the move event. so we get both events when entering.
    this.dispatchEvent(Events.EVENT_ROW_DRAG_ENTER, draggingEvent);
    this.dragAndDropService.setGhostIcon(DragAndDropService.ICON_MOVE);
    draggingEvent.dragItem.rowNode.setDragging(true);
    this.onEnterOrDragging(draggingEvent);
  };

  RowDragFeature.prototype.onDragging = function (draggingEvent) {
    this.onEnterOrDragging(draggingEvent);
  };

  RowDragFeature.prototype.onEnterOrDragging = function (draggingEvent) {
    // this event is fired for enter and move
    this.dispatchEvent(Events.EVENT_ROW_DRAG_MOVE, draggingEvent);
    this.lastDraggingEvent = draggingEvent;
    var pixel = this.normaliseForScroll(draggingEvent.y);
    var managedDrag = this.gridOptionsWrapper.isRowDragManaged();

    if (managedDrag) {
      this.doManagedDrag(draggingEvent, pixel);
    }

    this.checkCenterForScrolling(pixel);
  };

  RowDragFeature.prototype.doManagedDrag = function (draggingEvent, pixel) {
    var rowNode = draggingEvent.dragItem.rowNode;
    var rowWasMoved = this.clientSideRowModel.ensureRowAtPixel(rowNode, pixel);

    if (rowWasMoved) {
      this.focusedCellController.clearFocusedCell();

      if (this.rangeController) {
        this.rangeController.removeAllCellRanges();
      }
    }
  };

  RowDragFeature.prototype.normaliseForScroll = function (pixel) {
    var gridPanelHasScrolls = this.gridOptionsWrapper.getDomLayout() === Constants.DOM_LAYOUT_NORMAL;

    if (gridPanelHasScrolls) {
      var pixelRange = this.gridPanel.getVScrollPosition();
      return pixel + pixelRange.top;
    } else {
      return pixel;
    }
  };

  RowDragFeature.prototype.checkCenterForScrolling = function (pixel) {
    // scroll if the mouse is within 50px of the grid edge
    var pixelRange = this.gridPanel.getVScrollPosition(); // console.log(`pixelRange = (${pixelRange.top}, ${pixelRange.bottom})`);

    this.needToMoveUp = pixel < pixelRange.top + 50;
    this.needToMoveDown = pixel > pixelRange.bottom - 50; // console.log(`needToMoveUp = ${this.needToMoveUp} = pixel < (pixelRange.top + 50) = ${pixel} < (${pixelRange.top} + 50)`);
    // console.log(`needToMoveDown = ${this.needToMoveDown} = pixel < (pixelRange.top + 50) = ${pixel} < (${pixelRange.top} + 50)`);

    if (this.needToMoveUp || this.needToMoveDown) {
      this.ensureIntervalStarted();
    } else {
      this.ensureIntervalCleared();
    }
  };

  RowDragFeature.prototype.ensureIntervalStarted = function () {
    if (!this.movingIntervalId) {
      this.intervalCount = 0;
      this.movingIntervalId = window.setInterval(this.moveInterval.bind(this), 100);
    }
  };

  RowDragFeature.prototype.ensureIntervalCleared = function () {
    if (this.moveInterval) {
      window.clearInterval(this.movingIntervalId);
      this.movingIntervalId = null;
    }
  };

  RowDragFeature.prototype.moveInterval = function () {
    // the amounts we move get bigger at each interval, so the speed accelerates, starting a bit slow
    // and getting faster. this is to give smoother user experience. we max at 100px to limit the speed.
    var pixelsToMove;
    this.intervalCount++;
    pixelsToMove = 10 + this.intervalCount * 5;

    if (pixelsToMove > 100) {
      pixelsToMove = 100;
    }

    var pixelsMoved;

    if (this.needToMoveDown) {
      pixelsMoved = this.gridPanel.scrollVertically(pixelsToMove);
    } else if (this.needToMoveUp) {
      pixelsMoved = this.gridPanel.scrollVertically(-pixelsToMove);
    }

    if (pixelsMoved !== 0) {
      this.onDragging(this.lastDraggingEvent);
    }
  }; // i tried using generics here with this:
  //     public createEvent<T extends RowDragEvent>(type: string, clazz: {new(): T; }, draggingEvent: DraggingEvent) {
  // but it didn't work - i think it's because it only works with classes, and not interfaces, (the events are interfaces)


  RowDragFeature.prototype.dispatchEvent = function (type, draggingEvent) {
    var yNormalised = this.normaliseForScroll(draggingEvent.y);
    var overIndex = -1;
    var overNode = null;
    var mouseIsPastLastRow = yNormalised > this.rowModel.getCurrentPageHeight();

    if (!mouseIsPastLastRow) {
      overIndex = this.rowModel.getRowIndexAtPixel(yNormalised);
      overNode = this.rowModel.getRow(overIndex);
    }

    var vDirectionString;

    switch (draggingEvent.vDirection) {
      case exports.VerticalDirection.Down:
        vDirectionString = 'down';
        break;

      case exports.VerticalDirection.Up:
        vDirectionString = 'up';
        break;

      default:
        vDirectionString = null;
        break;
    }

    var event = {
      type: type,
      api: this.gridOptionsWrapper.getApi(),
      columnApi: this.gridOptionsWrapper.getColumnApi(),
      event: draggingEvent.event,
      node: draggingEvent.dragItem.rowNode,
      overIndex: overIndex,
      overNode: overNode,
      y: yNormalised,
      vDirection: vDirectionString
    };
    this.eventService.dispatchEvent(event);
  };

  RowDragFeature.prototype.onDragLeave = function (draggingEvent) {
    this.dispatchEvent(Events.EVENT_ROW_DRAG_LEAVE, draggingEvent);
    this.stopDragging(draggingEvent);
  };

  RowDragFeature.prototype.onDragStop = function (draggingEvent) {
    this.dispatchEvent(Events.EVENT_ROW_DRAG_END, draggingEvent);
    this.stopDragging(draggingEvent);
  };

  RowDragFeature.prototype.stopDragging = function (draggingEvent) {
    this.ensureIntervalCleared();
    draggingEvent.dragItem.rowNode.setDragging(false);
  };

  __decorate$T([Autowired('dragAndDropService')], RowDragFeature.prototype, "dragAndDropService", void 0);

  __decorate$T([Autowired('rowModel')], RowDragFeature.prototype, "rowModel", void 0);

  __decorate$T([Autowired('focusedCellController')], RowDragFeature.prototype, "focusedCellController", void 0);

  __decorate$T([Autowired('gridOptionsWrapper')], RowDragFeature.prototype, "gridOptionsWrapper", void 0);

  __decorate$T([Optional('rangeController')], RowDragFeature.prototype, "rangeController", void 0);

  __decorate$T([Autowired('eventService')], RowDragFeature.prototype, "eventService", void 0);

  __decorate$T([PostConstruct], RowDragFeature.prototype, "postConstruct", null);

  return RowDragFeature;
}();
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __extends$M = undefined && undefined.__extends || function () {
  var _extendStatics49 = function extendStatics(d, b) {
    _extendStatics49 = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics49(d, b);
  };

  return function (d, b) {
    _extendStatics49(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var __decorate$U = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
}; // in the html below, it is important that there are no white space between some of the divs, as if there is white space,
// it won't render correctly in safari, as safari renders white space as a gap


var GRID_PANEL_NORMAL_TEMPLATE = "<div class=\"ag-root ag-unselectable\" role=\"grid\" unselectable=\"on\">\n        <ag-header-root ref=\"headerRoot\" unselectable=\"on\"></ag-header-root>\n        <div class=\"ag-floating-top\" ref=\"eTop\" role=\"presentation\" unselectable=\"on\">\n            <div class=\"ag-pinned-left-floating-top\" ref=\"eLeftTop\" role=\"presentation\" unselectable=\"on\"></div>\n            <div class=\"ag-floating-top-viewport\" ref=\"eTopViewport\" role=\"presentation\" unselectable=\"on\">\n                <div class=\"ag-floating-top-container\" ref=\"eTopContainer\" role=\"presentation\" unselectable=\"on\"></div>\n            </div>\n            <div class=\"ag-pinned-right-floating-top\" ref=\"eRightTop\" role=\"presentation\" unselectable=\"on\"></div>\n            <div class=\"ag-floating-top-full-width-container\" ref=\"eTopFullWidthContainer\" role=\"presentation\" unselectable=\"on\"></div>\n        </div>\n        <div class=\"ag-body-viewport\" ref=\"eBodyViewport\" role=\"presentation\" unselectable=\"on\">\n            <div class=\"ag-pinned-left-cols-container\" ref=\"eLeftContainer\" role=\"presentation\" unselectable=\"on\"></div>\n            <div class=\"ag-center-cols-clipper\" ref=\"eCenterColsClipper\" role=\"presentation\" unselectable=\"on\">\n                <div class=\"ag-center-cols-viewport\" ref=\"eCenterViewport\" role=\"presentation\" unselectable=\"on\">\n                    <div class=\"ag-center-cols-container\" ref=\"eCenterContainer\" role=\"rowgroup\" unselectable=\"on\"></div>\n                </div>\n            </div>\n            <div class=\"ag-pinned-right-cols-container\" ref=\"eRightContainer\" role=\"presentation\" unselectable=\"on\"></div>\n            <div class=\"ag-full-width-container\" ref=\"eFullWidthContainer\" role=\"presentation\" unselectable=\"on\"></div>\n        </div>\n        <div class=\"ag-floating-bottom\" ref=\"eBottom\" role=\"presentation\" unselectable=\"on\">\n            <div class=\"ag-pinned-left-floating-bottom\" ref=\"eLeftBottom\" role=\"presentation\" unselectable=\"on\"></div>\n            <div class=\"ag-floating-bottom-viewport\" ref=\"eBottomViewport\" role=\"presentation\" unselectable=\"on\">\n                <div class=\"ag-floating-bottom-container\" ref=\"eBottomContainer\" role=\"presentation\" unselectable=\"on\"></div>\n            </div>\n            <div class=\"ag-pinned-right-floating-bottom\" ref=\"eRightBottom\" role=\"presentation\" unselectable=\"on\"></div>\n            <div class=\"ag-floating-bottom-full-width-container\" ref=\"eBottomFullWidthContainer\" role=\"presentation\" unselectable=\"on\"></div>\n        </div>\n        <div class=\"ag-body-horizontal-scroll\" ref=\"eHorizontalScrollBody\" aria-hidden=\"true\">\n            <div class=\"ag-horizontal-left-spacer\" ref=\"eHorizontalLeftSpacer\"></div>\n            <div class=\"ag-body-horizontal-scroll-viewport\" ref=\"eBodyHorizontalScrollViewport\">\n                <div class=\"ag-body-horizontal-scroll-container\" ref=\"eBodyHorizontalScrollContainer\"></div>\n            </div>\n            <div class=\"ag-horizontal-right-spacer\" ref=\"eHorizontalRightSpacer\"></div>\n        </div>\n        <ag-overlay-wrapper ref=\"overlayWrapper\"></ag-overlay-wrapper>\n    </div>";

var GridPanel =
/** @class */
function (_super) {
  __extends$M(GridPanel, _super);

  function GridPanel() {
    var _this = _super.call(this, GRID_PANEL_NORMAL_TEMPLATE) || this;

    _this.scrollLeft = -1;
    _this.scrollTop = -1;
    _this.resetLastHorizontalScrollElementDebounce = _.debounce(_this.resetLastHorizontalScrollElement.bind(_this), 500);
    return _this;
  }

  GridPanel.prototype.getVScrollPosition = function () {
    var result = {
      top: this.eBodyViewport.scrollTop,
      bottom: this.eBodyViewport.scrollTop + this.eBodyViewport.offsetHeight
    };
    return result;
  };

  GridPanel.prototype.getHScrollPosition = function () {
    var result = {
      left: this.eCenterViewport.scrollLeft,
      right: this.eCenterViewport.scrollLeft + this.eCenterViewport.offsetWidth
    };
    return result;
  };

  GridPanel.prototype.onRowDataChanged = function () {
    this.showOrHideOverlay();
  };

  GridPanel.prototype.showOrHideOverlay = function () {
    var isEmpty = this.paginationProxy.isEmpty();
    var isSuppressNoRowsOverlay = this.gridOptionsWrapper.isSuppressNoRowsOverlay();
    var method = isEmpty && !isSuppressNoRowsOverlay ? 'showNoRowsOverlay' : 'hideOverlay';
    this[method]();
  };

  GridPanel.prototype.onNewColumnsLoaded = function () {
    // hide overlay if columns and rows exist, this can happen if columns are loaded after data.
    // this problem exists before of the race condition between the services (column controller in this case)
    // and the view (grid panel). if the model beans were all initialised first, and then the view beans second,
    // this race condition would not happen.
    if (this.columnController.isReady() && !this.paginationProxy.isEmpty()) {
      this.hideOverlay();
    }
  };

  GridPanel.prototype.init = function () {
    var _this = this;

    this.scrollWidth = this.gridOptionsWrapper.getScrollbarWidth();
    this.enableRtl = this.gridOptionsWrapper.isEnableRtl();
    this.printLayout = this.gridOptionsWrapper.getDomLayout() === Constants.DOM_LAYOUT_PRINT; // these elements have different CSS when layout changes

    this.gridOptionsWrapper.addLayoutElement(this.getGui());
    this.gridOptionsWrapper.addLayoutElement(this.eBodyViewport);
    this.suppressScrollOnFloatingRow();
    this.setupRowAnimationCssClass();
    this.buildRowContainerComponents();
    this.addEventListeners();
    this.addDragListeners();
    this.addScrollListener();

    if (this.gridOptionsWrapper.isRowModelDefault() && !this.gridOptionsWrapper.getRowData()) {
      this.showLoadingOverlay();
    }

    this.setCellTextSelection(this.gridOptionsWrapper.isEnableCellTextSelect());
    this.setPinnedContainerSize();
    this.setHeaderAndFloatingHeights();
    this.disableBrowserDragging();
    this.addMouseListeners();
    this.addKeyboardEvents();
    this.addBodyViewportListener();
    this.addStopEditingWhenGridLosesFocus();
    this.mockContextMenuForIPad();
    this.addRowDragListener();

    if (this.$scope) {
      this.addAngularApplyCheck();
    }

    this.onDisplayedColumnsWidthChanged();
    this.gridApi.registerGridComp(this);
    this.alignedGridsService.registerGridComp(this);
    this.headerRootComp.registerGridComp(this);
    this.navigationService.registerGridComp(this);
    this.heightScaler.registerGridComp(this);
    this.autoHeightCalculator.registerGridComp(this);
    this.columnAnimationService.registerGridComp(this);
    this.autoWidthCalculator.registerGridComp(this);
    this.paginationAutoPageSizeService.registerGridComp(this);
    this.beans.registerGridComp(this);
    this.rowRenderer.registerGridComp(this);

    if (this.rangeController) {
      this.rangeController.registerGridComp(this);
    }

    [this.eCenterViewport, this.eBodyViewport].forEach(function (viewport) {
      var unsubscribeFromResize = _this.resizeObserverService.observeResize(viewport, _this.onCenterViewportResized.bind(_this));

      _this.addDestroyFunc(function () {
        return unsubscribeFromResize();
      });
    });
  };

  GridPanel.prototype.onDomLayoutChanged = function () {
    var newPrintLayout = this.gridOptionsWrapper.getDomLayout() === Constants.DOM_LAYOUT_PRINT;

    if (this.printLayout !== newPrintLayout) {
      this.printLayout = newPrintLayout;
      this.setWidthsOfContainers(); // pinned containers are always hidden for print layout

      this.setPinnedContainerSize();
    }
  };

  GridPanel.prototype.onCenterViewportResized = function () {
    if (_.isVisible(this.eCenterViewport)) {
      this.checkViewportAndScrolls();
      this.columnController.refreshFlexedColumns(this.getCenterWidth());
    } else {
      this.bodyHeight = 0;
    }
  }; // used by ColumnAnimationService


  GridPanel.prototype.setColumnMovingCss = function (moving) {
    this.addOrRemoveCssClass('ag-column-moving', moving);
  };

  GridPanel.prototype.setCellTextSelection = function (selectable) {
    if (selectable === void 0) {
      selectable = false;
    }

    [this.eTop, this.eBodyViewport, this.eBottom].forEach(function (ct) {
      return _.addOrRemoveCssClass(ct, 'ag-selectable', selectable);
    });
  };

  GridPanel.prototype.addRowDragListener = function () {
    var rowDragFeature = new RowDragFeature(this.eBodyViewport, this);
    this.getContext().wireBean(rowDragFeature);
    this.dragAndDropService.addDropTarget(rowDragFeature);
  };

  GridPanel.prototype.addStopEditingWhenGridLosesFocus = function () {
    var _this = this;

    if (!this.gridOptionsWrapper.isStopEditingWhenGridLosesFocus()) {
      return;
    }

    var focusOutListener = function focusOutListener(event) {
      // this is the element the focus is moving to
      var elementWithFocus = event.relatedTarget; // see if the element the focus is going to is part of the grid

      var clickInsideGrid = false;
      var pointer = elementWithFocus;

      while (_.exists(pointer) && !clickInsideGrid) {
        var isPopup = !!_this.gridOptionsWrapper.getDomData(pointer, PopupEditorWrapper.DOM_KEY_POPUP_EDITOR_WRAPPER);
        var isBody = _this.eBodyViewport === pointer || _this.eBottom === pointer || _this.eTop === pointer;
        clickInsideGrid = isPopup || isBody;
        pointer = pointer.parentNode;
      }

      if (!clickInsideGrid) {
        _this.rowRenderer.stopEditing();
      }
    };

    this.addDestroyableEventListener(this.eBodyViewport, 'focusout', focusOutListener);
    this.addDestroyableEventListener(this.eTop, 'focusout', focusOutListener);
    this.addDestroyableEventListener(this.eBottom, 'focusout', focusOutListener);
  };

  GridPanel.prototype.addAngularApplyCheck = function () {
    var _this = this; // this makes sure if we queue up requests, we only execute oe


    var applyTriggered = false;

    var listener = function listener() {
      // only need to do one apply at a time
      if (applyTriggered) {
        return;
      }

      applyTriggered = true; // mark 'need apply' to true

      window.setTimeout(function () {
        applyTriggered = false;

        _this.$scope.$apply();
      }, 0);
    }; // these are the events we need to do an apply after - these are the ones that can end up
    // with columns added or removed


    this.addDestroyableEventListener(this.eventService, Events.EVENT_DISPLAYED_COLUMNS_CHANGED, listener);
    this.addDestroyableEventListener(this.eventService, Events.EVENT_VIRTUAL_COLUMNS_CHANGED, listener);
  }; // if we do not do this, then the user can select a pic in the grid (eg an image in a custom cell renderer)
  // and then that will start the browser native drag n' drop, which messes up with our own drag and drop.


  GridPanel.prototype.disableBrowserDragging = function () {
    this.addGuiEventListener('dragstart', function (event) {
      if (event.target instanceof HTMLImageElement) {
        event.preventDefault();
        return false;
      }
    });
  };

  GridPanel.prototype.addEventListeners = function () {
    this.addDestroyableEventListener(this.eventService, Events.EVENT_DISPLAYED_COLUMNS_CHANGED, this.onDisplayedColumnsChanged.bind(this));
    this.addDestroyableEventListener(this.eventService, Events.EVENT_DISPLAYED_COLUMNS_WIDTH_CHANGED, this.onDisplayedColumnsWidthChanged.bind(this));
    this.addDestroyableEventListener(this.eventService, Events.EVENT_PINNED_ROW_DATA_CHANGED, this.setHeaderAndFloatingHeights.bind(this));
    this.addDestroyableEventListener(this.eventService, Events.EVENT_ROW_DATA_CHANGED, this.onRowDataChanged.bind(this));
    this.addDestroyableEventListener(this.eventService, Events.EVENT_ROW_DATA_UPDATED, this.onRowDataChanged.bind(this));
    this.addDestroyableEventListener(this.eventService, Events.EVENT_NEW_COLUMNS_LOADED, this.onNewColumnsLoaded.bind(this));
    this.addDestroyableEventListener(this.gridOptionsWrapper, GridOptionsWrapper.PROP_HEADER_HEIGHT, this.setHeaderAndFloatingHeights.bind(this));
    this.addDestroyableEventListener(this.gridOptionsWrapper, GridOptionsWrapper.PROP_PIVOT_HEADER_HEIGHT, this.setHeaderAndFloatingHeights.bind(this));
    this.addDestroyableEventListener(this.gridOptionsWrapper, GridOptionsWrapper.PROP_GROUP_HEADER_HEIGHT, this.setHeaderAndFloatingHeights.bind(this));
    this.addDestroyableEventListener(this.gridOptionsWrapper, GridOptionsWrapper.PROP_PIVOT_GROUP_HEADER_HEIGHT, this.setHeaderAndFloatingHeights.bind(this));
    this.addDestroyableEventListener(this.gridOptionsWrapper, GridOptionsWrapper.PROP_FLOATING_FILTERS_HEIGHT, this.setHeaderAndFloatingHeights.bind(this));
    this.addDestroyableEventListener(this.gridOptionsWrapper, GridOptionsWrapper.PROP_DOM_LAYOUT, this.onDomLayoutChanged.bind(this));
  };

  GridPanel.prototype.addDragListeners = function () {
    var _this = this;

    if (!this.gridOptionsWrapper.isEnableRangeSelection() || // no range selection if no property
    _.missing(this.rangeController) // no range selection if not enterprise version
    ) {
        return;
      }

    var containers = [this.eLeftContainer, this.eRightContainer, this.eCenterContainer, this.eTop, this.eBottom];
    containers.forEach(function (container) {
      var params = {
        eElement: container,
        onDragStart: _this.rangeController.onDragStart.bind(_this.rangeController),
        onDragStop: _this.rangeController.onDragStop.bind(_this.rangeController),
        onDragging: _this.rangeController.onDragging.bind(_this.rangeController)
      };

      _this.dragService.addDragSource(params);

      _this.addDestroyFunc(function () {
        return _this.dragService.removeDragSource(params);
      });
    });
  };

  GridPanel.prototype.addMouseListeners = function () {
    var _this = this;

    var eventNames = ['click', 'mousedown', 'dblclick', 'contextmenu', 'mouseover', 'mouseout'];
    eventNames.forEach(function (eventName) {
      var listener = _this.processMouseEvent.bind(_this, eventName);

      _this.eAllCellContainers.forEach(function (container) {
        return _this.addDestroyableEventListener(container, eventName, listener);
      });
    });
  };

  GridPanel.prototype.addKeyboardEvents = function () {
    var _this = this;

    var eventNames = ['keydown', 'keypress'];
    eventNames.forEach(function (eventName) {
      var listener = _this.processKeyboardEvent.bind(_this, eventName);

      _this.eAllCellContainers.forEach(function (container) {
        _this.addDestroyableEventListener(container, eventName, listener);
      });
    });
  };

  GridPanel.prototype.addBodyViewportListener = function () {
    var _this = this; // we want to listen for clicks directly on the eBodyViewport, so the user has a way of showing
    // the context menu if no rows or columns are displayed, or user simply clicks outside of a cell


    var listener = function listener(mouseEvent) {
      var target = _.getTarget(mouseEvent);

      if (target === _this.eBodyViewport || target === _this.eCenterViewport) {
        // show it
        _this.onContextMenu(mouseEvent, null, null, null, null);

        _this.preventDefaultOnContextMenu(mouseEvent);
      }
    };

    this.addDestroyableEventListener(this.eBodyViewport, 'contextmenu', listener);
  }; // + rangeController - used to know when to scroll when user is dragging outside the
  // main viewport while doing a range selection


  GridPanel.prototype.getBodyClientRect = function () {
    if (!this.eBodyViewport) {
      return;
    }

    return this.eBodyViewport.getBoundingClientRect();
  };

  GridPanel.prototype.getRowForEvent = function (event) {
    var sourceElement = _.getTarget(event);

    while (sourceElement) {
      var renderedRow = this.gridOptionsWrapper.getDomData(sourceElement, RowComp.DOM_DATA_KEY_RENDERED_ROW);

      if (renderedRow) {
        return renderedRow;
      }

      sourceElement = sourceElement.parentElement;
    }

    return null;
  };

  GridPanel.prototype.processKeyboardEvent = function (eventName, keyboardEvent) {
    var cellComp = _.getCellCompForEvent(this.gridOptionsWrapper, keyboardEvent);

    if (!cellComp) {
      return;
    }

    var rowNode = cellComp.getRenderedRow().getRowNode();
    var column = cellComp.getColumn();
    var editing = cellComp.isEditing();
    var gridProcessingAllowed = !_.isUserSuppressingKeyboardEvent(this.gridOptionsWrapper, keyboardEvent, rowNode, column, editing);

    if (gridProcessingAllowed) {
      switch (eventName) {
        case 'keydown':
          // first see if it's a scroll key, page up / down, home / end etc
          var wasScrollKey = !editing && this.navigationService.handlePageScrollingKey(keyboardEvent); // if not a scroll key, then we pass onto cell

          if (!wasScrollKey) {
            cellComp.onKeyDown(keyboardEvent);
          } // perform clipboard and undo / redo operations


          this.doGridOperations(keyboardEvent, cellComp);
          break;

        case 'keypress':
          cellComp.onKeyPress(keyboardEvent);
          break;
      }
    }

    if (eventName === 'keydown') {
      var cellKeyDownEvent = cellComp.createEvent(keyboardEvent, Events.EVENT_CELL_KEY_DOWN);
      this.beans.eventService.dispatchEvent(cellKeyDownEvent);
    }

    if (eventName === 'keypress') {
      var cellKeyPressEvent = cellComp.createEvent(keyboardEvent, Events.EVENT_CELL_KEY_PRESS);
      this.beans.eventService.dispatchEvent(cellKeyPressEvent);
    }
  };

  GridPanel.prototype.doGridOperations = function (keyboardEvent, cellComp) {
    // check if ctrl or meta key pressed
    if (!keyboardEvent.ctrlKey && !keyboardEvent.metaKey) {
      return;
    } // if the cell the event came from is editing, then we do not
    // want to do the default shortcut keys, otherwise the editor
    // (eg a text field) would not be able to do the normal cut/copy/paste


    if (cellComp.isEditing()) {
      return;
    } // for copy / paste, we don't want to execute when the event
    // was from a child grid (happens in master detail)


    if (!this.mouseEventService.isEventFromThisGrid(keyboardEvent)) {
      return;
    }

    switch (keyboardEvent.which) {
      case Constants.KEY_A:
        return this.onCtrlAndA(keyboardEvent);

      case Constants.KEY_C:
        return this.onCtrlAndC(keyboardEvent);

      case Constants.KEY_V:
        return this.onCtrlAndV();

      case Constants.KEY_D:
        return this.onCtrlAndD(keyboardEvent);

      case Constants.KEY_Z:
        return keyboardEvent.shiftKey ? this.undoRedoService.redo() : this.undoRedoService.undo();

      case Constants.KEY_Y:
        return this.undoRedoService.redo();
    }
  }; // gets called by rowRenderer when new data loaded, as it will want to scroll to the top


  GridPanel.prototype.scrollToTop = function () {
    this.eBodyViewport.scrollTop = 0;
  };

  GridPanel.prototype.processMouseEvent = function (eventName, mouseEvent) {
    if (!this.mouseEventService.isEventFromThisGrid(mouseEvent) || _.isStopPropagationForAgGrid(mouseEvent)) {
      return;
    }

    var rowComp = this.getRowForEvent(mouseEvent);
    var cellComp = this.mouseEventService.getRenderedCellForEvent(mouseEvent);

    if (eventName === "contextmenu") {
      this.handleContextMenuMouseEvent(mouseEvent, null, rowComp, cellComp);
    } else {
      if (cellComp) {
        cellComp.onMouseEvent(eventName, mouseEvent);
      }

      if (rowComp) {
        rowComp.onMouseEvent(eventName, mouseEvent);
      }
    }

    this.preventDefaultOnContextMenu(mouseEvent);
  };

  GridPanel.prototype.mockContextMenuForIPad = function () {
    var _this = this; // we do NOT want this when not in iPad, otherwise we will be doing


    if (!_.isIOSUserAgent()) {
      return;
    }

    this.eAllCellContainers.forEach(function (container) {
      var touchListener = new TouchListener(container);

      var longTapListener = function longTapListener(event) {
        var rowComp = _this.getRowForEvent(event.touchEvent);

        var cellComp = _this.mouseEventService.getRenderedCellForEvent(event.touchEvent);

        _this.handleContextMenuMouseEvent(null, event.touchEvent, rowComp, cellComp);
      };

      _this.addDestroyableEventListener(touchListener, TouchListener.EVENT_LONG_TAP, longTapListener);

      _this.addDestroyFunc(function () {
        return touchListener.destroy();
      });
    });
  };

  GridPanel.prototype.handleContextMenuMouseEvent = function (mouseEvent, touchEvent, rowComp, cellComp) {
    var rowNode = rowComp ? rowComp.getRowNode() : null;
    var column = cellComp ? cellComp.getColumn() : null;
    var value = null;

    if (column) {
      var event_1 = mouseEvent ? mouseEvent : touchEvent;
      cellComp.dispatchCellContextMenuEvent(event_1);
      value = this.valueService.getValue(column, rowNode);
    }

    this.onContextMenu(mouseEvent, touchEvent, rowNode, column, value);
  };

  GridPanel.prototype.onContextMenu = function (mouseEvent, touchEvent, rowNode, column, value) {
    // to allow us to debug in chrome, we ignore the event if ctrl is pressed.
    // not everyone wants this, so first 'if' below allows to turn this hack off.
    if (!this.gridOptionsWrapper.isAllowContextMenuWithControlKey()) {
      // then do the check
      if (mouseEvent && (mouseEvent.ctrlKey || mouseEvent.metaKey)) {
        return;
      }
    }

    if (this.contextMenuFactory && !this.gridOptionsWrapper.isSuppressContextMenu()) {
      var eventOrTouch = mouseEvent ? mouseEvent : touchEvent.touches[0];
      this.contextMenuFactory.showMenu(rowNode, column, value, eventOrTouch);
      var event_2 = mouseEvent ? mouseEvent : touchEvent;
      event_2.preventDefault();
    }
  };

  GridPanel.prototype.preventDefaultOnContextMenu = function (mouseEvent) {
    // if we don't do this, then middle click will never result in a 'click' event, as 'mousedown'
    // will be consumed by the browser to mean 'scroll' (as you can scroll with the middle mouse
    // button in the browser). so this property allows the user to receive middle button clicks if
    // they want.
    var gridOptionsWrapper = this.gridOptionsWrapper;
    var which = mouseEvent.which;

    if (gridOptionsWrapper.isPreventDefaultOnContextMenu() || gridOptionsWrapper.isSuppressMiddleClickScrolls() && which === 2) {
      mouseEvent.preventDefault();
    }
  };

  GridPanel.prototype.onCtrlAndA = function (event) {
    var _a = this,
        columnController = _a.columnController,
        pinnedRowModel = _a.pinnedRowModel,
        paginationProxy = _a.paginationProxy,
        rangeController = _a.rangeController;

    var PINNED_BOTTOM = Constants.PINNED_BOTTOM,
        PINNED_TOP = Constants.PINNED_TOP;

    if (rangeController && paginationProxy.isRowsToRender()) {
      var _b = [pinnedRowModel.isEmpty(PINNED_TOP), pinnedRowModel.isEmpty(PINNED_BOTTOM)],
          isEmptyPinnedTop = _b[0],
          isEmptyPinnedBottom = _b[1];
      var floatingStart = isEmptyPinnedTop ? null : PINNED_TOP;
      var floatingEnd = void 0;
      var rowEnd = void 0;

      if (isEmptyPinnedBottom) {
        floatingEnd = null;
        rowEnd = this.paginationProxy.getRowCount() - 1;
      } else {
        floatingEnd = PINNED_BOTTOM;
        rowEnd = pinnedRowModel.getPinnedBottomRowData().length - 1;
      }

      var allDisplayedColumns = columnController.getAllDisplayedColumns();

      if (_.missingOrEmpty(allDisplayedColumns)) {
        return;
      }

      rangeController.setCellRange({
        rowStartIndex: 0,
        rowStartPinned: floatingStart,
        rowEndIndex: rowEnd,
        rowEndPinned: floatingEnd,
        columnStart: allDisplayedColumns[0],
        columnEnd: _.last(allDisplayedColumns)
      });
    }

    event.preventDefault();
  };

  GridPanel.prototype.onCtrlAndC = function (event) {
    if (!this.clipboardService || this.gridOptionsWrapper.isEnableCellTextSelection()) {
      return;
    }

    var focusedCell = this.focusedCellController.getFocusedCell();
    this.clipboardService.copyToClipboard();
    event.preventDefault(); // the copy operation results in loosing focus on the cell,
    // because of the trickery the copy logic uses with a temporary
    // widget. so we set it back again.

    if (focusedCell) {
      this.focusedCellController.setFocusedCell(focusedCell.rowIndex, focusedCell.column, focusedCell.rowPinned, true);
    }
  };

  GridPanel.prototype.onCtrlAndV = function () {
    if (ModuleRegistry.isRegistered(exports.ModuleNames.ClipboardModule)) {
      this.clipboardService.pasteFromClipboard();
    }
  };

  GridPanel.prototype.onCtrlAndD = function (event) {
    if (ModuleRegistry.isRegistered(exports.ModuleNames.ClipboardModule)) {
      this.clipboardService.copyRangeDown();
      event.preventDefault();
    }
  }; // Valid values for position are bottom, middle and top
  // position should be {'top','middle','bottom', or undefined/null}.
  // if undefined/null, then the grid will to the minimal amount of scrolling,
  // eg if grid needs to scroll up, it scrolls until row is on top,
  //    if grid needs to scroll down, it scrolls until row is on bottom,
  //    if row is already in view, grid does not scroll
  // fixme - how does this work in the new way


  GridPanel.prototype.ensureIndexVisible = function (index, position) {
    // if for print or auto height, everything is always visible
    if (this.printLayout) {
      return;
    }

    var rowCount = this.paginationProxy.getRowCount();

    if (typeof index !== 'number' || index < 0 || index >= rowCount) {
      console.warn('invalid row index for ensureIndexVisible: ' + index);
      return;
    }

    this.paginationProxy.goToPageWithIndex(index);
    var rowNode = this.paginationProxy.getRow(index);
    var rowGotShiftedDuringOperation;

    do {
      var startingRowTop = rowNode.rowTop;
      var startingRowHeight = rowNode.rowHeight;
      var paginationOffset = this.paginationProxy.getPixelOffset();
      var rowTopPixel = rowNode.rowTop - paginationOffset;
      var rowBottomPixel = rowTopPixel + rowNode.rowHeight;
      var scrollPosition = this.getVScrollPosition();
      var heightOffset = this.heightScaler.getOffset();
      var vScrollTop = scrollPosition.top + heightOffset;
      var vScrollBottom = scrollPosition.bottom + heightOffset;
      var viewportHeight = vScrollBottom - vScrollTop; // work out the pixels for top, middle and bottom up front,
      // make the if/else below easier to read

      var pxTop = this.heightScaler.getScrollPositionForPixel(rowTopPixel);
      var pxBottom = this.heightScaler.getScrollPositionForPixel(rowBottomPixel - viewportHeight); // make sure if middle, the row is not outside the top of the grid

      var pxMiddle = Math.min((pxTop + pxBottom) / 2, rowTopPixel);
      var rowBelowViewport = vScrollTop > rowTopPixel;
      var rowAboveViewport = vScrollBottom < rowBottomPixel;
      var newScrollPosition = null;

      if (position === 'top') {
        newScrollPosition = pxTop;
      } else if (position === 'bottom') {
        newScrollPosition = pxBottom;
      } else if (position === 'middle') {
        newScrollPosition = pxMiddle;
      } else if (rowBelowViewport) {
        // if row is before, scroll up with row at top
        newScrollPosition = pxTop;
      } else if (rowAboveViewport) {
        // if row is below, scroll down with row at bottom
        newScrollPosition = pxBottom;
      }

      if (newScrollPosition !== null) {
        this.eBodyViewport.scrollTop = newScrollPosition;
        this.rowRenderer.redrawAfterScroll();
      } // the row can get shifted if during the rendering (during rowRenderer.redrawAfterScroll()),
      // the height of a row changes due to lazy calculation of row heights when using
      // colDef.autoHeight or gridOptions.getRowHeight.
      // if row was shifted, then the position we scrolled to is incorrect.


      rowGotShiftedDuringOperation = startingRowTop !== rowNode.rowTop || startingRowHeight !== rowNode.rowHeight;
    } while (rowGotShiftedDuringOperation); // so when we return back to user, the cells have rendered


    this.animationFrameService.flushAllFrames();
  }; // + moveColumnController


  GridPanel.prototype.getCenterWidth = function () {
    return this.eCenterViewport.clientWidth;
  };

  GridPanel.prototype.isVerticalScrollShowing = function () {
    var isAlwaysShowVerticalScroll = this.gridOptionsWrapper.isAlwaysShowVerticalScroll();

    _.addOrRemoveCssClass(this.eBodyViewport, 'ag-force-vertical-scroll', isAlwaysShowVerticalScroll);

    return isAlwaysShowVerticalScroll || _.isVerticalScrollShowing(this.eBodyViewport);
  };

  GridPanel.prototype.isHorizontalScrollShowing = function () {
    return _.isHorizontalScrollShowing(this.eCenterViewport);
  }; // gets called every time the viewport size changes. we use this to check visibility of scrollbars
  // in the grid panel, and also to check size and position of viewport for row and column virtualisation.


  GridPanel.prototype.checkViewportAndScrolls = function () {
    // results in updating anything that depends on scroll showing
    this.updateScrollVisibleService(); // fires event if height changes, used by PaginationService, HeightScalerService, RowRenderer

    this.checkBodyHeight(); // check for virtual columns for ColumnController

    this.onHorizontalViewportChanged();
    this.setPinnedContainerSize(); // this is to cater for AG-3274, where grid is removed from the dom and then inserted back in again.
    // (which happens with some implementations of tabbing). this can result in horizontal scroll getting
    // reset back to the left, however no scroll event is fired. so we need to get header to also scroll
    // back to the left to be kept in sync.
    // adding and removing the grid from the DOM both resets the scroll position and
    // triggers a resize event, so notify listeners if the scroll position has changed

    if (this.scrollLeft !== this.getCenterViewportScrollLeft()) {
      this.onBodyHorizontalScroll(this.eCenterViewport);
    }
  };

  GridPanel.prototype.updateScrollVisibleService = function () {
    // because of column animation (which takes 200ms), we have to do this twice.
    // eg if user removes cols anywhere except at the RHS, then the cols on the RHS
    // will animate to the left to fill the gap. this animation means just after
    // the cols are removed, the remaining cols are still in the original location
    // at the start of the animation, so pre animation the H scrollbar is still needed,
    // but post animation it is not.
    this.updateScrollVisibleServiceImpl();
    setTimeout(this.updateScrollVisibleServiceImpl.bind(this), 500);
  };

  GridPanel.prototype.updateScrollVisibleServiceImpl = function () {
    var params = {
      horizontalScrollShowing: false,
      verticalScrollShowing: false
    };
    params.verticalScrollShowing = this.isVerticalScrollShowing();
    params.horizontalScrollShowing = this.isHorizontalScrollShowing();
    this.scrollVisibleService.setScrollsVisible(params);
    this.setHorizontalScrollVisible(params.horizontalScrollShowing);
    this.setVerticalScrollPaddingVisible(params.verticalScrollShowing);
  };

  GridPanel.prototype.setHorizontalScrollVisible = function (visible) {
    var isSuppressHorizontalScroll = this.gridOptionsWrapper.isSuppressHorizontalScroll();
    var scrollSize = visible ? this.gridOptionsWrapper.getScrollbarWidth() || 0 : 0;
    var scrollContainerSize = !isSuppressHorizontalScroll ? scrollSize : 0;
    var addIEPadding = _.isBrowserIE() && visible;
    this.eCenterViewport.style.height = "calc(100% + " + scrollSize + "px)";

    _.setFixedHeight(this.eHorizontalScrollBody, scrollContainerSize); // we have to add an extra pixel to the scroller viewport on IE because
    // if the container has the same size as the scrollbar, the scroll button won't work


    _.setFixedHeight(this.eBodyHorizontalScrollViewport, scrollContainerSize + (addIEPadding ? 1 : 0));

    _.setFixedHeight(this.eBodyHorizontalScrollContainer, scrollContainerSize);
  };

  GridPanel.prototype.setVerticalScrollPaddingVisible = function (show) {
    var scroller = show ? 'scroll' : 'hidden';
    this.eTop.style.overflowY = this.eBottom.style.overflowY = scroller;
    this.setFakeHScrollSpacerWidths();
  };

  GridPanel.prototype.updateRowCount = function () {
    var headerCount = this.headerRootComp.getHeaderRowCount();
    var rowCount = this.paginationProxy.getRowCount();
    var total = (headerCount + rowCount).toString();
    this.getGui().setAttribute('aria-rowcount', total);
  };

  GridPanel.prototype.updateColumnCount = function () {
    var columns = this.beans.columnController.getAllDisplayedColumns();
    this.getGui().setAttribute('aria-colcount', columns.length.toString());
  };

  GridPanel.prototype.ensureColumnVisible = function (key) {
    var column = this.columnController.getGridColumn(key);

    if (!column) {
      return;
    }

    if (column.isPinned()) {
      console.warn('calling ensureIndexVisible on a ' + column.getPinned() + ' pinned column doesn\'t make sense for column ' + column.getColId());
      return;
    }

    if (!this.columnController.isColumnDisplayed(column)) {
      console.warn('column is not currently visible');
      return;
    }

    var colLeftPixel = column.getLeft();
    var colRightPixel = colLeftPixel + column.getActualWidth();
    var viewportWidth = this.eCenterViewport.clientWidth;
    var scrollPosition = this.getCenterViewportScrollLeft();
    var bodyWidth = this.columnController.getBodyContainerWidth();
    var viewportLeftPixel;
    var viewportRightPixel; // the logic of working out left and right viewport px is both here and in the ColumnController,
    // need to refactor it out to one place

    if (this.enableRtl) {
      viewportLeftPixel = bodyWidth - scrollPosition - viewportWidth;
      viewportRightPixel = bodyWidth - scrollPosition;
    } else {
      viewportLeftPixel = scrollPosition;
      viewportRightPixel = viewportWidth + scrollPosition;
    }

    var viewportScrolledPastCol = viewportLeftPixel > colLeftPixel;
    var viewportScrolledBeforeCol = viewportRightPixel < colRightPixel;
    var colToSmallForViewport = viewportWidth < column.getActualWidth();
    var alignColToLeft = viewportScrolledPastCol || colToSmallForViewport;
    var alignColToRight = viewportScrolledBeforeCol;
    var newScrollPosition = this.getCenterViewportScrollLeft();

    if (alignColToLeft || alignColToRight) {
      if (this.enableRtl) {
        newScrollPosition = alignColToLeft ? bodyWidth - viewportWidth - colLeftPixel : bodyWidth - colRightPixel;
      } else {
        newScrollPosition = alignColToLeft ? colLeftPixel : colRightPixel - viewportWidth;
      }

      this.setCenterViewportScrollLeft(newScrollPosition);
    } // this will happen anyway, as the move will cause a 'scroll' event on the body, however
    // it is possible that the ensureColumnVisible method is called from within ag-Grid and
    // the caller will need to have the columns rendered to continue, which will be before
    // the event has been worked on (which is the case for cell navigation).


    this.onHorizontalViewportChanged(); // so when we return back to user, the cells have rendered

    this.animationFrameService.flushAllFrames();
  };

  GridPanel.prototype.showLoadingOverlay = function () {
    if (!this.gridOptionsWrapper.isSuppressLoadingOverlay()) {
      this.overlayWrapper.showLoadingOverlay();
    }
  };

  GridPanel.prototype.showNoRowsOverlay = function () {
    if (!this.gridOptionsWrapper.isSuppressNoRowsOverlay()) {
      this.overlayWrapper.showNoRowsOverlay();
    }
  };

  GridPanel.prototype.hideOverlay = function () {
    this.overlayWrapper.hideOverlay();
  }; // method will call itself if no available width. this covers if the grid
  // isn't visible, but is just about to be visible.


  GridPanel.prototype.sizeColumnsToFit = function (nextTimeout) {
    var _this = this;

    var availableWidth = this.eBodyViewport.clientWidth;

    if (availableWidth > 0) {
      this.columnController.sizeColumnsToFit(availableWidth, "sizeColumnsToFit");
      return;
    }

    if (nextTimeout === undefined) {
      window.setTimeout(function () {
        _this.sizeColumnsToFit(100);
      }, 0);
    } else if (nextTimeout === 100) {
      window.setTimeout(function () {
        _this.sizeColumnsToFit(500);
      }, 100);
    } else if (nextTimeout === 500) {
      window.setTimeout(function () {
        _this.sizeColumnsToFit(-1);
      }, 500);
    } else {
      console.warn('ag-Grid: tried to call sizeColumnsToFit() but the grid is coming back with ' + 'zero width, maybe the grid is not visible yet on the screen?');
    }
  }; // used by autoWidthCalculator and autoHeightCalculator


  GridPanel.prototype.getCenterContainer = function () {
    return this.eCenterContainer;
  };

  GridPanel.prototype.getDropTargetBodyContainers = function () {
    return [this.eCenterViewport, this.eTopViewport, this.eBottomViewport];
  };

  GridPanel.prototype.getDropTargetLeftContainers = function () {
    return [this.eLeftContainer, this.eLeftBottom, this.eLeftTop];
  };

  GridPanel.prototype.getDropTargetRightContainers = function () {
    return [this.eRightContainer, this.eRightBottom, this.eRightTop];
  };

  GridPanel.prototype.buildRowContainerComponents = function () {
    var _this = this;

    this.eAllCellContainers = [this.eLeftContainer, this.eRightContainer, this.eCenterContainer, this.eTop, this.eBottom, this.eFullWidthContainer];
    this.rowContainerComponents = {
      body: new RowContainerComponent({
        eContainer: this.eCenterContainer,
        eWrapper: this.eCenterColsClipper,
        eViewport: this.eBodyViewport
      }),
      fullWidth: new RowContainerComponent({
        eContainer: this.eFullWidthContainer
      }),
      pinnedLeft: new RowContainerComponent({
        eContainer: this.eLeftContainer
      }),
      pinnedRight: new RowContainerComponent({
        eContainer: this.eRightContainer
      }),
      floatingTop: new RowContainerComponent({
        eContainer: this.eTopContainer
      }),
      floatingTopPinnedLeft: new RowContainerComponent({
        eContainer: this.eLeftTop
      }),
      floatingTopPinnedRight: new RowContainerComponent({
        eContainer: this.eRightTop
      }),
      floatingTopFullWidth: new RowContainerComponent({
        eContainer: this.eTopFullWidthContainer,
        hideWhenNoChildren: true
      }),
      floatingBottom: new RowContainerComponent({
        eContainer: this.eBottomContainer
      }),
      floatingBottomPinnedLeft: new RowContainerComponent({
        eContainer: this.eLeftBottom
      }),
      floatingBottomPinnedRight: new RowContainerComponent({
        eContainer: this.eRightBottom
      }),
      floatingBottomFullWith: new RowContainerComponent({
        eContainer: this.eBottomFullWidthContainer,
        hideWhenNoChildren: true
      })
    };

    _.iterateObject(this.rowContainerComponents, function (key, container) {
      if (container) {
        _this.getContext().wireBean(container);
      }
    });
  };

  GridPanel.prototype.setupRowAnimationCssClass = function () {
    var _this = this;

    var listener = function listener() {
      // we don't want to use row animation if scaling, as rows jump strangely as you scroll,
      // when scaling and doing row animation.
      var animateRows = _this.gridOptionsWrapper.isAnimateRows() && !_this.heightScaler.isScaling();

      _.addOrRemoveCssClass(_this.eBodyViewport, 'ag-row-animation', animateRows);

      _.addOrRemoveCssClass(_this.eBodyViewport, 'ag-row-no-animation', !animateRows);
    };

    listener();
    this.addDestroyableEventListener(this.eventService, Events.EVENT_HEIGHT_SCALE_CHANGED, listener);
  }; // when editing a pinned row, if the cell is half outside the scrollable area, the browser can
  // scroll the column into view. we do not want this, the pinned sections should never scroll.
  // so we listen to scrolls on these containers and reset the scroll if we find one.


  GridPanel.prototype.suppressScrollOnFloatingRow = function () {
    var _this = this;

    var resetTopScroll = function resetTopScroll() {
      return _this.eTopViewport.scrollLeft = 0;
    };

    var resetBottomScroll = function resetBottomScroll() {
      return _this.eTopViewport.scrollLeft = 0;
    };

    this.addDestroyableEventListener(this.eTopViewport, 'scroll', resetTopScroll);
    this.addDestroyableEventListener(this.eBottomViewport, 'scroll', resetBottomScroll);
  };

  GridPanel.prototype.getRowContainers = function () {
    return this.rowContainerComponents;
  };

  GridPanel.prototype.getFloatingTopBottom = function () {
    return [this.eTop, this.eBottom];
  };

  GridPanel.prototype.onDisplayedColumnsChanged = function () {
    this.setPinnedContainerSize();
    this.setHeaderAndFloatingHeights();
    this.onHorizontalViewportChanged();
    this.updateScrollVisibleService();
    this.updateColumnCount();
  };

  GridPanel.prototype.onDisplayedColumnsWidthChanged = function () {
    this.setWidthsOfContainers();
    this.onHorizontalViewportChanged();
    this.updateScrollVisibleService();

    if (this.enableRtl) {
      // because RTL is all backwards, a change in the width of the row
      // can cause a change in the scroll position, without a scroll event,
      // because the scroll position in RTL is a function that depends on
      // the width. to be convinced of this, take out this line, enable RTL,
      // scroll all the way to the left and then resize a column
      this.horizontallyScrollHeaderCenterAndFloatingCenter();
    }
  };

  GridPanel.prototype.setWidthsOfContainers = function () {
    this.setCenterWidth();
    this.setPinnedContainerSize();
  };

  GridPanel.prototype.setCenterWidth = function () {
    var width = this.columnController.getBodyContainerWidth();

    if (this.printLayout) {
      var pinnedContainerWidths = this.columnController.getPinnedLeftContainerWidth() + this.columnController.getPinnedRightContainerWidth();
      width += pinnedContainerWidths;
    }

    this.headerRootComp.setHeaderContainerWidth(width);
    var widthPx = width + "px";
    this.eCenterContainer.style.width = widthPx;
    this.eBottomContainer.style.width = widthPx;
    this.eTopContainer.style.width = widthPx;

    if (!this.printLayout) {
      this.eBodyHorizontalScrollContainer.style.width = widthPx;
    }
  };

  GridPanel.prototype.setPinnedLeftWidth = function () {
    var _this = this;

    var oldPinning = this.pinningLeft;
    var widthOfCols = this.columnController.getPinnedLeftContainerWidth();
    var newPinning = this.pinningLeft = !this.printLayout && widthOfCols > 0;
    var containers = [this.eLeftContainer, this.eLeftTop, this.eLeftBottom];

    if (oldPinning !== newPinning) {
      this.headerRootComp.setLeftVisible(newPinning);
    }

    containers.forEach(function (e) {
      return _.setDisplayed(e, _this.pinningLeft);
    });

    if (newPinning) {
      containers.forEach(function (ct) {
        return _.setFixedWidth(ct, widthOfCols);
      });
    }
  };

  GridPanel.prototype.setPinnedRightWidth = function () {
    var oldPinning = this.pinningRight;
    var widthOfCols = this.columnController.getPinnedRightContainerWidth();
    var newPinning = this.pinningRight = !this.printLayout && widthOfCols > 0;
    var containers = [this.eRightContainer, this.eRightTop, this.eRightBottom];

    if (oldPinning !== newPinning) {
      this.headerRootComp.setRightVisible(newPinning);
    }

    containers.forEach(function (ct) {
      return _.setDisplayed(ct, newPinning);
    });

    if (newPinning) {
      containers.forEach(function (ct) {
        return _.setFixedWidth(ct, widthOfCols);
      });
    }
  };

  GridPanel.prototype.setPinnedContainerSize = function () {
    this.setPinnedLeftWidth();
    this.setPinnedRightWidth();
    this.setFakeHScrollSpacerWidths();
  };

  GridPanel.prototype.setFakeHScrollSpacerWidths = function () {
    // we pad the right based on a) if cols are pinned to the right and
    // b) if v scroll is showing on the right (normal position of scroll)
    var rightSpacing = this.columnController.getPinnedRightContainerWidth();
    var scrollOnRight = !this.enableRtl && this.isVerticalScrollShowing();

    if (scrollOnRight) {
      rightSpacing += this.scrollWidth;
    }

    _.setFixedWidth(this.eHorizontalRightSpacer, rightSpacing);

    _.addOrRemoveCssClass(this.eHorizontalRightSpacer, 'ag-scroller-corner', rightSpacing <= this.scrollWidth); // we pad the left based on a) if cols are pinned to the left and
    // b) if v scroll is showing on the left (happens in LTR layout only)


    var leftSpacing = this.columnController.getPinnedLeftContainerWidth();
    var scrollOnLeft = this.enableRtl && this.isVerticalScrollShowing();

    if (scrollOnLeft) {
      leftSpacing += this.scrollWidth;
    }

    _.setFixedWidth(this.eHorizontalLeftSpacer, leftSpacing);

    _.addOrRemoveCssClass(this.eHorizontalLeftSpacer, 'ag-scroller-corner', leftSpacing <= this.scrollWidth);
  };

  GridPanel.prototype.checkBodyHeight = function () {
    var bodyHeight = this.eBodyViewport.clientHeight;

    if (this.bodyHeight !== bodyHeight) {
      this.bodyHeight = bodyHeight;
      var event_3 = {
        type: Events.EVENT_BODY_HEIGHT_CHANGED,
        api: this.gridApi,
        columnApi: this.columnApi
      };
      this.eventService.dispatchEvent(event_3);
    }
  };

  GridPanel.prototype.setHeaderAndFloatingHeights = function () {
    var _a = this,
        columnController = _a.columnController,
        gridOptionsWrapper = _a.gridOptionsWrapper,
        pinnedRowModel = _a.pinnedRowModel,
        eTop = _a.eTop,
        eBottom = _a.eBottom;

    var numberOfFloating = 0;
    var headerRowCount = columnController.getHeaderRowCount();
    var totalHeaderHeight;
    var groupHeight;
    var headerHeight;

    if (!columnController.isPivotMode()) {
      if (gridOptionsWrapper.isFloatingFilter()) {
        headerRowCount++;
      }

      numberOfFloating = gridOptionsWrapper.isFloatingFilter() ? 1 : 0;
      groupHeight = gridOptionsWrapper.getGroupHeaderHeight();
      headerHeight = gridOptionsWrapper.getHeaderHeight();
    } else {
      numberOfFloating = 0;
      groupHeight = gridOptionsWrapper.getPivotGroupHeaderHeight();
      headerHeight = gridOptionsWrapper.getPivotHeaderHeight();
    }

    var numberOfNonGroups = 1 + numberOfFloating;
    var numberOfGroups = headerRowCount - numberOfNonGroups;
    totalHeaderHeight = numberOfFloating * gridOptionsWrapper.getFloatingFiltersHeight();
    totalHeaderHeight += numberOfGroups * groupHeight;
    totalHeaderHeight += headerHeight;
    this.headerRootComp.setHeight(totalHeaderHeight);
    var floatingTopHeight = pinnedRowModel.getPinnedTopTotalHeight();

    if (floatingTopHeight) {
      // adding 1px for cell bottom border
      floatingTopHeight += 1;
    }

    var floatingBottomHeight = pinnedRowModel.getPinnedBottomTotalHeight();

    if (floatingBottomHeight) {
      // adding 1px for cell bottom border
      floatingBottomHeight += 1;
    }

    var floatingTopHeightString = floatingTopHeight + "px";
    var floatingBottomHeightString = floatingBottomHeight + "px";
    eTop.style.minHeight = floatingTopHeightString;
    eTop.style.height = floatingTopHeightString;
    eTop.style.display = floatingTopHeight ? 'inherit' : 'none';
    eBottom.style.minHeight = floatingBottomHeightString;
    eBottom.style.height = floatingBottomHeightString;
    eBottom.style.display = floatingBottomHeight ? 'inherit' : 'none';
    this.checkBodyHeight();
  };

  GridPanel.prototype.getBodyHeight = function () {
    return this.bodyHeight;
  }; // called by scrollHorizontally method and alignedGridsService


  GridPanel.prototype.setHorizontalScrollPosition = function (hScrollPosition) {
    this.eCenterViewport.scrollLeft = hScrollPosition; // we need to manually do the event handling (rather than wait for the event)
    // for the alignedGridsService, as if we don't, the aligned grid service gets
    // notified async, and then it's 'consuming' flag doesn't get used right, and
    // we can end up with an infinite loop

    this.doHorizontalScroll(hScrollPosition);
  };

  GridPanel.prototype.setVerticalScrollPosition = function (vScrollPosition) {
    this.eBodyViewport.scrollTop = vScrollPosition;
  }; // called by the headerRootComp and moveColumnController


  GridPanel.prototype.scrollHorizontally = function (pixels) {
    var oldScrollPosition = this.eCenterViewport.scrollLeft;
    this.setHorizontalScrollPosition(oldScrollPosition + pixels);
    return this.eCenterViewport.scrollLeft - oldScrollPosition;
  }; // called by rowDragFeature


  GridPanel.prototype.scrollVertically = function (pixels) {
    var oldScrollPosition = this.eBodyViewport.scrollTop;
    this.setVerticalScrollPosition(oldScrollPosition + pixels);
    return this.eBodyViewport.scrollTop - oldScrollPosition;
  };

  GridPanel.prototype.addScrollListener = function () {
    this.addDestroyableEventListener(this.eCenterViewport, 'scroll', this.onCenterViewportScroll.bind(this));
    this.addDestroyableEventListener(this.eBodyHorizontalScrollViewport, 'scroll', this.onFakeHorizontalScroll.bind(this));
    this.addDestroyableEventListener(this.eBodyViewport, 'scroll', this.onVerticalScroll.bind(this));
  };

  GridPanel.prototype.onVerticalScroll = function () {
    var scrollTop = this.eBodyViewport.scrollTop;
    this.animationFrameService.setScrollTop(scrollTop);
    this.scrollTop = scrollTop;
    this.redrawRowsAfterScroll();
  };

  GridPanel.prototype.isControllingScroll = function (eDiv) {
    if (!this.lastHorizontalScrollElement) {
      this.lastHorizontalScrollElement = eDiv;
      return true;
    }

    return eDiv === this.lastHorizontalScrollElement;
  };

  GridPanel.prototype.onFakeHorizontalScroll = function () {
    if (!this.isControllingScroll(this.eBodyHorizontalScrollViewport)) {
      return;
    }

    this.onBodyHorizontalScroll(this.eBodyHorizontalScrollViewport);
  };

  GridPanel.prototype.onCenterViewportScroll = function () {
    if (!this.isControllingScroll(this.eCenterViewport)) {
      return;
    }

    this.onBodyHorizontalScroll(this.eCenterViewport);
  };

  GridPanel.prototype.onBodyHorizontalScroll = function (eSource) {
    var _a = this.eCenterViewport,
        scrollWidth = _a.scrollWidth,
        clientWidth = _a.clientWidth; // in chrome, fractions can be in the scroll left, eg 250.342234 - which messes up our 'scrollWentPastBounds'
    // formula. so we floor it to allow the formula to work.

    var scrollLeft = Math.floor(_.getScrollLeft(eSource, this.enableRtl)); // touch devices allow elastic scroll - which temporally scrolls the panel outside of the viewport
    // (eg user uses touch to go to the left of the grid, but drags past the left, the rows will actually
    // scroll past the left until the user releases the mouse). when this happens, we want ignore the scroll,
    // as otherwise it was causing the rows and header to flicker.

    var scrollWentPastBounds = scrollLeft < 0 || scrollLeft + clientWidth > scrollWidth;

    if (scrollWentPastBounds) {
      return;
    }

    this.doHorizontalScroll(scrollLeft);
    this.resetLastHorizontalScrollElementDebounce();
  };

  GridPanel.prototype.resetLastHorizontalScrollElement = function () {
    this.lastHorizontalScrollElement = null;
  };

  GridPanel.prototype.doHorizontalScroll = function (scrollLeft) {
    this.scrollLeft = scrollLeft;
    var event = {
      type: Events.EVENT_BODY_SCROLL,
      api: this.gridApi,
      columnApi: this.columnApi,
      direction: 'horizontal',
      left: this.scrollLeft,
      top: this.scrollTop
    };
    this.eventService.dispatchEvent(event);
    this.horizontallyScrollHeaderCenterAndFloatingCenter(scrollLeft);
    this.onHorizontalViewportChanged();
  };

  GridPanel.prototype.redrawRowsAfterScroll = function () {
    var event = {
      type: Events.EVENT_BODY_SCROLL,
      direction: 'vertical',
      api: this.gridApi,
      columnApi: this.columnApi,
      left: this.scrollLeft,
      top: this.scrollTop
    };
    this.eventService.dispatchEvent(event);
  }; // this gets called whenever a change in the viewport, so we can inform column controller it has to work
  // out the virtual columns again. gets called from following locations:
  // + ensureColVisible, scroll, init, layoutChanged, displayedColumnsChanged, API (doLayout)


  GridPanel.prototype.onHorizontalViewportChanged = function () {
    var scrollWidth = this.eCenterViewport.clientWidth;
    var scrollPosition = this.getCenterViewportScrollLeft();
    this.columnController.setVirtualViewportPosition(scrollWidth, scrollPosition);
  };

  GridPanel.prototype.getCenterViewportScrollLeft = function () {
    // we defer to a util, as how you calculated scrollLeft when doing RTL depends on the browser
    return _.getScrollLeft(this.eCenterViewport, this.enableRtl);
  };

  GridPanel.prototype.setCenterViewportScrollLeft = function (value) {
    // we defer to a util, as how you calculated scrollLeft when doing RTL depends on the browser
    _.setScrollLeft(this.eCenterViewport, value, this.enableRtl);
  };

  GridPanel.prototype.horizontallyScrollHeaderCenterAndFloatingCenter = function (scrollLeft) {
    if (scrollLeft === undefined) {
      scrollLeft = this.getCenterViewportScrollLeft();
    }

    var offset = this.enableRtl ? scrollLeft : -scrollLeft;
    var _a = this.eCenterViewport,
        clientWidth = _a.clientWidth,
        scrollWidth = _a.scrollWidth;
    var scrollWentPastBounds = Math.abs(offset) + clientWidth > scrollWidth;

    if (scrollWentPastBounds || this.enableRtl && offset < 0 || !this.enableRtl && offset > 0) {
      return;
    }

    this.headerRootComp.setHorizontalScroll(offset);
    this.eBottomContainer.style.transform = "translateX(" + offset + "px)";
    this.eTopContainer.style.transform = "translateX(" + offset + "px)";
    var partner = this.lastHorizontalScrollElement === this.eCenterViewport ? this.eBodyHorizontalScrollViewport : this.eCenterViewport;

    _.setScrollLeft(partner, scrollLeft, this.enableRtl);
  }; // + rangeController


  GridPanel.prototype.addScrollEventListener = function (listener) {
    this.eBodyViewport.addEventListener('scroll', listener);
  }; // + rangeController


  GridPanel.prototype.removeScrollEventListener = function (listener) {
    this.eBodyViewport.removeEventListener('scroll', listener);
  };

  __decorate$U([Autowired('alignedGridsService')], GridPanel.prototype, "alignedGridsService", void 0);

  __decorate$U([Autowired('gridOptionsWrapper')], GridPanel.prototype, "gridOptionsWrapper", void 0);

  __decorate$U([Autowired('columnController')], GridPanel.prototype, "columnController", void 0);

  __decorate$U([Autowired('rowRenderer')], GridPanel.prototype, "rowRenderer", void 0);

  __decorate$U([Autowired('pinnedRowModel')], GridPanel.prototype, "pinnedRowModel", void 0);

  __decorate$U([Autowired('eventService')], GridPanel.prototype, "eventService", void 0);

  __decorate$U([Autowired('animationFrameService')], GridPanel.prototype, "animationFrameService", void 0);

  __decorate$U([Autowired('navigationService')], GridPanel.prototype, "navigationService", void 0);

  __decorate$U([Autowired('autoHeightCalculator')], GridPanel.prototype, "autoHeightCalculator", void 0);

  __decorate$U([Autowired('columnAnimationService')], GridPanel.prototype, "columnAnimationService", void 0);

  __decorate$U([Autowired('autoWidthCalculator')], GridPanel.prototype, "autoWidthCalculator", void 0);

  __decorate$U([Autowired('paginationAutoPageSizeService')], GridPanel.prototype, "paginationAutoPageSizeService", void 0);

  __decorate$U([Autowired('beans')], GridPanel.prototype, "beans", void 0);

  __decorate$U([Autowired('paginationProxy')], GridPanel.prototype, "paginationProxy", void 0);

  __decorate$U([Autowired('columnApi')], GridPanel.prototype, "columnApi", void 0);

  __decorate$U([Autowired('gridApi')], GridPanel.prototype, "gridApi", void 0);

  __decorate$U([Autowired('dragService')], GridPanel.prototype, "dragService", void 0);

  __decorate$U([Autowired('mouseEventService')], GridPanel.prototype, "mouseEventService", void 0);

  __decorate$U([Autowired('focusedCellController')], GridPanel.prototype, "focusedCellController", void 0);

  __decorate$U([Autowired('$scope')], GridPanel.prototype, "$scope", void 0);

  __decorate$U([Autowired('scrollVisibleService')], GridPanel.prototype, "scrollVisibleService", void 0);

  __decorate$U([Autowired('valueService')], GridPanel.prototype, "valueService", void 0);

  __decorate$U([Autowired('dragAndDropService')], GridPanel.prototype, "dragAndDropService", void 0);

  __decorate$U([Autowired('maxDivHeightScaler')], GridPanel.prototype, "heightScaler", void 0);

  __decorate$U([Autowired('resizeObserverService')], GridPanel.prototype, "resizeObserverService", void 0);

  __decorate$U([Autowired('undoRedoService')], GridPanel.prototype, "undoRedoService", void 0);

  __decorate$U([Optional('rangeController')], GridPanel.prototype, "rangeController", void 0);

  __decorate$U([Optional('contextMenuFactory')], GridPanel.prototype, "contextMenuFactory", void 0);

  __decorate$U([Optional('clipboardService')], GridPanel.prototype, "clipboardService", void 0);

  __decorate$U([RefSelector('eBodyViewport')], GridPanel.prototype, "eBodyViewport", void 0);

  __decorate$U([RefSelector('eCenterContainer')], GridPanel.prototype, "eCenterContainer", void 0);

  __decorate$U([RefSelector('eCenterViewport')], GridPanel.prototype, "eCenterViewport", void 0);

  __decorate$U([RefSelector('eLeftContainer')], GridPanel.prototype, "eLeftContainer", void 0);

  __decorate$U([RefSelector('eRightContainer')], GridPanel.prototype, "eRightContainer", void 0);

  __decorate$U([RefSelector('eCenterColsClipper')], GridPanel.prototype, "eCenterColsClipper", void 0);

  __decorate$U([RefSelector('eHorizontalScrollBody')], GridPanel.prototype, "eHorizontalScrollBody", void 0);

  __decorate$U([RefSelector('eHorizontalLeftSpacer')], GridPanel.prototype, "eHorizontalLeftSpacer", void 0);

  __decorate$U([RefSelector('eHorizontalRightSpacer')], GridPanel.prototype, "eHorizontalRightSpacer", void 0);

  __decorate$U([RefSelector('eBodyHorizontalScrollViewport')], GridPanel.prototype, "eBodyHorizontalScrollViewport", void 0);

  __decorate$U([RefSelector('eBodyHorizontalScrollContainer')], GridPanel.prototype, "eBodyHorizontalScrollContainer", void 0);

  __decorate$U([RefSelector('eFullWidthContainer')], GridPanel.prototype, "eFullWidthContainer", void 0);

  __decorate$U([RefSelector('eTop')], GridPanel.prototype, "eTop", void 0);

  __decorate$U([RefSelector('eLeftTop')], GridPanel.prototype, "eLeftTop", void 0);

  __decorate$U([RefSelector('eRightTop')], GridPanel.prototype, "eRightTop", void 0);

  __decorate$U([RefSelector('eTopContainer')], GridPanel.prototype, "eTopContainer", void 0);

  __decorate$U([RefSelector('eTopViewport')], GridPanel.prototype, "eTopViewport", void 0);

  __decorate$U([RefSelector('eTopFullWidthContainer')], GridPanel.prototype, "eTopFullWidthContainer", void 0);

  __decorate$U([RefSelector('eBottom')], GridPanel.prototype, "eBottom", void 0);

  __decorate$U([RefSelector('eLeftBottom')], GridPanel.prototype, "eLeftBottom", void 0);

  __decorate$U([RefSelector('eRightBottom')], GridPanel.prototype, "eRightBottom", void 0);

  __decorate$U([RefSelector('eBottomContainer')], GridPanel.prototype, "eBottomContainer", void 0);

  __decorate$U([RefSelector('eBottomViewport')], GridPanel.prototype, "eBottomViewport", void 0);

  __decorate$U([RefSelector('eBottomFullWidthContainer')], GridPanel.prototype, "eBottomFullWidthContainer", void 0);

  __decorate$U([RefSelector('headerRoot')], GridPanel.prototype, "headerRootComp", void 0);

  __decorate$U([RefSelector('overlayWrapper')], GridPanel.prototype, "overlayWrapper", void 0);

  __decorate$U([PostConstruct], GridPanel.prototype, "init", null);

  return GridPanel;
}(Component);
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __decorate$V = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var GridApi =
/** @class */
function () {
  function GridApi() {
    this.detailGridInfoMap = {};
  }

  GridApi.prototype.registerGridComp = function (gridPanel) {
    this.gridPanel = gridPanel;
  };

  GridApi.prototype.registerGridCore = function (gridCore) {
    this.gridCore = gridCore;
  };

  GridApi.prototype.registerHeaderRootComp = function (headerRootComp) {
    this.headerRootComp = headerRootComp;
  };

  GridApi.prototype.init = function () {
    switch (this.rowModel.getType()) {
      case Constants.ROW_MODEL_TYPE_CLIENT_SIDE:
        this.clientSideRowModel = this.rowModel;
        break;

      case Constants.ROW_MODEL_TYPE_INFINITE:
        this.infiniteRowModel = this.rowModel;
        break;

      case Constants.ROW_MODEL_TYPE_SERVER_SIDE:
        this.serverSideRowModel = this.rowModel;
        break;
    }
  };
  /** Used internally by grid. Not intended to be used by the client. Interface may change between releases. */


  GridApi.prototype.__getAlignedGridService = function () {
    return this.alignedGridsService;
  };

  GridApi.prototype.addDetailGridInfo = function (id, gridInfo) {
    this.detailGridInfoMap[id] = gridInfo;
  };

  GridApi.prototype.removeDetailGridInfo = function (id) {
    this.detailGridInfoMap[id] = undefined;
  };

  GridApi.prototype.getDetailGridInfo = function (id) {
    return this.detailGridInfoMap[id];
  };

  GridApi.prototype.forEachDetailGridInfo = function (callback) {
    var index = 0;

    _.iterateObject(this.detailGridInfoMap, function (id, gridInfo) {
      // check for undefined, as old references will still be lying around
      if (_.exists(gridInfo)) {
        callback(gridInfo, index);
        index++;
      }
    });
  };

  GridApi.prototype.getDataAsCsv = function (params) {
    if (ModuleRegistry.assertRegistered(exports.ModuleNames.CsvExportModule, 'api.getDataAsCsv')) {
      return this.csvCreator.getDataAsCsv(params);
    }
  };

  GridApi.prototype.exportDataAsCsv = function (params) {
    if (ModuleRegistry.assertRegistered(exports.ModuleNames.CsvExportModule, 'api.exportDataAsCSv')) {
      this.csvCreator.exportDataAsCsv(params);
    }
  };

  GridApi.prototype.getDataAsExcel = function (params) {
    if (ModuleRegistry.assertRegistered(exports.ModuleNames.ExcelExportModule, 'api.getDataAsExcel')) {
      return this.excelCreator.getDataAsExcelXml(params);
    }
  };

  GridApi.prototype.exportDataAsExcel = function (params) {
    if (ModuleRegistry.assertRegistered(exports.ModuleNames.ExcelExportModule, 'api.exportDataAsExcel')) {
      this.excelCreator.exportDataAsExcel(params);
    }
  };
  /** @deprecated */


  GridApi.prototype.setEnterpriseDatasource = function (datasource) {
    console.warn("ag-grid: since version 18.x, api.setEnterpriseDatasource() should be replaced with api.setServerSideDatasource()");
    this.setServerSideDatasource(datasource);
  };

  GridApi.prototype.setServerSideDatasource = function (datasource) {
    if (this.gridOptionsWrapper.isRowModelServerSide()) {
      // should really have an IEnterpriseRowModel interface, so we are not casting to any
      this.rowModel.setDatasource(datasource);
    } else {
      console.warn("ag-Grid: you can only use an enterprise datasource when gridOptions.rowModelType is '" + Constants.ROW_MODEL_TYPE_SERVER_SIDE + "'");
    }
  };

  GridApi.prototype.setDatasource = function (datasource) {
    if (this.gridOptionsWrapper.isRowModelInfinite()) {
      this.rowModel.setDatasource(datasource);
    } else {
      console.warn("ag-Grid: you can only use a datasource when gridOptions.rowModelType is '" + Constants.ROW_MODEL_TYPE_INFINITE + "'");
    }
  };

  GridApi.prototype.setViewportDatasource = function (viewportDatasource) {
    if (this.gridOptionsWrapper.isRowModelViewport()) {
      // this is bad coding, because it's using an interface that's exposed in the enterprise.
      // really we should create an interface in the core for viewportDatasource and let
      // the enterprise implement it, rather than casting to 'any' here
      this.rowModel.setViewportDatasource(viewportDatasource);
    } else {
      console.warn("ag-Grid: you can only use a viewport datasource when gridOptions.rowModelType is '" + Constants.ROW_MODEL_TYPE_VIEWPORT + "'");
    }
  };

  GridApi.prototype.setRowData = function (rowData) {
    if (this.gridOptionsWrapper.isRowModelDefault()) {
      if (this.gridOptionsWrapper.isDeltaRowDataMode()) {
        var _a = this.immutableService.createTransactionForRowData(rowData),
            transaction = _a[0],
            orderIdMap = _a[1];

        this.clientSideRowModel.updateRowData(transaction, orderIdMap); // need to force updating of full width rows - note this wouldn't be necessary the full width cell comp listened
        // to the data change event on the row node and refreshed itself.

        this.rowRenderer.refreshFullWidthRows();
      } else {
        this.selectionController.reset();
        this.clientSideRowModel.setRowData(rowData);
      }
    } else {
      console.warn('cannot call setRowData unless using normal row model');
    }
  };
  /** @deprecated */


  GridApi.prototype.setFloatingTopRowData = function (rows) {
    console.warn('ag-Grid: since v12, api.setFloatingTopRowData() is now api.setPinnedTopRowData()');
    this.setPinnedTopRowData(rows);
  };
  /** @deprecated */


  GridApi.prototype.setFloatingBottomRowData = function (rows) {
    console.warn('ag-Grid: since v12, api.setFloatingBottomRowData() is now api.setPinnedBottomRowData()');
    this.setPinnedBottomRowData(rows);
  };
  /** @deprecated */


  GridApi.prototype.getFloatingTopRowCount = function () {
    console.warn('ag-Grid: since v12, api.getFloatingTopRowCount() is now api.getPinnedTopRowCount()');
    return this.getPinnedTopRowCount();
  };
  /** @deprecated */


  GridApi.prototype.getFloatingBottomRowCount = function () {
    console.warn('ag-Grid: since v12, api.getFloatingBottomRowCount() is now api.getPinnedBottomRowCount()');
    return this.getPinnedBottomRowCount();
  };
  /** @deprecated */


  GridApi.prototype.getFloatingTopRow = function (index) {
    console.warn('ag-Grid: since v12, api.getFloatingTopRow() is now api.getPinnedTopRow()');
    return this.getPinnedTopRow(index);
  };
  /** @deprecated */


  GridApi.prototype.getFloatingBottomRow = function (index) {
    console.warn('ag-Grid: since v12, api.getFloatingBottomRow() is now api.getPinnedBottomRow()');
    return this.getPinnedBottomRow(index);
  };

  GridApi.prototype.setPinnedTopRowData = function (rows) {
    this.pinnedRowModel.setPinnedTopRowData(rows);
  };

  GridApi.prototype.setPinnedBottomRowData = function (rows) {
    this.pinnedRowModel.setPinnedBottomRowData(rows);
  };

  GridApi.prototype.getPinnedTopRowCount = function () {
    return this.pinnedRowModel.getPinnedTopRowCount();
  };

  GridApi.prototype.getPinnedBottomRowCount = function () {
    return this.pinnedRowModel.getPinnedBottomRowCount();
  };

  GridApi.prototype.getPinnedTopRow = function (index) {
    return this.pinnedRowModel.getPinnedTopRow(index);
  };

  GridApi.prototype.getPinnedBottomRow = function (index) {
    return this.pinnedRowModel.getPinnedBottomRow(index);
  };

  GridApi.prototype.setColumnDefs = function (colDefs, source) {
    if (source === void 0) {
      source = "api";
    }

    this.columnController.setColumnDefs(colDefs, source);
  };

  GridApi.prototype.expireValueCache = function () {
    this.valueCache.expire();
  };

  GridApi.prototype.getVerticalPixelRange = function () {
    return this.gridPanel.getVScrollPosition();
  };

  GridApi.prototype.getHorizontalPixelRange = function () {
    return this.gridPanel.getHScrollPosition();
  };

  GridApi.prototype.setAlwaysShowVerticalScroll = function (show) {
    this.gridOptionsWrapper.setProperty('alwaysShowVerticalScroll', show);
  };

  GridApi.prototype.refreshToolPanel = function () {
    this.gridCore.refreshSideBar();
  };

  GridApi.prototype.refreshCells = function (params) {
    if (params === void 0) {
      params = {};
    }

    if (Array.isArray(params)) {
      // the old version of refreshCells() took an array of rowNodes for the first argument
      console.warn('since ag-Grid v11.1, refreshCells() now takes parameters, please see the documentation.');
      return;
    }

    this.rowRenderer.refreshCells(params);
  };

  GridApi.prototype.flashCells = function (params) {
    if (params === void 0) {
      params = {};
    }

    this.rowRenderer.flashCells(params);
  };

  GridApi.prototype.redrawRows = function (params) {
    if (params === void 0) {
      params = {};
    }

    if (params && params.rowNodes) {
      this.rowRenderer.redrawRows(params.rowNodes);
    } else {
      this.rowRenderer.redrawAfterModelUpdate();
    }
  };

  GridApi.prototype.timeFullRedraw = function (count) {
    if (count === void 0) {
      count = 1;
    }

    var iterationCount = 0;
    var totalProcessing = 0;
    var totalReflow = 0;
    var that = this;
    doOneIteration();

    function doOneIteration() {
      var start = new Date().getTime();
      that.rowRenderer.redrawAfterModelUpdate();
      var endProcessing = new Date().getTime();
      window.setTimeout(function () {
        var endReflow = new Date().getTime();
        var durationProcessing = endProcessing - start;
        var durationReflow = endReflow - endProcessing; // tslint:disable-next-line

        console.log('duration:  processing = ' + durationProcessing + 'ms, reflow = ' + durationReflow + 'ms');
        iterationCount++;
        totalProcessing += durationProcessing;
        totalReflow += durationReflow;

        if (iterationCount < count) {
          // wait for 1s between tests
          window.setTimeout(doOneIteration, 1000);
        } else {
          finish();
        }
      }, 0);
    }

    function finish() {
      // tslint:disable-next-line
      console.log('tests complete. iteration count = ' + iterationCount); // tslint:disable-next-line

      console.log('average processing = ' + totalProcessing / iterationCount + 'ms'); // tslint:disable-next-line

      console.log('average reflow = ' + totalReflow / iterationCount + 'ms');
    }
  };
  /** @deprecated */


  GridApi.prototype.refreshView = function () {
    console.warn('ag-Grid: since v11.1, refreshView() is deprecated, please call refreshCells() or redrawRows() instead');
    this.redrawRows();
  }; //** @deprecated */


  GridApi.prototype.refreshRows = function (rowNodes) {
    console.warn('since ag-Grid v11.1, refreshRows() is deprecated, please use refreshCells({rowNodes: rows}) or redrawRows({rowNodes: rows}) instead');
    this.refreshCells({
      rowNodes: rowNodes
    });
  };
  /** @deprecated */


  GridApi.prototype.rowDataChanged = function (rows) {
    console.warn('ag-Grid: rowDataChanged is deprecated, either call refreshView() to refresh everything, or call rowNode.setRowData(newData) to set value on a particular node');
    this.redrawRows();
  };
  /** @deprecated */


  GridApi.prototype.softRefreshView = function () {
    console.error('ag-Grid: since v16, softRefreshView() is no longer supported. Please check the documentation on how to refresh.');
  };
  /** @deprecated */


  GridApi.prototype.refreshGroupRows = function () {
    console.warn('ag-Grid: since v11.1, refreshGroupRows() is no longer supported, call refreshCells() instead. ' + 'Because refreshCells() now does dirty checking, it will only refresh cells that have changed, so it should ' + 'not be necessary to only refresh the group rows.');
    this.refreshCells();
  };

  GridApi.prototype.setFunctionsReadOnly = function (readOnly) {
    this.gridOptionsWrapper.setProperty('functionsReadOnly', readOnly);
  };

  GridApi.prototype.refreshHeader = function () {
    this.headerRootComp.refreshHeader();
    this.gridPanel.setHeaderAndFloatingHeights();
  };

  GridApi.prototype.isAnyFilterPresent = function () {
    return this.filterManager.isAnyFilterPresent();
  };

  GridApi.prototype.isAdvancedFilterPresent = function () {
    console.warn('ag-Grid: isAdvancedFilterPresent() is deprecated, please use isColumnFilterPresent()');
    return this.isColumnFilterPresent();
  };

  GridApi.prototype.isColumnFilterPresent = function () {
    return this.filterManager.isAdvancedFilterPresent();
  };

  GridApi.prototype.isQuickFilterPresent = function () {
    return this.filterManager.isQuickFilterPresent();
  };

  GridApi.prototype.getModel = function () {
    return this.rowModel;
  };

  GridApi.prototype.setRowNodeExpanded = function (rowNode, expanded) {
    if (rowNode) {
      rowNode.setExpanded(expanded);
    }
  };

  GridApi.prototype.onGroupExpandedOrCollapsed = function (deprecated_refreshFromIndex) {
    if (_.missing(this.clientSideRowModel)) {
      console.warn('ag-Grid: cannot call onGroupExpandedOrCollapsed unless using normal row model');
    }

    if (_.exists(deprecated_refreshFromIndex)) {
      console.warn('ag-Grid: api.onGroupExpandedOrCollapsed - refreshFromIndex parameter is no longer used, the grid will refresh all rows');
    } // we don't really want the user calling this if only one rowNode was expanded, instead they should be
    // calling rowNode.setExpanded(boolean) - this way we do a 'keepRenderedRows=false' so that the whole
    // grid gets refreshed again - otherwise the row with the rowNodes that were changed won't get updated,
    // and thus the expand icon in the group cell won't get 'opened' or 'closed'.


    this.clientSideRowModel.refreshModel({
      step: Constants.STEP_MAP
    });
  };

  GridApi.prototype.refreshInMemoryRowModel = function (step) {
    console.warn("ag-grid: since version 18.x, api.refreshInMemoryRowModel() should be replaced with api.refreshClientSideRowModel()");
    this.refreshClientSideRowModel(step);
  };

  GridApi.prototype.refreshClientSideRowModel = function (step) {
    if (_.missing(this.clientSideRowModel)) {
      console.warn('cannot call refreshClientSideRowModel unless using normal row model');
    }

    var paramsStep = Constants.STEP_EVERYTHING;
    var stepsMapped = {
      group: Constants.STEP_EVERYTHING,
      filter: Constants.STEP_FILTER,
      map: Constants.STEP_MAP,
      aggregate: Constants.STEP_AGGREGATE,
      sort: Constants.STEP_SORT,
      pivot: Constants.STEP_PIVOT
    };

    if (_.exists(step)) {
      paramsStep = stepsMapped[step];
    }

    if (_.missing(paramsStep)) {
      console.error("ag-Grid: invalid step " + step + ", available steps are " + Object.keys(stepsMapped).join(', '));
      return;
    }

    var modelParams = {
      step: paramsStep,
      keepRenderedRows: true,
      animate: true,
      keepEditingRows: true
    };
    this.clientSideRowModel.refreshModel(modelParams);
  };

  GridApi.prototype.isAnimationFrameQueueEmpty = function () {
    return this.animationFrameService.isQueueEmpty();
  };

  GridApi.prototype.getRowNode = function (id) {
    return this.rowModel.getRowNode(id);
  };

  GridApi.prototype.expandAll = function () {
    if (_.missing(this.clientSideRowModel)) {
      console.warn('ag-Grid: cannot call expandAll unless using normal row model');
      return;
    }

    this.clientSideRowModel.expandOrCollapseAll(true);
  };

  GridApi.prototype.collapseAll = function () {
    if (_.missing(this.clientSideRowModel)) {
      console.warn('ag-Grid: cannot call collapseAll unless using normal row model');
      return;
    }

    this.clientSideRowModel.expandOrCollapseAll(false);
  };

  GridApi.prototype.getToolPanelInstance = function (id) {
    return this.gridCore.getToolPanelInstance(id);
  };

  GridApi.prototype.addVirtualRowListener = function (eventName, rowIndex, callback) {
    if (typeof eventName !== 'string') {
      console.warn('ag-Grid: addVirtualRowListener is deprecated, please use addRenderedRowListener.');
    }

    this.addRenderedRowListener(eventName, rowIndex, callback);
  };

  GridApi.prototype.addRenderedRowListener = function (eventName, rowIndex, callback) {
    if (eventName === 'virtualRowSelected') {
      console.warn("ag-Grid: event virtualRowSelected is deprecated, to register for individual row\n                selection events, add a listener directly to the row node.");
    }

    this.rowRenderer.addRenderedRowListener(eventName, rowIndex, callback);
  };

  GridApi.prototype.setQuickFilter = function (newFilter) {
    this.filterManager.setQuickFilter(newFilter);
  };

  GridApi.prototype.selectIndex = function (index, tryMulti, suppressEvents) {
    console.warn('ag-Grid: do not use api for selection, call node.setSelected(value) instead');

    if (suppressEvents) {
      console.warn('ag-Grid: suppressEvents is no longer supported, stop listening for the event if you no longer want it');
    }

    this.selectionController.selectIndex(index, tryMulti);
  };

  GridApi.prototype.deselectIndex = function (index, suppressEvents) {
    if (suppressEvents === void 0) {
      suppressEvents = false;
    }

    console.warn('ag-Grid: do not use api for selection, call node.setSelected(value) instead');

    if (suppressEvents) {
      console.warn('ag-Grid: suppressEvents is no longer supported, stop listening for the event if you no longer want it');
    }

    this.selectionController.deselectIndex(index);
  };

  GridApi.prototype.selectNode = function (node, tryMulti, suppressEvents) {
    if (tryMulti === void 0) {
      tryMulti = false;
    }

    if (suppressEvents === void 0) {
      suppressEvents = false;
    }

    console.warn('ag-Grid: API for selection is deprecated, call node.setSelected(value) instead');

    if (suppressEvents) {
      console.warn('ag-Grid: suppressEvents is no longer supported, stop listening for the event if you no longer want it');
    }

    node.setSelectedParams({
      newValue: true,
      clearSelection: !tryMulti
    });
  };

  GridApi.prototype.deselectNode = function (node, suppressEvents) {
    if (suppressEvents === void 0) {
      suppressEvents = false;
    }

    console.warn('ag-Grid: API for selection is deprecated, call node.setSelected(value) instead');

    if (suppressEvents) {
      console.warn('ag-Grid: suppressEvents is no longer supported, stop listening for the event if you no longer want it');
    }

    node.setSelectedParams({
      newValue: false
    });
  };

  GridApi.prototype.selectAll = function () {
    this.selectionController.selectAllRowNodes();
  };

  GridApi.prototype.deselectAll = function () {
    this.selectionController.deselectAllRowNodes();
  };

  GridApi.prototype.selectAllFiltered = function () {
    this.selectionController.selectAllRowNodes(true);
  };

  GridApi.prototype.deselectAllFiltered = function () {
    this.selectionController.deselectAllRowNodes(true);
  };

  GridApi.prototype.recomputeAggregates = function () {
    if (_.missing(this.clientSideRowModel)) {
      console.warn('cannot call recomputeAggregates unless using normal row model');
    }

    console.warn("recomputeAggregates is deprecated, please call api.refreshClientSideRowModel('aggregate') instead");
    this.clientSideRowModel.refreshModel({
      step: Constants.STEP_AGGREGATE
    });
  };

  GridApi.prototype.sizeColumnsToFit = function () {
    this.gridPanel.sizeColumnsToFit();
  };

  GridApi.prototype.showLoadingOverlay = function () {
    this.gridPanel.showLoadingOverlay();
  };

  GridApi.prototype.showNoRowsOverlay = function () {
    this.gridPanel.showNoRowsOverlay();
  };

  GridApi.prototype.hideOverlay = function () {
    this.gridPanel.hideOverlay();
  };

  GridApi.prototype.isNodeSelected = function (node) {
    console.warn('ag-Grid: no need to call api.isNodeSelected(), just call node.isSelected() instead');
    return node.isSelected();
  };

  GridApi.prototype.getSelectedNodesById = function () {
    console.error('ag-Grid: since version 3.4, getSelectedNodesById no longer exists, use getSelectedNodes() instead');
    return null;
  };

  GridApi.prototype.getSelectedNodes = function () {
    return this.selectionController.getSelectedNodes();
  };

  GridApi.prototype.getSelectedRows = function () {
    return this.selectionController.getSelectedRows();
  };

  GridApi.prototype.getBestCostNodeSelection = function () {
    return this.selectionController.getBestCostNodeSelection();
  };

  GridApi.prototype.getRenderedNodes = function () {
    return this.rowRenderer.getRenderedNodes();
  };

  GridApi.prototype.ensureColIndexVisible = function (index) {
    console.warn('ag-Grid: ensureColIndexVisible(index) no longer supported, use ensureColumnVisible(colKey) instead.');
  };

  GridApi.prototype.ensureColumnVisible = function (key) {
    this.gridPanel.ensureColumnVisible(key);
  }; // Valid values for position are bottom, middle and top


  GridApi.prototype.ensureIndexVisible = function (index, position) {
    this.gridPanel.ensureIndexVisible(index, position);
  }; // Valid values for position are bottom, middle and top


  GridApi.prototype.ensureNodeVisible = function (comparator, position) {
    this.gridCore.ensureNodeVisible(comparator, position);
  };

  GridApi.prototype.forEachLeafNode = function (callback) {
    if (_.missing(this.clientSideRowModel)) {
      console.warn('cannot call forEachNode unless using normal row model');
    }

    this.clientSideRowModel.forEachLeafNode(callback);
  };

  GridApi.prototype.forEachNode = function (callback) {
    this.rowModel.forEachNode(callback);
  };

  GridApi.prototype.forEachNodeAfterFilter = function (callback) {
    if (_.missing(this.clientSideRowModel)) {
      console.warn('cannot call forEachNodeAfterFilter unless using normal row model');
    }

    this.clientSideRowModel.forEachNodeAfterFilter(callback);
  };

  GridApi.prototype.forEachNodeAfterFilterAndSort = function (callback) {
    if (_.missing(this.clientSideRowModel)) {
      console.warn('cannot call forEachNodeAfterFilterAndSort unless using normal row model');
    }

    this.clientSideRowModel.forEachNodeAfterFilterAndSort(callback);
  };

  GridApi.prototype.getFilterApiForColDef = function (colDef) {
    console.warn('ag-grid API method getFilterApiForColDef deprecated, use getFilterApi instead');
    return this.getFilterInstance(colDef);
  };

  GridApi.prototype.getFilterInstance = function (key) {
    var column = this.columnController.getPrimaryColumn(key);

    if (column) {
      return this.filterManager.getFilterComponent(column, 'NO_UI').resolveNow(null, function (filterComp) {
        return filterComp;
      });
    }
  };

  GridApi.prototype.getFilterApi = function (key) {
    console.warn('ag-Grid: getFilterApi is deprecated, use getFilterInstance instead');
    return this.getFilterInstance(key);
  };

  GridApi.prototype.destroyFilter = function (key) {
    var column = this.columnController.getPrimaryColumn(key);

    if (column) {
      return this.filterManager.destroyFilter(column, "filterDestroyed");
    }
  };

  GridApi.prototype.getStatusPanel = function (key) {
    if (this.statusBarService) {
      return this.statusBarService.getStatusPanel(key);
    }
  };

  GridApi.prototype.getColumnDef = function (key) {
    var column = this.columnController.getPrimaryColumn(key);

    if (column) {
      return column.getColDef();
    } else {
      return null;
    }
  };

  GridApi.prototype.onFilterChanged = function () {
    this.filterManager.onFilterChanged();
  };

  GridApi.prototype.onSortChanged = function () {
    this.sortController.onSortChanged();
  };

  GridApi.prototype.setSortModel = function (sortModel, source) {
    if (source === void 0) {
      source = "api";
    }

    this.sortController.setSortModel(sortModel, source);
  };

  GridApi.prototype.getSortModel = function () {
    return this.sortController.getSortModel();
  };

  GridApi.prototype.setFilterModel = function (model) {
    this.filterManager.setFilterModel(model);
  };

  GridApi.prototype.getFilterModel = function () {
    return this.filterManager.getFilterModel();
  };

  GridApi.prototype.getFocusedCell = function () {
    return this.focusedCellController.getFocusedCell();
  };

  GridApi.prototype.clearFocusedCell = function () {
    return this.focusedCellController.clearFocusedCell();
  };

  GridApi.prototype.setFocusedCell = function (rowIndex, colKey, floating) {
    this.focusedCellController.setFocusedCell(rowIndex, colKey, floating, true);
  };

  GridApi.prototype.setSuppressRowDrag = function (value) {
    this.gridOptionsWrapper.setProperty(GridOptionsWrapper.PROP_SUPPRESS_ROW_DRAG, value);
  };

  GridApi.prototype.setHeaderHeight = function (headerHeight) {
    this.gridOptionsWrapper.setProperty(GridOptionsWrapper.PROP_HEADER_HEIGHT, headerHeight);
    this.doLayout();
  };

  GridApi.prototype.setGridAutoHeight = function (gridAutoHeight) {
    console.warn('api.setGridAutoHeight(boolean) is deprecated, please use api.setDomLayout() instead');
    this.setDomLayout(gridAutoHeight ? 'autoHeight' : 'normal');
  };

  GridApi.prototype.setDomLayout = function (domLayout) {
    this.gridOptionsWrapper.setProperty(GridOptionsWrapper.PROP_DOM_LAYOUT, domLayout);
  };

  GridApi.prototype.setEnableCellTextSelection = function (selectable) {
    this.gridPanel.setCellTextSelection(selectable);
  };

  GridApi.prototype.setGroupHeaderHeight = function (headerHeight) {
    this.gridOptionsWrapper.setProperty(GridOptionsWrapper.PROP_GROUP_HEADER_HEIGHT, headerHeight);
    this.doLayout();
  };

  GridApi.prototype.setFloatingFiltersHeight = function (headerHeight) {
    this.gridOptionsWrapper.setProperty(GridOptionsWrapper.PROP_FLOATING_FILTERS_HEIGHT, headerHeight);
    this.doLayout();
  };

  GridApi.prototype.setPivotGroupHeaderHeight = function (headerHeight) {
    this.gridOptionsWrapper.setProperty(GridOptionsWrapper.PROP_PIVOT_GROUP_HEADER_HEIGHT, headerHeight);
    this.doLayout();
  };

  GridApi.prototype.setPivotHeaderHeight = function (headerHeight) {
    this.gridOptionsWrapper.setProperty(GridOptionsWrapper.PROP_PIVOT_HEADER_HEIGHT, headerHeight);
    this.doLayout();
  };

  GridApi.prototype.isSideBarVisible = function () {
    return this.gridCore.isSideBarVisible();
  };

  GridApi.prototype.setSideBarVisible = function (show) {
    this.gridCore.setSideBarVisible(show);
  };

  GridApi.prototype.setSideBarPosition = function (position) {
    this.gridCore.setSideBarPosition(position);
  };

  GridApi.prototype.showToolPanel = function (show) {
    console.warn("ag-grid: from v19 api.showToolPanel has been deprecated in favour of api.setSideBarVisible");
    this.setSideBarVisible(show);
  };

  GridApi.prototype.openToolPanel = function (key) {
    this.gridCore.openToolPanel(key);
  };

  GridApi.prototype.closeToolPanel = function () {
    this.gridCore.closeToolPanel();
  };

  GridApi.prototype.getOpenedToolPanel = function () {
    return this.gridCore.getOpenedToolPanel();
  };

  GridApi.prototype.getSideBar = function () {
    return this.gridCore.getSideBar();
  };

  GridApi.prototype.setSideBar = function (def) {
    return this.gridCore.setSideBar(def);
  };

  GridApi.prototype.setSuppressClipboardPaste = function (value) {
    this.gridOptionsWrapper.setProperty(GridOptionsWrapper.PROP_SUPPRESS_CLIPBOARD_PASTE, value);
  };

  GridApi.prototype.isToolPanelShowing = function () {
    return this.gridCore.isToolPanelShowing();
  };

  GridApi.prototype.doLayout = function () {
    this.gridPanel.checkViewportAndScrolls();
  };

  GridApi.prototype.resetRowHeights = function () {
    if (_.exists(this.clientSideRowModel)) {
      this.clientSideRowModel.resetRowHeights();
    }
  };

  GridApi.prototype.setGroupRemoveSingleChildren = function (value) {
    this.gridOptionsWrapper.setProperty(GridOptionsWrapper.PROP_GROUP_REMOVE_SINGLE_CHILDREN, value);
  };

  GridApi.prototype.setGroupRemoveLowestSingleChildren = function (value) {
    this.gridOptionsWrapper.setProperty(GridOptionsWrapper.PROP_GROUP_REMOVE_LOWEST_SINGLE_CHILDREN, value);
  };

  GridApi.prototype.onRowHeightChanged = function () {
    if (_.exists(this.clientSideRowModel)) {
      this.clientSideRowModel.onRowHeightChanged();
    }
  };

  GridApi.prototype.getValue = function (colKey, rowNode) {
    var column = this.columnController.getPrimaryColumn(colKey);

    if (_.missing(column)) {
      column = this.columnController.getGridColumn(colKey);
    }

    if (_.missing(column)) {
      return null;
    } else {
      return this.valueService.getValue(column, rowNode);
    }
  };

  GridApi.prototype.addEventListener = function (eventType, listener) {
    var async = this.gridOptionsWrapper.useAsyncEvents();
    this.eventService.addEventListener(eventType, listener, async);
  };

  GridApi.prototype.addGlobalListener = function (listener) {
    var async = this.gridOptionsWrapper.useAsyncEvents();
    this.eventService.addGlobalListener(listener, async);
  };

  GridApi.prototype.removeEventListener = function (eventType, listener) {
    var async = this.gridOptionsWrapper.useAsyncEvents();
    this.eventService.removeEventListener(eventType, listener, async);
  };

  GridApi.prototype.removeGlobalListener = function (listener) {
    var async = this.gridOptionsWrapper.useAsyncEvents();
    this.eventService.removeGlobalListener(listener, async);
  };

  GridApi.prototype.dispatchEvent = function (event) {
    this.eventService.dispatchEvent(event);
  };

  GridApi.prototype.destroy = function () {
    // destroy the UI first (as they use the services)
    this.gridCore.destroy(); // destroy the services

    this.context.destroy();
  };

  GridApi.prototype.resetQuickFilter = function () {
    this.rowModel.forEachNode(function (node) {
      return node.quickFilterAggregateText = null;
    });
  };

  GridApi.prototype.getRangeSelections = function () {
    console.warn("ag-Grid: in v20.1.x, api.getRangeSelections() is gone, please use getCellRanges() instead.\n        We had to change how cell selections works a small bit to allow charting to integrate. The return type of\n        getCellRanges() is a bit different, please check the ag-Grid documentation.");
    return null;
  };

  GridApi.prototype.getCellRanges = function () {
    if (this.rangeController) {
      return this.rangeController.getCellRanges();
    } else {
      console.warn('ag-Grid: cell range selection is only available in ag-Grid Enterprise');
      return null;
    }
  };

  GridApi.prototype.camelCaseToHumanReadable = function (camelCase) {
    return _.camelCaseToHumanText(camelCase);
  };

  GridApi.prototype.addRangeSelection = function (deprecatedNoLongerUsed) {
    console.warn('ag-Grid: As of version 21.x, range selection changed slightly to allow charting integration. Please call api.addCellRange() instead of api.addRangeSelection()');
  };

  GridApi.prototype.addCellRange = function (params) {
    if (!this.rangeController) {
      console.warn('ag-Grid: cell range selection is only available in ag-Grid Enterprise');
    }

    this.rangeController.addCellRange(params);
  };

  GridApi.prototype.clearRangeSelection = function () {
    if (!this.rangeController) {
      console.warn('ag-Grid: cell range selection is only available in ag-Grid Enterprise');
    }

    this.rangeController.removeAllCellRanges();
  };

  GridApi.prototype.undoCellEditing = function () {
    this.undoRedoService.undo();
  };

  GridApi.prototype.redoCellEditing = function () {
    this.undoRedoService.redo();
  };

  GridApi.prototype.getChartModels = function () {
    if (ModuleRegistry.assertRegistered(exports.ModuleNames.RangeSelectionModule, 'api.getChartModels') && ModuleRegistry.assertRegistered(exports.ModuleNames.GridChartsModule, 'api.getChartModels')) {
      return this.chartService.getChartModels();
    }
  };

  GridApi.prototype.createRangeChart = function (params) {
    if (ModuleRegistry.assertRegistered(exports.ModuleNames.RangeSelectionModule, 'api.createRangeChart') && ModuleRegistry.assertRegistered(exports.ModuleNames.GridChartsModule, 'api.createRangeChart')) {
      return this.chartService.createRangeChart(params);
    }
  };

  GridApi.prototype.createPivotChart = function (params) {
    if (ModuleRegistry.assertRegistered(exports.ModuleNames.RangeSelectionModule, 'api.createPivotChart') && ModuleRegistry.assertRegistered(exports.ModuleNames.GridChartsModule, 'api.createPivotChart')) {
      return this.chartService.createPivotChart(params);
    }
  };

  GridApi.prototype.copySelectedRowsToClipboard = function (includeHeader, columnKeys) {
    if (!this.clipboardService) {
      console.warn('ag-Grid: clipboard is only available in ag-Grid Enterprise');
    }

    this.clipboardService.copySelectedRowsToClipboard(includeHeader, columnKeys);
  };

  GridApi.prototype.copySelectedRangeToClipboard = function (includeHeader) {
    if (!this.clipboardService) {
      console.warn('ag-Grid: clipboard is only available in ag-Grid Enterprise');
    }

    this.clipboardService.copySelectedRangeToClipboard(includeHeader);
  };

  GridApi.prototype.copySelectedRangeDown = function () {
    if (!this.clipboardService) {
      console.warn('ag-Grid: clipboard is only available in ag-Grid Enterprise');
    }

    this.clipboardService.copyRangeDown();
  };

  GridApi.prototype.showColumnMenuAfterButtonClick = function (colKey, buttonElement) {
    // use grid column so works with pivot mode
    var column = this.columnController.getGridColumn(colKey);
    this.menuFactory.showMenuAfterButtonClick(column, buttonElement);
  };

  GridApi.prototype.showColumnMenuAfterMouseClick = function (colKey, mouseEvent) {
    // use grid column so works with pivot mode
    var column = this.columnController.getGridColumn(colKey);
    this.menuFactory.showMenuAfterMouseEvent(column, mouseEvent);
  };

  GridApi.prototype.hidePopupMenu = function () {
    // hide the context menu if in enterprise
    if (this.contextMenuFactory) {
      this.contextMenuFactory.hideActiveMenu();
    } // and hide the column menu always


    this.menuFactory.hideActiveMenu();
  };

  GridApi.prototype.setPopupParent = function (ePopupParent) {
    this.gridOptionsWrapper.setProperty(GridOptionsWrapper.PROP_POPUP_PARENT, ePopupParent);
  };

  GridApi.prototype.tabToNextCell = function () {
    return this.rowRenderer.tabToNextCell(false);
  };

  GridApi.prototype.tabToPreviousCell = function () {
    return this.rowRenderer.tabToNextCell(true);
  };

  GridApi.prototype.getCellRendererInstances = function (params) {
    if (params === void 0) {
      params = {};
    }

    return this.rowRenderer.getCellRendererInstances(params);
  };

  GridApi.prototype.getCellEditorInstances = function (params) {
    if (params === void 0) {
      params = {};
    }

    return this.rowRenderer.getCellEditorInstances(params);
  };

  GridApi.prototype.getEditingCells = function () {
    return this.rowRenderer.getEditingCells();
  };

  GridApi.prototype.stopEditing = function (cancel) {
    if (cancel === void 0) {
      cancel = false;
    }

    this.rowRenderer.stopEditing(cancel);
  };

  GridApi.prototype.startEditingCell = function (params) {
    var column = this.columnController.getGridColumn(params.colKey);

    if (!column) {
      console.warn("ag-Grid: no column found for " + params.colKey);
      return;
    }

    var cellPosition = {
      rowIndex: params.rowIndex,
      rowPinned: params.rowPinned,
      column: column
    };

    var notPinned = _.missing(params.rowPinned);

    if (notPinned) {
      this.gridPanel.ensureIndexVisible(params.rowIndex);
    }

    this.rowRenderer.startEditingCell(cellPosition, params.keyPress, params.charPress);
  };

  GridApi.prototype.addAggFunc = function (key, aggFunc) {
    if (this.aggFuncService) {
      this.aggFuncService.addAggFunc(key, aggFunc);
    }
  };

  GridApi.prototype.addAggFuncs = function (aggFuncs) {
    if (this.aggFuncService) {
      this.aggFuncService.addAggFuncs(aggFuncs);
    }
  };

  GridApi.prototype.clearAggFuncs = function () {
    if (this.aggFuncService) {
      this.aggFuncService.clear();
    }
  };

  GridApi.prototype.updateRowData = function (rowDataTransaction) {
    var res = null;

    if (this.clientSideRowModel) {
      res = this.clientSideRowModel.updateRowData(rowDataTransaction);
    } else if (this.infiniteRowModel) {
      this.infiniteRowModel.updateRowData(rowDataTransaction);
    } else {
      console.error('ag-Grid: updateRowData() only works with ClientSideRowModel and InfiniteRowModel.');
    } // refresh all the full width rows


    this.rowRenderer.refreshFullWidthRows(); // do change detection for all present cells

    if (!this.gridOptionsWrapper.isSuppressChangeDetection()) {
      this.rowRenderer.refreshCells();
    }

    return res;
  };

  GridApi.prototype.batchUpdateRowData = function (rowDataTransaction, callback) {
    if (!this.clientSideRowModel) {
      console.error('ag-Grid: api.batchUpdateRowData() only works with ClientSideRowModel.');
      return;
    }

    this.clientSideRowModel.batchUpdateRowData(rowDataTransaction, callback);
  };

  GridApi.prototype.insertItemsAtIndex = function (index, items, skipRefresh) {
    console.warn('ag-Grid: insertItemsAtIndex() is deprecated, use updateRowData(transaction) instead.');
    this.updateRowData({
      add: items,
      addIndex: index,
      update: null,
      remove: null
    });
  };

  GridApi.prototype.removeItems = function (rowNodes, skipRefresh) {
    console.warn('ag-Grid: removeItems() is deprecated, use updateRowData(transaction) instead.');
    var dataToRemove = rowNodes.map(function (rowNode) {
      return rowNode.data;
    });
    this.updateRowData({
      add: null,
      addIndex: null,
      update: null,
      remove: dataToRemove
    });
  };

  GridApi.prototype.addItems = function (items, skipRefresh) {
    console.warn('ag-Grid: addItems() is deprecated, use updateRowData(transaction) instead.');
    this.updateRowData({
      add: items,
      addIndex: null,
      update: null,
      remove: null
    });
  };

  GridApi.prototype.refreshVirtualPageCache = function () {
    console.warn('ag-Grid: refreshVirtualPageCache() is now called refreshInfiniteCache(), please call refreshInfiniteCache() instead');
    this.refreshInfiniteCache();
  };

  GridApi.prototype.refreshInfinitePageCache = function () {
    console.warn('ag-Grid: refreshInfinitePageCache() is now called refreshInfiniteCache(), please call refreshInfiniteCache() instead');
    this.refreshInfiniteCache();
  };

  GridApi.prototype.refreshInfiniteCache = function () {
    if (this.infiniteRowModel) {
      this.infiniteRowModel.refreshCache();
    } else {
      console.warn("ag-Grid: api.refreshInfiniteCache is only available when rowModelType='infinite'.");
    }
  };

  GridApi.prototype.purgeVirtualPageCache = function () {
    console.warn('ag-Grid: purgeVirtualPageCache() is now called purgeInfiniteCache(), please call purgeInfiniteCache() instead');
    this.purgeInfinitePageCache();
  };

  GridApi.prototype.purgeInfinitePageCache = function () {
    console.warn('ag-Grid: purgeInfinitePageCache() is now called purgeInfiniteCache(), please call purgeInfiniteCache() instead');
    this.purgeInfiniteCache();
  };

  GridApi.prototype.purgeInfiniteCache = function () {
    if (this.infiniteRowModel) {
      this.infiniteRowModel.purgeCache();
    } else {
      console.warn("ag-Grid: api.purgeInfiniteCache is only available when rowModelType='infinite'.");
    }
  };
  /** @deprecated */


  GridApi.prototype.purgeEnterpriseCache = function (route) {
    console.warn("ag-grid: since version 18.x, api.purgeEnterpriseCache() should be replaced with api.purgeServerSideCache()");
    this.purgeServerSideCache(route);
  };

  GridApi.prototype.purgeServerSideCache = function (route) {
    if (this.serverSideRowModel) {
      this.serverSideRowModel.purgeCache(route);
    } else {
      console.warn("ag-Grid: api.purgeServerSideCache is only available when rowModelType='enterprise'.");
    }
  };

  GridApi.prototype.getVirtualRowCount = function () {
    console.warn('ag-Grid: getVirtualRowCount() is now called getInfiniteRowCount(), please call getInfiniteRowCount() instead');
    return this.getInfiniteRowCount();
  };

  GridApi.prototype.getInfiniteRowCount = function () {
    if (this.infiniteRowModel) {
      return this.infiniteRowModel.getVirtualRowCount();
    } else {
      console.warn("ag-Grid: api.getVirtualRowCount is only available when rowModelType='virtual'.");
    }
  };

  GridApi.prototype.isMaxRowFound = function () {
    if (this.infiniteRowModel) {
      return this.infiniteRowModel.isMaxRowFound();
    } else {
      console.warn("ag-Grid: api.isMaxRowFound is only available when rowModelType='virtual'.");
    }
  };

  GridApi.prototype.setVirtualRowCount = function (rowCount, maxRowFound) {
    console.warn('ag-Grid: setVirtualRowCount() is now called setInfiniteRowCount(), please call setInfiniteRowCount() instead');
    this.setInfiniteRowCount(rowCount, maxRowFound);
  };

  GridApi.prototype.setInfiniteRowCount = function (rowCount, maxRowFound) {
    if (this.infiniteRowModel) {
      this.infiniteRowModel.setVirtualRowCount(rowCount, maxRowFound);
    } else {
      console.warn("ag-Grid: api.setVirtualRowCount is only available when rowModelType='virtual'.");
    }
  };

  GridApi.prototype.getVirtualPageState = function () {
    console.warn('ag-Grid: getVirtualPageState() is now called getCacheBlockState(), please call getCacheBlockState() instead');
    return this.getCacheBlockState();
  };

  GridApi.prototype.getInfinitePageState = function () {
    console.warn('ag-Grid: getInfinitePageState() is now called getCacheBlockState(), please call getCacheBlockState() instead');
    return this.getCacheBlockState();
  };

  GridApi.prototype.getCacheBlockState = function () {
    if (this.infiniteRowModel) {
      return this.infiniteRowModel.getBlockState();
    } else if (this.serverSideRowModel) {
      return this.serverSideRowModel.getBlockState();
    } else {
      console.warn("ag-Grid: api.getCacheBlockState() is only available when rowModelType='infinite' or rowModelType='serverSide'.");
    }
  };

  GridApi.prototype.checkGridSize = function () {
    this.gridPanel.setHeaderAndFloatingHeights();
  };

  GridApi.prototype.getFirstRenderedRow = function () {
    console.warn('in ag-Grid v12, getFirstRenderedRow() was renamed to getFirstDisplayedRow()');
    return this.getFirstDisplayedRow();
  };

  GridApi.prototype.getFirstDisplayedRow = function () {
    return this.rowRenderer.getFirstVirtualRenderedRow();
  };

  GridApi.prototype.getLastRenderedRow = function () {
    console.warn('in ag-Grid v12, getLastRenderedRow() was renamed to getLastDisplayedRow()');
    return this.getLastDisplayedRow();
  };

  GridApi.prototype.getLastDisplayedRow = function () {
    return this.rowRenderer.getLastVirtualRenderedRow();
  };

  GridApi.prototype.getDisplayedRowAtIndex = function (index) {
    return this.rowModel.getRow(index);
  };

  GridApi.prototype.getDisplayedRowCount = function () {
    return this.rowModel.getRowCount();
  };

  GridApi.prototype.paginationIsLastPageFound = function () {
    return this.paginationProxy.isLastPageFound();
  };

  GridApi.prototype.paginationGetPageSize = function () {
    return this.paginationProxy.getPageSize();
  };

  GridApi.prototype.paginationSetPageSize = function (size) {
    this.gridOptionsWrapper.setProperty('paginationPageSize', size);
  };

  GridApi.prototype.paginationGetCurrentPage = function () {
    return this.paginationProxy.getCurrentPage();
  };

  GridApi.prototype.paginationGetTotalPages = function () {
    return this.paginationProxy.getTotalPages();
  };

  GridApi.prototype.paginationGetRowCount = function () {
    return this.paginationProxy.getMasterRowCount();
  };

  GridApi.prototype.paginationGoToNextPage = function () {
    this.paginationProxy.goToNextPage();
  };

  GridApi.prototype.paginationGoToPreviousPage = function () {
    this.paginationProxy.goToPreviousPage();
  };

  GridApi.prototype.paginationGoToFirstPage = function () {
    this.paginationProxy.goToFirstPage();
  };

  GridApi.prototype.paginationGoToLastPage = function () {
    this.paginationProxy.goToLastPage();
  };

  GridApi.prototype.paginationGoToPage = function (page) {
    this.paginationProxy.goToPage(page);
  };

  __decorate$V([Optional('immutableService')], GridApi.prototype, "immutableService", void 0);

  __decorate$V([Optional('csvCreator')], GridApi.prototype, "csvCreator", void 0);

  __decorate$V([Optional('excelCreator')], GridApi.prototype, "excelCreator", void 0);

  __decorate$V([Autowired('rowRenderer')], GridApi.prototype, "rowRenderer", void 0);

  __decorate$V([Autowired('filterManager')], GridApi.prototype, "filterManager", void 0);

  __decorate$V([Autowired('columnController')], GridApi.prototype, "columnController", void 0);

  __decorate$V([Autowired('selectionController')], GridApi.prototype, "selectionController", void 0);

  __decorate$V([Autowired('gridOptionsWrapper')], GridApi.prototype, "gridOptionsWrapper", void 0);

  __decorate$V([Autowired('valueService')], GridApi.prototype, "valueService", void 0);

  __decorate$V([Autowired('alignedGridsService')], GridApi.prototype, "alignedGridsService", void 0);

  __decorate$V([Autowired('eventService')], GridApi.prototype, "eventService", void 0);

  __decorate$V([Autowired('pinnedRowModel')], GridApi.prototype, "pinnedRowModel", void 0);

  __decorate$V([Autowired('context')], GridApi.prototype, "context", void 0);

  __decorate$V([Autowired('rowModel')], GridApi.prototype, "rowModel", void 0);

  __decorate$V([Autowired('sortController')], GridApi.prototype, "sortController", void 0);

  __decorate$V([Autowired('paginationProxy')], GridApi.prototype, "paginationProxy", void 0);

  __decorate$V([Autowired('focusedCellController')], GridApi.prototype, "focusedCellController", void 0);

  __decorate$V([Optional('rangeController')], GridApi.prototype, "rangeController", void 0);

  __decorate$V([Optional('clipboardService')], GridApi.prototype, "clipboardService", void 0);

  __decorate$V([Optional('aggFuncService')], GridApi.prototype, "aggFuncService", void 0);

  __decorate$V([Autowired('menuFactory')], GridApi.prototype, "menuFactory", void 0);

  __decorate$V([Optional('contextMenuFactory')], GridApi.prototype, "contextMenuFactory", void 0);

  __decorate$V([Autowired('cellRendererFactory')], GridApi.prototype, "cellRendererFactory", void 0);

  __decorate$V([Autowired('valueCache')], GridApi.prototype, "valueCache", void 0);

  __decorate$V([Autowired('animationFrameService')], GridApi.prototype, "animationFrameService", void 0);

  __decorate$V([Optional('statusBarService')], GridApi.prototype, "statusBarService", void 0);

  __decorate$V([Optional('chartService')], GridApi.prototype, "chartService", void 0);

  __decorate$V([Optional('undoRedoService')], GridApi.prototype, "undoRedoService", void 0);

  __decorate$V([PostConstruct], GridApi.prototype, "init", null);

  GridApi = __decorate$V([Bean('gridApi')], GridApi);
  return GridApi;
}();
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __decorate$W = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var __param$7 = undefined && undefined.__param || function (paramIndex, decorator) {
  return function (target, key) {
    decorator(target, key, paramIndex);
  };
};

var ExpressionService =
/** @class */
function () {
  function ExpressionService() {
    this.expressionToFunctionCache = {};
  }

  ExpressionService.prototype.setBeans = function (loggerFactory) {
    this.logger = loggerFactory.create('ExpressionService');
  };

  ExpressionService.prototype.evaluate = function (expressionOrFunc, params) {
    if (typeof expressionOrFunc === 'function') {
      // valueGetter is a function, so just call it
      var func = expressionOrFunc;
      return func(params);
    } else if (typeof expressionOrFunc === 'string') {
      // valueGetter is an expression, so execute the expression
      var expression = expressionOrFunc;
      return this.evaluateExpression(expression, params);
    } else {
      console.error('ag-Grid: value should be either a string or a function', expressionOrFunc);
    }
  };

  ExpressionService.prototype.evaluateExpression = function (expression, params) {
    try {
      var javaScriptFunction = this.createExpressionFunction(expression); // the params don't have all these values, rather we add every possible
      // value a params can have, which makes whatever is in the params available.

      var result = javaScriptFunction(params.value, params.context, params.oldValue, params.newValue, params.value, params.node, params.data, params.colDef, params.rowIndex, params.api, params.columnApi, params.getValue, params.column, params.columnGroup);
      return result;
    } catch (e) {
      // the expression failed, which can happen, as it's the client that
      // provides the expression. so print a nice message
      // tslint:disable-next-line
      console.log('Processing of the expression failed'); // tslint:disable-next-line

      console.log('Expression = ' + expression); // tslint:disable-next-line

      console.log('Params =', params); // tslint:disable-next-line

      console.log('Exception = ' + e);
      return null;
    }
  };

  ExpressionService.prototype.createExpressionFunction = function (expression) {
    // check cache first
    if (this.expressionToFunctionCache[expression]) {
      return this.expressionToFunctionCache[expression];
    } // if not found in cache, return the function


    var functionBody = this.createFunctionBody(expression);
    var theFunction = new Function('x, ctx, oldValue, newValue, value, node, data, colDef, rowIndex, api, columnApi, getValue, column, columnGroup', functionBody); // store in cache

    this.expressionToFunctionCache[expression] = theFunction;
    return theFunction;
  };

  ExpressionService.prototype.createFunctionBody = function (expression) {
    // if the expression has the 'return' word in it, then use as is,
    // if not, then wrap it with return and ';' to make a function
    if (expression.indexOf('return') >= 0) {
      return expression;
    } else {
      return 'return ' + expression + ';';
    }
  };

  __decorate$W([__param$7(0, Qualifier('loggerFactory'))], ExpressionService.prototype, "setBeans", null);

  ExpressionService = __decorate$W([Bean('expressionService')], ExpressionService);
  return ExpressionService;
}();
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __decorate$X = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var TemplateService =
/** @class */
function () {
  function TemplateService() {
    this.templateCache = {};
    this.waitingCallbacks = {};
  } // returns the template if it is loaded, or null if it is not loaded
  // but will call the callback when it is loaded


  TemplateService.prototype.getTemplate = function (url, callback) {
    var templateFromCache = this.templateCache[url];

    if (templateFromCache) {
      return templateFromCache;
    }

    var callbackList = this.waitingCallbacks[url];
    var that = this;

    if (!callbackList) {
      // first time this was called, so need a new list for callbacks
      callbackList = [];
      this.waitingCallbacks[url] = callbackList; // and also need to do the http request

      var client = new XMLHttpRequest();

      client.onload = function () {
        that.handleHttpResult(this, url);
      };

      client.open("GET", url);
      client.send();
    } // add this callback


    if (callback) {
      callbackList.push(callback);
    } // caller needs to wait for template to load, so return null


    return null;
  };

  TemplateService.prototype.handleHttpResult = function (httpResult, url) {
    if (httpResult.status !== 200 || httpResult.response === null) {
      console.warn("Unable to get template error " + httpResult.status + " - " + url);
      return;
    } // response success, so process it
    // in IE9 the response is in - responseText


    this.templateCache[url] = httpResult.response || httpResult.responseText; // inform all listeners that this is now in the cache

    var callbacks = this.waitingCallbacks[url];

    for (var i = 0; i < callbacks.length; i++) {
      var callback = callbacks[i]; // we could pass the callback the response, however we know the client of this code
      // is the cell renderer, and it passes the 'cellRefresh' method in as the callback
      // which doesn't take any parameters.

      callback();
    }

    if (this.$scope) {
      var that_1 = this;
      window.setTimeout(function () {
        that_1.$scope.$apply();
      }, 0);
    }
  };

  __decorate$X([Autowired('$scope')], TemplateService.prototype, "$scope", void 0);

  TemplateService = __decorate$X([Bean('templateService')], TemplateService);
  return TemplateService;
}();
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __decorate$Y = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var PopupService =
/** @class */
function () {
  function PopupService() {
    this.popupList = [];
  }

  PopupService.prototype.registerGridCore = function (gridCore) {
    this.gridCore = gridCore;
  };

  PopupService.prototype.getDocument = function () {
    return this.gridOptionsWrapper.getDocument();
  };

  PopupService.prototype.getPopupParent = function () {
    var ePopupParent = this.gridOptionsWrapper.getPopupParent();

    if (ePopupParent) {
      return ePopupParent;
    }

    return this.gridCore.getRootGui();
  };

  PopupService.prototype.positionPopupForMenu = function (params) {
    var sourceRect = params.eventSource.getBoundingClientRect();
    var eDocument = this.getDocument();
    var popupParent = this.getPopupParent();
    var parentRect;

    if (popupParent === eDocument.body) {
      parentRect = eDocument.documentElement.getBoundingClientRect();
    } else {
      parentRect = popupParent.getBoundingClientRect();
    }

    var y = sourceRect.top - parentRect.top;
    y = this.keepYWithinBounds(params, y);
    var minWidth = params.ePopup.clientWidth > 0 ? params.ePopup.clientWidth : 200;
    params.ePopup.style.minWidth = minWidth + "px";
    var widthOfParent = parentRect.right - parentRect.left;
    var maxX = widthOfParent - minWidth; // the x position of the popup depends on RTL or LTR. for normal cases, LTR, we put the child popup
    // to the right, unless it doesn't fit and we then put it to the left. for RTL it's the other way around,
    // we try place it first to the left, and then if not to the right.

    var x;

    if (this.gridOptionsWrapper.isEnableRtl()) {
      // for RTL, try left first
      x = xLeftPosition();

      if (x < 0) {
        x = xRightPosition();
      }

      if (x > maxX) {
        x = 0;
      }
    } else {
      // for LTR, try right first
      x = xRightPosition();

      if (x > maxX) {
        x = xLeftPosition();
      }

      if (x < 0) {
        x = 0;
      }
    }

    params.ePopup.style.left = x + "px";
    params.ePopup.style.top = y + "px";

    function xRightPosition() {
      return sourceRect.right - parentRect.left - 2;
    }

    function xLeftPosition() {
      return sourceRect.left - parentRect.left - minWidth;
    }
  };

  PopupService.prototype.positionPopupUnderMouseEvent = function (params) {
    var _a = this.calculatePointerAlign(params.mouseEvent),
        x = _a.x,
        y = _a.y;

    var ePopup = params.ePopup,
        nudgeX = params.nudgeX,
        nudgeY = params.nudgeY;
    this.positionPopup({
      ePopup: ePopup,
      x: x,
      y: y,
      nudgeX: nudgeX,
      nudgeY: nudgeY,
      keepWithinBounds: true
    });
    this.callPostProcessPopup(params.ePopup, null, params.mouseEvent, params.type, params.column, params.rowNode);
  };

  PopupService.prototype.calculatePointerAlign = function (e) {
    var eDocument = this.getDocument();
    var popupParent = this.getPopupParent();
    var parentRect = popupParent.getBoundingClientRect();
    var documentRect = eDocument.documentElement.getBoundingClientRect();
    return {
      x: e.clientX - (popupParent === eDocument.body ? documentRect.left : parentRect.left),
      y: e.clientY - (popupParent === eDocument.body ? documentRect.top : parentRect.top)
    };
  };

  PopupService.prototype.positionPopupUnderComponent = function (params) {
    var sourceRect = params.eventSource.getBoundingClientRect();
    var eDocument = this.getDocument();
    var popupParent = this.getPopupParent();
    var alignSide = params.alignSide || 'left';
    var parentRect;

    if (popupParent === eDocument.body) {
      parentRect = eDocument.documentElement.getBoundingClientRect();
    } else {
      parentRect = popupParent.getBoundingClientRect();
    }

    var x = sourceRect.left - parentRect.left;

    if (alignSide === 'right') {
      x -= params.ePopup.offsetWidth - sourceRect.width;
    }

    this.positionPopup({
      ePopup: params.ePopup,
      minWidth: params.minWidth,
      minHeight: params.minHeight,
      nudgeX: params.nudgeX,
      nudgeY: params.nudgeY,
      x: x,
      y: sourceRect.top - parentRect.top + sourceRect.height,
      keepWithinBounds: params.keepWithinBounds
    });
    this.callPostProcessPopup(params.ePopup, params.eventSource, null, params.type, params.column, params.rowNode);
  };

  PopupService.prototype.positionPopupOverComponent = function (params) {
    var sourceRect = params.eventSource.getBoundingClientRect();
    var eDocument = this.getDocument();
    var popupParent = this.getPopupParent();
    var parentRect;

    if (popupParent === eDocument.body) {
      parentRect = eDocument.documentElement.getBoundingClientRect();
    } else {
      parentRect = popupParent.getBoundingClientRect();
    }

    this.positionPopup({
      ePopup: params.ePopup,
      minWidth: params.minWidth,
      nudgeX: params.nudgeX,
      nudgeY: params.nudgeY,
      x: sourceRect.left - parentRect.left,
      y: sourceRect.top - parentRect.top,
      keepWithinBounds: params.keepWithinBounds
    });
    this.callPostProcessPopup(params.ePopup, params.eventSource, null, params.type, params.column, params.rowNode);
  };

  PopupService.prototype.callPostProcessPopup = function (ePopup, eventSource, mouseEvent, type, column, rowNode) {
    var callback = this.gridOptionsWrapper.getPostProcessPopupFunc();

    if (callback) {
      var params = {
        column: column,
        rowNode: rowNode,
        ePopup: ePopup,
        type: type,
        eventSource: eventSource,
        mouseEvent: mouseEvent
      };
      callback(params);
    }
  };

  PopupService.prototype.positionPopup = function (params) {
    var x = params.x;
    var y = params.y;

    if (params.nudgeX) {
      x += params.nudgeX;
    }

    if (params.nudgeY) {
      y += params.nudgeY;
    } // if popup is overflowing to the bottom, move it up


    if (params.keepWithinBounds) {
      x = this.keepXWithinBounds(params, x);
      y = this.keepYWithinBounds(params, y);
    }

    params.ePopup.style.left = x + "px";
    params.ePopup.style.top = y + "px";
  };

  PopupService.prototype.keepYWithinBounds = function (params, y) {
    var eDocument = this.gridOptionsWrapper.getDocument();
    var docElement = eDocument.documentElement;
    var popupParent = this.getPopupParent();
    var parentRect = popupParent.getBoundingClientRect();
    var documentRect = eDocument.documentElement.getBoundingClientRect();
    var isBody = popupParent === eDocument.body;
    var defaultPadding = 3;
    var minHeight = Math.min(200, parentRect.height);
    var diff = 0;

    if (params.minHeight && params.minHeight < minHeight) {
      minHeight = params.minHeight;
    } else if (params.ePopup.offsetHeight > 0) {
      minHeight = params.ePopup.clientHeight;
      diff = _.getAbsoluteHeight(params.ePopup) - minHeight;
    }

    var heightOfParent = isBody ? _.getAbsoluteHeight(docElement) + docElement.scrollTop : parentRect.height;

    if (isBody) {
      heightOfParent -= Math.abs(documentRect.top - parentRect.top);
    }

    var maxY = heightOfParent - minHeight - diff - defaultPadding;
    return Math.min(Math.max(y, 0), Math.abs(maxY));
  };

  PopupService.prototype.keepXWithinBounds = function (params, x) {
    var eDocument = this.gridOptionsWrapper.getDocument();
    var docElement = eDocument.documentElement;
    var popupParent = this.getPopupParent();
    var parentRect = popupParent.getBoundingClientRect();
    var documentRect = eDocument.documentElement.getBoundingClientRect();
    var isBody = popupParent === eDocument.body;
    var defaultPadding = 3;
    var ePopup = params.ePopup;
    var minWidth = Math.min(200, parentRect.width);
    var diff = 0;

    if (params.minWidth && params.minWidth < minWidth) {
      minWidth = params.minWidth;
    } else if (ePopup.offsetWidth > 0) {
      minWidth = ePopup.offsetWidth;
      ePopup.style.minWidth = minWidth + "px";
      diff = _.getAbsoluteWidth(ePopup) - minWidth;
    }

    var widthOfParent = isBody ? _.getAbsoluteWidth(docElement) + docElement.scrollLeft : parentRect.width;

    if (isBody) {
      widthOfParent -= Math.abs(documentRect.left - parentRect.left);
    }

    var maxX = widthOfParent - minWidth - diff - defaultPadding;
    return Math.min(Math.max(x, 0), Math.abs(maxX));
  }; // adds an element to a div, but also listens to background checking for clicks,
  // so that when the background is clicked, the child is removed again, giving
  // a model look to popups.


  PopupService.prototype.addAsModalPopup = function (eChild, closeOnEsc, closedCallback, click) {
    return this.addPopup(true, eChild, closeOnEsc, closedCallback, click);
  };

  PopupService.prototype.addPopup = function (modal, eChild, closeOnEsc, closedCallback, click, alwaysOnTop) {
    var _this = this;

    var eDocument = this.gridOptionsWrapper.getDocument();

    if (!eDocument) {
      console.warn('ag-grid: could not find the document, document is empty');
      return function () {};
    }

    var pos = _.findIndex(this.popupList, function (popup) {
      return popup.element === eChild;
    });

    if (pos !== -1) {
      var popup = this.popupList[pos];
      return popup.hideFunc;
    }

    var ePopupParent = this.getPopupParent(); // for angular specifically, but shouldn't cause an issue with js or other fw's
    // https://github.com/angular/angular/issues/8563

    ePopupParent.appendChild(eChild);
    eChild.style.top = '0px';
    eChild.style.left = '0px'; // add env CSS class to child, in case user provided a popup parent, which means
    // theme class may be missing

    var eWrapper = document.createElement('div');
    var theme = this.environment.getTheme().theme;

    if (theme) {
      _.addCssClass(eWrapper, theme);
    }

    _.addCssClass(eWrapper, 'ag-popup');

    _.addCssClass(eChild, this.gridOptionsWrapper.isEnableRtl() ? 'ag-rtl' : 'ag-ltr');

    eWrapper.appendChild(eChild);
    ePopupParent.appendChild(eWrapper);

    if (alwaysOnTop) {
      this.setAlwaysOnTop(eWrapper, true);
    } else {
      this.bringPopupToFront(eWrapper);
    }

    var popupHidden = false;

    var hidePopupOnKeyboardEvent = function hidePopupOnKeyboardEvent(event) {
      var key = event.which || event.keyCode;

      if (key === Constants.KEY_ESCAPE && eWrapper.contains(document.activeElement)) {
        hidePopup(null);
      }
    };

    var hidePopupOnMouseEvent = function hidePopupOnMouseEvent(event) {
      hidePopup(event);
    };

    var hidePopupOnTouchEvent = function hidePopupOnTouchEvent(event) {
      hidePopup(null, event);
    };

    var hidePopup = function hidePopup(mouseEvent, touchEvent) {
      if ( // we don't hide popup if the event was on the child, or any
      // children of this child
      _this.isEventFromCurrentPopup(mouseEvent, touchEvent, eChild) || // if the event to close is actually the open event, then ignore it
      _this.isEventSameChainAsOriginalEvent(click, mouseEvent, touchEvent) || // this method should only be called once. the client can have different
      // paths, each one wanting to close, so this method may be called multiple times.
      popupHidden) {
        return;
      }

      popupHidden = true;
      ePopupParent.removeChild(eWrapper);
      eDocument.removeEventListener('keydown', hidePopupOnKeyboardEvent);
      eDocument.removeEventListener('mousedown', hidePopupOnMouseEvent);
      eDocument.removeEventListener('touchstart', hidePopupOnTouchEvent);
      eDocument.removeEventListener('contextmenu', hidePopupOnMouseEvent);

      _this.eventService.removeEventListener(Events.EVENT_DRAG_STARTED, hidePopupOnMouseEvent);

      if (closedCallback) {
        closedCallback();
      }

      _this.popupList = _this.popupList.filter(function (popup) {
        return popup.element !== eChild;
      });
    }; // if we add these listeners now, then the current mouse
    // click will be included, which we don't want


    window.setTimeout(function () {
      if (closeOnEsc) {
        eDocument.addEventListener('keydown', hidePopupOnKeyboardEvent);
      }

      if (modal) {
        eDocument.addEventListener('mousedown', hidePopupOnMouseEvent);

        _this.eventService.addEventListener(Events.EVENT_DRAG_STARTED, hidePopupOnMouseEvent);

        eDocument.addEventListener('touchstart', hidePopupOnTouchEvent);
        eDocument.addEventListener('contextmenu', hidePopupOnMouseEvent);
      }
    }, 0);
    this.popupList.push({
      element: eChild,
      hideFunc: hidePopup
    });
    return hidePopup;
  };

  PopupService.prototype.isEventFromCurrentPopup = function (mouseEvent, touchEvent, eChild) {
    var event = mouseEvent ? mouseEvent : touchEvent;

    if (!event) {
      return false;
    }

    var indexOfThisChild = _.findIndex(this.popupList, function (popup) {
      return popup.element === eChild;
    });

    if (indexOfThisChild === -1) {
      return false;
    }

    for (var i = indexOfThisChild; i < this.popupList.length; i++) {
      var popup = this.popupList[i];

      if (_.isElementInEventPath(popup.element, event)) {
        return true;
      }
    } // if the user did not write their own Custom Element to be rendered as popup
    // and this component has additional popup element, they should have the
    // `ag-custom-component-popup` class to be detected as part of the Custom Component


    var el = event.target;

    while (el && el != document.body) {
      if (el.classList.contains('ag-custom-component-popup') || el.parentElement === null) {
        return true;
      }

      el = el.parentElement;
    }
  }; // in some browsers, the context menu event can be fired before the click event, which means
  // the context menu event could open the popup, but then the click event closes it straight away.


  PopupService.prototype.isEventSameChainAsOriginalEvent = function (originalClick, mouseEvent, touchEvent) {
    // we check the coordinates of the event, to see if it's the same event. there is a 1 / 1000 chance that
    // the event is a different event, however that is an edge case that is not very relevant (the user clicking
    // twice on the same location isn't a normal path).
    // event could be mouse event or touch event.
    var mouseEventOrTouch = null;

    if (mouseEvent) {
      // mouse event can be used direction, it has coordinates
      mouseEventOrTouch = mouseEvent;
    } else if (touchEvent) {
      // touch event doesn't have coordinates, need it's touch object
      mouseEventOrTouch = touchEvent.touches[0];
    }

    if (mouseEventOrTouch && originalClick) {
      // for x, allow 4px margin, to cover iPads, where touch (which opens menu) is followed
      // by browser click (when you life finger up, touch is interrupted as click in browser)
      var screenX_1 = mouseEvent ? mouseEvent.screenX : 0;
      var screenY_1 = mouseEvent ? mouseEvent.screenY : 0;
      var xMatch = Math.abs(originalClick.screenX - screenX_1) < 5;
      var yMatch = Math.abs(originalClick.screenY - screenY_1) < 5;

      if (xMatch && yMatch) {
        return true;
      }
    }

    return false;
  };

  PopupService.prototype.getWrapper = function (ePopup) {
    while (!_.containsClass(ePopup, 'ag-popup') && ePopup.parentElement) {
      ePopup = ePopup.parentElement;
    }

    return _.containsClass(ePopup, 'ag-popup') ? ePopup : null;
  };

  PopupService.prototype.setAlwaysOnTop = function (ePopup, alwaysOnTop) {
    var eWrapper = this.getWrapper(ePopup);

    if (!eWrapper) {
      return;
    }

    _.addOrRemoveCssClass(eWrapper, 'ag-always-on-top', !!alwaysOnTop);

    if (alwaysOnTop) {
      this.bringPopupToFront(eWrapper);
    }
  };

  PopupService.prototype.bringPopupToFront = function (ePopup) {
    var parent = this.getPopupParent();
    var popupList = Array.prototype.slice.call(parent.querySelectorAll('.ag-popup'));
    var popupLen = popupList.length;
    var alwaysOnTopList = Array.prototype.slice.call(parent.querySelectorAll('.ag-popup.ag-always-on-top'));
    var onTopLength = alwaysOnTopList.length;
    var eWrapper = this.getWrapper(ePopup);

    if (!eWrapper || popupLen <= 1 || !parent.contains(ePopup)) {
      return;
    }

    var pos = popupList.indexOf(eWrapper);

    if (onTopLength) {
      var isPopupAlwaysOnTop = _.containsClass(eWrapper, 'ag-always-on-top');

      if (isPopupAlwaysOnTop) {
        if (pos !== popupLen - 1) {
          _.last(alwaysOnTopList).insertAdjacentElement('afterend', eWrapper);
        }
      } else if (pos !== popupLen - onTopLength - 1) {
        alwaysOnTopList[0].insertAdjacentElement('beforebegin', eWrapper);
      }
    } else if (pos !== popupLen - 1) {
      _.last(popupList).insertAdjacentElement('afterend', eWrapper);
    }

    var params = {
      type: 'popupToFront',
      api: this.gridOptionsWrapper.getApi(),
      columnApi: this.gridOptionsWrapper.getColumnApi(),
      eWrapper: eWrapper
    };
    this.eventService.dispatchEvent(params);
  };

  __decorate$Y([Autowired('gridOptionsWrapper')], PopupService.prototype, "gridOptionsWrapper", void 0);

  __decorate$Y([Autowired('environment')], PopupService.prototype, "environment", void 0);

  __decorate$Y([Autowired('eventService')], PopupService.prototype, "eventService", void 0);

  PopupService = __decorate$Y([Bean('popupService')], PopupService);
  return PopupService;
}();
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __decorate$Z = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var __param$8 = undefined && undefined.__param || function (paramIndex, decorator) {
  return function (target, key) {
    decorator(target, key, paramIndex);
  };
};

var LoggerFactory =
/** @class */
function () {
  function LoggerFactory() {}

  LoggerFactory.prototype.setBeans = function (gridOptionsWrapper) {
    this.logging = gridOptionsWrapper.isDebug();
  };

  LoggerFactory.prototype.create = function (name) {
    return new Logger(name, this.isLogging.bind(this));
  };

  LoggerFactory.prototype.isLogging = function () {
    return this.logging;
  };

  __decorate$Z([__param$8(0, Qualifier('gridOptionsWrapper'))], LoggerFactory.prototype, "setBeans", null);

  LoggerFactory = __decorate$Z([Bean('loggerFactory')], LoggerFactory);
  return LoggerFactory;
}();

var Logger =
/** @class */
function () {
  function Logger(name, isLoggingFunc) {
    this.name = name;
    this.isLoggingFunc = isLoggingFunc;
  }

  Logger.prototype.isLogging = function () {
    return this.isLoggingFunc();
  };

  Logger.prototype.log = function (message) {
    if (this.isLoggingFunc()) {
      // tslint:disable-next-line
      console.log('ag-Grid.' + this.name + ': ' + message);
    }
  };

  return Logger;
}();
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __decorate$_ = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var AutoWidthCalculator =
/** @class */
function () {
  function AutoWidthCalculator() {}

  AutoWidthCalculator.prototype.registerGridComp = function (gridPanel) {
    this.gridPanel = gridPanel;
  };

  AutoWidthCalculator.prototype.registerHeaderRootComp = function (headerRootComp) {
    this.headerRootComp = headerRootComp;
  }; // this is the trick: we create a dummy container and clone all the cells
  // into the dummy, then check the dummy's width. then destroy the dummy
  // as we don't need it any more.
  // drawback: only the cells visible on the screen are considered


  AutoWidthCalculator.prototype.getPreferredWidthForColumn = function (column, skipHeader) {
    var eHeaderCell = this.getHeaderCellForColumn(column); // cell isn't visible

    if (!eHeaderCell) {
      return -1;
    }

    var eDummyContainer = document.createElement('span'); // position fixed, so it isn't restricted to the boundaries of the parent

    eDummyContainer.style.position = 'fixed'; // we put the dummy into the body container, so it will inherit all the
    // css styles that the real cells are inheriting

    var eBodyContainer = this.gridPanel.getCenterContainer();
    eBodyContainer.appendChild(eDummyContainer); // get all the cells that are currently displayed (this only brings back
    // rendered cells, rows not rendered due to row visualisation will not be here)

    this.putRowCellsIntoDummyContainer(column, eDummyContainer);

    if (!skipHeader) {
      // we only consider the lowest level cell, not the group cell. in 99% of the time, this
      // will be enough. if we consider groups, then it gets too complicated for what it's worth,
      // as the groups can span columns and this class only considers one column at a time.
      this.cloneItemIntoDummy(eHeaderCell, eDummyContainer);
    } // at this point, all the clones are lined up vertically with natural widths. the dummy
    // container will have a width wide enough just to fit the largest.


    var dummyContainerWidth = eDummyContainer.offsetWidth; // we are finished with the dummy container, so get rid of it

    eBodyContainer.removeChild(eDummyContainer); // we add padding as I found sometimes the gui still put '...' after some of the texts. so the
    // user can configure the grid to add a few more pixels after the calculated width

    var autoSizePadding = this.gridOptionsWrapper.getAutoSizePadding();
    return dummyContainerWidth + autoSizePadding;
  };

  AutoWidthCalculator.prototype.getHeaderCellForColumn = function (column) {
    var comp = null; // find the rendered header cell

    this.headerRootComp.forEachHeaderElement(function (headerElement) {
      if (headerElement instanceof HeaderWrapperComp) {
        var headerWrapperComp = headerElement;

        if (headerWrapperComp.getColumn() === column) {
          comp = headerWrapperComp;
        }
      }
    });
    return comp ? comp.getGui() : null;
  };

  AutoWidthCalculator.prototype.putRowCellsIntoDummyContainer = function (column, eDummyContainer) {
    var _this = this;

    var eCells = this.rowRenderer.getAllCellsForColumn(column);
    eCells.forEach(function (eCell) {
      return _this.cloneItemIntoDummy(eCell, eDummyContainer);
    });
  };

  AutoWidthCalculator.prototype.cloneItemIntoDummy = function (eCell, eDummyContainer) {
    // make a deep clone of the cell
    var eCellClone = eCell.cloneNode(true); // the original has a fixed width, we remove this to allow the natural width based on content

    eCellClone.style.width = ''; // the original has position = absolute, we need to remove this so it's positioned normally

    eCellClone.style.position = 'static';
    eCellClone.style.left = ''; // we put the cell into a containing div, as otherwise the cells would just line up
    // on the same line, standard flow layout, by putting them into divs, they are laid
    // out one per line

    var eCloneParent = document.createElement('div'); // table-row, so that each cell is on a row. i also tried display='block', but this
    // didn't work in IE

    eCloneParent.style.display = 'table-row'; // the twig on the branch, the branch on the tree, the tree in the hole,
    // the hole in the bog, the bog in the clone, the clone in the parent,
    // the parent in the dummy, and the dummy down in the vall-e-ooo, OOOOOOOOO! Oh row the rattling bog....

    eCloneParent.appendChild(eCellClone);
    eDummyContainer.appendChild(eCloneParent);
  };

  __decorate$_([Autowired('rowRenderer')], AutoWidthCalculator.prototype, "rowRenderer", void 0);

  __decorate$_([Autowired('gridOptionsWrapper')], AutoWidthCalculator.prototype, "gridOptionsWrapper", void 0);

  AutoWidthCalculator = __decorate$_([Bean('autoWidthCalculator')], AutoWidthCalculator);
  return AutoWidthCalculator;
}();
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __decorate$$ = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var HorizontalResizeService =
/** @class */
function () {
  function HorizontalResizeService() {}

  HorizontalResizeService.prototype.addResizeBar = function (params) {
    var _this = this;

    var dragSource = {
      dragStartPixels: params.dragStartPixels || 0,
      eElement: params.eResizeBar,
      onDragStart: this.onDragStart.bind(this, params),
      onDragStop: this.onDragStop.bind(this, params),
      onDragging: this.onDragging.bind(this, params)
    };
    this.dragService.addDragSource(dragSource, true); // we pass remove func back to the caller, so call can tell us when they
    // are finished, and then we remove the listener from the drag source

    var finishedWithResizeFunc = function finishedWithResizeFunc() {
      return _this.dragService.removeDragSource(dragSource);
    };

    return finishedWithResizeFunc;
  };

  HorizontalResizeService.prototype.onDragStart = function (params, mouseEvent) {
    this.draggingStarted = true;
    this.dragStartX = mouseEvent.clientX;
    this.setResizeIcons();
    var shiftKey = mouseEvent instanceof MouseEvent ? mouseEvent.shiftKey === true : false;
    params.onResizeStart(shiftKey);
  };

  HorizontalResizeService.prototype.setResizeIcons = function () {
    this.oldBodyCursor = this.eGridDiv.style.cursor;
    this.oldMsUserSelect = this.eGridDiv.style.msUserSelect;
    this.oldWebkitUserSelect = this.eGridDiv.style.webkitUserSelect; // change the body cursor, so when drag moves out of the drag bar, the cursor is still 'resize' (or 'move'

    this.eGridDiv.style.cursor = 'col-resize'; // we don't want text selection outside the grid (otherwise it looks weird as text highlights when we move)

    this.eGridDiv.style.msUserSelect = 'none';
    this.eGridDiv.style.webkitUserSelect = 'none';
  };

  HorizontalResizeService.prototype.onDragStop = function (params, mouseEvent) {
    params.onResizeEnd(this.resizeAmount);
    this.resetIcons();
  };

  HorizontalResizeService.prototype.resetIcons = function () {
    // we don't want text selection outside the grid (otherwise it looks weird as text highlights when we move)
    this.eGridDiv.style.cursor = this.oldBodyCursor;
    this.eGridDiv.style.msUserSelect = this.oldMsUserSelect;
    this.eGridDiv.style.webkitUserSelect = this.oldWebkitUserSelect;
  };

  HorizontalResizeService.prototype.onDragging = function (params, mouseEvent) {
    this.resizeAmount = mouseEvent.clientX - this.dragStartX;
    params.onResizing(this.resizeAmount);
  };

  __decorate$$([Autowired('gridOptionsWrapper')], HorizontalResizeService.prototype, "gridOptionsWrapper", void 0);

  __decorate$$([Autowired('dragService')], HorizontalResizeService.prototype, "dragService", void 0);

  __decorate$$([Autowired('eGridDiv')], HorizontalResizeService.prototype, "eGridDiv", void 0);

  HorizontalResizeService = __decorate$$([Bean('horizontalResizeService')], HorizontalResizeService);
  return HorizontalResizeService;
}();
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __extends$N = undefined && undefined.__extends || function () {
  var _extendStatics50 = function extendStatics(d, b) {
    _extendStatics50 = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics50(d, b);
  };

  return function (d, b) {
    _extendStatics50(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var __decorate$10 = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var GridCore =
/** @class */
function (_super) {
  __extends$N(GridCore, _super);

  function GridCore() {
    return _super !== null && _super.apply(this, arguments) || this;
  }

  GridCore.prototype.init = function () {
    var _this = this;

    this.logger = this.loggerFactory.create('GridCore');
    var template = this.createTemplate();
    this.setTemplate(template); // register with services that need grid core

    [this.gridApi, this.filterManager, this.rowRenderer, this.popupService].forEach(function (service) {
      return service.registerGridCore(_this);
    });

    if (ModuleRegistry.isRegistered(exports.ModuleNames.ClipboardModule)) {
      this.clipboardService.registerGridCore(this);
    }

    this.gridOptionsWrapper.addLayoutElement(this.getGui());
    this.eGridDiv.appendChild(this.getGui());
    this.addDestroyFunc(function () {
      _this.eGridDiv.removeChild(_this.getGui());
    }); // if using angular, watch for quickFilter changes

    if (this.$scope) {
      var quickFilterUnregisterFn = this.$scope.$watch(this.quickFilterOnScope, function (newFilter) {
        return _this.filterManager.setQuickFilter(newFilter);
      });
      this.addDestroyFunc(quickFilterUnregisterFn);
    } // important to set rtl before doLayout, as setting the RTL class impacts the scroll position,
    // which doLayout indirectly depends on


    this.addRtlSupport();
    this.logger.log('ready');
    this.gridOptionsWrapper.addLayoutElement(this.eRootWrapperBody);
    var gridPanelEl = this.gridPanel.getGui();
    this.addDestroyableEventListener(gridPanelEl, 'focusin', function () {
      _.addCssClass(gridPanelEl, 'ag-has-focus');
    });
    this.addDestroyableEventListener(gridPanelEl, 'focusout', function (e) {
      if (!gridPanelEl.contains(e.relatedTarget)) {
        _.removeCssClass(gridPanelEl, 'ag-has-focus');
      }
    });
    var unsubscribeFromResize = this.resizeObserverService.observeResize(this.eGridDiv, this.onGridSizeChanged.bind(this));
    this.addDestroyFunc(function () {
      return unsubscribeFromResize();
    });
    var theme = this.environment.getTheme().theme;

    if (/^ag-theme-(fresh|dark|blue|bootstrap)$/.test(theme)) {
      console.warn("ag-Grid: \"" + theme + "\" theme is deprecated and will be removed in the next major release (v23)");
    }
  };

  GridCore.prototype.createTemplate = function () {
    var sideBarModuleLoaded = ModuleRegistry.isRegistered(exports.ModuleNames.SideBarModule);
    var statusBarModuleLoaded = ModuleRegistry.isRegistered(exports.ModuleNames.StatusBarModule);
    var rowGroupingLoaded = ModuleRegistry.isRegistered(exports.ModuleNames.RowGroupingModule);
    var enterpriseCoreLoaded = ModuleRegistry.isRegistered(exports.ModuleNames.EnterpriseCoreModule);
    var dropZones = rowGroupingLoaded ? '<ag-grid-header-drop-zones></ag-grid-header-drop-zones>' : '';
    var sideBar = sideBarModuleLoaded ? '<ag-side-bar ref="sideBar"></ag-side-bar>' : '';
    var statusBar = statusBarModuleLoaded ? '<ag-status-bar ref="statusBar"></ag-status-bar>' : '';
    var watermark = enterpriseCoreLoaded ? '<ag-watermark></ag-watermark>' : '';
    var template = "<div class=\"ag-root-wrapper\">\n                " + dropZones + "\n                <div class=\"ag-root-wrapper-body\" ref=\"rootWrapperBody\">\n                    <ag-grid-comp ref=\"gridPanel\"></ag-grid-comp>                    \n                    " + sideBar + "\n                </div>\n                " + statusBar + "\n                <ag-pagination></ag-pagination>\n                " + watermark + "\n            </div>";
    return template;
  };

  GridCore.prototype.onGridSizeChanged = function () {
    var event = {
      type: Events.EVENT_GRID_SIZE_CHANGED,
      api: this.gridApi,
      columnApi: this.columnApi,
      clientWidth: this.eGridDiv.clientWidth,
      clientHeight: this.eGridDiv.clientHeight
    };
    this.eventService.dispatchEvent(event);
  };

  GridCore.prototype.addRtlSupport = function () {
    var cssClass = this.gridOptionsWrapper.isEnableRtl() ? 'ag-rtl' : 'ag-ltr';

    _.addCssClass(this.getGui(), cssClass);
  };

  GridCore.prototype.getRootGui = function () {
    return this.getGui();
  };

  GridCore.prototype.isSideBarVisible = function () {
    if (!this.sideBarComp) {
      return false;
    }

    return this.sideBarComp.isDisplayed();
  };

  GridCore.prototype.setSideBarVisible = function (show) {
    if (!this.sideBarComp) {
      if (show) {
        console.warn('ag-Grid: sideBar is not loaded');
      }

      return;
    }

    this.sideBarComp.setDisplayed(show);
  };

  GridCore.prototype.setSideBarPosition = function (position) {
    if (!this.sideBarComp) {
      console.warn('ag-Grid: sideBar is not loaded');
      return;
    }

    this.sideBarComp.setSideBarPosition(position);
  };

  GridCore.prototype.closeToolPanel = function () {
    if (!this.sideBarComp) {
      console.warn('ag-Grid: toolPanel is only available in ag-Grid Enterprise');
      return;
    }

    this.sideBarComp.close();
  };

  GridCore.prototype.getSideBar = function () {
    return this.gridOptions.sideBar;
  };

  GridCore.prototype.getToolPanelInstance = function (key) {
    if (!this.sideBarComp) {
      console.warn('ag-Grid: toolPanel is only available in ag-Grid Enterprise');
      return;
    }

    return this.sideBarComp.getToolPanelInstance(key);
  };

  GridCore.prototype.refreshSideBar = function () {
    if (this.sideBarComp) {
      this.sideBarComp.refresh();
    }
  };

  GridCore.prototype.setSideBar = function (def) {
    if (!this.sideBarComp) {
      return;
    }

    this.eRootWrapperBody.removeChild(this.sideBarComp.getGui());
    this.gridOptions.sideBar = SideBarDefParser.parse(def);
    this.sideBarComp.reset();
    this.eRootWrapperBody.appendChild(this.sideBarComp.getGui());
  };

  GridCore.prototype.getOpenedToolPanel = function () {
    if (!this.sideBarComp) {
      return null;
    }

    return this.sideBarComp.openedItem();
  };

  GridCore.prototype.openToolPanel = function (key) {
    if (!this.sideBarComp) {
      console.warn('ag-Grid: toolPanel is only available in ag-Grid Enterprise');
      return;
    }

    this.sideBarComp.openToolPanel(key);
  };

  GridCore.prototype.isToolPanelShowing = function () {
    return this.sideBarComp.isToolPanelShowing();
  };

  GridCore.prototype.destroy = function () {
    _super.prototype.destroy.call(this);

    this.logger.log('Grid DOM removed');
  }; // Valid values for position are bottom, middle and top


  GridCore.prototype.ensureNodeVisible = function (comparator, position) {
    if (position === void 0) {
      position = 'top';
    }

    if (this.doingVirtualPaging) {
      throw new Error('Cannot use ensureNodeVisible when doing virtual paging, as we cannot check rows that are not in memory');
    } // look for the node index we want to display


    var rowCount = this.rowModel.getRowCount();
    var comparatorIsAFunction = typeof comparator === 'function';
    var indexToSelect = -1; // go through all the nodes, find the one we want to show

    for (var i = 0; i < rowCount; i++) {
      var node = this.rowModel.getRow(i);

      if (comparatorIsAFunction) {
        if (comparator(node)) {
          indexToSelect = i;
          break;
        }
      } else {
        // check object equality against node and data
        if (comparator === node || comparator === node.data) {
          indexToSelect = i;
          break;
        }
      }
    }

    if (indexToSelect >= 0) {
      this.gridPanel.ensureIndexVisible(indexToSelect, position);
    }
  };

  __decorate$10([Autowired('gridOptions')], GridCore.prototype, "gridOptions", void 0);

  __decorate$10([Autowired('gridOptionsWrapper')], GridCore.prototype, "gridOptionsWrapper", void 0);

  __decorate$10([Autowired('rowModel')], GridCore.prototype, "rowModel", void 0);

  __decorate$10([Autowired('resizeObserverService')], GridCore.prototype, "resizeObserverService", void 0);

  __decorate$10([Autowired('columnController')], GridCore.prototype, "columnController", void 0);

  __decorate$10([Autowired('rowRenderer')], GridCore.prototype, "rowRenderer", void 0);

  __decorate$10([Autowired('filterManager')], GridCore.prototype, "filterManager", void 0);

  __decorate$10([Autowired('eventService')], GridCore.prototype, "eventService", void 0);

  __decorate$10([Autowired('eGridDiv')], GridCore.prototype, "eGridDiv", void 0);

  __decorate$10([Autowired('$scope')], GridCore.prototype, "$scope", void 0);

  __decorate$10([Autowired('quickFilterOnScope')], GridCore.prototype, "quickFilterOnScope", void 0);

  __decorate$10([Autowired('popupService')], GridCore.prototype, "popupService", void 0);

  __decorate$10([Autowired('focusedCellController')], GridCore.prototype, "focusedCellController", void 0);

  __decorate$10([Autowired('loggerFactory')], GridCore.prototype, "loggerFactory", void 0);

  __decorate$10([Autowired('columnApi')], GridCore.prototype, "columnApi", void 0);

  __decorate$10([Autowired('gridApi')], GridCore.prototype, "gridApi", void 0);

  __decorate$10([Autowired('environment')], GridCore.prototype, "environment", void 0);

  __decorate$10([Optional('clipboardService')], GridCore.prototype, "clipboardService", void 0);

  __decorate$10([RefSelector('gridPanel')], GridCore.prototype, "gridPanel", void 0);

  __decorate$10([RefSelector('sideBar')], GridCore.prototype, "sideBarComp", void 0);

  __decorate$10([RefSelector('rootWrapperBody')], GridCore.prototype, "eRootWrapperBody", void 0);

  __decorate$10([PostConstruct], GridCore.prototype, "init", null);

  return GridCore;
}(Component);
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __decorate$11 = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var StandardMenuFactory =
/** @class */
function () {
  function StandardMenuFactory() {}

  StandardMenuFactory.prototype.hideActiveMenu = function () {
    if (this.hidePopup) {
      this.hidePopup();
    }
  };

  StandardMenuFactory.prototype.showMenuAfterMouseEvent = function (column, mouseEvent) {
    var _this = this;

    this.showPopup(column, function (eMenu) {
      _this.popupService.positionPopupUnderMouseEvent({
        column: column,
        type: 'columnMenu',
        mouseEvent: mouseEvent,
        ePopup: eMenu
      });
    });
  };

  StandardMenuFactory.prototype.showMenuAfterButtonClick = function (column, eventSource) {
    var _this = this;

    this.showPopup(column, function (eMenu) {
      _this.popupService.positionPopupUnderComponent({
        type: 'columnMenu',
        eventSource: eventSource,
        ePopup: eMenu,
        keepWithinBounds: true,
        column: column
      });
    });
  };

  StandardMenuFactory.prototype.showPopup = function (column, positionCallback) {
    var _this = this;

    var filterWrapper = this.filterManager.getOrCreateFilterWrapper(column, 'COLUMN_MENU');
    var eMenu = document.createElement('div');

    _.addCssClass(eMenu, 'ag-menu');

    filterWrapper.guiPromise.promise.then(function (gui) {
      eMenu.appendChild(gui);
    });
    var hidePopup;

    var bodyScrollListener = function bodyScrollListener(event) {
      // if h scroll, popup is no longer over the column
      if (event.direction === 'horizontal') {
        hidePopup();
      }
    };

    this.eventService.addEventListener('bodyScroll', bodyScrollListener);

    var closedCallback = function closedCallback() {
      _this.eventService.removeEventListener('bodyScroll', bodyScrollListener);

      column.setMenuVisible(false, "contextMenu");
    }; // need to show filter before positioning, as only after filter
    // is visible can we find out what the width of it is


    hidePopup = this.popupService.addAsModalPopup(eMenu, true, closedCallback);
    positionCallback(eMenu);
    filterWrapper.filterPromise.then(function (filter) {
      if (filter.afterGuiAttached) {
        var params = {
          hidePopup: hidePopup
        };
        filter.afterGuiAttached(params);
      }
    });
    this.hidePopup = hidePopup;
    column.setMenuVisible(true, "contextMenu");
  };

  StandardMenuFactory.prototype.isMenuEnabled = function (column) {
    // for standard, we show menu if filter is enabled, and he menu is not suppressed
    return column.isFilterAllowed();
  };

  __decorate$11([Autowired('eventService')], StandardMenuFactory.prototype, "eventService", void 0);

  __decorate$11([Autowired('filterManager')], StandardMenuFactory.prototype, "filterManager", void 0);

  __decorate$11([Autowired('popupService')], StandardMenuFactory.prototype, "popupService", void 0);

  __decorate$11([Autowired('gridOptionsWrapper')], StandardMenuFactory.prototype, "gridOptionsWrapper", void 0);

  StandardMenuFactory = __decorate$11([Bean('menuFactory')], StandardMenuFactory);
  return StandardMenuFactory;
}();
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __decorate$12 = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};
/** Adds drag listening onto an element. In ag-Grid this is used twice, first is resizing columns,
 * second is moving the columns and column groups around (ie the 'drag' part of Drag and Drop. */


var DragService =
/** @class */
function () {
  function DragService() {
    this.onMouseUpListener = this.onMouseUp.bind(this);
    this.onMouseMoveListener = this.onMouseMove.bind(this);
    this.onTouchEndListener = this.onTouchUp.bind(this);
    this.onTouchMoveListener = this.onTouchMove.bind(this);
    this.dragEndFunctions = [];
    this.dragSources = [];
  }

  DragService.prototype.init = function () {
    this.logger = this.loggerFactory.create('DragService');
  };

  DragService.prototype.destroy = function () {
    this.dragSources.forEach(this.removeListener.bind(this));
    this.dragSources.length = 0;
  };

  DragService.prototype.removeListener = function (dragSourceAndListener) {
    var element = dragSourceAndListener.dragSource.eElement;
    var mouseDownListener = dragSourceAndListener.mouseDownListener;
    element.removeEventListener('mousedown', mouseDownListener); // remove touch listener only if it exists

    if (dragSourceAndListener.touchEnabled) {
      var touchStartListener = dragSourceAndListener.touchStartListener;
      element.removeEventListener('touchstart', touchStartListener, {
        passive: true
      });
    }
  };

  DragService.prototype.removeDragSource = function (params) {
    var dragSourceAndListener = _.find(this.dragSources, function (item) {
      return item.dragSource === params;
    });

    if (!dragSourceAndListener) {
      return;
    }

    this.removeListener(dragSourceAndListener);

    _.removeFromArray(this.dragSources, dragSourceAndListener);
  };

  DragService.prototype.setNoSelectToBody = function (noSelect) {
    var eDocument = this.gridOptionsWrapper.getDocument();
    var eBody = eDocument.querySelector('body');

    if (_.exists(eBody)) {
      // when we drag the mouse in ag-Grid, this class gets added / removed from the body, so that
      // the mouse isn't selecting text when dragging.
      _.addOrRemoveCssClass(eBody, 'ag-unselectable', noSelect);
    }
  };

  DragService.prototype.addDragSource = function (params, includeTouch) {
    if (includeTouch === void 0) {
      includeTouch = false;
    }

    var mouseListener = this.onMouseDown.bind(this, params);
    params.eElement.addEventListener('mousedown', mouseListener);
    var touchListener = null;
    var suppressTouch = this.gridOptionsWrapper.isSuppressTouch();

    if (includeTouch && !suppressTouch) {
      touchListener = this.onTouchStart.bind(this, params);
      params.eElement.addEventListener('touchstart', touchListener, {
        passive: false
      });
    }

    this.dragSources.push({
      dragSource: params,
      mouseDownListener: mouseListener,
      touchStartListener: touchListener,
      touchEnabled: includeTouch
    });
  }; // gets called whenever mouse down on any drag source


  DragService.prototype.onTouchStart = function (params, touchEvent) {
    var _this = this;

    this.currentDragParams = params;
    this.dragging = false;
    var touch = touchEvent.touches[0];
    this.touchLastTime = touch;
    this.touchStart = touch;
    touchEvent.preventDefault(); // we temporally add these listeners, for the duration of the drag, they
    // are removed in touch end handling.

    params.eElement.addEventListener('touchmove', this.onTouchMoveListener, {
      passive: true
    });
    params.eElement.addEventListener('touchend', this.onTouchEndListener, {
      passive: true
    });
    params.eElement.addEventListener('touchcancel', this.onTouchEndListener, {
      passive: true
    });
    this.dragEndFunctions.push(function () {
      params.eElement.removeEventListener('touchmove', _this.onTouchMoveListener, {
        passive: true
      });
      params.eElement.removeEventListener('touchend', _this.onTouchEndListener, {
        passive: true
      });
      params.eElement.removeEventListener('touchcancel', _this.onTouchEndListener, {
        passive: true
      });
    }); // see if we want to start dragging straight away

    if (params.dragStartPixels === 0) {
      this.onCommonMove(touch, this.touchStart);
    }
  }; // gets called whenever mouse down on any drag source


  DragService.prototype.onMouseDown = function (params, mouseEvent) {
    var _this = this; // we ignore when shift key is pressed. this is for the range selection, as when
    // user shift-clicks a cell, this should not be interpreted as the start of a drag.
    // if (mouseEvent.shiftKey) { return; }


    if (params.skipMouseEvent) {
      if (params.skipMouseEvent(mouseEvent)) {
        return;
      }
    } // if there are two elements with parent / child relationship, and both are draggable,
    // when we drag the child, we should NOT drag the parent. an example of this is row moving
    // and range selection - row moving should get preference when use drags the rowDrag component.


    if (mouseEvent._alreadyProcessedByDragService) {
      return;
    }

    mouseEvent._alreadyProcessedByDragService = true; // only interested in left button clicks

    if (mouseEvent.button !== 0) {
      return;
    }

    this.currentDragParams = params;
    this.dragging = false;
    this.mouseStartEvent = mouseEvent;
    var eDocument = this.gridOptionsWrapper.getDocument();
    this.setNoSelectToBody(true); // we temporally add these listeners, for the duration of the drag, they
    // are removed in mouseup handling.

    eDocument.addEventListener('mousemove', this.onMouseMoveListener);
    eDocument.addEventListener('mouseup', this.onMouseUpListener);
    this.dragEndFunctions.push(function () {
      eDocument.removeEventListener('mousemove', _this.onMouseMoveListener);
      eDocument.removeEventListener('mouseup', _this.onMouseUpListener);
    }); //see if we want to start dragging straight away

    if (params.dragStartPixels === 0) {
      this.onMouseMove(mouseEvent);
    }
  }; // returns true if the event is close to the original event by X pixels either vertically or horizontally.
  // we only start dragging after X pixels so this allows us to know if we should start dragging yet.


  DragService.prototype.isEventNearStartEvent = function (currentEvent, startEvent) {
    // by default, we wait 4 pixels before starting the drag
    var dragStartPixels = this.currentDragParams.dragStartPixels;
    var requiredPixelDiff = _.exists(dragStartPixels) ? dragStartPixels : 4;
    return _.areEventsNear(currentEvent, startEvent, requiredPixelDiff);
  };

  DragService.prototype.getFirstActiveTouch = function (touchList) {
    for (var i = 0; i < touchList.length; i++) {
      if (touchList[i].identifier === this.touchStart.identifier) {
        return touchList[i];
      }
    }

    return null;
  };

  DragService.prototype.onCommonMove = function (currentEvent, startEvent) {
    if (!this.dragging) {
      // if mouse hasn't travelled from the start position enough, do nothing
      if (!this.dragging && this.isEventNearStartEvent(currentEvent, startEvent)) {
        return;
      }

      this.dragging = true;
      var event_1 = {
        type: Events.EVENT_DRAG_STARTED,
        api: this.gridApi,
        columnApi: this.columnApi
      };
      this.eventService.dispatchEvent(event_1);
      this.currentDragParams.onDragStart(startEvent);
    }

    this.currentDragParams.onDragging(currentEvent);
  };

  DragService.prototype.onTouchMove = function (touchEvent) {
    var touch = this.getFirstActiveTouch(touchEvent.touches);

    if (!touch) {
      return;
    } // this.___statusPanel.setInfoText(Math.random() + ' onTouchMove preventDefault stopPropagation');
    // if we don't preview default, then the browser will try and do it's own touch stuff,
    // like do 'back button' (chrome does this) or scroll the page (eg drag column could  be confused
    // with scroll page in the app)
    // touchEvent.preventDefault();


    this.onCommonMove(touch, this.touchStart);
  }; // only gets called after a mouse down - as this is only added after mouseDown
  // and is removed when mouseUp happens


  DragService.prototype.onMouseMove = function (mouseEvent) {
    this.onCommonMove(mouseEvent, this.mouseStartEvent);
  };

  DragService.prototype.onTouchUp = function (touchEvent) {
    var touch = this.getFirstActiveTouch(touchEvent.changedTouches); // i haven't worked this out yet, but there is no matching touch
    // when we get the touch up event. to get around this, we swap in
    // the last touch. this is a hack to 'get it working' while we
    // figure out what's going on, why we are not getting a touch in
    // current event.

    if (!touch) {
      touch = this.touchLastTime;
    } // if mouse was left up before we started to move, then this is a tap.
    // we check this before onUpCommon as onUpCommon resets the dragging
    // let tap = !this.dragging;
    // let tapTarget = this.currentDragParams.eElement;


    this.onUpCommon(touch); // if tap, tell user
    // console.log(`${Math.random()} tap = ${tap}`);
    // if (tap) {
    //     tapTarget.click();
    // }
  };

  DragService.prototype.onMouseUp = function (mouseEvent) {
    this.onUpCommon(mouseEvent);
  };

  DragService.prototype.onUpCommon = function (eventOrTouch) {
    if (this.dragging) {
      this.dragging = false;
      this.currentDragParams.onDragStop(eventOrTouch);
      var event_2 = {
        type: Events.EVENT_DRAG_STOPPED,
        api: this.gridApi,
        columnApi: this.columnApi
      };
      this.eventService.dispatchEvent(event_2);
    }

    this.setNoSelectToBody(false);
    this.mouseStartEvent = null;
    this.touchStart = null;
    this.touchLastTime = null;
    this.currentDragParams = null;
    this.dragEndFunctions.forEach(function (func) {
      return func();
    });
    this.dragEndFunctions.length = 0;
  };

  __decorate$12([Autowired('loggerFactory')], DragService.prototype, "loggerFactory", void 0);

  __decorate$12([Autowired('eventService')], DragService.prototype, "eventService", void 0);

  __decorate$12([Autowired('gridOptionsWrapper')], DragService.prototype, "gridOptionsWrapper", void 0);

  __decorate$12([Autowired('columnApi')], DragService.prototype, "columnApi", void 0);

  __decorate$12([Autowired('gridApi')], DragService.prototype, "gridApi", void 0);

  __decorate$12([PostConstruct], DragService.prototype, "init", null);

  __decorate$12([PreDestroy], DragService.prototype, "destroy", null);

  DragService = __decorate$12([Bean('dragService')], DragService);
  return DragService;
}();
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __decorate$13 = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var SortController =
/** @class */
function () {
  function SortController() {
    var _this = this; // used by the public api, for saving the sort model


    this.getSortModel = function () {
      return _this.getColumnsWithSortingOrdered().map(function (column) {
        return {
          colId: column.getColId(),
          sort: column.getSort()
        };
      });
    };
  }

  SortController_1 = SortController;

  SortController.prototype.progressSort = function (column, multiSort, source) {
    if (source === void 0) {
      source = "api";
    }

    var nextDirection = this.getNextSortDirection(column);
    this.setSortForColumn(column, nextDirection, multiSort, source);
  };

  SortController.prototype.setSortForColumn = function (column, sort, multiSort, source) {
    if (source === void 0) {
      source = "api";
    } // auto correct - if sort not legal value, then set it to 'no sort' (which is null)


    if (sort !== Constants.SORT_ASC && sort !== Constants.SORT_DESC) {
      sort = null;
    } // update sort on current col


    column.setSort(sort, source); // sortedAt used for knowing order of cols when multi-col sort

    if (column.getSort()) {
      var sortedAt = Number(new Date().valueOf());
      column.setSortedAt(sortedAt);
    } else {
      column.setSortedAt(null);
    }

    var doingMultiSort = multiSort && !this.gridOptionsWrapper.isSuppressMultiSort(); // clear sort on all columns except this one, and update the icons

    if (!doingMultiSort) {
      this.clearSortBarThisColumn(column, source);
    }

    this.dispatchSortChangedEvents();
  }; // gets called by API, so if data changes, use can call this, which will end up
  // working out the sort order again of the rows.


  SortController.prototype.onSortChanged = function () {
    this.dispatchSortChangedEvents();
  };

  SortController.prototype.dispatchSortChangedEvents = function () {
    var event = {
      type: Events.EVENT_SORT_CHANGED,
      api: this.gridApi,
      columnApi: this.columnApi
    };
    this.eventService.dispatchEvent(event);
  };

  SortController.prototype.clearSortBarThisColumn = function (columnToSkip, source) {
    this.columnController.getPrimaryAndSecondaryAndAutoColumns().forEach(function (columnToClear) {
      // Do not clear if either holding shift, or if column in question was clicked
      if (!(columnToClear === columnToSkip)) {
        // setting to 'undefined' as null means 'none' rather than cleared, otherwise issue will arise
        // if sort order is: ['desc', null , 'asc'], as it will start at null rather than 'desc'.
        columnToClear.setSort(undefined, source);
      }
    });
  };

  SortController.prototype.getNextSortDirection = function (column) {
    var sortingOrder;

    if (column.getColDef().sortingOrder) {
      sortingOrder = column.getColDef().sortingOrder;
    } else if (this.gridOptionsWrapper.getSortingOrder()) {
      sortingOrder = this.gridOptionsWrapper.getSortingOrder();
    } else {
      sortingOrder = SortController_1.DEFAULT_SORTING_ORDER;
    }

    if (!Array.isArray(sortingOrder) || sortingOrder.length <= 0) {
      console.warn("ag-grid: sortingOrder must be an array with at least one element, currently it's " + sortingOrder);
      return null;
    }

    var currentIndex = sortingOrder.indexOf(column.getSort());
    var notInArray = currentIndex < 0;
    var lastItemInArray = currentIndex == sortingOrder.length - 1;
    var result;

    if (notInArray || lastItemInArray) {
      result = sortingOrder[0];
    } else {
      result = sortingOrder[currentIndex + 1];
    } // verify the sort type exists, as the user could provide the sortingOrder, need to make sure it's valid


    if (SortController_1.DEFAULT_SORTING_ORDER.indexOf(result) < 0) {
      console.warn('ag-grid: invalid sort type ' + result);
      return null;
    }

    return result;
  };

  SortController.prototype.setSortModel = function (sortModel, source) {
    var _this = this;

    if (source === void 0) {
      source = "api";
    } // first up, clear any previous sort


    var sortModelProvided = sortModel && sortModel.length > 0;
    var allColumnsIncludingAuto = this.columnController.getPrimaryAndSecondaryAndAutoColumns();
    allColumnsIncludingAuto.forEach(function (column) {
      var sortForCol = null;
      var sortedAt = -1;

      if (sortModelProvided && column.getColDef().sortable) {
        for (var j = 0; j < sortModel.length; j++) {
          var sortModelEntry = sortModel[j];

          if (typeof sortModelEntry.colId === 'string' && typeof column.getColId() === 'string' && _this.compareColIds(sortModelEntry, column)) {
            sortForCol = sortModelEntry.sort;
            sortedAt = j;
          }
        }
      }

      if (sortForCol) {
        column.setSort(sortForCol, source);
        column.setSortedAt(sortedAt);
      } else {
        column.setSort(null, source);
        column.setSortedAt(null);
      }
    });
    this.dispatchSortChangedEvents();
  };

  SortController.prototype.compareColIds = function (sortModelEntry, column) {
    return sortModelEntry.colId === column.getColId();
  };

  SortController.prototype.getColumnsWithSortingOrdered = function () {
    // pull out all the columns that have sorting set
    var allColumnsIncludingAuto = this.columnController.getPrimaryAndSecondaryAndAutoColumns();
    var columnsWithSorting = allColumnsIncludingAuto.filter(function (column) {
      return !!column.getSort();
    }); // put the columns in order of which one got sorted first

    columnsWithSorting.sort(function (a, b) {
      return a.sortedAt - b.sortedAt;
    });
    return columnsWithSorting;
  }; // used by row controller, when doing the sorting


  SortController.prototype.getSortForRowController = function () {
    return this.getColumnsWithSortingOrdered().map(function (column) {
      var isAscending = column.getSort() === Constants.SORT_ASC;
      return {
        inverter: isAscending ? 1 : -1,
        column: column
      };
    });
  };

  var SortController_1;
  SortController.DEFAULT_SORTING_ORDER = [Constants.SORT_ASC, Constants.SORT_DESC, null];

  __decorate$13([Autowired('gridOptionsWrapper')], SortController.prototype, "gridOptionsWrapper", void 0);

  __decorate$13([Autowired('columnController')], SortController.prototype, "columnController", void 0);

  __decorate$13([Autowired('eventService')], SortController.prototype, "eventService", void 0);

  __decorate$13([Autowired('columnApi')], SortController.prototype, "columnApi", void 0);

  __decorate$13([Autowired('gridApi')], SortController.prototype, "gridApi", void 0);

  SortController = SortController_1 = __decorate$13([Bean('sortController')], SortController);
  return SortController;
}();
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __decorate$14 = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var FocusedCellController =
/** @class */
function () {
  function FocusedCellController() {}

  FocusedCellController.prototype.init = function () {
    this.eventService.addEventListener(Events.EVENT_COLUMN_PIVOT_MODE_CHANGED, this.clearFocusedCell.bind(this));
    this.eventService.addEventListener(Events.EVENT_COLUMN_EVERYTHING_CHANGED, this.clearFocusedCell.bind(this));
    this.eventService.addEventListener(Events.EVENT_COLUMN_GROUP_OPENED, this.clearFocusedCell.bind(this));
    this.eventService.addEventListener(Events.EVENT_COLUMN_ROW_GROUP_CHANGED, this.clearFocusedCell.bind(this)); // we used to remove focus when moving column, am not sure why. so taking this out and see who complains.
    // we can delete these three lines of code soon.
    // this.eventService.addEventListener(Events.EVENT_COLUMN_MOVED, this.clearFocusedCell.bind(this));
    // this.eventService.addEventListener(Events.EVENT_COLUMN_PINNED, this.clearFocusedCell.bind(this));
    // this.eventService.addEventListener(Events.EVENT_COLUMN_VISIBLE, this.clearFocusedCell.bind(this));
  };

  FocusedCellController.prototype.clearFocusedCell = function () {
    this.focusedCellPosition = null;
    this.onCellFocused(false);
  };

  FocusedCellController.prototype.getFocusedCell = function () {
    return this.focusedCellPosition;
  }; // we check if the browser is focusing something, and if it is, and
  // it's the cell we think is focused, then return the cell. so this
  // methods returns the cell if a) we think it has focus and b) the
  // browser thinks it has focus. this then returns nothing if we
  // first focus a cell, then second click outside the grid, as then the
  // grid cell will still be focused as far as the grid is concerned,
  // however the browser focus will have moved somewhere else.


  FocusedCellController.prototype.getFocusCellToUseAfterRefresh = function () {
    if (this.gridOptionsWrapper.isSuppressFocusAfterRefresh()) {
      return null;
    }

    if (!this.focusedCellPosition) {
      return null;
    } // we check that the browser is actually focusing on the grid, if it is not, then
    // we have nothing to worry about


    var browserFocusedCell = this.getGridCellForDomElement(document.activeElement);

    if (!browserFocusedCell) {
      return null;
    }

    return this.focusedCellPosition;
  };

  FocusedCellController.prototype.getGridCellForDomElement = function (eBrowserCell) {
    var ePointer = eBrowserCell;

    while (ePointer) {
      var cellComp = this.gridOptionsWrapper.getDomData(ePointer, CellComp.DOM_DATA_KEY_CELL_COMP);

      if (cellComp) {
        return cellComp.getCellPosition();
      }

      ePointer = ePointer.parentNode;
    }

    return null;
  };

  FocusedCellController.prototype.setFocusedCell = function (rowIndex, colKey, floating, forceBrowserFocus) {
    if (forceBrowserFocus === void 0) {
      forceBrowserFocus = false;
    }

    var column = _.makeNull(this.columnController.getGridColumn(colKey));

    this.focusedCellPosition = {
      rowIndex: rowIndex,
      rowPinned: _.makeNull(floating),
      column: column
    };
    this.onCellFocused(forceBrowserFocus);
  };

  FocusedCellController.prototype.isCellFocused = function (cellPosition) {
    if (_.missing(this.focusedCellPosition)) {
      return false;
    }

    return this.focusedCellPosition.column === cellPosition.column && this.isRowFocused(cellPosition.rowIndex, cellPosition.rowPinned);
  };

  FocusedCellController.prototype.isRowNodeFocused = function (rowNode) {
    return this.isRowFocused(rowNode.rowIndex, rowNode.rowPinned);
  };

  FocusedCellController.prototype.isAnyCellFocused = function () {
    return !!this.focusedCellPosition;
  };

  FocusedCellController.prototype.isRowFocused = function (rowIndex, floating) {
    if (_.missing(this.focusedCellPosition)) {
      return false;
    }

    var floatingOrNull = _.makeNull(floating);

    return this.focusedCellPosition.rowIndex === rowIndex && this.focusedCellPosition.rowPinned === floatingOrNull;
  };

  FocusedCellController.prototype.onCellFocused = function (forceBrowserFocus) {
    var event = {
      type: Events.EVENT_CELL_FOCUSED,
      forceBrowserFocus: forceBrowserFocus,
      rowIndex: null,
      column: null,
      floating: null,
      api: this.gridApi,
      columnApi: this.columnApi,
      rowPinned: null
    };

    if (this.focusedCellPosition) {
      event.rowIndex = this.focusedCellPosition.rowIndex;
      event.column = this.focusedCellPosition.column;
      event.rowPinned = this.focusedCellPosition.rowPinned;
    }

    this.eventService.dispatchEvent(event);
  };

  __decorate$14([Autowired('eventService')], FocusedCellController.prototype, "eventService", void 0);

  __decorate$14([Autowired('gridOptionsWrapper')], FocusedCellController.prototype, "gridOptionsWrapper", void 0);

  __decorate$14([Autowired('columnController')], FocusedCellController.prototype, "columnController", void 0);

  __decorate$14([Autowired('columnApi')], FocusedCellController.prototype, "columnApi", void 0);

  __decorate$14([Autowired('gridApi')], FocusedCellController.prototype, "gridApi", void 0);

  __decorate$14([PostConstruct], FocusedCellController.prototype, "init", null);

  FocusedCellController = __decorate$14([Bean('focusedCellController')], FocusedCellController);
  return FocusedCellController;
}();
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __decorate$15 = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var MouseEventService =
/** @class */
function () {
  function MouseEventService() {
    this.gridInstanceId = MouseEventService_1.gridInstanceSequence.next();
  }

  MouseEventService_1 = MouseEventService;

  MouseEventService.prototype.init = function () {
    this.stampDomElementWithGridInstance();
  }; // we put the instance id onto the main DOM element. this is used for events, when grids are inside grids,
  // so the grid can work out if the even came from this grid or a grid inside this one. see the ctrl+v logic
  // for where this is used.


  MouseEventService.prototype.stampDomElementWithGridInstance = function () {
    this.eGridDiv[MouseEventService_1.GRID_DOM_KEY] = this.gridInstanceId;
  };

  MouseEventService.prototype.getRenderedCellForEvent = function (event) {
    return _.getCellCompForEvent(this.gridOptionsWrapper, event);
  }; // walks the path of the event, and returns true if this grid is the first one that it finds. if doing
  // master / detail grids, and a child grid is found, then it returns false. this stops things like copy/paste
  // getting executed on many grids at the same time.


  MouseEventService.prototype.isEventFromThisGrid = function (event) {
    var path = _.getEventPath(event);

    for (var i = 0; i < path.length; i++) {
      var element = path[i];
      var instanceId = element[MouseEventService_1.GRID_DOM_KEY];

      if (_.exists(instanceId)) {
        var eventFromThisGrid = instanceId === this.gridInstanceId;
        return eventFromThisGrid;
      }
    }

    return false;
  };

  MouseEventService.prototype.getCellPositionForEvent = function (event) {
    var cellComp = this.getRenderedCellForEvent(event);
    return cellComp ? cellComp.getCellPosition() : null;
  };

  var MouseEventService_1;
  MouseEventService.gridInstanceSequence = new NumberSequence();
  MouseEventService.GRID_DOM_KEY = '__ag_grid_instance';

  __decorate$15([Autowired('gridOptionsWrapper')], MouseEventService.prototype, "gridOptionsWrapper", void 0);

  __decorate$15([Autowired('eGridDiv')], MouseEventService.prototype, "eGridDiv", void 0);

  __decorate$15([PostConstruct], MouseEventService.prototype, "init", null);

  MouseEventService = MouseEventService_1 = __decorate$15([Bean('mouseEventService')], MouseEventService);
  return MouseEventService;
}();
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __decorate$16 = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var CellNavigationService =
/** @class */
function () {
  function CellNavigationService() {} // returns null if no cell to focus on, ie at the end of the grid


  CellNavigationService.prototype.getNextCellToFocus = function (key, lastCellToFocus) {
    // starting with the provided cell, we keep moving until we find a cell we can
    // focus on.
    var pointer = lastCellToFocus;
    var finished = false; // finished will be true when either:
    // a) cell found that we can focus on
    // b) run out of cells (ie the method returns null)

    while (!finished) {
      switch (key) {
        case Constants.KEY_UP:
          pointer = this.getCellAbove(pointer);
          break;

        case Constants.KEY_DOWN:
          pointer = this.getCellBelow(pointer);
          break;

        case Constants.KEY_RIGHT:
          if (this.gridOptionsWrapper.isEnableRtl()) {
            pointer = this.getCellToLeft(pointer);
          } else {
            pointer = this.getCellToRight(pointer);
          }

          break;

        case Constants.KEY_LEFT:
          if (this.gridOptionsWrapper.isEnableRtl()) {
            pointer = this.getCellToRight(pointer);
          } else {
            pointer = this.getCellToLeft(pointer);
          }

          break;

        default:
          pointer = null;
          console.warn('ag-Grid: unknown key for navigation ' + key);
          break;
      }

      if (pointer) {
        finished = this.isCellGoodToFocusOn(pointer);
      } else {
        finished = true;
      }
    }

    return pointer;
  };

  CellNavigationService.prototype.isCellGoodToFocusOn = function (gridCell) {
    var column = gridCell.column;
    var rowNode;

    switch (gridCell.rowPinned) {
      case Constants.PINNED_TOP:
        rowNode = this.pinnedRowModel.getPinnedTopRow(gridCell.rowIndex);
        break;

      case Constants.PINNED_BOTTOM:
        rowNode = this.pinnedRowModel.getPinnedBottomRow(gridCell.rowIndex);
        break;

      default:
        rowNode = this.rowModel.getRow(gridCell.rowIndex);
        break;
    }

    var suppressNavigable = column.isSuppressNavigable(rowNode);
    return !suppressNavigable;
  };

  CellNavigationService.prototype.getCellToLeft = function (lastCell) {
    if (!lastCell) {
      return null;
    }

    var colToLeft = this.columnController.getDisplayedColBefore(lastCell.column);

    if (!colToLeft) {
      return null;
    }

    return {
      rowIndex: lastCell.rowIndex,
      column: colToLeft,
      rowPinned: lastCell.rowPinned
    };
  };

  CellNavigationService.prototype.getCellToRight = function (lastCell) {
    if (!lastCell) {
      return null;
    }

    var colToRight = this.columnController.getDisplayedColAfter(lastCell.column); // if already on right, do nothing

    if (!colToRight) {
      return null;
    }

    return {
      rowIndex: lastCell.rowIndex,
      column: colToRight,
      rowPinned: lastCell.rowPinned
    };
  };

  CellNavigationService.prototype.getRowBelow = function (rowPosition) {
    // if already on top row, do nothing
    var index = rowPosition.rowIndex;
    var pinned = rowPosition.rowPinned;

    if (this.isLastRowInContainer(rowPosition)) {
      switch (pinned) {
        case Constants.PINNED_BOTTOM:
          // never any rows after pinned bottom
          return null;

        case Constants.PINNED_TOP:
          // if on last row of pinned top, then next row is main body (if rows exist),
          // otherwise it's the pinned bottom
          if (this.rowModel.isRowsToRender()) {
            return {
              rowIndex: 0,
              rowPinned: null
            };
          } else if (this.pinnedRowModel.isRowsToRender(Constants.PINNED_BOTTOM)) {
            return {
              rowIndex: 0,
              rowPinned: Constants.PINNED_BOTTOM
            };
          }

          return null;

        default:
          // if in the main body, then try pinned bottom, otherwise return nothing
          if (this.pinnedRowModel.isRowsToRender(Constants.PINNED_BOTTOM)) {
            return {
              rowIndex: 0,
              rowPinned: Constants.PINNED_BOTTOM
            };
          }

          return null;
      }
    }

    return {
      rowIndex: index + 1,
      rowPinned: pinned
    };
  };

  CellNavigationService.prototype.getCellBelow = function (lastCell) {
    if (!lastCell) {
      return null;
    }

    var rowBelow = this.getRowBelow(lastCell);

    if (rowBelow) {
      return {
        rowIndex: rowBelow.rowIndex,
        column: lastCell.column,
        rowPinned: rowBelow.rowPinned
      };
    }

    return null;
  };

  CellNavigationService.prototype.isLastRowInContainer = function (rowPosition) {
    var pinned = rowPosition.rowPinned;
    var index = rowPosition.rowIndex;

    if (pinned === Constants.PINNED_TOP) {
      var lastTopIndex = this.pinnedRowModel.getPinnedTopRowData().length - 1;
      return lastTopIndex <= index;
    } else if (pinned === Constants.PINNED_BOTTOM) {
      var lastBottomIndex = this.pinnedRowModel.getPinnedBottomRowData().length - 1;
      return lastBottomIndex <= index;
    }

    var lastBodyIndex = this.rowModel.getRowCount() - 1;
    return lastBodyIndex <= index;
  };

  CellNavigationService.prototype.getRowAbove = function (rowPosition) {
    // if already on top row, do nothing
    var index = rowPosition.rowIndex;
    var pinned = rowPosition.rowPinned; // if already on top row, do nothing

    if (index === 0) {
      if (pinned === Constants.PINNED_TOP) {
        return null;
      } else if (!pinned) {
        if (this.pinnedRowModel.isRowsToRender(Constants.PINNED_TOP)) {
          return this.getLastFloatingTopRow();
        }

        return null;
      } else {
        // last floating bottom
        if (this.rowModel.isRowsToRender()) {
          return this.getLastBodyCell();
        } else if (this.pinnedRowModel.isRowsToRender(Constants.PINNED_TOP)) {
          return this.getLastFloatingTopRow();
        }

        return null;
      }
    }

    return {
      rowIndex: index - 1,
      rowPinned: pinned
    };
  };

  CellNavigationService.prototype.getCellAbove = function (lastCell) {
    if (!lastCell) {
      return null;
    }

    var rowAbove = this.getRowAbove({
      rowIndex: lastCell.rowIndex,
      rowPinned: lastCell.rowPinned
    });

    if (rowAbove) {
      return {
        rowIndex: rowAbove.rowIndex,
        column: lastCell.column,
        rowPinned: rowAbove.rowPinned
      };
    }

    return null;
  };

  CellNavigationService.prototype.getLastBodyCell = function () {
    var lastBodyRow = this.rowModel.getRowCount() - 1;
    return {
      rowIndex: lastBodyRow,
      rowPinned: null
    };
  };

  CellNavigationService.prototype.getLastFloatingTopRow = function () {
    var lastFloatingRow = this.pinnedRowModel.getPinnedTopRowData().length - 1;
    return {
      rowIndex: lastFloatingRow,
      rowPinned: Constants.PINNED_TOP
    };
  };

  CellNavigationService.prototype.getNextTabbedCell = function (gridCell, backwards) {
    if (backwards) {
      return this.getNextTabbedCellBackwards(gridCell);
    }

    return this.getNextTabbedCellForwards(gridCell);
  };

  CellNavigationService.prototype.getNextTabbedCellForwards = function (gridCell) {
    var displayedColumns = this.columnController.getAllDisplayedColumns();
    var newRowIndex = gridCell.rowIndex;
    var newFloating = gridCell.rowPinned; // move along to the next cell

    var newColumn = this.columnController.getDisplayedColAfter(gridCell.column); // check if end of the row, and if so, go forward a row

    if (!newColumn) {
      newColumn = displayedColumns[0];
      var rowBelow = this.getRowBelow(gridCell);

      if (_.missing(rowBelow)) {
        return null;
      }

      newRowIndex = rowBelow ? rowBelow.rowIndex : null;
      newFloating = rowBelow ? rowBelow.rowPinned : null;
    }

    return {
      rowIndex: newRowIndex,
      column: newColumn,
      rowPinned: newFloating
    };
  };

  CellNavigationService.prototype.getNextTabbedCellBackwards = function (gridCell) {
    var displayedColumns = this.columnController.getAllDisplayedColumns();
    var newRowIndex = gridCell.rowIndex;
    var newFloating = gridCell.rowPinned; // move along to the next cell

    var newColumn = this.columnController.getDisplayedColBefore(gridCell.column); // check if end of the row, and if so, go forward a row

    if (!newColumn) {
      newColumn = _.last(displayedColumns);
      var rowAbove = this.getRowAbove({
        rowIndex: gridCell.rowIndex,
        rowPinned: gridCell.rowPinned
      });

      if (_.missing(rowAbove)) {
        return null;
      }

      newRowIndex = rowAbove ? rowAbove.rowIndex : null;
      newFloating = rowAbove ? rowAbove.rowPinned : null;
    }

    return {
      rowIndex: newRowIndex,
      column: newColumn,
      rowPinned: newFloating
    };
  };

  __decorate$16([Autowired('columnController')], CellNavigationService.prototype, "columnController", void 0);

  __decorate$16([Autowired('rowModel')], CellNavigationService.prototype, "rowModel", void 0);

  __decorate$16([Autowired('pinnedRowModel')], CellNavigationService.prototype, "pinnedRowModel", void 0);

  __decorate$16([Autowired('gridOptionsWrapper')], CellNavigationService.prototype, "gridOptionsWrapper", void 0);

  CellNavigationService = __decorate$16([Bean('cellNavigationService')], CellNavigationService);
  return CellNavigationService;
}();
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __decorate$17 = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var CellRendererFactory =
/** @class */
function () {
  function CellRendererFactory() {
    this.cellRendererMap = {};
  }

  CellRendererFactory_1 = CellRendererFactory;

  CellRendererFactory.prototype.init = function () {
    this.cellRendererMap[CellRendererFactory_1.ANIMATE_SLIDE] = AnimateSlideCellRenderer;
    this.cellRendererMap[CellRendererFactory_1.ANIMATE_SHOW_CHANGE] = AnimateShowChangeCellRenderer;
    this.cellRendererMap[CellRendererFactory_1.GROUP] = GroupCellRenderer; // this.registerRenderersFromGridOptions();
  }; // private registerRenderersFromGridOptions(): void {
  //     let userProvidedCellRenderers = this.gridOptionsWrapper.getCellRenderers();
  //     _.iterateObject(userProvidedCellRenderers, (key: string, cellRenderer: {new(): ICellRenderer} | ICellRendererFunc)=> {
  //         this.addCellRenderer(key, cellRenderer);
  //     });
  // }


  CellRendererFactory.prototype.addCellRenderer = function (key, cellRenderer) {
    this.cellRendererMap[key] = cellRenderer;
  };

  CellRendererFactory.prototype.getCellRenderer = function (key) {
    var result = this.cellRendererMap[key];

    if (_.missing(result)) {
      console.warn('ag-Grid: unable to find cellRenderer for key ' + key);
      return null;
    }

    return result;
  };

  var CellRendererFactory_1;
  CellRendererFactory.ANIMATE_SLIDE = 'animateSlide';
  CellRendererFactory.ANIMATE_SHOW_CHANGE = 'animateShowChange';
  CellRendererFactory.GROUP = 'group';

  __decorate$17([Autowired('gridOptionsWrapper')], CellRendererFactory.prototype, "gridOptionsWrapper", void 0);

  __decorate$17([Autowired('expressionService')], CellRendererFactory.prototype, "expressionService", void 0);

  __decorate$17([Autowired('eventService')], CellRendererFactory.prototype, "eventService", void 0);

  __decorate$17([PostConstruct], CellRendererFactory.prototype, "init", null);

  CellRendererFactory = CellRendererFactory_1 = __decorate$17([Bean('cellRendererFactory')], CellRendererFactory);
  return CellRendererFactory;
}();
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __decorate$18 = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var ValueFormatterService =
/** @class */
function () {
  function ValueFormatterService() {}

  ValueFormatterService.prototype.formatValue = function (column, rowNode, $scope, value) {
    var formatter;
    var colDef = column.getColDef(); // if floating, give preference to the floating formatter

    if (rowNode && rowNode.rowPinned) {
      formatter = colDef.pinnedRowValueFormatter ? colDef.pinnedRowValueFormatter : colDef.valueFormatter;
    } else {
      formatter = colDef.valueFormatter;
    }

    var result = null;

    if (formatter) {
      var params = {
        value: value,
        node: rowNode,
        data: rowNode ? rowNode.data : null,
        colDef: column.getColDef(),
        column: column,
        api: this.gridOptionsWrapper.getApi(),
        columnApi: this.gridOptionsWrapper.getColumnApi(),
        context: this.gridOptionsWrapper.getContext()
      }; // originally we put the angular 1 scope here, but we don't want the scope
      // in the params interface, as other frameworks will see the interface, and
      // angular 1 is not cool any more. so we hack the scope in here (we cannot
      // include it above, as it's not in the interface, so would cause a compile error).
      // in the future, when we stop supporting angular 1, we can take this out.

      params.$scope = $scope;
      result = this.expressionService.evaluate(formatter, params);
    } else if (colDef.refData) {
      return colDef.refData[value] || '';
    } // if we don't do this, then arrays get displayed as 1,2,3, but we want 1, 2, 3 (ie with spaces)


    if ((result === null || result === undefined) && Array.isArray(value)) {
      result = value.join(', ');
    }

    return result;
  };

  __decorate$18([Autowired('gridOptionsWrapper')], ValueFormatterService.prototype, "gridOptionsWrapper", void 0);

  __decorate$18([Autowired('expressionService')], ValueFormatterService.prototype, "expressionService", void 0);

  ValueFormatterService = __decorate$18([Bean('valueFormatterService')], ValueFormatterService);
  return ValueFormatterService;
}();
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __extends$O = undefined && undefined.__extends || function () {
  var _extendStatics51 = function extendStatics(d, b) {
    _extendStatics51 = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics51(d, b);
  };

  return function (d, b) {
    _extendStatics51(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var AgRadioButton =
/** @class */
function (_super) {
  __extends$O(AgRadioButton, _super);

  function AgRadioButton() {
    var _this = _super !== null && _super.apply(this, arguments) || this;

    _this.className = 'ag-radio-button';
    _this.nativeInputClassName = 'ag-native-radio-button';
    _this.inputType = 'radio';
    _this.iconMap = {
      selected: 'radioButtonOn',
      unselected: 'radioButtonOff'
    };
    return _this;
  }

  AgRadioButton.prototype.toggle = function () {
    var nextValue = this.getNextValue();
    this.setValue(nextValue);
  };

  AgRadioButton.prototype.getIconName = function () {
    var prop = this.getValue() ? 'selected' : 'unselected';
    var readOnlyStr = this.isReadOnly() ? 'ReadOnly' : '';
    return "" + this.iconMap[prop] + readOnlyStr;
  };

  return AgRadioButton;
}(AgCheckbox);
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */

/** The base frameworks, eg React & Angular 2, override this bean with implementations specific to their requirement. */


var VanillaFrameworkOverrides =
/** @class */
function () {
  function VanillaFrameworkOverrides() {} // for Vanilla JS, we use simple timeout


  VanillaFrameworkOverrides.prototype.setTimeout = function (action, timeout) {
    window.setTimeout(action, timeout);
  }; // for Vanilla JS, we just add the event to the element


  VanillaFrameworkOverrides.prototype.addEventListenerOutsideAngular = function (element, type, listener, useCapture) {
    element.addEventListener(type, listener, useCapture);
  };

  return VanillaFrameworkOverrides;
}();
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __decorate$19 = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var ScrollVisibleService =
/** @class */
function () {
  function ScrollVisibleService() {}

  ScrollVisibleService.prototype.setScrollsVisible = function (params) {
    var atLeastOneDifferent = this.horizontalScrollShowing !== params.horizontalScrollShowing || this.verticalScrollShowing !== params.verticalScrollShowing;

    if (atLeastOneDifferent) {
      this.horizontalScrollShowing = params.horizontalScrollShowing;
      this.verticalScrollShowing = params.verticalScrollShowing;
      var event_1 = {
        type: Events.EVENT_SCROLL_VISIBILITY_CHANGED,
        api: this.gridApi,
        columnApi: this.columnApi
      };
      this.eventService.dispatchEvent(event_1);
    }
  }; // used by pagination service - to know page height


  ScrollVisibleService.prototype.isHorizontalScrollShowing = function () {
    return this.horizontalScrollShowing;
  }; // used by header container


  ScrollVisibleService.prototype.isVerticalScrollShowing = function () {
    return this.verticalScrollShowing;
  };

  __decorate$19([Autowired('eventService')], ScrollVisibleService.prototype, "eventService", void 0);

  __decorate$19([Autowired('columnController')], ScrollVisibleService.prototype, "columnController", void 0);

  __decorate$19([Autowired('columnApi')], ScrollVisibleService.prototype, "columnApi", void 0);

  __decorate$19([Autowired('gridApi')], ScrollVisibleService.prototype, "gridApi", void 0);

  __decorate$19([Autowired('gridOptionsWrapper')], ScrollVisibleService.prototype, "gridOptionsWrapper", void 0);

  ScrollVisibleService = __decorate$19([Bean('scrollVisibleService')], ScrollVisibleService);
  return ScrollVisibleService;
}();
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __decorate$1a = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var StylingService =
/** @class */
function () {
  function StylingService() {}

  StylingService.prototype.processAllCellClasses = function (colDef, params, onApplicableClass, onNotApplicableClass) {
    this.processClassRules(colDef.cellClassRules, params, onApplicableClass, onNotApplicableClass);
    this.processStaticCellClasses(colDef, params, onApplicableClass);
  };

  StylingService.prototype.processClassRules = function (classRules, params, onApplicableClass, onNotApplicableClass) {
    if (typeof classRules === 'object' && classRules !== null) {
      var classNames = Object.keys(classRules);

      for (var i = 0; i < classNames.length; i++) {
        var className = classNames[i];
        var rule = classRules[className];
        var resultOfRule = void 0;

        if (typeof rule === 'string') {
          resultOfRule = this.expressionService.evaluate(rule, params);
        } else if (typeof rule === 'function') {
          resultOfRule = rule(params);
        }

        if (resultOfRule) {
          onApplicableClass(className);
        } else if (onNotApplicableClass) {
          onNotApplicableClass(className);
        }
      }
    }
  };

  StylingService.prototype.processStaticCellClasses = function (colDef, params, onApplicableClass) {
    var cellClass = colDef.cellClass;

    if (cellClass) {
      var classOrClasses = void 0;

      if (typeof colDef.cellClass === 'function') {
        var cellClassFunc = colDef.cellClass;
        classOrClasses = cellClassFunc(params);
      } else {
        classOrClasses = colDef.cellClass;
      }

      if (typeof classOrClasses === 'string') {
        onApplicableClass(classOrClasses);
      } else if (Array.isArray(classOrClasses)) {
        classOrClasses.forEach(function (cssClassItem) {
          onApplicableClass(cssClassItem);
        });
      }
    }
  };

  __decorate$1a([Autowired('expressionService')], StylingService.prototype, "expressionService", void 0);

  StylingService = __decorate$1a([Bean('stylingService')], StylingService);
  return StylingService;
}();
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __extends$P = undefined && undefined.__extends || function () {
  var _extendStatics52 = function extendStatics(d, b) {
    _extendStatics52 = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics52(d, b);
  };

  return function (d, b) {
    _extendStatics52(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var __decorate$1b = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var ColumnHoverService =
/** @class */
function (_super) {
  __extends$P(ColumnHoverService, _super);

  function ColumnHoverService() {
    return _super !== null && _super.apply(this, arguments) || this;
  }

  ColumnHoverService.prototype.setMouseOver = function (columns) {
    this.selectedColumns = columns;
    var event = {
      type: Events.EVENT_COLUMN_HOVER_CHANGED,
      api: this.gridApi,
      columnApi: this.columnApi
    };
    this.eventService.dispatchEvent(event);
  };

  ColumnHoverService.prototype.clearMouseOver = function () {
    this.selectedColumns = null;
    var event = {
      type: Events.EVENT_COLUMN_HOVER_CHANGED,
      api: this.gridApi,
      columnApi: this.columnApi
    };
    this.eventService.dispatchEvent(event);
  };

  ColumnHoverService.prototype.isHovered = function (column) {
    return this.selectedColumns && this.selectedColumns.indexOf(column) >= 0;
  };

  __decorate$1b([Autowired('eventService')], ColumnHoverService.prototype, "eventService", void 0);

  __decorate$1b([Autowired('columnApi')], ColumnHoverService.prototype, "columnApi", void 0);

  __decorate$1b([Autowired('gridApi')], ColumnHoverService.prototype, "gridApi", void 0);

  ColumnHoverService = __decorate$1b([Bean('columnHoverService')], ColumnHoverService);
  return ColumnHoverService;
}(BeanStub);
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __decorate$1c = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var ColumnAnimationService =
/** @class */
function () {
  function ColumnAnimationService() {
    this.executeNextFuncs = [];
    this.executeLaterFuncs = [];
    this.active = false;
    this.animationThreadCount = 0;
  }

  ColumnAnimationService.prototype.registerGridComp = function (gridPanel) {
    this.gridPanel = gridPanel;
  };

  ColumnAnimationService.prototype.isActive = function () {
    return this.active;
  };

  ColumnAnimationService.prototype.start = function () {
    if (this.active) {
      return;
    }

    if (this.gridOptionsWrapper.isSuppressColumnMoveAnimation()) {
      return;
    } // if doing RTL, we don't animate open / close as due to how the pixels are inverted,
    // the animation moves all the row the the right rather than to the left (ie it's the static
    // columns that actually get their coordinates updated)


    if (this.gridOptionsWrapper.isEnableRtl()) {
      return;
    }

    this.ensureAnimationCssClassPresent();
    this.active = true;
  };

  ColumnAnimationService.prototype.finish = function () {
    if (!this.active) {
      return;
    }

    this.flush();
    this.active = false;
  };

  ColumnAnimationService.prototype.executeNextVMTurn = function (func) {
    if (this.active) {
      this.executeNextFuncs.push(func);
    } else {
      func();
    }
  };

  ColumnAnimationService.prototype.executeLaterVMTurn = function (func) {
    if (this.active) {
      this.executeLaterFuncs.push(func);
    } else {
      func();
    }
  };

  ColumnAnimationService.prototype.ensureAnimationCssClassPresent = function () {
    var _this = this; // up the count, so we can tell if someone else has updated the count
    // by the time the 'wait' func executes


    this.animationThreadCount++;
    var animationThreadCountCopy = this.animationThreadCount;
    this.gridPanel.setColumnMovingCss(true);
    this.executeLaterFuncs.push(function () {
      // only remove the class if this thread was the last one to update it
      if (_this.animationThreadCount === animationThreadCountCopy) {
        _this.gridPanel.setColumnMovingCss(false);
      }
    });
  };

  ColumnAnimationService.prototype.flush = function () {
    var nowFuncs = this.executeNextFuncs;
    this.executeNextFuncs = [];
    var waitFuncs = this.executeLaterFuncs;
    this.executeLaterFuncs = [];

    if (nowFuncs.length === 0 && waitFuncs.length === 0) {
      return;
    }

    window.setTimeout(function () {
      return nowFuncs.forEach(function (func) {
        return func();
      });
    }, 0);
    window.setTimeout(function () {
      return waitFuncs.forEach(function (func) {
        return func();
      });
    }, 300);
  };

  __decorate$1c([Autowired('gridOptionsWrapper')], ColumnAnimationService.prototype, "gridOptionsWrapper", void 0);

  ColumnAnimationService = __decorate$1c([Bean('columnAnimationService')], ColumnAnimationService);
  return ColumnAnimationService;
}();
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __decorate$1d = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var AutoGroupColService =
/** @class */
function () {
  function AutoGroupColService() {}

  AutoGroupColService_1 = AutoGroupColService;

  AutoGroupColService.prototype.createAutoGroupColumns = function (rowGroupColumns) {
    var _this = this;

    var groupAutoColumns = [];
    var doingTreeData = this.gridOptionsWrapper.isTreeData();
    var doingMultiAutoColumn = this.gridOptionsWrapper.isGroupMultiAutoColumn();

    if (doingTreeData && doingMultiAutoColumn) {
      console.warn('ag-Grid: you cannot mix groupMultiAutoColumn with treeData, only one column can be used to display groups when doing tree data');
      doingMultiAutoColumn = false;
    } // if doing groupMultiAutoColumn, then we call the method multiple times, once
    // for each column we are grouping by


    if (doingMultiAutoColumn) {
      rowGroupColumns.forEach(function (rowGroupCol, index) {
        groupAutoColumns.push(_this.createOneAutoGroupColumn(rowGroupCol, index));
      });
    } else {
      groupAutoColumns.push(this.createOneAutoGroupColumn());
    }

    return groupAutoColumns;
  }; // rowGroupCol and index are missing if groupMultiAutoColumn=false


  AutoGroupColService.prototype.createOneAutoGroupColumn = function (rowGroupCol, index) {
    // if one provided by user, use it, otherwise create one
    var defaultAutoColDef = this.generateDefaultColDef(rowGroupCol); // if doing multi, set the field

    var colId;

    if (rowGroupCol) {
      colId = Constants.GROUP_AUTO_COLUMN_ID + "-" + rowGroupCol.getId();
    } else {
      colId = AutoGroupColService_1.GROUP_AUTO_COLUMN_BUNDLE_ID;
    }

    var userAutoColDef = this.gridOptionsWrapper.getAutoGroupColumnDef();

    _.mergeDeep(defaultAutoColDef, userAutoColDef);

    defaultAutoColDef = this.columnFactory.mergeColDefs(defaultAutoColDef);
    defaultAutoColDef.colId = colId; // For tree data the filter is always allowed

    if (!this.gridOptionsWrapper.isTreeData()) {
      // we would only allow filter if the user has provided field or value getter. otherwise the filter
      // would not be able to work.
      var noFieldOrValueGetter = _.missing(defaultAutoColDef.field) && _.missing(defaultAutoColDef.valueGetter) && _.missing(defaultAutoColDef.filterValueGetter);

      if (noFieldOrValueGetter) {
        defaultAutoColDef.filter = false;
      }
    } // if showing many cols, we don't want to show more than one with a checkbox for selection


    if (index && index > 0) {
      defaultAutoColDef.headerCheckboxSelection = false;
    }

    var newCol = new Column(defaultAutoColDef, null, colId, true);
    this.context.wireBean(newCol);
    return newCol;
  };

  AutoGroupColService.prototype.generateDefaultColDef = function (rowGroupCol) {
    var userAutoColDef = this.gridOptionsWrapper.getAutoGroupColumnDef();
    var localeTextFunc = this.gridOptionsWrapper.getLocaleTextFunc();
    var defaultAutoColDef = {
      headerName: localeTextFunc('group', 'Group')
    };
    var userHasProvidedGroupCellRenderer = userAutoColDef && (userAutoColDef.cellRenderer || userAutoColDef.cellRendererFramework); // only add the default group cell renderer if user hasn't provided one

    if (!userHasProvidedGroupCellRenderer) {
      defaultAutoColDef.cellRenderer = 'agGroupCellRenderer';
    } // we never allow moving the group column
    // defaultAutoColDef.suppressMovable = true;


    if (rowGroupCol) {
      var rowGroupColDef = rowGroupCol.getColDef();

      _.assign(defaultAutoColDef, {
        // cellRendererParams.groupKey: colDefToCopy.field;
        headerName: this.columnController.getDisplayNameForColumn(rowGroupCol, 'header'),
        headerValueGetter: rowGroupColDef.headerValueGetter
      });

      if (rowGroupColDef.cellRenderer) {
        _.assign(defaultAutoColDef, {
          cellRendererParams: {
            innerRenderer: rowGroupColDef.cellRenderer,
            innerRendererParams: rowGroupColDef.cellRendererParams
          }
        });
      }

      defaultAutoColDef.showRowGroup = rowGroupCol.getColId();
    } else {
      defaultAutoColDef.showRowGroup = true;
    }

    return defaultAutoColDef;
  };

  var AutoGroupColService_1;
  AutoGroupColService.GROUP_AUTO_COLUMN_BUNDLE_ID = Constants.GROUP_AUTO_COLUMN_ID;

  __decorate$1d([Autowired('gridOptionsWrapper')], AutoGroupColService.prototype, "gridOptionsWrapper", void 0);

  __decorate$1d([Autowired('context')], AutoGroupColService.prototype, "context", void 0);

  __decorate$1d([Autowired('columnController')], AutoGroupColService.prototype, "columnController", void 0);

  __decorate$1d([Autowired('columnFactory')], AutoGroupColService.prototype, "columnFactory", void 0);

  AutoGroupColService = AutoGroupColService_1 = __decorate$1d([Bean('autoGroupColService')], AutoGroupColService);
  return AutoGroupColService;
}();
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __extends$Q = undefined && undefined.__extends || function () {
  var _extendStatics53 = function extendStatics(d, b) {
    _extendStatics53 = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics53(d, b);
  };

  return function (d, b) {
    _extendStatics53(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var __decorate$1e = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var PaginationProxy =
/** @class */
function (_super) {
  __extends$Q(PaginationProxy, _super);

  function PaginationProxy() {
    var _this = _super !== null && _super.apply(this, arguments) || this;

    _this.currentPage = 0;
    _this.topDisplayedRowIndex = 0;
    _this.bottomDisplayedRowIndex = 0;
    _this.pixelOffset = 0;
    _this.masterRowCount = 0;
    return _this;
  }

  PaginationProxy.prototype.postConstruct = function () {
    this.active = this.gridOptionsWrapper.isPagination();
    this.paginateChildRows = this.gridOptionsWrapper.isPaginateChildRows();
    this.addDestroyableEventListener(this.eventService, Events.EVENT_MODEL_UPDATED, this.onModelUpdated.bind(this));
    this.addDestroyableEventListener(this.gridOptionsWrapper, 'paginationPageSize', this.onModelUpdated.bind(this));
    this.onModelUpdated();
  };

  PaginationProxy.prototype.ensureRowHeightsValid = function (startPixel, endPixel, startLimitIndex, endLimitIndex) {
    var res = this.rowModel.ensureRowHeightsValid(startPixel, endPixel, this.getPageFirstRow(), this.getPageLastRow());

    if (res) {
      this.calculatePages();
    }

    return res;
  };

  PaginationProxy.prototype.onModelUpdated = function (modelUpdatedEvent) {
    this.calculatePages();
    var paginationChangedEvent = {
      type: Events.EVENT_PAGINATION_CHANGED,
      animate: modelUpdatedEvent ? modelUpdatedEvent.animate : false,
      newData: modelUpdatedEvent ? modelUpdatedEvent.newData : false,
      newPage: modelUpdatedEvent ? modelUpdatedEvent.newPage : false,
      keepRenderedRows: modelUpdatedEvent ? modelUpdatedEvent.keepRenderedRows : false,
      api: this.gridApi,
      columnApi: this.columnApi
    };
    this.eventService.dispatchEvent(paginationChangedEvent);
  };

  PaginationProxy.prototype.goToPage = function (page) {
    if (!this.active) {
      return;
    }

    if (this.currentPage === page) {
      return;
    }

    this.currentPage = page;
    var event = {
      type: Events.EVENT_MODEL_UPDATED,
      animate: false,
      keepRenderedRows: false,
      newData: false,
      newPage: true,
      api: this.gridApi,
      columnApi: this.columnApi
    };
    this.onModelUpdated(event);
  };

  PaginationProxy.prototype.getPixelOffset = function () {
    return this.pixelOffset;
  };

  PaginationProxy.prototype.getRow = function (index) {
    return this.rowModel.getRow(index);
  };

  PaginationProxy.prototype.getRowNode = function (id) {
    return this.rowModel.getRowNode(id);
  };

  PaginationProxy.prototype.getRowIndexAtPixel = function (pixel) {
    return this.rowModel.getRowIndexAtPixel(pixel);
  };

  PaginationProxy.prototype.getCurrentPageHeight = function () {
    if (_.missing(this.topRowBounds) || _.missing(this.bottomRowBounds)) {
      return 0;
    }

    return Math.max(this.bottomRowBounds.rowTop + this.bottomRowBounds.rowHeight - this.topRowBounds.rowTop, 0);
  };

  PaginationProxy.prototype.isRowPresent = function (rowNode) {
    if (!this.rowModel.isRowPresent(rowNode)) {
      return false;
    }

    var nodeIsInPage = rowNode.rowIndex >= this.topDisplayedRowIndex && rowNode.rowIndex <= this.bottomDisplayedRowIndex;
    return nodeIsInPage;
  };

  PaginationProxy.prototype.isEmpty = function () {
    return this.rowModel.isEmpty();
  };

  PaginationProxy.prototype.isRowsToRender = function () {
    return this.rowModel.isRowsToRender();
  };

  PaginationProxy.prototype.getNodesInRangeForSelection = function (firstInRange, lastInRange) {
    return this.rowModel.getNodesInRangeForSelection(firstInRange, lastInRange);
  };

  PaginationProxy.prototype.forEachNode = function (callback) {
    return this.rowModel.forEachNode(callback);
  };

  PaginationProxy.prototype.getType = function () {
    return this.rowModel.getType();
  };

  PaginationProxy.prototype.getRowBounds = function (index) {
    var res = this.rowModel.getRowBounds(index);
    res.rowIndex = index;
    return res;
  };

  PaginationProxy.prototype.getPageFirstRow = function () {
    return this.topRowBounds ? this.topRowBounds.rowIndex : -1;
  };

  PaginationProxy.prototype.getPageLastRow = function () {
    return this.bottomRowBounds ? this.bottomRowBounds.rowIndex : -1;
  };

  PaginationProxy.prototype.getRowCount = function () {
    return this.rowModel.getRowCount();
  };

  PaginationProxy.prototype.goToPageWithIndex = function (index) {
    if (!this.active) {
      return;
    }

    var pageNumber = Math.floor(index / this.pageSize);
    this.goToPage(pageNumber);
  };

  PaginationProxy.prototype.isLastPageFound = function () {
    return this.rowModel.isLastRowFound();
  };

  PaginationProxy.prototype.getCurrentPage = function () {
    return this.currentPage;
  };

  PaginationProxy.prototype.goToNextPage = function () {
    this.goToPage(this.currentPage + 1);
  };

  PaginationProxy.prototype.goToPreviousPage = function () {
    this.goToPage(this.currentPage - 1);
  };

  PaginationProxy.prototype.goToFirstPage = function () {
    this.goToPage(0);
  };

  PaginationProxy.prototype.goToLastPage = function () {
    var rowCount = this.rowModel.getRowCount();
    var lastPage = Math.floor(rowCount / this.pageSize);
    this.goToPage(lastPage);
  };

  PaginationProxy.prototype.getPageSize = function () {
    return this.pageSize;
  };

  PaginationProxy.prototype.getTotalPages = function () {
    return this.totalPages;
  };

  PaginationProxy.prototype.setPageSize = function () {
    // show put this into super class
    this.pageSize = this.gridOptionsWrapper.getPaginationPageSize();

    if (!(this.pageSize >= 1)) {
      this.pageSize = 100;
    }
  };

  PaginationProxy.prototype.calculatePages = function () {
    if (this.active) {
      this.setPageSize();

      if (this.paginateChildRows) {
        this.calculatePagesAllRows();
      } else {
        this.calculatePagesMasterRowsOnly();
      }
    } else {
      this.calculatedPagesNotActive();
    }

    this.topRowBounds = this.rowModel.getRowBounds(this.topDisplayedRowIndex);

    if (this.topRowBounds) {
      this.topRowBounds.rowIndex = this.topDisplayedRowIndex;
    }

    this.bottomRowBounds = this.rowModel.getRowBounds(this.bottomDisplayedRowIndex);

    if (this.bottomRowBounds) {
      this.bottomRowBounds.rowIndex = this.bottomDisplayedRowIndex;
    }

    this.pixelOffset = _.exists(this.topRowBounds) ? this.topRowBounds.rowTop : 0;
  };

  PaginationProxy.prototype.setZeroRows = function () {
    this.topDisplayedRowIndex = 0;
    this.bottomDisplayedRowIndex = -1;
    this.currentPage = 0;
    this.totalPages = 0;
  };

  PaginationProxy.prototype.calculatePagesMasterRowsOnly = function () {
    // const csrm = <ClientSideRowModel> this.rowModel;
    // const rootNode = csrm.getRootNode();
    // const masterRows = rootNode.childrenAfterSort;
    this.masterRowCount = this.rowModel.getTopLevelRowCount();

    if (this.masterRowCount === 0) {
      this.setZeroRows();
      return;
    }

    var masterLastRowIndex = this.masterRowCount - 1;
    this.totalPages = Math.floor(masterLastRowIndex / this.pageSize) + 1;

    if (this.currentPage >= this.totalPages) {
      this.currentPage = this.totalPages - 1;
    }

    if (!_.isNumeric(this.currentPage) || this.currentPage < 0) {
      this.currentPage = 0;
    }

    var masterPageStartIndex = this.pageSize * this.currentPage;
    var masterPageEndIndex = this.pageSize * (this.currentPage + 1) - 1;

    if (masterPageEndIndex > masterLastRowIndex) {
      masterPageEndIndex = masterLastRowIndex;
    }

    this.topDisplayedRowIndex = this.rowModel.getTopLevelRowDisplayedIndex(masterPageStartIndex); // masterRows[masterPageStartIndex].rowIndex;

    if (masterPageEndIndex === masterLastRowIndex) {
      // if showing the last master row, then we want to show the very last row of the model
      this.bottomDisplayedRowIndex = this.rowModel.getRowCount() - 1;
    } else {
      var firstIndexNotToShow = this.rowModel.getTopLevelRowDisplayedIndex(masterPageEndIndex + 1); //masterRows[masterPageEndIndex + 1].rowIndex;
      // this gets the index of the last child - eg current row is open, we want to display all children,
      // the index of the last child is one less than the index of the next parent row.

      this.bottomDisplayedRowIndex = firstIndexNotToShow - 1;
    }
  };

  PaginationProxy.prototype.getMasterRowCount = function () {
    return this.masterRowCount;
  };

  PaginationProxy.prototype.calculatePagesAllRows = function () {
    this.masterRowCount = this.rowModel.getRowCount();

    if (this.masterRowCount === 0) {
      this.setZeroRows();
      return;
    }

    var maxRowIndex = this.masterRowCount - 1;
    this.totalPages = Math.floor(maxRowIndex / this.pageSize) + 1;

    if (this.currentPage >= this.totalPages) {
      this.currentPage = this.totalPages - 1;
    }

    if (!_.isNumeric(this.currentPage) || this.currentPage < 0) {
      this.currentPage = 0;
    }

    this.topDisplayedRowIndex = this.pageSize * this.currentPage;
    this.bottomDisplayedRowIndex = this.pageSize * (this.currentPage + 1) - 1;

    if (this.bottomDisplayedRowIndex > maxRowIndex) {
      this.bottomDisplayedRowIndex = maxRowIndex;
    }
  };

  PaginationProxy.prototype.calculatedPagesNotActive = function () {
    this.pageSize = this.rowModel.getRowCount();
    this.totalPages = 1;
    this.currentPage = 0;
    this.topDisplayedRowIndex = 0;
    this.bottomDisplayedRowIndex = this.rowModel.getRowCount() - 1;
  };

  __decorate$1e([Autowired('rowModel')], PaginationProxy.prototype, "rowModel", void 0);

  __decorate$1e([Autowired('eventService')], PaginationProxy.prototype, "eventService", void 0);

  __decorate$1e([Autowired('gridOptionsWrapper')], PaginationProxy.prototype, "gridOptionsWrapper", void 0);

  __decorate$1e([Autowired('selectionController')], PaginationProxy.prototype, "selectionController", void 0);

  __decorate$1e([Autowired('columnApi')], PaginationProxy.prototype, "columnApi", void 0);

  __decorate$1e([Autowired('gridApi')], PaginationProxy.prototype, "gridApi", void 0);

  __decorate$1e([PostConstruct], PaginationProxy.prototype, "postConstruct", null);

  PaginationProxy = __decorate$1e([Bean('paginationProxy')], PaginationProxy);
  return PaginationProxy;
}(BeanStub);
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __extends$R = undefined && undefined.__extends || function () {
  var _extendStatics54 = function extendStatics(d, b) {
    _extendStatics54 = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics54(d, b);
  };

  return function (d, b) {
    _extendStatics54(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var __decorate$1f = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var PaginationAutoPageSizeService =
/** @class */
function (_super) {
  __extends$R(PaginationAutoPageSizeService, _super);

  function PaginationAutoPageSizeService() {
    return _super !== null && _super.apply(this, arguments) || this;
  }

  PaginationAutoPageSizeService.prototype.registerGridComp = function (gridPanel) {
    this.gridPanel = gridPanel;
    this.addDestroyableEventListener(this.eventService, Events.EVENT_BODY_HEIGHT_CHANGED, this.onBodyHeightChanged.bind(this));
    this.addDestroyableEventListener(this.eventService, Events.EVENT_SCROLL_VISIBILITY_CHANGED, this.onScrollVisibilityChanged.bind(this));
    this.checkPageSize();
  };

  PaginationAutoPageSizeService.prototype.notActive = function () {
    return !this.gridOptionsWrapper.isPaginationAutoPageSize();
  };

  PaginationAutoPageSizeService.prototype.onScrollVisibilityChanged = function () {
    this.checkPageSize();
  };

  PaginationAutoPageSizeService.prototype.onBodyHeightChanged = function () {
    this.checkPageSize();
  };

  PaginationAutoPageSizeService.prototype.checkPageSize = function () {
    if (this.notActive()) {
      return;
    }

    var rowHeight = this.gridOptionsWrapper.getRowHeightAsNumber();
    var bodyHeight = this.gridPanel.getBodyHeight();

    if (bodyHeight > 0) {
      var newPageSize = Math.floor(bodyHeight / rowHeight);
      this.gridOptionsWrapper.setProperty('paginationPageSize', newPageSize);
    }
  };

  __decorate$1f([Autowired('eventService')], PaginationAutoPageSizeService.prototype, "eventService", void 0);

  __decorate$1f([Autowired('gridOptionsWrapper')], PaginationAutoPageSizeService.prototype, "gridOptionsWrapper", void 0);

  __decorate$1f([Autowired('scrollVisibleService')], PaginationAutoPageSizeService.prototype, "scrollVisibleService", void 0);

  PaginationAutoPageSizeService = __decorate$1f([Bean('paginationAutoPageSizeService')], PaginationAutoPageSizeService);
  return PaginationAutoPageSizeService;
}(BeanStub);
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __decorate$1g = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var ValueCache =
/** @class */
function () {
  function ValueCache() {
    this.cacheVersion = 0;
  }

  ValueCache.prototype.init = function () {
    this.active = this.gridOptionsWrapper.isValueCache();
    this.neverExpires = this.gridOptionsWrapper.isValueCacheNeverExpires();
  };

  ValueCache.prototype.onDataChanged = function () {
    if (this.neverExpires) {
      return;
    }

    this.expire();
  };

  ValueCache.prototype.expire = function () {
    this.cacheVersion++;
  };

  ValueCache.prototype.setValue = function (rowNode, colId, value) {
    if (this.active) {
      if (rowNode.__cacheVersion !== this.cacheVersion) {
        rowNode.__cacheVersion = this.cacheVersion;
        rowNode.__cacheData = {};
      }

      rowNode.__cacheData[colId] = value;
    }
  };

  ValueCache.prototype.getValue = function (rowNode, colId) {
    if (!this.active || rowNode.__cacheVersion !== this.cacheVersion) {
      return undefined;
    }

    return rowNode.__cacheData[colId];
  };

  __decorate$1g([Autowired('gridOptionsWrapper')], ValueCache.prototype, "gridOptionsWrapper", void 0);

  __decorate$1g([PostConstruct], ValueCache.prototype, "init", null);

  ValueCache = __decorate$1g([Bean('valueCache')], ValueCache);
  return ValueCache;
}();
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __extends$S = undefined && undefined.__extends || function () {
  var _extendStatics55 = function extendStatics(d, b) {
    _extendStatics55 = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics55(d, b);
  };

  return function (d, b) {
    _extendStatics55(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var __decorate$1h = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var ChangeDetectionService =
/** @class */
function (_super) {
  __extends$S(ChangeDetectionService, _super);

  function ChangeDetectionService() {
    return _super !== null && _super.apply(this, arguments) || this;
  }

  ChangeDetectionService.prototype.init = function () {
    if (this.rowModel.getType() === Constants.ROW_MODEL_TYPE_CLIENT_SIDE) {
      this.clientSideRowModel = this.rowModel;
    }

    this.addDestroyableEventListener(this.eventService, Events.EVENT_CELL_VALUE_CHANGED, this.onCellValueChanged.bind(this));
  };

  ChangeDetectionService.prototype.onCellValueChanged = function (event) {
    // Clipboard service manages its own change detection, so no need to do it here.
    // The clipboard manages its own as otherwise this would happen once for every cell
    // that got updated as part of a paste operation, so e.g. if 100 cells in a paste operation,
    // this doChangeDetection would get called 100 times (once for each cell), instead clipboard
    // service executes the logic we have here once (in essence batching up all cell changes
    // into one change detection).
    if (event.source === Constants.SOURCE_PASTE) {
      return;
    }

    this.doChangeDetection(event.node, event.column);
  };

  ChangeDetectionService.prototype.doChangeDetection = function (rowNode, column) {
    if (this.gridOptionsWrapper.isSuppressChangeDetection()) {
      return;
    } // step 1 of change detection is to update the aggregated values


    if (this.clientSideRowModel && !rowNode.isRowPinned()) {
      var onlyChangedColumns = this.gridOptionsWrapper.isAggregateOnlyChangedColumns();
      var changedPath = new ChangedPath(onlyChangedColumns, this.clientSideRowModel.getRootNode());
      changedPath.addParentNode(rowNode.parent, [column]);
      this.clientSideRowModel.doAggregate(changedPath);
    } // step 2 of change detection is to refresh the cells


    this.rowRenderer.refreshCells();
  };

  __decorate$1h([Autowired('gridOptionsWrapper')], ChangeDetectionService.prototype, "gridOptionsWrapper", void 0);

  __decorate$1h([Autowired('rowModel')], ChangeDetectionService.prototype, "rowModel", void 0);

  __decorate$1h([Autowired('rowRenderer')], ChangeDetectionService.prototype, "rowRenderer", void 0);

  __decorate$1h([Autowired('eventService')], ChangeDetectionService.prototype, "eventService", void 0);

  __decorate$1h([PostConstruct], ChangeDetectionService.prototype, "init", null);

  ChangeDetectionService = __decorate$1h([Bean('changeDetectionService')], ChangeDetectionService);
  return ChangeDetectionService;
}(BeanStub);
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __decorate$1i = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var __param$9 = undefined && undefined.__param || function (paramIndex, decorator) {
  return function (target, key) {
    decorator(target, key, paramIndex);
  };
};

var AlignedGridsService =
/** @class */
function () {
  function AlignedGridsService() {
    // flag to mark if we are consuming. to avoid cyclic events (ie other grid firing back to master
    // while processing a master event) we mark this if consuming an event, and if we are, then
    // we don't fire back any events.
    this.consuming = false;
  }

  AlignedGridsService.prototype.setBeans = function (loggerFactory) {
    this.logger = loggerFactory.create('AlignedGridsService');
  };

  AlignedGridsService.prototype.registerGridComp = function (gridPanel) {
    this.gridPanel = gridPanel;
  };

  AlignedGridsService.prototype.init = function () {
    this.eventService.addEventListener(Events.EVENT_COLUMN_MOVED, this.fireColumnEvent.bind(this));
    this.eventService.addEventListener(Events.EVENT_COLUMN_VISIBLE, this.fireColumnEvent.bind(this));
    this.eventService.addEventListener(Events.EVENT_COLUMN_PINNED, this.fireColumnEvent.bind(this));
    this.eventService.addEventListener(Events.EVENT_COLUMN_GROUP_OPENED, this.fireColumnEvent.bind(this));
    this.eventService.addEventListener(Events.EVENT_COLUMN_RESIZED, this.fireColumnEvent.bind(this));
    this.eventService.addEventListener(Events.EVENT_BODY_SCROLL, this.fireScrollEvent.bind(this));
  }; // common logic across all the fire methods


  AlignedGridsService.prototype.fireEvent = function (callback) {
    // if we are already consuming, then we are acting on an event from a master,
    // so we don't cause a cyclic firing of events
    if (this.consuming) {
      return;
    } // iterate through the aligned grids, and pass each aligned grid service to the callback


    var otherGrids = this.gridOptionsWrapper.getAlignedGrids();

    if (otherGrids) {
      otherGrids.forEach(function (otherGridOptions) {
        if (otherGridOptions.api) {
          var alignedGridService = otherGridOptions.api.__getAlignedGridService();

          callback(alignedGridService);
        }
      });
    }
  }; // common logic across all consume methods. very little common logic, however extracting
  // guarantees consistency across the methods.


  AlignedGridsService.prototype.onEvent = function (callback) {
    this.consuming = true;
    callback();
    this.consuming = false;
  };

  AlignedGridsService.prototype.fireColumnEvent = function (event) {
    this.fireEvent(function (alignedGridsService) {
      alignedGridsService.onColumnEvent(event);
    });
  };

  AlignedGridsService.prototype.fireScrollEvent = function (event) {
    if (event.direction !== 'horizontal') {
      return;
    }

    this.fireEvent(function (alignedGridsService) {
      alignedGridsService.onScrollEvent(event);
    });
  };

  AlignedGridsService.prototype.onScrollEvent = function (event) {
    var _this = this;

    this.onEvent(function () {
      _this.gridPanel.setHorizontalScrollPosition(event.left);
    });
  };

  AlignedGridsService.prototype.getMasterColumns = function (event) {
    var result = [];

    if (event.columns) {
      event.columns.forEach(function (column) {
        result.push(column);
      });
    } else if (event.column) {
      result.push(event.column);
    }

    return result;
  };

  AlignedGridsService.prototype.getColumnIds = function (event) {
    var result = [];

    if (event.columns) {
      event.columns.forEach(function (column) {
        result.push(column.getColId());
      });
    } else if (event.column) {
      result.push(event.column.getColId());
    }

    return result;
  };

  AlignedGridsService.prototype.onColumnEvent = function (event) {
    var _this = this;

    this.onEvent(function () {
      switch (event.type) {
        case Events.EVENT_COLUMN_MOVED:
        case Events.EVENT_COLUMN_VISIBLE:
        case Events.EVENT_COLUMN_PINNED:
        case Events.EVENT_COLUMN_RESIZED:
          var colEvent = event;

          _this.processColumnEvent(colEvent);

          break;

        case Events.EVENT_COLUMN_GROUP_OPENED:
          var groupOpenedEvent = event;

          _this.processGroupOpenedEvent(groupOpenedEvent);

          break;

        case Events.EVENT_COLUMN_PIVOT_CHANGED:
          // we cannot support pivoting with aligned grids as the columns will be out of sync as the
          // grids will have columns created based on the row data of the grid.
          console.warn('ag-Grid: pivoting is not supported with aligned grids. ' + 'You can only use one of these features at a time in a grid.');
          break;
      }
    });
  };

  AlignedGridsService.prototype.processGroupOpenedEvent = function (groupOpenedEvent) {
    // likewise for column group
    var masterColumnGroup = groupOpenedEvent.columnGroup;
    var otherColumnGroup;

    if (masterColumnGroup) {
      var groupId = masterColumnGroup.getGroupId();
      otherColumnGroup = this.columnController.getOriginalColumnGroup(groupId);
    }

    if (masterColumnGroup && !otherColumnGroup) {
      return;
    }

    this.logger.log('onColumnEvent-> processing ' + groupOpenedEvent + ' expanded = ' + masterColumnGroup.isExpanded());
    this.columnController.setColumnGroupOpened(otherColumnGroup, masterColumnGroup.isExpanded(), "alignedGridChanged");
  };

  AlignedGridsService.prototype.processColumnEvent = function (colEvent) {
    var _this = this; // the column in the event is from the master grid. need to
    // look up the equivalent from this (other) grid


    var masterColumn = colEvent.column;
    var otherColumn;

    if (masterColumn) {
      otherColumn = this.columnController.getPrimaryColumn(masterColumn.getColId());
    } // if event was with respect to a master column, that is not present in this
    // grid, then we ignore the event


    if (masterColumn && !otherColumn) {
      return;
    } // in time, all the methods below should use the column ids, it's a more generic way
    // of handling columns, and also allows for single or multi column events


    var columnIds = this.getColumnIds(colEvent);
    var masterColumns = this.getMasterColumns(colEvent);

    switch (colEvent.type) {
      case Events.EVENT_COLUMN_MOVED:
        var movedEvent = colEvent;
        this.logger.log("onColumnEvent-> processing " + colEvent.type + " toIndex = " + movedEvent.toIndex);
        this.columnController.moveColumns(columnIds, movedEvent.toIndex, "alignedGridChanged");
        break;

      case Events.EVENT_COLUMN_VISIBLE:
        var visibleEvent = colEvent;
        this.logger.log("onColumnEvent-> processing " + colEvent.type + " visible = " + visibleEvent.visible);
        this.columnController.setColumnsVisible(columnIds, visibleEvent.visible, "alignedGridChanged");
        break;

      case Events.EVENT_COLUMN_PINNED:
        var pinnedEvent = colEvent;
        this.logger.log("onColumnEvent-> processing " + colEvent.type + " pinned = " + pinnedEvent.pinned);
        this.columnController.setColumnsPinned(columnIds, pinnedEvent.pinned, "alignedGridChanged");
        break;

      case Events.EVENT_COLUMN_RESIZED:
        var resizedEvent_1 = colEvent;
        masterColumns.forEach(function (column) {
          _this.logger.log("onColumnEvent-> processing " + colEvent.type + " actualWidth = " + column.getActualWidth());

          _this.columnController.setColumnWidth(column.getColId(), column.getActualWidth(), false, resizedEvent_1.finished, "alignedGridChanged");
        });
        break;
    }

    var isVerticalScrollShowing = this.gridPanel.isVerticalScrollShowing();
    var alignedGrids = this.gridOptionsWrapper.getAlignedGrids();
    alignedGrids.forEach(function (grid) {
      grid.api.setAlwaysShowVerticalScroll(isVerticalScrollShowing);
    });
  };

  __decorate$1i([Autowired('gridOptionsWrapper')], AlignedGridsService.prototype, "gridOptionsWrapper", void 0);

  __decorate$1i([Autowired('columnController')], AlignedGridsService.prototype, "columnController", void 0);

  __decorate$1i([Autowired('eventService')], AlignedGridsService.prototype, "eventService", void 0);

  __decorate$1i([__param$9(0, Qualifier('loggerFactory'))], AlignedGridsService.prototype, "setBeans", null);

  __decorate$1i([PostConstruct], AlignedGridsService.prototype, "init", null);

  AlignedGridsService = __decorate$1i([Bean('alignedGridsService')], AlignedGridsService);
  return AlignedGridsService;
}();
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __decorate$1j = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var AgComponentUtils =
/** @class */
function () {
  function AgComponentUtils() {}

  AgComponentUtils.prototype.adaptFunction = function (propertyName, hardcodedJsFunction, componentFromFramework, source) {
    if (hardcodedJsFunction == null) {
      return {
        component: null,
        componentFromFramework: componentFromFramework,
        source: source,
        paramsFromSelector: null
      };
    }

    var metadata = this.componentMetadataProvider.retrieve(propertyName);

    if (metadata && metadata.functionAdapter) {
      return {
        componentFromFramework: componentFromFramework,
        component: metadata.functionAdapter(hardcodedJsFunction),
        source: source,
        paramsFromSelector: null
      };
    }

    return null;
  };

  AgComponentUtils.prototype.adaptCellRendererFunction = function (callback) {
    var Adapter =
    /** @class */
    function () {
      function Adapter() {}

      Adapter.prototype.refresh = function (params) {
        return false;
      };

      Adapter.prototype.getGui = function () {
        var callbackResult = callback(this.params);
        var type = typeof callbackResult;

        if (type === 'string' || type === 'number' || type === 'boolean') {
          return _.loadTemplate('<span>' + callbackResult + '</span>');
        } else {
          return callbackResult;
        }
      };

      Adapter.prototype.init = function (params) {
        this.params = params;
      };

      return Adapter;
    }();

    return Adapter;
  };

  AgComponentUtils.prototype.doesImplementIComponent = function (candidate) {
    if (!candidate) {
      return false;
    }

    return candidate.prototype && 'getGui' in candidate.prototype;
  };

  __decorate$1j([Autowired("componentMetadataProvider")], AgComponentUtils.prototype, "componentMetadataProvider", void 0);

  AgComponentUtils = __decorate$1j([Bean("agComponentUtils")], AgComponentUtils);
  return AgComponentUtils;
}();
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __decorate$1k = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var ComponentMetadataProvider =
/** @class */
function () {
  function ComponentMetadataProvider() {}

  ComponentMetadataProvider.prototype.postConstruct = function () {
    this.componentMetaData = {
      dateComponent: {
        mandatoryMethodList: ['getDate', 'setDate'],
        optionalMethodList: ['afterGuiAttached']
      },
      detailCellRenderer: {
        mandatoryMethodList: [],
        optionalMethodList: ['refresh']
      },
      headerComponent: {
        mandatoryMethodList: [],
        optionalMethodList: []
      },
      headerGroupComponent: {
        mandatoryMethodList: [],
        optionalMethodList: []
      },
      loadingCellRenderer: {
        mandatoryMethodList: [],
        optionalMethodList: []
      },
      loadingOverlayComponent: {
        mandatoryMethodList: [],
        optionalMethodList: []
      },
      noRowsOverlayComponent: {
        mandatoryMethodList: [],
        optionalMethodList: []
      },
      floatingFilterComponent: {
        mandatoryMethodList: ['onParentModelChanged'],
        optionalMethodList: ['afterGuiAttached']
      },
      floatingFilterWrapperComponent: {
        mandatoryMethodList: [],
        optionalMethodList: []
      },
      cellRenderer: {
        mandatoryMethodList: [],
        optionalMethodList: ['refresh', 'afterGuiAttached'],
        functionAdapter: this.agComponentUtils.adaptCellRendererFunction.bind(this.agComponentUtils)
      },
      cellEditor: {
        mandatoryMethodList: ['getValue'],
        optionalMethodList: ['isPopup', 'isCancelBeforeStart', 'isCancelAfterEnd', 'focusIn', 'focusOut', 'afterGuiAttached']
      },
      innerRenderer: {
        mandatoryMethodList: [],
        optionalMethodList: ['afterGuiAttached'],
        functionAdapter: this.agComponentUtils.adaptCellRendererFunction.bind(this.agComponentUtils)
      },
      fullWidthCellRenderer: {
        mandatoryMethodList: [],
        optionalMethodList: ['refresh', 'afterGuiAttached'],
        functionAdapter: this.agComponentUtils.adaptCellRendererFunction.bind(this.agComponentUtils)
      },
      pinnedRowCellRenderer: {
        mandatoryMethodList: [],
        optionalMethodList: ['refresh', 'afterGuiAttached'],
        functionAdapter: this.agComponentUtils.adaptCellRendererFunction.bind(this.agComponentUtils)
      },
      groupRowInnerRenderer: {
        mandatoryMethodList: [],
        optionalMethodList: ['afterGuiAttached'],
        functionAdapter: this.agComponentUtils.adaptCellRendererFunction.bind(this.agComponentUtils)
      },
      groupRowRenderer: {
        mandatoryMethodList: [],
        optionalMethodList: ['afterGuiAttached'],
        functionAdapter: this.agComponentUtils.adaptCellRendererFunction.bind(this.agComponentUtils)
      },
      filter: {
        mandatoryMethodList: ['isFilterActive', 'doesFilterPass', 'getModel', 'setModel'],
        optionalMethodList: ['afterGuiAttached', 'onNewRowsLoaded', 'getModelAsString', 'onFloatingFilterChanged']
      },
      filterComponent: {
        mandatoryMethodList: ['isFilterActive', 'doesFilterPass', 'getModel', 'setModel'],
        optionalMethodList: ['afterGuiAttached', 'onNewRowsLoaded', 'getModelAsString', 'onFloatingFilterChanged']
      },
      statusPanel: {
        mandatoryMethodList: [],
        optionalMethodList: ['afterGuiAttached']
      },
      toolPanel: {
        mandatoryMethodList: [],
        optionalMethodList: ['refresh', 'afterGuiAttached']
      },
      tooltipComponent: {
        mandatoryMethodList: [],
        optionalMethodList: []
      }
    };
  };

  ComponentMetadataProvider.prototype.retrieve = function (name) {
    return this.componentMetaData[name];
  };

  __decorate$1k([Autowired("agComponentUtils")], ComponentMetadataProvider.prototype, "agComponentUtils", void 0);

  __decorate$1k([PostConstruct], ComponentMetadataProvider.prototype, "postConstruct", null);

  ComponentMetadataProvider = __decorate$1k([Bean("componentMetadataProvider")], ComponentMetadataProvider);
  return ComponentMetadataProvider;
}();
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __decorate$1l = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};
/** Using the IoC has a slight performance consideration, which is no problem most of the
 * time, unless we are trashing objects - which is the case when scrolling and rowComp
 * and cellComp. So for performance reasons, RowComp and CellComp do not get autowired
 * with the IoC. Instead they get passed this object which is all the beans the RowComp
 * and CellComp need. Not autowiring all the cells gives performance improvement. */


var Beans =
/** @class */
function () {
  function Beans() {}

  Beans.prototype.registerGridComp = function (gridPanel) {
    this.gridPanel = gridPanel;
  };

  Beans.prototype.postConstruct = function () {
    this.doingMasterDetail = this.gridOptionsWrapper.isMasterDetail();
  };

  __decorate$1l([Autowired('paginationProxy')], Beans.prototype, "paginationProxy", void 0);

  __decorate$1l([Autowired('context')], Beans.prototype, "context", void 0);

  __decorate$1l([Autowired('columnApi')], Beans.prototype, "columnApi", void 0);

  __decorate$1l([Autowired('gridApi')], Beans.prototype, "gridApi", void 0);

  __decorate$1l([Autowired('gridOptionsWrapper')], Beans.prototype, "gridOptionsWrapper", void 0);

  __decorate$1l([Autowired('expressionService')], Beans.prototype, "expressionService", void 0);

  __decorate$1l([Autowired('rowRenderer')], Beans.prototype, "rowRenderer", void 0);

  __decorate$1l([Autowired('$compile')], Beans.prototype, "$compile", void 0);

  __decorate$1l([Autowired('templateService')], Beans.prototype, "templateService", void 0);

  __decorate$1l([Autowired('valueService')], Beans.prototype, "valueService", void 0);

  __decorate$1l([Autowired('eventService')], Beans.prototype, "eventService", void 0);

  __decorate$1l([Autowired('columnController')], Beans.prototype, "columnController", void 0);

  __decorate$1l([Autowired('columnAnimationService')], Beans.prototype, "columnAnimationService", void 0);

  __decorate$1l([Optional('rangeController')], Beans.prototype, "rangeController", void 0);

  __decorate$1l([Autowired('focusedCellController')], Beans.prototype, "focusedCellController", void 0);

  __decorate$1l([Optional('contextMenuFactory')], Beans.prototype, "contextMenuFactory", void 0);

  __decorate$1l([Autowired('cellRendererFactory')], Beans.prototype, "cellRendererFactory", void 0);

  __decorate$1l([Autowired('popupService')], Beans.prototype, "popupService", void 0);

  __decorate$1l([Autowired('valueFormatterService')], Beans.prototype, "valueFormatterService", void 0);

  __decorate$1l([Autowired('stylingService')], Beans.prototype, "stylingService", void 0);

  __decorate$1l([Autowired('columnHoverService')], Beans.prototype, "columnHoverService", void 0);

  __decorate$1l([Autowired('userComponentFactory')], Beans.prototype, "userComponentFactory", void 0);

  __decorate$1l([Autowired('animationFrameService')], Beans.prototype, "taskQueue", void 0);

  __decorate$1l([Autowired('dragAndDropService')], Beans.prototype, "dragAndDropService", void 0);

  __decorate$1l([Autowired('sortController')], Beans.prototype, "sortController", void 0);

  __decorate$1l([Autowired('filterManager')], Beans.prototype, "filterManager", void 0);

  __decorate$1l([Autowired('maxDivHeightScaler')], Beans.prototype, "maxDivHeightScaler", void 0);

  __decorate$1l([Autowired('tooltipManager')], Beans.prototype, "tooltipManager", void 0);

  __decorate$1l([Autowired('frameworkOverrides')], Beans.prototype, "frameworkOverrides", void 0);

  __decorate$1l([Autowired('detailRowCompCache')], Beans.prototype, "detailRowCompCache", void 0);

  __decorate$1l([Autowired('cellPositionUtils')], Beans.prototype, "cellPositionUtils", void 0);

  __decorate$1l([Autowired('rowPositionUtils')], Beans.prototype, "rowPositionUtils", void 0);

  __decorate$1l([PostConstruct], Beans.prototype, "postConstruct", null);

  Beans = __decorate$1l([Bean('beans')], Beans);
  return Beans;
}();
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __decorate$1m = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var MAT_GRID_SIZE = 8;
var FRESH_GRID_SIZE = 4;
var BALHAM_GRID_SIZE = 4;
var ALPINE_GRID_SIZE = 6;
var HARD_CODED_SIZES = {
  'ag-theme-material': {
    headerHeight: MAT_GRID_SIZE * 7,
    virtualItemHeight: MAT_GRID_SIZE * 5,
    rowHeight: MAT_GRID_SIZE * 6,
    chartMenuPanelWidth: 220
  },
  'ag-theme-classic': {
    headerHeight: 25,
    virtualItemHeight: FRESH_GRID_SIZE * 5,
    rowHeight: 25,
    chartMenuPanelWidth: 220
  },
  'ag-theme-balham': {
    headerHeight: BALHAM_GRID_SIZE * 8,
    virtualItemHeight: BALHAM_GRID_SIZE * 7,
    rowHeight: BALHAM_GRID_SIZE * 7,
    chartMenuPanelWidth: 220
  },
  'ag-theme-alpine': {
    headerHeight: ALPINE_GRID_SIZE * 8,
    virtualItemHeight: ALPINE_GRID_SIZE * 5,
    rowHeight: ALPINE_GRID_SIZE * 7,
    chartMenuPanelWidth: 240
  }
};
/**
 * this object contains a list of Sass variables and an array
 * of CSS styles required to get the correct value.
 * eg. $virtual-item-height requires a structure, so we can get its height.
 * <div class="ag-theme-balham">
 *     <div class="ag-virtual-list-container">
 *         <div class="ag-virtual-list-item"></div>
 *     </div>
 */

var SASS_PROPERTY_BUILDER = {
  headerHeight: ['ag-header-row'],
  virtualItemHeight: ['ag-virtual-list-container', 'ag-virtual-list-item'],
  rowHeight: ['ag-row'],
  chartMenuPanelWidth: ['ag-chart-docked-container']
};
var CALCULATED_SIZES = {};

var Environment =
/** @class */
function () {
  function Environment() {}

  Environment.prototype.getSassVariable = function (theme, key) {
    var useTheme = 'ag-theme-' + (theme.match('material') ? 'material' : theme.match('balham') ? 'balham' : theme.match('alpine') ? 'alpine' : 'classic');
    var defaultValue = HARD_CODED_SIZES[useTheme][key];
    var calculatedValue = 0;

    if (!CALCULATED_SIZES[theme]) {
      CALCULATED_SIZES[theme] = {};
    }

    if (CALCULATED_SIZES[theme][key]) {
      return CALCULATED_SIZES[theme][key];
    }

    if (SASS_PROPERTY_BUILDER[key]) {
      var classList = SASS_PROPERTY_BUILDER[key];
      var div = document.createElement('div');
      var el = classList.reduce(function (el, currentClass, idx) {
        if (idx === 0) {
          _.addCssClass(el, theme);
        }

        var div = document.createElement('div');

        _.addCssClass(div, currentClass);

        el.appendChild(div);
        return div;
      }, div);

      if (document.body) {
        document.body.appendChild(div);
        calculatedValue = parseInt(window.getComputedStyle(el).height, 10);
        document.body.removeChild(div);
      }
    }

    CALCULATED_SIZES[theme][key] = calculatedValue || defaultValue;
    return CALCULATED_SIZES[theme][key];
  };

  Environment.prototype.isThemeDark = function () {
    var theme = this.getTheme().theme;
    return !!theme && theme.indexOf('dark') >= 0;
  };

  Environment.prototype.useNativeCheckboxes = function () {
    var theme = this.getTheme().theme;
    return !!theme && theme.indexOf('alpine') >= 0;
  };

  Environment.prototype.chartMenuPanelWidth = function () {
    return HARD_CODED_SIZES[this.getTheme().themeFamily].chartMenuPanelWidth;
  };

  Environment.prototype.getTheme = function () {
    var reg = /\bag-(fresh|dark|blue|material|bootstrap|(?:theme-([\w\-]*)))\b/;
    var el = this.eGridDiv;
    var themeMatch;

    while (el) {
      themeMatch = reg.exec(el.className);

      if (!themeMatch) {
        el = el.parentElement;
      } else {
        break;
      }
    }

    if (!themeMatch) {
      return {};
    }

    var theme = themeMatch[0];
    var usingOldTheme = themeMatch[2] === undefined;

    if (usingOldTheme) {
      var newTheme_1 = theme.replace('ag-', 'ag-theme-');

      _.doOnce(function () {
        return console.warn("ag-Grid: As of v19 old theme are no longer provided. Please replace " + theme + " with " + newTheme_1 + ".");
      }, 'using-old-theme');
    }

    return {
      theme: theme,
      el: el,
      themeFamily: theme.replace(/-dark$/, '')
    };
  };

  __decorate$1m([Autowired('eGridDiv')], Environment.prototype, "eGridDiv", void 0);

  Environment = __decorate$1m([Bean('environment')], Environment);
  return Environment;
}();
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __decorate$1n = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var AnimationFrameService =
/** @class */
function () {
  function AnimationFrameService() {
    // p1 and p2 are create tasks are to do with row and cell creation.
    // for them we want to execute according to row order, so we use
    // TaskItem so we know what index the item is for.
    this.createTasksP1 = {
      list: [],
      sorted: false
    }; // eg drawing back-ground of rows

    this.createTasksP2 = {
      list: [],
      sorted: false
    }; // eg cell renderers, adding hover functionality
    // destroy tasks are to do with row removal. they are done after row creation as the user will need to see new
    // rows first (as blank is scrolled into view), when we remove the old rows (no longer in view) is not as
    // important.

    this.destroyTasks = [];
    this.ticking = false; // we need to know direction of scroll, to build up rows in the direction of
    // the scroll. eg if user scrolls down, we extend the rows by building down.

    this.scrollGoingDown = true;
    this.lastScrollTop = 0;
    this.taskCount = 0;
    this.cancelledTasks = new Set();
  }

  AnimationFrameService.prototype.setScrollTop = function (scrollTop) {
    this.scrollGoingDown = scrollTop > this.lastScrollTop;
    this.lastScrollTop = scrollTop;
  };

  AnimationFrameService.prototype.init = function () {
    this.useAnimationFrame = !this.gridOptionsWrapper.isSuppressAnimationFrame();
  }; // this method is for our ag-Grid sanity only - if animation frames are turned off,
  // then no place in the code should be looking to add any work to be done in animation
  // frames. this stops bugs - where some code is asking for a frame to be executed
  // when it should not.


  AnimationFrameService.prototype.verifyAnimationFrameOn = function (methodName) {
    if (this.useAnimationFrame === false) {
      console.warn("ag-Grid: AnimationFrameService." + methodName + " called but animation frames are off");
    }
  };

  AnimationFrameService.prototype.createTask = function (task, index, list) {
    this.verifyAnimationFrameOn(list);
    var taskItem = {
      task: task,
      index: index,
      createOrder: ++this.taskCount
    };
    this.addTaskToList(this[list], taskItem);
    this.schedule();
  };

  AnimationFrameService.prototype.cancelTask = function (task) {
    this.cancelledTasks.add(task);
  };

  AnimationFrameService.prototype.addTaskToList = function (taskList, task) {
    taskList.list.push(task);
    taskList.sorted = false;
  };

  AnimationFrameService.prototype.sortTaskList = function (taskList) {
    if (taskList.sorted) {
      return;
    }

    var sortDirection = this.scrollGoingDown ? 1 : -1; // sort first by row index (taking into account scroll direction), then by
    // order of task creation (always ascending, so cells will render left-to-right)

    taskList.list.sort(function (a, b) {
      return a.index !== b.index ? sortDirection * (b.index - a.index) : b.createOrder - a.createOrder;
    });
    taskList.sorted = true;
  };

  AnimationFrameService.prototype.addDestroyTask = function (task) {
    this.verifyAnimationFrameOn('createTasksP3');
    this.destroyTasks.push(task);
    this.schedule();
  };

  AnimationFrameService.prototype.executeFrame = function (millis) {
    this.verifyAnimationFrameOn('executeFrame');
    var p1TaskList = this.createTasksP1;
    var p1Tasks = p1TaskList.list;
    var p2TaskList = this.createTasksP2;
    var p2Tasks = p2TaskList.list;
    var destroyTasks = this.destroyTasks;
    var frameStart = new Date().getTime();
    var duration = new Date().getTime() - frameStart; // 16ms is 60 fps

    var noMaxMillis = millis <= 0;

    while (noMaxMillis || duration < millis) {
      var task = void 0;

      if (p1Tasks.length) {
        this.sortTaskList(p1TaskList);
        task = p1Tasks.pop().task;
      } else if (p2Tasks.length) {
        this.sortTaskList(p2TaskList);
        task = p2Tasks.pop().task;
      } else if (destroyTasks.length) {
        task = destroyTasks.pop();
      } else {
        this.cancelledTasks.clear();
        break;
      }

      if (!this.cancelledTasks.has(task)) {
        task();
      }

      duration = new Date().getTime() - frameStart;
    }

    if (p1Tasks.length || p2Tasks.length || destroyTasks.length) {
      this.requestFrame();
    } else {
      this.stopTicking();
    }
  };

  AnimationFrameService.prototype.stopTicking = function () {
    this.ticking = false;
    var event = {
      type: Events.EVENT_ANIMATION_QUEUE_EMPTY,
      columnApi: this.gridOptionsWrapper.getColumnApi(),
      api: this.gridOptionsWrapper.getApi()
    };
    this.eventService.dispatchEvent(event);
  };

  AnimationFrameService.prototype.flushAllFrames = function () {
    if (!this.useAnimationFrame) {
      return;
    }

    this.executeFrame(-1);
  };

  AnimationFrameService.prototype.schedule = function () {
    if (!this.useAnimationFrame) {
      return;
    }

    if (!this.ticking) {
      this.ticking = true;
      this.requestFrame();
    }
  };

  AnimationFrameService.prototype.requestFrame = function () {
    // check for the existence of requestAnimationFrame, and if
    // it's missing, then we polyfill it with setTimeout()
    var callback = this.executeFrame.bind(this, 60);

    if (window.requestAnimationFrame) {
      window.requestAnimationFrame(callback);
    } else if (window.webkitRequestAnimationFrame) {
      window.webkitRequestAnimationFrame(callback);
    } else {
      window.setTimeout(callback, 0);
    }
  };

  AnimationFrameService.prototype.isQueueEmpty = function () {
    return !this.ticking;
  };

  __decorate$1n([Autowired('gridOptionsWrapper')], AnimationFrameService.prototype, "gridOptionsWrapper", void 0);

  __decorate$1n([Autowired('eventService')], AnimationFrameService.prototype, "eventService", void 0);

  __decorate$1n([PostConstruct], AnimationFrameService.prototype, "init", null);

  AnimationFrameService = __decorate$1n([Bean('animationFrameService')], AnimationFrameService);
  return AnimationFrameService;
}();
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __decorate$1o = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var NavigationService =
/** @class */
function () {
  function NavigationService() {
    this.timeLastPageEventProcessed = 0;
  }

  NavigationService.prototype.init = function () {
    this.scrollWidth = this.gridOptionsWrapper.getScrollbarWidth();
  };

  NavigationService.prototype.registerGridComp = function (gridPanel) {
    this.gridPanel = gridPanel;
  };

  NavigationService.prototype.handlePageScrollingKey = function (event) {
    var key = event.which || event.keyCode;
    var alt = event.altKey;
    var ctrl = event.ctrlKey;
    var currentCell = this.mouseEventService.getCellPositionForEvent(event);

    if (!currentCell) {
      return false;
    }

    var processed = false;

    switch (key) {
      case Constants.KEY_PAGE_HOME:
      case Constants.KEY_PAGE_END:
        // handle home and end when ctrl & alt are NOT pressed
        if (!ctrl && !alt) {
          this.onHomeOrEndKey(key);
          processed = true;
        }

        break;

      case Constants.KEY_LEFT:
      case Constants.KEY_RIGHT:
        // handle left and right when ctrl is pressed only
        if (ctrl && !alt) {
          this.onCtrlLeftOrRight(key, currentCell);
          processed = true;
        }

        break;

      case Constants.KEY_UP:
      case Constants.KEY_DOWN:
        // handle up and down when ctrl is pressed only
        if (ctrl && !alt) {
          this.onCtrlUpOrDown(key, currentCell);
          processed = true;
        }

        break;

      case Constants.KEY_PAGE_DOWN:
        // handle page up and page down when ctrl & alt are NOT pressed
        if (!ctrl && !alt) {
          this.onPageDown(currentCell);
          processed = true;
        }

        break;

      case Constants.KEY_PAGE_UP:
        // handle page up and page down when ctrl & alt are NOT pressed
        if (!ctrl && !alt) {
          this.onPageUp(currentCell);
          processed = true;
        }

        break;
    }

    if (processed) {
      event.preventDefault();
    }

    return processed;
  }; // the page up/down keys caused a problem, in that if the user
  // held the page up/down key down, lots of events got generated,
  // which clogged up the event queue (as they take time to process)
  // which in turn froze the grid. Logic below makes sure we wait 100ms
  // between processing the page up/down events, so when user has finger
  // held down on key, we ignore page up/down events until 100ms has passed,
  // which effectively empties the queue of page up/down events.


  NavigationService.prototype.isTimeSinceLastPageEventToRecent = function () {
    var now = new Date().getTime();
    var diff = now - this.timeLastPageEventProcessed;
    return diff < 100;
  };

  NavigationService.prototype.setTimeLastPageEventProcessed = function () {
    this.timeLastPageEventProcessed = new Date().getTime();
  };

  NavigationService.prototype.onPageDown = function (gridCell) {
    if (this.isTimeSinceLastPageEventToRecent()) {
      return;
    }

    var scrollPosition = this.gridPanel.getVScrollPosition();
    var pixelsInOnePage = scrollPosition.bottom - scrollPosition.top;

    if (this.gridPanel.isHorizontalScrollShowing()) {
      pixelsInOnePage -= this.scrollWidth;
    }

    var pagingPixelOffset = this.paginationProxy.getPixelOffset();
    var currentPageBottomPixel = scrollPosition.top + pixelsInOnePage;
    var currentPageBottomRow = this.paginationProxy.getRowIndexAtPixel(currentPageBottomPixel + pagingPixelOffset);
    var scrollIndex = currentPageBottomRow;
    var currentCellPixel = this.paginationProxy.getRow(gridCell.rowIndex).rowTop;
    var nextCellPixel = currentCellPixel + pixelsInOnePage - pagingPixelOffset;
    var focusIndex = this.paginationProxy.getRowIndexAtPixel(nextCellPixel + pagingPixelOffset);
    var pageLastRow = this.paginationProxy.getPageLastRow();

    if (focusIndex > pageLastRow) {
      focusIndex = pageLastRow;
    }

    if (scrollIndex > pageLastRow) {
      scrollIndex = pageLastRow;
    }

    this.navigateTo(scrollIndex, 'top', null, focusIndex, gridCell.column);
    this.setTimeLastPageEventProcessed();
  };

  NavigationService.prototype.onPageUp = function (gridCell) {
    if (this.isTimeSinceLastPageEventToRecent()) {
      return;
    }

    var scrollPosition = this.gridPanel.getVScrollPosition();
    var pixelsInOnePage = scrollPosition.bottom - scrollPosition.top;

    if (this.gridPanel.isHorizontalScrollShowing()) {
      pixelsInOnePage -= this.scrollWidth;
    }

    var pagingPixelOffset = this.paginationProxy.getPixelOffset();
    var currentPageTopPixel = scrollPosition.top;
    var currentPageTopRow = this.paginationProxy.getRowIndexAtPixel(currentPageTopPixel + pagingPixelOffset);
    var scrollIndex = currentPageTopRow;
    var currentRowNode = this.paginationProxy.getRow(gridCell.rowIndex);
    var nextCellPixel = currentRowNode.rowTop + currentRowNode.rowHeight - pixelsInOnePage - pagingPixelOffset;
    var focusIndex = this.paginationProxy.getRowIndexAtPixel(nextCellPixel + pagingPixelOffset);
    var firstRow = this.paginationProxy.getPageFirstRow();

    if (focusIndex < firstRow) {
      focusIndex = firstRow;
    }

    if (scrollIndex < firstRow) {
      scrollIndex = firstRow;
    }

    this.navigateTo(scrollIndex, 'bottom', null, focusIndex, gridCell.column);
    this.setTimeLastPageEventProcessed();
  }; // common logic to navigate. takes parameters:
  // scrollIndex - what row to vertically scroll to
  // scrollType - what position to put scroll index ie top/bottom
  // scrollColumn - what column to horizontally scroll to
  // focusIndex / focusColumn - for page up / down, we want to scroll to one row/column, but focus another


  NavigationService.prototype.navigateTo = function (scrollIndex, scrollType, scrollColumn, focusIndex, focusColumn) {
    if (_.exists(scrollColumn)) {
      this.gridPanel.ensureColumnVisible(scrollColumn);
    }

    if (_.exists(scrollIndex)) {
      this.gridPanel.ensureIndexVisible(scrollIndex, scrollType);
    } // make sure the cell is rendered, needed if we are to focus


    this.animationFrameService.flushAllFrames(); // if we don't do this, the range will be left on the last cell, which will leave the last focused cell
    // highlighted.

    this.focusedCellController.setFocusedCell(focusIndex, focusColumn, null, true);

    if (this.rangeController) {
      var cellPosition = {
        rowIndex: focusIndex,
        rowPinned: null,
        column: focusColumn
      };
      this.rangeController.setRangeToCell(cellPosition);
    }
  }; // ctrl + up/down will bring focus to same column, first/last row. no horizontal scrolling.


  NavigationService.prototype.onCtrlUpOrDown = function (key, gridCell) {
    var upKey = key === Constants.KEY_UP;
    var rowIndexToScrollTo = upKey ? 0 : this.paginationProxy.getPageLastRow();
    this.navigateTo(rowIndexToScrollTo, null, gridCell.column, rowIndexToScrollTo, gridCell.column);
  }; // ctrl + left/right will bring focus to same row, first/last cell. no vertical scrolling.


  NavigationService.prototype.onCtrlLeftOrRight = function (key, gridCell) {
    var leftKey = key === Constants.KEY_LEFT;
    var allColumns = this.columnController.getAllDisplayedColumns();
    var columnToSelect = leftKey ? allColumns[0] : _.last(allColumns);
    this.navigateTo(gridCell.rowIndex, null, columnToSelect, gridCell.rowIndex, columnToSelect);
  }; // home brings focus to top left cell, end brings focus to bottom right, grid scrolled to bring
  // same cell into view (which means either scroll all the way up, or all the way down).


  NavigationService.prototype.onHomeOrEndKey = function (key) {
    var homeKey = key === Constants.KEY_PAGE_HOME;
    var allColumns = this.columnController.getAllDisplayedColumns();
    var columnToSelect = homeKey ? allColumns[0] : _.last(allColumns);
    var rowIndexToScrollTo = homeKey ? 0 : this.paginationProxy.getPageLastRow();
    this.navigateTo(rowIndexToScrollTo, null, columnToSelect, rowIndexToScrollTo, columnToSelect);
  };

  __decorate$1o([Autowired('mouseEventService')], NavigationService.prototype, "mouseEventService", void 0);

  __decorate$1o([Autowired('paginationProxy')], NavigationService.prototype, "paginationProxy", void 0);

  __decorate$1o([Autowired('focusedCellController')], NavigationService.prototype, "focusedCellController", void 0);

  __decorate$1o([Autowired('animationFrameService')], NavigationService.prototype, "animationFrameService", void 0);

  __decorate$1o([Optional('rangeController')], NavigationService.prototype, "rangeController", void 0);

  __decorate$1o([Autowired('columnController')], NavigationService.prototype, "columnController", void 0);

  __decorate$1o([Autowired('gridOptionsWrapper')], NavigationService.prototype, "gridOptionsWrapper", void 0);

  __decorate$1o([PostConstruct], NavigationService.prototype, "init", null);

  NavigationService = __decorate$1o([Bean('navigationService')], NavigationService);
  return NavigationService;
}();
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __extends$T = undefined && undefined.__extends || function () {
  var _extendStatics56 = function extendStatics(d, b) {
    _extendStatics56 = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics56(d, b);
  };

  return function (d, b) {
    _extendStatics56(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var __decorate$1p = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};
/**
 * This class solves the 'max height' problem, where the user might want to show more data than
 * the max div height actually allows.
 */


var MaxDivHeightScaler =
/** @class */
function (_super) {
  __extends$T(MaxDivHeightScaler, _super);

  function MaxDivHeightScaler() {
    var _this = _super !== null && _super.apply(this, arguments) || this; // the scrollY position


    _this.scrollY = 0; // how tall the body is

    _this.uiBodyHeight = 0;
    return _this;
  }

  MaxDivHeightScaler.prototype.postConstruct = function () {
    this.addDestroyableEventListener(this.eventService, Events.EVENT_BODY_HEIGHT_CHANGED, this.updateOffset.bind(this));
    this.scrollBarWidth = this.gridOptionsWrapper.getScrollbarWidth();
    this.maxDivHeight = _.getMaxDivHeight();
  };

  MaxDivHeightScaler.prototype.registerGridComp = function (gridPanel) {
    this.gridPanel = gridPanel;
  };

  MaxDivHeightScaler.prototype.isScaling = function () {
    return this.scaling;
  };

  MaxDivHeightScaler.prototype.getOffset = function () {
    return this.offset;
  };

  MaxDivHeightScaler.prototype.updateOffset = function () {
    if (!this.scaling) {
      return;
    }

    var newScrollY = this.gridPanel.getVScrollPosition().top;
    var newBodyHeight = this.getUiBodyHeight();
    var atLeastOneChanged = newScrollY !== this.scrollY || newBodyHeight !== this.uiBodyHeight;

    if (atLeastOneChanged) {
      this.scrollY = newScrollY;
      this.uiBodyHeight = newBodyHeight;
      this.calculateOffset();
    }
  };

  MaxDivHeightScaler.prototype.calculateOffset = function () {
    this.uiContainerHeight = this.maxDivHeight;
    this.pixelsToShave = this.modelHeight - this.uiContainerHeight;
    this.maxScrollY = this.uiContainerHeight - this.uiBodyHeight;
    var scrollPercent = this.scrollY / this.maxScrollY;
    this.setOffset(scrollPercent * this.pixelsToShave);
  };

  MaxDivHeightScaler.prototype.clearOffset = function () {
    this.uiContainerHeight = this.modelHeight;
    this.pixelsToShave = 0;
    this.setOffset(0);
  };

  MaxDivHeightScaler.prototype.setOffset = function (newOffset) {
    // because we are talking pixels, no point in confusing things with half numbers
    var newOffsetFloor = typeof newOffset === 'number' ? Math.floor(newOffset) : null;

    if (this.offset === newOffsetFloor) {
      return;
    }

    this.offset = newOffsetFloor;
    this.eventService.dispatchEvent({
      type: Events.EVENT_HEIGHT_SCALE_CHANGED
    });
  };

  MaxDivHeightScaler.prototype.setModelHeight = function (modelHeight) {
    this.modelHeight = modelHeight;
    this.scaling = this.maxDivHeight > 0 && modelHeight > this.maxDivHeight;

    if (this.scaling) {
      this.calculateOffset();
    } else {
      this.clearOffset();
    }
  };

  MaxDivHeightScaler.prototype.getUiContainerHeight = function () {
    return this.uiContainerHeight;
  };

  MaxDivHeightScaler.prototype.getRealPixelPosition = function (modelPixel) {
    return modelPixel - this.offset;
  };

  MaxDivHeightScaler.prototype.getUiBodyHeight = function () {
    var pos = this.gridPanel.getVScrollPosition();
    return pos.bottom - pos.top;
  };

  MaxDivHeightScaler.prototype.getScrollPositionForPixel = function (rowTop) {
    if (this.pixelsToShave <= 0) {
      return rowTop;
    }

    var modelMaxScroll = this.modelHeight - this.getUiBodyHeight();
    var scrollPercent = rowTop / modelMaxScroll;
    var scrollPixel = this.maxScrollY * scrollPercent;
    return scrollPixel;
  };

  __decorate$1p([Autowired('eventService')], MaxDivHeightScaler.prototype, "eventService", void 0);

  __decorate$1p([Autowired('gridOptionsWrapper')], MaxDivHeightScaler.prototype, "gridOptionsWrapper", void 0);

  __decorate$1p([PostConstruct], MaxDivHeightScaler.prototype, "postConstruct", null);

  MaxDivHeightScaler = __decorate$1p([Bean('maxDivHeightScaler')], MaxDivHeightScaler);
  return MaxDivHeightScaler;
}(BeanStub);
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __decorate$1q = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var SelectableService =
/** @class */
function () {
  function SelectableService() {}

  SelectableService.prototype.init = function () {
    this.groupSelectsChildren = this.gridOptionsWrapper.isGroupSelectsChildren();
    this.isRowSelectableFunc = this.gridOptionsWrapper.getIsRowSelectableFunc();
  };

  SelectableService.prototype.updateSelectableAfterGrouping = function (rowNode) {
    if (this.isRowSelectableFunc) {
      var nextChildrenFunc = function nextChildrenFunc(rowNode) {
        return rowNode.childrenAfterGroup;
      };

      this.recurseDown(rowNode.childrenAfterGroup, nextChildrenFunc);
    }
  };

  SelectableService.prototype.updateSelectableAfterFiltering = function (rowNode) {
    if (this.isRowSelectableFunc) {
      var nextChildrenFunc = function nextChildrenFunc(rowNode) {
        return rowNode.childrenAfterFilter;
      };

      this.recurseDown(rowNode.childrenAfterGroup, nextChildrenFunc);
    }
  };

  SelectableService.prototype.recurseDown = function (children, nextChildrenFunc) {
    var _this = this;

    children.forEach(function (child) {
      if (!child.group) {
        return;
      } // only interested in groups


      if (child.hasChildren()) {
        _this.recurseDown(nextChildrenFunc(child), nextChildrenFunc);
      }

      var rowSelectable;

      if (_this.groupSelectsChildren) {
        // have this group selectable if at least one direct child is selectable
        var firstSelectable = _.find(nextChildrenFunc(child), 'selectable', true);

        rowSelectable = _.exists(firstSelectable);
      } else {
        // directly retrieve selectable value from user callback
        rowSelectable = _this.isRowSelectableFunc ? _this.isRowSelectableFunc(child) : false;
      }

      child.setRowSelectable(rowSelectable);
    });
  };

  __decorate$1q([Autowired('gridOptionsWrapper')], SelectableService.prototype, "gridOptionsWrapper", void 0);

  __decorate$1q([PostConstruct], SelectableService.prototype, "init", null);

  SelectableService = __decorate$1q([Bean('selectableService')], SelectableService);
  return SelectableService;
}();
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __decorate$1r = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var AutoHeightCalculator =
/** @class */
function () {
  function AutoHeightCalculator() {}

  AutoHeightCalculator.prototype.registerGridComp = function (gridPanel) {
    this.gridPanel = gridPanel;
  };

  AutoHeightCalculator.prototype.getPreferredHeightForRow = function (rowNode) {
    var _this = this;

    if (!this.eDummyContainer) {
      this.eDummyContainer = document.createElement('div'); // so any styles on row also get applied in dummy, otherwise
      // the content in dummy may differ to the real

      _.addCssClass(this.eDummyContainer, 'ag-row ag-row-no-focus');
    } // we put the dummy into the body container, so it will inherit all the
    // css styles that the real cells are inheriting


    var eBodyContainer = this.gridPanel.getCenterContainer();
    eBodyContainer.appendChild(this.eDummyContainer);
    var cellComps = [];
    var autoRowHeightCols = this.columnController.getAllAutoRowHeightCols();
    var visibleAutoRowHeightCols = autoRowHeightCols.filter(function (col) {
      return col.isVisible();
    });
    visibleAutoRowHeightCols.forEach(function (col) {
      var cellComp = new CellComp(_this.$scope, _this.beans, col, rowNode, null, true, false);
      cellComp.setParentRow(_this.eDummyContainer);
      cellComps.push(cellComp);
    });
    var template = cellComps.map(function (cellComp) {
      return cellComp.getCreateTemplate();
    }).join(' ');
    this.eDummyContainer.innerHTML = template; // this gets any cellComps that are using components to put the components in

    cellComps.forEach(function (cellComp) {
      return cellComp.afterAttached();
    }); // we should be able to just take the height of the row at this point, however
    // the row isn't expanding to cover the cell heights, i don't know why, i couldn't
    // figure it out so instead looking at the individual cells instead

    var maxCellHeight = 0;

    for (var i = 0; i < this.eDummyContainer.children.length; i++) {
      var child = this.eDummyContainer.children[i];

      if (child.offsetHeight > maxCellHeight) {
        maxCellHeight = child.offsetHeight;
      }
    } // we are finished with the dummy container, so get rid of it


    eBodyContainer.removeChild(this.eDummyContainer);
    cellComps.forEach(function (cellComp) {
      // dunno why we need to detach first, doing it here to be consistent with code in RowComp
      cellComp.detach();
      cellComp.destroy();
    }); // in case anything left over from last time

    _.clearElement(this.eDummyContainer);

    return maxCellHeight;
  };

  __decorate$1r([Autowired('beans')], AutoHeightCalculator.prototype, "beans", void 0);

  __decorate$1r([Autowired("$scope")], AutoHeightCalculator.prototype, "$scope", void 0);

  __decorate$1r([Autowired("columnController")], AutoHeightCalculator.prototype, "columnController", void 0);

  AutoHeightCalculator = __decorate$1r([Bean('autoHeightCalculator')], AutoHeightCalculator);
  return AutoHeightCalculator;
}();
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __extends$U = undefined && undefined.__extends || function () {
  var _extendStatics57 = function extendStatics(d, b) {
    _extendStatics57 = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics57(d, b);
  };

  return function (d, b) {
    _extendStatics57(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var __decorate$1s = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var PaginationComp =
/** @class */
function (_super) {
  __extends$U(PaginationComp, _super);

  function PaginationComp() {
    return _super.call(this) || this;
  }

  PaginationComp.prototype.postConstruct = function () {
    var isRtl = this.gridOptionsWrapper.isEnableRtl();
    this.setTemplate(this.getTemplate());
    this.btFirst.insertAdjacentElement('afterbegin', _.createIconNoSpan(isRtl ? 'last' : 'first', this.gridOptionsWrapper));
    this.btPrevious.insertAdjacentElement('afterbegin', _.createIconNoSpan(isRtl ? 'next' : 'previous', this.gridOptionsWrapper));
    this.btNext.insertAdjacentElement('afterbegin', _.createIconNoSpan(isRtl ? 'previous' : 'next', this.gridOptionsWrapper));
    this.btLast.insertAdjacentElement('afterbegin', _.createIconNoSpan(isRtl ? 'first' : 'last', this.gridOptionsWrapper));

    if (this.rowModel.getType() === Constants.ROW_MODEL_TYPE_SERVER_SIDE) {
      this.serverSideRowModel = this.rowModel;
    }

    var isPaging = this.gridOptionsWrapper.isPagination();
    var paginationPanelEnabled = isPaging && !this.gridOptionsWrapper.isSuppressPaginationPanel();

    if (!paginationPanelEnabled) {
      this.setDisplayed(false);
      return;
    }

    this.addDestroyableEventListener(this.eventService, Events.EVENT_PAGINATION_CHANGED, this.onPaginationChanged.bind(this));
    this.addDestroyableEventListener(this.btFirst, 'click', this.onBtFirst.bind(this));
    this.addDestroyableEventListener(this.btLast, 'click', this.onBtLast.bind(this));
    this.addDestroyableEventListener(this.btNext, 'click', this.onBtNext.bind(this));
    this.addDestroyableEventListener(this.btPrevious, 'click', this.onBtPrevious.bind(this));
    this.onPaginationChanged();
  };

  PaginationComp.prototype.onPaginationChanged = function () {
    this.enableOrDisableButtons();
    this.updateRowLabels();
    this.setCurrentPageLabel();
    this.setTotalLabels();
  };

  PaginationComp.prototype.setCurrentPageLabel = function () {
    var pagesExist = this.paginationProxy.getTotalPages() > 0;
    var currentPage = this.paginationProxy.getCurrentPage();
    var toDisplay = pagesExist ? currentPage + 1 : 0;
    this.lbCurrent.innerHTML = this.formatNumber(toDisplay);
  };

  PaginationComp.prototype.formatNumber = function (value) {
    var userFunc = this.gridOptionsWrapper.getPaginationNumberFormatterFunc();

    if (userFunc) {
      return userFunc({
        value: value
      });
    } else {
      return _.formatNumberCommas(value);
    }
  };

  PaginationComp.prototype.getTemplate = function () {
    var localeTextFunc = this.gridOptionsWrapper.getLocaleTextFunc();
    var strPage = localeTextFunc('page', 'Page');
    var strTo = localeTextFunc('to', 'to');
    var strOf = localeTextFunc('of', 'of');
    var strFirst = localeTextFunc('first', 'First');
    var strPrevious = localeTextFunc('previous', 'Previous');
    var strNext = localeTextFunc('next', 'Next');
    var strLast = localeTextFunc('last', 'Last');
    return "<div class=\"ag-paging-panel ag-unselectable\">\n                <span ref=\"eSummaryPanel\" class=\"ag-paging-row-summary-panel\">\n                    <span ref=\"lbFirstRowOnPage\"></span> " + strTo + " <span ref=\"lbLastRowOnPage\"></span> " + strOf + " <span ref=\"lbRecordCount\"></span>\n                </span>\n                <span class=\"ag-paging-page-summary-panel\">\n                    <div ref=\"btFirst\" class=\"ag-paging-button\">\n                        <button type=\"button\">" + strFirst + "</button>\n                    </div>\n                    <div ref=\"btPrevious\" class=\"ag-paging-button\">\n                        <button type=\"button\">" + strPrevious + "</button>\n                    </div>\n                    " + strPage + " <span ref=\"lbCurrent\"></span> " + strOf + " <span ref=\"lbTotal\"></span>\n                    <div ref=\"btNext\" class=\"ag-paging-button\">\n                        <button type=\"button\">" + strNext + "</button>\n                    </div>\n                    <div ref=\"btLast\" class=\"ag-paging-button\">\n                        <button type=\"button\">" + strLast + "</button>\n                    </div>\n                </span>\n            </div>";
  };

  PaginationComp.prototype.onBtNext = function () {
    this.paginationProxy.goToNextPage();
  };

  PaginationComp.prototype.onBtPrevious = function () {
    this.paginationProxy.goToPreviousPage();
  };

  PaginationComp.prototype.onBtFirst = function () {
    this.paginationProxy.goToFirstPage();
  };

  PaginationComp.prototype.onBtLast = function () {
    this.paginationProxy.goToLastPage();
  };

  PaginationComp.prototype.enableOrDisableButtons = function () {
    var currentPage = this.paginationProxy.getCurrentPage();
    var maxRowFound = this.paginationProxy.isLastPageFound();
    var totalPages = this.paginationProxy.getTotalPages();
    var disablePreviousAndFirst = currentPage === 0;

    _.addOrRemoveCssClass(this.btPrevious, 'ag-disabled', disablePreviousAndFirst);

    _.addOrRemoveCssClass(this.btFirst, 'ag-disabled', disablePreviousAndFirst);

    var zeroPagesToDisplay = this.isZeroPagesToDisplay();
    var onLastPage = maxRowFound && currentPage === totalPages - 1;
    var disableNext = onLastPage || zeroPagesToDisplay;

    _.addOrRemoveCssClass(this.btNext, 'ag-disabled', disableNext);

    var disableLast = !maxRowFound || zeroPagesToDisplay || currentPage === totalPages - 1;

    _.addOrRemoveCssClass(this.btLast, 'ag-disabled', disableLast);
  };

  PaginationComp.prototype.updateRowLabels = function () {
    var currentPage = this.paginationProxy.getCurrentPage();
    var pageSize = this.paginationProxy.getPageSize();
    var maxRowFound = this.paginationProxy.isLastPageFound();
    var rowCount = this.paginationProxy.isLastPageFound() ? this.paginationProxy.getMasterRowCount() : null;
    var startRow;
    var endRow;

    if (this.isZeroPagesToDisplay()) {
      startRow = 0;
      endRow = 0;
    } else {
      startRow = pageSize * currentPage + 1;
      endRow = startRow + pageSize - 1;

      if (maxRowFound && endRow > rowCount) {
        endRow = rowCount;
      }
    }

    this.lbFirstRowOnPage.innerHTML = this.formatNumber(startRow);

    if (this.serverSideRowModel && this.serverSideRowModel.isLoading()) {
      this.lbLastRowOnPage.innerHTML = '?';
    } else {
      this.lbLastRowOnPage.innerHTML = this.formatNumber(endRow);
    }
  };

  PaginationComp.prototype.isZeroPagesToDisplay = function () {
    var maxRowFound = this.paginationProxy.isLastPageFound();
    var totalPages = this.paginationProxy.getTotalPages();
    return maxRowFound && totalPages === 0;
  };

  PaginationComp.prototype.setTotalLabels = function () {
    var lastPageFound = this.paginationProxy.isLastPageFound();
    var totalPages = this.paginationProxy.getTotalPages();
    var rowCount = this.paginationProxy.isLastPageFound() ? this.paginationProxy.getMasterRowCount() : null;

    if (lastPageFound) {
      this.lbTotal.innerHTML = this.formatNumber(totalPages);
      this.lbRecordCount.innerHTML = this.formatNumber(rowCount);
    } else {
      var moreText = this.gridOptionsWrapper.getLocaleTextFunc()('more', 'more');
      this.lbTotal.innerHTML = moreText;
      this.lbRecordCount.innerHTML = moreText;
    }
  };

  __decorate$1s([Autowired('gridOptionsWrapper')], PaginationComp.prototype, "gridOptionsWrapper", void 0);

  __decorate$1s([Autowired('eventService')], PaginationComp.prototype, "eventService", void 0);

  __decorate$1s([Autowired('paginationProxy')], PaginationComp.prototype, "paginationProxy", void 0);

  __decorate$1s([Autowired('rowRenderer')], PaginationComp.prototype, "rowRenderer", void 0);

  __decorate$1s([Autowired('rowModel')], PaginationComp.prototype, "rowModel", void 0);

  __decorate$1s([RefSelector('btFirst')], PaginationComp.prototype, "btFirst", void 0);

  __decorate$1s([RefSelector('btPrevious')], PaginationComp.prototype, "btPrevious", void 0);

  __decorate$1s([RefSelector('btNext')], PaginationComp.prototype, "btNext", void 0);

  __decorate$1s([RefSelector('btLast')], PaginationComp.prototype, "btLast", void 0);

  __decorate$1s([RefSelector('lbRecordCount')], PaginationComp.prototype, "lbRecordCount", void 0);

  __decorate$1s([RefSelector('lbFirstRowOnPage')], PaginationComp.prototype, "lbFirstRowOnPage", void 0);

  __decorate$1s([RefSelector('lbLastRowOnPage')], PaginationComp.prototype, "lbLastRowOnPage", void 0);

  __decorate$1s([RefSelector('eSummaryPanel')], PaginationComp.prototype, "eSummaryPanel", void 0);

  __decorate$1s([RefSelector('lbCurrent')], PaginationComp.prototype, "lbCurrent", void 0);

  __decorate$1s([RefSelector('lbTotal')], PaginationComp.prototype, "lbTotal", void 0);

  __decorate$1s([PostConstruct], PaginationComp.prototype, "postConstruct", null);

  return PaginationComp;
}(Component);
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __decorate$1t = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var ResizeObserverService =
/** @class */
function () {
  function ResizeObserverService() {}

  ResizeObserverService.prototype.observeResize = function (element, callback, debounceDelay) {
    if (debounceDelay === void 0) {
      debounceDelay = 50;
    } // put in variable, so available to usePolyfill() function below


    var frameworkFactory = this.frameworkOverrides; // this gets fired too often and might cause some relayout issues
    // so we add a debounce to the callback here to avoid the flashing effect.

    var debouncedCallback = _.debounce(callback, debounceDelay);

    var useBrowserResizeObserver = function useBrowserResizeObserver() {
      var resizeObserver = new window.ResizeObserver(debouncedCallback);
      resizeObserver.observe(element);
      return function () {
        return resizeObserver.disconnect();
      };
    };

    var usePolyfill = function usePolyfill() {
      // initialise to the current width and height, so first call will have no changes
      var widthLastTime = _.offsetWidth(element);

      var heightLastTime = _.offsetHeight(element); // when finished, this gets turned to false.


      var running = true;

      var periodicallyCheckWidthAndHeight = function periodicallyCheckWidthAndHeight() {
        if (running) {
          var newWidth = _.offsetWidth(element);

          var newHeight = _.offsetHeight(element);

          var changed = newWidth !== widthLastTime || newHeight !== heightLastTime;

          if (changed) {
            widthLastTime = newWidth;
            heightLastTime = newHeight;
            callback();
          }

          frameworkFactory.setTimeout(periodicallyCheckWidthAndHeight, debounceDelay);
        }
      };

      periodicallyCheckWidthAndHeight(); // the callback function we return sets running to false

      return function () {
        return running = false;
      };
    };

    var suppressResize = this.gridOptionsWrapper.isSuppressBrowserResizeObserver();
    var resizeObserverExists = !!window.ResizeObserver;

    if (resizeObserverExists && !suppressResize) {
      return useBrowserResizeObserver();
    } else {
      return usePolyfill();
    }
  };

  __decorate$1t([Autowired('gridOptionsWrapper')], ResizeObserverService.prototype, "gridOptionsWrapper", void 0);

  __decorate$1t([Autowired('frameworkOverrides')], ResizeObserverService.prototype, "frameworkOverrides", void 0);

  ResizeObserverService = __decorate$1t([Bean('resizeObserverService')], ResizeObserverService);
  return ResizeObserverService;
}();
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __decorate$1u = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var TooltipManager =
/** @class */
function () {
  function TooltipManager() {
    this.DEFAULT_HIDE_TOOLTIP_TIMEOUT = 10000;
    this.MOUSEOUT_HIDE_TOOLTIP_TIMEOUT = 1000;
    this.MOUSEOVER_SHOW_TOOLTIP_TIMEOUT = 2000;
    this.HIDE_SHOW_ONLY = true;
    this.showTimeoutId = 0;
    this.hideTimeoutId = 0; // map of compId to [tooltip component, close function]

    this.registeredComponents = {};
  }

  TooltipManager.prototype.registerTooltip = function (targetCmp) {
    var _this = this;

    var el = targetCmp.getGui();
    var id = targetCmp.getCompId();
    this.registeredComponents[id] = {
      tooltipComp: undefined,
      destroyFunc: undefined,
      eventDestroyFuncs: [targetCmp.addDestroyableEventListener(el, 'mouseover', function (e) {
        return _this.processMouseOver(e, targetCmp);
      }), targetCmp.addDestroyableEventListener(el, 'mousemove', function (e) {
        return _this.processMouseMove(e);
      }), targetCmp.addDestroyableEventListener(el, 'mousedown', this.hideTooltip.bind(this)), targetCmp.addDestroyableEventListener(el, 'mouseout', this.processMouseOut.bind(this))]
    };
    targetCmp.addDestroyFunc(function () {
      return _this.unregisterTooltip(targetCmp);
    });
  };

  TooltipManager.prototype.unregisterTooltip = function (targetCmp) {
    var id = targetCmp.getCompId();
    var registeredComponent = this.registeredComponents[id]; // hide the tooltip if it's being displayed while unregistering the component

    if (this.activeComponent === targetCmp) {
      this.hideTooltip();
    }

    if (targetCmp.isAlive() && registeredComponent && registeredComponent.eventDestroyFuncs.length) {
      registeredComponent.eventDestroyFuncs.forEach(function (func) {
        return func();
      });
    }

    delete this.registeredComponents[id];
  };

  TooltipManager.prototype.processMouseOver = function (e, targetCmp) {
    var delay = this.MOUSEOVER_SHOW_TOOLTIP_TIMEOUT;

    if (this.activeComponent) {
      // lastHoveredComponent will be the activeComponent when we are hovering
      // a component with many child elements like the grid header
      if (this.lastHoveredComponent === this.activeComponent) {
        return;
      }

      delay = 200;
    } else if (this.showTimeoutId && this.lastHoveredComponent === targetCmp) {
      return;
    }

    this.clearTimers(this.HIDE_SHOW_ONLY); // lastHoveredComponent will be the targetCmp when a click hid the tooltip
    // and the lastHoveredComponent has many child elements

    if (this.lastHoveredComponent === targetCmp) {
      return;
    }

    this.lastHoveredComponent = targetCmp;
    this.lastMouseEvent = e;
    this.showTimeoutId = window.setTimeout(this.showTooltip.bind(this), delay, e);
  };

  TooltipManager.prototype.processMouseOut = function (e) {
    var activeComponent = this.activeComponent;
    var relatedTarget = e.relatedTarget;

    if (!activeComponent) {
      if (this.lastHoveredComponent) {
        var containsElement = this.lastHoveredComponent.getGui().contains(relatedTarget);

        if (this.showTimeoutId && containsElement) {
          // if we are hovering within a component with multiple child elements before
          // the tooltip has been displayed, we should cancel this event
          return;
        } else if (!containsElement) {
          // when a click hides the tooltip we need to reset the lastHoveredComponent
          // otherwise the tooltip won't appear until another registered component is hovered.
          this.lastHoveredComponent = undefined;
        }
      }

      this.clearTimers();
      return;
    } // the mouseout was called from within the activeComponent so we do nothing


    if (activeComponent.getGui().contains(relatedTarget)) {
      return;
    }

    var registeredComponent = this.registeredComponents[activeComponent.getCompId()];

    _.addCssClass(registeredComponent.tooltipComp.getGui(), 'ag-tooltip-hiding');

    this.lastHoveredComponent = undefined;
    this.clearTimers();
    this.hideTimeoutId = window.setTimeout(this.hideTooltip.bind(this), this.MOUSEOUT_HIDE_TOOLTIP_TIMEOUT);
  };

  TooltipManager.prototype.processMouseMove = function (e) {
    // there is a delay from the time we mouseOver a component and the time the
    // tooltip is displayed, so we need to track mousemove to be able to correctly
    // position the tooltip when showTooltip is called.
    this.lastMouseEvent = e;
  };

  TooltipManager.prototype.showTooltip = function (e) {
    var targetCmp = this.lastHoveredComponent;
    var cellComp = targetCmp;
    var registeredComponent = this.registeredComponents[targetCmp.getCompId()];
    this.hideTooltip();
    var params = {
      api: this.gridApi,
      columnApi: this.columnApi,
      colDef: targetCmp.getComponentHolder(),
      column: cellComp.getColumn && cellComp.getColumn(),
      context: this.gridOptionsWrapper.getContext(),
      rowIndex: cellComp.getCellPosition && cellComp.getCellPosition().rowIndex,
      value: targetCmp.getTooltipText()
    };
    this.createTooltipComponent(params, registeredComponent, e);
  };

  TooltipManager.prototype.createTooltipComponent = function (params, cmp, e) {
    var _this = this;

    var mouseEvent = this.lastMouseEvent;

    if (!mouseEvent) {
      return;
    }

    this.userComponentFactory.newTooltipComponent(params).then(function (tooltipComp) {
      // if the component was unregistered while creating
      // the tooltip (async) we should return undefined here.
      if (!cmp) {
        return;
      }

      cmp.tooltipComp = tooltipComp;
      var eGui = tooltipComp.getGui();

      if (!_.containsClass(eGui, 'ag-tooltip')) {
        _.addCssClass(eGui, 'ag-tooltip-custom');
      }

      var closeFnc = _this.popupService.addPopup(false, eGui, false);

      cmp.destroyFunc = function () {
        closeFnc();

        if (tooltipComp.destroy) {
          tooltipComp.destroy();
        }
      };

      _this.popupService.positionPopupUnderMouseEvent({
        type: 'tooltip',
        mouseEvent: mouseEvent,
        ePopup: eGui,
        nudgeY: 18
      });

      _this.activeComponent = _this.lastHoveredComponent;
      _this.hideTimeoutId = window.setTimeout(_this.hideTooltip.bind(_this), _this.DEFAULT_HIDE_TOOLTIP_TIMEOUT);
    });
  };

  TooltipManager.prototype.hideTooltip = function () {
    var activeComponent = this.activeComponent;
    this.clearTimers();

    if (!activeComponent) {
      return;
    }

    var id = activeComponent.getCompId();
    var registeredComponent = this.registeredComponents[id];
    this.activeComponent = undefined;

    if (!registeredComponent) {
      return;
    }

    if (registeredComponent.destroyFunc) {
      registeredComponent.destroyFunc();
    }

    this.clearRegisteredComponent(registeredComponent);
  };

  TooltipManager.prototype.clearRegisteredComponent = function (registeredComponent) {
    delete registeredComponent.destroyFunc;
    delete registeredComponent.tooltipComp;
  };

  TooltipManager.prototype.clearTimers = function (showOnly) {
    if (showOnly === void 0) {
      showOnly = false;
    }

    if (this.hideTimeoutId && !showOnly) {
      window.clearTimeout(this.hideTimeoutId);
      this.hideTimeoutId = 0;
    }

    if (this.showTimeoutId) {
      window.clearTimeout(this.showTimeoutId);
      this.showTimeoutId = 0;
    }
  };

  __decorate$1u([Autowired('popupService')], TooltipManager.prototype, "popupService", void 0);

  __decorate$1u([Autowired('userComponentFactory')], TooltipManager.prototype, "userComponentFactory", void 0);

  __decorate$1u([Autowired('columnApi')], TooltipManager.prototype, "columnApi", void 0);

  __decorate$1u([Autowired('gridApi')], TooltipManager.prototype, "gridApi", void 0);

  __decorate$1u([Autowired('gridOptionsWrapper')], TooltipManager.prototype, "gridOptionsWrapper", void 0);

  TooltipManager = __decorate$1u([Bean('tooltipManager')], TooltipManager);
  return TooltipManager;
}();
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __extends$V = undefined && undefined.__extends || function () {
  var _extendStatics58 = function extendStatics(d, b) {
    _extendStatics58 = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics58(d, b);
  };

  return function (d, b) {
    _extendStatics58(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var __decorate$1v = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var LoadingType;

(function (LoadingType) {
  LoadingType[LoadingType["Loading"] = 0] = "Loading";
  LoadingType[LoadingType["NoRows"] = 1] = "NoRows";
})(LoadingType || (LoadingType = {}));

var OverlayWrapperComponent =
/** @class */
function (_super) {
  __extends$V(OverlayWrapperComponent, _super);

  function OverlayWrapperComponent() {
    var _this = _super.call(this, OverlayWrapperComponent.TEMPLATE) || this;

    _this.inProgress = false;
    _this.destroyRequested = false;
    return _this;
  }

  OverlayWrapperComponent.prototype.postConstruct = function () {
    this.gridOptionsWrapper.addLayoutElement(this.eOverlayWrapper);
    this.setDisplayed(false);
  };

  OverlayWrapperComponent.prototype.setWrapperTypeClass = function (loadingType) {
    _.addOrRemoveCssClass(this.eOverlayWrapper, 'ag-overlay-loading-wrapper', loadingType === LoadingType.Loading);

    _.addOrRemoveCssClass(this.eOverlayWrapper, 'ag-overlay-no-rows-wrapper', loadingType === LoadingType.NoRows);
  };

  OverlayWrapperComponent.prototype.showLoadingOverlay = function () {
    var workItem = this.userComponentFactory.newLoadingOverlayComponent({
      api: this.gridOptionsWrapper.getApi()
    });
    this.showOverlay(workItem);
  };

  OverlayWrapperComponent.prototype.showNoRowsOverlay = function () {
    var workItem = this.userComponentFactory.newNoRowsOverlayComponent({
      api: this.gridOptionsWrapper.getApi()
    });
    this.showOverlay(workItem);
  };

  OverlayWrapperComponent.prototype.showOverlay = function (workItem) {
    var _this = this;

    if (this.inProgress) {
      return;
    }

    this.setWrapperTypeClass(LoadingType.NoRows);
    this.destroyActiveOverlay();
    this.inProgress = true;
    workItem.then(function (comp) {
      _this.inProgress = false;

      _this.eOverlayWrapper.appendChild(comp.getGui());

      _this.activeOverlay = comp;

      if (_this.destroyRequested) {
        _this.destroyRequested = false;

        _this.destroyActiveOverlay();
      }
    });
    this.setDisplayed(true);
  };

  OverlayWrapperComponent.prototype.destroyActiveOverlay = function () {
    if (this.inProgress) {
      this.destroyRequested = true;
      return;
    }

    if (!this.activeOverlay) {
      return;
    }

    if (this.activeOverlay.destroy) {
      this.activeOverlay.destroy();
    }

    this.activeOverlay = undefined;

    _.clearElement(this.eOverlayWrapper);
  };

  OverlayWrapperComponent.prototype.hideOverlay = function () {
    this.destroyActiveOverlay();
    this.setDisplayed(false);
  };

  OverlayWrapperComponent.prototype.destroy = function () {
    _super.prototype.destroy.call(this);

    this.destroyActiveOverlay();
  }; // wrapping in outer div, and wrapper, is needed to center the loading icon
  // The idea for centering came from here: http://www.vanseodesign.com/css/vertical-centering/


  OverlayWrapperComponent.TEMPLATE = "<div class=\"ag-overlay\" aria-hidden=\"true\">\n            <div class=\"ag-overlay-panel\">\n                <div class=\"ag-overlay-wrapper\" ref=\"eOverlayWrapper\"></div>\n            </div>\n        </div>";

  __decorate$1v([Autowired('gridOptionsWrapper')], OverlayWrapperComponent.prototype, "gridOptionsWrapper", void 0);

  __decorate$1v([Autowired('userComponentFactory')], OverlayWrapperComponent.prototype, "userComponentFactory", void 0);

  __decorate$1v([RefSelector('eOverlayWrapper')], OverlayWrapperComponent.prototype, "eOverlayWrapper", void 0);

  __decorate$1v([PostConstruct], OverlayWrapperComponent.prototype, "postConstruct", null);

  return OverlayWrapperComponent;
}(Component);
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __extends$W = undefined && undefined.__extends || function () {
  var _extendStatics59 = function extendStatics(d, b) {
    _extendStatics59 = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics59(d, b);
  };

  return function (d, b) {
    _extendStatics59(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var __decorate$1w = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var AgGroupComponent =
/** @class */
function (_super) {
  __extends$W(AgGroupComponent, _super);

  function AgGroupComponent(params) {
    var _this = _super.call(this, AgGroupComponent.TEMPLATE) || this;

    _this.suppressEnabledCheckbox = true;
    _this.suppressOpenCloseIcons = false;

    if (!params) {
      params = {};
    }

    var title = params.title,
        enabled = params.enabled,
        items = params.items,
        suppressEnabledCheckbox = params.suppressEnabledCheckbox,
        suppressOpenCloseIcons = params.suppressOpenCloseIcons;
    _this.title = title;
    _this.enabled = enabled != null ? enabled : true;
    _this.items = items || [];
    _this.alignItems = params.alignItems || 'center';

    if (suppressEnabledCheckbox != null) {
      _this.suppressEnabledCheckbox = suppressEnabledCheckbox;
    }

    if (suppressOpenCloseIcons != null) {
      _this.suppressOpenCloseIcons = suppressOpenCloseIcons;
    }

    return _this;
  }

  AgGroupComponent.prototype.postConstruct = function () {
    if (this.items.length) {
      var initialItems = this.items;
      this.items = [];
      this.addItems(initialItems);
    }

    var localeTextFunc = this.gridOptionsWrapper.getLocaleTextFunc();
    this.cbGroupEnabled.setLabel(localeTextFunc('enabled', 'Enabled'));

    if (this.title) {
      this.setTitle(this.title);
    }

    if (this.enabled) {
      this.setEnabled(this.enabled);
    }

    this.setAlignItems(this.alignItems);
    this.hideEnabledCheckbox(this.suppressEnabledCheckbox);
    this.hideOpenCloseIcons(this.suppressOpenCloseIcons);
    this.setupExpandContract();
  };

  AgGroupComponent.prototype.setupExpandContract = function () {
    var _this = this;

    this.eGroupClosedIcon.appendChild(_.createIcon('columnSelectClosed', this.gridOptionsWrapper, null));
    this.eGroupOpenedIcon.appendChild(_.createIcon('columnSelectOpen', this.gridOptionsWrapper, null));
    this.setOpenClosedIcons();
    this.addDestroyableEventListener(this.groupTitle, 'click', function () {
      return _this.toggleGroupExpand();
    });
  };

  AgGroupComponent.prototype.setOpenClosedIcons = function () {
    var folderOpen = this.expanded;

    _.setDisplayed(this.eGroupClosedIcon, !folderOpen);

    _.setDisplayed(this.eGroupOpenedIcon, folderOpen);
  };

  AgGroupComponent.prototype.isExpanded = function () {
    return this.expanded;
  };

  AgGroupComponent.prototype.setAlignItems = function (alignment) {
    var eGui = this.getGui();

    if (this.alignItems !== alignment) {
      _.removeCssClass(eGui, "ag-alignment-" + this.alignItems);
    }

    this.alignItems = alignment;
    var newCls = "ag-alignment-" + this.alignItems;

    if (alignment !== 'center' && !_.containsClass(eGui, newCls)) {
      _.addCssClass(eGui, newCls);
    }

    return this;
  };

  AgGroupComponent.prototype.toggleGroupExpand = function (expanded) {
    var eGui = this.getGui();

    if (this.suppressOpenCloseIcons) {
      this.expanded = true;

      _.removeCssClass(eGui, 'ag-collapsed');

      return this;
    }

    expanded = expanded != null ? expanded : !this.expanded;

    if (this.expanded === expanded) {
      return this;
    }

    this.expanded = expanded;
    this.setOpenClosedIcons();

    _.addOrRemoveCssClass(eGui, 'ag-collapsed', !expanded);

    if (this.expanded) {
      var event_1 = {
        type: 'expanded'
      };
      this.dispatchEvent(event_1);
    } else {
      var event_2 = {
        type: 'collapsed'
      };
      this.dispatchEvent(event_2);
    }

    return this;
  };

  AgGroupComponent.prototype.addItems = function (items) {
    var _this = this;

    items.forEach(function (item) {
      return _this.addItem(item);
    });
  };

  AgGroupComponent.prototype.addItem = function (item) {
    var container = this.groupContainer;
    var el = item instanceof Component ? item.getGui() : item;

    _.addCssClass(el, 'ag-group-item');

    container.appendChild(el);
    this.items.push(el);
  };

  AgGroupComponent.prototype.hideItem = function (hide, index) {
    var itemToHide = this.items[index];

    _.addOrRemoveCssClass(itemToHide, 'ag-hidden', hide);
  };

  AgGroupComponent.prototype.setTitle = function (title) {
    this.lbGroupTitle.innerText = title;
    return this;
  };

  AgGroupComponent.prototype.setEnabled = function (enabled, skipToggle) {
    this.enabled = enabled;

    _.addOrRemoveCssClass(this.getGui(), 'ag-disabled', !enabled);

    this.toggleGroupExpand(enabled);

    if (!skipToggle) {
      this.cbGroupEnabled.setValue(enabled);
    }

    return this;
  };

  AgGroupComponent.prototype.isEnabled = function () {
    return this.enabled;
  };

  AgGroupComponent.prototype.onEnableChange = function (callbackFn) {
    var _this = this;

    this.cbGroupEnabled.onValueChange(function (newSelection) {
      _this.setEnabled(newSelection, true);

      callbackFn(newSelection);
    });
    return this;
  };

  AgGroupComponent.prototype.hideEnabledCheckbox = function (hide) {
    _.addOrRemoveCssClass(this.eToolbar, 'ag-hidden', hide);

    return this;
  };

  AgGroupComponent.prototype.hideOpenCloseIcons = function (hide) {
    this.suppressOpenCloseIcons = hide;

    _.addOrRemoveCssClass(this.getGui(), 'ag-collapsible', !hide);

    if (hide) {
      this.toggleGroupExpand(true);
    }

    return this;
  };

  AgGroupComponent.TEMPLATE = "<div class=\"ag-group-component\">\n            <div class=\"ag-group-component-title-bar\" ref=\"groupTitle\">\n                 <span class=\"ag-column-group-icons\">\n                    <span class=\"ag-column-group-opened-icon\" ref=\"eGroupOpenedIcon\"></span>\n                    <span class=\"ag-column-group-closed-icon\" ref=\"eGroupClosedIcon\"></span>\n                </span>\n                <span ref=\"lbGroupTitle\" class=\"ag-group-component-title\"></span>\n            </div>\n            <div ref=\"eToolbar\" class=\"ag-group-component-toolbar\">\n                <ag-checkbox ref=\"cbGroupEnabled\"></ag-checkbox>\n            </div>\n            <div ref=\"eContainer\" class=\"ag-group-component-container\"></div>\n        </div>";

  __decorate$1w([Autowired('gridOptionsWrapper')], AgGroupComponent.prototype, "gridOptionsWrapper", void 0);

  __decorate$1w([RefSelector('groupTitle')], AgGroupComponent.prototype, "groupTitle", void 0);

  __decorate$1w([RefSelector('eGroupOpenedIcon')], AgGroupComponent.prototype, "eGroupOpenedIcon", void 0);

  __decorate$1w([RefSelector('eGroupClosedIcon')], AgGroupComponent.prototype, "eGroupClosedIcon", void 0);

  __decorate$1w([RefSelector('eToolbar')], AgGroupComponent.prototype, "eToolbar", void 0);

  __decorate$1w([RefSelector('cbGroupEnabled')], AgGroupComponent.prototype, "cbGroupEnabled", void 0);

  __decorate$1w([RefSelector("lbGroupTitle")], AgGroupComponent.prototype, "lbGroupTitle", void 0);

  __decorate$1w([RefSelector("eContainer")], AgGroupComponent.prototype, "groupContainer", void 0);

  __decorate$1w([PostConstruct], AgGroupComponent.prototype, "postConstruct", null);

  return AgGroupComponent;
}(Component);
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __extends$X = undefined && undefined.__extends || function () {
  var _extendStatics60 = function extendStatics(d, b) {
    _extendStatics60 = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics60(d, b);
  };

  return function (d, b) {
    _extendStatics60(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var __decorate$1x = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var AgPanel =
/** @class */
function (_super) {
  __extends$X(AgPanel, _super);

  function AgPanel(config) {
    var _this = _super.call(this, AgPanel.TEMPLATE) || this;

    _this.closable = true;
    _this.positioned = false;
    _this.dragStartPosition = {
      x: 0,
      y: 0
    };
    _this.position = {
      x: 0,
      y: 0
    };
    _this.size = {
      width: undefined,
      height: undefined
    };
    _this.config = config;
    return _this;
  }

  AgPanel.prototype.postConstruct = function () {
    var _this = this;

    var _a = this.config,
        component = _a.component,
        closable = _a.closable,
        hideTitleBar = _a.hideTitleBar,
        title = _a.title,
        minWidth = _a.minWidth,
        width = _a.width,
        minHeight = _a.minHeight,
        height = _a.height,
        centered = _a.centered,
        x = _a.x,
        y = _a.y;
    var eGui = this.getGui();

    if (component) {
      this.setBodyComponent(component);
    }

    if (!hideTitleBar) {
      if (title) {
        this.setTitle(title);
      }

      this.setClosable(closable != null ? closable : this.closable);
    } else {
      _.addCssClass(this.eTitleBar, 'ag-hidden');
    }

    this.addDestroyableEventListener(this.eTitleBar, 'mousedown', function (e) {
      if (eGui.contains(e.relatedTarget) || eGui.contains(document.activeElement) || _this.eTitleBarButtons.contains(e.target)) {
        e.preventDefault();
        return;
      }

      var focusEl = _this.eContentWrapper.querySelector('button, [href], input, select, textarea, [tabindex]');

      if (focusEl) {
        focusEl.focus();
      }
    });

    if (this.positioned) {
      return;
    }

    this.minHeight = minHeight != null ? minHeight : 250;
    this.minWidth = minWidth != null ? minWidth : 250;
    this.popupParent = this.popupService.getPopupParent();

    if (width) {
      this.setWidth(width);
    }

    if (height) {
      this.setHeight(height);
    }

    if (this.renderComponent) {
      this.renderComponent();
    }

    if (!width || !height) {
      this.refreshSize();
    }

    if (centered) {
      this.center();
    } else if (x || y) {
      this.offsetElement(x, y);
    }

    this.positioned = true;
    this.eContentWrapper.style.height = '0';
  };

  AgPanel.prototype.renderComponent = function () {
    var _this = this;

    var eGui = this.getGui();
    eGui.focus();

    this.close = function () {
      eGui.parentElement.removeChild(eGui);

      _this.destroy();
    };
  };

  AgPanel.prototype.updateDragStartPosition = function (x, y) {
    this.dragStartPosition = {
      x: x,
      y: y
    };
  };

  AgPanel.prototype.calculateMouseMovement = function (params) {
    var parentRect = this.popupParent.getBoundingClientRect();
    var e = params.e,
        isLeft = params.isLeft,
        isTop = params.isTop,
        anywhereWithin = params.anywhereWithin,
        topBuffer = params.topBuffer;
    var movementX = e.clientX - this.dragStartPosition.x;
    var movementY = e.clientY - this.dragStartPosition.y;
    var width = this.getWidth();
    var height = this.getHeight(); // skip if cursor is outside of popupParent horizontally

    var skipX = parentRect.left >= e.clientX && this.position.x <= 0 || parentRect.right <= e.clientX && parentRect.right <= this.position.x + parentRect.left + width;

    if (!skipX) {
      if (isLeft) {
        skipX = // skip if we are moving to the left and the cursor
        // is positioned to the right of the left side anchor
        movementX < 0 && e.clientX > this.position.x + parentRect.left || // skip if we are moving to the right and the cursor
        // is positioned to the left of the dialog
        movementX > 0 && e.clientX < this.position.x + parentRect.left;
      } else {
        if (anywhereWithin) {
          // if anywhereWithin is true, we allow to move
          // as long as the cursor is within the dialog
          skipX = movementX < 0 && e.clientX > this.position.x + parentRect.left + width || movementX > 0 && e.clientX < this.position.x + parentRect.left;
        } else {
          skipX = // if the movement is bound to the right side of the dialog
          // we skip if we are moving to the left and the cursor
          // is to the right of the dialog
          movementX < 0 && e.clientX > this.position.x + parentRect.left + width || // or skip if we are moving to the right and the cursor
          // is to the left of the right side anchor
          movementX > 0 && e.clientX < this.position.x + parentRect.left + width;
        }
      }
    }

    movementX = skipX ? 0 : movementX;
    var skipY = // skip if cursor is outside of popupParent vertically
    parentRect.top >= e.clientY && this.position.y <= 0 || parentRect.bottom <= e.clientY && parentRect.bottom <= this.position.y + parentRect.top + height || isTop && ( // skip if we are moving to towards top and the cursor is
    // below the top anchor + topBuffer
    // note: topBuffer is used when moving the dialog using the title bar
    movementY < 0 && e.clientY > this.position.y + parentRect.top + (topBuffer || 0) || // skip if we are moving to the bottom and the cursor is
    // above the top anchor
    movementY > 0 && e.clientY < this.position.y + parentRect.top) || // we are anchored to the bottom of the dialog
    !isTop && ( // skip if we are moving towards the top and the cursor
    // is below the bottom anchor
    movementY < 0 && e.clientY > this.position.y + parentRect.top + height || // skip if we are moving towards the bottom and the cursor
    // is above the bottom anchor
    movementY > 0 && e.clientY < this.position.y + parentRect.top + height);
    movementY = skipY ? 0 : movementY;
    return {
      movementX: movementX,
      movementY: movementY
    };
  };

  AgPanel.prototype.refreshSize = function () {
    var _a = this.size,
        width = _a.width,
        height = _a.height;

    if (!width) {
      this.setWidth(this.getGui().offsetWidth);
    }

    if (!height) {
      this.setHeight(this.getGui().offsetHeight);
    }
  };

  AgPanel.prototype.offsetElement = function (x, y) {
    if (x === void 0) {
      x = 0;
    }

    if (y === void 0) {
      y = 0;
    }

    var ePopup = this.getGui();
    this.popupService.positionPopup({
      ePopup: ePopup,
      x: x,
      y: y,
      minWidth: this.minWidth,
      minHeight: this.minHeight,
      keepWithinBounds: true
    });
    this.position.x = parseInt(ePopup.style.left, 10);
    this.position.y = parseInt(ePopup.style.top, 10);
  };

  AgPanel.prototype.getHeight = function () {
    return this.size.height;
  };

  AgPanel.prototype.setHeight = function (height) {
    var eGui = this.getGui();
    var isPercent = false;

    if (typeof height === 'string' && height.indexOf('%') !== -1) {
      _.setFixedHeight(eGui, height);

      height = _.getAbsoluteHeight(eGui);
      isPercent = true;
    } else {
      height = Math.max(this.minHeight, height);
      var offsetParent = eGui.offsetParent;

      if (offsetParent && offsetParent.clientHeight && height + this.position.y > offsetParent.clientHeight) {
        height = offsetParent.clientHeight - this.position.y;
      }
    }

    if (this.size.height === height) {
      return;
    }

    this.size.height = height;

    if (!isPercent) {
      _.setFixedHeight(eGui, height);
    } else {
      eGui.style.maxHeight = 'unset';
      eGui.style.minHeight = 'unset';
    }
  };

  AgPanel.prototype.getWidth = function () {
    return this.size.width;
  };

  AgPanel.prototype.setWidth = function (width) {
    var eGui = this.getGui();
    var isPercent = false;

    if (typeof width === 'string' && width.indexOf('%') !== -1) {
      _.setFixedWidth(eGui, width);

      width = _.getAbsoluteWidth(eGui);
      isPercent = true;
    } else {
      width = Math.max(this.minWidth, width);
      var offsetParent = eGui.offsetParent;

      if (offsetParent && offsetParent.clientWidth && width + this.position.x > offsetParent.clientWidth) {
        width = offsetParent.clientWidth - this.position.x;
      }
    }

    if (this.size.width === width) {
      return;
    }

    this.size.width = width;

    if (!isPercent) {
      _.setFixedWidth(eGui, width);
    } else {
      eGui.style.maxWidth = 'unset';
      eGui.style.minWidth = 'unset';
    }
  };

  AgPanel.prototype.center = function () {
    var eGui = this.getGui();
    var x = eGui.offsetParent.clientWidth / 2 - this.getWidth() / 2;
    var y = eGui.offsetParent.clientHeight / 2 - this.getHeight() / 2;
    this.offsetElement(x, y);
  };

  AgPanel.prototype.setClosable = function (closable) {
    if (closable !== this.closable) {
      this.closable = closable;
    }

    if (closable) {
      var closeButtonComp = this.closeButtonComp = new Component(AgPanel.CLOSE_BTN_TEMPLATE);
      this.getContext().wireBean(closeButtonComp);
      var eGui = closeButtonComp.getGui();
      eGui.appendChild(_.createIconNoSpan('close', this.gridOptionsWrapper));
      this.addTitleBarButton(closeButtonComp);
      closeButtonComp.addDestroyableEventListener(eGui, 'click', this.onBtClose.bind(this));
    } else if (this.closeButtonComp) {
      var eGui = this.closeButtonComp.getGui();
      eGui.parentElement.removeChild(eGui);
      this.closeButtonComp.destroy();
      this.closeButtonComp = undefined;
    }
  };

  AgPanel.prototype.setBodyComponent = function (bodyComponent) {
    bodyComponent.setParentComponent(this);
    this.eContentWrapper.appendChild(bodyComponent.getGui());
  };

  AgPanel.prototype.addTitleBarButton = function (button, position) {
    var eTitleBarButtons = this.eTitleBarButtons;
    var buttons = eTitleBarButtons.children;
    var len = buttons.length;

    if (position == null) {
      position = len;
    }

    position = Math.max(0, Math.min(position, len));
    var eGui = button.getGui();

    _.addCssClass(eGui, 'ag-button');

    if (position === 0) {
      eTitleBarButtons.insertAdjacentElement('afterbegin', eGui);
    } else if (position === len) {
      eTitleBarButtons.insertAdjacentElement('beforeend', eGui);
    } else {
      buttons[position - 1].insertAdjacentElement('afterend', eGui);
    }

    button.setParentComponent(this);
  };

  AgPanel.prototype.getBodyHeight = function () {
    return _.getInnerHeight(this.eContentWrapper);
  };

  AgPanel.prototype.getBodyWidth = function () {
    return _.getInnerWidth(this.eContentWrapper);
  };

  AgPanel.prototype.setTitle = function (title) {
    this.eTitle.innerText = title;
  }; // called when user hits the 'x' in the top right


  AgPanel.prototype.onBtClose = function () {
    this.close();
  };

  AgPanel.prototype.destroy = function () {
    _super.prototype.destroy.call(this);

    if (this.closeButtonComp) {
      this.closeButtonComp.destroy();
      this.closeButtonComp = undefined;
    }

    var eGui = this.getGui();

    if (eGui && eGui.offsetParent) {
      this.close();
    }
  };

  AgPanel.TEMPLATE = "<div class=\"ag-panel\" tabindex=\"-1\">\n            <div ref=\"eTitleBar\" class=\"ag-title-bar ag-unselectable\">\n                <span ref=\"eTitle\" class=\"ag-title-bar-title\"></span>\n                <div ref=\"eTitleBarButtons\" class=\"ag-title-bar-buttons\"></div>\n            </div>\n            <div ref=\"eContentWrapper\" class=\"ag-panel-content-wrapper\"></div>\n        </div>";
  AgPanel.CLOSE_BTN_TEMPLATE = "<div class=\"ag-button\"></div>";

  __decorate$1x([Autowired('popupService')], AgPanel.prototype, "popupService", void 0);

  __decorate$1x([Autowired('gridOptionsWrapper')], AgPanel.prototype, "gridOptionsWrapper", void 0);

  __decorate$1x([RefSelector('eContentWrapper')], AgPanel.prototype, "eContentWrapper", void 0);

  __decorate$1x([RefSelector('eTitleBar')], AgPanel.prototype, "eTitleBar", void 0);

  __decorate$1x([RefSelector('eTitleBarButtons')], AgPanel.prototype, "eTitleBarButtons", void 0);

  __decorate$1x([RefSelector('eTitle')], AgPanel.prototype, "eTitle", void 0);

  __decorate$1x([PostConstruct], AgPanel.prototype, "postConstruct", null);

  return AgPanel;
}(Component);
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __extends$Y = undefined && undefined.__extends || function () {
  var _extendStatics61 = function extendStatics(d, b) {
    _extendStatics61 = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics61(d, b);
  };

  return function (d, b) {
    _extendStatics61(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var __decorate$1y = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var AgDialog =
/** @class */
function (_super) {
  __extends$Y(AgDialog, _super);

  function AgDialog(config) {
    var _this = _super.call(this, config) || this;

    _this.RESIZE_TEMPLATE = "\n        <div class=\"ag-resizer-wrapper\">\n            <div ref=\"eTopLeftResizer\" class=\"ag-resizer ag-resizer-topLeft\"></div>\n            <div ref=\"eTopResizer\" class=\"ag-resizer ag-resizer-top\"></div>\n            <div ref=\"eTopRightResizer\" class=\"ag-resizer ag-resizer-topRight\"></div>\n            <div ref=\"eRightResizer\" class=\"ag-resizer ag-resizer-right\"></div>\n            <div ref=\"eBottomRightResizer\" class=\"ag-resizer ag-resizer-bottomRight\"></div>\n            <div ref=\"eBottomResizer\" class=\"ag-resizer ag-resizer-bottom\"></div>\n            <div ref=\"eBottomLeftResizer\" class=\"ag-resizer ag-resizer-bottomLeft\"></div>\n            <div ref=\"eLeftResizer\" class=\"ag-resizer ag-resizer-left\"></div>\n        </div>\n    ";
    _this.MAXIMIZE_BTN_TEMPLATE = "<div class=\"ag-dialog-button\"></span>";
    _this.resizable = {};
    _this.isResizable = false;
    _this.movable = false;
    _this.isMoving = false;
    _this.isMaximizable = false;
    _this.isMaximized = false;
    _this.maximizeListeners = [];
    _this.resizeListenerDestroy = null;
    _this.isResizing = false;
    _this.lastPosition = {
      x: 0,
      y: 0,
      width: 0,
      height: 0
    };
    return _this;
  }

  AgDialog.prototype.postConstruct = function () {
    var _this = this;

    var eGui = this.getGui();
    var _a = this.config,
        movable = _a.movable,
        resizable = _a.resizable,
        maximizable = _a.maximizable;

    _.addCssClass(eGui, 'ag-dialog');

    this.moveElement = this.eTitleBar;

    _super.prototype.postConstruct.call(this);

    this.addDestroyableEventListener(eGui, 'focusin', function (e) {
      if (eGui.contains(e.relatedTarget)) {
        return;
      }

      _this.popupService.bringPopupToFront(eGui);
    });

    if (movable) {
      this.setMovable(movable);
    }

    if (maximizable) {
      this.setMaximizable(maximizable);
    }

    this.addResizers();

    if (resizable) {
      this.setResizable(resizable);
    }
  };

  AgDialog.prototype.renderComponent = function () {
    var eGui = this.getGui();
    var _a = this.config,
        alwaysOnTop = _a.alwaysOnTop,
        modal = _a.modal;
    this.close = this.popupService.addPopup(modal, eGui, true, this.destroy.bind(this), undefined, alwaysOnTop);
    eGui.focus();
  };

  AgDialog.prototype.addResizers = function () {
    var eGui = this.getGui();

    if (!eGui) {
      return;
    }

    var parser = new DOMParser();
    var resizers = parser.parseFromString(this.RESIZE_TEMPLATE, 'text/html').body;
    eGui.appendChild(resizers.firstChild);
    this.createMap();
  };

  AgDialog.prototype.createMap = function () {
    var eGui = this.getGui();
    this.resizerMap = {
      topLeft: {
        element: eGui.querySelector('[ref=eTopLeftResizer]')
      },
      top: {
        element: eGui.querySelector('[ref=eTopResizer]')
      },
      topRight: {
        element: eGui.querySelector('[ref=eTopRightResizer]')
      },
      right: {
        element: eGui.querySelector('[ref=eRightResizer]')
      },
      bottomRight: {
        element: eGui.querySelector('[ref=eBottomRightResizer]')
      },
      bottom: {
        element: eGui.querySelector('[ref=eBottomResizer]')
      },
      bottomLeft: {
        element: eGui.querySelector('[ref=eBottomLeftResizer]')
      },
      left: {
        element: eGui.querySelector('[ref=eLeftResizer]')
      }
    };
  };

  AgDialog.prototype.getResizerElement = function (side) {
    return this.resizerMap[side].element;
  };

  AgDialog.prototype.onResizeStart = function (e) {
    this.isResizing = true;
    this.updateDragStartPosition(e.clientX, e.clientY);
  };

  AgDialog.prototype.onResize = function (e, side) {
    if (!this.isResizing) {
      return;
    }

    var isLeft = !!side.match(/left/i);
    var isRight = !!side.match(/right/i);
    var isTop = !!side.match(/top/i);
    var isBottom = !!side.match(/bottom/i);
    var isHorizontal = isLeft || isRight;
    var isVertical = isTop || isBottom;

    var _a = this.calculateMouseMovement({
      e: e,
      isLeft: isLeft,
      isTop: isTop
    }),
        movementX = _a.movementX,
        movementY = _a.movementY;

    var offsetLeft = 0;
    var offsetTop = 0;

    if (isHorizontal && movementX) {
      var direction = isLeft ? -1 : 1;
      var oldWidth = this.getWidth();
      var newWidth = oldWidth + movementX * direction;
      var skipWidth = false;

      if (isLeft) {
        offsetLeft = oldWidth - newWidth;

        if (this.position.x + offsetLeft <= 0 || newWidth <= this.minWidth) {
          skipWidth = true;
          offsetLeft = 0;
        }
      }

      if (!skipWidth) {
        this.setWidth(newWidth);
      }
    }

    if (isVertical && movementY) {
      var direction = isTop ? -1 : 1;
      var oldHeight = this.getHeight();
      var newHeight = oldHeight + movementY * direction;
      var skipHeight = false;

      if (isTop) {
        offsetTop = oldHeight - newHeight;

        if (this.position.y + offsetTop <= 0 || newHeight <= this.minHeight) {
          skipHeight = true;
          offsetTop = 0;
        }
      }

      if (!skipHeight) {
        this.setHeight(newHeight);
      }
    }

    this.updateDragStartPosition(e.clientX, e.clientY);

    if (offsetLeft || offsetTop) {
      this.offsetElement(this.position.x + offsetLeft, this.position.y + offsetTop);
    }
  };

  AgDialog.prototype.onResizeEnd = function () {
    this.isResizing = false;
    var params = {
      type: 'resize',
      api: this.gridOptionsWrapper.getApi(),
      columnApi: this.gridOptionsWrapper.getColumnApi()
    };

    if (this.localEventService) {
      this.localEventService.dispatchEvent(params);
    }
  };

  AgDialog.prototype.onMoveStart = function (e) {
    this.isMoving = true;
    this.updateDragStartPosition(e.clientX, e.clientY);
  };

  AgDialog.prototype.onMove = function (e) {
    if (!this.isMoving) {
      return;
    }

    var _a = this.position,
        x = _a.x,
        y = _a.y;

    var _b = this.calculateMouseMovement({
      e: e,
      isTop: true,
      anywhereWithin: true,
      topBuffer: this.getHeight() - this.getBodyHeight()
    }),
        movementX = _b.movementX,
        movementY = _b.movementY;

    this.offsetElement(x + movementX, y + movementY);
    this.updateDragStartPosition(e.clientX, e.clientY);
  };

  AgDialog.prototype.onMoveEnd = function () {
    this.isMoving = false;
  };

  AgDialog.prototype.toggleMaximize = function () {
    if (this.isMaximized) {
      var _a = this.lastPosition,
          x = _a.x,
          y = _a.y,
          width = _a.width,
          height = _a.height;
      this.setWidth(width);
      this.setHeight(height);
      this.offsetElement(x, y);
    } else {
      this.lastPosition.width = this.getWidth();
      this.lastPosition.height = this.getHeight();
      this.lastPosition.x = this.position.x;
      this.lastPosition.y = this.position.y;
      this.offsetElement(0, 0);
      this.setHeight('100%');
      this.setWidth('100%');
    }

    this.isMaximized = !this.isMaximized;
    this.refreshMaximizeIcon();
  };

  AgDialog.prototype.refreshMaximizeIcon = function () {
    _.addOrRemoveCssClass(this.maximizeIcon, 'ag-hidden', this.isMaximized);

    _.addOrRemoveCssClass(this.minimizeIcon, 'ag-hidden', !this.isMaximized);
  };

  AgDialog.prototype.clearMaximizebleListeners = function () {
    if (this.maximizeListeners.length) {
      this.maximizeListeners.forEach(function (destroyListener) {
        return destroyListener();
      });
      this.maximizeListeners.length = 0;
    }

    if (this.resizeListenerDestroy) {
      this.resizeListenerDestroy();
      this.resizeListenerDestroy = null;
    }
  };

  AgDialog.prototype.destroy = function () {
    _super.prototype.destroy.call(this);

    this.setResizable(false);
    this.setMovable(false);

    if (this.maximizeButtonComp) {
      this.maximizeButtonComp.destroy();
      this.maximizeButtonComp = undefined;
    }

    this.clearMaximizebleListeners();
  };

  AgDialog.prototype.setResizable = function (resizable) {
    var _this = this;

    var isResizable = false;

    if (typeof resizable === 'boolean') {
      resizable = {
        topLeft: resizable,
        top: resizable,
        topRight: resizable,
        right: resizable,
        bottomRight: resizable,
        bottom: resizable,
        bottomLeft: resizable,
        left: resizable
      };
    }

    Object.keys(resizable).forEach(function (side) {
      var r = resizable;
      var s = side;
      var val = !!r[s];

      var el = _this.getResizerElement(s);

      var params = _this.resizerMap[s].dragSource || {
        eElement: el,
        onDragStart: _this.onResizeStart.bind(_this),
        onDragging: function onDragging(e) {
          return _this.onResize(e, s);
        },
        onDragStop: _this.onResizeEnd.bind(_this)
      };

      if (!!_this.resizable[s] !== val || !_this.isAlive() && !val) {
        if (val) {
          _this.dragService.addDragSource(params);

          el.style.pointerEvents = 'all';
          isResizable = true;
        } else {
          _this.dragService.removeDragSource(params);

          el.style.pointerEvents = 'none';
        }

        _this.resizerMap[s].dragSource = val ? params : undefined;
      }
    });
    this.isResizable = isResizable;
  };

  AgDialog.prototype.setMovable = function (movable) {
    if (movable !== this.movable) {
      this.movable = movable;
      var params = this.moveElementDragListener || {
        eElement: this.moveElement,
        onDragStart: this.onMoveStart.bind(this),
        onDragging: this.onMove.bind(this),
        onDragStop: this.onMoveEnd.bind(this)
      };

      if (movable) {
        this.dragService.addDragSource(params);
        this.moveElementDragListener = params;
      } else {
        this.dragService.removeDragSource(params);
        this.moveElementDragListener = undefined;
      }
    }
  };

  AgDialog.prototype.setMaximizable = function (maximizable) {
    var _this = this;

    if (maximizable === false) {
      this.clearMaximizebleListeners();

      if (this.maximizeButtonComp) {
        this.maximizeButtonComp.destroy();
        this.maximizeButtonComp = this.maximizeIcon = this.minimizeIcon = undefined;
      }

      return;
    }

    var eTitleBar = this.eTitleBar;

    if (!eTitleBar || maximizable === this.isMaximizable) {
      return;
    }

    var maximizeButtonComp = this.maximizeButtonComp = new Component(this.MAXIMIZE_BTN_TEMPLATE);
    this.getContext().wireBean(maximizeButtonComp);
    var eGui = maximizeButtonComp.getGui();
    eGui.appendChild(this.maximizeIcon = _.createIconNoSpan('maximize', this.gridOptionsWrapper));
    eGui.appendChild(this.minimizeIcon = _.createIconNoSpan('minimize', this.gridOptionsWrapper));

    _.addCssClass(this.minimizeIcon, 'ag-hidden');

    maximizeButtonComp.addDestroyableEventListener(eGui, 'click', this.toggleMaximize.bind(this));
    this.addTitleBarButton(maximizeButtonComp, 0);
    this.maximizeListeners.push(this.addDestroyableEventListener(eTitleBar, 'dblclick', this.toggleMaximize.bind(this)));
    this.resizeListenerDestroy = this.addDestroyableEventListener(this, 'resize', function () {
      _this.isMaximized = false;

      _this.refreshMaximizeIcon();
    });
  };

  __decorate$1y([Autowired('dragService')], AgDialog.prototype, "dragService", void 0);

  return AgDialog;
}(AgPanel);
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __extends$Z = undefined && undefined.__extends || function () {
  var _extendStatics62 = function extendStatics(d, b) {
    _extendStatics62 = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics62(d, b);
  };

  return function (d, b) {
    _extendStatics62(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var AgInputTextField =
/** @class */
function (_super) {
  __extends$Z(AgInputTextField, _super);

  function AgInputTextField(config) {
    var _this = _super.call(this) || this;

    _this.className = 'ag-text-field';
    _this.displayTag = 'input';
    _this.inputType = 'text';

    _this.setTemplate(_this.TEMPLATE.replace(/%displayField%/g, _this.displayTag));

    if (config) {
      _this.config = config;
    }

    return _this;
  }

  AgInputTextField.prototype.setValue = function (value, silent) {
    var ret = _super.prototype.setValue.call(this, value, silent);

    if (this.eInput.value !== value) {
      this.eInput.value = value;
    }

    return ret;
  };

  return AgInputTextField;
}(AgAbstractInputField);
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __extends$_ = undefined && undefined.__extends || function () {
  var _extendStatics63 = function extendStatics(d, b) {
    _extendStatics63 = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics63(d, b);
  };

  return function (d, b) {
    _extendStatics63(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var AgInputTextArea =
/** @class */
function (_super) {
  __extends$_(AgInputTextArea, _super);

  function AgInputTextArea(config) {
    var _this = _super.call(this) || this;

    _this.className = 'ag-text-area';
    _this.displayTag = 'textarea';
    _this.inputType = '';

    _this.setTemplate(_this.TEMPLATE.replace(/%displayField%/g, _this.displayTag));

    if (config) {
      _this.config = config;
    }

    return _this;
  }

  AgInputTextArea.prototype.setValue = function (value, silent) {
    var ret = _super.prototype.setValue.call(this, value, silent);

    this.eInput.value = value;
    return ret;
  };

  return AgInputTextArea;
}(AgAbstractInputField);
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __extends$$ = undefined && undefined.__extends || function () {
  var _extendStatics64 = function extendStatics(d, b) {
    _extendStatics64 = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics64(d, b);
  };

  return function (d, b) {
    _extendStatics64(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var __decorate$1z = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var AgSlider =
/** @class */
function (_super) {
  __extends$$(AgSlider, _super);

  function AgSlider() {
    var _this = _super.call(this, AgSlider.TEMPLATE) || this;

    _this.labelAlignment = 'top';
    return _this;
  }

  AgSlider.prototype.onValueChange = function (callbackFn) {
    var _this = this;

    var eventChanged = AgAbstractField.EVENT_CHANGED;
    this.addDestroyableEventListener(this.eText, eventChanged, function () {
      var textValue = parseFloat(_this.eText.getValue());

      _this.eSlider.setValue(textValue.toString(), true);

      callbackFn(textValue || 0);
    });
    this.addDestroyableEventListener(this.eSlider, eventChanged, function () {
      var sliderValue = _this.eSlider.getValue();

      _this.eText.setValue(sliderValue, true);

      callbackFn(parseFloat(sliderValue));
    });
    return this;
  };

  AgSlider.prototype.setSliderWidth = function (width) {
    this.eSlider.setWidth(width);
    return this;
  };

  AgSlider.prototype.setTextFieldWidth = function (width) {
    this.eText.setWidth(width);
    return this;
  };

  AgSlider.prototype.setMinValue = function (minValue) {
    this.eSlider.setMinValue(minValue);
    this.eText.setMin(minValue);
    return this;
  };

  AgSlider.prototype.setMaxValue = function (maxValue) {
    this.eSlider.setMaxValue(maxValue);
    this.eText.setMax(maxValue);
    return this;
  };

  AgSlider.prototype.getValue = function () {
    return this.eText.getValue();
  };

  AgSlider.prototype.setValue = function (value) {
    if (this.getValue() === value) {
      return this;
    }

    this.eText.setValue(value, true);
    this.eSlider.setValue(value, true);
    this.dispatchEvent({
      type: AgAbstractField.EVENT_CHANGED
    });
    return this;
  };

  AgSlider.prototype.setStep = function (step) {
    this.eSlider.setStep(step);
    this.eText.setStep(step);
    return this;
  };

  AgSlider.TEMPLATE = "<div class=\"ag-slider\">\n            <label ref=\"eLabel\"></label>\n            <div class=\"ag-wrapper\">\n                <ag-input-range ref=\"eSlider\"></ag-input-range>\n                <ag-input-number-field ref=\"eText\"></ag-input-number-field>\n            </div>\n        </div>";

  __decorate$1z([RefSelector('eLabel')], AgSlider.prototype, "eLabel", void 0);

  __decorate$1z([RefSelector('eSlider')], AgSlider.prototype, "eSlider", void 0);

  __decorate$1z([RefSelector('eText')], AgSlider.prototype, "eText", void 0);

  return AgSlider;
}(AgAbstractLabel);
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __extends$10 = undefined && undefined.__extends || function () {
  var _extendStatics65 = function extendStatics(d, b) {
    _extendStatics65 = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics65(d, b);
  };

  return function (d, b) {
    _extendStatics65(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var __decorate$1A = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var AgColorPanel =
/** @class */
function (_super) {
  __extends$10(AgColorPanel, _super);

  function AgColorPanel(config) {
    var _this = _super.call(this, AgColorPanel.TEMPLATE) || this;

    _this.H = 1; // in the [0, 1] range

    _this.S = 1; // in the [0, 1] range

    _this.B = 1; // in the [0, 1] range

    _this.A = 1; // in the [0, 1] range

    _this.isSpectrumDragging = false;
    _this.isSpectrumHueDragging = false;
    _this.isSpectrumAlphaDragging = false;
    _this.colorChanged = false;
    _this.picker = config.picker;
    return _this;
  }

  AgColorPanel.prototype.postConstruct = function () {
    var eGui = this.getGui();
    this.initRecentColors();
    this.addDestroyableEventListener(this.spectrumVal, 'mousedown', this.onSpectrumDraggerDown.bind(this));
    this.addDestroyableEventListener(eGui, 'mousemove', this.onSpectrumDraggerMove.bind(this));
    this.addDestroyableEventListener(this.spectrumHue, 'mousedown', this.onSpectrumHueDown.bind(this));
    this.addDestroyableEventListener(eGui, 'mousemove', this.onSpectrumHueMove.bind(this));
    this.addDestroyableEventListener(this.spectrumAlpha, 'mousedown', this.onSpectrumAlphaDown.bind(this));
    this.addDestroyableEventListener(eGui, 'mousemove', this.onSpectrumAlphaMove.bind(this)); // Listening to `mouseup` on the document on purpose. The user might release the mouse button
    // outside the UI control. When the mouse returns back to the control's area, the dragging
    // of the thumb is not expected and seen as a bug.

    this.addDestroyableEventListener(document, 'mouseup', this.onMouseUp.bind(this));
    this.addDestroyableEventListener(this.recentColors, 'click', this.onRecentColorClick.bind(this));
  };

  AgColorPanel.prototype.refreshSpectrumRect = function () {
    return this.spectrumValRect = this.spectrumVal.getBoundingClientRect();
  };

  AgColorPanel.prototype.refreshHueRect = function () {
    return this.spectrumHueRect = this.spectrumHue.getBoundingClientRect();
  };

  AgColorPanel.prototype.refreshAlphaRect = function () {
    return this.spectrumAlphaRect = this.spectrumAlpha.getBoundingClientRect();
  };

  AgColorPanel.prototype.onSpectrumDraggerDown = function (e) {
    this.refreshSpectrumRect();
    this.isSpectrumDragging = true;
    this.moveDragger(e);
  };

  AgColorPanel.prototype.onSpectrumDraggerMove = function (e) {
    if (this.isSpectrumDragging) {
      this.moveDragger(e);
    }
  };

  AgColorPanel.prototype.onSpectrumHueDown = function (e) {
    this.refreshHueRect();
    this.isSpectrumHueDragging = true;
    this.moveHueSlider(e);
  };

  AgColorPanel.prototype.onSpectrumHueMove = function (e) {
    if (this.isSpectrumHueDragging) {
      this.moveHueSlider(e);
    }
  };

  AgColorPanel.prototype.onSpectrumAlphaDown = function (e) {
    this.refreshAlphaRect();
    this.isSpectrumAlphaDragging = true;
    this.moveAlphaSlider(e);
  };

  AgColorPanel.prototype.onSpectrumAlphaMove = function (e) {
    if (this.isSpectrumAlphaDragging) {
      this.moveAlphaSlider(e);
    }
  };

  AgColorPanel.prototype.onMouseUp = function () {
    this.isSpectrumDragging = false;
    this.isSpectrumHueDragging = false;
    this.isSpectrumAlphaDragging = false;
  };

  AgColorPanel.prototype.moveDragger = function (e) {
    var valRect = this.spectrumValRect;

    if (valRect) {
      var x = e.clientX - valRect.left;
      var y = e.clientY - valRect.top;
      x = Math.max(x, 0);
      x = Math.min(x, valRect.width);
      y = Math.max(y, 0);
      y = Math.min(y, valRect.height);
      this.setSpectrumValue(x / valRect.width, 1 - y / valRect.height);
    }
  };

  AgColorPanel.prototype.moveHueSlider = function (e) {
    var hueRect = this.spectrumHueRect;

    if (hueRect) {
      var slider = this.spectrumHueSlider;
      var sliderRect = slider.getBoundingClientRect();
      var x = e.clientX - hueRect.left;
      x = Math.max(x, 0);
      x = Math.min(x, hueRect.width);
      this.H = 1 - x / hueRect.width;
      slider.style.left = x + sliderRect.width / 2 + 'px';
      this.update();
    }
  };

  AgColorPanel.prototype.moveAlphaSlider = function (e) {
    var alphaRect = this.spectrumAlphaRect;

    if (alphaRect) {
      var slider = this.spectrumAlphaSlider;
      var sliderRect = slider.getBoundingClientRect();
      var x = e.clientX - alphaRect.left;
      x = Math.max(x, 0);
      x = Math.min(x, alphaRect.width);
      this.A = x / alphaRect.width;
      slider.style.left = x + sliderRect.width / 2 + 'px';
      this.update();
    }
  };

  AgColorPanel.prototype.update = function () {
    var color = Color.fromHSB(this.H * 360, this.S, this.B, this.A);
    var spectrumColor = Color.fromHSB(this.H * 360, 1, 1);
    var rgbaColor = color.toRgbaString(); // the recent color list needs to know color has actually changed

    var colorPicker = this.picker;
    var existingColor = Color.fromString(colorPicker.getValue());

    if (existingColor.toRgbaString() !== rgbaColor) {
      this.colorChanged = true;
    }

    colorPicker.setValue(rgbaColor);
    this.spectrumColor.style.backgroundColor = spectrumColor.toRgbaString();
    this.spectrumDragger.style.backgroundColor = rgbaColor;
  };
  /**
   * @param saturation In the [0, 1] interval.
   * @param brightness In the [0, 1] interval.
   */


  AgColorPanel.prototype.setSpectrumValue = function (saturation, brightness) {
    var valRect = this.spectrumValRect || this.refreshSpectrumRect();

    if (valRect) {
      var dragger = this.spectrumDragger;
      var draggerRect = dragger.getBoundingClientRect();
      saturation = Math.max(0, saturation);
      saturation = Math.min(1, saturation);
      brightness = Math.max(0, brightness);
      brightness = Math.min(1, brightness);
      this.S = saturation;
      this.B = brightness;
      dragger.style.left = saturation * valRect.width - draggerRect.width / 2 + 'px';
      dragger.style.top = (1 - brightness) * valRect.height - draggerRect.height / 2 + 'px';
      this.update();
    }
  };

  AgColorPanel.prototype.initRecentColors = function () {
    var recentColors = AgColorPanel.recentColors;
    var innerHtml = recentColors.map(function (color, index) {
      return "<div class=\"ag-recent-color\" id=" + index + " style=\"background-color: " + color + "; width: 15px; height: 15px;\" recent-color=\"" + color + "\"></div>";
    });
    this.recentColors.innerHTML = innerHtml.join('');
  };

  AgColorPanel.prototype.setValue = function (val) {
    var color = Color.fromString(val);

    var _a = color.toHSB(),
        h = _a[0],
        s = _a[1],
        b = _a[2];

    this.H = (isNaN(h) ? 0 : h) / 360;
    this.A = color.a;
    var spectrumHueRect = this.spectrumHueRect || this.refreshHueRect();
    var spectrumAlphaRect = this.spectrumAlphaRect || this.refreshAlphaRect();
    this.spectrumHueSlider.style.left = (this.H - 1) * -spectrumHueRect.width + "px";
    this.spectrumAlphaSlider.style.left = this.A * spectrumAlphaRect.width + "px";
    this.setSpectrumValue(s, b);
  };

  AgColorPanel.prototype.onRecentColorClick = function (e) {
    var target = e.target;

    if (!_.exists(target.id)) {
      return;
    }

    var id = parseInt(target.id, 10);
    this.setValue(AgColorPanel.recentColors[id]);
    this.destroy();
  };

  AgColorPanel.prototype.addRecentColor = function () {
    var color = Color.fromHSB(this.H * 360, this.S, this.B, this.A);
    var rgbaColor = color.toRgbaString();
    var recentColors = AgColorPanel.recentColors;

    if (!this.colorChanged || recentColors[0] === rgbaColor) {
      return;
    } // remove duplicate color


    recentColors = recentColors.filter(function (color) {
      return color != rgbaColor;
    }); // add color to head

    recentColors = [rgbaColor].concat(recentColors); // ensure we don't exceed max number of recent colors

    if (recentColors.length > AgColorPanel.maxRecentColors) {
      recentColors = recentColors.slice(0, AgColorPanel.maxRecentColors);
    }

    AgColorPanel.recentColors = recentColors;
  };

  AgColorPanel.prototype.destroy = function () {
    _super.prototype.destroy.call(this);

    this.addRecentColor();
  };

  AgColorPanel.maxRecentColors = 8;
  AgColorPanel.recentColors = [];
  AgColorPanel.TEMPLATE = "<div class=\"ag-color-panel\">\n            <div ref=\"spectrumColor\" class=\"ag-spectrum-color\">\n                <div class=\"ag-spectrum-sat ag-fill\">\n                    <div ref=\"spectrumVal\" class=\"ag-spectrum-val ag-fill\">\n                        <div ref=\"spectrumDragger\" class=\"ag-spectrum-dragger\"></div>\n                    </div>\n                </div>\n            </div>\n            <div class=\"ag-spectrum-tools\">\n                <div ref=\"spectrumHue\" class=\"ag-spectrum-hue ag-hue-alpha\">\n                    <div class=\"ag-spectrum-hue-background\"></div>\n                    <div ref=\"spectrumHueSlider\" class=\"ag-spectrum-slider\"></div>\n                </div>\n                <div ref=\"spectrumAlpha\" class=\"ag-spectrum-alpha ag-hue-alpha\">\n                    <div class=\"ag-spectrum-alpha-background\"></div>\n                    <div ref=\"spectrumAlphaSlider\" class=\"ag-spectrum-slider\"></div>\n                </div>\n                <div ref=\"recentColors\" class=\"ag-recent-colors\"></div>\n            </div>\n        </div>";

  __decorate$1A([RefSelector('spectrumColor')], AgColorPanel.prototype, "spectrumColor", void 0);

  __decorate$1A([RefSelector('spectrumVal')], AgColorPanel.prototype, "spectrumVal", void 0);

  __decorate$1A([RefSelector('spectrumDragger')], AgColorPanel.prototype, "spectrumDragger", void 0);

  __decorate$1A([RefSelector('spectrumHue')], AgColorPanel.prototype, "spectrumHue", void 0);

  __decorate$1A([RefSelector('spectrumHueSlider')], AgColorPanel.prototype, "spectrumHueSlider", void 0);

  __decorate$1A([RefSelector('spectrumAlpha')], AgColorPanel.prototype, "spectrumAlpha", void 0);

  __decorate$1A([RefSelector('spectrumAlphaSlider')], AgColorPanel.prototype, "spectrumAlphaSlider", void 0);

  __decorate$1A([RefSelector('recentColors')], AgColorPanel.prototype, "recentColors", void 0);

  __decorate$1A([PostConstruct], AgColorPanel.prototype, "postConstruct", null);

  return AgColorPanel;
}(Component);
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __extends$11 = undefined && undefined.__extends || function () {
  var _extendStatics66 = function extendStatics(d, b) {
    _extendStatics66 = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics66(d, b);
  };

  return function (d, b) {
    _extendStatics66(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var __decorate$1B = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var AgPickerField =
/** @class */
function (_super) {
  __extends$11(AgPickerField, _super);

  function AgPickerField() {
    var _this = _super !== null && _super.apply(this, arguments) || this;

    _this.TEMPLATE = "<div class=\"ag-picker-field\">\n            <label ref=\"eLabel\"></label>\n            <div ref=\"eWrapper\" class=\"ag-wrapper\">\n                <%displayField% ref=\"eDisplayField\"></%displayField%>\n                <button ref=\"eButton\" class=\"ag-picker-button\"> </button>\n            </div>\n        </div>";
    _this.displayedPicker = false;
    _this.isDestroyingPicker = false;
    return _this;
  }

  AgPickerField.prototype.postConstruct = function () {
    var _this = this;

    _super.prototype.postConstruct.call(this);

    this.addDestroyableEventListener(this.eButton, 'click', function () {
      _this.showPicker();
    });

    if (this.pickerIcon) {
      this.eButton.appendChild(_.createIconNoSpan(this.pickerIcon, this.gridOptionsWrapper, null));
    }
  };

  AgPickerField.prototype.setInputWidth = function (width) {
    _.setElementWidth(this.eWrapper, width);

    return this;
  };

  __decorate$1B([Autowired('gridOptionsWrapper')], AgPickerField.prototype, "gridOptionsWrapper", void 0);

  __decorate$1B([RefSelector('eLabel')], AgPickerField.prototype, "eLabel", void 0);

  __decorate$1B([RefSelector('eWrapper')], AgPickerField.prototype, "eWrapper", void 0);

  __decorate$1B([RefSelector('eDisplayField')], AgPickerField.prototype, "eDisplayField", void 0);

  __decorate$1B([RefSelector('eButton')], AgPickerField.prototype, "eButton", void 0);

  return AgPickerField;
}(AgAbstractField);
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __extends$12 = undefined && undefined.__extends || function () {
  var _extendStatics67 = function extendStatics(d, b) {
    _extendStatics67 = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics67(d, b);
  };

  return function (d, b) {
    _extendStatics67(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var AgColorPicker =
/** @class */
function (_super) {
  __extends$12(AgColorPicker, _super);

  function AgColorPicker(config) {
    var _this = _super.call(this) || this;

    _this.displayTag = 'div';
    _this.className = 'ag-color-picker';
    _this.pickerIcon = 'colorPicker';

    _this.setTemplate(_this.TEMPLATE.replace(/%displayField%/g, _this.displayTag));

    if (config && config.color) {
      _this.value = config.color;
    }

    return _this;
  }

  AgColorPicker.prototype.postConstruct = function () {
    var _this = this;

    _super.prototype.postConstruct.call(this);

    _.addCssClass(this.getGui(), this.className);

    this.addDestroyableEventListener(this.eDisplayField, 'click', function () {
      return _this.showPicker();
    });

    if (this.value) {
      this.setValue(this.value);
    }
  };

  AgColorPicker.prototype.showPicker = function () {
    var _this = this;

    if (this.displayedPicker) {
      this.displayedPicker = false;
      return;
    }

    var eGuiRect = this.getGui().getBoundingClientRect();
    var colorDialog = new AgDialog({
      closable: false,
      modal: true,
      hideTitleBar: true,
      minWidth: 190,
      width: 190,
      height: 250,
      x: eGuiRect.right - 190,
      y: eGuiRect.top - 250
    });
    this.getContext().wireBean(colorDialog);

    _.addCssClass(colorDialog.getGui(), 'ag-color-dialog');

    var colorPanel = new AgColorPanel({
      picker: this
    });
    this.getContext().wireBean(colorPanel);
    colorPanel.addDestroyFunc(function () {
      if (colorDialog.isAlive()) {
        colorDialog.destroy();
      }
    });
    colorDialog.setParentComponent(this);
    colorDialog.setBodyComponent(colorPanel);
    colorPanel.setValue(this.getValue());
    colorDialog.addDestroyFunc(function () {
      var wasDestroying = _this.isDestroyingPicker;
      _this.displayedPicker = false; // here we check if the picker was already being
      // destroyed to avoid a stackoverflow

      if (!wasDestroying) {
        _this.isDestroyingPicker = true;

        if (colorPanel.isAlive()) {
          colorPanel.destroy();
        }
      } else {
        _this.isDestroyingPicker = false;
      }
    });
  };

  AgColorPicker.prototype.setValue = function (color) {
    if (this.value === color) {
      return this;
    }

    this.value = color;
    this.eDisplayField.style.backgroundColor = color;
    this.dispatchEvent({
      type: AgAbstractField.EVENT_CHANGED
    });
    return this;
  };

  AgColorPicker.prototype.getValue = function () {
    return this.value;
  };

  return AgColorPicker;
}(AgPickerField);
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __extends$13 = undefined && undefined.__extends || function () {
  var _extendStatics68 = function extendStatics(d, b) {
    _extendStatics68 = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics68(d, b);
  };

  return function (d, b) {
    _extendStatics68(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var AgInputNumberField =
/** @class */
function (_super) {
  __extends$13(AgInputNumberField, _super);

  function AgInputNumberField() {
    var _this = _super !== null && _super.apply(this, arguments) || this;

    _this.className = 'ag-number-field';
    _this.inputType = 'number';
    return _this;
  }

  AgInputNumberField.prototype.postConstruct = function () {
    var _this = this;

    _super.prototype.postConstruct.call(this);

    this.addDestroyableEventListener(this.eInput, 'blur', function () {
      var value = _this.normalizeValue(_this.eInput.value);

      if (_this.value !== value) {
        _this.setValue(value);
      }
    });
  };

  AgInputNumberField.prototype.normalizeValue = function (value) {
    if (value === '') {
      return '';
    }

    if (this.precision) {
      value = this.adjustPrecision(value);
    }

    var val = parseFloat(value);

    if (this.min != null && val < this.min) {
      value = this.min.toString();
    } else if (this.max != null && val > this.max) {
      value = this.max.toString();
    }

    return value;
  };

  AgInputNumberField.prototype.adjustPrecision = function (value) {
    if (this.precision) {
      var floatString = parseFloat(value).toFixed(this.precision);
      value = parseFloat(floatString).toString();
    }

    return value;
  };

  AgInputNumberField.prototype.setMin = function (min) {
    if (this.min === min) {
      return this;
    }

    this.min = min;

    if (this.min != null) {
      this.eInput.setAttribute('min', min.toString());
    } else {
      this.eInput.removeAttribute('min');
    }

    return this;
  };

  AgInputNumberField.prototype.setMax = function (max) {
    if (this.max === max) {
      return this;
    }

    this.max = max;

    if (this.max != null) {
      this.eInput.setAttribute('max', max.toString());
    } else {
      this.eInput.removeAttribute('max');
    }

    return this;
  };

  AgInputNumberField.prototype.setPrecision = function (precision) {
    this.precision = precision;
    return this;
  };

  AgInputNumberField.prototype.setStep = function (step) {
    if (this.step === step) {
      return this;
    }

    this.step = step;

    if (step != null) {
      this.eInput.setAttribute('step', step.toString());
    } else {
      this.eInput.removeAttribute('step');
    }

    return this;
  };

  AgInputNumberField.prototype.setValue = function (value, silent) {
    value = this.adjustPrecision(value);
    var normalizedValue = this.normalizeValue(value);

    if (value != normalizedValue) {
      return this;
    }

    return _super.prototype.setValue.call(this, value, silent);
  };

  return AgInputNumberField;
}(AgInputTextField);
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __extends$14 = undefined && undefined.__extends || function () {
  var _extendStatics69 = function extendStatics(d, b) {
    _extendStatics69 = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics69(d, b);
  };

  return function (d, b) {
    _extendStatics69(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var AgInputRange =
/** @class */
function (_super) {
  __extends$14(AgInputRange, _super);

  function AgInputRange(config) {
    var _this = _super.call(this) || this;

    _this.className = 'ag-range-field';
    _this.displayTag = 'input';
    _this.inputType = 'range';

    _this.setTemplate(_this.TEMPLATE.replace(/%displayField%/g, _this.displayTag));

    if (config) {
      _this.config = config;
    }

    return _this;
  }

  AgInputRange.prototype.postConstruct = function () {
    _super.prototype.postConstruct.call(this);

    var _a = this.config,
        min = _a.min,
        max = _a.max,
        step = _a.step;

    if (min != null) {
      this.setMinValue(min);
    }

    if (max != null) {
      this.setMaxValue(max);
    }

    this.setStep(step || 1);
  };

  AgInputRange.prototype.addInputListeners = function () {
    var _this = this;

    var isIE = _.isBrowserIE();

    var eventName = isIE ? 'change' : 'input';
    this.addDestroyableEventListener(this.eInput, eventName, function (e) {
      var value = e.target.value;

      _this.setValue(value);
    });
  };

  AgInputRange.prototype.setMinValue = function (value) {
    this.min = value;
    this.eInput.setAttribute('min', value.toString());
    return this;
  };

  AgInputRange.prototype.setMaxValue = function (value) {
    this.max = value;
    this.eInput.setAttribute('max', value.toString());
    return this;
  };

  AgInputRange.prototype.setStep = function (value) {
    this.step = value;
    this.eInput.setAttribute('step', value.toString());
    return this;
  };

  AgInputRange.prototype.setValue = function (value, silent) {
    if (this.min != null) {
      value = Math.max(parseFloat(value), this.min).toString();
    }

    if (this.max != null) {
      value = Math.min(parseFloat(value), this.max).toString();
    }

    var ret = _super.prototype.setValue.call(this, value, silent);

    this.eInput.value = value;
    return ret;
  };

  return AgInputRange;
}(AgAbstractInputField);
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __extends$15 = undefined && undefined.__extends || function () {
  var _extendStatics70 = function extendStatics(d, b) {
    _extendStatics70 = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics70(d, b);
  };

  return function (d, b) {
    _extendStatics70(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var AgSelect =
/** @class */
function (_super) {
  __extends$15(AgSelect, _super);

  function AgSelect() {
    var _this = _super.call(this) || this;

    _this.className = 'ag-select';
    _this.displayTag = 'select';
    _this.inputType = '';

    _this.setTemplate(_this.TEMPLATE.replace(/%displayField%/g, _this.displayTag));

    return _this;
  }

  AgSelect.prototype.addOptions = function (options) {
    var _this = this;

    options.forEach(function (option) {
      return _this.addOption(option);
    });
    return this;
  };

  AgSelect.prototype.addOption = function (option) {
    var optionEl = document.createElement('option');
    optionEl.value = option.value;
    optionEl.text = option.text || option.value;
    this.eInput.appendChild(optionEl);
    return this;
  };

  AgSelect.prototype.setValue = function (value, silent) {
    var ret = _super.prototype.setValue.call(this, value, silent);

    this.eInput.value = value;
    return ret;
  };

  return AgSelect;
}(AgAbstractInputField);
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __extends$16 = undefined && undefined.__extends || function () {
  var _extendStatics71 = function extendStatics(d, b) {
    _extendStatics71 = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics71(d, b);
  };

  return function (d, b) {
    _extendStatics71(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var __decorate$1C = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var AgAngleSelect =
/** @class */
function (_super) {
  __extends$16(AgAngleSelect, _super);

  function AgAngleSelect() {
    var _this = _super.call(this, AgAngleSelect.TEMPLATE) || this;

    _this.radius = 0;
    _this.offsetX = 0;
    _this.offsetY = 0;
    return _this;
  }

  AgAngleSelect.prototype.postConstruct = function () {
    var _this = this;

    _super.prototype.postConstruct.call(this);

    this.dragListener = {
      eElement: this.eParentCircle,
      dragStartPixels: 0,
      onDragStart: function onDragStart(e) {
        _this.parentCircleRect = _this.eParentCircle.getBoundingClientRect();
      },
      onDragging: function onDragging(e) {
        return _this.calculateAngleDrag(e);
      },
      onDragStop: function onDragStop() {}
    };
    this.dragService.addDragSource(this.dragListener);
    this.eAngleValue.setLabel('').setLabelWidth(5).setInputWidth(45).setMin(0).setMax(360).setValue("" + this.degrees).onValueChange(function (value) {
      if (value == null || value === '') {
        value = '0';
      }

      value = _this.eAngleValue.normalizeValue(value);
      var floatValue = parseFloat(value);

      if (floatValue > 180) {
        floatValue = floatValue - 360;
      }

      _this.setValue(floatValue);
    });
    this.updateNumberInput();

    if (_.exists(this.getValue())) {
      this.eAngleValue.setValue(this.normalizeNegativeValue(this.getValue()).toString());
    }

    this.addDestroyableEventListener(this, AgAbstractField.EVENT_CHANGED, function () {
      if (_this.eAngleValue.getInputElement().contains(document.activeElement)) {
        return;
      }

      _this.updateNumberInput();
    });
  };

  AgAngleSelect.prototype.updateNumberInput = function () {
    var normalizedValue = this.normalizeNegativeValue(this.getValue());
    this.eAngleValue.setValue(normalizedValue.toString());
  };

  AgAngleSelect.prototype.positionChildCircle = function (radians) {
    var rect = this.parentCircleRect || {
      width: 24,
      height: 24
    };
    var eChildCircle = this.eChildCircle;
    var centerX = rect.width / 2;
    var centerY = rect.height / 2;
    eChildCircle.style.left = centerX + Math.cos(radians) * 8 + "px";
    eChildCircle.style.top = centerY + Math.sin(radians) * 8 + "px";
  };

  AgAngleSelect.prototype.calculatePolar = function () {
    var x = this.offsetX;
    var y = this.offsetY;
    var radians = Math.atan2(y, x);
    this.degrees = this.toDegrees(radians);
    this.radius = Math.sqrt(x * x + y * y);
    this.positionChildCircle(radians);
  };

  AgAngleSelect.prototype.calculateCartesian = function () {
    var radians = this.toRadians(this.getValue());
    var radius = this.getRadius();
    this.setOffsetX(Math.cos(radians) * radius).setOffsetY(Math.sin(radians) * radius);
  };

  AgAngleSelect.prototype.setOffsetX = function (offset) {
    if (this.offsetX !== offset) {
      this.offsetX = offset;
      this.calculatePolar();
    }

    return this;
  };

  AgAngleSelect.prototype.setOffsetY = function (offset) {
    if (this.offsetY !== offset) {
      this.offsetY = offset;
      this.calculatePolar();
    }

    return this;
  };

  AgAngleSelect.prototype.calculateAngleDrag = function (e) {
    var rect = this.parentCircleRect;
    var centerX = rect.width / 2;
    var centerY = rect.height / 2;
    var x = e.clientX - rect.left;
    var y = e.clientY - rect.top;
    var dx = x - centerX;
    var dy = y - centerY;
    var radians = Math.atan2(dy, dx);
    this.setValue(radians, true);
  };

  AgAngleSelect.prototype.toDegrees = function (radians) {
    return radians / Math.PI * 180;
  };

  AgAngleSelect.prototype.toRadians = function (degrees) {
    return degrees / 180 * Math.PI;
  };

  AgAngleSelect.prototype.normalizeNegativeValue = function (degrees) {
    return degrees < 0 ? 360 + degrees : degrees;
  };

  AgAngleSelect.prototype.normalizeAngle180 = function (radians) {
    radians %= Math.PI * 2;

    if (radians < -Math.PI) {
      radians += Math.PI * 2;
    } else if (radians >= Math.PI) {
      radians -= Math.PI * 2;
    }

    return radians;
  };

  AgAngleSelect.prototype.getRadius = function () {
    return this.radius;
  };

  AgAngleSelect.prototype.setRadius = function (r) {
    if (this.radius === r) {
      return this;
    }

    this.radius = r;
    this.calculateCartesian();
    return this;
  };

  AgAngleSelect.prototype.onValueChange = function (callbackFn) {
    var _this = this;

    this.addDestroyableEventListener(this, AgAbstractField.EVENT_CHANGED, function () {
      callbackFn(_this.degrees);
    });
    return this;
  };

  AgAngleSelect.prototype.getValue = function (radians) {
    return radians ? this.toRadians(this.degrees) : this.degrees;
  };

  AgAngleSelect.prototype.setValue = function (degrees, radians) {
    var radiansValue;

    if (!radians) {
      radiansValue = this.normalizeAngle180(this.toRadians(degrees));
    } else {
      radiansValue = degrees;
    }

    degrees = this.toDegrees(radiansValue);

    if (this.degrees !== degrees) {
      this.degrees = Math.floor(degrees);
      this.calculateCartesian();
      this.positionChildCircle(radiansValue);
      this.dispatchEvent({
        type: AgAbstractField.EVENT_CHANGED
      });
    }

    return this;
  };

  AgAngleSelect.prototype.setWidth = function (width) {
    _.setFixedWidth(this.getGui(), width);

    return this;
  };

  AgAngleSelect.prototype.destroy = function () {
    _super.prototype.destroy.call(this);

    this.dragService.removeDragSource(this.dragListener);
  };

  AgAngleSelect.TEMPLATE = "<div class=\"ag-angle-select\">\n            <label ref=\"eLabel\"></label>\n            <div class=\"ag-wrapper\">\n                <div ref=\"eAngleSelectField\" class=\"ag-angle-select-field\">\n                    <div ref=\"eParentCircle\" class=\"ag-parent-circle\">\n                        <div ref=\"eChildCircle\" class=\"ag-child-circle\"></div>\n                    </div>\n                </div>\n                <ag-input-number-field ref=\"eAngleValue\"></ag-input-number-field>\n            </div>\n        </div>";

  __decorate$1C([RefSelector('eLabel')], AgAngleSelect.prototype, "eLabel", void 0);

  __decorate$1C([RefSelector('eParentCircle')], AgAngleSelect.prototype, "eParentCircle", void 0);

  __decorate$1C([RefSelector('eChildCircle')], AgAngleSelect.prototype, "eChildCircle", void 0);

  __decorate$1C([RefSelector('eAngleValue')], AgAngleSelect.prototype, "eAngleValue", void 0);

  __decorate$1C([Autowired("dragService")], AgAngleSelect.prototype, "dragService", void 0);

  return AgAngleSelect;
}(AgAbstractLabel);
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __extends$17 = undefined && undefined.__extends || function () {
  var _extendStatics72 = function extendStatics(d, b) {
    _extendStatics72 = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics72(d, b);
  };

  return function (d, b) {
    _extendStatics72(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var AgToggleButton =
/** @class */
function (_super) {
  __extends$17(AgToggleButton, _super);

  function AgToggleButton() {
    var _this = _super !== null && _super.apply(this, arguments) || this;

    _this.className = 'ag-toggle-button';
    _this.nativeInputClassName = 'ag-native-toggle-button';
    _this.inputType = 'checkbox';
    return _this;
  }

  AgToggleButton.prototype.postConstruct = function () {
    _super.prototype.postConstruct.call(this);

    if (!this.gridOptionsWrapper.useNativeCheckboxes()) {
      _.addCssClass(this.eIconEl, 'ag-icon');
    }
  };

  AgToggleButton.prototype.updateIcons = function () {
    if (!this.gridOptionsWrapper.useNativeCheckboxes()) {
      var value = this.getValue();

      _.addOrRemoveCssClass(this.eIconEl, 'ag-icon-toggle-on', value);

      _.addOrRemoveCssClass(this.eIconEl, 'ag-icon-toggle-off', !value);
    }
  };

  AgToggleButton.prototype.setValue = function (value, silent) {
    _super.prototype.setValue.call(this, value, silent);

    _.addOrRemoveCssClass(this.getGui(), 'ag-selected', this.getValue());

    return this;
  };

  return AgToggleButton;
}(AgRadioButton);
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __decorate$1D = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};
/**
 * For Master Detail, it is required to keep components between expanding & collapsing parents.
 * For example a user expands row A (and shows a detail grid for this row), then when row A
 * is closed, we want to keep the detail grid, so next time row A is expanded the detail grid
 * is showed with it's context intact, eg if user sorted in the detail grid, that sort should
 * still be applied after the detail grid is shown for the second time.
 */


var DetailRowCompCache =
/** @class */
function () {
  function DetailRowCompCache() {
    this.cacheItems = [];
  }

  DetailRowCompCache.prototype.postConstruct = function () {
    this.active = this.gridOptionsWrapper.isKeepDetailRows();
    this.maxCacheSize = this.gridOptionsWrapper.getKeepDetailRowsCount();
  };

  DetailRowCompCache.prototype.addOrDestroy = function (rowNode, pinned, comp) {
    // only accept detail rows
    var doNotUseCache = !this.active || !rowNode.detail;

    if (doNotUseCache) {
      this.destroyFullWidthRow(comp);
      return;
    }

    var item = this.getCacheItem(rowNode, true); // put the comp in the right location of the item.
    // we also destroy any previous comp - this should never happen
    // as the logic outside of this class shouldn't be adding same item to the
    // cache twice, however we cater for it in case in future releases code
    // outside of this class is changed and this could happen.

    switch (pinned) {
      case Constants.PINNED_LEFT:
        this.destroyFullWidthRow(item.left);
        item.left = comp;
        break;

      case Constants.PINNED_RIGHT:
        this.destroyFullWidthRow(item.right);
        item.right = comp;
        break;

      default:
        this.destroyFullWidthRow(item.center);
        item.center = comp;
        break;
    }

    this.cacheItems.sort(function (a, b) {
      return b.lastAccessedTime - a.lastAccessedTime;
    });
    this.purgeCache(this.maxCacheSize);
  };

  DetailRowCompCache.prototype.getCacheItem = function (rowNode, autoCreate) {
    if (autoCreate === void 0) {
      autoCreate = false;
    }

    var res;

    for (var i = 0; i < this.cacheItems.length; i++) {
      var item = this.cacheItems[i];

      if (item.rowNode === rowNode) {
        res = item;
        break;
      }
    }

    if (!res && autoCreate) {
      res = {
        rowNode: rowNode
      };
      this.cacheItems.push(res);
    }

    if (res) {
      this.stampCacheItem(res);
    }

    return res;
  };

  DetailRowCompCache.prototype.stampCacheItem = function (item) {
    item.lastAccessedTime = new Date().getTime();
  };

  DetailRowCompCache.prototype.destroyFullWidthRow = function (comp) {
    if (comp && comp.destroy) {
      comp.destroy();
    }
  };

  DetailRowCompCache.prototype.purgeCache = function (startIndex) {
    // delete all rows past the index of interest
    for (var i = startIndex; i < this.cacheItems.length; i++) {
      var item = this.cacheItems[i];
      this.destroyFullWidthRow(item.center);
      this.destroyFullWidthRow(item.left);
      this.destroyFullWidthRow(item.right);
    } // change the length of the array so it no longer contains the deleted items


    if (this.cacheItems.length > startIndex) {
      this.cacheItems.length = startIndex;
    }
  };

  DetailRowCompCache.prototype.get = function (rowNode, pinned) {
    if (!rowNode.detail) {
      return undefined;
    }

    var item = this.getCacheItem(rowNode);
    var res;

    if (item) {
      switch (pinned) {
        case Constants.PINNED_LEFT:
          if (item.left) {
            res = item.left;
            item.left = undefined;
          }

          break;

        case Constants.PINNED_RIGHT:
          if (item.right) {
            res = item.right;
            item.right = undefined;
          }

          break;

        default:
          if (item.center) {
            res = item.center;
            item.center = undefined;
          }

          break;
      }
    }

    return res;
  };

  DetailRowCompCache.prototype.destroy = function () {
    this.purgeCache(0);
  };

  __decorate$1D([Autowired('gridOptionsWrapper')], DetailRowCompCache.prototype, "gridOptionsWrapper", void 0);

  __decorate$1D([PostConstruct], DetailRowCompCache.prototype, "postConstruct", null);

  __decorate$1D([PreDestroy], DetailRowCompCache.prototype, "destroy", null);

  DetailRowCompCache = __decorate$1D([Bean('detailRowCompCache')], DetailRowCompCache);
  return DetailRowCompCache;
}();
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __decorate$1E = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var RowPositionUtils =
/** @class */
function () {
  function RowPositionUtils() {}

  RowPositionUtils.prototype.getRowNode = function (gridRow) {
    switch (gridRow.rowPinned) {
      case Constants.PINNED_TOP:
        return this.pinnedRowModel.getPinnedTopRowData()[gridRow.rowIndex];

      case Constants.PINNED_BOTTOM:
        return this.pinnedRowModel.getPinnedBottomRowData()[gridRow.rowIndex];

      default:
        return this.rowModel.getRow(gridRow.rowIndex);
    }
  };

  RowPositionUtils.prototype.sameRow = function (rowA, rowB) {
    // if both missing
    if (!rowA && !rowB) {
      return true;
    } // if only one missing


    if (rowA && !rowB || !rowA && rowB) {
      return false;
    } // otherwise compare (use == to compare rowPinned because it can be null or undefined)


    return rowA.rowIndex === rowB.rowIndex && rowA.rowPinned == rowB.rowPinned;
  }; // tests if this row selection is before the other row selection


  RowPositionUtils.prototype.before = function (rowA, rowB) {
    switch (rowA.rowPinned) {
      case Constants.PINNED_TOP:
        // we we are floating top, and other isn't, then we are always before
        if (rowB.rowPinned !== Constants.PINNED_TOP) {
          return true;
        }

        break;

      case Constants.PINNED_BOTTOM:
        // if we are floating bottom, and the other isn't, then we are never before
        if (rowB.rowPinned !== Constants.PINNED_BOTTOM) {
          return false;
        }

        break;

      default:
        // if we are not floating, but the other one is floating...
        if (_.exists(rowB.rowPinned)) {
          return rowB.rowPinned !== Constants.PINNED_TOP;
        }

        break;
    }

    return rowA.rowIndex < rowB.rowIndex;
  };

  __decorate$1E([Autowired('rowModel')], RowPositionUtils.prototype, "rowModel", void 0);

  __decorate$1E([Autowired('pinnedRowModel')], RowPositionUtils.prototype, "pinnedRowModel", void 0);

  RowPositionUtils = __decorate$1E([Bean('rowPositionUtils')], RowPositionUtils);
  return RowPositionUtils;
}();
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __decorate$1F = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var CellPositionUtils =
/** @class */
function () {
  function CellPositionUtils() {}

  CellPositionUtils.prototype.createId = function (cellPosition) {
    var rowIndex = cellPosition.rowIndex,
        rowPinned = cellPosition.rowPinned,
        column = cellPosition.column;
    return this.createIdFromValues(rowIndex, column, rowPinned);
  };

  CellPositionUtils.prototype.createIdFromValues = function (rowIndex, column, rowPinned) {
    return rowIndex + "." + (rowPinned == null ? 'null' : rowPinned) + "." + column.getId();
  };

  CellPositionUtils.prototype.equals = function (cellA, cellB) {
    var colsMatch = cellA.column === cellB.column;
    var floatingMatch = cellA.rowPinned === cellB.rowPinned;
    var indexMatch = cellA.rowIndex === cellB.rowIndex;
    return colsMatch && floatingMatch && indexMatch;
  };

  CellPositionUtils = __decorate$1F([Bean('cellPositionUtils')], CellPositionUtils);
  return CellPositionUtils;
}();
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __decorate$1G = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var PinnedRowModel =
/** @class */
function () {
  function PinnedRowModel() {}

  PinnedRowModel.prototype.init = function () {
    this.setPinnedTopRowData(this.gridOptionsWrapper.getPinnedTopRowData());
    this.setPinnedBottomRowData(this.gridOptionsWrapper.getPinnedBottomRowData());
  };

  PinnedRowModel.prototype.isEmpty = function (floating) {
    var rows = floating === Constants.PINNED_TOP ? this.pinnedTopRows : this.pinnedBottomRows;
    return _.missingOrEmpty(rows);
  };

  PinnedRowModel.prototype.isRowsToRender = function (floating) {
    return !this.isEmpty(floating);
  };

  PinnedRowModel.prototype.getRowAtPixel = function (pixel, floating) {
    var rows = floating === Constants.PINNED_TOP ? this.pinnedTopRows : this.pinnedBottomRows;

    if (_.missingOrEmpty(rows)) {
      return 0; // this should never happen, just in case, 0 is graceful failure
    }

    for (var i = 0; i < rows.length; i++) {
      var rowNode = rows[i];
      var rowTopPixel = rowNode.rowTop + rowNode.rowHeight - 1; // only need to range check against the top pixel, as we are going through the list
      // in order, first row to hit the pixel wins

      if (rowTopPixel >= pixel) {
        return i;
      }
    }

    return rows.length - 1;
  };

  PinnedRowModel.prototype.setPinnedTopRowData = function (rowData) {
    this.pinnedTopRows = this.createNodesFromData(rowData, true);
    var event = {
      type: Events.EVENT_PINNED_ROW_DATA_CHANGED,
      api: this.gridApi,
      columnApi: this.columnApi
    };
    this.eventService.dispatchEvent(event);
  };

  PinnedRowModel.prototype.setPinnedBottomRowData = function (rowData) {
    this.pinnedBottomRows = this.createNodesFromData(rowData, false);
    var event = {
      type: Events.EVENT_PINNED_ROW_DATA_CHANGED,
      api: this.gridApi,
      columnApi: this.columnApi
    };
    this.eventService.dispatchEvent(event);
  };

  PinnedRowModel.prototype.createNodesFromData = function (allData, isTop) {
    var _this = this;

    var rowNodes = [];

    if (allData) {
      var nextRowTop_1 = 0;
      allData.forEach(function (dataItem, index) {
        var rowNode = new RowNode();

        _this.context.wireBean(rowNode);

        rowNode.data = dataItem;
        rowNode.id = (isTop ? 't' : 'b') + "-" + index;
        rowNode.rowPinned = isTop ? Constants.PINNED_TOP : Constants.PINNED_BOTTOM;
        rowNode.setRowTop(nextRowTop_1);
        rowNode.setRowHeight(_this.gridOptionsWrapper.getRowHeightForNode(rowNode).height);
        rowNode.setRowIndex(index);
        nextRowTop_1 += rowNode.rowHeight;
        rowNodes.push(rowNode);
      });
    }

    return rowNodes;
  };

  PinnedRowModel.prototype.getPinnedTopRowData = function () {
    return this.pinnedTopRows;
  };

  PinnedRowModel.prototype.getPinnedBottomRowData = function () {
    return this.pinnedBottomRows;
  };

  PinnedRowModel.prototype.getPinnedTopTotalHeight = function () {
    return this.getTotalHeight(this.pinnedTopRows);
  };

  PinnedRowModel.prototype.getPinnedTopRowCount = function () {
    return this.pinnedTopRows ? this.pinnedTopRows.length : 0;
  };

  PinnedRowModel.prototype.getPinnedBottomRowCount = function () {
    return this.pinnedBottomRows ? this.pinnedBottomRows.length : 0;
  };

  PinnedRowModel.prototype.getPinnedTopRow = function (index) {
    return this.pinnedTopRows[index];
  };

  PinnedRowModel.prototype.getPinnedBottomRow = function (index) {
    return this.pinnedBottomRows[index];
  };

  PinnedRowModel.prototype.forEachPinnedTopRow = function (callback) {
    if (_.missingOrEmpty(this.pinnedTopRows)) {
      return;
    }

    this.pinnedTopRows.forEach(callback);
  };

  PinnedRowModel.prototype.forEachPinnedBottomRow = function (callback) {
    if (_.missingOrEmpty(this.pinnedBottomRows)) {
      return;
    }

    this.pinnedBottomRows.forEach(callback);
  };

  PinnedRowModel.prototype.getPinnedBottomTotalHeight = function () {
    return this.getTotalHeight(this.pinnedBottomRows);
  };

  PinnedRowModel.prototype.getTotalHeight = function (rowNodes) {
    if (!rowNodes || rowNodes.length === 0) {
      return 0;
    } else {
      var lastNode = _.last(rowNodes);

      return lastNode.rowTop + lastNode.rowHeight;
    }
  };

  __decorate$1G([Autowired('gridOptionsWrapper')], PinnedRowModel.prototype, "gridOptionsWrapper", void 0);

  __decorate$1G([Autowired('eventService')], PinnedRowModel.prototype, "eventService", void 0);

  __decorate$1G([Autowired('context')], PinnedRowModel.prototype, "context", void 0);

  __decorate$1G([Autowired('columnApi')], PinnedRowModel.prototype, "columnApi", void 0);

  __decorate$1G([Autowired('gridApi')], PinnedRowModel.prototype, "gridApi", void 0);

  __decorate$1G([PostConstruct], PinnedRowModel.prototype, "init", null);

  PinnedRowModel = __decorate$1G([Bean('pinnedRowModel')], PinnedRowModel);
  return PinnedRowModel;
}();
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __extends$18 = undefined && undefined.__extends || function () {
  var _extendStatics73 = function extendStatics(d, b) {
    _extendStatics73 = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics73(d, b);
  };

  return function (d, b) {
    _extendStatics73(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var UndoRedoAction =
/** @class */
function () {
  function UndoRedoAction(cellValueChanges) {
    this.cellValueChanges = cellValueChanges;
  }

  return UndoRedoAction;
}();

var FillUndoRedoAction =
/** @class */
function (_super) {
  __extends$18(FillUndoRedoAction, _super);

  function FillUndoRedoAction(cellValueChanges, initialRange, finalRange) {
    var _this = _super.call(this, cellValueChanges) || this;

    _this.initialRange = initialRange;
    _this.finalRange = finalRange;
    return _this;
  }

  return FillUndoRedoAction;
}(UndoRedoAction);

var UndoRedoStack =
/** @class */
function () {
  function UndoRedoStack(maxStackSize) {
    this.actionStack = [];
    this.maxStackSize = maxStackSize ? maxStackSize : UndoRedoStack.DEFAULT_STACK_SIZE;
    this.actionStack = new Array(this.maxStackSize);
  }

  UndoRedoStack.prototype.pop = function () {
    return this.actionStack.pop();
  };

  UndoRedoStack.prototype.push = function (item) {
    var shouldAddActions = item.cellValueChanges && item.cellValueChanges.length > 0;

    if (!shouldAddActions) {
      return;
    }

    if (this.actionStack.length === this.maxStackSize) {
      this.actionStack.shift();
    }

    this.actionStack.push(item);
  };

  UndoRedoStack.prototype.clear = function () {
    this.actionStack = [];
  };

  UndoRedoStack.DEFAULT_STACK_SIZE = 10;
  return UndoRedoStack;
}();
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __decorate$1H = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var UndoRedoService =
/** @class */
function () {
  function UndoRedoService() {
    var _this = this;

    this.cellValueChanges = [];
    this.isCellEditing = false;
    this.isRowEditing = false;
    this.isPasting = false;
    this.isFilling = false;

    this.onCellValueChanged = function (event) {
      var shouldCaptureAction = _this.isCellEditing || _this.isRowEditing || _this.isPasting || _this.isFilling;

      if (!shouldCaptureAction) {
        return;
      }

      var rowPinned = event.rowPinned,
          rowIndex = event.rowIndex,
          column = event.column,
          oldValue = event.oldValue,
          value = event.value;
      var cellValueChange = {
        rowPinned: rowPinned,
        rowIndex: rowIndex,
        columnId: column.getColId(),
        oldValue: oldValue,
        newValue: value
      };

      _this.cellValueChanges.push(cellValueChange);
    };

    this.clearStacks = function () {
      _this.undoStack.clear();

      _this.redoStack.clear();
    };
  }

  UndoRedoService.prototype.init = function () {
    if (!this.gridOptionsWrapper.isUndoRedoCellEditing()) {
      return;
    }

    var undoRedoLimit = this.gridOptionsWrapper.getUndoRedoCellEditingLimit();

    if (undoRedoLimit <= 0) {
      return;
    }

    this.undoStack = new UndoRedoStack(undoRedoLimit);
    this.redoStack = new UndoRedoStack(undoRedoLimit);
    this.addRowEditingListeners();
    this.addCellEditingListeners();
    this.addPasteListeners();
    this.addFillListeners();
    this.eventService.addEventListener(Events.EVENT_CELL_VALUE_CHANGED, this.onCellValueChanged); // undo / redo is restricted to actual editing so we clear the stacks when other operations are
    // performed that change the order of the row / cols.

    this.eventService.addEventListener(Events.EVENT_MODEL_UPDATED, this.clearStacks);
    this.eventService.addEventListener(Events.EVENT_COLUMN_PIVOT_MODE_CHANGED, this.clearStacks);
    this.eventService.addEventListener(Events.EVENT_COLUMN_EVERYTHING_CHANGED, this.clearStacks);
    this.eventService.addEventListener(Events.EVENT_COLUMN_GROUP_OPENED, this.clearStacks);
    this.eventService.addEventListener(Events.EVENT_COLUMN_ROW_GROUP_CHANGED, this.clearStacks);
    this.eventService.addEventListener(Events.EVENT_COLUMN_MOVED, this.clearStacks);
    this.eventService.addEventListener(Events.EVENT_COLUMN_PINNED, this.clearStacks);
    this.eventService.addEventListener(Events.EVENT_COLUMN_VISIBLE, this.clearStacks);
    this.eventService.addEventListener(Events.EVENT_ROW_DRAG_END, this.clearStacks);
  };

  UndoRedoService.prototype.undo = function () {
    if (!this.undoStack) {
      return;
    }

    var undoAction = this.undoStack.pop();

    if (!undoAction || !undoAction.cellValueChanges) {
      return;
    }

    this.processAction(undoAction, function (cellValueChange) {
      return cellValueChange.oldValue;
    });

    if (undoAction instanceof FillUndoRedoAction) {
      this.processRangeAndCellFocus(undoAction.cellValueChanges, undoAction.initialRange);
    } else {
      this.processRangeAndCellFocus(undoAction.cellValueChanges);
    }

    this.redoStack.push(undoAction);
  };

  UndoRedoService.prototype.redo = function () {
    if (!this.redoStack) {
      return;
    }

    var redoAction = this.redoStack.pop();

    if (!redoAction || !redoAction.cellValueChanges) {
      return;
    }

    this.processAction(redoAction, function (cellValueChange) {
      return cellValueChange.newValue;
    });

    if (redoAction instanceof FillUndoRedoAction) {
      this.processRangeAndCellFocus(redoAction.cellValueChanges, redoAction.finalRange);
    } else {
      this.processRangeAndCellFocus(redoAction.cellValueChanges);
    }

    this.undoStack.push(redoAction);
  };

  UndoRedoService.prototype.processAction = function (action, valueExtractor) {
    var _this = this;

    action.cellValueChanges.forEach(function (cellValueChange) {
      var rowIndex = cellValueChange.rowIndex,
          rowPinned = cellValueChange.rowPinned,
          columnId = cellValueChange.columnId;
      var rowPosition = {
        rowIndex: rowIndex,
        rowPinned: rowPinned
      };

      var currentRow = _this.getRowNode(rowPosition); // checks if the row has been filtered out


      if (currentRow.rowTop == null) {
        return;
      }

      currentRow.setDataValue(columnId, valueExtractor(cellValueChange));
    });
  };

  UndoRedoService.prototype.processRangeAndCellFocus = function (cellValueChanges, range) {
    if (range) {
      var startRow = range.startRow;
      var endRow = range.endRow;
      var lastFocusedCell_1 = {
        rowPinned: startRow.rowPinned,
        rowIndex: startRow.rowIndex,
        columnId: range.startColumn.getColId()
      };
      this.setLastFocusedCell(lastFocusedCell_1);
      var cellRangeParams = {
        rowStartIndex: startRow.rowIndex,
        rowStartPinned: startRow.rowPinned,
        rowEndIndex: endRow.rowIndex,
        rowEndPinned: endRow.rowPinned,
        columnStart: range.startColumn,
        columns: range.columns
      };
      this.gridApi.addCellRange(cellRangeParams);
      return;
    }

    var cellValueChange = cellValueChanges[0];
    var rowIndex = cellValueChange.rowIndex,
        rowPinned = cellValueChange.rowPinned;
    var rowPosition = {
      rowIndex: rowIndex,
      rowPinned: rowPinned
    };
    var row = this.getRowNode(rowPosition);
    var lastFocusedCell = {
      rowPinned: cellValueChange.rowPinned,
      rowIndex: row.rowIndex,
      columnId: cellValueChange.columnId
    };
    this.setLastFocusedCell(lastFocusedCell);
  };

  UndoRedoService.prototype.setLastFocusedCell = function (lastFocusedCell) {
    var rowIndex = lastFocusedCell.rowIndex,
        columnId = lastFocusedCell.columnId,
        rowPinned = lastFocusedCell.rowPinned;
    this.gridApi.ensureIndexVisible(rowIndex);
    this.gridApi.ensureColumnVisible(columnId);

    if (ModuleRegistry.isRegistered(exports.ModuleNames.RangeSelectionModule)) {
      this.gridApi.clearRangeSelection();
    }

    this.focusedCellController.setFocusedCell(rowIndex, columnId, rowPinned, true);
  };

  UndoRedoService.prototype.addRowEditingListeners = function () {
    var _this = this;

    this.eventService.addEventListener(Events.EVENT_ROW_EDITING_STARTED, function () {
      _this.isRowEditing = true;
    });
    this.eventService.addEventListener(Events.EVENT_ROW_EDITING_STOPPED, function () {
      var action = new UndoRedoAction(_this.cellValueChanges);

      _this.pushActionsToUndoStack(action);

      _this.isRowEditing = false;
    });
  };

  UndoRedoService.prototype.addCellEditingListeners = function () {
    var _this = this;

    this.eventService.addEventListener(Events.EVENT_CELL_EDITING_STARTED, function () {
      _this.isCellEditing = true;
    });
    this.eventService.addEventListener(Events.EVENT_CELL_EDITING_STOPPED, function () {
      _this.isCellEditing = false;
      var shouldPushAction = !_this.isRowEditing && !_this.isPasting && !_this.isFilling;

      if (shouldPushAction) {
        var action = new UndoRedoAction(_this.cellValueChanges);

        _this.pushActionsToUndoStack(action);
      }
    });
  };

  UndoRedoService.prototype.addPasteListeners = function () {
    var _this = this;

    this.eventService.addEventListener(Events.EVENT_PASTE_START, function () {
      _this.isPasting = true;
    });
    this.eventService.addEventListener(Events.EVENT_PASTE_END, function () {
      var action = new UndoRedoAction(_this.cellValueChanges);

      _this.pushActionsToUndoStack(action);

      _this.isPasting = false;
    });
  };

  UndoRedoService.prototype.addFillListeners = function () {
    var _this = this;

    this.eventService.addEventListener(Events.EVENT_FILL_START, function () {
      _this.isFilling = true;
    });
    this.eventService.addEventListener(Events.EVENT_FILL_END, function (event) {
      var action = new FillUndoRedoAction(_this.cellValueChanges, event.initialRange, event.finalRange);

      _this.pushActionsToUndoStack(action);

      _this.isFilling = false;
    });
  };

  UndoRedoService.prototype.pushActionsToUndoStack = function (action) {
    this.undoStack.push(action);
    this.cellValueChanges = [];
    this.redoStack.clear();
  };

  UndoRedoService.prototype.getRowNode = function (gridRow) {
    switch (gridRow.rowPinned) {
      case Constants.PINNED_TOP:
        return this.pinnedRowModel.getPinnedTopRowData()[gridRow.rowIndex];

      case Constants.PINNED_BOTTOM:
        return this.pinnedRowModel.getPinnedBottomRowData()[gridRow.rowIndex];

      default:
        return this.rowModel.getRow(gridRow.rowIndex);
    }
  };

  __decorate$1H([Autowired('gridOptionsWrapper')], UndoRedoService.prototype, "gridOptionsWrapper", void 0);

  __decorate$1H([Autowired('focusedCellController')], UndoRedoService.prototype, "focusedCellController", void 0);

  __decorate$1H([Autowired('eventService')], UndoRedoService.prototype, "eventService", void 0);

  __decorate$1H([Autowired('gridApi')], UndoRedoService.prototype, "gridApi", void 0);

  __decorate$1H([Autowired('rowModel')], UndoRedoService.prototype, "rowModel", void 0);

  __decorate$1H([Autowired('pinnedRowModel')], UndoRedoService.prototype, "pinnedRowModel", void 0);

  __decorate$1H([PostConstruct], UndoRedoService.prototype, "init", null);

  UndoRedoService = __decorate$1H([Bean('undoRedoService')], UndoRedoService);
  return UndoRedoService;
}();
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var Grid =
/** @class */
function () {
  function Grid(eGridDiv, gridOptions, params) {
    if (!eGridDiv) {
      console.error('ag-Grid: no div element provided to the grid');
      return;
    }

    if (!gridOptions) {
      console.error('ag-Grid: no gridOptions provided to the grid');
      return;
    }

    var debug = !!gridOptions.debug;
    this.gridOptions = gridOptions;
    var registeredModules = this.getRegisteredModules(params);
    var beanClasses = this.createBeansList(registeredModules);
    var agStackComponents = this.createAgStackComponentsList(registeredModules);
    var providedBeanInstances = this.createProvidedBeans(eGridDiv, params);

    if (!beanClasses) {
      return;
    } // happens when no row model found


    var contextParams = {
      providedBeanInstances: providedBeanInstances,
      beanClasses: beanClasses,
      components: agStackComponents,
      debug: debug
    };
    this.logger = new Logger('ag-Grid', function () {
      return gridOptions.debug;
    });
    var contextLogger = new Logger('Context', function () {
      return contextParams.debug;
    });
    this.context = new Context(contextParams, contextLogger);
    this.registerModuleUserComponents(registeredModules);
    var gridCore = new GridCore();
    this.context.wireBean(gridCore);
    this.setColumnsAndData();
    this.dispatchGridReadyEvent(gridOptions);
    var isEnterprise = ModuleRegistry.isRegistered(exports.ModuleNames.EnterpriseCoreModule);
    this.logger.log("initialised successfully, enterprise = " + isEnterprise);
  }

  Grid.prototype.getRegisteredModules = function (params) {
    var passedViaConstructor = params ? params.modules : null;
    var registered = ModuleRegistry.getRegisteredModules();
    var allModules = [];
    var mapNames = {}; // adds to list and removes duplicates

    function addModule(module) {
      function addIndividualModule(module) {
        if (!mapNames[module.moduleName]) {
          mapNames[module.moduleName] = true;
          allModules.push(module);
          ModuleRegistry.register(module);
        }
      }

      addIndividualModule(module);

      if (module.dependantModules) {
        module.dependantModules.forEach(addModule);
      }
    }

    if (passedViaConstructor) {
      passedViaConstructor.forEach(addModule);
    }

    if (registered) {
      registered.forEach(addModule);
    }

    return allModules;
  };

  Grid.prototype.registerModuleUserComponents = function (registeredModules) {
    var userComponentRegistry = this.context.getBean('userComponentRegistry');
    var moduleUserComps = this.extractModuleEntity(registeredModules, function (module) {
      return module.userComponents ? module.userComponents : [];
    });
    moduleUserComps.forEach(function (compMeta) {
      userComponentRegistry.registerDefaultComponent(compMeta.componentName, compMeta.componentClass);
    });
  };

  Grid.prototype.createProvidedBeans = function (eGridDiv, params) {
    var frameworkOverrides = params ? params.frameworkOverrides : null;

    if (_.missing(frameworkOverrides)) {
      frameworkOverrides = new VanillaFrameworkOverrides();
    }

    var seed = {
      gridOptions: this.gridOptions,
      eGridDiv: eGridDiv,
      $scope: params ? params.$scope : null,
      $compile: params ? params.$compile : null,
      quickFilterOnScope: params ? params.quickFilterOnScope : null,
      globalEventListener: params ? params.globalEventListener : null,
      frameworkOverrides: frameworkOverrides
    };

    if (params && params.providedBeanInstances) {
      _.assign(seed, params.providedBeanInstances);
    }

    return seed;
  };

  Grid.prototype.createAgStackComponentsList = function (registeredModules) {
    var components = [{
      componentName: 'AgCheckbox',
      componentClass: AgCheckbox
    }, {
      componentName: 'AgRadioButton',
      componentClass: AgRadioButton
    }, {
      componentName: 'AgToggleButton',
      componentClass: AgToggleButton
    }, {
      componentName: 'AgInputTextField',
      componentClass: AgInputTextField
    }, {
      componentName: 'AgInputTextArea',
      componentClass: AgInputTextArea
    }, {
      componentName: 'AgInputNumberField',
      componentClass: AgInputNumberField
    }, {
      componentName: 'AgInputRange',
      componentClass: AgInputRange
    }, {
      componentName: 'AgSelect',
      componentClass: AgSelect
    }, {
      componentName: 'AgSlider',
      componentClass: AgSlider
    }, {
      componentName: 'AgAngleSelect',
      componentClass: AgAngleSelect
    }, {
      componentName: 'AgColorPicker',
      componentClass: AgColorPicker
    }, {
      componentName: 'AgGridComp',
      componentClass: GridPanel
    }, {
      componentName: 'AgHeaderRoot',
      componentClass: HeaderRootComp
    }, {
      componentName: 'AgPagination',
      componentClass: PaginationComp
    }, {
      componentName: 'AgOverlayWrapper',
      componentClass: OverlayWrapperComponent
    }, {
      componentName: 'AgGroupComponent',
      componentClass: AgGroupComponent
    }, {
      componentName: 'AgPanel',
      componentClass: AgPanel
    }, {
      componentName: 'AgDialog',
      componentClass: AgDialog
    }];
    var moduleAgStackComps = this.extractModuleEntity(registeredModules, function (module) {
      return module.agStackComponents ? module.agStackComponents : [];
    });
    components = components.concat(moduleAgStackComps);
    return components;
  };

  Grid.prototype.createBeansList = function (registeredModules) {
    var rowModelClass = this.getRowModelClass(registeredModules);

    if (!rowModelClass) {
      return undefined;
    } // beans should only contain SERVICES, it should NEVER contain COMPONENTS


    var beans = [rowModelClass, Beans, RowPositionUtils, CellPositionUtils, PaginationAutoPageSizeService, GridApi, UserComponentRegistry, AgComponentUtils, ComponentMetadataProvider, ResizeObserverService, UserComponentFactory, MaxDivHeightScaler, AutoHeightCalculator, CellRendererFactory, HorizontalResizeService, PinnedRowModel, DragService, DisplayedGroupCreator, EventService, GridOptionsWrapper, PopupService, SelectionController, FilterManager, ColumnController, PaginationProxy, RowRenderer, ExpressionService, ColumnFactory, TemplateService, AlignedGridsService, NavigationService, ValueCache, ValueService, LoggerFactory, ColumnUtils, AutoWidthCalculator, StandardMenuFactory, DragAndDropService, ColumnApi, FocusedCellController, MouseEventService, Environment, CellNavigationService, ValueFormatterService, StylingService, ScrollVisibleService, SortController, ColumnHoverService, ColumnAnimationService, SelectableService, AutoGroupColService, ChangeDetectionService, AnimationFrameService, TooltipManager, DetailRowCompCache, UndoRedoService];
    var moduleBeans = this.extractModuleEntity(registeredModules, function (module) {
      return module.beans ? module.beans : [];
    });
    beans.push.apply(beans, moduleBeans); // check for duplicates, as different modules could include the same beans that
    // they depend on, eg ClientSideRowModel in enterprise, and ClientSideRowModel in community

    var beansNoDuplicates = [];
    beans.forEach(function (bean) {
      if (beansNoDuplicates.indexOf(bean) < 0) {
        beansNoDuplicates.push(bean);
      }
    });
    return beansNoDuplicates;
  };

  Grid.prototype.extractModuleEntity = function (moduleEntities, extractor) {
    return [].concat.apply([], moduleEntities.map(extractor));
  };

  Grid.prototype.setColumnsAndData = function () {
    var gridOptionsWrapper = this.context.getBean('gridOptionsWrapper');
    var columnController = this.context.getBean('columnController');
    var columnDefs = gridOptionsWrapper.getColumnDefs();

    if (_.exists(columnDefs)) {
      columnController.setColumnDefs(columnDefs, "gridInitializing");
    }

    var rowModel = this.context.getBean('rowModel');
    rowModel.start();
  };

  Grid.prototype.dispatchGridReadyEvent = function (gridOptions) {
    var eventService = this.context.getBean('eventService');
    var readyEvent = {
      type: Events.EVENT_GRID_READY,
      api: gridOptions.api,
      columnApi: gridOptions.columnApi
    };
    eventService.dispatchEvent(readyEvent);
  };

  Grid.prototype.getRowModelClass = function (registeredModules) {
    var rowModelType = this.gridOptions.rowModelType; //TODO: temporary measure before 'enterprise' is completely removed (similar handling in gridOptionsWrapper is also required)

    if (rowModelType === 'enterprise') {
      console.warn("ag-Grid: enterprise rowModel deprecated. Should now be called server side row model instead.");
      rowModelType = Constants.ROW_MODEL_TYPE_SERVER_SIDE;
    }

    if (rowModelType === 'normal') {
      console.warn("ag-Grid: normal rowModel deprecated. Should now be called client side row model instead.");
      rowModelType = Constants.ROW_MODEL_TYPE_CLIENT_SIDE;
    } // default to client side


    if (!rowModelType) {
      rowModelType = Constants.ROW_MODEL_TYPE_CLIENT_SIDE;
    }

    var rowModelClasses = {};
    registeredModules.forEach(function (module) {
      _.iterateObject(module.rowModels, function (key, value) {
        rowModelClasses[key] = value;
      });
    });
    var rowModelClass = rowModelClasses[rowModelType];

    if (_.exists(rowModelClass)) {
      return rowModelClass;
    } else {
      if (rowModelType === Constants.ROW_MODEL_TYPE_INFINITE) {
        console.error("ag-Grid: Row Model \"Infinite\" not found. Please ensure the " + "InfiniteRowModelModule is loaded using: import '@ag-grid-community/infinite-row-model';");
      }

      console.error('ag-Grid: could not find matching row model for rowModelType ' + rowModelType);

      if (rowModelType === Constants.ROW_MODEL_TYPE_VIEWPORT) {
        console.error("ag-Grid: Row Model \"Viewport\" not found. For this row model to work you must " + "a) be using ag-Grid Enterprise and " + "b) ensure ViewportRowModelModule is " + "loaded using: import '@ag-grid-enterprise/viewport-row-model;");
      }

      if (rowModelType === Constants.ROW_MODEL_TYPE_SERVER_SIDE) {
        console.error("ag-Grid: Row Model \"Server Side\" not found. For this row model to work you must " + "a) be using ag-Grid Enterprise and " + "b) ensure ServerSideRowModelModule is " + "loaded using: import '@ag-grid-enterprise/server-server-side-row-model';");
      }

      if (rowModelType === Constants.ROW_MODEL_TYPE_CLIENT_SIDE) {
        console.error("ag-Grid: Row Model \"Client Side\" not found. Please ensure the " + "ClientSideRowModelModule is loaded using: import '@ag-grid-community/client-side-row-model';");
      }

      return undefined;
    }
  };

  Grid.prototype.destroy = function () {
    this.gridOptions.api.destroy();
  };

  return Grid;
}();
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


function initialiseAgGridWithAngular1(angular) {
  var angularModule = angular.module("agGrid", []);
  angularModule.directive("agGrid", function () {
    return {
      restrict: "A",
      controller: ['$element', '$scope', '$compile', '$attrs', AngularDirectiveController],
      scope: true
    };
  });
}

function AngularDirectiveController($element, $scope, $compile, $attrs) {
  var gridOptions;
  var quickFilterOnScope;
  var keyOfGridInScope = $attrs.agGrid;
  quickFilterOnScope = keyOfGridInScope + '.quickFilterText';
  gridOptions = $scope.$eval(keyOfGridInScope);

  if (!gridOptions) {
    console.warn("WARNING - grid options for ag-Grid not found. Please ensure the attribute ag-grid points to a valid object on the scope");
    return;
  }

  var eGridDiv = $element[0];
  var gridParams = {
    $scope: $scope,
    $compile: $compile,
    quickFilterOnScope: quickFilterOnScope
  };
  var grid = new Grid(eGridDiv, gridOptions, gridParams);
  $scope.$on("$destroy", function () {
    grid.destroy();
    grid = null;
  });
}
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var registered = false;

function initialiseAgGridWithWebComponents() {
  console.warn('ag-grid: initialiseAgGridWithWebComponents is deprecated. Please use the ag-grid-webcomponent dependency instead. '); // only register to WebComponents once

  if (registered) {
    return;
  }

  registered = true;

  if (typeof document === 'undefined' || !document.registerElement) {
    console.error('ag-Grid: unable to find document.registerElement() function, unable to initialise ag-Grid as a Web Component');
  } // i don't think this type of extension is possible in TypeScript, so back to
  // plain Javascript to create this object


  var AgileGridProto = Object.create(HTMLElement.prototype); // wrap each property with a get and set method, so we can track when changes are done

  ComponentUtil.ALL_PROPERTIES.forEach(function (key) {
    Object.defineProperty(AgileGridProto, key, {
      set: function set(v) {
        this.__agGridSetProperty(key, v);
      },
      get: function get() {
        return this.__agGridGetProperty(key);
      },
      enumerable: true,
      configurable: true
    });
  });
  var agGridProtoNoType = AgileGridProto;

  agGridProtoNoType.__agGridSetProperty = function (key, value) {
    if (!this.__attributes) {
      this.__attributes = {};
    }

    this.__attributes[key] = value; // keeping this consistent with the ng2 onChange, so I can reuse the handling code

    var changeObject = {};
    changeObject[key] = {
      currentValue: value
    };
    this.onChange(changeObject);
  };

  agGridProtoNoType.onChange = function (changes) {
    if (this._initialised) {
      ComponentUtil.processOnChange(changes, this._gridOptions, this.api, this.columnApi);
    }
  };

  agGridProtoNoType.__agGridGetProperty = function (key) {
    if (!this.__attributes) {
      this.__attributes = {};
    }

    return this.__attributes[key];
  };

  agGridProtoNoType.setGridOptions = function (options) {
    var globalEventListener = this.globalEventListener.bind(this);
    this._gridOptions = ComponentUtil.copyAttributesToGridOptions(options, this);
    var gridParams = {
      globalEventListener: globalEventListener
    };
    this._agGrid = new Grid(this, this._gridOptions, gridParams);
    this.api = options.api;
    this.columnApi = options.columnApi;
    this._initialised = true;
  }; // copies all the attributes into this object


  agGridProtoNoType.createdCallback = function () {
    for (var i = 0; i < this.attributes.length; i++) {
      var attribute = this.attributes[i];
      this.setPropertyFromAttribute(attribute);
    }
  };

  agGridProtoNoType.setPropertyFromAttribute = function (attribute) {
    var name = toCamelCase(attribute.nodeName);
    var value = attribute.nodeValue;

    if (ComponentUtil.ALL_PROPERTIES.indexOf(name) >= 0) {
      this[name] = value;
    }
  };

  agGridProtoNoType.attachedCallback = function (params) {};

  agGridProtoNoType.detachedCallback = function (params) {};

  agGridProtoNoType.attributeChangedCallback = function (attributeName) {
    var attribute = this.attributes[attributeName];
    this.setPropertyFromAttribute(attribute);
  };

  agGridProtoNoType.globalEventListener = function (eventType, event) {
    var eventLowerCase = eventType.toLowerCase();
    var browserEvent = new Event(eventLowerCase);
    var browserEventNoType = browserEvent;
    browserEventNoType.agGridDetails = event;
    this.dispatchEvent(browserEvent);
    var callbackMethod = 'on' + eventLowerCase;

    if (typeof this[callbackMethod] === 'function') {
      this[callbackMethod](browserEvent);
    }
  }; // finally, register


  document.registerElement('ag-grid', {
    prototype: AgileGridProto
  });
}

function toCamelCase(myString) {
  if (typeof myString === 'string') {
    var result = myString.replace(/-([a-z])/g, function (g) {
      return g[1].toUpperCase();
    });
    return result;
  } else {
    return myString;
  }
}
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var TabbedLayout =
/** @class */
function () {
  function TabbedLayout(params) {
    var _this = this;

    this.items = [];
    this.params = params;
    this.eGui = document.createElement('div');
    this.eGui.innerHTML = TabbedLayout.TEMPLATE;
    this.eHeader = this.eGui.querySelector('[ref="tabHeader"]');
    this.eBody = this.eGui.querySelector('[ref="tabBody"]');

    _.addCssClass(this.eGui, params.cssClass);

    if (params.items) {
      params.items.forEach(function (item) {
        return _this.addItem(item);
      });
    }
  }

  TabbedLayout.prototype.setAfterAttachedParams = function (params) {
    this.afterAttachedParams = params;
  };

  TabbedLayout.prototype.getMinDimensions = function () {
    var eDummyContainer = this.eGui.cloneNode(true);
    var eDummyBody = eDummyContainer.querySelector('[ref="tabBody"]'); // position fixed, so it isn't restricted to the boundaries of the parent

    eDummyContainer.style.position = 'fixed'; // we put the dummy into the body container, so it will inherit all the
    // css styles that the real cells are inheriting

    this.eGui.appendChild(eDummyContainer);
    var minWidth = 0;
    var minHeight = 0;
    this.items.forEach(function (itemWrapper) {
      _.clearElement(eDummyBody);

      var eClone = itemWrapper.tabbedItem.bodyPromise.resolveNow(null, function (body) {
        return body.cloneNode(true);
      });

      if (eClone == null) {
        return;
      }

      eDummyBody.appendChild(eClone);

      if (minWidth < eDummyContainer.offsetWidth) {
        minWidth = eDummyContainer.offsetWidth;
      }

      if (minHeight < eDummyContainer.offsetHeight) {
        minHeight = eDummyContainer.offsetHeight;
      }
    }); // finally check the parent tabs are no wider, as if they
    // are, then these are the min width and not the child tabs
    // if (minWidth<this.eGui.offsetWidth) {
    //     minWidth = this.eGui.offsetWidth;
    // }

    this.eGui.removeChild(eDummyContainer);
    return {
      height: minHeight,
      width: minWidth
    };
  };

  TabbedLayout.prototype.showFirstItem = function () {
    if (this.items.length > 0) {
      this.showItemWrapper(this.items[0]);
    }
  };

  TabbedLayout.prototype.addItem = function (item) {
    var eHeaderButton = document.createElement('span');
    eHeaderButton.appendChild(item.title);

    _.addCssClass(eHeaderButton, 'ag-tab');

    this.eHeader.appendChild(eHeaderButton);
    var wrapper = {
      tabbedItem: item,
      eHeaderButton: eHeaderButton
    };
    this.items.push(wrapper);
    eHeaderButton.addEventListener('click', this.showItemWrapper.bind(this, wrapper));
  };

  TabbedLayout.prototype.showItem = function (tabbedItem) {
    var itemWrapper = _.find(this.items, function (wrapper) {
      return wrapper.tabbedItem === tabbedItem;
    });

    if (itemWrapper) {
      this.showItemWrapper(itemWrapper);
    }
  };

  TabbedLayout.prototype.showItemWrapper = function (wrapper) {
    var _this = this;

    if (this.params.onItemClicked) {
      this.params.onItemClicked({
        item: wrapper.tabbedItem
      });
    }

    if (this.activeItem === wrapper) {
      _.callIfPresent(this.params.onActiveItemClicked);

      return;
    }

    _.clearElement(this.eBody);

    wrapper.tabbedItem.bodyPromise.then(function (body) {
      _this.eBody.appendChild(body);
    });

    if (this.activeItem) {
      _.removeCssClass(this.activeItem.eHeaderButton, 'ag-tab-selected');
    }

    _.addCssClass(wrapper.eHeaderButton, 'ag-tab-selected');

    this.activeItem = wrapper;

    if (wrapper.tabbedItem.afterAttachedCallback) {
      wrapper.tabbedItem.afterAttachedCallback(this.afterAttachedParams);
    }
  };

  TabbedLayout.prototype.getGui = function () {
    return this.eGui;
  };

  TabbedLayout.TEMPLATE = '<div>' + '<div ref="tabHeader" class="ag-tab-header"></div>' + '<div ref="tabBody" class="ag-tab-body"></div>' + '</div>';
  return TabbedLayout;
}();
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


function simpleHttpRequest(params) {
  return new Promise(function (resolve) {
    var httpRequest = new XMLHttpRequest();
    httpRequest.open('GET', params.url);
    httpRequest.send();

    httpRequest.onreadystatechange = function () {
      if (httpRequest.readyState == 4 && httpRequest.status == 200) {
        var httpResponse = JSON.parse(httpRequest.responseText);
        resolve(httpResponse);
      }
    };
  });
}
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __extends$19 = undefined && undefined.__extends || function () {
  var _extendStatics74 = function extendStatics(d, b) {
    _extendStatics74 = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics74(d, b);
  };

  return function (d, b) {
    _extendStatics74(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var RowNodeBlock =
/** @class */
function (_super) {
  __extends$19(RowNodeBlock, _super);

  function RowNodeBlock(blockNumber, rowNodeCacheParams) {
    var _this = _super.call(this) || this;

    _this.version = 0;
    _this.state = RowNodeBlock.STATE_DIRTY;
    _this.rowNodeCacheParams = rowNodeCacheParams;
    _this.blockNumber = blockNumber; // we don't need to calculate these now, as the inputs don't change,
    // however it makes the code easier to read if we work them out up front

    _this.startRow = blockNumber * rowNodeCacheParams.blockSize;
    _this.endRow = _this.startRow + rowNodeCacheParams.blockSize;
    return _this;
  }

  RowNodeBlock.prototype.isAnyNodeOpen = function (rowCount) {
    var result = false;
    this.forEachNodeCallback(function (rowNode) {
      if (rowNode.expanded) {
        result = true;
      }
    }, rowCount);
    return result;
  };

  RowNodeBlock.prototype.forEachNodeCallback = function (callback, rowCount) {
    for (var rowIndex = this.startRow; rowIndex < this.endRow; rowIndex++) {
      // we check against rowCount as this page may be the last one, and if it is, then
      // the last rows are not part of the set
      if (rowIndex < rowCount) {
        var rowNode = this.getRowUsingLocalIndex(rowIndex);
        callback(rowNode, rowIndex);
      }
    }
  };

  RowNodeBlock.prototype.forEachNode = function (callback, sequence, rowCount, deep) {
    this.forEachNodeCallback(function (rowNode) {
      callback(rowNode, sequence.next()); // this will only every happen for server side row model, as infinite
      // row model doesn't have groups

      if (deep && rowNode.childrenCache) {
        rowNode.childrenCache.forEachNodeDeep(callback, sequence);
      }
    }, rowCount);
  };

  RowNodeBlock.prototype.forEachNodeDeep = function (callback, sequence, rowCount) {
    this.forEachNode(callback, sequence, rowCount, true);
  };

  RowNodeBlock.prototype.forEachNodeShallow = function (callback, sequence, rowCount) {
    this.forEachNode(callback, sequence, rowCount, false);
  };

  RowNodeBlock.prototype.getVersion = function () {
    return this.version;
  };

  RowNodeBlock.prototype.getLastAccessed = function () {
    return this.lastAccessed;
  };

  RowNodeBlock.prototype.getRowUsingLocalIndex = function (rowIndex, dontTouchLastAccessed) {
    if (dontTouchLastAccessed === void 0) {
      dontTouchLastAccessed = false;
    }

    if (!dontTouchLastAccessed) {
      this.lastAccessed = this.rowNodeCacheParams.lastAccessedSequence.next();
    }

    var localIndex = rowIndex - this.startRow;
    return this.rowNodes[localIndex];
  };

  RowNodeBlock.prototype.init = function (beans) {
    this.beans = beans;
    this.createRowNodes();
  };

  RowNodeBlock.prototype.getStartRow = function () {
    return this.startRow;
  };

  RowNodeBlock.prototype.getEndRow = function () {
    return this.endRow;
  };

  RowNodeBlock.prototype.getBlockNumber = function () {
    return this.blockNumber;
  };

  RowNodeBlock.prototype.setDirty = function () {
    // in case any current loads in progress, this will have their results ignored
    this.version++;
    this.state = RowNodeBlock.STATE_DIRTY;
  };

  RowNodeBlock.prototype.setDirtyAndPurge = function () {
    this.setDirty();
    this.rowNodes.forEach(function (rowNode) {
      rowNode.setData(null);
    });
  };

  RowNodeBlock.prototype.getState = function () {
    return this.state;
  };

  RowNodeBlock.prototype.setRowNode = function (rowIndex, rowNode) {
    var localIndex = rowIndex - this.startRow;
    this.rowNodes[localIndex] = rowNode;
  };

  RowNodeBlock.prototype.setBlankRowNode = function (rowIndex) {
    var localIndex = rowIndex - this.startRow;
    var newRowNode = this.createBlankRowNode(rowIndex);
    this.rowNodes[localIndex] = newRowNode;
    return newRowNode;
  };

  RowNodeBlock.prototype.setNewData = function (rowIndex, dataItem) {
    var newRowNode = this.setBlankRowNode(rowIndex);
    this.setDataAndId(newRowNode, dataItem, this.startRow + rowIndex);
    return newRowNode;
  };

  RowNodeBlock.prototype.createBlankRowNode = function (rowIndex) {
    var rowNode = new RowNode();
    this.beans.context.wireBean(rowNode);
    rowNode.setRowHeight(this.rowNodeCacheParams.rowHeight);
    return rowNode;
  }; // creates empty row nodes, data is missing as not loaded yet


  RowNodeBlock.prototype.createRowNodes = function () {
    this.rowNodes = [];

    for (var i = 0; i < this.rowNodeCacheParams.blockSize; i++) {
      var rowIndex = this.startRow + i;
      var rowNode = this.createBlankRowNode(rowIndex);
      this.rowNodes.push(rowNode);
    }
  };

  RowNodeBlock.prototype.load = function () {
    this.state = RowNodeBlock.STATE_LOADING;
    this.loadFromDatasource();
  };

  RowNodeBlock.prototype.pageLoadFailed = function () {
    this.state = RowNodeBlock.STATE_FAILED;
    var event = {
      type: RowNodeBlock.EVENT_LOAD_COMPLETE,
      success: false,
      page: this,
      lastRow: null
    };
    this.dispatchEvent(event);
  };

  RowNodeBlock.prototype.populateWithRowData = function (rows) {
    var _this = this;

    var rowNodesToRefresh = [];
    this.rowNodes.forEach(function (rowNode, index) {
      var data = rows[index];

      if (rowNode.stub) {
        rowNodesToRefresh.push(rowNode);
      }

      _this.setDataAndId(rowNode, data, _this.startRow + index);
    });

    if (rowNodesToRefresh.length > 0) {
      this.beans.rowRenderer.redrawRows(rowNodesToRefresh);
    }
  };

  RowNodeBlock.prototype.destroy = function () {
    _super.prototype.destroy.call(this);

    this.rowNodes.forEach(function (rowNode) {
      if (rowNode.childrenCache) {
        rowNode.childrenCache.destroy();
        rowNode.childrenCache = null;
      } // this is needed, so row render knows to fade out the row, otherwise it
      // sees row top is present, and thinks the row should be shown. maybe
      // rowNode should have a flag on whether it is visible???


      rowNode.clearRowTop();
    });
  };

  RowNodeBlock.prototype.pageLoaded = function (version, rows, lastRow) {
    // we need to check the version, in case there was an old request
    // from the server that was sent before we refreshed the cache,
    // if the load was done as a result of a cache refresh
    if (version === this.version) {
      this.state = RowNodeBlock.STATE_LOADED;
      this.populateWithRowData(rows);
    }

    lastRow = _.cleanNumber(lastRow); // check here if lastRow should be set

    var event = {
      type: RowNodeBlock.EVENT_LOAD_COMPLETE,
      success: true,
      page: this,
      lastRow: lastRow
    };
    this.dispatchEvent(event);
  };

  RowNodeBlock.EVENT_LOAD_COMPLETE = 'loadComplete';
  RowNodeBlock.STATE_DIRTY = 'dirty';
  RowNodeBlock.STATE_LOADING = 'loading';
  RowNodeBlock.STATE_LOADED = 'loaded';
  RowNodeBlock.STATE_FAILED = 'failed';
  return RowNodeBlock;
}(BeanStub);
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __decorate$1I = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var __param$a = undefined && undefined.__param || function (paramIndex, decorator) {
  return function (target, key) {
    decorator(target, key, paramIndex);
  };
};

var RowNodeBlockLoader =
/** @class */
function () {
  function RowNodeBlockLoader(maxConcurrentRequests, blockLoadDebounceMillis) {
    this.activeBlockLoadsCount = 0;
    this.blocks = [];
    this.active = true;
    this.maxConcurrentRequests = maxConcurrentRequests;

    if (blockLoadDebounceMillis && blockLoadDebounceMillis > 0) {
      this.checkBlockToLoadDebounce = _.debounce(this.performCheckBlocksToLoad.bind(this), blockLoadDebounceMillis);
    }
  }

  RowNodeBlockLoader.prototype.setBeans = function (loggerFactory) {
    this.logger = loggerFactory.create('RowNodeBlockLoader');
  };

  RowNodeBlockLoader.prototype.addBlock = function (block) {
    this.blocks.push(block);
  };

  RowNodeBlockLoader.prototype.removeBlock = function (block) {
    _.removeFromArray(this.blocks, block);
  };

  RowNodeBlockLoader.prototype.destroy = function () {
    this.active = false;
  };

  RowNodeBlockLoader.prototype.loadComplete = function () {
    this.activeBlockLoadsCount--;
  };

  RowNodeBlockLoader.prototype.checkBlockToLoad = function () {
    if (this.checkBlockToLoadDebounce) {
      this.checkBlockToLoadDebounce();
    } else {
      this.performCheckBlocksToLoad();
    }
  };

  RowNodeBlockLoader.prototype.performCheckBlocksToLoad = function () {
    if (!this.active) {
      return;
    }

    this.printCacheStatus();

    if (this.activeBlockLoadsCount >= this.maxConcurrentRequests) {
      this.logger.log("checkBlockToLoad: max loads exceeded");
      return;
    }

    var blockToLoad = null;
    this.blocks.forEach(function (block) {
      if (block.getState() === RowNodeBlock.STATE_DIRTY) {
        blockToLoad = block;
      }
    });

    if (blockToLoad) {
      blockToLoad.load();
      this.activeBlockLoadsCount++;
      this.logger.log("checkBlockToLoad: loading page " + blockToLoad.getBlockNumber());
      this.printCacheStatus();
    } else {
      this.logger.log("checkBlockToLoad: no pages to load");
    }
  };

  RowNodeBlockLoader.prototype.getBlockState = function () {
    var result = {};
    this.blocks.forEach(function (block) {
      var nodeIdPrefix = block.getNodeIdPrefix();
      var stateItem = {
        blockNumber: block.getBlockNumber(),
        startRow: block.getStartRow(),
        endRow: block.getEndRow(),
        pageStatus: block.getState()
      };

      if (_.exists(nodeIdPrefix)) {
        result[nodeIdPrefix + block.getBlockNumber()] = stateItem;
      } else {
        result[block.getBlockNumber()] = stateItem;
      }
    });
    return result;
  };

  RowNodeBlockLoader.prototype.printCacheStatus = function () {
    if (this.logger.isLogging()) {
      this.logger.log("printCacheStatus: activePageLoadsCount = " + this.activeBlockLoadsCount + "," + (" blocks = " + JSON.stringify(this.getBlockState())));
    }
  };

  RowNodeBlockLoader.prototype.isLoading = function () {
    return this.activeBlockLoadsCount > 0;
  };

  __decorate$1I([__param$a(0, Qualifier('loggerFactory'))], RowNodeBlockLoader.prototype, "setBeans", null);

  return RowNodeBlockLoader;
}();
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __extends$1a = undefined && undefined.__extends || function () {
  var _extendStatics75 = function extendStatics(d, b) {
    _extendStatics75 = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics75(d, b);
  };

  return function (d, b) {
    _extendStatics75(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var RowNodeCache =
/** @class */
function (_super) {
  __extends$1a(RowNodeCache, _super);

  function RowNodeCache(cacheParams) {
    var _this = _super.call(this) || this;

    _this.maxRowFound = false;
    _this.blocks = {};
    _this.blockCount = 0;
    _this.virtualRowCount = cacheParams.initialRowCount;
    _this.cacheParams = cacheParams;
    return _this;
  }

  RowNodeCache.prototype.destroy = function () {
    var _this = this;

    _super.prototype.destroy.call(this);

    this.forEachBlockInOrder(function (block) {
      return _this.destroyBlock(block);
    });
  };

  RowNodeCache.prototype.init = function () {
    var _this = this;

    this.active = true;
    this.addDestroyFunc(function () {
      return _this.active = false;
    });
  };

  RowNodeCache.prototype.isActive = function () {
    return this.active;
  };

  RowNodeCache.prototype.getVirtualRowCount = function () {
    return this.virtualRowCount;
  };

  RowNodeCache.prototype.hack_setVirtualRowCount = function (virtualRowCount) {
    this.virtualRowCount = virtualRowCount;
  };

  RowNodeCache.prototype.isMaxRowFound = function () {
    return this.maxRowFound;
  }; // listener on EVENT_LOAD_COMPLETE


  RowNodeCache.prototype.onPageLoaded = function (event) {
    this.cacheParams.rowNodeBlockLoader.loadComplete();
    this.checkBlockToLoad(); // if we are not active, then we ignore all events, otherwise we could end up getting the
    // grid to refresh even though we are no longer the active cache

    if (!this.isActive()) {
      return;
    }

    this.logger.log("onPageLoaded: page = " + event.page.getBlockNumber() + ", lastRow = " + event.lastRow);

    if (event.success) {
      this.checkVirtualRowCount(event.page, event.lastRow);
    }
  };

  RowNodeCache.prototype.purgeBlocksIfNeeded = function (blockToExclude) {
    var _this = this; // put all candidate blocks into a list for sorting


    var blocksForPurging = [];
    this.forEachBlockInOrder(function (block) {
      // we exclude checking for the page just created, as this has yet to be accessed and hence
      // the lastAccessed stamp will not be updated for the first time yet
      if (block === blockToExclude) {
        return;
      }

      blocksForPurging.push(block);
    }); // note: need to verify that this sorts items in the right order

    blocksForPurging.sort(function (a, b) {
      return b.getLastAccessed() - a.getLastAccessed();
    }); // we remove (maxBlocksInCache - 1) as we already excluded the 'just created' page.
    // in other words, after the splice operation below, we have taken out the blocks
    // we want to keep, which means we are left with blocks that we can potentially purge

    var maxBlocksProvided = this.cacheParams.maxBlocksInCache > 0;
    var blocksToKeep = maxBlocksProvided ? this.cacheParams.maxBlocksInCache - 1 : null;
    var emptyBlocksToKeep = RowNodeCache.MAX_EMPTY_BLOCKS_TO_KEEP - 1;
    blocksForPurging.forEach(function (block, index) {
      var purgeBecauseBlockEmpty = block.getState() === RowNodeBlock.STATE_DIRTY && index >= emptyBlocksToKeep;
      var purgeBecauseCacheFull = maxBlocksProvided ? index >= blocksToKeep : false;

      if (purgeBecauseBlockEmpty || purgeBecauseCacheFull) {
        // we never purge blocks if they are open, as purging them would mess up with
        // our indexes, it would be very messy to restore the purged block to it's
        // previous state if it had open children (and what if open children of open
        // children, jeeeesus, just thinking about it freaks me out) so best is have a
        // rule, if block is open, we never purge.
        if (block.isAnyNodeOpen(_this.virtualRowCount)) {
          return;
        } // at this point, block is not needed, and no open nodes, so burn baby burn


        _this.removeBlockFromCache(block);
      }
    });
  };

  RowNodeCache.prototype.postCreateBlock = function (newBlock) {
    newBlock.addEventListener(RowNodeBlock.EVENT_LOAD_COMPLETE, this.onPageLoaded.bind(this));
    this.setBlock(newBlock.getBlockNumber(), newBlock);
    this.purgeBlocksIfNeeded(newBlock);
    this.checkBlockToLoad();
  };

  RowNodeCache.prototype.removeBlockFromCache = function (blockToRemove) {
    if (!blockToRemove) {
      return;
    }

    this.destroyBlock(blockToRemove); // we do not want to remove the 'loaded' event listener, as the
    // concurrent loads count needs to be updated when the load is complete
    // if the purged page is in loading state
  }; // gets called after: 1) block loaded 2) block created 3) cache refresh


  RowNodeCache.prototype.checkBlockToLoad = function () {
    this.cacheParams.rowNodeBlockLoader.checkBlockToLoad();
  };

  RowNodeCache.prototype.checkVirtualRowCount = function (block, lastRow) {
    // if client provided a last row, we always use it, as it could change between server calls
    // if user deleted data and then called refresh on the grid.
    if (typeof lastRow === 'number' && lastRow >= 0) {
      this.virtualRowCount = lastRow;
      this.maxRowFound = true;
      this.onCacheUpdated();
    } else if (!this.maxRowFound) {
      // otherwise, see if we need to add some virtual rows
      var lastRowIndex = (block.getBlockNumber() + 1) * this.cacheParams.blockSize;
      var lastRowIndexPlusOverflow = lastRowIndex + this.cacheParams.overflowSize;

      if (this.virtualRowCount < lastRowIndexPlusOverflow) {
        this.virtualRowCount = lastRowIndexPlusOverflow;
        this.onCacheUpdated();
      } else if (this.cacheParams.dynamicRowHeight) {
        // the only other time is if dynamic row height, as loading rows
        // will change the height of the block, given the height of the rows
        // is only known after the row is loaded.
        this.onCacheUpdated();
      }
    }
  };

  RowNodeCache.prototype.setVirtualRowCount = function (rowCount, maxRowFound) {
    this.virtualRowCount = rowCount; // if undefined is passed, we do not set this value, if one of {true,false}
    // is passed, we do set the value.

    if (_.exists(maxRowFound)) {
      this.maxRowFound = maxRowFound;
    } // if we are still searching, then the row count must not end at the end
    // of a particular page, otherwise the searching will not pop into the
    // next page


    if (!this.maxRowFound) {
      if (this.virtualRowCount % this.cacheParams.blockSize === 0) {
        this.virtualRowCount++;
      }
    }

    this.onCacheUpdated();
  };

  RowNodeCache.prototype.forEachNodeDeep = function (callback, sequence) {
    var _this = this;

    if (sequence === void 0) {
      sequence = new NumberSequence();
    }

    this.forEachBlockInOrder(function (block) {
      block.forEachNodeDeep(callback, sequence, _this.virtualRowCount);
    });
  };

  RowNodeCache.prototype.forEachBlockInOrder = function (callback) {
    var ids = this.getBlockIdsSorted();
    this.forEachBlockId(ids, callback);
  };

  RowNodeCache.prototype.forEachBlockInReverseOrder = function (callback) {
    var ids = this.getBlockIdsSorted().reverse();
    this.forEachBlockId(ids, callback);
  };

  RowNodeCache.prototype.forEachBlockId = function (ids, callback) {
    var _this = this;

    ids.forEach(function (id) {
      var block = _this.blocks[id];
      callback(block, id);
    });
  };

  RowNodeCache.prototype.getBlockIdsSorted = function () {
    // get all page id's as NUMBERS (not strings, as we need to sort as numbers) and in descending order
    var numberComparator = function numberComparator(a, b) {
      return a - b;
    }; // default comparator for array is string comparison


    var blockIds = Object.keys(this.blocks).map(function (idStr) {
      return parseInt(idStr, 10);
    }).sort(numberComparator);
    return blockIds;
  };

  RowNodeCache.prototype.getBlock = function (blockId) {
    return this.blocks[blockId];
  };

  RowNodeCache.prototype.setBlock = function (id, block) {
    this.blocks[id] = block;
    this.blockCount++;
    this.cacheParams.rowNodeBlockLoader.addBlock(block);
  };

  RowNodeCache.prototype.destroyBlock = function (block) {
    delete this.blocks[block.getBlockNumber()];
    block.destroy();
    this.blockCount--;
    this.cacheParams.rowNodeBlockLoader.removeBlock(block);
  }; // gets called 1) row count changed 2) cache purged 3) items inserted


  RowNodeCache.prototype.onCacheUpdated = function () {
    if (this.isActive()) {
      // this results in both row models (infinite and server side) firing ModelUpdated,
      // however server side row model also updates the row indexes first
      var event_1 = {
        type: RowNodeCache.EVENT_CACHE_UPDATED
      };
      this.dispatchEvent(event_1);
    }
  };

  RowNodeCache.prototype.purgeCache = function () {
    var _this = this;

    this.forEachBlockInOrder(function (block) {
      return _this.removeBlockFromCache(block);
    });

    if (this.virtualRowCount === 0) {
      // re-initialise cache - this ensures a cache with no rows can reload when purged!
      this.virtualRowCount = this.cacheParams.initialRowCount;
      this.maxRowFound = false;
    }

    this.onCacheUpdated();
  };

  RowNodeCache.prototype.getRowNodesInRange = function (firstInRange, lastInRange) {
    var _this = this;

    var result = [];
    var lastBlockId = -1;
    var inActiveRange = false;
    var numberSequence = new NumberSequence(); // if only one node passed, we start the selection at the top

    if (_.missing(firstInRange)) {
      inActiveRange = true;
    }

    var foundGapInSelection = false;
    this.forEachBlockInOrder(function (block, id) {
      if (foundGapInSelection) {
        return;
      }

      if (inActiveRange && lastBlockId + 1 !== id) {
        foundGapInSelection = true;
        return;
      }

      lastBlockId = id;
      block.forEachNodeShallow(function (rowNode) {
        var hitFirstOrLast = rowNode === firstInRange || rowNode === lastInRange;

        if (inActiveRange || hitFirstOrLast) {
          result.push(rowNode);
        }

        if (hitFirstOrLast) {
          inActiveRange = !inActiveRange;
        }
      }, numberSequence, _this.virtualRowCount);
    }); // inActiveRange will be still true if we never hit the second rowNode

    var invalidRange = foundGapInSelection || inActiveRange;
    return invalidRange ? [] : result;
  };

  RowNodeCache.EVENT_CACHE_UPDATED = 'cacheUpdated'; // this property says how many empty blocks should be in a cache, eg if scrolls down fast and creates 10
  // blocks all for loading, the grid will only load the last 2 - it will assume the blocks the user quickly
  // scrolled over are not needed to be loaded.

  RowNodeCache.MAX_EMPTY_BLOCKS_TO_KEEP = 2;
  return RowNodeCache;
}(BeanStub);
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __extends$1b = undefined && undefined.__extends || function () {
  var _extendStatics76 = function extendStatics(d, b) {
    _extendStatics76 = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics76(d, b);
  };

  return function (d, b) {
    _extendStatics76(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var __decorate$1J = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var MessageBox =
/** @class */
function (_super) {
  __extends$1b(MessageBox, _super);

  function MessageBox(config) {
    var _this = _super.call(this, config) || this;

    _this.message = config.message;
    return _this;
  }

  MessageBox.prototype.postConstruct = function () {
    var _this = this;

    var messageBodyComp = new MessageBody();
    this.wireDependentBean(messageBodyComp);
    messageBodyComp.setMessage(this.message);
    this.setBodyComponent(messageBodyComp);

    _super.prototype.postConstruct.call(this);

    this.addDestroyableEventListener(messageBodyComp, 'onBtOk', function () {
      return _this.close();
    });
  };

  return MessageBox;
}(AgDialog);

var MessageBody =
/** @class */
function (_super) {
  __extends$1b(MessageBody, _super);

  function MessageBody() {
    return _super.call(this, MessageBody.TEMPLATE) || this;
  }

  MessageBody.prototype.setMessage = function (message) {
    this.eCenter.innerText = message;
  };

  MessageBody.prototype.postConstruct = function () {
    this.addDestroyableEventListener(this.eOk, 'click', this.onBtOk.bind(this));
  };

  MessageBody.prototype.onBtOk = function () {
    this.dispatchEvent({
      type: 'onBtOk'
    });
  };

  MessageBody.TEMPLATE = "<div class=\"ag-message-box\">\n            <div ref=\"eCenter\" class=\"ag-message-box-content\"></div>\n            <div ref=\"eButtons\" class=\"ag-message-box-button-bar\">\n                <button ref=\"eOk\">OK</button>\n            </div>\n        </div>";

  __decorate$1J([RefSelector('eCenter')], MessageBody.prototype, "eCenter", void 0);

  __decorate$1J([RefSelector('eOk')], MessageBody.prototype, "eOk", void 0);

  __decorate$1J([PostConstruct], MessageBody.prototype, "postConstruct", null);

  return MessageBody;
}(Component);
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var __extends$1c = undefined && undefined.__extends || function () {
  var _extendStatics77 = function extendStatics(d, b) {
    _extendStatics77 = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics77(d, b);
  };

  return function (d, b) {
    _extendStatics77(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var __decorate$1K = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var VirtualList =
/** @class */
function (_super) {
  __extends$1c(VirtualList, _super);

  function VirtualList() {
    var _this = _super.call(this, undefined) || this;

    _this.rowsInBodyContainer = {};
    _this.rowHeight = 20;
    return _this;
  }

  VirtualList.prototype.init = function () {
    this.setTemplate(VirtualList.TEMPLATE);
    this.eListContainer = this.queryForHtmlElement(".ag-virtual-list-container");
    this.addScrollListener();
    var item = document.createElement('div');

    _.addCssClass(item, 'ag-virtual-list-item');

    this.rowHeight = this.getItemHeight();
  };

  VirtualList.prototype.getItemHeight = function () {
    return this.gridOptionsWrapper.getVirtualItemHeight();
  };

  VirtualList.prototype.ensureIndexVisible = function (index) {
    var lastRow = this.model.getRowCount();

    if (typeof index !== 'number' || index < 0 || index >= lastRow) {
      console.warn('invalid row index for ensureIndexVisible: ' + index);
      return;
    } // let nodeAtIndex = this.rowModel.getRow(index);


    var rowTopPixel = index * this.rowHeight;
    var rowBottomPixel = rowTopPixel + this.rowHeight;
    var viewportTopPixel = this.getGui().scrollTop;
    var viewportHeight = this.getGui().offsetHeight;
    var viewportBottomPixel = viewportTopPixel + viewportHeight;
    var viewportScrolledPastRow = viewportTopPixel > rowTopPixel;
    var viewportScrolledBeforeRow = viewportBottomPixel < rowBottomPixel;

    if (viewportScrolledPastRow) {
      // if row is before, scroll up with row at top
      this.getGui().scrollTop = rowTopPixel;
    } else if (viewportScrolledBeforeRow) {
      // if row is below, scroll down with row at bottom
      var newScrollPosition = rowBottomPixel - viewportHeight;
      this.getGui().scrollTop = newScrollPosition;
    }
  };

  VirtualList.prototype.setComponentCreator = function (componentCreator) {
    this.componentCreator = componentCreator;
  };

  VirtualList.prototype.getRowHeight = function () {
    return this.rowHeight;
  };

  VirtualList.prototype.getScrollTop = function () {
    return this.getGui().scrollTop;
  };

  VirtualList.prototype.setRowHeight = function (rowHeight) {
    this.rowHeight = rowHeight;
    this.refresh();
  };

  VirtualList.prototype.refresh = function () {
    if (_.missing(this.model)) {
      return;
    }

    this.eListContainer.style.height = this.model.getRowCount() * this.rowHeight + "px";
    this.clearVirtualRows();
    this.drawVirtualRows();
  };

  VirtualList.prototype.clearVirtualRows = function () {
    var rowsToRemove = Object.keys(this.rowsInBodyContainer);
    this.removeVirtualRows(rowsToRemove);
  };

  VirtualList.prototype.drawVirtualRows = function () {
    var topPixel = this.getGui().scrollTop;
    var bottomPixel = topPixel + this.getGui().offsetHeight;
    var firstRow = Math.floor(topPixel / this.rowHeight);
    var lastRow = Math.floor(bottomPixel / this.rowHeight);
    this.ensureRowsRendered(firstRow, lastRow);
  };

  VirtualList.prototype.ensureRowsRendered = function (start, finish) {
    // at the end, this array will contain the items we need to remove
    var rowsToRemove = Object.keys(this.rowsInBodyContainer); // add in new rows

    for (var rowIndex = start; rowIndex <= finish; rowIndex++) {
      // see if item already there, and if yes, take it out of the 'to remove' array
      if (rowsToRemove.indexOf(rowIndex.toString()) >= 0) {
        rowsToRemove.splice(rowsToRemove.indexOf(rowIndex.toString()), 1);
        continue;
      } // check this row actually exists (in case overflow buffer window exceeds real data)


      if (this.model.getRowCount() > rowIndex) {
        var value = this.model.getRow(rowIndex);
        this.insertRow(value, rowIndex);
      }
    } // at this point, everything in our 'rowsToRemove' . . .


    this.removeVirtualRows(rowsToRemove);
  }; // takes array of row id's


  VirtualList.prototype.removeVirtualRows = function (rowsToRemove) {
    var _this = this;

    rowsToRemove.forEach(function (index) {
      var component = _this.rowsInBodyContainer[index];

      _this.eListContainer.removeChild(component.eDiv);

      if (component.rowComponent.destroy) {
        component.rowComponent.destroy();
      }

      delete _this.rowsInBodyContainer[index];
    });
  };

  VirtualList.prototype.insertRow = function (value, rowIndex) {
    var eDiv = document.createElement('div');

    _.addCssClass(eDiv, 'ag-virtual-list-item');

    eDiv.style.top = this.rowHeight * rowIndex + "px";
    var rowComponent = this.componentCreator(value);
    eDiv.appendChild(rowComponent.getGui());
    this.eListContainer.appendChild(eDiv);
    this.rowsInBodyContainer[rowIndex] = {
      rowComponent: rowComponent,
      eDiv: eDiv
    };
  };

  VirtualList.prototype.addScrollListener = function () {
    var _this = this;

    this.addGuiEventListener('scroll', function () {
      _this.drawVirtualRows();
    });
  };

  VirtualList.prototype.setModel = function (model) {
    this.model = model;
  };

  VirtualList.TEMPLATE = "<div class=\"ag-virtual-list-viewport\">\n            <div class=\"ag-virtual-list-container\"></div>\n        </div>";

  __decorate$1K([Autowired('environment')], VirtualList.prototype, "environment", void 0);

  __decorate$1K([Autowired('gridOptionsWrapper')], VirtualList.prototype, "gridOptionsWrapper", void 0);

  __decorate$1K([PostConstruct], VirtualList.prototype, "init", null);

  return VirtualList;
}(Component);
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


function defaultGroupComparator(valueA, valueB, nodeA, nodeB, accentedCompare) {
  if (accentedCompare === void 0) {
    accentedCompare = false;
  }

  console.warn('ag-Grid: Since ag-grid 11.0.0 defaultGroupComparator is not necessary. You can remove this from your colDef');
  var nodeAIsGroup = _.exists(nodeA) && nodeA.group;
  var nodeBIsGroup = _.exists(nodeB) && nodeB.group;
  var bothAreGroups = nodeAIsGroup && nodeBIsGroup;
  var bothAreNormal = !nodeAIsGroup && !nodeBIsGroup;

  if (bothAreGroups) {
    return _.defaultComparator(nodeA.key, nodeB.key, accentedCompare);
  } else if (bothAreNormal) {
    return _.defaultComparator(valueA, valueB, accentedCompare);
  } else if (nodeAIsGroup) {
    return 1;
  } else {
    return -1;
  }
}
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var BaseComponentWrapper =
/** @class */
function () {
  function BaseComponentWrapper() {}

  BaseComponentWrapper.prototype.wrap = function (OriginalConstructor, mandatoryMethodList, optionalMethodList, componentName) {
    var _this = this;

    if (optionalMethodList === void 0) {
      optionalMethodList = [];
    }

    var wrapper = this.createWrapper(OriginalConstructor, componentName);
    mandatoryMethodList.forEach(function (methodName) {
      _this.createMethod(wrapper, methodName, true);
    });
    optionalMethodList.forEach(function (methodName) {
      _this.createMethod(wrapper, methodName, false);
    });
    return wrapper;
  };

  BaseComponentWrapper.prototype.createMethod = function (wrapper, methodName, mandatory) {
    wrapper.addMethod(methodName, this.createMethodProxy(wrapper, methodName, mandatory));
  };

  BaseComponentWrapper.prototype.createMethodProxy = function (wrapper, methodName, mandatory) {
    return function () {
      if (wrapper.hasMethod(methodName)) {
        return wrapper.callMethod(methodName, arguments);
      }

      if (mandatory) {
        console.warn('ag-Grid: Framework component is missing the method ' + methodName + '()');
      }

      return null;
    };
  };

  return BaseComponentWrapper;
}();
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


(function (ChartType) {
  ChartType["GroupedColumn"] = "groupedColumn";
  ChartType["StackedColumn"] = "stackedColumn";
  ChartType["NormalizedColumn"] = "normalizedColumn";
  ChartType["GroupedBar"] = "groupedBar";
  ChartType["StackedBar"] = "stackedBar";
  ChartType["NormalizedBar"] = "normalizedBar";
  ChartType["Line"] = "line";
  ChartType["Scatter"] = "scatter";
  ChartType["Bubble"] = "bubble";
  ChartType["Pie"] = "pie";
  ChartType["Doughnut"] = "doughnut";
  ChartType["Area"] = "area";
  ChartType["StackedArea"] = "stackedArea";
  ChartType["NormalizedArea"] = "normalizedArea";
})(exports.ChartType || (exports.ChartType = {}));
/**
 * @ag-grid-community/core - Advanced Data Grid / Data Table supporting Javascript / React / AngularJS / Web Components
 * @version v22.1.1
 * @link http://www.ag-grid.com/
 * @license MIT
 */


var globalObj = typeof global === 'undefined' ? {} : global;
globalObj.HTMLElement = typeof HTMLElement === 'undefined' ? {} : HTMLElement;
globalObj.HTMLButtonElement = typeof HTMLButtonElement === 'undefined' ? {} : HTMLButtonElement;
globalObj.HTMLSelectElement = typeof HTMLSelectElement === 'undefined' ? {} : HTMLSelectElement;
globalObj.HTMLInputElement = typeof HTMLInputElement === 'undefined' ? {} : HTMLInputElement;
globalObj.Node = typeof Node === 'undefined' ? {} : Node;
globalObj.MouseEvent = typeof MouseEvent === 'undefined' ? {} : MouseEvent;

var ClientSideNodeManager =
/** @class */
function () {
  function ClientSideNodeManager(rootNode, gridOptionsWrapper, context, eventService, columnController, gridApi, columnApi, selectionController) {
    this.nextId = 0; // when user is provide the id's, we also keep a map of ids to row nodes for convenience

    this.allNodesMap = {};
    this.rootNode = rootNode;
    this.gridOptionsWrapper = gridOptionsWrapper;
    this.context = context;
    this.eventService = eventService;
    this.columnController = columnController;
    this.gridApi = gridApi;
    this.columnApi = columnApi;
    this.selectionController = selectionController;
    this.rootNode.group = true;
    this.rootNode.level = -1;
    this.rootNode.id = ClientSideNodeManager.ROOT_NODE_ID;
    this.rootNode.allLeafChildren = [];
    this.rootNode.childrenAfterGroup = [];
    this.rootNode.childrenAfterSort = [];
    this.rootNode.childrenAfterFilter = []; // if we make this class a bean, then can annotate postConstruct

    this.postConstruct();
  } // @PostConstruct - this is not a bean, so postConstruct called by constructor


  ClientSideNodeManager.prototype.postConstruct = function () {
    // func below doesn't have 'this' pointer, so need to pull out these bits
    this.getNodeChildDetails = this.gridOptionsWrapper.getNodeChildDetailsFunc();
    this.suppressParentsInRowNodes = this.gridOptionsWrapper.isSuppressParentsInRowNodes();
    this.doesDataFlower = this.gridOptionsWrapper.getDoesDataFlowerFunc();
    this.isRowMasterFunc = this.gridOptionsWrapper.getIsRowMasterFunc();
    this.doingLegacyTreeData = _.exists(this.getNodeChildDetails);
    this.doingMasterDetail = this.gridOptionsWrapper.isMasterDetail();

    if (this.getNodeChildDetails) {
      console.warn("ag-Grid: the callback nodeChildDetailsFunc() is now deprecated. The new way of doing\n                                    tree data in ag-Grid was introduced in v14 (released November 2017). In the next\n                                    major release of ag-Grid we will be dropping support for the old version of\n                                    tree data. If you are reading this message, please go to the docs to see how\n                                    to implement Tree Data without using nodeChildDetailsFunc().");
    }
  };

  ClientSideNodeManager.prototype.getCopyOfNodesMap = function () {
    var result = _.cloneObject(this.allNodesMap);

    return result;
  };

  ClientSideNodeManager.prototype.getRowNode = function (id) {
    return this.allNodesMap[id];
  };

  ClientSideNodeManager.prototype.setRowData = function (rowData) {
    this.rootNode.childrenAfterFilter = null;
    this.rootNode.childrenAfterGroup = null;
    this.rootNode.childrenAfterSort = null;
    this.rootNode.childrenMapped = null;
    this.nextId = 0;
    this.allNodesMap = {};

    if (!rowData) {
      this.rootNode.allLeafChildren = [];
      this.rootNode.childrenAfterGroup = [];
      return;
    } // kick off recursion
    // we add rootNode as the parent, however if using ag-grid-enterprise, the grouping stage
    // sets the parent node on each row (even if we are not grouping). so setting parent node
    // here is for benefit of ag-grid-community users


    var result = this.recursiveFunction(rowData, this.rootNode, ClientSideNodeManager.TOP_LEVEL);

    if (this.doingLegacyTreeData) {
      this.rootNode.childrenAfterGroup = result;
      this.setLeafChildren(this.rootNode);
    } else {
      this.rootNode.allLeafChildren = result;
    }
  };

  ClientSideNodeManager.prototype.updateRowData = function (rowDataTran, rowNodeOrder) {
    if (this.isLegacyTreeData()) {
      return null;
    }

    var rowNodeTransaction = {
      remove: [],
      update: [],
      add: []
    };
    this.executeAdd(rowDataTran, rowNodeTransaction);
    this.executeRemove(rowDataTran, rowNodeTransaction);
    this.executeUpdate(rowDataTran, rowNodeTransaction);

    if (rowNodeOrder) {
      _.sortRowNodesByOrder(this.rootNode.allLeafChildren, rowNodeOrder);
    }

    return rowNodeTransaction;
  };

  ClientSideNodeManager.prototype.executeAdd = function (rowDataTran, rowNodeTransaction) {
    var _this = this;

    var add = rowDataTran.add,
        addIndex = rowDataTran.addIndex;

    if (!add) {
      return;
    }

    var useIndex = typeof addIndex === 'number' && addIndex >= 0;

    if (useIndex) {
      // items get inserted in reverse order for index insertion
      add.reverse().forEach(function (item) {
        var newRowNode = _this.addRowNode(item, addIndex);

        rowNodeTransaction.add.push(newRowNode);
      });
    } else {
      add.forEach(function (item) {
        var newRowNode = _this.addRowNode(item);

        rowNodeTransaction.add.push(newRowNode);
      });
    }
  };

  ClientSideNodeManager.prototype.executeRemove = function (rowDataTran, rowNodeTransaction) {
    var _this = this;

    var remove = rowDataTran.remove;

    if (!remove) {
      return;
    }

    var rowIdsRemoved = {};
    var anyNodesSelected = false;
    remove.forEach(function (item) {
      var rowNode = _this.lookupRowNode(item);

      if (!rowNode) {
        return;
      }

      if (rowNode.isSelected()) {
        anyNodesSelected = true;
      } // do delete - setting 'tailingNodeInSequence = true' to ensure EVENT_SELECTION_CHANGED is not raised for
      // each row node updated, instead it is raised once by the calling code if any selected nodes exist.


      rowNode.setSelected(false, false, true); // so row renderer knows to fade row out (and not reposition it)

      rowNode.clearRowTop(); // NOTE: were we could remove from allLeaveChildren, however _.removeFromArray() is expensive, especially
      // if called multiple times (eg deleting lots of rows) and if allLeafChildren is a large list

      rowIdsRemoved[rowNode.id] = true; // _.removeFromArray(this.rootNode.allLeafChildren, rowNode);

      delete _this.allNodesMap[rowNode.id];
      rowNodeTransaction.remove.push(rowNode);
    });
    this.rootNode.allLeafChildren = this.rootNode.allLeafChildren.filter(function (rowNode) {
      return !rowIdsRemoved[rowNode.id];
    });

    if (anyNodesSelected) {
      this.selectionController.updateGroupsFromChildrenSelections();
      var event_1 = {
        type: Events.EVENT_SELECTION_CHANGED,
        api: this.gridApi,
        columnApi: this.columnApi
      };
      this.eventService.dispatchEvent(event_1);
    }
  };

  ClientSideNodeManager.prototype.executeUpdate = function (rowDataTran, rowNodeTransaction) {
    var _this = this;

    var update = rowDataTran.update;

    if (!update) {
      return;
    }

    update.forEach(function (item) {
      var rowNode = _this.lookupRowNode(item);

      if (!rowNode) {
        return;
      }

      rowNode.updateData(item);
      rowNodeTransaction.update.push(rowNode);
    });
  };

  ClientSideNodeManager.prototype.addRowNode = function (data, index) {
    var newNode = this.createNode(data, this.rootNode, ClientSideNodeManager.TOP_LEVEL);

    if (_.exists(index)) {
      _.insertIntoArray(this.rootNode.allLeafChildren, newNode, index);
    } else {
      this.rootNode.allLeafChildren.push(newNode);
    }

    return newNode;
  };

  ClientSideNodeManager.prototype.lookupRowNode = function (data) {
    var rowNodeIdFunc = this.gridOptionsWrapper.getRowNodeIdFunc();
    var rowNode;

    if (_.exists(rowNodeIdFunc)) {
      // find rowNode using id
      var id = rowNodeIdFunc(data);
      rowNode = this.allNodesMap[id];

      if (!rowNode) {
        console.error("ag-Grid: could not find row id=" + id + ", data item was not found for this id");
        return null;
      }
    } else {
      // find rowNode using object references
      rowNode = _.find(this.rootNode.allLeafChildren, function (rowNode) {
        return rowNode.data === data;
      });

      if (!rowNode) {
        console.error("ag-Grid: could not find data item as object was not found", data);
        return null;
      }
    }

    return rowNode;
  };

  ClientSideNodeManager.prototype.recursiveFunction = function (rowData, parent, level) {
    var _this = this; // make sure the rowData is an array and not a string of json - this was a commonly reported problem on the forum


    if (typeof rowData === 'string') {
      console.warn('ag-Grid: rowData must be an array, however you passed in a string. If you are loading JSON, make sure you convert the JSON string to JavaScript objects first');
      return;
    }

    var rowNodes = [];
    rowData.forEach(function (dataItem) {
      var node = _this.createNode(dataItem, parent, level);

      rowNodes.push(node);
    });
    return rowNodes;
  };

  ClientSideNodeManager.prototype.createNode = function (dataItem, parent, level) {
    var node = new RowNode();
    this.context.wireBean(node);
    var doingTreeData = this.gridOptionsWrapper.isTreeData();

    var doingLegacyTreeData = !doingTreeData && _.exists(this.getNodeChildDetails);

    var nodeChildDetails = doingLegacyTreeData ? this.getNodeChildDetails(dataItem) : null;

    if (nodeChildDetails && nodeChildDetails.group) {
      node.group = true;
      node.childrenAfterGroup = this.recursiveFunction(nodeChildDetails.children, node, level + 1);
      node.expanded = nodeChildDetails.expanded === true;
      node.field = nodeChildDetails.field;
      node.key = nodeChildDetails.key;
      /** @deprecated is now 'master' */

      node.canFlower = node.master; // pull out all the leaf children and add to our node

      this.setLeafChildren(node);
    } else {
      node.group = false;

      if (doingTreeData) {
        node.master = false;
        node.expanded = false;
      } else {
        // this is the default, for when doing grid data
        if (this.doesDataFlower) {
          node.master = this.doesDataFlower(dataItem);
        } else if (this.doingMasterDetail) {
          // if we are doing master detail, then the
          // default is that everything can flower.
          if (this.isRowMasterFunc) {
            node.master = this.isRowMasterFunc(dataItem);
          } else {
            node.master = true;
          }
        } else {
          node.master = false;
        }

        var rowGroupColumns = this.columnController.getRowGroupColumns();
        var numRowGroupColumns = rowGroupColumns ? rowGroupColumns.length : 0; // need to take row group into account when determining level

        var masterRowLevel = level + numRowGroupColumns;
        node.expanded = node.master ? this.isExpanded(masterRowLevel) : false;
      }
    } // support for backwards compatibility, canFlow is now called 'master'


    node.canFlower = node.master;

    if (parent && !this.suppressParentsInRowNodes) {
      node.parent = parent;
    }

    node.level = level;
    node.setDataAndId(dataItem, this.nextId.toString());

    if (this.allNodesMap[node.id]) {
      console.warn("ag-grid: duplicate node id '" + node.id + "' detected from getRowNodeId callback, this could cause issues in your grid.");
    }

    this.allNodesMap[node.id] = node;
    this.nextId++;
    return node;
  };

  ClientSideNodeManager.prototype.isExpanded = function (level) {
    var expandByDefault = this.gridOptionsWrapper.getGroupDefaultExpanded();

    if (expandByDefault === -1) {
      return true;
    } else {
      return level < expandByDefault;
    }
  }; // this is only used for doing legacy tree data


  ClientSideNodeManager.prototype.setLeafChildren = function (node) {
    node.allLeafChildren = [];

    if (node.childrenAfterGroup) {
      node.childrenAfterGroup.forEach(function (childAfterGroup) {
        if (childAfterGroup.group) {
          if (childAfterGroup.allLeafChildren) {
            childAfterGroup.allLeafChildren.forEach(function (leafChild) {
              return node.allLeafChildren.push(leafChild);
            });
          }
        } else {
          node.allLeafChildren.push(childAfterGroup);
        }
      });
    }
  };

  ClientSideNodeManager.prototype.isLegacyTreeData = function () {
    var rowsAlreadyGrouped = _.exists(this.gridOptionsWrapper.getNodeChildDetailsFunc());

    if (rowsAlreadyGrouped) {
      console.warn('ag-Grid: adding and removing rows is not supported when using nodeChildDetailsFunc, ie it is not ' + 'supported for legacy tree data. Please see the docs on the new preferred way of providing tree data that works with delta updates.');
      return true;
    } else {
      return false;
    }
  };

  ClientSideNodeManager.TOP_LEVEL = 0;
  ClientSideNodeManager.ROOT_NODE_ID = 'ROOT_NODE_ID';
  return ClientSideNodeManager;
}();

var __decorate$1L = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var RecursionType;

(function (RecursionType) {
  RecursionType[RecursionType["Normal"] = 0] = "Normal";
  RecursionType[RecursionType["AfterFilter"] = 1] = "AfterFilter";
  RecursionType[RecursionType["AfterFilterAndSort"] = 2] = "AfterFilterAndSort";
  RecursionType[RecursionType["PivotNodes"] = 3] = "PivotNodes";
})(RecursionType || (RecursionType = {}));

var ClientSideRowModel =
/** @class */
function () {
  function ClientSideRowModel() {}

  ClientSideRowModel.prototype.init = function () {
    var refreshEverythingFunc = this.refreshModel.bind(this, {
      step: Constants.STEP_EVERYTHING
    });
    var refreshEverythingAfterColsChangedFunc = this.refreshModel.bind(this, {
      step: Constants.STEP_EVERYTHING,
      afterColumnsChanged: true
    });
    this.eventService.addModalPriorityEventListener(Events.EVENT_COLUMN_EVERYTHING_CHANGED, refreshEverythingAfterColsChangedFunc);
    this.eventService.addModalPriorityEventListener(Events.EVENT_COLUMN_ROW_GROUP_CHANGED, refreshEverythingFunc);
    this.eventService.addModalPriorityEventListener(Events.EVENT_COLUMN_VALUE_CHANGED, this.onValueChanged.bind(this));
    this.eventService.addModalPriorityEventListener(Events.EVENT_COLUMN_PIVOT_CHANGED, this.refreshModel.bind(this, {
      step: Constants.STEP_PIVOT
    }));
    this.eventService.addModalPriorityEventListener(Events.EVENT_ROW_GROUP_OPENED, this.onRowGroupOpened.bind(this));
    this.eventService.addModalPriorityEventListener(Events.EVENT_FILTER_CHANGED, this.onFilterChanged.bind(this));
    this.eventService.addModalPriorityEventListener(Events.EVENT_SORT_CHANGED, this.onSortChanged.bind(this));
    this.eventService.addModalPriorityEventListener(Events.EVENT_COLUMN_PIVOT_MODE_CHANGED, refreshEverythingFunc);
    var refreshMapFunc = this.refreshModel.bind(this, {
      step: Constants.STEP_MAP,
      keepRenderedRows: true,
      animate: true
    });
    this.gridOptionsWrapper.addEventListener(GridOptionsWrapper.PROP_GROUP_REMOVE_SINGLE_CHILDREN, refreshMapFunc);
    this.gridOptionsWrapper.addEventListener(GridOptionsWrapper.PROP_GROUP_REMOVE_LOWEST_SINGLE_CHILDREN, refreshMapFunc);
    this.rootNode = new RowNode();
    this.nodeManager = new ClientSideNodeManager(this.rootNode, this.gridOptionsWrapper, this.context, this.eventService, this.columnController, this.gridApi, this.columnApi, this.selectionController);
    this.context.wireBean(this.rootNode);
  };

  ClientSideRowModel.prototype.start = function () {
    var rowData = this.gridOptionsWrapper.getRowData();

    if (rowData) {
      this.setRowData(rowData);
    }
  };

  ClientSideRowModel.prototype.ensureRowHeightsValid = function (startPixel, endPixel, startLimitIndex, endLimitIndex) {
    var atLeastOneChange;
    var res = false; // we do this multiple times as changing the row heights can also change the first and last rows,
    // so the first pass can make lots of rows smaller, which means the second pass we end up changing
    // more rows.

    do {
      atLeastOneChange = false;
      var rowAtStartPixel = this.getRowIndexAtPixel(startPixel);
      var rowAtEndPixel = this.getRowIndexAtPixel(endPixel); // keep check to current page if doing pagination

      var firstRow = Math.max(rowAtStartPixel, startLimitIndex);
      var lastRow = Math.min(rowAtEndPixel, endLimitIndex);

      for (var rowIndex = firstRow; rowIndex <= lastRow; rowIndex++) {
        var rowNode = this.getRow(rowIndex);

        if (rowNode.rowHeightEstimated) {
          var rowHeight = this.gridOptionsWrapper.getRowHeightForNode(rowNode);
          rowNode.setRowHeight(rowHeight.height);
          atLeastOneChange = true;
          res = true;
        }
      }

      if (atLeastOneChange) {
        this.setRowTops();
      }
    } while (atLeastOneChange);

    return res;
  };

  ClientSideRowModel.prototype.setRowTops = function () {
    var nextRowTop = 0;

    for (var i = 0; i < this.rowsToDisplay.length; i++) {
      // we don't estimate if doing fullHeight or autoHeight, as all rows get rendered all the time
      // with these two layouts.
      var allowEstimate = this.gridOptionsWrapper.getDomLayout() === Constants.DOM_LAYOUT_NORMAL;
      var rowNode = this.rowsToDisplay[i];

      if (_.missing(rowNode.rowHeight)) {
        var rowHeight = this.gridOptionsWrapper.getRowHeightForNode(rowNode, allowEstimate);
        rowNode.setRowHeight(rowHeight.height, rowHeight.estimated);
      }

      rowNode.setRowTop(nextRowTop);
      rowNode.setRowIndex(i);
      nextRowTop += rowNode.rowHeight;
    }
  };

  ClientSideRowModel.prototype.resetRowTops = function (rowNode, changedPath) {
    rowNode.clearRowTop();

    if (rowNode.hasChildren()) {
      if (rowNode.childrenAfterGroup) {
        // if a changedPath is active, it means we are here because of a transaction update or
        // a change detection. neither of these impacts the open/closed state of groups. so if
        // a group is not open this time, it was not open last time. so we know all closed groups
        // already have their top positions cleared. so there is no need to traverse all the way
        // when changedPath is active and the rowNode is not expanded.
        var skipChildren = changedPath.isActive() && !rowNode.expanded;

        if (!skipChildren) {
          for (var i = 0; i < rowNode.childrenAfterGroup.length; i++) {
            this.resetRowTops(rowNode.childrenAfterGroup[i], changedPath);
          }
        }
      }

      if (rowNode.sibling) {
        rowNode.sibling.clearRowTop();
      }
    }

    if (rowNode.detailNode) {
      rowNode.detailNode.clearRowTop();
    }
  }; // returns false if row was moved, otherwise true


  ClientSideRowModel.prototype.ensureRowAtPixel = function (rowNode, pixel) {
    var indexAtPixelNow = this.getRowIndexAtPixel(pixel);
    var rowNodeAtPixelNow = this.getRow(indexAtPixelNow);

    if (rowNodeAtPixelNow === rowNode) {
      return false;
    }

    _.removeFromArray(this.rootNode.allLeafChildren, rowNode);

    _.insertIntoArray(this.rootNode.allLeafChildren, rowNode, indexAtPixelNow);

    this.refreshModel({
      step: Constants.STEP_EVERYTHING,
      keepRenderedRows: true,
      animate: true,
      keepEditingRows: true
    });
    return true;
  };

  ClientSideRowModel.prototype.isLastRowFound = function () {
    return true;
  };

  ClientSideRowModel.prototype.getRowCount = function () {
    if (this.rowsToDisplay) {
      return this.rowsToDisplay.length;
    } else {
      return 0;
    }
  };

  ClientSideRowModel.prototype.getTopLevelRowCount = function () {
    var showingRootNode = this.rowsToDisplay && this.rowsToDisplay[0] === this.rootNode;

    if (showingRootNode) {
      return 1;
    } else {
      return this.rootNode.childrenAfterFilter ? this.rootNode.childrenAfterFilter.length : 0;
    }
  };

  ClientSideRowModel.prototype.getTopLevelRowDisplayedIndex = function (topLevelIndex) {
    var showingRootNode = this.rowsToDisplay && this.rowsToDisplay[0] === this.rootNode;

    if (showingRootNode) {
      return topLevelIndex;
    } else {
      var rowNode = this.rootNode.childrenAfterSort[topLevelIndex];

      if (this.gridOptionsWrapper.isGroupHideOpenParents()) {
        // if hideOpenParents, and this row open, then this row is now displayed at this index, first child is
        while (rowNode.expanded && rowNode.childrenAfterSort && rowNode.childrenAfterSort.length > 0) {
          rowNode = rowNode.childrenAfterSort[0];
        }
      }

      return rowNode.rowIndex;
    }
  };

  ClientSideRowModel.prototype.getRowBounds = function (index) {
    if (_.missing(this.rowsToDisplay)) {
      return null;
    }

    var rowNode = this.rowsToDisplay[index];

    if (rowNode) {
      return {
        rowTop: rowNode.rowTop,
        rowHeight: rowNode.rowHeight
      };
    } else {
      return null;
    }
  };

  ClientSideRowModel.prototype.onRowGroupOpened = function () {
    var animate = this.gridOptionsWrapper.isAnimateRows();
    this.refreshModel({
      step: Constants.STEP_MAP,
      keepRenderedRows: true,
      animate: animate
    });
  };

  ClientSideRowModel.prototype.onFilterChanged = function () {
    var animate = this.gridOptionsWrapper.isAnimateRows();
    this.refreshModel({
      step: Constants.STEP_FILTER,
      keepRenderedRows: true,
      animate: animate
    });
  };

  ClientSideRowModel.prototype.onSortChanged = function () {
    var animate = this.gridOptionsWrapper.isAnimateRows();
    this.refreshModel({
      step: Constants.STEP_SORT,
      keepRenderedRows: true,
      animate: animate,
      keepEditingRows: true
    });
  };

  ClientSideRowModel.prototype.getType = function () {
    return Constants.ROW_MODEL_TYPE_CLIENT_SIDE;
  };

  ClientSideRowModel.prototype.onValueChanged = function () {
    if (this.columnController.isPivotActive()) {
      this.refreshModel({
        step: Constants.STEP_PIVOT
      });
    } else {
      this.refreshModel({
        step: Constants.STEP_AGGREGATE
      });
    }
  };

  ClientSideRowModel.prototype.createChangePath = function (rowNodeTransactions) {
    // for updates, if the row is updated at all, then we re-calc all the values
    // in that row. we could compare each value to each old value, however if we
    // did this, we would be calling the valueService twice, once on the old value
    // and once on the new value. so it's less valueGetter calls if we just assume
    // each column is different. that way the changedPath is used so that only
    // the impacted parent rows are recalculated, parents who's children have
    // not changed are not impacted.
    var noTransactions = _.missingOrEmpty(rowNodeTransactions);

    var changedPath = new ChangedPath(false, this.rootNode);

    if (noTransactions || this.gridOptionsWrapper.isTreeData()) {
      changedPath.setInactive();
    }

    return changedPath;
  };

  ClientSideRowModel.prototype.refreshModel = function (params) {
    // this goes through the pipeline of stages. what's in my head is similar
    // to the diagram on this page:
    // http://commons.apache.org/sandbox/commons-pipeline/pipeline_basics.html
    // however we want to keep the results of each stage, hence we manually call
    // each step rather than have them chain each other.
    var _this = this; // fallthrough in below switch is on purpose,
    // eg if STEP_FILTER, then all steps below this
    // step get done
    // let start: number;
    // console.log('======= start =======');


    var changedPath = this.createChangePath(params.rowNodeTransactions);

    switch (params.step) {
      case Constants.STEP_EVERYTHING:
        // start = new Date().getTime();
        this.doRowGrouping(params.groupState, params.rowNodeTransactions, params.rowNodeOrder, changedPath, params.afterColumnsChanged);
      // console.log('rowGrouping = ' + (new Date().getTime() - start));

      case Constants.STEP_FILTER:
        // start = new Date().getTime();
        this.doFilter(changedPath);
      // console.log('filter = ' + (new Date().getTime() - start));

      case Constants.STEP_PIVOT:
        this.doPivot(changedPath);

      case Constants.STEP_AGGREGATE:
        // depends on agg fields
        // start = new Date().getTime();
        this.doAggregate(changedPath);
      // console.log('aggregation = ' + (new Date().getTime() - start));

      case Constants.STEP_SORT:
        // start = new Date().getTime();
        this.doSort(params.rowNodeTransactions, changedPath);
      // console.log('sort = ' + (new Date().getTime() - start));

      case Constants.STEP_MAP:
        // start = new Date().getTime();
        this.doRowsToDisplay();
      // console.log('rowsToDisplay = ' + (new Date().getTime() - start));
    } // set all row tops to null, then set row tops on all visible rows. if we don't
    // do this, then the algorithm below only sets row tops, old row tops from old rows
    // will still lie around


    this.resetRowTops(this.rootNode, changedPath);
    this.setRowTops();
    var event = {
      type: Events.EVENT_MODEL_UPDATED,
      api: this.gridApi,
      columnApi: this.columnApi,
      animate: params.animate,
      keepRenderedRows: params.keepRenderedRows,
      newData: params.newData,
      newPage: false
    };
    this.eventService.dispatchEvent(event);

    if (this.$scope) {
      window.setTimeout(function () {
        _this.$scope.$apply();
      }, 0);
    }
  };

  ClientSideRowModel.prototype.isEmpty = function () {
    var rowsMissing;

    var doingLegacyTreeData = _.exists(this.gridOptionsWrapper.getNodeChildDetailsFunc());

    if (doingLegacyTreeData) {
      rowsMissing = _.missing(this.rootNode.childrenAfterGroup) || this.rootNode.childrenAfterGroup.length === 0;
    } else {
      rowsMissing = _.missing(this.rootNode.allLeafChildren) || this.rootNode.allLeafChildren.length === 0;
    }

    var empty = _.missing(this.rootNode) || rowsMissing || !this.columnController.isReady();
    return empty;
  };

  ClientSideRowModel.prototype.isRowsToRender = function () {
    return _.exists(this.rowsToDisplay) && this.rowsToDisplay.length > 0;
  };

  ClientSideRowModel.prototype.getNodesInRangeForSelection = function (firstInRange, lastInRange) {
    // if lastSelectedNode is missing, we start at the first row
    var firstRowHit = !lastInRange;
    var lastRowHit = false;
    var lastRow;
    var result = [];
    var groupsSelectChildren = this.gridOptionsWrapper.isGroupSelectsChildren();
    this.forEachNodeAfterFilterAndSort(function (rowNode) {
      var lookingForLastRow = firstRowHit && !lastRowHit; // check if we need to flip the select switch

      if (!firstRowHit) {
        if (rowNode === lastInRange || rowNode === firstInRange) {
          firstRowHit = true;
        }
      }

      var skipThisGroupNode = rowNode.group && groupsSelectChildren;

      if (!skipThisGroupNode) {
        var inRange = firstRowHit && !lastRowHit;
        var childOfLastRow = rowNode.isParentOfNode(lastRow);

        if (inRange || childOfLastRow) {
          result.push(rowNode);
        }
      }

      if (lookingForLastRow) {
        if (rowNode === lastInRange || rowNode === firstInRange) {
          lastRowHit = true;

          if (rowNode === lastInRange) {
            lastRow = lastInRange;
          } else {
            lastRow = firstInRange;
          }
        }
      }
    });
    return result;
  };

  ClientSideRowModel.prototype.setDatasource = function (datasource) {
    console.error('ag-Grid: should never call setDatasource on clientSideRowController');
  };

  ClientSideRowModel.prototype.getTopLevelNodes = function () {
    return this.rootNode ? this.rootNode.childrenAfterGroup : null;
  };

  ClientSideRowModel.prototype.getRootNode = function () {
    return this.rootNode;
  };

  ClientSideRowModel.prototype.getRow = function (index) {
    return this.rowsToDisplay[index];
  };

  ClientSideRowModel.prototype.isRowPresent = function (rowNode) {
    return this.rowsToDisplay.indexOf(rowNode) >= 0;
  };

  ClientSideRowModel.prototype.getRowIndexAtPixel = function (pixelToMatch) {
    if (this.isEmpty()) {
      return -1;
    } // do binary search of tree
    // http://oli.me.uk/2013/06/08/searching-javascript-arrays-with-a-binary-search/


    var bottomPointer = 0;
    var topPointer = this.rowsToDisplay.length - 1; // quick check, if the pixel is out of bounds, then return last row

    if (pixelToMatch <= 0) {
      // if pixel is less than or equal zero, it's always the first row
      return 0;
    }

    var lastNode = _.last(this.rowsToDisplay);

    if (lastNode.rowTop <= pixelToMatch) {
      return this.rowsToDisplay.length - 1;
    }

    while (true) {
      var midPointer = Math.floor((bottomPointer + topPointer) / 2);
      var currentRowNode = this.rowsToDisplay[midPointer];

      if (this.isRowInPixel(currentRowNode, pixelToMatch)) {
        return midPointer;
      } else if (currentRowNode.rowTop < pixelToMatch) {
        bottomPointer = midPointer + 1;
      } else if (currentRowNode.rowTop > pixelToMatch) {
        topPointer = midPointer - 1;
      }
    }
  };

  ClientSideRowModel.prototype.isRowInPixel = function (rowNode, pixelToMatch) {
    var topPixel = rowNode.rowTop;
    var bottomPixel = rowNode.rowTop + rowNode.rowHeight;
    var pixelInRow = topPixel <= pixelToMatch && bottomPixel > pixelToMatch;
    return pixelInRow;
  };

  ClientSideRowModel.prototype.getCurrentPageHeight = function () {
    if (this.rowsToDisplay && this.rowsToDisplay.length > 0) {
      var lastRow = _.last(this.rowsToDisplay);

      var lastPixel = lastRow.rowTop + lastRow.rowHeight;
      return lastPixel;
    } else {
      return 0;
    }
  };

  ClientSideRowModel.prototype.forEachLeafNode = function (callback) {
    if (this.rootNode.allLeafChildren) {
      this.rootNode.allLeafChildren.forEach(function (rowNode, index) {
        return callback(rowNode, index);
      });
    }
  };

  ClientSideRowModel.prototype.forEachNode = function (callback) {
    this.recursivelyWalkNodesAndCallback(this.rootNode.childrenAfterGroup, callback, RecursionType.Normal, 0);
  };

  ClientSideRowModel.prototype.forEachNodeAfterFilter = function (callback) {
    this.recursivelyWalkNodesAndCallback(this.rootNode.childrenAfterFilter, callback, RecursionType.AfterFilter, 0);
  };

  ClientSideRowModel.prototype.forEachNodeAfterFilterAndSort = function (callback) {
    this.recursivelyWalkNodesAndCallback(this.rootNode.childrenAfterSort, callback, RecursionType.AfterFilterAndSort, 0);
  };

  ClientSideRowModel.prototype.forEachPivotNode = function (callback) {
    this.recursivelyWalkNodesAndCallback([this.rootNode], callback, RecursionType.PivotNodes, 0);
  }; // iterates through each item in memory, and calls the callback function
  // nodes - the rowNodes to traverse
  // callback - the user provided callback
  // recursion type - need this to know what child nodes to recurse, eg if looking at all nodes, or filtered notes etc
  // index - works similar to the index in forEach in javascript's array function


  ClientSideRowModel.prototype.recursivelyWalkNodesAndCallback = function (nodes, callback, recursionType, index) {
    if (nodes) {
      for (var i = 0; i < nodes.length; i++) {
        var node = nodes[i];
        callback(node, index++); // go to the next level if it is a group

        if (node.hasChildren()) {
          // depending on the recursion type, we pick a difference set of children
          var nodeChildren = null;

          switch (recursionType) {
            case RecursionType.Normal:
              nodeChildren = node.childrenAfterGroup;
              break;

            case RecursionType.AfterFilter:
              nodeChildren = node.childrenAfterFilter;
              break;

            case RecursionType.AfterFilterAndSort:
              nodeChildren = node.childrenAfterSort;
              break;

            case RecursionType.PivotNodes:
              // for pivot, we don't go below leafGroup levels
              nodeChildren = !node.leafGroup ? node.childrenAfterSort : null;
              break;
          }

          if (nodeChildren) {
            index = this.recursivelyWalkNodesAndCallback(nodeChildren, callback, recursionType, index);
          }
        }
      }
    }

    return index;
  }; // it's possible to recompute the aggregate without doing the other parts
  // + gridApi.recomputeAggregates()


  ClientSideRowModel.prototype.doAggregate = function (changedPath) {
    if (this.aggregationStage) {
      this.aggregationStage.execute({
        rowNode: this.rootNode,
        changedPath: changedPath
      });
    }
  }; // + gridApi.expandAll()
  // + gridApi.collapseAll()


  ClientSideRowModel.prototype.expandOrCollapseAll = function (expand) {
    var usingTreeData = this.gridOptionsWrapper.isTreeData();

    if (this.rootNode) {
      recursiveExpandOrCollapse(this.rootNode.childrenAfterGroup);
    }

    function recursiveExpandOrCollapse(rowNodes) {
      if (!rowNodes) {
        return;
      }

      rowNodes.forEach(function (rowNode) {
        var shouldExpandOrCollapse = usingTreeData ? _.exists(rowNode.childrenAfterGroup) : rowNode.group;

        if (shouldExpandOrCollapse) {
          rowNode.expanded = expand;
          recursiveExpandOrCollapse(rowNode.childrenAfterGroup);
        }
      });
    }

    this.refreshModel({
      step: Constants.STEP_MAP
    });
    var eventSource = expand ? 'expandAll' : 'collapseAll';
    var event = {
      api: this.gridApi,
      columnApi: this.columnApi,
      type: Events.EVENT_EXPAND_COLLAPSE_ALL,
      source: eventSource
    };
    this.eventService.dispatchEvent(event);
  };

  ClientSideRowModel.prototype.doSort = function (rowNodeTransactions, changedPath) {
    this.sortStage.execute({
      rowNode: this.rootNode,
      rowNodeTransactions: rowNodeTransactions,
      changedPath: changedPath
    });
  };

  ClientSideRowModel.prototype.doRowGrouping = function (groupState, rowNodeTransactions, rowNodeOrder, changedPath, afterColumnsChanged) {
    // grouping is enterprise only, so if service missing, skip the step
    var doingLegacyTreeData = _.exists(this.gridOptionsWrapper.getNodeChildDetailsFunc());

    if (doingLegacyTreeData) {
      return;
    }

    if (this.groupStage) {
      if (rowNodeTransactions) {
        var merged_1 = {
          add: [],
          remove: [],
          update: []
        };
        rowNodeTransactions.forEach(function (tran) {
          _.pushAll(merged_1.add, tran.add);

          _.pushAll(merged_1.remove, tran.remove);

          _.pushAll(merged_1.update, tran.update);
        });
        this.groupStage.execute({
          rowNode: this.rootNode,
          rowNodeTransaction: merged_1,
          rowNodeOrder: rowNodeOrder,
          changedPath: changedPath
        });
      } else {
        // groups are about to get disposed, so need to deselect any that are selected
        this.selectionController.removeGroupsFromSelection();
        this.groupStage.execute({
          rowNode: this.rootNode,
          changedPath: changedPath,
          afterColumnsChanged: afterColumnsChanged
        }); // set open/closed state on groups

        this.restoreGroupState(groupState);
      }

      if (this.gridOptionsWrapper.isGroupSelectsChildren()) {
        this.selectionController.updateGroupsFromChildrenSelections(changedPath);
      }
    } else {
      this.rootNode.childrenAfterGroup = this.rootNode.allLeafChildren;
    }
  };

  ClientSideRowModel.prototype.restoreGroupState = function (groupState) {
    if (!groupState) {
      return;
    }

    _.traverseNodesWithKey(this.rootNode.childrenAfterGroup, function (node, key) {
      // if the group was open last time, then open it this time. however
      // if was not open last time, then don't touch the group, so the 'groupDefaultExpanded'
      // setting will take effect.
      if (typeof groupState[key] === 'boolean') {
        node.expanded = groupState[key];
      }
    });
  };

  ClientSideRowModel.prototype.doFilter = function (changedPath) {
    this.filterStage.execute({
      rowNode: this.rootNode,
      changedPath: changedPath
    });
  };

  ClientSideRowModel.prototype.doPivot = function (changedPath) {
    if (this.pivotStage) {
      this.pivotStage.execute({
        rowNode: this.rootNode,
        changedPath: changedPath
      });
    }
  };

  ClientSideRowModel.prototype.getGroupState = function () {
    if (!this.rootNode.childrenAfterGroup || !this.gridOptionsWrapper.isRememberGroupStateWhenNewData()) {
      return null;
    }

    var result = {};

    _.traverseNodesWithKey(this.rootNode.childrenAfterGroup, function (node, key) {
      return result[key] = node.expanded;
    });

    return result;
  };

  ClientSideRowModel.prototype.getCopyOfNodesMap = function () {
    return this.nodeManager.getCopyOfNodesMap();
  };

  ClientSideRowModel.prototype.getRowNode = function (id) {
    return this.nodeManager.getRowNode(id);
  }; // rows: the rows to put into the model


  ClientSideRowModel.prototype.setRowData = function (rowData) {
    // no need to invalidate cache, as the cache is stored on the rowNode,
    // so new rowNodes means the cache is wiped anyway.
    // remember group state, so we can expand groups that should be expanded
    var groupState = this.getGroupState();
    this.nodeManager.setRowData(rowData); // this event kicks off:
    // - clears selection
    // - updates filters
    // - shows 'no rows' overlay if needed

    var rowDataChangedEvent = {
      type: Events.EVENT_ROW_DATA_CHANGED,
      api: this.gridApi,
      columnApi: this.columnApi
    };
    this.eventService.dispatchEvent(rowDataChangedEvent);
    this.refreshModel({
      step: Constants.STEP_EVERYTHING,
      groupState: groupState,
      newData: true
    });
  };

  ClientSideRowModel.prototype.batchUpdateRowData = function (rowDataTransaction, callback) {
    var _this = this;

    if (!this.rowDataTransactionBatch) {
      this.rowDataTransactionBatch = [];
      var waitMillis = this.gridOptionsWrapper.getBatchUpdateWaitMillis();
      window.setTimeout(function () {
        _this.executeBatchUpdateRowData();

        _this.rowDataTransactionBatch = null;
      }, waitMillis);
    }

    this.rowDataTransactionBatch.push({
      rowDataTransaction: rowDataTransaction,
      callback: callback
    });
  };

  ClientSideRowModel.prototype.executeBatchUpdateRowData = function () {
    var _this = this;

    this.valueCache.onDataChanged();
    var callbackFuncsBound = [];
    var rowNodeTrans = [];

    if (this.rowDataTransactionBatch) {
      this.rowDataTransactionBatch.forEach(function (tranItem) {
        var rowNodeTran = _this.nodeManager.updateRowData(tranItem.rowDataTransaction, null);

        rowNodeTrans.push(rowNodeTran);

        if (tranItem.callback) {
          callbackFuncsBound.push(tranItem.callback.bind(null, rowNodeTran));
        }
      });
    }

    this.commonUpdateRowData(rowNodeTrans); // do callbacks in next VM turn so it's async

    if (callbackFuncsBound.length > 0) {
      window.setTimeout(function () {
        callbackFuncsBound.forEach(function (func) {
          return func();
        });
      }, 0);
    }
  };

  ClientSideRowModel.prototype.updateRowData = function (rowDataTran, rowNodeOrder) {
    this.valueCache.onDataChanged();
    var rowNodeTran = this.nodeManager.updateRowData(rowDataTran, rowNodeOrder);
    this.commonUpdateRowData([rowNodeTran], rowNodeOrder);
    return rowNodeTran;
  }; // common to updateRowData and batchUpdateRowData


  ClientSideRowModel.prototype.commonUpdateRowData = function (rowNodeTrans, rowNodeOrder) {
    this.refreshModel({
      step: Constants.STEP_EVERYTHING,
      rowNodeTransactions: rowNodeTrans,
      rowNodeOrder: rowNodeOrder,
      keepRenderedRows: true,
      animate: true,
      keepEditingRows: true
    });
    var event = {
      type: Events.EVENT_ROW_DATA_UPDATED,
      api: this.gridApi,
      columnApi: this.columnApi
    };
    this.eventService.dispatchEvent(event);
  };

  ClientSideRowModel.prototype.doRowsToDisplay = function () {
    this.rowsToDisplay = this.flattenStage.execute({
      rowNode: this.rootNode
    });
  };

  ClientSideRowModel.prototype.onRowHeightChanged = function () {
    this.refreshModel({
      step: Constants.STEP_MAP,
      keepRenderedRows: true,
      keepEditingRows: true
    });
  };

  ClientSideRowModel.prototype.resetRowHeights = function () {
    this.forEachNode(function (rowNode) {
      return rowNode.setRowHeight(null);
    });
    this.onRowHeightChanged();
  };

  __decorate$1L([Autowired('gridOptionsWrapper')], ClientSideRowModel.prototype, "gridOptionsWrapper", void 0);

  __decorate$1L([Autowired('columnController')], ClientSideRowModel.prototype, "columnController", void 0);

  __decorate$1L([Autowired('filterManager')], ClientSideRowModel.prototype, "filterManager", void 0);

  __decorate$1L([Autowired('$scope')], ClientSideRowModel.prototype, "$scope", void 0);

  __decorate$1L([Autowired('selectionController')], ClientSideRowModel.prototype, "selectionController", void 0);

  __decorate$1L([Autowired('eventService')], ClientSideRowModel.prototype, "eventService", void 0);

  __decorate$1L([Autowired('context')], ClientSideRowModel.prototype, "context", void 0);

  __decorate$1L([Autowired('valueService')], ClientSideRowModel.prototype, "valueService", void 0);

  __decorate$1L([Autowired('valueCache')], ClientSideRowModel.prototype, "valueCache", void 0);

  __decorate$1L([Autowired('columnApi')], ClientSideRowModel.prototype, "columnApi", void 0);

  __decorate$1L([Autowired('gridApi')], ClientSideRowModel.prototype, "gridApi", void 0);

  __decorate$1L([Autowired('filterStage')], ClientSideRowModel.prototype, "filterStage", void 0);

  __decorate$1L([Autowired('sortStage')], ClientSideRowModel.prototype, "sortStage", void 0);

  __decorate$1L([Autowired('flattenStage')], ClientSideRowModel.prototype, "flattenStage", void 0);

  __decorate$1L([Optional('groupStage')], ClientSideRowModel.prototype, "groupStage", void 0);

  __decorate$1L([Optional('aggregationStage')], ClientSideRowModel.prototype, "aggregationStage", void 0);

  __decorate$1L([Optional('pivotStage')], ClientSideRowModel.prototype, "pivotStage", void 0);

  __decorate$1L([PostConstruct], ClientSideRowModel.prototype, "init", null);

  ClientSideRowModel = __decorate$1L([Bean('rowModel')], ClientSideRowModel);
  return ClientSideRowModel;
}();

var __decorate$1M = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var FilterStage =
/** @class */
function () {
  function FilterStage() {}

  FilterStage.prototype.execute = function (params) {
    var rowNode = params.rowNode,
        changedPath = params.changedPath;
    this.filterService.filter(changedPath);
    this.selectableService.updateSelectableAfterFiltering(rowNode);
  };

  __decorate$1M([Autowired('gridOptionsWrapper')], FilterStage.prototype, "gridOptionsWrapper", void 0);

  __decorate$1M([Autowired('selectableService')], FilterStage.prototype, "selectableService", void 0);

  __decorate$1M([Autowired('filterService')], FilterStage.prototype, "filterService", void 0);

  FilterStage = __decorate$1M([Bean('filterStage')], FilterStage);
  return FilterStage;
}();

var __decorate$1N = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var SortStage =
/** @class */
function () {
  function SortStage() {}

  SortStage.prototype.execute = function (params) {
    var sortOptions = this.sortController.getSortForRowController();
    var sortActive = _.exists(sortOptions) && sortOptions.length > 0;
    var deltaSort = sortActive && _.exists(params.rowNodeTransactions) // in time we can remove this check, so that delta sort is always
    // on if transactions are present. it's off for now so that we can
    // selectively turn it on and test it with some select users before
    // rolling out to everyone.
    && this.gridOptionsWrapper.isDeltaSort(); // we only need dirty nodes if doing delta sort

    var dirtyLeafNodes = deltaSort ? this.calculateDirtyNodes(params.rowNodeTransactions) : null;
    var valueColumns = this.columnController.getValueColumns();

    var noAggregations = _.missingOrEmpty(valueColumns);

    this.sortService.sort(sortOptions, sortActive, deltaSort, dirtyLeafNodes, params.changedPath, noAggregations);
  };

  SortStage.prototype.calculateDirtyNodes = function (rowNodeTransactions) {
    var dirtyNodes = {};

    var addNodesFunc = function addNodesFunc(rowNodes) {
      if (rowNodes) {
        rowNodes.forEach(function (rowNode) {
          return dirtyNodes[rowNode.id] = true;
        });
      }
    }; // all leaf level nodes in the transaction were impacted


    rowNodeTransactions.forEach(function (tran) {
      addNodesFunc(tran.add);
      addNodesFunc(tran.update);
      addNodesFunc(tran.remove);
    });
    return dirtyNodes;
  };

  __decorate$1N([Autowired('gridOptionsWrapper')], SortStage.prototype, "gridOptionsWrapper", void 0);

  __decorate$1N([Autowired('sortService')], SortStage.prototype, "sortService", void 0);

  __decorate$1N([Autowired('sortController')], SortStage.prototype, "sortController", void 0);

  __decorate$1N([Autowired('columnController')], SortStage.prototype, "columnController", void 0);

  SortStage = __decorate$1N([Bean('sortStage')], SortStage);
  return SortStage;
}();

var __decorate$1O = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var FlattenStage =
/** @class */
function () {
  function FlattenStage() {}

  FlattenStage.prototype.execute = function (params) {
    var rootNode = params.rowNode; // even if not doing grouping, we do the mapping, as the client might
    // of passed in data that already has a grouping in it somewhere

    var result = []; // putting value into a wrapper so it's passed by reference

    var nextRowTop = {
      value: 0
    };
    var skipLeafNodes = this.columnController.isPivotMode(); // if we are reducing, and not grouping, then we want to show the root node, as that
    // is where the pivot values are

    var showRootNode = skipLeafNodes && rootNode.leafGroup;
    var topList = showRootNode ? [rootNode] : rootNode.childrenAfterSort;
    this.recursivelyAddToRowsToDisplay(topList, result, nextRowTop, skipLeafNodes, 0); // we do not want the footer total if the gris is empty

    var atLeastOneRowPresent = result.length > 0;
    var includeGroupTotalFooter = !showRootNode // don't show total footer when showRootNode is true (i.e. in pivot mode and no groups)
    && atLeastOneRowPresent && this.gridOptionsWrapper.isGroupIncludeTotalFooter();

    if (includeGroupTotalFooter) {
      this.ensureFooterNodeExists(rootNode);
      this.addRowNodeToRowsToDisplay(rootNode.sibling, result, nextRowTop, 0);
    }

    return result;
  };

  FlattenStage.prototype.recursivelyAddToRowsToDisplay = function (rowsToFlatten, result, nextRowTop, skipLeafNodes, uiLevel) {
    if (_.missingOrEmpty(rowsToFlatten)) {
      return;
    }

    var groupSuppressRow = this.gridOptionsWrapper.isGroupSuppressRow();
    var hideOpenParents = this.gridOptionsWrapper.isGroupHideOpenParents(); // these two are mutually exclusive, so if first set, we don't set the second

    var groupRemoveSingleChildren = this.gridOptionsWrapper.isGroupRemoveSingleChildren();
    var groupRemoveLowestSingleChildren = !groupRemoveSingleChildren && this.gridOptionsWrapper.isGroupRemoveLowestSingleChildren();

    for (var i = 0; i < rowsToFlatten.length; i++) {
      var rowNode = rowsToFlatten[i]; // check all these cases, for working out if this row should be included in the final mapped list

      var isParent = rowNode.hasChildren();
      var isGroupSuppressedNode = groupSuppressRow && isParent;
      var isSkippedLeafNode = skipLeafNodes && !isParent;
      var isRemovedSingleChildrenGroup = groupRemoveSingleChildren && isParent && rowNode.childrenAfterGroup.length === 1;
      var isRemovedLowestSingleChildrenGroup = groupRemoveLowestSingleChildren && isParent && rowNode.leafGroup && rowNode.childrenAfterGroup.length === 1; // hide open parents means when group is open, we don't show it. we also need to make sure the
      // group is expandable in the first place (as leaf groups are not expandable if pivot mode is on).
      // the UI will never allow expanding leaf  groups, however the user might via the API (or menu option 'expand all')

      var neverAllowToExpand = skipLeafNodes && rowNode.leafGroup;
      var isHiddenOpenParent = hideOpenParents && rowNode.expanded && !neverAllowToExpand;
      var thisRowShouldBeRendered = !isSkippedLeafNode && !isGroupSuppressedNode && !isHiddenOpenParent && !isRemovedSingleChildrenGroup && !isRemovedLowestSingleChildrenGroup;

      if (thisRowShouldBeRendered) {
        this.addRowNodeToRowsToDisplay(rowNode, result, nextRowTop, uiLevel);
      } // if we are pivoting, we never map below the leaf group


      if (skipLeafNodes && rowNode.leafGroup) {
        continue;
      }

      if (isParent) {
        var excludedParent = isRemovedSingleChildrenGroup || isRemovedLowestSingleChildrenGroup; // we traverse the group if it is expended, however we always traverse if the parent node
        // was removed (as the group will never be opened if it is not displayed, we show the children instead)

        if (rowNode.expanded || excludedParent) {
          // if the parent was excluded, then ui level is that of the parent
          var uiLevelForChildren = excludedParent ? uiLevel : uiLevel + 1;
          this.recursivelyAddToRowsToDisplay(rowNode.childrenAfterSort, result, nextRowTop, skipLeafNodes, uiLevelForChildren); // put a footer in if user is looking for it

          if (this.gridOptionsWrapper.isGroupIncludeFooter()) {
            this.ensureFooterNodeExists(rowNode);
            this.addRowNodeToRowsToDisplay(rowNode.sibling, result, nextRowTop, uiLevel);
          }
        }
      } else if (rowNode.master && rowNode.expanded) {
        var detailNode = this.createDetailNode(rowNode);
        this.addRowNodeToRowsToDisplay(detailNode, result, nextRowTop, uiLevel);
      }
    }
  }; // duplicated method, it's also in floatingRowModel


  FlattenStage.prototype.addRowNodeToRowsToDisplay = function (rowNode, result, nextRowTop, uiLevel) {
    result.push(rowNode);
    var isGroupMultiAutoColumn = this.gridOptionsWrapper.isGroupMultiAutoColumn();
    rowNode.setUiLevel(isGroupMultiAutoColumn ? 0 : uiLevel);
  };

  FlattenStage.prototype.ensureFooterNodeExists = function (groupNode) {
    // only create footer node once, otherwise we have daemons and
    // the animate screws up with the daemons hanging around
    if (_.exists(groupNode.sibling)) {
      return;
    }

    var footerNode = new RowNode();
    this.context.wireBean(footerNode);
    Object.keys(groupNode).forEach(function (key) {
      footerNode[key] = groupNode[key];
    });
    footerNode.footer = true;
    footerNode.rowTop = null;
    footerNode.oldRowTop = null;

    if (_.exists(footerNode.id)) {
      footerNode.id = 'rowGroupFooter_' + footerNode.id;
    } // get both header and footer to reference each other as siblings. this is never undone,
    // only overwritten. so if a group is expanded, then contracted, it will have a ghost
    // sibling - but that's fine, as we can ignore this if the header is contracted.


    footerNode.sibling = groupNode;
    groupNode.sibling = footerNode;
  };

  FlattenStage.prototype.createDetailNode = function (masterNode) {
    if (_.exists(masterNode.detailNode)) {
      return masterNode.detailNode;
    } else {
      var detailNode = new RowNode();
      this.context.wireBean(detailNode);
      detailNode.detail = true;
      detailNode.selectable = false; // flower was renamed to 'detail', but keeping for backwards compatibility

      detailNode.flower = detailNode.detail;
      detailNode.parent = masterNode;

      if (_.exists(masterNode.id)) {
        detailNode.id = 'detail_' + masterNode.id;
      }

      detailNode.data = masterNode.data;
      detailNode.level = masterNode.level + 1;
      masterNode.detailNode = detailNode;
      masterNode.childFlower = masterNode.detailNode; // for backwards compatibility

      return detailNode;
    }
  };

  __decorate$1O([Autowired('gridOptionsWrapper')], FlattenStage.prototype, "gridOptionsWrapper", void 0);

  __decorate$1O([Autowired('selectionController')], FlattenStage.prototype, "selectionController", void 0);

  __decorate$1O([Autowired('eventService')], FlattenStage.prototype, "eventService", void 0);

  __decorate$1O([Autowired('context')], FlattenStage.prototype, "context", void 0);

  __decorate$1O([Autowired('columnController')], FlattenStage.prototype, "columnController", void 0);

  FlattenStage = __decorate$1O([Bean('flattenStage')], FlattenStage);
  return FlattenStage;
}();

var __decorate$1P = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var SortService =
/** @class */
function () {
  function SortService() {}

  SortService.prototype.init = function () {
    this.postSortFunc = this.gridOptionsWrapper.getPostSortFunc();
  };

  SortService.prototype.sort = function (sortOptions, sortActive, deltaSort, dirtyLeafNodes, changedPath, noAggregations) {
    var _this = this;

    var callback = function callback(rowNode) {
      // we clear out the 'pull down open parents' first, as the values mix up the sorting
      _this.pullDownGroupDataForHideOpenParents(rowNode.childrenAfterFilter, true); // Javascript sort is non deterministic when all the array items are equals, ie Comparator always returns 0,
      // so to ensure the array keeps its order, add an additional sorting condition manually, in this case we
      // are going to inspect the original array position. This is what sortedRowNodes is for.


      if (sortActive) {
        var sortedRowNodes = deltaSort ? _this.doDeltaSort(rowNode, sortOptions, dirtyLeafNodes, changedPath, noAggregations) : _this.doFullSort(rowNode, sortOptions);
        rowNode.childrenAfterSort = sortedRowNodes.map(function (sorted) {
          return sorted.rowNode;
        });
      } else {
        rowNode.childrenAfterSort = rowNode.childrenAfterFilter.slice(0);
      }

      _this.updateChildIndexes(rowNode);

      if (_this.postSortFunc) {
        _this.postSortFunc(rowNode.childrenAfterSort);
      }
    };

    changedPath.forEachChangedNodeDepthFirst(callback);
    this.updateGroupDataForHiddenOpenParents(changedPath);
  };

  SortService.prototype.doFullSort = function (rowNode, sortOptions) {
    var sortedRowNodes = rowNode.childrenAfterFilter.map(this.mapNodeToSortedNode.bind(this));
    sortedRowNodes.sort(this.compareRowNodes.bind(this, sortOptions));
    return sortedRowNodes;
  };

  SortService.prototype.mapNodeToSortedNode = function (rowNode, pos) {
    return {
      currentPos: pos,
      rowNode: rowNode
    };
  };

  SortService.prototype.doDeltaSort = function (rowNode, sortOptions, dirtyLeafNodes, changedPath, noAggregations) {
    // clean nodes will be a list of all row nodes that remain in the set
    // and ordered. we start with the old sorted set and take out any nodes
    // that were removed or changed (but not added, added doesn't make sense,
    // if a node was added, there is no way it could be here from last time).
    var cleanNodes = rowNode.childrenAfterSort.filter(function (rowNode) {
      // take out all nodes that were changed as part of the current transaction.
      // a changed node could a) be in a different sort position or b) may
      // no longer be in this set as the changed node may not pass filtering,
      // or be in a different group.
      var passesDirtyNodesCheck = !dirtyLeafNodes[rowNode.id]; // also remove group nodes in the changed path, as they can have different aggregate
      // values which could impact the sort order.
      // note: changed path is not active if a) no value columns or b) no transactions. it is never
      // (b) in deltaSort as we only do deltaSort for transactions. for (a) if no value columns, then
      // there is no value in the group that could of changed (ie no aggregate values)

      var passesChangedPathCheck = noAggregations || changedPath.canSkip(rowNode);
      return passesDirtyNodesCheck && passesChangedPathCheck;
    }).map(this.mapNodeToSortedNode.bind(this)); // for fast access below, we map them

    var cleanNodesMapped = {};
    cleanNodes.forEach(function (sortedRowNode) {
      return cleanNodesMapped[sortedRowNode.rowNode.id] = sortedRowNode.rowNode;
    }); // these are all nodes that need to be placed

    var changedNodes = rowNode.childrenAfterFilter // ignore nodes in the clean list
    .filter(function (rowNode) {
      return !cleanNodesMapped[rowNode.id];
    }).map(this.mapNodeToSortedNode.bind(this)); // sort changed nodes. note that we don't need to sort cleanNodes as they are
    // already sorted from last time.

    changedNodes.sort(this.compareRowNodes.bind(this, sortOptions));

    if (changedNodes.length === 0) {
      return cleanNodes;
    } else if (cleanNodes.length === 0) {
      return changedNodes;
    } else {
      return this.mergeSortedArrays(sortOptions, cleanNodes, changedNodes);
    }
  }; // Merge two sorted arrays into each other


  SortService.prototype.mergeSortedArrays = function (sortOptions, arr1, arr2) {
    var res = [];
    var i = 0;
    var j = 0; // Traverse both array, adding them in order

    while (i < arr1.length && j < arr2.length) {
      // Check if current element of first
      // array is smaller than current element
      // of second array. If yes, store first
      // array element and increment first array
      // index. Otherwise do same with second array
      var compareResult = this.compareRowNodes(sortOptions, arr1[i], arr2[j]);

      if (compareResult < 0) {
        res.push(arr1[i++]);
      } else {
        res.push(arr2[j++]);
      }
    } // add remaining from arr1


    while (i < arr1.length) {
      res.push(arr1[i++]);
    } // add remaining from arr2


    while (j < arr2.length) {
      res.push(arr2[j++]);
    }

    return res;
  };

  SortService.prototype.compareRowNodes = function (sortOptions, sortedNodeA, sortedNodeB) {
    var nodeA = sortedNodeA.rowNode;
    var nodeB = sortedNodeB.rowNode; // Iterate columns, return the first that doesn't match

    for (var i = 0, len = sortOptions.length; i < len; i++) {
      var sortOption = sortOptions[i]; // let compared = compare(nodeA, nodeB, sortOption.column, sortOption.inverter === -1);

      var isInverted = sortOption.inverter === -1;
      var valueA = this.getValue(nodeA, sortOption.column);
      var valueB = this.getValue(nodeB, sortOption.column);
      var comparatorResult = void 0;

      if (sortOption.column.getColDef().comparator) {
        //if comparator provided, use it
        comparatorResult = sortOption.column.getColDef().comparator(valueA, valueB, nodeA, nodeB, isInverted);
      } else {
        //otherwise do our own comparison
        comparatorResult = _.defaultComparator(valueA, valueB, this.gridOptionsWrapper.isAccentedSort());
      }

      if (comparatorResult !== 0) {
        return comparatorResult * sortOption.inverter;
      }
    } // All matched, we make is so that the original sort order is kept:


    return sortedNodeA.currentPos - sortedNodeB.currentPos;
  };

  SortService.prototype.getValue = function (nodeA, column) {
    return this.valueService.getValue(column, nodeA);
  };

  SortService.prototype.updateChildIndexes = function (rowNode) {
    if (_.missing(rowNode.childrenAfterSort)) {
      return;
    }

    var listToSort = rowNode.childrenAfterSort;

    for (var i = 0; i < listToSort.length; i++) {
      var child = listToSort[i];
      var firstChild = i === 0;
      var lastChild = i === rowNode.childrenAfterSort.length - 1;
      child.setFirstChild(firstChild);
      child.setLastChild(lastChild);
      child.setChildIndex(i);
    }
  };

  SortService.prototype.updateGroupDataForHiddenOpenParents = function (changedPath) {
    var _this = this;

    if (!this.gridOptionsWrapper.isGroupHideOpenParents()) {
      return;
    } // recurse breadth first over group nodes after sort to 'pull down' group data to child groups


    var callback = function callback(rowNode) {
      _this.pullDownGroupDataForHideOpenParents(rowNode.childrenAfterSort, false);

      rowNode.childrenAfterSort.forEach(function (child) {
        if (child.hasChildren()) {
          callback(child);
        }
      });
    };

    changedPath.executeFromRootNode(function (rowNode) {
      return callback(rowNode);
    });
  };

  SortService.prototype.pullDownGroupDataForHideOpenParents = function (rowNodes, clearOperation) {
    var _this = this;

    if (_.missing(rowNodes)) {
      return;
    }

    if (!this.gridOptionsWrapper.isGroupHideOpenParents()) {
      return;
    }

    rowNodes.forEach(function (childRowNode) {
      var groupDisplayCols = _this.columnController.getGroupDisplayColumns();

      groupDisplayCols.forEach(function (groupDisplayCol) {
        var showRowGroup = groupDisplayCol.getColDef().showRowGroup;

        if (typeof showRowGroup !== 'string') {
          console.error('ag-Grid: groupHideOpenParents only works when specifying specific columns for colDef.showRowGroup');
          return;
        }

        var displayingGroupKey = showRowGroup;

        var rowGroupColumn = _this.columnController.getPrimaryColumn(displayingGroupKey);

        var thisRowNodeMatches = rowGroupColumn === childRowNode.rowGroupColumn;

        if (thisRowNodeMatches) {
          return;
        }

        if (clearOperation) {
          // if doing a clear operation, we clear down the value for every possible group column
          childRowNode.setGroupValue(groupDisplayCol.getId(), null);
        } else {
          // if doing a set operation, we set only where the pull down is to occur
          var parentToStealFrom = childRowNode.getFirstChildOfFirstChild(rowGroupColumn);

          if (parentToStealFrom) {
            childRowNode.setGroupValue(groupDisplayCol.getId(), parentToStealFrom.key);
          }
        }
      });
    });
  };

  __decorate$1P([Autowired('sortController')], SortService.prototype, "sortController", void 0);

  __decorate$1P([Autowired('columnController')], SortService.prototype, "columnController", void 0);

  __decorate$1P([Autowired('valueService')], SortService.prototype, "valueService", void 0);

  __decorate$1P([Autowired('gridOptionsWrapper')], SortService.prototype, "gridOptionsWrapper", void 0);

  __decorate$1P([PostConstruct], SortService.prototype, "init", null);

  SortService = __decorate$1P([Bean('sortService')], SortService);
  return SortService;
}();

var __decorate$1Q = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var FilterService =
/** @class */
function () {
  function FilterService() {}

  FilterService.prototype.postConstruct = function () {
    this.doingTreeData = this.gridOptionsWrapper.isTreeData();
  };

  FilterService.prototype.filter = function (changedPath) {
    var filterActive = this.filterManager.isAnyFilterPresent();
    this.filterNodes(filterActive, changedPath);
  };

  FilterService.prototype.filterNodes = function (filterActive, changedPath) {
    var _this = this;

    var filterCallback = function filterCallback(rowNode, includeChildNodes) {
      // recursively get all children that are groups to also filter
      if (rowNode.hasChildren()) {
        // result of filter for this node. when filtering tree data, includeChildNodes = true when parent passes
        if (filterActive && !includeChildNodes) {
          rowNode.childrenAfterFilter = rowNode.childrenAfterGroup.filter(function (childNode) {
            // a group is included in the result if it has any children of it's own.
            // by this stage, the child groups are already filtered
            var passBecauseChildren = childNode.childrenAfterFilter && childNode.childrenAfterFilter.length > 0; // both leaf level nodes and tree data nodes have data. these get added if
            // the data passes the filter

            var passBecauseDataPasses = childNode.data && _this.filterManager.doesRowPassFilter(childNode); // note - tree data nodes pass either if a) they pass themselves or b) any children of that node pass


            return passBecauseChildren || passBecauseDataPasses;
          });
        } else {
          // if not filtering, the result is the original list
          rowNode.childrenAfterFilter = rowNode.childrenAfterGroup;
        }

        _this.setAllChildrenCount(rowNode);
      } else {
        rowNode.childrenAfterFilter = rowNode.childrenAfterGroup;
        rowNode.setAllChildrenCount(null);
      }
    };

    if (this.doingTreeDataFiltering()) {
      var treeDataDepthFirstFilter_1 = function treeDataDepthFirstFilter_1(rowNode, alreadyFoundInParent) {
        // tree data filter traverses the hierarchy depth first and includes child nodes if parent passes
        // filter, and parent nodes will be include if any children exist.
        if (rowNode.childrenAfterGroup) {
          for (var i = 0; i < rowNode.childrenAfterGroup.length; i++) {
            var childNode = rowNode.childrenAfterGroup[i]; // first check if current node passes filter before invoking child nodes

            var foundInParent = alreadyFoundInParent || _this.filterManager.doesRowPassFilter(childNode);

            if (childNode.childrenAfterGroup) {
              treeDataDepthFirstFilter_1(rowNode.childrenAfterGroup[i], foundInParent);
            } else {
              filterCallback(childNode, foundInParent);
            }
          }
        }

        filterCallback(rowNode, alreadyFoundInParent);
      };

      var treeDataFilterCallback = function treeDataFilterCallback(rowNode) {
        return treeDataDepthFirstFilter_1(rowNode, false);
      };

      changedPath.executeFromRootNode(treeDataFilterCallback);
    } else {
      var defaultFilterCallback = function defaultFilterCallback(rowNode) {
        return filterCallback(rowNode, false);
      };

      changedPath.forEachChangedNodeDepthFirst(defaultFilterCallback, true);
    }
  };

  FilterService.prototype.setAllChildrenCountTreeData = function (rowNode) {
    // for tree data, we include all children, groups and leafs
    var allChildrenCount = 0;
    rowNode.childrenAfterFilter.forEach(function (child) {
      // include child itself
      allChildrenCount++; // include children of children

      allChildrenCount += child.allChildrenCount;
    });
    rowNode.setAllChildrenCount(allChildrenCount);
  };

  FilterService.prototype.setAllChildrenCountGridGrouping = function (rowNode) {
    // for grid data, we only count the leafs
    var allChildrenCount = 0;
    rowNode.childrenAfterFilter.forEach(function (child) {
      if (child.group) {
        allChildrenCount += child.allChildrenCount;
      } else {
        allChildrenCount++;
      }
    });
    rowNode.setAllChildrenCount(allChildrenCount);
  };

  FilterService.prototype.setAllChildrenCount = function (rowNode) {
    if (this.doingTreeData) {
      this.setAllChildrenCountTreeData(rowNode);
    } else {
      this.setAllChildrenCountGridGrouping(rowNode);
    }
  };

  FilterService.prototype.doingTreeDataFiltering = function () {
    return this.gridOptionsWrapper.isTreeData() && !this.gridOptionsWrapper.isExcludeChildrenWhenTreeDataFiltering();
  };

  __decorate$1Q([Autowired('filterManager')], FilterService.prototype, "filterManager", void 0);

  __decorate$1Q([Autowired('gridOptionsWrapper')], FilterService.prototype, "gridOptionsWrapper", void 0);

  __decorate$1Q([PostConstruct], FilterService.prototype, "postConstruct", null);

  FilterService = __decorate$1Q([Bean("filterService")], FilterService);
  return FilterService;
}();

var __decorate$1R = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var ImmutableService =
/** @class */
function () {
  function ImmutableService() {}

  ImmutableService.prototype.postConstruct = function () {
    if (this.rowModel.getType() === Constants.ROW_MODEL_TYPE_CLIENT_SIDE) {
      this.clientSideRowModel = this.rowModel;
    }
  }; // converts the setRowData() command to a transaction


  ImmutableService.prototype.createTransactionForRowData = function (data) {
    if (_.missing(this.clientSideRowModel)) {
      console.error('ag-Grid: ImmutableService only works with ClientSideRowModel');
      return;
    }

    var getRowNodeIdFunc = this.gridOptionsWrapper.getRowNodeIdFunc();

    if (!getRowNodeIdFunc || _.missing(getRowNodeIdFunc)) {
      console.error('ag-Grid: ImmutableService requires getRowNodeId() callback to be implemented, your row data need IDs!');
      return;
    } // convert the data into a transaction object by working out adds, removes and updates


    var transaction = {
      remove: [],
      update: [],
      add: []
    };
    var existingNodesMap = this.clientSideRowModel.getCopyOfNodesMap();
    var suppressSortOrder = this.gridOptionsWrapper.isSuppressMaintainUnsortedOrder();
    var orderMap = suppressSortOrder ? null : {};

    if (_.exists(data)) {
      // split all the new data in the following:
      // if new, push to 'add'
      // if update, push to 'update'
      // if not changed, do not include in the transaction
      data.forEach(function (dataItem, index) {
        var id = getRowNodeIdFunc(dataItem);
        var existingNode = existingNodesMap[id];

        if (orderMap) {
          orderMap[id] = index;
        }

        if (existingNode) {
          var dataHasChanged = existingNode.data !== dataItem;

          if (dataHasChanged) {
            transaction.update.push(dataItem);
          } // otherwise, if data not changed, we just don't include it anywhere, as it's not a delta
          // remove from list, so we know the item is not to be removed


          existingNodesMap[id] = undefined;
        } else {
          transaction.add.push(dataItem);
        }
      });
    } // at this point, all rows that are left, should be removed


    _.iterateObject(existingNodesMap, function (id, rowNode) {
      if (rowNode) {
        transaction.remove.push(rowNode.data);
      }
    });

    return [transaction, orderMap];
  };

  __decorate$1R([Autowired('rowModel')], ImmutableService.prototype, "rowModel", void 0);

  __decorate$1R([Autowired('gridOptionsWrapper')], ImmutableService.prototype, "gridOptionsWrapper", void 0);

  __decorate$1R([PostConstruct], ImmutableService.prototype, "postConstruct", null);

  ImmutableService = __decorate$1R([Bean('immutableService')], ImmutableService);
  return ImmutableService;
}();

var ClientSideRowModelModule = {
  moduleName: exports.ModuleNames.ClientSideRowModelModule,
  beans: [FilterStage, SortStage, FlattenStage, SortService, FilterService, ImmutableService],
  rowModels: {
    clientSide: ClientSideRowModel
  }
};

var __extends$1d = undefined && undefined.__extends || function () {
  var _extendStatics78 = function extendStatics(d, b) {
    _extendStatics78 = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics78(d, b);
  };

  return function (d, b) {
    _extendStatics78(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var __decorate$1S = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var InfiniteBlock =
/** @class */
function (_super) {
  __extends$1d(InfiniteBlock, _super);

  function InfiniteBlock(pageNumber, params) {
    var _this = _super.call(this, pageNumber, params) || this;

    _this.cacheParams = params;
    return _this;
  }

  InfiniteBlock.prototype.createBlankRowNode = function (rowIndex) {
    var rowNode = _super.prototype.createBlankRowNode.call(this, rowIndex);

    rowNode.uiLevel = 0;
    this.setIndexAndTopOnRowNode(rowNode, rowIndex);
    return rowNode;
  };

  InfiniteBlock.prototype.setDataAndId = function (rowNode, data, index) {
    if (_.exists(data)) {
      // this means if the user is not providing id's we just use the
      // index for the row. this will allow selection to work (that is based
      // on index) as long user is not inserting or deleting rows,
      // or wanting to keep selection between server side sorting or filtering
      rowNode.setDataAndId(data, index.toString());
    } else {
      rowNode.setDataAndId(undefined, undefined);
    }
  };

  InfiniteBlock.prototype.setRowNode = function (rowIndex, rowNode) {
    _super.prototype.setRowNode.call(this, rowIndex, rowNode);

    this.setIndexAndTopOnRowNode(rowNode, rowIndex);
  };

  InfiniteBlock.prototype.init = function () {
    _super.prototype.init.call(this, {
      context: this.getContext(),
      rowRenderer: this.rowRenderer
    });
  };

  InfiniteBlock.prototype.getNodeIdPrefix = function () {
    return null;
  };

  InfiniteBlock.prototype.getRow = function (displayIndex) {
    return this.getRowUsingLocalIndex(displayIndex);
  };

  InfiniteBlock.prototype.setIndexAndTopOnRowNode = function (rowNode, rowIndex) {
    rowNode.setRowIndex(rowIndex);
    rowNode.rowTop = this.cacheParams.rowHeight * rowIndex;
  };

  InfiniteBlock.prototype.loadFromDatasource = function () {
    var _this = this; // PROBLEM . . . . when the user sets sort via colDef.sort, then this code
    // is executing before the sort is set up, so server is not getting the sort
    // model. need to change with regards order - so the server side request is
    // AFTER thus it gets the right sort model.


    var params = {
      startRow: this.getStartRow(),
      endRow: this.getEndRow(),
      successCallback: this.pageLoaded.bind(this, this.getVersion()),
      failCallback: this.pageLoadFailed.bind(this),
      sortModel: this.cacheParams.sortModel,
      filterModel: this.cacheParams.filterModel,
      context: this.gridOptionsWrapper.getContext()
    };

    if (_.missing(this.cacheParams.datasource.getRows)) {
      console.warn("ag-Grid: datasource is missing getRows method");
      return;
    } // put in timeout, to force result to be async


    window.setTimeout(function () {
      _this.cacheParams.datasource.getRows(params);
    }, 0);
  };

  __decorate$1S([Autowired('gridOptionsWrapper')], InfiniteBlock.prototype, "gridOptionsWrapper", void 0);

  __decorate$1S([Autowired('rowRenderer')], InfiniteBlock.prototype, "rowRenderer", void 0);

  __decorate$1S([PostConstruct], InfiniteBlock.prototype, "init", null);

  return InfiniteBlock;
}(RowNodeBlock);

var __extends$1e = undefined && undefined.__extends || function () {
  var _extendStatics79 = function extendStatics(d, b) {
    _extendStatics79 = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics79(d, b);
  };

  return function (d, b) {
    _extendStatics79(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var __decorate$1T = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var __param$b = undefined && undefined.__param || function (paramIndex, decorator) {
  return function (target, key) {
    decorator(target, key, paramIndex);
  };
};

var InfiniteCache =
/** @class */
function (_super) {
  __extends$1e(InfiniteCache, _super);

  function InfiniteCache(params) {
    return _super.call(this, params) || this;
  }

  InfiniteCache.prototype.setBeans = function (loggerFactory) {
    this.logger = loggerFactory.create('InfiniteCache');
  };

  InfiniteCache.prototype.init = function () {
    _super.prototype.init.call(this); // start load of data, as the virtualRowCount will remain at 0 otherwise,
    // so we need this to kick things off, otherwise grid would never call getRow()


    this.getRow(0);
  };

  InfiniteCache.prototype.moveItemsDown = function (block, moveFromIndex, moveCount) {
    var startRow = block.getStartRow();
    var endRow = block.getEndRow();
    var indexOfLastRowToMove = moveFromIndex + moveCount; // all rows need to be moved down below the insertion index

    for (var currentRowIndex = endRow - 1; currentRowIndex >= startRow; currentRowIndex--) {
      // don't move rows at or before the insertion index
      if (currentRowIndex < indexOfLastRowToMove) {
        continue;
      }

      var indexOfNodeWeWant = currentRowIndex - moveCount;
      var nodeForThisIndex = this.getRow(indexOfNodeWeWant, true);

      if (nodeForThisIndex) {
        block.setRowNode(currentRowIndex, nodeForThisIndex);
      } else {
        block.setBlankRowNode(currentRowIndex);
        block.setDirty();
      }
    }
  };

  InfiniteCache.prototype.insertItems = function (block, indexToInsert, items) {
    var pageStartRow = block.getStartRow();
    var pageEndRow = block.getEndRow();
    var newRowNodes = []; // next stage is insert the rows into this page, if applicable

    for (var index = 0; index < items.length; index++) {
      var rowIndex = indexToInsert + index;
      var currentRowInThisPage = rowIndex >= pageStartRow && rowIndex < pageEndRow;

      if (currentRowInThisPage) {
        var dataItem = items[index];
        var newRowNode = block.setNewData(rowIndex, dataItem);
        newRowNodes.push(newRowNode);
      }
    }

    return newRowNodes;
  };

  InfiniteCache.prototype.insertItemsAtIndex = function (indexToInsert, items) {
    // get all page id's as NUMBERS (not strings, as we need to sort as numbers) and in descending order
    var _this = this;

    var newNodes = [];
    this.forEachBlockInReverseOrder(function (block) {
      var pageEndRow = block.getEndRow(); // if the insertion is after this page, then this page is not impacted

      if (pageEndRow <= indexToInsert) {
        return;
      }

      _this.moveItemsDown(block, indexToInsert, items.length);

      var newNodesThisPage = _this.insertItems(block, indexToInsert, items);

      newNodesThisPage.forEach(function (rowNode) {
        return newNodes.push(rowNode);
      });
    });

    if (this.isMaxRowFound()) {
      this.hack_setVirtualRowCount(this.getVirtualRowCount() + items.length);
    }

    this.onCacheUpdated();
    var event = {
      type: Events.EVENT_ROW_DATA_UPDATED,
      api: this.gridApi,
      columnApi: this.columnApi
    };
    this.eventService.dispatchEvent(event);
  }; // the rowRenderer will not pass dontCreatePage, meaning when rendering the grid,
  // it will want new pages in the cache as it asks for rows. only when we are inserting /
  // removing rows via the api is dontCreatePage set, where we move rows between the pages.


  InfiniteCache.prototype.getRow = function (rowIndex, dontCreatePage) {
    if (dontCreatePage === void 0) {
      dontCreatePage = false;
    }

    var blockId = Math.floor(rowIndex / this.cacheParams.blockSize);
    var block = this.getBlock(blockId);

    if (!block) {
      if (dontCreatePage) {
        return null;
      } else {
        block = this.createBlock(blockId);
      }
    }

    return block.getRow(rowIndex);
  };

  InfiniteCache.prototype.createBlock = function (blockNumber) {
    var newBlock = new InfiniteBlock(blockNumber, this.cacheParams);
    this.getContext().wireBean(newBlock);
    this.postCreateBlock(newBlock);
    return newBlock;
  }; // we have this on infinite row model only, not server side row model,
  // because for server side, it would leave the children in inconsistent
  // state - eg if a node had children, but after the refresh it had data
  // for a different row, then the children would be with the wrong row node.


  InfiniteCache.prototype.refreshCache = function () {
    this.forEachBlockInOrder(function (block) {
      return block.setDirty();
    });
    this.checkBlockToLoad();
  };

  __decorate$1T([Autowired('eventService')], InfiniteCache.prototype, "eventService", void 0);

  __decorate$1T([Autowired('columnApi')], InfiniteCache.prototype, "columnApi", void 0);

  __decorate$1T([Autowired('gridApi')], InfiniteCache.prototype, "gridApi", void 0);

  __decorate$1T([__param$b(0, Qualifier('loggerFactory'))], InfiniteCache.prototype, "setBeans", null);

  __decorate$1T([PostConstruct], InfiniteCache.prototype, "init", null);

  return InfiniteCache;
}(RowNodeCache);

var __extends$1f = undefined && undefined.__extends || function () {
  var _extendStatics80 = function extendStatics(d, b) {
    _extendStatics80 = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics80(d, b);
  };

  return function (d, b) {
    _extendStatics80(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var __decorate$1U = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var InfiniteRowModel =
/** @class */
function (_super) {
  __extends$1f(InfiniteRowModel, _super);

  function InfiniteRowModel() {
    return _super !== null && _super.apply(this, arguments) || this;
  }

  InfiniteRowModel.prototype.getRowBounds = function (index) {
    return {
      rowHeight: this.rowHeight,
      rowTop: this.rowHeight * index
    };
  }; // we don't implement as lazy row heights is not supported in this row model


  InfiniteRowModel.prototype.ensureRowHeightsValid = function (startPixel, endPixel, startLimitIndex, endLimitIndex) {
    return false;
  };

  InfiniteRowModel.prototype.init = function () {
    var _this = this;

    if (!this.gridOptionsWrapper.isRowModelInfinite()) {
      return;
    }

    this.rowHeight = this.gridOptionsWrapper.getRowHeightAsNumber();
    this.addEventListeners();
    this.addDestroyFunc(function () {
      return _this.destroyCache();
    });
  };

  InfiniteRowModel.prototype.start = function () {
    this.setDatasource(this.gridOptionsWrapper.getDatasource());
  };

  InfiniteRowModel.prototype.destroyDatasource = function () {
    if (this.datasource) {
      if (this.datasource.destroy) {
        this.datasource.destroy();
      }

      this.rowRenderer.datasourceChanged();
      this.datasource = null;
    }
  };

  InfiniteRowModel.prototype.isLastRowFound = function () {
    return this.infiniteCache ? this.infiniteCache.isMaxRowFound() : false;
  };

  InfiniteRowModel.prototype.addEventListeners = function () {
    this.addDestroyableEventListener(this.eventService, Events.EVENT_FILTER_CHANGED, this.onFilterChanged.bind(this));
    this.addDestroyableEventListener(this.eventService, Events.EVENT_SORT_CHANGED, this.onSortChanged.bind(this));
    this.addDestroyableEventListener(this.eventService, Events.EVENT_COLUMN_EVERYTHING_CHANGED, this.onColumnEverything.bind(this));
  };

  InfiniteRowModel.prototype.onFilterChanged = function () {
    this.reset();
  };

  InfiniteRowModel.prototype.onSortChanged = function () {
    this.reset();
  };

  InfiniteRowModel.prototype.onColumnEverything = function () {
    var resetRequired; // if cache params, we require reset only if sort model has changed. we don't need to check
    // for filter model, as the filter manager will fire an event when columns change that result
    // in the filter changing.

    if (this.cacheParams) {
      resetRequired = this.isSortModelDifferent();
    } else {
      // if no cacheParams, means first time creating the cache, so always create one
      resetRequired = true;
    }

    if (resetRequired) {
      this.reset();
    }
  };

  InfiniteRowModel.prototype.isSortModelDifferent = function () {
    return !_.jsonEquals(this.cacheParams.sortModel, this.sortController.getSortModel());
  };

  InfiniteRowModel.prototype.getType = function () {
    return Constants.ROW_MODEL_TYPE_INFINITE;
  };

  InfiniteRowModel.prototype.setDatasource = function (datasource) {
    this.destroyDatasource();
    this.datasource = datasource; // only reset if we have a valid datasource to working with

    if (datasource) {
      this.checkForDeprecated();
      this.reset();
    }
  };

  InfiniteRowModel.prototype.checkForDeprecated = function () {
    var ds = this.datasource; // the number of concurrent loads we are allowed to the server

    if (_.exists(ds.maxConcurrentRequests)) {
      console.error('ag-Grid: since version 5.1.x, maxConcurrentRequests is replaced with grid property maxConcurrentDatasourceRequests');
    }

    if (_.exists(ds.maxPagesInCache)) {
      console.error('ag-Grid: since version 5.1.x, maxPagesInCache is replaced with grid property maxPagesInPaginationCache');
    }

    if (_.exists(ds.overflowSize)) {
      console.error('ag-Grid: since version 5.1.x, overflowSize is replaced with grid property paginationOverflowSize');
    }

    if (_.exists(ds.blockSize)) {
      console.error('ag-Grid: since version 5.1.x, pageSize/blockSize is replaced with grid property infinitePageSize');
    }
  };

  InfiniteRowModel.prototype.isEmpty = function () {
    return _.missing(this.infiniteCache);
  };

  InfiniteRowModel.prototype.isRowsToRender = function () {
    return _.exists(this.infiniteCache);
  };

  InfiniteRowModel.prototype.getNodesInRangeForSelection = function (firstInRange, lastInRange) {
    return this.infiniteCache ? this.infiniteCache.getRowNodesInRange(firstInRange, lastInRange) : [];
  };

  InfiniteRowModel.prototype.reset = function () {
    // important to return here, as the user could be setting filter or sort before
    // data-source is set
    if (_.missing(this.datasource)) {
      return;
    } // if user is providing id's, then this means we can keep the selection between datasource hits,
    // as the rows will keep their unique id's even if, for example, server side sorting or filtering
    // is done.


    var userGeneratingIds = _.exists(this.gridOptionsWrapper.getRowNodeIdFunc());

    if (!userGeneratingIds) {
      this.selectionController.reset();
    }

    this.resetCache();
    var event = this.createModelUpdatedEvent();
    this.eventService.dispatchEvent(event);
  };

  InfiniteRowModel.prototype.createModelUpdatedEvent = function () {
    return {
      type: Events.EVENT_MODEL_UPDATED,
      api: this.gridApi,
      columnApi: this.columnApi,
      // not sure if these should all be false - noticed if after implementing,
      // maybe they should be true?
      newPage: false,
      newData: false,
      keepRenderedRows: false,
      animate: false
    };
  };

  InfiniteRowModel.prototype.resetCache = function () {
    // if not first time creating a cache, need to destroy the old one
    this.destroyCache();
    var maxConcurrentRequests = this.gridOptionsWrapper.getMaxConcurrentDatasourceRequests();
    var blockLoadDebounceMillis = this.gridOptionsWrapper.getBlockLoadDebounceMillis(); // there is a bi-directional dependency between the loader and the cache,
    // so we create loader here, and then pass dependencies in setDependencies() method later

    this.rowNodeBlockLoader = new RowNodeBlockLoader(maxConcurrentRequests, blockLoadDebounceMillis);
    this.getContext().wireBean(this.rowNodeBlockLoader);
    this.cacheParams = {
      // the user provided datasource
      datasource: this.datasource,
      // sort and filter model
      filterModel: this.filterManager.getFilterModel(),
      sortModel: this.sortController.getSortModel(),
      rowNodeBlockLoader: this.rowNodeBlockLoader,
      // properties - this way we take a snapshot of them, so if user changes any, they will be
      // used next time we create a new cache, which is generally after a filter or sort change,
      // or a new datasource is set
      maxConcurrentRequests: maxConcurrentRequests,
      overflowSize: this.gridOptionsWrapper.getCacheOverflowSize(),
      initialRowCount: this.gridOptionsWrapper.getInfiniteInitialRowCount(),
      maxBlocksInCache: this.gridOptionsWrapper.getMaxBlocksInCache(),
      blockSize: this.gridOptionsWrapper.getCacheBlockSize(),
      rowHeight: this.gridOptionsWrapper.getRowHeightAsNumber(),
      // the cache could create this, however it is also used by the pages, so handy to create it
      // here as the settings are also passed to the pages
      lastAccessedSequence: new NumberSequence()
    }; // set defaults

    if (!this.cacheParams.maxConcurrentRequests || !(this.cacheParams.maxConcurrentRequests >= 1)) {
      this.cacheParams.maxConcurrentRequests = 2;
    } // page size needs to be 1 or greater. having it at 1 would be silly, as you would be hitting the
    // server for one page at a time. so the default if not specified is 100.


    if (!this.cacheParams.blockSize || !(this.cacheParams.blockSize >= 1)) {
      this.cacheParams.blockSize = 100;
    } // if user doesn't give initial rows to display, we assume zero


    if (!(this.cacheParams.initialRowCount >= 1)) {
      this.cacheParams.initialRowCount = 0;
    } // if user doesn't provide overflow, we use default overflow of 1, so user can scroll past
    // the current page and request first row of next page


    if (!(this.cacheParams.overflowSize >= 1)) {
      this.cacheParams.overflowSize = 1;
    }

    this.infiniteCache = new InfiniteCache(this.cacheParams);
    this.getContext().wireBean(this.infiniteCache);
    this.infiniteCache.addEventListener(RowNodeCache.EVENT_CACHE_UPDATED, this.onCacheUpdated.bind(this));
  };

  InfiniteRowModel.prototype.destroyCache = function () {
    if (this.infiniteCache) {
      this.infiniteCache.destroy();
      this.infiniteCache = null;
    }

    if (this.rowNodeBlockLoader) {
      this.rowNodeBlockLoader.destroy();
      this.rowNodeBlockLoader = null;
    }
  };

  InfiniteRowModel.prototype.onCacheUpdated = function () {
    var event = this.createModelUpdatedEvent();
    this.eventService.dispatchEvent(event);
  };

  InfiniteRowModel.prototype.getRow = function (rowIndex) {
    return this.infiniteCache ? this.infiniteCache.getRow(rowIndex) : null;
  };

  InfiniteRowModel.prototype.getRowNode = function (id) {
    var result = null;
    this.forEachNode(function (rowNode) {
      if (rowNode.id === id) {
        result = rowNode;
      }
    });
    return result;
  };

  InfiniteRowModel.prototype.forEachNode = function (callback) {
    if (this.infiniteCache) {
      this.infiniteCache.forEachNodeDeep(callback, new NumberSequence());
    }
  };

  InfiniteRowModel.prototype.getCurrentPageHeight = function () {
    return this.getRowCount() * this.rowHeight;
  };

  InfiniteRowModel.prototype.getTopLevelRowCount = function () {
    return this.getRowCount();
  };

  InfiniteRowModel.prototype.getTopLevelRowDisplayedIndex = function (topLevelIndex) {
    return topLevelIndex;
  };

  InfiniteRowModel.prototype.getRowIndexAtPixel = function (pixel) {
    if (this.rowHeight !== 0) {
      // avoid divide by zero error
      var rowIndexForPixel = Math.floor(pixel / this.rowHeight);
      var lastRowIndex = this.getRowCount() - 1;

      if (rowIndexForPixel > lastRowIndex) {
        return lastRowIndex;
      } else {
        return rowIndexForPixel;
      }
    } else {
      return 0;
    }
  };

  InfiniteRowModel.prototype.getRowCount = function () {
    return this.infiniteCache ? this.infiniteCache.getVirtualRowCount() : 0;
  };

  InfiniteRowModel.prototype.updateRowData = function (transaction) {
    if (_.exists(transaction.remove) || _.exists(transaction.update)) {
      console.warn('ag-Grid: updateRowData for InfiniteRowModel does not support remove or update, only add');
      return;
    }

    if (_.missing(transaction.addIndex)) {
      console.warn('ag-Grid: updateRowData for InfiniteRowModel requires add and addIndex to be set');
      return;
    }

    if (this.infiniteCache) {
      this.infiniteCache.insertItemsAtIndex(transaction.addIndex, transaction.add);
    }
  };

  InfiniteRowModel.prototype.isRowPresent = function (rowNode) {
    return false;
  };

  InfiniteRowModel.prototype.refreshCache = function () {
    if (this.infiniteCache) {
      this.infiniteCache.refreshCache();
    }
  };

  InfiniteRowModel.prototype.purgeCache = function () {
    if (this.infiniteCache) {
      this.infiniteCache.purgeCache();
    }
  };

  InfiniteRowModel.prototype.getVirtualRowCount = function () {
    if (this.infiniteCache) {
      return this.infiniteCache.getVirtualRowCount();
    } else {
      return null;
    }
  };

  InfiniteRowModel.prototype.isMaxRowFound = function () {
    if (this.infiniteCache) {
      return this.infiniteCache.isMaxRowFound();
    }
  };

  InfiniteRowModel.prototype.setVirtualRowCount = function (rowCount, maxRowFound) {
    if (this.infiniteCache) {
      this.infiniteCache.setVirtualRowCount(rowCount, maxRowFound);
    }
  };

  InfiniteRowModel.prototype.getBlockState = function () {
    if (this.rowNodeBlockLoader) {
      return this.rowNodeBlockLoader.getBlockState();
    } else {
      return null;
    }
  };

  __decorate$1U([Autowired('gridOptionsWrapper')], InfiniteRowModel.prototype, "gridOptionsWrapper", void 0);

  __decorate$1U([Autowired('filterManager')], InfiniteRowModel.prototype, "filterManager", void 0);

  __decorate$1U([Autowired('sortController')], InfiniteRowModel.prototype, "sortController", void 0);

  __decorate$1U([Autowired('selectionController')], InfiniteRowModel.prototype, "selectionController", void 0);

  __decorate$1U([Autowired('eventService')], InfiniteRowModel.prototype, "eventService", void 0);

  __decorate$1U([Autowired('gridApi')], InfiniteRowModel.prototype, "gridApi", void 0);

  __decorate$1U([Autowired('columnApi')], InfiniteRowModel.prototype, "columnApi", void 0);

  __decorate$1U([Autowired('rowRenderer')], InfiniteRowModel.prototype, "rowRenderer", void 0);

  __decorate$1U([PostConstruct], InfiniteRowModel.prototype, "init", null);

  __decorate$1U([PreDestroy], InfiniteRowModel.prototype, "destroyDatasource", null);

  InfiniteRowModel = __decorate$1U([Bean('rowModel')], InfiniteRowModel);
  return InfiniteRowModel;
}(BeanStub);

var InfiniteRowModelModule = {
  moduleName: exports.ModuleNames.InfiniteRowModelModule,
  rowModels: {
    'infinite': InfiniteRowModel
  }
};

var __decorate$1V = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var BaseGridSerializingSession =
/** @class */
function () {
  function BaseGridSerializingSession(config) {
    var columnController = config.columnController,
        valueService = config.valueService,
        gridOptionsWrapper = config.gridOptionsWrapper,
        processCellCallback = config.processCellCallback,
        processHeaderCallback = config.processHeaderCallback,
        processGroupHeaderCallback = config.processGroupHeaderCallback,
        processRowGroupCallback = config.processRowGroupCallback;
    this.columnController = columnController;
    this.valueService = valueService;
    this.gridOptionsWrapper = gridOptionsWrapper;
    this.processCellCallback = processCellCallback;
    this.processHeaderCallback = processHeaderCallback;
    this.processGroupHeaderCallback = processGroupHeaderCallback;
    this.processRowGroupCallback = processRowGroupCallback;
  }

  BaseGridSerializingSession.prototype.prepare = function (columnsToExport) {
    this.firstGroupColumn = _.find(columnsToExport, function (col) {
      return !!col.getColDef().showRowGroup;
    });
  };

  BaseGridSerializingSession.prototype.extractHeaderValue = function (column) {
    var value = this.getHeaderName(this.processHeaderCallback, column);
    return value != null ? value : '';
  };

  BaseGridSerializingSession.prototype.extractRowCellValue = function (column, index, type, node) {
    // we render the group summary text e.g. "-> Parent -> Child"...
    var renderGroupSummaryCell = // on group rows
    node && node.group && ( // in the first group column if groups appear in regular grid cells
    column === this.firstGroupColumn // or the first cell in the row, if we're doing full width rows
    || index === 0 && this.gridOptionsWrapper.isGroupUseEntireRow(this.columnController.isPivotMode()));
    var valueForCell;

    if (renderGroupSummaryCell) {
      valueForCell = this.createValueForGroupNode(node);
    } else {
      valueForCell = this.valueService.getValue(column, node);
    }

    var value = this.processCell(node, column, valueForCell, this.processCellCallback, type);
    return value != null ? value : '';
  };

  BaseGridSerializingSession.prototype.getHeaderName = function (callback, column) {
    if (callback) {
      return callback({
        column: column,
        api: this.gridOptionsWrapper.getApi(),
        columnApi: this.gridOptionsWrapper.getColumnApi(),
        context: this.gridOptionsWrapper.getContext()
      });
    } else {
      return this.columnController.getDisplayNameForColumn(column, 'csv', true);
    }
  };

  BaseGridSerializingSession.prototype.createValueForGroupNode = function (node) {
    if (this.processRowGroupCallback) {
      return this.processRowGroupCallback({
        node: node,
        api: this.gridOptionsWrapper.getApi(),
        columnApi: this.gridOptionsWrapper.getColumnApi(),
        context: this.gridOptionsWrapper.getContext()
      });
    }

    var keys = [node.key];

    while (node.parent) {
      node = node.parent;
      keys.push(node.key);
    }

    return keys.reverse().join(' -> ');
  };

  BaseGridSerializingSession.prototype.processCell = function (rowNode, column, value, processCellCallback, type) {
    if (processCellCallback) {
      return processCellCallback({
        column: column,
        node: rowNode,
        value: value,
        api: this.gridOptionsWrapper.getApi(),
        columnApi: this.gridOptionsWrapper.getColumnApi(),
        context: this.gridOptionsWrapper.getContext(),
        type: type
      });
    } else {
      return value;
    }
  };

  return BaseGridSerializingSession;
}();

var GridSerializer =
/** @class */
function () {
  function GridSerializer() {}

  GridSerializer.prototype.serialize = function (gridSerializingSession, params) {
    if (params === void 0) {
      params = {};
    }

    var rowSkipper = params.shouldRowBeSkipped || function () {
      return false;
    };

    var api = this.gridOptionsWrapper.getApi();
    var columnApi = this.gridOptionsWrapper.getColumnApi();
    var skipSingleChildrenGroup = this.gridOptionsWrapper.isGroupRemoveSingleChildren();
    var skipLowestSingleChildrenGroup = this.gridOptionsWrapper.isGroupRemoveLowestSingleChildren();
    var context = this.gridOptionsWrapper.getContext(); // when in pivot mode, we always render cols on screen, never 'all columns'

    var isPivotMode = this.columnController.isPivotMode();
    var rowModelNormal = this.rowModel.getType() === Constants.ROW_MODEL_TYPE_CLIENT_SIDE;
    var onlySelectedNonStandardModel = !rowModelNormal && params.onlySelected;
    var columnsToExport = [];

    if (_.existsAndNotEmpty(params.columnKeys)) {
      columnsToExport = this.columnController.getGridColumns(params.columnKeys);
    } else if (params.allColumns && !isPivotMode) {
      // add auto group column for tree data
      columnsToExport = this.gridOptionsWrapper.isTreeData() ? this.columnController.getGridColumns([Constants.GROUP_AUTO_COLUMN_ID]) : [];
      columnsToExport = columnsToExport.concat(this.columnController.getAllPrimaryColumns() || []);
    } else {
      columnsToExport = this.columnController.getAllDisplayedColumns();
    }

    if (params.customHeader) {
      gridSerializingSession.addCustomContent(params.customHeader);
    }

    gridSerializingSession.prepare(columnsToExport); // first pass, put in the header names of the cols

    if (params.columnGroups) {
      var groupInstanceIdCreator = new GroupInstanceIdCreator();
      var displayedGroups = this.displayedGroupCreator.createDisplayedGroups(columnsToExport, this.columnController.getGridBalancedTree(), groupInstanceIdCreator, null);
      this.recursivelyAddHeaderGroups(displayedGroups, gridSerializingSession, params.processGroupHeaderCallback);
    }

    if (!params.skipHeader) {
      var gridRowIterator_1 = gridSerializingSession.onNewHeaderRow();
      columnsToExport.forEach(function (column, index) {
        gridRowIterator_1.onColumn(column, index, undefined);
      });
    }

    this.pinnedRowModel.forEachPinnedTopRow(processRow);

    if (isPivotMode) {
      if (this.rowModel.forEachPivotNode) {
        this.rowModel.forEachPivotNode(processRow);
      } else {
        //Must be enterprise, so we can just loop through all the nodes
        this.rowModel.forEachNode(processRow);
      }
    } else {
      // onlySelectedAllPages: user doing pagination and wants selected items from
      // other pages, so cannot use the standard row model as it won't have rows from
      // other pages.
      // onlySelectedNonStandardModel: if user wants selected in non standard row model
      // (eg viewport) then again rowmodel cannot be used, so need to use selected instead.
      if (params.onlySelectedAllPages || onlySelectedNonStandardModel) {
        var selectedNodes = this.selectionController.getSelectedNodes();
        selectedNodes.forEach(function (node) {
          processRow(node);
        });
      } else {
        // here is everything else - including standard row model and selected. we don't use
        // the selection model even when just using selected, so that the result is the order
        // of the rows appearing on the screen.
        if (rowModelNormal) {
          this.rowModel.forEachNodeAfterFilterAndSort(processRow);
        } else {
          this.rowModel.forEachNode(processRow);
        }
      }
    }

    this.pinnedRowModel.forEachPinnedBottomRow(processRow);

    if (params.customFooter) {
      gridSerializingSession.addCustomContent(params.customFooter);
    }

    function processRow(node) {
      var shouldSkipLowestGroup = skipLowestSingleChildrenGroup && node.leafGroup;
      var shouldSkipCurrentGroup = node.allChildrenCount === 1 && (skipSingleChildrenGroup || shouldSkipLowestGroup);

      if (node.group && (params.skipGroups || shouldSkipCurrentGroup)) {
        return;
      }

      if (params.skipFooters && node.footer) {
        return;
      }

      if (params.onlySelected && !node.isSelected()) {
        return;
      }

      if (params.skipPinnedTop && node.rowPinned === 'top') {
        return;
      }

      if (params.skipPinnedBottom && node.rowPinned === 'bottom') {
        return;
      } // if we are in pivotMode, then the grid will show the root node only
      // if it's not a leaf group


      var nodeIsRootNode = node.level === -1;

      if (nodeIsRootNode && !node.leafGroup) {
        return;
      }

      var shouldRowBeSkipped = rowSkipper({
        node: node,
        api: api,
        context: context
      });

      if (shouldRowBeSkipped) {
        return;
      }

      var rowAccumulator = gridSerializingSession.onNewBodyRow();
      columnsToExport.forEach(function (column, index) {
        rowAccumulator.onColumn(column, index, node);
      });

      if (params.getCustomContentBelowRow) {
        var content = params.getCustomContentBelowRow({
          node: node,
          api: api,
          columnApi: columnApi,
          context: context
        });

        if (content) {
          gridSerializingSession.addCustomContent(content);
        }
      }
    }

    return gridSerializingSession.parse();
  };

  GridSerializer.prototype.recursivelyAddHeaderGroups = function (displayedGroups, gridSerializingSession, processGroupHeaderCallback) {
    var directChildrenHeaderGroups = [];
    displayedGroups.forEach(function (columnGroupChild) {
      var columnGroup = columnGroupChild;

      if (!columnGroup.getChildren) {
        return;
      }

      columnGroup.getChildren().forEach(function (it) {
        return directChildrenHeaderGroups.push(it);
      });
    });

    if (displayedGroups.length > 0 && displayedGroups[0] instanceof ColumnGroup) {
      this.doAddHeaderHeader(gridSerializingSession, displayedGroups, processGroupHeaderCallback);
    }

    if (directChildrenHeaderGroups && directChildrenHeaderGroups.length > 0) {
      this.recursivelyAddHeaderGroups(directChildrenHeaderGroups, gridSerializingSession, processGroupHeaderCallback);
    }
  };

  GridSerializer.prototype.doAddHeaderHeader = function (gridSerializingSession, displayedGroups, processGroupHeaderCallback) {
    var _this = this;

    var gridRowIterator = gridSerializingSession.onNewHeaderGroupingRow();
    var columnIndex = 0;
    displayedGroups.forEach(function (columnGroupChild) {
      var columnGroup = columnGroupChild;
      var name;

      if (processGroupHeaderCallback) {
        name = processGroupHeaderCallback({
          columnGroup: columnGroup,
          api: _this.gridOptionsWrapper.getApi(),
          columnApi: _this.gridOptionsWrapper.getColumnApi(),
          context: _this.gridOptionsWrapper.getContext()
        });
      } else {
        name = _this.columnController.getDisplayNameForColumnGroup(columnGroup, 'header');
      }

      gridRowIterator.onColumn(name || '', columnIndex++, columnGroup.getLeafColumns().length - 1);
    });
  };

  __decorate$1V([Autowired('displayedGroupCreator')], GridSerializer.prototype, "displayedGroupCreator", void 0);

  __decorate$1V([Autowired('columnController')], GridSerializer.prototype, "columnController", void 0);

  __decorate$1V([Autowired('rowModel')], GridSerializer.prototype, "rowModel", void 0);

  __decorate$1V([Autowired('pinnedRowModel')], GridSerializer.prototype, "pinnedRowModel", void 0);

  __decorate$1V([Autowired('selectionController')], GridSerializer.prototype, "selectionController", void 0);

  __decorate$1V([Autowired('columnFactory')], GridSerializer.prototype, "columnFactory", void 0);

  __decorate$1V([Autowired('gridOptionsWrapper')], GridSerializer.prototype, "gridOptionsWrapper", void 0);

  GridSerializer = __decorate$1V([Bean("gridSerializer")], GridSerializer);
  return GridSerializer;
}();

var RowType;

(function (RowType) {
  RowType[RowType["HEADER_GROUPING"] = 0] = "HEADER_GROUPING";
  RowType[RowType["HEADER"] = 1] = "HEADER";
  RowType[RowType["BODY"] = 2] = "BODY";
})(RowType || (RowType = {}));

var __extends$1g = undefined && undefined.__extends || function () {
  var _extendStatics81 = function extendStatics(d, b) {
    _extendStatics81 = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) {
        if (b.hasOwnProperty(p)) d[p] = b[p];
      }
    };

    return _extendStatics81(d, b);
  };

  return function (d, b) {
    _extendStatics81(d, b);

    function __() {
      this.constructor = d;
    }

    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();

var __decorate$1W = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var LINE_SEPARATOR = '\r\n';

var CsvSerializingSession =
/** @class */
function (_super) {
  __extends$1g(CsvSerializingSession, _super);

  function CsvSerializingSession(config) {
    var _this = _super.call(this, config) || this;

    _this.isFirstLine = true;
    _this.result = '';
    var suppressQuotes = config.suppressQuotes,
        columnSeparator = config.columnSeparator;
    _this.suppressQuotes = suppressQuotes;
    _this.columnSeparator = columnSeparator;
    return _this;
  }

  CsvSerializingSession.prototype.addCustomContent = function (content) {
    var _this = this;

    if (!content) {
      return;
    }

    if (typeof content === 'string') {
      // we used to require the customFooter to be prefixed with a newline but no longer do,
      // so only add the newline if the user has not supplied one
      if (!/^\s*\n/.test(content)) {
        this.beginNewLine();
      } // replace whatever newlines are supplied with the style we're using


      content = content.replace(/\r?\n/g, LINE_SEPARATOR);
      this.result += content;
    } else {
      content.forEach(function (row) {
        _this.beginNewLine();

        row.forEach(function (cell, index) {
          if (index !== 0) {
            _this.result += _this.columnSeparator;
          }

          _this.result += _this.putInQuotes(cell.data.value || '');

          if (cell.mergeAcross) {
            _this.appendEmptyCells(cell.mergeAcross);
          }
        });
      });
    }
  };

  CsvSerializingSession.prototype.onNewHeaderGroupingRow = function () {
    this.beginNewLine();
    return {
      onColumn: this.onNewHeaderGroupingRowColumn.bind(this)
    };
  };

  CsvSerializingSession.prototype.onNewHeaderGroupingRowColumn = function (header, index, span) {
    if (index != 0) {
      this.result += this.columnSeparator;
    }

    this.result += this.putInQuotes(header);
    this.appendEmptyCells(span);
  };

  CsvSerializingSession.prototype.appendEmptyCells = function (count) {
    for (var i = 1; i <= count; i++) {
      this.result += this.columnSeparator + this.putInQuotes("");
    }
  };

  CsvSerializingSession.prototype.onNewHeaderRow = function () {
    this.beginNewLine();
    return {
      onColumn: this.onNewHeaderRowColumn.bind(this)
    };
  };

  CsvSerializingSession.prototype.onNewHeaderRowColumn = function (column, index, node) {
    if (index != 0) {
      this.result += this.columnSeparator;
    }

    this.result += this.putInQuotes(this.extractHeaderValue(column));
  };

  CsvSerializingSession.prototype.onNewBodyRow = function () {
    this.beginNewLine();
    return {
      onColumn: this.onNewBodyRowColumn.bind(this)
    };
  };

  CsvSerializingSession.prototype.onNewBodyRowColumn = function (column, index, node) {
    if (index != 0) {
      this.result += this.columnSeparator;
    }

    this.result += this.putInQuotes(this.extractRowCellValue(column, index, Constants.EXPORT_TYPE_CSV, node));
  };

  CsvSerializingSession.prototype.putInQuotes = function (value) {
    if (this.suppressQuotes) {
      return value;
    }

    if (value === null || value === undefined) {
      return '""';
    }

    var stringValue;

    if (typeof value === 'string') {
      stringValue = value;
    } else if (typeof value.toString === 'function') {
      stringValue = value.toString();
    } else {
      console.warn('unknown value type during csv conversion');
      stringValue = '';
    } // replace each " with "" (ie two sets of double quotes is how to do double quotes in csv)


    var valueEscaped = stringValue.replace(/"/g, "\"\"");
    return '"' + valueEscaped + '"';
  };

  CsvSerializingSession.prototype.parse = function () {
    return this.result + LINE_SEPARATOR;
  };

  CsvSerializingSession.prototype.beginNewLine = function () {
    if (!this.isFirstLine) {
      this.result += LINE_SEPARATOR;
    }

    this.isFirstLine = false;
  };

  return CsvSerializingSession;
}(BaseGridSerializingSession);

var BaseCreator =
/** @class */
function () {
  function BaseCreator() {}

  BaseCreator.prototype.setBeans = function (beans) {
    this.beans = beans;
  };

  BaseCreator.prototype.export = function (userParams) {
    if (this.isExportSuppressed()) {
      console.warn("ag-grid: Export cancelled. Export is not allowed as per your configuration.");
      return '';
    }

    var _a = this.getMergedParamsAndData(userParams),
        mergedParams = _a.mergedParams,
        data = _a.data;

    var fileNamePresent = mergedParams && mergedParams.fileName && mergedParams.fileName.length !== 0;
    var fileName = fileNamePresent ? mergedParams.fileName : this.getDefaultFileName();

    if (fileName.indexOf(".") === -1) {
      fileName = fileName + "." + this.getDefaultFileExtension();
    }

    this.beans.downloader.download(fileName, this.packageFile(data));
    return data;
  };

  BaseCreator.prototype.getData = function (params) {
    return this.getMergedParamsAndData(params).data;
  };

  BaseCreator.prototype.getMergedParamsAndData = function (userParams) {
    var mergedParams = this.mergeDefaultParams(userParams);
    var data = this.beans.gridSerializer.serialize(this.createSerializingSession(mergedParams), mergedParams);
    return {
      mergedParams: mergedParams,
      data: data
    };
  };

  BaseCreator.prototype.mergeDefaultParams = function (userParams) {
    var baseParams = this.beans.gridOptionsWrapper.getDefaultExportParams();
    var params = {};

    _.assign(params, baseParams);

    _.assign(params, userParams);

    return params;
  };

  BaseCreator.prototype.packageFile = function (data) {
    return new Blob(["\uFEFF", data], {
      type: window.navigator.msSaveOrOpenBlob ? this.getMimeType() : 'octet/stream'
    });
  };

  return BaseCreator;
}();

var CsvCreator =
/** @class */
function (_super) {
  __extends$1g(CsvCreator, _super);

  function CsvCreator() {
    return _super !== null && _super.apply(this, arguments) || this;
  }

  CsvCreator.prototype.postConstruct = function () {
    this.setBeans({
      downloader: this.downloader,
      gridSerializer: this.gridSerializer,
      gridOptionsWrapper: this.gridOptionsWrapper
    });
  };

  CsvCreator.prototype.exportDataAsCsv = function (params) {
    return this.export(params);
  };

  CsvCreator.prototype.getDataAsCsv = function (params) {
    return this.getData(params);
  };

  CsvCreator.prototype.getMimeType = function () {
    return 'text/csv;charset=utf-8;';
  };

  CsvCreator.prototype.getDefaultFileName = function () {
    return 'export.csv';
  };

  CsvCreator.prototype.getDefaultFileExtension = function () {
    return 'csv';
  };

  CsvCreator.prototype.createSerializingSession = function (params) {
    var _a = this,
        columnController = _a.columnController,
        valueService = _a.valueService,
        gridOptionsWrapper = _a.gridOptionsWrapper;

    var processCellCallback = params.processCellCallback,
        processHeaderCallback = params.processHeaderCallback,
        processGroupHeaderCallback = params.processGroupHeaderCallback,
        processRowGroupCallback = params.processRowGroupCallback,
        suppressQuotes = params.suppressQuotes,
        columnSeparator = params.columnSeparator;
    return new CsvSerializingSession({
      columnController: columnController,
      valueService: valueService,
      gridOptionsWrapper: gridOptionsWrapper,
      processCellCallback: processCellCallback || undefined,
      processHeaderCallback: processHeaderCallback || undefined,
      processGroupHeaderCallback: processGroupHeaderCallback || undefined,
      processRowGroupCallback: processRowGroupCallback || undefined,
      suppressQuotes: suppressQuotes || false,
      columnSeparator: columnSeparator || ','
    });
  };

  CsvCreator.prototype.isExportSuppressed = function () {
    return this.gridOptionsWrapper.isSuppressCsvExport();
  };

  __decorate$1W([Autowired('columnController')], CsvCreator.prototype, "columnController", void 0);

  __decorate$1W([Autowired('valueService')], CsvCreator.prototype, "valueService", void 0);

  __decorate$1W([Autowired('downloader')], CsvCreator.prototype, "downloader", void 0);

  __decorate$1W([Autowired('gridSerializer')], CsvCreator.prototype, "gridSerializer", void 0);

  __decorate$1W([Autowired('gridOptionsWrapper')], CsvCreator.prototype, "gridOptionsWrapper", void 0);

  __decorate$1W([PostConstruct], CsvCreator.prototype, "postConstruct", null);

  CsvCreator = __decorate$1W([Bean('csvCreator')], CsvCreator);
  return CsvCreator;
}(BaseCreator);

var __decorate$1X = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var Downloader =
/** @class */
function () {
  function Downloader() {}

  Downloader.prototype.download = function (fileName, content) {
    // Internet Explorer
    if (window.navigator.msSaveOrOpenBlob) {
      window.navigator.msSaveOrOpenBlob(content, fileName);
    } else {
      // Other Browsers
      var element = document.createElement("a");
      var url_1 = window.URL.createObjectURL(content);
      element.setAttribute("href", url_1);
      element.setAttribute("download", fileName);
      element.style.display = "none";
      document.body.appendChild(element);
      element.dispatchEvent(new MouseEvent('click', {
        bubbles: false,
        cancelable: true,
        view: window
      }));
      document.body.removeChild(element);
      window.setTimeout(function () {
        window.URL.revokeObjectURL(url_1);
      }, 0);
    }
  };

  Downloader = __decorate$1X([Bean("downloader")], Downloader);
  return Downloader;
}();

var __decorate$1Y = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
};

var LINE_SEPARATOR$1 = '\r\n';

var XmlFactory =
/** @class */
function () {
  function XmlFactory() {}

  XmlFactory.prototype.createHeader = function (headerElement) {
    if (headerElement === void 0) {
      headerElement = {};
    }

    var headerStart = '<?';
    var headerEnd = '?>';
    var keys = ['version'];

    if (!headerElement.version) {
      headerElement.version = "1.0";
    }

    if (headerElement.encoding) {
      keys.push('encoding');
    }

    if (headerElement.standalone) {
      keys.push('standalone');
    }

    var att = keys.map(function (key) {
      return key + "=\"" + headerElement[key] + "\"";
    }).join(' ');
    return headerStart + "xml " + att + " " + headerEnd;
  };

  XmlFactory.prototype.createXml = function (xmlElement, booleanTransformer) {
    var _this = this;

    var props = '';

    if (xmlElement.properties) {
      if (xmlElement.properties.prefixedAttributes) {
        xmlElement.properties.prefixedAttributes.forEach(function (prefixedSet) {
          Object.keys(prefixedSet.map).forEach(function (key) {
            props += _this.returnAttributeIfPopulated(prefixedSet.prefix + key, prefixedSet.map[key], booleanTransformer);
          });
        });
      }

      if (xmlElement.properties.rawMap) {
        Object.keys(xmlElement.properties.rawMap).forEach(function (key) {
          props += _this.returnAttributeIfPopulated(key, xmlElement.properties.rawMap[key], booleanTransformer);
        });
      }
    }

    var result = '<' + xmlElement.name + props;

    if (!xmlElement.children && xmlElement.textNode == null) {
      return result + '/>' + LINE_SEPARATOR$1;
    }

    if (xmlElement.textNode != null) {
      return result + '>' + xmlElement.textNode + '</' + xmlElement.name + '>' + LINE_SEPARATOR$1;
    }

    result += '>' + LINE_SEPARATOR$1;

    if (xmlElement.children) {
      xmlElement.children.forEach(function (it) {
        result += _this.createXml(it, booleanTransformer);
      });
    }

    return result + '</' + xmlElement.name + '>' + LINE_SEPARATOR$1;
  };

  XmlFactory.prototype.returnAttributeIfPopulated = function (key, value, booleanTransformer) {
    if (!value && value !== '' && value !== 0) {
      return '';
    }

    var xmlValue = value;

    if (typeof value === 'boolean') {
      if (booleanTransformer) {
        xmlValue = booleanTransformer(value);
      }
    }

    return " " + key + "=\"" + xmlValue + "\"";
  };

  XmlFactory = __decorate$1Y([Bean('xmlFactory')], XmlFactory);
  return XmlFactory;
}();

var __decorate$1Z = undefined && undefined.__decorate || function (decorators, target, key, desc) {
  var c = arguments.length,
      r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc,
      d;
  if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);else for (var i = decorators.length - 1; i >= 0; i--) {
    if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
  }
  return c > 3 && r && Object.defineProperty(target, key, r), r;
}; // table for crc calculation
// from: https://referencesource.microsoft.com/#System/sys/System/IO/compression/Crc32Helper.cs,3b31978c7d7f7246,references


var crcTable = [0, 1996959894, -301047508, -1727442502, 124634137, 1886057615, -379345611, -1637575261, 249268274, 2044508324, -522852066, -1747789432, 162941995, 2125561021, -407360249, -1866523247, 498536548, 1789927666, -205950648, -2067906082, 450548861, 1843258603, -187386543, -2083289657, 325883990, 1684777152, -43845254, -1973040660, 335633487, 1661365465, -99664541, -1928851979, 997073096, 1281953886, -715111964, -1570279054, 1006888145, 1258607687, -770865667, -1526024853, 901097722, 1119000684, -608450090, -1396901568, 853044451, 1172266101, -589951537, -1412350631, 651767980, 1373503546, -925412992, -1076862698, 565507253, 1454621731, -809855591, -1195530993, 671266974, 1594198024, -972236366, -1324619484, 795835527, 1483230225, -1050600021, -1234817731, 1994146192, 31158534, -1731059524, -271249366, 1907459465, 112637215, -1614814043, -390540237, 2013776290, 251722036, -1777751922, -519137256, 2137656763, 141376813, -1855689577, -429695999, 1802195444, 476864866, -2056965928, -228458418, 1812370925, 453092731, -2113342271, -183516073, 1706088902, 314042704, -1950435094, -54949764, 1658658271, 366619977, -1932296973, -69972891, 1303535960, 984961486, -1547960204, -725929758, 1256170817, 1037604311, -1529756563, -740887301, 1131014506, 879679996, -1385723834, -631195440, 1141124467, 855842277, -1442165665, -586318647, 1342533948, 654459306, -1106571248, -921952122, 1466479909, 544179635, -1184443383, -832445281, 1591671054, 702138776, -1328506846, -942167884, 1504918807, 783551873, -1212326853, -1061524307, -306674912, -1698712650, 62317068, 1957810842, -355121351, -1647151185, 81470997, 1943803523, -480048366, -1805370492, 225274430, 2053790376, -468791541, -1828061283, 167816743, 2097651377, -267414716, -2029476910, 503444072, 1762050814, -144550051, -2140837941, 426522225, 1852507879, -19653770, -1982649376, 282753626, 1742555852, -105259153, -1900089351, 397917763, 1622183637, -690576408, -1580100738, 953729732, 1340076626, -776247311, -1497606297, 1068828381, 1219638859, -670225446, -1358292148, 906185462, 1090812512, -547295293, -1469587627, 829329135, 1181335161, -882789492, -1134132454, 628085408, 1382605366, -871598187, -1156888829, 570562233, 1426400815, -977650754, -1296233688, 733239954, 1555261956, -1026031705, -1244606671, 752459403, 1541320221, -1687895376, -328994266, 1969922972, 40735498, -1677130071, -351390145, 1913087877, 83908371, -1782625662, -491226604, 2075208622, 213261112, -1831694693, -438977011, 2094854071, 198958881, -2032938284, -237706686, 1759359992, 534414190, -2118248755, -155638181, 1873836001, 414664567, -2012718362, -15766928, 1711684554, 285281116, -1889165569, -127750551, 1634467795, 376229701, -1609899400, -686959890, 1308918612, 956543938, -1486412191, -799009033, 1231636301, 1047427035, -1362007478, -640263460, 1088359270, 936918000, -1447252397, -558129467, 1202900863, 817233897, -1111625188, -893730166, 1404277552, 615818150, -1160759803, -841546093, 1423857449, 601450431, -1285129682, -1000256840, 1567103746, 711928724, -1274298825, -1022587231, 1510334235, 755167117];

var ZipContainer =
/** @class */
function () {
  function ZipContainer() {
    var _this = this;

    this.folders = [];
    this.files = [];

    this.addFolder = function (path) {
      _this.folders.push({
        path: path,
        created: new Date()
      });
    };
  }

  ZipContainer.prototype.addFolders = function (paths) {
    paths.forEach(this.addFolder);
  };

  ZipContainer.prototype.addFile = function (path, content) {
    this.files.push({
      path: path,
      created: new Date(),
      content: content
    });
  };

  ZipContainer.prototype.clearStream = function () {
    this.folders = [];
    this.files = [];
  };

  ZipContainer.prototype.getContent = function (mimeType) {
    if (mimeType === void 0) {
      mimeType = 'application/zip';
    }

    var textOutput = this.buildFileStream();
    var uInt8Output = this.buildUint8Array(textOutput);
    this.clearStream();
    return new Blob([uInt8Output], {
      type: mimeType
    });
  };

  ZipContainer.prototype.buildFileStream = function (fData) {
    if (fData === void 0) {
      fData = '';
    }

    var totalFiles = this.folders.concat(this.files);
    var len = totalFiles.length;
    var foData = '';
    var lL = 0;
    var cL = 0;

    for (var _i = 0, totalFiles_1 = totalFiles; _i < totalFiles_1.length; _i++) {
      var currentFile = totalFiles_1[_i];

      var _a = this.getHeader(currentFile, lL),
          fileHeader = _a.fileHeader,
          folderHeader = _a.folderHeader,
          content = _a.content;

      lL += fileHeader.length + content.length;
      cL += folderHeader.length;
      fData += fileHeader + content;
      foData += folderHeader;
    }

    var foEnd = this.buildFolderEnd(len, cL, lL);
    return fData + foData + foEnd;
  };

  ZipContainer.prototype.getHeader = function (currentFile, offset) {
    var content = currentFile.content,
        path = currentFile.path,
        created = currentFile.created;
    var utf8_encode = _.utf8_encode,
        decToHex = _.decToHex;
    var utfPath = utf8_encode(path);
    var isUTF8 = utfPath !== path;
    var time = this.convertTime(created);
    var dt = this.convertDate(created);
    var extraFields = '';

    if (isUTF8) {
      var uExtraFieldPath = decToHex(1, 1) + decToHex(this.getFromCrc32Table(utfPath), 4) + utfPath;
      extraFields = "\x75\x70" + decToHex(uExtraFieldPath.length, 2) + uExtraFieldPath;
    }

    var header = '\x0A\x00' + (isUTF8 ? '\x00\x08' : '\x00\x00') + '\x00\x00' + decToHex(time, 2) + // last modified time
    decToHex(dt, 2) + // last modified date
    decToHex(content ? this.getFromCrc32Table(content) : 0, 4) + decToHex(content ? content.length : 0, 4) + // compressed size
    decToHex(content ? content.length : 0, 4) + // uncompressed size
    decToHex(utfPath.length, 2) + // file name length
    decToHex(extraFields.length, 2); // extra field length

    var fileHeader = 'PK\x03\x04' + header + utfPath + extraFields;
    var folderHeader = 'PK\x01\x02' + // central header
    '\x14\x00' + header + // file header
    '\x00\x00' + '\x00\x00' + '\x00\x00' + (content ? '\x00\x00\x00\x00' : '\x10\x00\x00\x00') + // external file attributes
    decToHex(offset, 4) + // relative offset of local header
    utfPath + // file name
    extraFields; // extra field

    return {
      fileHeader: fileHeader,
      folderHeader: folderHeader,
      content: content || ''
    };
  };

  ZipContainer.prototype.buildFolderEnd = function (tLen, cLen, lLen) {
    var decToHex = _.decToHex;
    return 'PK\x05\x06' + // central folder end
    '\x00\x00' + '\x00\x00' + decToHex(tLen, 2) + // total number of entries in the central folder
    decToHex(tLen, 2) + // total number of entries in the central folder
    decToHex(cLen, 4) + // size of the central folder
    decToHex(lLen, 4) + // central folder start offset
    '\x00\x00';
  };

  ZipContainer.prototype.buildUint8Array = function (content) {
    var uint8 = new Uint8Array(content.length);

    for (var i = 0; i < uint8.length; i++) {
      uint8[i] = content.charCodeAt(i);
    }

    return uint8;
  };

  ZipContainer.prototype.getFromCrc32Table = function (content, crc) {
    if (crc === void 0) {
      crc = 0;
    }

    if (!content.length) {
      return 0;
    }

    crc ^= -1;
    var j = 0;
    var k = 0;
    var l = 0;

    for (var i = 0; i < content.length; i++) {
      j = content.charCodeAt(i);
      k = (crc ^ j) & 0xFF;
      l = crcTable[k];
      crc = crc >>> 8 ^ l;
    }

    return crc ^ -1;
  };

  ZipContainer.prototype.convertTime = function (date) {
    var time = date.getHours();
    time <<= 6;
    time = time | date.getMinutes();
    time <<= 5;
    time = time | date.getSeconds() / 2;
    return time;
  };

  ZipContainer.prototype.convertDate = function (date) {
    var dt = date.getFullYear() - 1980;
    dt <<= 4;
    dt = dt | date.getMonth() + 1;
    dt <<= 5;
    dt = dt | date.getDate();
    return dt;
  };

  ZipContainer = __decorate$1Z([Bean('zipContainer')], ZipContainer);
  return ZipContainer;
}();

var CsvExportModule = {
  moduleName: exports.ModuleNames.CsvExportModule,
  beans: [CsvCreator, Downloader, XmlFactory, GridSerializer, ZipContainer]
};
ModuleRegistry.register(ClientSideRowModelModule);
ModuleRegistry.register(InfiniteRowModelModule);
ModuleRegistry.register(CsvExportModule);
exports.AgAbstractField = AgAbstractField;
exports.AgAngleSelect = AgAngleSelect;
exports.AgCheckbox = AgCheckbox;
exports.AgColorPicker = AgColorPicker;
exports.AgDialog = AgDialog;
exports.AgGroupComponent = AgGroupComponent;
exports.AgInputNumberField = AgInputNumberField;
exports.AgInputRange = AgInputRange;
exports.AgInputTextArea = AgInputTextArea;
exports.AgInputTextField = AgInputTextField;
exports.AgPanel = AgPanel;
exports.AgRadioButton = AgRadioButton;
exports.AgSelect = AgSelect;
exports.AgSlider = AgSlider;
exports.AgToggleButton = AgToggleButton;
exports.AlignedGridsService = AlignedGridsService;
exports.AnimateShowChangeCellRenderer = AnimateShowChangeCellRenderer;
exports.AnimateSlideCellRenderer = AnimateSlideCellRenderer;
exports.AutoWidthCalculator = AutoWidthCalculator;
exports.Autowired = Autowired;
exports.BaseComponentWrapper = BaseComponentWrapper;
exports.Bean = Bean;
exports.BeanStub = BeanStub;
exports.BodyDropPivotTarget = BodyDropPivotTarget;
exports.BodyDropTarget = BodyDropTarget;
exports.CellComp = CellComp;
exports.CellNavigationService = CellNavigationService;
exports.CellPositionUtils = CellPositionUtils;
exports.CellRendererFactory = CellRendererFactory;
exports.ChangedPath = ChangedPath;
exports.CheckboxSelectionComponent = CheckboxSelectionComponent;
exports.ColDefUtil = ColDefUtil;
exports.Color = Color;
exports.Column = Column;
exports.ColumnApi = ColumnApi;
exports.ColumnController = ColumnController;
exports.ColumnFactory = ColumnFactory;
exports.ColumnGroup = ColumnGroup;
exports.ColumnKeyCreator = ColumnKeyCreator;
exports.ColumnUtils = ColumnUtils;
exports.Component = Component;
exports.ComponentUtil = ComponentUtil;
exports.Constants = Constants;
exports.Context = Context;
exports.CssClassApplier = CssClassApplier;
exports.DateFilter = DateFilter;
exports.DisplayedGroupCreator = DisplayedGroupCreator;
exports.DragAndDropService = DragAndDropService;
exports.DragService = DragService;
exports.Environment = Environment;
exports.EventService = EventService;
exports.Events = Events;
exports.ExpressionService = ExpressionService;
exports.FilterManager = FilterManager;
exports.FocusedCellController = FocusedCellController;
exports.Grid = Grid;
exports.GridApi = GridApi;
exports.GridCore = GridCore;
exports.GridOptionsWrapper = GridOptionsWrapper;
exports.GridPanel = GridPanel;
exports.GroupCellRenderer = GroupCellRenderer;
exports.GroupInstanceIdCreator = GroupInstanceIdCreator;
exports.HeaderContainer = HeaderContainer;
exports.HeaderRootComp = HeaderRootComp;
exports.HeaderRowComp = HeaderRowComp;
exports.HorizontalResizeService = HorizontalResizeService;
exports.LargeTextCellEditor = LargeTextCellEditor;
exports.Listener = Listener;
exports.Logger = Logger;
exports.LoggerFactory = LoggerFactory;
exports.MessageBox = MessageBox;
exports.ModuleRegistry = ModuleRegistry;
exports.MouseEventService = MouseEventService;
exports.MoveColumnController = MoveColumnController;
exports.NumberFilter = NumberFilter;
exports.NumberSequence = NumberSequence;
exports.Optional = Optional;
exports.OriginalColumnGroup = OriginalColumnGroup;
exports.PaginationProxy = PaginationProxy;
exports.PinnedRowModel = PinnedRowModel;
exports.PopupComponent = PopupComponent;
exports.PopupEditorWrapper = PopupEditorWrapper;
exports.PopupSelectCellEditor = PopupSelectCellEditor;
exports.PopupService = PopupService;
exports.PopupTextCellEditor = PopupTextCellEditor;
exports.PostConstruct = PostConstruct;
exports.PreConstruct = PreConstruct;
exports.PreDestroy = PreDestroy;
exports.Promise = Promise;
exports.ProvidedFilter = ProvidedFilter;
exports.Qualifier = Qualifier;
exports.QuerySelector = QuerySelector;
exports.RefSelector = RefSelector;
exports.ResizeObserverService = ResizeObserverService;
exports.RowComp = RowComp;
exports.RowNode = RowNode;
exports.RowNodeBlock = RowNodeBlock;
exports.RowNodeBlockLoader = RowNodeBlockLoader;
exports.RowNodeCache = RowNodeCache;
exports.RowPositionUtils = RowPositionUtils;
exports.RowRenderer = RowRenderer;
exports.ScalerFilter = ScalerFilter;
exports.ScrollVisibleService = ScrollVisibleService;
exports.SelectCellEditor = SelectCellEditor;
exports.SelectableService = SelectableService;
exports.SelectionController = SelectionController;
exports.SetLeftFeature = SetLeftFeature;
exports.SimpleFilter = SimpleFilter;
exports.SortController = SortController;
exports.StandardMenuFactory = StandardMenuFactory;
exports.StylingService = StylingService;
exports.TabbedLayout = TabbedLayout;
exports.TemplateService = TemplateService;
exports.TextCellEditor = TextCellEditor;
exports.TextFilter = TextFilter;
exports.TooltipManager = TooltipManager;
exports.TouchListener = TouchListener;
exports.UserComponentFactory = UserComponentFactory;
exports.UserComponentRegistry = UserComponentRegistry;
exports.Utils = Utils;
exports.ValueCache = ValueCache;
exports.ValueFormatterService = ValueFormatterService;
exports.ValueService = ValueService;
exports.VanillaFrameworkOverrides = VanillaFrameworkOverrides;
exports.VirtualList = VirtualList;
exports._ = _;
exports.defaultGroupComparator = defaultGroupComparator;
exports.initialiseAgGridWithAngular1 = initialiseAgGridWithAngular1;
exports.initialiseAgGridWithWebComponents = initialiseAgGridWithWebComponents;
exports.simpleHttpRequest = simpleHttpRequest;